// src/create/requirementsSpec.ts
import * as t3 from "io-ts";

// src/orgicore/utils/iots/index.ts
import * as t2 from "io-ts";
import { PathReporter } from "io-ts/lib/PathReporter";

// src/orgicore/utils/either.ts
var left = (l) => ({
  left: l
});
var error = left;
var right = (r) => ({
  right: r
});
var isLeft = (e) => "left" in e;
var isRight = (e) => "right" in e;
var getLeft = (e) => {
  if (isLeft(e)) return e.left;
  throw new Error("Tried to getLeft from a right");
};
var getRight = (e) => {
  if (isRight(e)) return e.right;
  if (typeof e.left === "string") {
    throw new Error(e.left);
  }
  throw e.left;
};
var getRightOr = (e, defaultValue) => isRight(e) ? e.right : defaultValue;
var getError = getLeft;
var errorToMessage = (e) => e instanceof Error ? e.message : typeof e === "string" ? e : String(e);

// src/orgicore/utils/lang.ts
import * as R from "remeda";

// src/orgicore/utils/date/date.ts
import { Temporal } from "@js-temporal/polyfill";
var today = () => {
  return Temporal.Now.plainDateISO();
};
var isPlainDate = (value) => {
  return value instanceof Temporal.PlainDate;
};
var dateFrom = (date) => {
  return Temporal.PlainDate.from(date);
};
var NAIVE_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
var isNaiveDateTime = (input) => {
  return typeof input === "string" && NAIVE_DATETIME_RE.test(input);
};
var toZonedDateTime = (input, timeZone) => {
  if (typeof input === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input))
      return Temporal.PlainDate.from(input).toZonedDateTime({
        timeZone,
        plainTime: { hour: 0 }
      });
    if (NAIVE_DATETIME_RE.test(input))
      return Temporal.PlainDateTime.from(input).toZonedDateTime(timeZone);
    if (input.includes("[")) {
      return Temporal.ZonedDateTime.from(input).withTimeZone(timeZone);
    }
    return Temporal.Instant.from(input).toZonedDateTimeISO(timeZone);
  }
  if (input instanceof Temporal.Instant)
    return input.toZonedDateTimeISO(timeZone);
  if (input instanceof Temporal.ZonedDateTime)
    return input.withTimeZone(timeZone);
  return input.toZonedDateTime({ timeZone, plainTime: { hour: 0 } });
};
var toPlainDate = (input, timeZone = "UTC") => {
  if (typeof input === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      return Temporal.PlainDate.from(input);
    }
    if (NAIVE_DATETIME_RE.test(input)) {
      return Temporal.PlainDateTime.from(input).toPlainDate();
    }
    const instant = Temporal.Instant.from(input);
    return instant.toZonedDateTimeISO(timeZone).toPlainDate();
  }
  if (input instanceof Temporal.Instant) {
    return input.toZonedDateTimeISO(timeZone).toPlainDate();
  }
  if (input instanceof Temporal.ZonedDateTime) {
    return input.toPlainDate();
  }
  return input;
};
var dateCompare = (a, b2) => {
  return Temporal.PlainDate.compare(a, b2);
};

// src/orgicore/utils/date/format.ts
var formatDate = (date) => {
  if (!date) return "Not published";
  const plainDate = toPlainDate(date);
  return plainDate.toLocaleString("en-US", {
    dateStyle: "medium"
  });
};

// src/orgicore/utils/date/friendlyDate.ts
import { Temporal as Temporal2 } from "@js-temporal/polyfill";
import * as t from "io-ts";
var WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];
var WEEKDAYS_SHORT = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat"
];
var MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
var MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var daysDiff = (a, b2) => {
  const da = a.toPlainDate();
  const db = b2.toPlainDate();
  return da.since(db, { largestUnit: "days" }).days;
};
var ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
var formatWeekday = (z) => WEEKDAYS[z.dayOfWeek % 7];
var formatDMon = (z) => `${ordinal(z.day)} ${MONTHS_SHORT[z.month - 1]}`;
var formatDMonYear = (z) => `${ordinal(z.day)} ${MONTHS_SHORT[z.month - 1]} ${z.year}`;
var dateFormat = t.union([
  t.literal("weekdayShort"),
  t.literal("dayOfMonth"),
  t.literal("weekdayLong"),
  t.literal("fullDate"),
  t.literal("monthLong"),
  t.literal("monthShort"),
  t.literal("monthNumber"),
  t.literal("year"),
  t.literal("monthYear")
]);
var formatDate2 = (input, format, opts = {}) => {
  try {
    return formatDate2Impl(input, format, opts);
  } catch {
    return String(input);
  }
};
var formatDate2Impl = (input, format, opts = {}) => {
  var _a2;
  const timeZone = (_a2 = opts.timeZone) != null ? _a2 : "Europe/London";
  const zdt = toZonedDateTime(input, timeZone);
  switch (format) {
    case "weekdayShort":
      return WEEKDAYS_SHORT[zdt.dayOfWeek % 7];
    case "dayOfMonth":
      return String(zdt.day);
    case "weekdayLong":
      return WEEKDAYS[zdt.dayOfWeek % 7];
    case "fullDate":
      return `${MONTHS_LONG[zdt.month - 1]} ${zdt.day}, ${zdt.year}`;
    case "monthLong":
      return MONTHS_LONG[zdt.month - 1];
    case "monthShort":
      return MONTHS_SHORT[zdt.month - 1];
    case "monthNumber":
      return String(zdt.month);
    case "year":
      return String(zdt.year);
    case "monthYear":
      return `${MONTHS_LONG[zdt.month - 1]} ${zdt.year}`;
  }
};
var timeOf = (input) => input.slice(11, 16);
var friendlyDayPhrase = (target, dayDelta, now) => {
  if (dayDelta === 0) return "today";
  if (dayDelta > 0) {
    if (dayDelta === 1) return "tomorrow";
    if (dayDelta < 7) return `on ${formatWeekday(target)}`;
    if (dayDelta <= 14) return `${formatWeekday(target)} ${formatDMon(target)}`;
    if (target.year === now.year) return formatDMon(target);
    return formatDMonYear(target);
  }
  const day = -dayDelta;
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  if (day < 14) return "1 week ago";
  if (day < 22) return `${Math.floor(day / 7)} weeks ago`;
  if (target.year === now.year) return formatDMon(target);
  return formatDMonYear(target);
};
var formatFriendly = (input, opts = {}) => {
  try {
    return formatFriendlyImpl(input, opts);
  } catch {
    return String(input);
  }
};
var formatFriendlyImpl = (input, opts = {}) => {
  var _a2, _b;
  const timeZone = (_a2 = opts.timeZone) != null ? _a2 : "Europe/London";
  const now = (_b = opts.now) != null ? _b : Temporal2.Now.zonedDateTimeISO(timeZone);
  const target = toZonedDateTime(input, timeZone);
  const dayDelta = daysDiff(target, now);
  if (isNaiveDateTime(input)) {
    return `${friendlyDayPhrase(target, dayDelta, now)}, ${timeOf(input)}`;
  }
  if (dayDelta > 0) return friendlyDayPhrase(target, dayDelta, now);
  const ms = Math.abs(target.epochMilliseconds - now.epochMilliseconds);
  const sec = Math.floor(ms / 1e3);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const wk = Math.floor(day / 7);
  if (sec < 45) return "just now";
  if (sec < 90) return "1 minute ago";
  if (min < 45) return `${min} minutes ago`;
  if (min < 90) return "1 hour ago";
  if (hr < 6) return `${Math.round(min / 60)} hours ago`;
  if (dayDelta === 0) return "today";
  if (day < 2) return "yesterday";
  if (day < 7) return `${day} days ago`;
  if (day < 14) return "1 week ago";
  if (day < 22) return `${wk} weeks ago`;
  if (target.year === now.year) return `${formatDMon(target)}`;
  return `${formatDMonYear(target)}`;
};
var formatPeriod = (start, end, opts = {}) => {
  try {
    return formatPeriodImpl(start, end, opts);
  } catch {
    return end ? `${String(start)} \u2013 ${String(end)}` : String(start);
  }
};
var formatPeriodImpl = (start, end, opts = {}) => {
  var _a2, _b;
  const timeZone = (_a2 = opts.timeZone) != null ? _a2 : "Europe/London";
  const now = (_b = opts.now) != null ? _b : Temporal2.Now.zonedDateTimeISO(timeZone);
  const startDate = toZonedDateTime(start, timeZone);
  if (!end) {
    return formatFriendly(start, opts);
  }
  const endDate = toZonedDateTime(end, timeZone);
  if (daysDiff(endDate, startDate) === 0) {
    const base = formatFriendly(start, opts);
    if (isNaiveDateTime(start) && isNaiveDateTime(end)) {
      return `${base}\u2013${timeOf(end)}`;
    }
    return base;
  }
  const duration = Math.abs(daysDiff(endDate, startDate)) + 1;
  const durationText = duration === 1 ? "1 day" : `${duration} days`;
  const startYear = startDate.year;
  const endYear = endDate.year;
  const currentYear = now.year;
  const includeStartYear = startYear !== currentYear || startYear !== endYear;
  const includeEndYear = endYear !== currentYear || startYear !== endYear;
  const startTime = isNaiveDateTime(start) ? `, ${timeOf(start)}` : "";
  const endTime = isNaiveDateTime(end) ? `, ${timeOf(end)}` : "";
  const startFormat = includeStartYear ? formatDMonYear(startDate) : formatDMon(startDate);
  const endFormat = includeEndYear ? formatDMonYear(endDate) : formatDMon(endDate);
  return `${durationText}, from ${startFormat}${startTime} to ${endFormat}${endTime}`;
};

// src/orgicore/utils/date/nextOccurrence.ts
import { Temporal as Temporal3 } from "@js-temporal/polyfill";
var nextOccurrence = (after, time, tz, days) => {
  const start = toInstant(after);
  const plainTime = toPlainTime(time);
  const allowed = toWeekdays(days);
  if (!start || !plainTime || !allowed || typeof tz !== "string") {
    return void 0;
  }
  let startDate;
  try {
    startDate = start.toZonedDateTimeISO(tz).toPlainDate();
  } catch {
    return void 0;
  }
  for (let offset = 0; offset <= 7; offset++) {
    const date = startDate.add({ days: offset });
    if (!allowed.has(date.dayOfWeek)) continue;
    const candidate = date.toZonedDateTime({ timeZone: tz, plainTime }).toInstant().epochMilliseconds;
    if (candidate > start.epochMilliseconds) return candidate;
  }
  return void 0;
};
var toInstant = (after) => {
  try {
    if (typeof after === "number") {
      return Number.isFinite(after) ? Temporal3.Instant.fromEpochMilliseconds(after) : void 0;
    }
    if (typeof after === "string") return Temporal3.Instant.from(after);
  } catch {
  }
  return void 0;
};
var TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
var toPlainTime = (time) => {
  const match2 = typeof time === "string" ? TIME.exec(time) : null;
  return match2 ? new Temporal3.PlainTime(Number(match2[1]), Number(match2[2])) : void 0;
};
var WEEKDAYS2 = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 7
};
var EVERY_DAY = /* @__PURE__ */ new Set([1, 2, 3, 4, 5, 6, 7]);
var toWeekdays = (days) => {
  if (days === void 0 || days === null) return EVERY_DAY;
  if (typeof days !== "string") return void 0;
  const names2 = days.split(",").map((d) => d.trim().toLowerCase()).filter((d) => d.length > 0);
  if (names2.length === 0) return void 0;
  const set = /* @__PURE__ */ new Set();
  for (const name of names2) {
    const day = WEEKDAYS2[name];
    if (day === void 0) return void 0;
    set.add(day);
  }
  return set;
};

// src/orgicore/utils/lang.ts
import { Temporal as Temporal4 } from "@js-temporal/polyfill";
function assertNotNullOrUndefined(value, message) {
  if (value === void 0 || value === null) {
    throw new Error(message);
  }
}
var provide = (provider, a) => {
  if (R.isFunction(provider)) {
    return provider(a);
  }
  return provider;
};
var isTrue = () => true;
var isError = (value) => {
  return value instanceof Error;
};
var getErrorMessage = (error2) => {
  if (isError(error2)) {
    return error2.message;
  }
  return String(error2);
};
var toArray = (value) => {
  if (value === void 0) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
};
var EMPTY_ARRAY = [];
var compareValues = (a, b2) => {
  if (a === b2) return 0;
  if (a === void 0) return 1;
  if (b2 === void 0) return -1;
  if (isPlainDate(a) && isPlainDate(b2))
    return Temporal4.PlainDate.compare(a, b2);
  if (typeof a === "number" && typeof b2 === "number") return a - b2;
  if (typeof a === "string" && typeof b2 === "string") return a.localeCompare(b2);
  return String(a).localeCompare(String(b2));
};
var getLogConfig = () => {
  var _a2;
  return (_a2 = globalThis.__LOG_CONFIG__) != null ? _a2 : {
    enabled: true,
    subsystems: {}
  };
};
var isSubsystemEnabled = (name) => {
  var _a2;
  const config = getLogConfig();
  if (!config.enabled) return false;
  return (_a2 = config.subsystems[name]) != null ? _a2 : true;
};
var log = console.log;
var createLogger = (subsystem) => (...args) => {
  if (isSubsystemEnabled(subsystem)) {
    console.log(`[${subsystem}]`, ...args);
  }
};
var configureLogging = (config) => {
  const current = getLogConfig();
  globalThis.__LOG_CONFIG__ = {
    ...current,
    ...config,
    subsystems: { ...current.subsystems, ...config.subsystems }
  };
};
var emptyObj = {};
var createToggle = (initialValue = false, booleanSetter) => {
  return () => {
    booleanSetter(!initialValue);
  };
};

// src/orgicore/utils/iots/index.ts
var optional = (c) => t2.union([c, t2.undefined]);
var optionalBoolean = optional(t2.boolean);
var optionalString = optional(t2.string);
var optionalNumber = optional(t2.number);
var unknownRecord = t2.record(t2.string, t2.unknown);
var unknownRecordArray = t2.array(unknownRecord);
var stringRecord = t2.record(t2.string, t2.string);
var booleanRecord = t2.record(t2.string, t2.boolean);
var getLiteralUnionValues = (unionCodec) => {
  const isAllLiterals = unionCodec.types.every(
    (c) => c._tag === "LiteralType"
  );
  if (!isAllLiterals) {
    throw new Error(
      "getLiteralUnionValues expects a union of t.literal(...) types"
    );
  }
  return unionCodec.types.map((c) => c.value);
};
var parseJson = (json, codec) => {
  try {
    const parsed = JSON.parse(json);
    const result = codec.decode(parsed);
    if (isLeft(result)) {
      const errors = PathReporter.report(result);
      return left(`Failed to decode: ${errors.join(", ")}`);
    }
    return right(result.right);
  } catch (error2) {
    return left(`Failed to parse JSON: ${getErrorMessage(error2)}`);
  }
};

// src/create/requirementsSpec.ts
var CURRENT_REQUIREMENTS_SPEC_VERSION = 1;
var requirementsSpecInput = t3.intersection([
  t3.type({
    version: t3.number,
    verticalId: t3.string,
    answers: unknownRecord,
    derived: t3.type({
      entities: t3.array(t3.string),
      features: t3.array(t3.string)
    })
  }),
  t3.partial({
    notes: t3.string
  })
]);
var riskStrategy = t3.keyof({
  clone: null,
  orgGen: null,
  orgGenHighRisk: null
});
var riskFactorResult = t3.type({
  id: t3.string,
  label: t3.string,
  weight: t3.number,
  triggered: t3.boolean
});
var riskAssessment = t3.type({
  score: t3.number,
  // Every evaluated factor, triggered or not — self-describing telemetry,
  // no separate lookup against the factor registry needed.
  factors: t3.array(riskFactorResult),
  strategy: riskStrategy
});
var requirementsSpec = t3.intersection([
  requirementsSpecInput,
  t3.type({ risk: riskAssessment })
]);

// src/create/riskScore.ts
var RISK_THRESHOLDS = { cloneMax: 2, orgGenMax: 6 };
var triageStrategy = (score2) => {
  if (score2 <= RISK_THRESHOLDS.cloneMax) return "clone";
  if (score2 <= RISK_THRESHOLDS.orgGenMax) return "orgGen";
  return "orgGenHighRisk";
};
var CUSTOM_ENTITY_THRESHOLD = 5;
var FEATURE_THRESHOLD = 6;
var NON_STANDARD_INTERACTION_KEYWORDS = [
  "drag and drop",
  "real-time",
  "realtime",
  "multiplayer",
  "voting",
  "matchmaking"
];
var GAME_MECHANIC_KEYWORDS = [
  "game",
  "rounds",
  "score",
  "cards",
  "dice",
  "board",
  "rummy",
  "turn-based"
];
var DATA_VOLUME_KEYWORDS = [
  "large data",
  "high volume",
  "thousands of",
  "bulk import"
];
var scannableStrings = (input) => [
  ...input.notes ? [input.notes] : [],
  ...input.derived.features,
  ...Object.values(input.answers).filter(
    (v) => typeof v === "string"
  )
];
var containsAnyKeyword = (input, keywords) => {
  const haystack = scannableStrings(input).join(" ").toLowerCase();
  return keywords.some((k) => haystack.includes(k.toLowerCase()));
};
var RISK_FACTORS = [
  {
    id: "customEntities",
    label: "Custom entities beyond the template baseline",
    weight: 2,
    detect: (input) => input.derived.entities.length > CUSTOM_ENTITY_THRESHOLD
  },
  {
    id: "richFeatureSet",
    label: "Large number of distinct requested features",
    weight: 1,
    detect: (input) => input.derived.features.length > FEATURE_THRESHOLD
  },
  {
    id: "nonStandardInteractions",
    label: "Non-standard interaction patterns requested",
    weight: 3,
    detect: (input) => containsAnyKeyword(input, NON_STANDARD_INTERACTION_KEYWORDS)
  },
  {
    id: "gameMechanics",
    label: "Game-style mechanics requested",
    weight: 4,
    detect: (input) => containsAnyKeyword(input, GAME_MECHANIC_KEYWORDS)
  },
  {
    id: "dataVolumeExpectations",
    label: "High expected data volume",
    weight: 2,
    detect: (input) => containsAnyKeyword(input, DATA_VOLUME_KEYWORDS)
  }
];
var computeRiskScore = (input) => {
  const factors = RISK_FACTORS.map((f) => ({
    id: f.id,
    label: f.label,
    weight: f.weight,
    triggered: f.detect(input)
  }));
  const score2 = factors.filter((f) => f.triggered).reduce((sum, f) => sum + f.weight, 0);
  return { score: score2, factors, strategy: triageStrategy(score2) };
};
var buildRequirementsSpec = (input) => ({ ...input, risk: computeRiskScore(input) });

// src/create/templateParams.ts
import * as t7 from "io-ts";

// src/orgicore/types/nodeOptions.ts
import * as t6 from "io-ts";

// src/orgicore/utils/codec.ts
import * as t4 from "io-ts";

// ../../node_modules/fp-ts/es6/function.js
var __spreadArray = function(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
function identity(a) {
  return a;
}
function pipe(a, ab, bc, cd, de, ef, fg, gh, hi) {
  switch (arguments.length) {
    case 1:
      return a;
    case 2:
      return ab(a);
    case 3:
      return bc(ab(a));
    case 4:
      return cd(bc(ab(a)));
    case 5:
      return de(cd(bc(ab(a))));
    case 6:
      return ef(de(cd(bc(ab(a)))));
    case 7:
      return fg(ef(de(cd(bc(ab(a))))));
    case 8:
      return gh(fg(ef(de(cd(bc(ab(a)))))));
    case 9:
      return hi(gh(fg(ef(de(cd(bc(ab(a))))))));
    default: {
      var ret = arguments[0];
      for (var i = 1; i < arguments.length; i++) {
        ret = arguments[i](ret);
      }
      return ret;
    }
  }
}
var dual = function(arity, body) {
  var isDataFirst = typeof arity === "number" ? function(args) {
    return args.length >= arity;
  } : arity;
  return function() {
    var args = Array.from(arguments);
    if (isDataFirst(arguments)) {
      return body.apply(this, args);
    }
    return function(self) {
      return body.apply(void 0, __spreadArray([self], args, false));
    };
  };
};

// ../../node_modules/fp-ts/es6/internal.js
var isLeft2 = function(ma) {
  return ma._tag === "Left";
};
var isRight2 = function(ma) {
  return ma._tag === "Right";
};
var left2 = function(e) {
  return { _tag: "Left", left: e };
};
var right2 = function(a) {
  return { _tag: "Right", right: a };
};

// ../../node_modules/fp-ts/es6/Functor.js
function as(F) {
  return function(self, b2) {
    return F.map(self, function() {
      return b2;
    });
  };
}
function asUnit(F) {
  var asM = as(F);
  return function(self) {
    return asM(self, void 0);
  };
}

// ../../node_modules/fp-ts/es6/Chain.js
function tap(M) {
  return function(first, f) {
    return M.chain(first, function(a) {
      return M.map(f(a), function() {
        return a;
      });
    });
  };
}

// ../../node_modules/fp-ts/es6/Either.js
var left3 = left2;
var right3 = right2;
var flatMap = /* @__PURE__ */ dual(2, function(ma, f) {
  return isLeft3(ma) ? ma : f(ma.right);
});
var _map = function(fa, f) {
  return pipe(fa, map(f));
};
var _ap = function(fab, fa) {
  return pipe(fab, ap(fa));
};
var URI = "Either";
var map = function(f) {
  return function(fa) {
    return isLeft3(fa) ? fa : right3(f(fa.right));
  };
};
var Functor = {
  URI,
  map: _map
};
var as2 = dual(2, as(Functor));
var asUnit2 = asUnit(Functor);
var apW = function(fa) {
  return function(fab) {
    return isLeft3(fab) ? fab : isLeft3(fa) ? fa : right3(fab.right(fa.right));
  };
};
var ap = apW;
var Chain = {
  URI,
  map: _map,
  ap: _ap,
  chain: flatMap
};
var FromEither = {
  URI,
  fromEither: identity
};
var isLeft3 = isLeft2;
var isRight3 = isRight2;
var matchW = function(onLeft, onRight) {
  return function(ma) {
    return isLeft3(ma) ? onLeft(ma.left) : onRight(ma.right);
  };
};
var match = matchW;
var fold = match;
var tap2 = /* @__PURE__ */ dual(2, tap(Chain));
var _FromEither = {
  fromEither: FromEither.fromEither
};

// src/orgicore/utils/codec.ts
var many = (codec) => t4.union([codec, t4.array(codec)]);
var laxEnum = (strict) => {
  const isString4 = (u) => typeof u === "string";
  return new t4.Type(
    `lax(${strict.name})`,
    isString4,
    (u, c) => isString4(u) ? t4.success(u) : t4.failure(u, c),
    t4.identity
  );
};
function withBetterError(codec, errorMapper) {
  return new t4.Type(
    codec.name,
    codec.is,
    (u, c) => {
      return pipe(
        codec.validate(u, c),
        fold(
          (errors) => {
            try {
              const customMessage = errorMapper(errors);
              const customErrors = errors.map((error2) => ({
                ...error2,
                message: customMessage
              }));
              return t4.failures(customErrors);
            } catch (e) {
              return t4.failures(errors);
            }
          },
          (value) => t4.success(value)
        )
      );
    },
    codec.encode
  );
}
function withContextErrors(codec, contextMessages) {
  return withBetterError(codec, (errors) => {
    for (const error2 of errors) {
      const contextPath = error2.context.map((ctx) => ctx.key).filter(Boolean).join(".");
      for (const [path, message] of Object.entries(contextMessages)) {
        if (contextPath === path) {
          return message;
        }
        if (contextPath.includes(path)) {
          return message;
        }
      }
    }
    return "Invalid format. Please check the expected structure.";
  });
}
function getErrorPath(errors) {
  if (errors.length === 0) return "";
  const context = errors[0].context;
  return context.map((ctx) => ctx.key).filter(Boolean).join(".");
}
function summarizeErrors(errors) {
  if (errors.length === 0) return "No errors";
  if (errors.length === 1) {
    const error2 = errors[0];
    const path = getErrorPath(errors);
    const pathStr5 = path ? ` at ${path}` : "";
    return `Invalid value${pathStr5}: ${error2.message || error2.value}`;
  }
  const errorsByPath = {};
  errors.forEach((error2) => {
    const path = getErrorPath([error2]);
    if (!errorsByPath[path]) errorsByPath[path] = [];
    errorsByPath[path].push(error2);
  });
  const pathCount = Object.keys(errorsByPath).length;
  const totalErrors = errors.length;
  return `${totalErrors} validation error${totalErrors > 1 ? "s" : ""} in ${pathCount} location${pathCount > 1 ? "s" : ""}`;
}
function validateOrThrow(codec, value, customErrorMessage) {
  return pipe(
    codec.decode(value),
    fold(
      (errors) => {
        const errorPath = getErrorPath(errors);
        const errorSummary = summarizeErrors(errors);
        const message = customErrorMessage ? `${customErrorMessage}: ${errorSummary}` : `Validation failed: ${errorSummary}`;
        const error2 = new Error(message);
        error2.validationErrors = errors;
        error2.errorPath = errorPath;
        throw error2;
      },
      (value2) => value2
    )
  );
}
var codecPropNames = (codec) => {
  if (codec instanceof t4.IntersectionType) {
    const names2 = codec.types.flatMap(codecPropNames);
    return [...new Set(names2)];
  }
  if (codec instanceof t4.InterfaceType || codec instanceof t4.PartialType) {
    return Object.keys(codec.props);
  }
  return [];
};

// src/orgicore/types/nodeOptions.manual.ts
import * as t5 from "io-ts";
var staticPluginParam = t5.type({
  type: t5.literal("static"),
  name: t5.string,
  value: t5.unknown
});
var dynamicPluginParam = t5.type({
  type: t5.literal("dynamic"),
  name: t5.string,
  formula: t5.string
});
var pluginParam = t5.recursion(
  "pluginParam",
  () => t5.union([
    staticPluginParam,
    dynamicPluginParam,
    t5.type({
      type: t5.literal("complex"),
      params: t5.array(pluginParam)
    })
  ])
);
var isDataQuery = (options) => {
  return (options == null ? void 0 : options.dataType) !== void 0;
};

// src/orgicore/types/nodeOptions.ts
var dataSourceType = laxEnum(t6.union([t6.literal("embedded"), t6.literal("storage"), t6.literal("realtime"), t6.literal("callable")]));
var featureSlotKind = laxEnum(t6.union([t6.literal("list"), t6.literal("value")]));
var authMode = laxEnum(t6.union([t6.literal("public"), t6.literal("private")]));
var mode = laxEnum(t6.union([t6.literal("columns"), t6.literal("grid"), t6.literal("spatial"), t6.literal("flow")]));
var axis = laxEnum(t6.union([t6.literal("x"), t6.literal("y")]));
var imageFormat = laxEnum(t6.union([t6.literal("jpeg"), t6.literal("png"), t6.literal("webp")]));
var inputSize = laxEnum(t6.union([t6.literal("small"), t6.literal("medium")]));
var subsectionVariant = laxEnum(t6.union([t6.literal("inline"), t6.literal("masterDetail")]));
var textVariant = laxEnum(t6.union([t6.literal("basic"), t6.literal("multiline"), t6.literal("stringArray")]));
var textTransform = laxEnum(t6.union([t6.literal("none"), t6.literal("uppercase"), t6.literal("lowercase")]));
var fileVariant = laxEnum(t6.union([t6.literal("image"), t6.literal("file"), t6.literal("video"), t6.literal("audio")]));
var numberVariant = laxEnum(t6.union([t6.literal("integer"), t6.literal("slider"), t6.literal("decimal"), t6.literal("money"), t6.literal("intArray")]));
var temporalVariant = laxEnum(t6.union([t6.literal("date"), t6.literal("datetime"), t6.literal("time")]));
var pickerVariant = laxEnum(t6.union([t6.literal("single"), t6.literal("multiple")]));
var pickerIdiom = laxEnum(t6.union([t6.literal("select"), t6.literal("radio"), t6.literal("combobox")]));
var dataScope = laxEnum(t6.union([t6.literal("global"), t6.literal("user"), t6.literal("variant"), t6.literal("group"), t6.literal("all"), t6.literal("inbox")]));
var themePreset = laxEnum(t6.union([t6.literal("light"), t6.literal("dark")]));
var callableButtonFunction = laxEnum(t6.union([t6.literal("setUserProfile"), t6.literal("triggerWorldcupSync"), t6.literal("socialFollow"), t6.literal("socialUnfollow"), t6.literal("socialLike"), t6.literal("socialUnlike"), t6.literal("kickFromHome")]));
var callableButtonVariant = laxEnum(t6.union([t6.literal("contained"), t6.literal("outlined"), t6.literal("text"), t6.literal("menu"), t6.literal("pill")]));
var audioGenerationModel = laxEnum(t6.union([t6.literal("musicgen"), t6.literal("minimax-music-2.5")]));
var transcribeModel = laxEnum(t6.literal("gpt-4o-transcribe"));
var imageGenerationModel = laxEnum(t6.union([t6.literal("flux-schnell"), t6.literal("nano-banana-2")]));
var textGenerationModel = laxEnum(t6.union([t6.literal("claude-haiku-5-5"), t6.literal("claude-haiku-4-5"), t6.literal("claude-sonnet-5")]));
var navigationStyle = laxEnum(t6.union([t6.literal("none"), t6.literal("menu"), t6.literal("tabs")]));
var transitionTrigger = laxEnum(t6.union([t6.literal("onWrite"), t6.literal("onTimer")]));
var spanMode = laxEnum(t6.union([t6.literal("repeat"), t6.literal("once"), t6.literal("grid")]));
var videoDisplayMode = laxEnum(t6.union([t6.literal("tiles"), t6.literal("bar"), t6.literal("audioOnly"), t6.literal("minimal")]));
var mediaMode = laxEnum(t6.union([t6.literal("video"), t6.literal("audio")]));
var objectTypeWriteAccess = laxEnum(t6.union([t6.literal("anyone"), t6.literal("admins")]));
var wellKnownType = laxEnum(t6.literal("Person"));
var fieldTypeBuiltin = laxEnum(t6.union([t6.literal("variable"), t6.literal("text"), t6.literal("number"), t6.literal("boolean"), t6.literal("temporal"), t6.literal("parentRef"), t6.literal("image"), t6.literal("imageUpload")]));
var recordMetaField = laxEnum(t6.union([t6.literal("id"), t6.literal("type"), t6.literal("_scope"), t6.literal("createdAt"), t6.literal("updatedAt"), t6.literal("createdBy"), t6.literal("updatedBy"), t6.literal("createdByUid"), t6.literal("updatedByUid"), t6.literal("isDeleted"), t6.literal("_parent")]));
var appNodeVariant = laxEnum(t6.union([t6.literal("tabGroup"), t6.literal("tab"), t6.literal("folder")]));
var flowStepVariant = laxEnum(t6.union([t6.literal("aiQuery"), t6.literal("aiChat"), t6.literal("fetchData"), t6.literal("imageGeneration"), t6.literal("audioGeneration"), t6.literal("transcribe"), t6.literal("publishData"), t6.literal("script")]));
var sortOptions = t6.partial({
  field: t6.string,
  direction: t6.union([t6.literal("asc"), t6.literal("desc")])
});
var span = t6.partial({
  startField: t6.string,
  endField: t6.string,
  axis: t6.string,
  mode: spanMode,
  slots: t6.string
});
var pushRule = t6.partial({
  label: t6.string,
  onPublishType: t6.string,
  enabled: t6.boolean,
  titleTemplate: t6.string,
  bodyTemplate: t6.string,
  clickPath: t6.string,
  onCreate: t6.boolean,
  onUpdate: t6.boolean,
  onDelete: t6.boolean,
  condition: t6.string
});
var transitionRule = t6.partial({
  label: t6.string,
  enabled: t6.boolean,
  trigger: transitionTrigger,
  watchType: t6.string,
  onDelete: t6.boolean,
  targetType: t6.string,
  targetId: t6.string,
  condition: t6.string,
  next: t6.string,
  rejectionReason: t6.string,
  notifyTurnUidField: t6.string,
  notifyTitleTemplate: t6.string,
  notifyBodyTemplate: t6.string,
  notifyClickPath: t6.string,
  notifyTo: t6.string,
  notifyTitle: t6.string,
  notifyBody: t6.string,
  notifyKey: t6.string,
  notifyClickVariant: t6.string,
  smsTo: t6.string,
  smsBody: t6.string,
  smsPrivateType: t6.string,
  smsReadUids: t6.string,
  dueAt: t6.string,
  nextPrivate: t6.string,
  privateType: t6.string,
  privateReadUids: t6.string,
  endOfLife: t6.boolean,
  archive: t6.boolean,
  archiveType: t6.string,
  archiveData: t6.string,
  archiveId: t6.string,
  perpetual: t6.boolean,
  seed: t6.string
});
var botActor = t6.partial({
  label: t6.string,
  enabled: t6.boolean,
  watchType: t6.string,
  moveType: t6.string,
  isBotTurn: t6.string,
  privateType: t6.string,
  privateKey: t6.string,
  privateReady: t6.string,
  state: t6.string,
  questions: t6.string,
  moveId: t6.string,
  move: t6.string,
  fallbackMove: t6.string,
  delayMs: t6.union([t6.number, t6.string]),
  maxDecisions: t6.number,
  maxDailyDecisions: t6.number,
  journal: t6.boolean
});
var callableSource = t6.partial({
  functionName: t6.string,
  args: t6.string
});
var formConfigOptions = t6.partial({
  dataType: t6.string,
  scope: dataScope,
  groupKey: t6.string
});
var layout = t6.partial({
  maxColumns: t6.number,
  columnBreaks: t6.array(t6.number),
  axis,
  mode,
  minColumnWidth: t6.number,
  columnWeights: t6.array(t6.number),
  cols: t6.union([t6.number, t6.string]),
  rows: t6.union([t6.number, t6.string]),
  rowHeight: t6.number
});
var place = t6.partial({
  x: t6.union([t6.number, t6.string]),
  y: t6.union([t6.number, t6.string]),
  w: t6.union([t6.number, t6.string]),
  h: t6.union([t6.number, t6.string])
});
var containerStyle = t6.partial({
  marginBottom: t6.number,
  justifySelf: t6.union([t6.literal("left"), t6.literal("center"), t6.literal("right")]),
  overlay: t6.union([t6.literal("topLeft"), t6.literal("topRight"), t6.literal("bottomLeft"), t6.literal("bottomRight")]),
  overlayInset: t6.number,
  overlayWidth: t6.number,
  maxWidth: t6.number
});
var dimension = t6.partial({
  width: t6.number,
  height: t6.number
});
var themeColors = t6.partial({
  bg: t6.string,
  surface: t6.string,
  accent: t6.string,
  text: t6.string,
  mutedText: t6.string,
  border: t6.string
});
var navigationConfig = t6.type({
  style: navigationStyle
});
var imageOptions = t6.intersection([
  dimension,
  t6.partial({
    format: imageFormat
  })
]);
var validationRule = t6.type({
  expression: t6.string,
  errorMessage: t6.string
});
var pickerOption = t6.intersection([
  t6.type({
    id: t6.string,
    label: t6.string
  }),
  t6.partial({
    group: t6.string
  })
]);
var stringOrPickerOption = t6.union([t6.string, pickerOption]);
var treeNodeOptions = t6.intersection([
  t6.type({
    allowedChildTypes: t6.array(t6.string)
  }),
  t6.partial({
    icon: t6.string,
    color: t6.string,
    category: t6.string,
    label: t6.string,
    labelField: t6.string,
    detail: t6.string,
    badge: t6.string,
    badgeColor: t6.string,
    lockedFields: t6.union([t6.string, t6.array(t6.string)]),
    canEdit: t6.union([t6.boolean, t6.string])
  })
]);
var treeNodeValue = t6.intersection([
  t6.type({
    type: t6.string
  }),
  t6.partial({
    id: t6.string
  })
]);
var optionalNumberVariant = t6.union([numberVariant, t6.undefined]);
var point = t6.type({
  x: t6.number,
  y: t6.number
});
var editState = t6.type({
  rotation: t6.number,
  zoom: t6.number,
  pan: point
});
var fileUploadValue = t6.intersection([
  t6.type({
    name: t6.string
  }),
  t6.partial({
    url: t6.string,
    ext: t6.string,
    aiPrompt: t6.string,
    previewUrl: t6.string,
    editState,
    takenAt: t6.string
  })
]);
var fileUploadItemValue = t6.intersection([
  t6.type({
    name: t6.string
  }),
  t6.partial({
    url: t6.string,
    ext: t6.string,
    previewUrl: t6.string,
    takenAt: t6.string
  })
]);
var fileUploadValueOrArray = t6.union([fileUploadValue, t6.array(fileUploadItemValue)]);
var actionConfirm = t6.intersection([
  t6.type({
    title: t6.string
  }),
  t6.partial({
    body: t6.string,
    ok: t6.string,
    cancel: t6.string,
    destructive: t6.boolean,
    info: t6.boolean
  })
]);
var contentAction = t6.partial({
  params: t6.array(t6.string),
  script: t6.string,
  confirm: actionConfirm
});
var parentRelation = t6.partial({
  type: t6.string,
  idField: t6.string
});
var objectTypeField = t6.partial({
  name: t6.string,
  type: t6.string,
  isList: t6.boolean,
  multiline: t6.boolean,
  variant: t6.string,
  compute: t6.string,
  isTransient: t6.boolean,
  description: t6.string,
  fileNameFormula: t6.string,
  aiEnabled: t6.boolean,
  aiSystemPromptFormula: t6.string,
  searchFormula: t6.string
});
var objectType = t6.intersection([
  t6.type({
    name: t6.string,
    fields: t6.array(objectTypeField)
  }),
  t6.partial({
    icon: t6.string,
    parent: parentRelation,
    writeAccess: objectTypeWriteAccess,
    wellKnown: wellKnownType
  })
]);
var libraryRef = t6.intersection([
  t6.type({
    id: t6.string,
    version: t6.number,
    hash: t6.string
  }),
  t6.partial({
    omitted: t6.array(t6.string)
  })
]);
var featureSlotPin = t6.intersection([
  t6.type({
    name: t6.string,
    node: t6.string,
    kind: featureSlotKind
  }),
  t6.partial({
    default: t6.string
  })
]);
var featureSeamPin = t6.type({
  name: t6.string,
  slot: t6.string,
  fn: t6.string,
  hostType: t6.string
});
var featureRef = t6.intersection([
  t6.type({
    id: t6.string,
    version: t6.number
  }),
  t6.partial({
    hash: t6.string,
    slots: t6.array(featureSlotPin),
    seams: t6.array(featureSeamPin)
  })
]);
var dataOptions = t6.partial({
  dataType: t6.string,
  sort: many(sortOptions),
  whereClause: t6.string,
  source: dataSourceType,
  scope: dataScope,
  groupKey: t6.string,
  callable: callableSource,
  span
});
var nodeOptionsCodec = t6.intersection([
  t6.type({
    name: t6.string,
    type: t6.string
  }),
  t6.partial({
    actionLabel: t6.string,
    actionMenu: t6.union([t6.boolean, t6.string]),
    actions: t6.record(t6.string, contentAction),
    aiEnabled: t6.boolean,
    aiSystemPromptFormula: t6.string,
    appLogo: t6.string,
    args: t6.string,
    audioSourceUrl: t6.string,
    authDescription: t6.string,
    authMode,
    authTitle: t6.string,
    autoAdvance: t6.boolean,
    botActors: many(botActor),
    canAdd: t6.union([t6.boolean, t6.string]),
    canEdit: t6.union([t6.boolean, t6.string]),
    collapsedByDefault: t6.union([t6.boolean, t6.string]),
    collapsible: t6.union([t6.boolean, t6.string]),
    color: t6.string,
    compute: t6.unknown,
    containerStyle,
    contentMaxWidth: t6.number,
    customCss: t6.string,
    data: dataOptions,
    deepHelp: t6.string,
    displayMode: videoDisplayMode,
    displayName: t6.string,
    durationSec: t6.union([t6.number, t6.string]),
    examples: t6.array(t6.string),
    feature: featureRef,
    fetchUrl: t6.string,
    focusIdentity: t6.string,
    format: t6.union([temporalVariant, t6.string]),
    formConfig: formConfigOptions,
    functionName: callableButtonFunction,
    headerLogo: t6.string,
    help: t6.string,
    hidden: t6.union([t6.boolean, t6.string]),
    hideHeader: t6.union([t6.boolean, t6.string]),
    hideNav: t6.union([t6.boolean, t6.string]),
    hideTopBar: t6.union([t6.boolean, t6.string]),
    htmlTemplate: t6.string,
    icon: t6.string,
    identity: t6.string,
    idiom: t6.union([pickerIdiom, t6.string]),
    idleDescription: t6.string,
    idleTitle: t6.string,
    imageOutputOptions: imageOptions,
    imageSource: t6.string,
    inputPlaceholder: t6.string,
    isDerived: t6.boolean,
    isOwnScope: t6.boolean,
    isRecord: t6.boolean,
    isRepeating: t6.boolean,
    isTransient: t6.boolean,
    itemLabel: t6.string,
    iterationLabel: t6.string,
    label: t6.string,
    language: t6.string,
    layout,
    library: libraryRef,
    lyrics: t6.string,
    max: t6.union([t6.number, t6.string]),
    maxLength: t6.union([t6.number, t6.string]),
    maxSizeMb: t6.union([t6.number, t6.string]),
    maxTiles: t6.union([t6.number, t6.string]),
    maxUserTurns: t6.number,
    mediaMode,
    min: t6.union([t6.number, t6.string]),
    mockAudioUrl: t6.string,
    mockEnabled: t6.boolean,
    mockImageUrl: t6.string,
    mockReplies: t6.string,
    mockResultCount: t6.number,
    mockResults: t6.string,
    mockTranscript: t6.string,
    model: t6.union([textGenerationModel, t6.string]),
    multiple: t6.boolean,
    navigation: navigationConfig,
    noCreditsDescription: t6.string,
    noCreditsTitle: t6.string,
    nodeTypes: t6.record(t6.string, treeNodeOptions),
    notifications: many(pushRule),
    nounLabel: t6.string,
    onSuccessNavigate: t6.string,
    orderField: t6.string,
    orgLogin: t6.boolean,
    pageSize: t6.union([t6.number, t6.string]),
    parameters: t6.string,
    pickerValues: t6.union([t6.string, t6.array(stringOrPickerOption)]),
    place,
    placeholder: t6.string,
    placeholderIcon: t6.string,
    pluginName: t6.string,
    pluginParams: t6.array(pluginParam),
    prompt: t6.string,
    promptTemplate: t6.string,
    publishDataType: t6.string,
    publishScope: t6.string,
    publishVariant: t6.string,
    purpose: t6.string,
    recordKey: t6.string,
    recordValue: t6.string,
    refreshCallables: t6.array(t6.string),
    reorderable: t6.union([t6.boolean, t6.string]),
    required: t6.union([t6.boolean, t6.string]),
    resultObjectType: t6.string,
    returns: t6.string,
    returnTypeHint: t6.string,
    roomKey: t6.string,
    rootType: t6.string,
    runningDescription: t6.string,
    runningTitle: t6.string,
    script: t6.string,
    showCamera: t6.boolean,
    showCommit: t6.boolean,
    showEdit: t6.boolean,
    showFilePicker: t6.boolean,
    showHeader: t6.union([t6.boolean, t6.string]),
    showSearch: t6.boolean,
    size: inputSize,
    sourceExpression: t6.string,
    speakReplies: t6.boolean,
    speechLang: t6.string,
    steeringChips: t6.string,
    step: t6.union([t6.number, t6.string]),
    submitObjectType: t6.string,
    successMessage: t6.string,
    summary: t6.string,
    textTransform,
    theme: themePreset,
    themeColors,
    transitions: many(transitionRule),
    typeDescription: t6.string,
    userAvatarUid: t6.string,
    validation: many(validationRule),
    value: t6.unknown,
    variant: t6.string,
    voiceInput: t6.boolean
  })
]);

// src/create/templateParams.ts
var templateManifest = t7.type({
  verticalId: t7.string,
  title: t7.string,
  /** objectType names a clone may rename. */
  entities: t7.array(t7.string),
  /** objectType names whose sample records a clone may empty, for AI seed
   * content (#1447) to fill. */
  seedSlots: t7.array(t7.string),
  /** appConfig node names whose `label` a clone may set. Unique in the
   * template's appConfig. */
  labels: t7.array(t7.string)
});
var templateThemePreset = t7.keyof({ light: null, dark: null });
var templateOverrides = t7.partial({
  /** Template node name → new label. */
  labels: t7.record(t7.string, t7.string),
  theme: t7.partial({
    preset: templateThemePreset,
    colors: themeColors
  }),
  /** Template objectType name → new name. */
  entityRenames: t7.record(t7.string, t7.string),
  /** Template objectType names whose sample records are removed. */
  seedSlots: t7.array(t7.string)
});
var templateParams = t7.intersection([
  t7.type({
    appName: t7.string
  }),
  templateOverrides
]);

// src/create/renameRefs.ts
import * as R2 from "remeda";

// src/validation/generated/enumRegistry.generated.ts
var ENUM_VALUES = {
  "dataSourceType": [
    "embedded",
    "storage",
    "realtime",
    "callable"
  ],
  "featureSlotKind": [
    "list",
    "value"
  ],
  "authMode": [
    "public",
    "private"
  ],
  "mode": [
    "columns",
    "grid",
    "spatial",
    "flow"
  ],
  "axis": [
    "x",
    "y"
  ],
  "imageFormat": [
    "jpeg",
    "png",
    "webp"
  ],
  "inputSize": [
    "small",
    "medium"
  ],
  "subsectionVariant": [
    "inline",
    "masterDetail"
  ],
  "textVariant": [
    "basic",
    "multiline",
    "stringArray"
  ],
  "textTransform": [
    "none",
    "uppercase",
    "lowercase"
  ],
  "fileVariant": [
    "image",
    "file",
    "video",
    "audio"
  ],
  "numberVariant": [
    "integer",
    "slider",
    "decimal",
    "money",
    "intArray"
  ],
  "temporalVariant": [
    "date",
    "datetime",
    "time"
  ],
  "pickerVariant": [
    "single",
    "multiple"
  ],
  "pickerIdiom": [
    "select",
    "radio",
    "combobox"
  ],
  "dataScope": [
    "global",
    "user",
    "variant",
    "group",
    "all",
    "inbox"
  ],
  "themePreset": [
    "light",
    "dark"
  ],
  "callableButtonFunction": [
    "setUserProfile",
    "triggerWorldcupSync",
    "socialFollow",
    "socialUnfollow",
    "socialLike",
    "socialUnlike",
    "kickFromHome"
  ],
  "callableButtonVariant": [
    "contained",
    "outlined",
    "text",
    "menu",
    "pill"
  ],
  "audioGenerationModel": [
    "musicgen",
    "minimax-music-2.5"
  ],
  "transcribeModel": [
    "gpt-4o-transcribe"
  ],
  "imageGenerationModel": [
    "flux-schnell",
    "nano-banana-2"
  ],
  "textGenerationModel": [
    "claude-haiku-5-5",
    "claude-haiku-4-5",
    "claude-sonnet-5"
  ],
  "navigationStyle": [
    "none",
    "menu",
    "tabs"
  ],
  "transitionTrigger": [
    "onWrite",
    "onTimer"
  ],
  "spanMode": [
    "repeat",
    "once",
    "grid"
  ],
  "videoDisplayMode": [
    "tiles",
    "bar",
    "audioOnly",
    "minimal"
  ],
  "mediaMode": [
    "video",
    "audio"
  ],
  "objectTypeWriteAccess": [
    "anyone",
    "admins"
  ],
  "wellKnownType": [
    "Person"
  ],
  "fieldTypeBuiltin": [
    "variable",
    "text",
    "number",
    "boolean",
    "temporal",
    "parentRef",
    "image",
    "imageUpload"
  ],
  "recordMetaField": [
    "id",
    "type",
    "_scope",
    "createdAt",
    "updatedAt",
    "createdBy",
    "updatedBy",
    "createdByUid",
    "updatedByUid",
    "isDeleted",
    "_parent"
  ]
};
var ENUM_FIELDS = {
  "app": {
    "authMode": "authMode",
    "theme": "themePreset",
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "appNode": {
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "screen": {
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "subsection": {
    "variant": "subsectionVariant",
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "form": {
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "flow": {
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "callableButton": {
    "functionName": "callableButtonFunction",
    "variant": "callableButtonVariant",
    "size": "inputSize"
  },
  "tree": {
    "data.source": "dataSourceType",
    "data.scope": "dataScope"
  },
  "textInput": {
    "textTransform": "textTransform",
    "size": "inputSize"
  },
  "numberInput": {
    "size": "inputSize"
  },
  "booleanInput": {
    "size": "inputSize"
  },
  "temporalInput": {
    "size": "inputSize"
  },
  "selectInput": {
    "size": "inputSize"
  },
  "fileInput": {
    "size": "inputSize"
  },
  "pluginLeaf": {
    "size": "inputSize"
  },
  "mediaRoom": {
    "mediaMode": "mediaMode",
    "displayMode": "videoDisplayMode"
  }
};
var ENUM_LABELS = {
  "authMode": {
    "public": "Public (no login required)",
    "private": "Private (login required)"
  },
  "subsectionVariant": {
    "inline": "Inline",
    "masterDetail": "Master / detail"
  },
  "textTransform": {
    "none": "None",
    "uppercase": "UPPERCASE",
    "lowercase": "lowercase"
  },
  "pickerIdiom": {
    "select": "Dropdown",
    "radio": "Radio Buttons",
    "combobox": "Combobox (type-to-filter)"
  },
  "callableButtonFunction": {
    "triggerWorldcupSync": "Sync World Cup results",
    "socialFollow": "Follow a person",
    "socialUnfollow": "Unfollow a person",
    "socialLike": "Like an app",
    "socialUnlike": "Unlike an app",
    "kickFromHome": "Kick an app off the home page"
  },
  "callableButtonVariant": {
    "contained": "Filled (default)",
    "outlined": "Outlined",
    "text": "Text only",
    "menu": "Menu (\u22EE)",
    "pill": "Pill (over an image)"
  },
  "navigationStyle": {
    "none": "No nav (default)",
    "menu": "Menu (tap the logo)",
    "tabs": "Scrollable tab strip"
  },
  "spanMode": {
    "repeat": "Repeat across the run",
    "once": "Once, at the start",
    "grid": "One bar per row segment"
  },
  "videoDisplayMode": {
    "tiles": "Tiles (grid of participants)",
    "bar": "Bar (compact strip)",
    "audioOnly": "Audio only (no video)",
    "minimal": "Minimal (join button only; the header carries the call)"
  },
  "mediaMode": {
    "video": "Video and audio",
    "audio": "Audio only"
  },
  "objectTypeWriteAccess": {
    "anyone": "Anyone using the app (default)",
    "admins": "Only the app's creator and admins"
  },
  "wellKnownType": {
    "Person": "Person \u2014 someone the org keeps a record of"
  },
  "fieldTypeBuiltin": {
    "variable": "Variable (hidden value)",
    "text": "Text",
    "number": "Number",
    "boolean": "Boolean",
    "temporal": "Date / time",
    "parentRef": "Parent Reference",
    "image": "Image (paste url)",
    "imageUpload": "Image (upload new)"
  }
};

// src/orgicore/types/fieldType.ts
var FIELD_TYPE_BUILTINS = new Set(
  ENUM_VALUES.fieldTypeBuiltin
);
var isFieldTypeBuiltin = (name) => FIELD_TYPE_BUILTINS.has(name);
var FIELD_TYPE_BUILTIN_VALUES = ENUM_VALUES.fieldTypeBuiltin.filter(isFieldTypeBuiltin);
var fieldTypeBuiltinLabel = (type14) => {
  var _a2, _b;
  return (_b = (_a2 = ENUM_LABELS.fieldTypeBuiltin) == null ? void 0 : _a2[type14]) != null ? _b : type14;
};
var IDENT = /^[A-Za-z_$][\w$]*$/;
var COLLECTION = /^(list|map)\s*<\s*(.*?)\s*>$/;
var parseScalar = (name) => {
  if (!IDENT.test(name)) return void 0;
  return FIELD_TYPE_BUILTINS.has(name) ? { kind: "builtin", name } : { kind: "ref", name };
};
var parseFieldType = (type14, isList) => {
  const raw = type14.trim();
  const m = COLLECTION.exec(raw);
  if (m) {
    const elem = parseScalar(m[2]);
    if (!elem) return { kind: "invalid", raw: type14 };
    return m[1] === "list" ? { kind: "list", elem } : { kind: "map", elem };
  }
  const scalar = parseScalar(raw);
  if (!scalar) return { kind: "invalid", raw: type14 };
  return isList ? { kind: "list", elem: scalar } : scalar;
};
var isCollectionType = (t30) => t30.kind === "list" || t30.kind === "map";
var scalarOf = (t30) => {
  if (t30.kind === "invalid") return void 0;
  return isCollectionType(t30) ? t30.elem : t30;
};
var formatFieldType = (t30) => {
  switch (t30.kind) {
    case "builtin":
    case "ref":
      return t30.name;
    case "list":
      return `list<${t30.elem.name}>`;
    case "map":
      return `map<${t30.elem.name}>`;
    case "invalid":
      return t30.raw;
  }
};

// src/orgicore/utils/evaluation/formula/parameters.ts
var parseParameters = (parameters) => {
  if (typeof parameters !== "string") return [];
  const out = [];
  for (const raw of parameters.split(",")) {
    const entry = raw.trim();
    if (!entry) continue;
    const colon = entry.indexOf(":");
    if (colon === -1) {
      out.push({ name: entry });
      continue;
    }
    const name = entry.slice(0, colon).trim();
    const type14 = entry.slice(colon + 1).trim();
    if (!name) continue;
    out.push(type14 ? { name, type: type14 } : { name });
  }
  return out;
};
var parameterNames = (parameters) => parseParameters(parameters).map((p) => p.name);

// src/validation/htmlAttrs.ts
var INTERP_PLACEHOLDER = "__interp";
var DATA_ATTR_RE = /\bdata-(script|args)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
var substituteInterpolations = (src) => {
  let out = "";
  const removed = [];
  let i = 0;
  while (i < src.length) {
    if (src[i] === "$" && src[i + 1] === "{") {
      let depth = 1;
      let j = i + 2;
      while (j < src.length && depth > 0) {
        if (src[j] === "{") depth++;
        else if (src[j] === "}") depth--;
        j++;
      }
      if (depth === 0) {
        removed.push(src.slice(i + 2, j - 1));
        out += INTERP_PLACEHOLDER;
        i = j;
        continue;
      }
      out += src.slice(i);
      break;
    }
    out += src[i];
    i++;
  }
  return { text: out, removed };
};
var quotedStringEnd = (src, open) => {
  const quote = src[open];
  let j = open + 1;
  while (j < src.length && src[j] !== quote && src[j] !== "\n") {
    j += src[j] === "\\" ? 2 : 1;
  }
  return Math.min(j, src.length);
};
var literalSegments = (src) => {
  const segments = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === "\\") {
      i += 2;
      continue;
    }
    if (ch === "'" || ch === '"') {
      const end = quotedStringEnd(src, i);
      segments.push({ text: src.slice(i + 1, end), quoted: true });
      i = end + 1;
      continue;
    }
    if (ch !== "`") {
      i++;
      continue;
    }
    const start = i + 1;
    let j = start;
    let depth = 0;
    while (j < src.length) {
      const c = src[j];
      if (c === "\\") {
        j += 2;
        continue;
      }
      if (c === "$" && src[j + 1] === "{") {
        depth++;
        j += 2;
        continue;
      }
      if (c === "}" && depth > 0) {
        depth--;
        j++;
        continue;
      }
      if (c === "`" && depth === 0) break;
      j++;
    }
    segments.push({ text: src.slice(start, j), quoted: false });
    i = j + 1;
  }
  return segments;
};
var interpolatedHtml = (removed) => {
  const out = [];
  for (const body of removed) {
    for (const segment2 of literalSegments(body)) {
      const nested = substituteInterpolations(segment2.text);
      out.push(nested.text);
      if (!segment2.quoted) out.push(...interpolatedHtml(nested.removed));
    }
  }
  return out;
};
var TAG_NAME_RE = /^[a-zA-Z][a-zA-Z0-9-]*/;
var RAW_TEXT_ELEMENTS = /* @__PURE__ */ new Set(["style", "script", "textarea", "title"]);
var scanHtmlTags = (html) => {
  const tags = [];
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf("<", i);
    if (lt === -1) break;
    if (html.startsWith("<!--", lt)) {
      const end = html.indexOf("-->", lt + 4);
      i = end === -1 ? html.length : end + 3;
      continue;
    }
    const closing = html[lt + 1] === "/";
    const nameStart = closing ? lt + 2 : lt + 1;
    const nameMatch = TAG_NAME_RE.exec(html.slice(nameStart));
    if (!nameMatch) {
      i = lt + 1;
      continue;
    }
    const name = nameMatch[0].toLowerCase();
    let j = nameStart + nameMatch[0].length;
    let quote = null;
    let lastMeaningful = "";
    while (j < html.length) {
      const ch = html[j];
      if (quote !== null) {
        if (ch === quote) quote = null;
      } else if ((ch === '"' || ch === "'") && lastMeaningful === "=") {
        quote = ch;
      } else if (ch === ">") {
        break;
      }
      if (!/\s/.test(ch)) lastMeaningful = ch;
      j++;
    }
    if (j >= html.length) break;
    const inner = html.slice(nameStart + nameMatch[0].length, j);
    const selfClosing = /\/\s*$/.test(inner);
    tags.push({
      name,
      attrs: closing ? "" : inner.replace(/\/\s*$/, ""),
      closing,
      selfClosing,
      index: lt
    });
    i = j + 1;
    if (!closing && !selfClosing && RAW_TEXT_ELEMENTS.has(name)) {
      const closeRe = new RegExp(`</${name}\\b`, "i");
      const rest = html.slice(i);
      const closeMatch = closeRe.exec(rest);
      i = closeMatch ? i + closeMatch.index : html.length;
    }
  }
  return tags;
};
var readAttr = (attrs, name) => {
  var _a2, _b;
  const re = new RegExp(
    `(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`,
    "i"
  );
  const m = re.exec(attrs);
  if (!m) return void 0;
  return (_b = (_a2 = m[1]) != null ? _a2 : m[2]) != null ? _b : "";
};
var hasAttr = (attrs, name) => {
  const re = new RegExp(`(?:^|\\s)${name}(?:\\s*=|\\s|$)`, "i");
  return re.test(attrs);
};
var listAttrNames = (attrs) => {
  const withoutValues = attrs.replace(/"[^"]*"|'[^']*'/g, "");
  const names2 = [];
  for (const m of withoutValues.matchAll(
    /(?:^|\s)([a-zA-Z_][a-zA-Z0-9_.:-]*)(?=\s*=|\s|$)/g
  )) {
    names2.push(m[1]);
  }
  return names2;
};

// src/orgicore/types/constants.ts
var APP = "app";
var APP_NODE = "appNode";
var TREE = "tree";
var SCREEN = "screen";
var FORM = "form";
var FLOW = "flow";
var FLOW_STEP = "flowStep";
var REPEAT = "repeat";
var CALLABLE_BUTTON = "callableButton";
var BUTTON = "button";
var CONTENT = "content";
var TEXT_INPUT = "textInput";
var NUMBER_INPUT = "numberInput";
var VARIABLE = "variable";
var FUNCTION = "function";
var CONST = "const";
var BOOLEAN_INPUT = "booleanInput";
var TEMPORAL_INPUT = "temporalInput";
var SELECT_INPUT = "selectInput";
var FILE_INPUT = "fileInput";
var PLUGIN_LEAF = "pluginLeaf";
var MEDIA_ROOM = "mediaRoom";
var LEGACY_VIDEO_ROOM = "videoRoom";
var SUBSECTION = "subsection";

// src/validation/generated/nodeRegistry.generated.ts
var NODE_TYPES = [
  "app",
  "appNode",
  "booleanInput",
  "callableButton",
  "const",
  "content",
  "fileInput",
  "flow",
  "flowStep",
  "form",
  "function",
  "mediaRoom",
  "numberInput",
  "pluginLeaf",
  "repeat",
  "screen",
  "selectInput",
  "subsection",
  "temporalInput",
  "textInput",
  "tree",
  "variable"
];
var VARIANT_TYPE_KEYS = [
  "appNode:folder",
  "appNode:tab",
  "appNode:tabGroup",
  "flowStep:aiChat",
  "flowStep:aiQuery",
  "flowStep:audioGeneration",
  "flowStep:fetchData",
  "flowStep:imageGeneration",
  "flowStep:publishData",
  "flowStep:script",
  "flowStep:transcribe"
];
var VARIANT_TYPES = {
  "aiChat": {
    "type": "flowStep",
    "variant": "aiChat"
  },
  "aiQuery": {
    "type": "flowStep",
    "variant": "aiQuery"
  },
  "audioGeneration": {
    "type": "flowStep",
    "variant": "audioGeneration"
  },
  "fetchData": {
    "type": "flowStep",
    "variant": "fetchData"
  },
  "folder": {
    "type": "appNode",
    "variant": "folder"
  },
  "imageGeneration": {
    "type": "flowStep",
    "variant": "imageGeneration"
  },
  "publishData": {
    "type": "flowStep",
    "variant": "publishData"
  },
  "script": {
    "type": "flowStep",
    "variant": "script"
  },
  "tab": {
    "type": "appNode",
    "variant": "tab"
  },
  "tabGroup": {
    "type": "appNode",
    "variant": "tabGroup"
  },
  "transcribe": {
    "type": "flowStep",
    "variant": "transcribe"
  }
};
var REQUIRED_FIELDS = {
  "form": [
    "formConfig"
  ],
  "callableButton": [
    "functionName",
    "name"
  ],
  "tree": [
    "nodeTypes",
    "rootType"
  ],
  "variable": [
    "name"
  ],
  "function": [
    "name"
  ],
  "const": [
    "name",
    "value"
  ],
  "mediaRoom": [
    "roomKey"
  ]
};
var LEGACY_NODES = {
  "form": {
    "since": "2026-10",
    "use": "inputs on a screen or flow screen, saved by `publishData` / `updateData` in a script"
  }
};
var LEGACY_PROPS = {
  "appNode": {
    "submitObjectType": {
      "since": "2026-10",
      "use": "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
    }
  },
  "appNode:folder": {
    "submitObjectType": {
      "since": "2026-10",
      "use": "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
    }
  },
  "appNode:tab": {
    "submitObjectType": {
      "since": "2026-10",
      "use": "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
    }
  },
  "appNode:tabGroup": {
    "submitObjectType": {
      "since": "2026-10",
      "use": "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
    }
  },
  "screen": {
    "submitObjectType": {
      "since": "2026-10",
      "use": "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
    }
  },
  "subsection": {
    "submitObjectType": {
      "since": "2026-10",
      "use": "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
    }
  }
};
var LEGACY_HELPER_PROPS = {
  "objectTypeField": {
    "isList": {
      "since": "2026-10",
      "use": '`type: "list<T>"`'
    },
    "multiline": {
      "since": "2026-10",
      "use": '`variant: "multiline"` on a text field'
    }
  }
};

// src/orgicore/types/generated/nodeTypeAliases.generated.ts
var NODE_TYPE_ALIASES = {
  "list": "subsection",
  "videoRoom": "mediaRoom"
};

// src/orgicore/types/nodeTypeAliases.ts
var resolveNodeType = (type14) => {
  var _a2;
  return (_a2 = NODE_TYPE_ALIASES[type14]) != null ? _a2 : type14;
};
var byNodeType = (map2, type14) => {
  var _a2;
  return (_a2 = map2[resolveNodeType(type14)]) != null ? _a2 : map2[type14];
};

// src/orgicore/types/variantTypes.ts
var toRuntimeType = (type14) => {
  var _a2;
  return (_a2 = VARIANT_TYPES[type14]) != null ? _a2 : { type: type14 };
};
var runtimeTypeOf = (node2) => {
  const authored = VARIANT_TYPES[node2.type];
  if (authored) return authored;
  const type14 = resolveNodeType(node2.type);
  return typeof node2.variant === "string" && node2.variant !== "" ? { type: type14, variant: node2.variant } : { type: type14 };
};
var isVariant = (node2, parent, variant) => {
  const r = runtimeTypeOf(node2);
  return r.type === parent && r.variant === variant;
};
var byRuntime = Object.fromEntries(
  Object.entries(VARIANT_TYPES).map(([authored, r]) => [
    `${r.type}:${r.variant}`,
    authored
  ])
);
var authoredTypeOf = (type14, variant) => typeof variant === "string" ? byRuntime[`${type14}:${variant}`] : void 0;
var toAuthoredNode = (node2) => {
  const authored = authoredTypeOf(node2.type, node2.variant);
  if (authored === void 0) return node2;
  const { variant: _dropped, ...rest } = node2;
  void _dropped;
  return { ...rest, type: authored };
};

// src/orgicore/utils/evaluation/jsep/setup.ts
import jsepArrow from "@jsep-plugin/arrow";
import jsepObject from "@jsep-plugin/object";
import jsepRegex from "@jsep-plugin/regex";
import jsepSpread from "@jsep-plugin/spread";
import jsepTemplate from "@jsep-plugin/template";
import jsep from "jsep";

// src/orgicore/utils/evaluation/jsep/matchPlugin.ts
var MATCH_EXP = "MatchExpression";
var MATCH_ARM = "MatchArm";
var MATCH_ARROW = "->";
var OCURLY_CODE = 123;
var CCURLY_CODE = 125;
var COMMA_CODE = 44;
var isIdentPartCode = (c) => c >= 48 && c <= 57 || c >= 65 && c <= 90 || c >= 97 && c <= 122 || c === 36 || c === 95 || c >= 128;
var codeAt = (parser) => parser.code;
var isNode = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var toArm = (parser, raw) => {
  if (isNode(raw) && raw.type === "ConditionalExpression") {
    const test = raw.test;
    if (isNode(test) && test.type === "BinaryExpression" && test.operator === MATCH_ARROW) {
      return {
        type: MATCH_ARM,
        test: test.left,
        consequent: {
          type: "ConditionalExpression",
          test: test.right,
          consequent: raw.consequent,
          alternate: raw.alternate
        }
      };
    }
  }
  if (isNode(raw) && raw.type === "BinaryExpression" && raw.operator === MATCH_ARROW) {
    return { type: MATCH_ARM, test: raw.left, consequent: raw.right };
  }
  return parser.throwError(
    "Expected `condition -> expression` in match block"
  );
};
var matchPlugin = {
  name: "orgigglyMatch",
  init(jsep2) {
    jsep2.addBinaryOp(MATCH_ARROW, 0.5);
    jsep2.hooks.add("gobble-token", function gobbleMatch(env) {
      this.gobbleSpaces();
      if (!this.expr.startsWith("match", this.index)) return;
      const afterKeyword = this.index + 5;
      if (afterKeyword < this.expr.length && isIdentPartCode(this.expr.charCodeAt(afterKeyword))) {
        return;
      }
      const backup = this.index;
      this.index = afterKeyword;
      this.gobbleSpaces();
      let scrutinee;
      if (codeAt(this) !== OCURLY_CODE) {
        let candidate = false;
        try {
          candidate = this.gobbleExpression();
        } catch {
          candidate = false;
        }
        this.gobbleSpaces();
        if (!candidate || codeAt(this) !== OCURLY_CODE) {
          this.index = backup;
          return;
        }
        scrutinee = candidate;
      }
      this.index++;
      const arms = [];
      for (; ; ) {
        this.gobbleSpaces();
        if (codeAt(this) === CCURLY_CODE) {
          this.index++;
          break;
        }
        if (Number.isNaN(codeAt(this))) {
          this.throwError("Unclosed match block \u2014 expected `}`");
        }
        const raw = this.gobbleExpression();
        if (!raw) {
          this.throwError("Expected `condition -> expression` in match block");
        }
        arms.push(toArm(this, raw));
        this.gobbleSpaces();
        if (codeAt(this) === COMMA_CODE) {
          this.index++;
          continue;
        }
        if (codeAt(this) === CCURLY_CODE) {
          this.index++;
          break;
        }
        this.throwError("Expected `,` or `}` after match arm");
      }
      if (arms.length === 0) {
        this.throwError("A match block needs at least one arm");
      }
      env.node = scrutinee ? { type: MATCH_EXP, scrutinee, arms } : { type: MATCH_EXP, arms };
    });
  }
};

// src/orgicore/utils/evaluation/jsep/letPlugin.ts
var LET_EXP = "LetExpression";
var EQUALS_CODE = 61;
var GT_CODE = 62;
var isIdentStartCode = (c) => c >= 65 && c <= 90 || c >= 97 && c <= 122 || c === 36 || c === 95 || c >= 128;
var isIdentPartCode2 = (c) => isIdentStartCode(c) || c >= 48 && c <= 57;
var codeAt2 = (parser) => parser.code;
var asNode = (e) => e;
var asExpr = (n) => n;
var PEEL_THROUGH_OPS = /* @__PURE__ */ new Set(["??", "||"]);
var peelLetInit = (e) => {
  const n = asNode(e);
  if (n.type === "BinaryExpression" && n.operator === ";") {
    const [init, restLeft] = peelLetInit(n.left);
    const rest = restLeft ? asExpr({
      type: "BinaryExpression",
      operator: ";",
      left: restLeft,
      right: n.right
    }) : n.right;
    return [init, rest];
  }
  if (n.type === "BinaryExpression" && PEEL_THROUGH_OPS.has(String(n.operator))) {
    const [init, restLeft] = peelLetInit(n.left);
    return restLeft ? [init, asExpr({ ...n, left: restLeft })] : [e, void 0];
  }
  if (n.type === "ConditionalExpression") {
    const [init, restTest] = peelLetInit(n.test);
    if (restTest) return [init, asExpr({ ...n, test: restTest })];
    const [alt, rest] = peelLetInit(n.alternate);
    return rest ? [asExpr({ ...n, alternate: alt }), rest] : [e, void 0];
  }
  if (n.type === "ArrowFunctionExpression") {
    const [body, rest] = peelLetInit(n.body);
    return rest ? [asExpr({ ...n, body }), rest] : [e, void 0];
  }
  return [e, void 0];
};
var letPlugin = {
  name: "orgigglyLet",
  init(jsep2) {
    jsep2.hooks.add(
      "gobble-token",
      function gobbleLet(env) {
        this.gobbleSpaces();
        if (!this.expr.startsWith("let", this.index)) return;
        const afterKeyword = this.index + 3;
        if (afterKeyword < this.expr.length && isIdentPartCode2(this.expr.charCodeAt(afterKeyword))) {
          return;
        }
        const backup = this.index;
        this.index = afterKeyword;
        this.gobbleSpaces();
        const nameStart = this.index;
        if (!isIdentStartCode(codeAt2(this))) {
          if (/^[^\s=]+\s*=(?![=>])/.test(this.expr.slice(this.index))) {
            this.throwError("Expected a name after let");
          }
          this.index = backup;
          return;
        }
        while (isIdentPartCode2(codeAt2(this))) this.index++;
        const id = this.expr.slice(nameStart, this.index);
        this.gobbleSpaces();
        if (codeAt2(this) !== EQUALS_CODE) {
          this.index = backup;
          return;
        }
        const next = this.expr.charCodeAt(this.index + 1);
        if (next === EQUALS_CODE || next === GT_CODE) {
          this.index = backup;
          return;
        }
        this.index++;
        const gobbled = this.gobbleExpression();
        if (!gobbled) {
          this.throwError(`Expected an initialiser after let ${id} =`);
        }
        const [init, body] = peelLetInit(gobbled);
        env.node = body ? { type: LET_EXP, id, init, body } : { type: LET_EXP, id, init };
      }
    );
  }
};

// src/orgicore/utils/evaluation/jsep/assignPlugin.ts
var asNode2 = (e) => e;
var isNode2 = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var isBinary = (n, op) => n.type === "BinaryExpression" && n.operator === op;
var ABSORBED_OPS = /* @__PURE__ */ new Set(["||", "&&", "??"]);
var binary = (operator, left4, right4) => ({
  type: "BinaryExpression",
  operator,
  left: left4,
  right: right4
});
var peelSemicolon = (n) => {
  if (isBinary(n, ";")) {
    const [first, restLeft] = peelSemicolon(asNode2(n.left));
    return [first, restLeft ? binary(";", restLeft, n.right) : asNode2(n.right)];
  }
  if (n.type === "ConditionalExpression") {
    const [alternate, rest] = peelSemicolon(asNode2(n.alternate));
    return rest ? [{ ...n, alternate }, rest] : [n];
  }
  return [n];
};
var atTail = (n, fix) => {
  if (!isBinary(n, ";")) return fix(n);
  const right4 = atTail(asNode2(n.right), fix);
  return right4 ? binary(";", n.left, right4) : void 0;
};
var asAssign = (n) => isBinary(n, "=") ? n : void 0;
var reassociate = (n) => {
  if (n.type === "BinaryExpression" && ABSORBED_OPS.has(String(n.operator)) && isNode2(n.left)) {
    const op = String(n.operator);
    const fixed = atTail(n.left, (tail) => {
      const assign = asAssign(tail);
      return assign ? binary(
        "=",
        assign.left,
        reassociate(binary(op, assign.right, n.right))
      ) : void 0;
    });
    if (fixed) return fixed;
  }
  if (n.type === "ConditionalExpression" && isNode2(n.test)) {
    const [alternate, rest] = peelSemicolon(asNode2(n.alternate));
    const fixed = atTail(n.test, (tail) => {
      const assign = asAssign(tail);
      return assign ? binary(
        "=",
        assign.left,
        reassociate({ ...n, test: assign.right, alternate })
      ) : void 0;
    });
    if (fixed) return rest ? binary(";", fixed, rest) : fixed;
  }
  return n;
};
var walk = (n) => {
  const out = { ...n };
  for (const key of Object.keys(n)) {
    if (key === "parent") continue;
    const v = n[key];
    if (isNode2(v)) out[key] = walk(v);
    else if (Array.isArray(v))
      out[key] = v.map((item) => isNode2(item) ? walk(item) : item);
  }
  return reassociate(out);
};
var assignPlugin = {
  name: "orgigglyAssign",
  init(jsep2) {
    jsep2.hooks.add("after-all", function fixAssignments(env) {
      if (isNode2(env.node)) env.node = walk(env.node);
    });
  }
};

// src/orgicore/utils/evaluation/jsep/setup.ts
var setupJsep = () => {
  if (!jsep._orgigglyTemplatePluginRegistered) {
    jsep.plugins.register(
      jsepTemplate
    );
    jsep._orgigglyTemplatePluginRegistered = true;
  }
  if (!jsep._orgigglyObjectPluginRegistered) {
    jsep.plugins.register(
      jsepObject
    );
    jsep._orgigglyObjectPluginRegistered = true;
  }
  if (!jsep._orgigglyRegexPluginRegistered) {
    jsep.plugins.register(
      jsepRegex
    );
    jsep._orgigglyRegexPluginRegistered = true;
  }
  if (!jsep._orgigglySpreadPluginRegistered) {
    jsep.plugins.register(
      jsepSpread
    );
    jsep._orgigglySpreadPluginRegistered = true;
  }
  if (!jsep._orgigglyArrowPluginRegistered) {
    jsep.plugins.register(
      jsepArrow
    );
    jsep._orgigglyArrowPluginRegistered = true;
  }
  if (!jsep._orgigglyAtUnaryRegistered) {
    jsep.addUnaryOp("@");
    jsep._orgigglyAtUnaryRegistered = true;
  }
  if (!jsep._orgigglysemicolonRegistered) {
    jsep.addBinaryOp(";", 1);
    jsep._orgigglysemicolonRegistered = true;
  }
  if (!jsep._orgigglyAssignmentRegistered) {
    jsep.addBinaryOp("=", 2, true);
    jsep._orgigglyAssignmentRegistered = true;
  }
  if (!jsep._orgigglyNullishCoalescingRegistered) {
    jsep.addBinaryOp("??", 1);
    jsep._orgigglyNullishCoalescingRegistered = true;
  }
  if (!jsep._orgigglyMatchPluginRegistered) {
    jsep.plugins.register(
      matchPlugin
    );
    jsep._orgigglyMatchPluginRegistered = true;
  }
  if (!jsep._orgigglyLetPluginRegistered) {
    jsep.plugins.register(
      letPlugin
    );
    jsep._orgigglyLetPluginRegistered = true;
  }
  if (!jsep._orgigglyAssignPluginRegistered) {
    jsep.plugins.register(
      assignPlugin
    );
    jsep._orgigglyAssignPluginRegistered = true;
  }
};
setupJsep();

// src/orgicore/utils/evaluation/jsep/typeCheckers.ts
var isLiteral = (e) => {
  return e.type === "Literal";
};
var isBinaryExpression = (e) => {
  return e.type === "BinaryExpression";
};
var isCallExpression = (e) => {
  return e.type === "CallExpression";
};
var isIdentifier = (e) => {
  return e.type === "Identifier";
};
var isMemberExpression = (e) => {
  return e.type === "MemberExpression";
};
var isThisExpression = (e) => {
  return e.type === "ThisExpression";
};
var isUnaryExpression = (e) => {
  return e.type === "UnaryExpression";
};
var isConditionalExpression = (e) => {
  return e.type === "ConditionalExpression";
};
var isSequenceExpression = (e) => {
  return e.type === "SequenceExpression";
};
var isCompound = (e) => {
  return e.type === "Compound";
};
var isArrayExpression = (e) => {
  return e.type === "ArrayExpression";
};
var isTemplateLiteral = (e) => {
  return e.type === "TemplateLiteral";
};
var isTaggedTemplateExpression = (e) => {
  return e.type === "TaggedTemplateExpression";
};
var isObjectExpression = (e) => {
  return e.type === "ObjectExpression";
};
var isSpreadElement = (e) => {
  return e && e.type === "SpreadElement";
};
var isArrowExpression = (e) => {
  return e.type === "ArrowFunctionExpression";
};
var isMatchExpression = (e) => {
  return e.type === "MatchExpression";
};
var isLetExpression = (e) => {
  return e.type === "LetExpression";
};

// src/orgicore/utils/evaluation/jsep/parseWithMeta.ts
var isExpression = (value) => typeof value === "object" && value !== null && "type" in value;
var getInputSource = (node2) => {
  let current = node2;
  while (current.parent) {
    current = current.parent;
  }
  const source = current._source;
  if (!source) return "";
  return source;
};
var getRaw = (node2) => {
  return getInputSource(node2).slice(node2.start, node2.end);
};
var addMetadata = (node2, source, searchFrom, parent) => {
  const metaNode = node2;
  metaNode.parent = parent;
  if (isIdentifier(node2)) {
    const start = source.indexOf(node2.name, searchFrom);
    const end = start + node2.name.length;
    metaNode.start = start;
    metaNode.end = end;
    return { start, end };
  }
  if (isLiteral(node2)) {
    const raw = node2.raw;
    if (raw) {
      const start2 = source.indexOf(raw, searchFrom);
      const end2 = start2 + raw.length;
      metaNode.start = start2;
      metaNode.end = end2;
      return { start: start2, end: end2 };
    }
    const valStr = String(node2.value);
    const start = source.indexOf(valStr, searchFrom);
    const end = start + valStr.length;
    metaNode.start = start;
    metaNode.end = end;
    return { start, end };
  }
  if (isBinaryExpression(node2)) {
    const leftResult = addMetadata(node2.left, source, searchFrom, metaNode);
    const rightResult = addMetadata(
      node2.right,
      source,
      leftResult.end,
      metaNode
    );
    metaNode.start = leftResult.start;
    metaNode.end = rightResult.end;
    return { start: leftResult.start, end: rightResult.end };
  }
  if (isUnaryExpression(node2)) {
    const opStart = source.indexOf(node2.operator, searchFrom);
    const argResult = addMetadata(
      node2.argument,
      source,
      opStart + node2.operator.length,
      metaNode
    );
    metaNode.start = opStart;
    metaNode.end = argResult.end;
    return { start: opStart, end: argResult.end };
  }
  if (isCallExpression(node2)) {
    const calleeResult = addMetadata(node2.callee, source, searchFrom, metaNode);
    let currentPos = calleeResult.end;
    for (const arg of node2.arguments) {
      const argResult = addMetadata(arg, source, currentPos, metaNode);
      currentPos = argResult.end;
    }
    const closeParen = source.indexOf(")", currentPos);
    const end = closeParen + 1;
    metaNode.start = calleeResult.start;
    metaNode.end = end;
    return { start: calleeResult.start, end };
  }
  if (isMemberExpression(node2)) {
    const objResult = addMetadata(node2.object, source, searchFrom, metaNode);
    if (node2.computed) {
      const propResult = addMetadata(
        node2.property,
        source,
        objResult.end,
        metaNode
      );
      const closeBracket = source.indexOf("]", propResult.end);
      const end = closeBracket + 1;
      metaNode.start = objResult.start;
      metaNode.end = end;
      return { start: objResult.start, end };
    } else {
      const propResult = addMetadata(
        node2.property,
        source,
        objResult.end,
        metaNode
      );
      metaNode.start = objResult.start;
      metaNode.end = propResult.end;
      return { start: objResult.start, end: propResult.end };
    }
  }
  if (isConditionalExpression(node2)) {
    const testResult = addMetadata(node2.test, source, searchFrom, metaNode);
    const consResult = addMetadata(
      node2.consequent,
      source,
      testResult.end,
      metaNode
    );
    const altResult = addMetadata(
      node2.alternate,
      source,
      consResult.end,
      metaNode
    );
    metaNode.start = testResult.start;
    metaNode.end = altResult.end;
    return { start: testResult.start, end: altResult.end };
  }
  if (isArrayExpression(node2)) {
    const openBracket = source.indexOf("[", searchFrom);
    let currentPos = openBracket + 1;
    for (const elem of node2.elements) {
      if (elem) {
        const elemResult = addMetadata(elem, source, currentPos, metaNode);
        currentPos = elemResult.end;
      }
    }
    const closeBracket = source.indexOf("]", currentPos);
    const end = closeBracket + 1;
    metaNode.start = openBracket;
    metaNode.end = end;
    return { start: openBracket, end };
  }
  if (isObjectExpression(node2)) {
    const openBrace = source.indexOf("{", searchFrom);
    let currentPos = openBrace + 1;
    for (const prop5 of node2.properties) {
      const p = prop5;
      if (p.key) {
        const keyResult = addMetadata(p.key, source, currentPos, metaNode);
        currentPos = keyResult.end;
      }
      if (p.value && !p.shorthand) {
        const valResult = addMetadata(p.value, source, currentPos, metaNode);
        currentPos = valResult.end;
      }
    }
    const closeBrace = source.indexOf("}", currentPos);
    const end = closeBrace + 1;
    metaNode.start = openBrace;
    metaNode.end = end;
    return { start: openBrace, end };
  }
  if (isTemplateLiteral(node2)) {
    const backtickStart = source.indexOf("`", searchFrom);
    let currentPos = backtickStart + 1;
    for (const expr of node2.expressions) {
      const exprResult = addMetadata(expr, source, currentPos, metaNode);
      currentPos = exprResult.end;
    }
    const backtickEnd = source.indexOf("`", currentPos);
    const end = backtickEnd + 1;
    metaNode.start = backtickStart;
    metaNode.end = end;
    return { start: backtickStart, end };
  }
  if (isThisExpression(node2)) {
    const start = source.indexOf("this", searchFrom);
    const end = start + 4;
    metaNode.start = start;
    metaNode.end = end;
    return { start, end };
  }
  if (isCompound(node2)) {
    const body = node2.body || [];
    if (body.length === 0) {
      metaNode.start = searchFrom;
      metaNode.end = searchFrom;
      return { start: searchFrom, end: searchFrom };
    }
    let currentPos = searchFrom;
    let firstStart = searchFrom;
    let lastEnd = searchFrom;
    for (let i = 0; i < body.length; i++) {
      const result = addMetadata(body[i], source, currentPos, metaNode);
      if (i === 0) firstStart = result.start;
      lastEnd = result.end;
      currentPos = result.end;
    }
    metaNode.start = firstStart;
    metaNode.end = lastEnd;
    return { start: firstStart, end: lastEnd };
  }
  {
    let firstStart = -1;
    let lastEnd = searchFrom;
    let currentPos = searchFrom;
    for (const [key, value] of Object.entries(node2)) {
      if (key === "type" || key === "parent") continue;
      if (Array.isArray(value)) {
        for (const child of value) {
          if (isExpression(child)) {
            const result = addMetadata(child, source, currentPos, metaNode);
            if (firstStart === -1) firstStart = result.start;
            lastEnd = result.end;
            currentPos = result.end;
          }
        }
      } else if (isExpression(value)) {
        const result = addMetadata(value, source, currentPos, metaNode);
        if (firstStart === -1) firstStart = result.start;
        lastEnd = result.end;
        currentPos = result.end;
      }
    }
    if (firstStart === -1) {
      metaNode.start = searchFrom;
      metaNode.end = searchFrom;
      return { start: searchFrom, end: searchFrom };
    }
    metaNode.start = firstStart;
    metaNode.end = lastEnd;
    return { start: firstStart, end: lastEnd };
  }
};
var parseExpressionWithMeta = (source) => {
  const ast = jsep(source);
  addMetadata(ast, source, 0);
  ast._source = source;
  return ast;
};

// src/orgicore/utils/evaluation/propDef.ts
import * as t8 from "io-ts";
var FORMULA_PREFIX = "@";
var parsePropDef = (source, targetCodec, name) => {
  const isPossibleFormula = t8.string.is(source);
  if (isPossibleFormula) {
    const isFormula = source.startsWith(FORMULA_PREFIX);
    if (isFormula) {
      const value = stripFormulaPrefix(source);
      return { isFormula, value, targetCodec, name };
    }
  }
  return { isFormula: false, value: source, targetCodec, name };
};
var stripFormulaPrefix = (source) => {
  return source.slice(FORMULA_PREFIX.length).trim();
};
var tryStripFormulaPrefix = (source) => {
  return source.startsWith(FORMULA_PREFIX) ? stripFormulaPrefix(source) : source;
};
var prependFormulaPrefix = (source) => {
  return FORMULA_PREFIX + source;
};

// src/orgicore/utils/evaluation/templateFormula.ts
var needsTemplateWrapper = (s) => !s.startsWith(FORMULA_PREFIX) && s.includes("${");
var asTemplateFormula = (s) => {
  if (!needsTemplateWrapper(s)) return s;
  const trimmed = s.trim();
  if (trimmed.length > 1 && trimmed.startsWith("`") && trimmed.endsWith("`")) {
    return FORMULA_PREFIX + s;
  }
  return `${FORMULA_PREFIX}\`${escapeLiteralBackticks(s)}\``;
};
var escapeLiteralBackticks = (s) => {
  let out = "";
  let i = 0;
  const copyQuoted = (quote) => {
    out += s[i++];
    while (i < s.length && s[i] !== quote) {
      if (s[i] === "\\") out += s[i++];
      if (i < s.length) out += s[i++];
    }
    if (i < s.length) out += s[i++];
  };
  const copyExpression = () => {
    let depth = 1;
    while (i < s.length) {
      const ch = s[i];
      if (ch === "'" || ch === '"') {
        copyQuoted(ch);
      } else if (ch === "`") {
        out += s[i++];
        copyTemplate(true);
      } else {
        if (ch === "{") depth++;
        if (ch === "}") depth--;
        out += s[i++];
        if (depth === 0) return;
      }
    }
  };
  const copyTemplate = (nested) => {
    while (i < s.length) {
      const ch = s[i];
      if (ch === "\\") {
        out += s[i++];
        if (i < s.length) out += s[i++];
      } else if (ch === "`") {
        if (nested) {
          out += s[i++];
          return;
        }
        out += "\\`";
        i++;
      } else if (ch === "$" && s[i + 1] === "{") {
        out += "${";
        i += 2;
        copyExpression();
      } else {
        out += s[i++];
      }
    }
  };
  copyTemplate(false);
  return out;
};

// src/create/formulaSplice.ts
var IDENT_START = /[A-Za-z_$]/;
var IDENT_PART = /[\w$]/;
var NUMBER_PART = /[\w.]/;
var ESCAPES = {
  n: "\n",
  t: "	",
  r: "\r",
  b: "\b",
  f: "\f",
  v: "\v"
};
var lex = (src, mode2) => {
  const tokens = [];
  const runs = [];
  let i = 0;
  let prevOperand = false;
  const punct = (text, start) => {
    tokens.push({ kind: "punct", start, end: start + text.length, text });
  };
  const lexString = () => {
    var _a2, _b;
    const quote = src[i];
    const start = i;
    i++;
    let value = "";
    while (i < src.length && src[i] !== quote) {
      if (src[i] === "\\") {
        const c = (_a2 = src[i + 1]) != null ? _a2 : "";
        value += (_b = ESCAPES[c]) != null ? _b : c;
        i += 2;
        continue;
      }
      value += src[i];
      i++;
    }
    const end = Math.min(i + 1, src.length);
    i = end;
    tokens.push({ kind: "string", start, end, value });
    runs.push({ pieces: [{ start: start + 1, end: end - 1 }] });
    prevOperand = true;
  };
  const lexRegex = () => {
    const start = i;
    i++;
    let inClass = false;
    while (i < src.length) {
      const c = src[i];
      if (c === "\\") {
        i += 2;
        continue;
      }
      i++;
      if (c === "[") inClass = true;
      else if (c === "]") inClass = false;
      else if (c === "/" && !inClass) break;
    }
    while (i < src.length && IDENT_PART.test(src[i])) i++;
    punct(src.slice(start, i), start);
    prevOperand = true;
  };
  const lexTemplate = (toEnd) => {
    const pieces = [];
    let pieceStart = i;
    while (i < src.length) {
      const c = src[i];
      if (c === "\\") {
        i += 2;
        continue;
      }
      if (!toEnd && c === "`") break;
      if (c === "$" && src[i + 1] === "{") {
        pieces.push({ start: pieceStart, end: i });
        punct("${", i);
        i += 2;
        prevOperand = false;
        lexExpr(true);
        if (src[i] === "}") {
          punct("}", i);
          i++;
        }
        pieceStart = i;
        continue;
      }
      i++;
    }
    pieces.push({ start: pieceStart, end: Math.min(i, src.length) });
    runs.push({ pieces });
  };
  const lexExpr = (inInterp) => {
    let depth = 0;
    while (i < src.length) {
      const c = src[i];
      if (/\s/.test(c)) {
        i++;
        continue;
      }
      if (c === "'" || c === '"') {
        lexString();
        continue;
      }
      if (c === "`") {
        punct("`", i);
        i++;
        lexTemplate(false);
        if (src[i] === "`") {
          punct("`", i);
          i++;
        }
        prevOperand = true;
        continue;
      }
      if (IDENT_START.test(c)) {
        const start = i;
        while (i < src.length && IDENT_PART.test(src[i])) i++;
        tokens.push({
          kind: "ident",
          start,
          end: i,
          text: src.slice(start, i)
        });
        prevOperand = true;
        continue;
      }
      if (/[0-9]/.test(c)) {
        const start = i;
        while (i < src.length && NUMBER_PART.test(src[i])) i++;
        punct(src.slice(start, i), start);
        prevOperand = true;
        continue;
      }
      if (c === "/" && !prevOperand) {
        lexRegex();
        continue;
      }
      if (c === "{") depth++;
      if (c === "}") {
        if (inInterp && depth === 0) return;
        depth--;
      }
      punct(c, i);
      prevOperand = c === ")" || c === "]" || c === "}";
      i++;
    }
  };
  if (mode2 === "template") lexTemplate(true);
  else lexExpr(false);
  return { tokens, runs };
};
var isAstNode = (x) => typeof x === "object" && x !== null && !Array.isArray(x) && typeof x.type === "string";
var childNodes = (n) => Object.values(n).flatMap(
  (v) => Array.isArray(v) ? v.filter(isAstNode) : isAstNode(v) ? [v] : []
);
var walkAst = (n, visit) => {
  visit(n);
  for (const c of childNodes(n)) walkAst(c, visit);
};
var isIdentNamed = (n, name) => isAstNode(n) && n.type === "Identifier" && n.name === name;
var isStringLiteral = (n) => isAstNode(n) && n.type === "Literal" && typeof n.value === "string";
var applyEdits = (src, edits) => [...edits].sort((a, b2) => b2.start - a.start).reduce((s, e) => s.slice(0, e.start) + e.text + s.slice(e.end), src);
var parseFor = (src, mode2) => {
  if (mode2 === "template" && !src.includes("${")) return void 0;
  const ast = jsep(
    mode2 === "template" ? asTemplateFormula(src).slice(1) : src
  );
  return isAstNode(ast) ? ast : void 0;
};
var isRecord = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var pathString = (segs) => segs.reduce(
  (acc, s) => typeof s === "number" ? `${acc}[${s}]` : acc ? `${acc}.${s}` : s,
  ""
);
var isScriptStep = (node2) => typeof node2.type === "string" && isVariant(
  {
    type: node2.type,
    variant: typeof node2.variant === "string" ? node2.variant : void 0
  },
  FLOW_STEP,
  "script"
);

// src/create/renameRefs.ts
var DATA_ROOT = "$data";
var BY_ID = "byId";
var RECORD_HOSTS = /* @__PURE__ */ new Set([
  "publishData",
  "updateData",
  "deleteData",
  "hardDelete"
]);
var PATH_HOSTS = /* @__PURE__ */ new Set([
  "setRealtime",
  "updateRealtime",
  "pushRealtime",
  "removeRealtime",
  "removeRealtimeOnDisconnect"
]);
var APP_VALUE_HOST = "setAppValue";
var PRESENCE_VAR = "presence";
var dataMemberProperty = (n) => n.type === "MemberExpression" && n.computed !== true && isIdentNamed(n.object, DATA_ROOT) && isAstNode(n.property) && n.property.type === "Identifier" ? n.property : void 0;
var objectProperty = (obj, key) => {
  if (!isAstNode(obj) || obj.type !== "ObjectExpression") return void 0;
  const props = Array.isArray(obj.properties) ? obj.properties : [];
  for (const p of props) {
    if (!isAstNode(p) || p.type !== "Property" || p.computed === true) continue;
    if (isIdentNamed(p.key, key)) return p.value;
    if (isStringLiteral(p.key) && p.key.value === key) return p.value;
  }
  return void 0;
};
var leftmostOperand = (n) => isAstNode(n) && n.type === "BinaryExpression" && n.operator === "+" ? leftmostOperand(n.left) : n;
var slotExpression = (n) => {
  if (n.type !== "CallExpression" || !isAstNode(n.callee)) return void 0;
  if (n.callee.type !== "Identifier" || typeof n.callee.name !== "string") {
    return void 0;
  }
  const callee = n.callee.name;
  const args = Array.isArray(n.arguments) ? n.arguments : [];
  if (callee === BY_ID) return { expr: args[0], isPath: false };
  if (RECORD_HOSTS.has(callee)) {
    return { expr: objectProperty(args[0], "type"), isPath: false };
  }
  if (PATH_HOSTS.has(callee)) {
    return { expr: leftmostOperand(args[0]), isPath: true };
  }
  if (callee === APP_VALUE_HOST && isStringLiteral(args[0]) && args[0].value === PRESENCE_VAR) {
    return {
      expr: leftmostOperand(objectProperty(args[1], "path")),
      isPath: true
    };
  }
  return void 0;
};
var backtickTypeName = (n, renames) => {
  var _a2, _b;
  const expr = (_a2 = slotExpression(n)) == null ? void 0 : _a2.expr;
  if (!isAstNode(expr) || expr.type !== "TemplateLiteral") return false;
  const text = JSON.stringify((_b = expr.quasis) != null ? _b : []);
  return Object.keys(renames).some((name) => text.includes(name));
};
var typeSlots = (n) => {
  const slot2 = slotExpression(n);
  return slot2 && isStringLiteral(slot2.expr) ? [{ literal: slot2.expr, isPath: slot2.isPath }] : [];
};
var renamedLiteral = (slot2, renames) => {
  const value = slot2.literal.value;
  const slash = slot2.isPath ? value.indexOf("/") : -1;
  const head2 = slash === -1 ? value : value.slice(0, slash);
  return head2 in renames ? { head: head2, nextHead: renames[head2] } : void 0;
};
var collectTargets = (ast, renames) => {
  const members = /* @__PURE__ */ new Map();
  const literals = /* @__PURE__ */ new Map();
  let backtickSlots = 0;
  walkAst(ast, (n) => {
    var _a2, _b;
    if (backtickTypeName(n, renames)) backtickSlots++;
    const prop5 = dataMemberProperty(n);
    if (prop5 && typeof prop5.name === "string" && prop5.name in renames) {
      members.set(prop5.name, ((_a2 = members.get(prop5.name)) != null ? _a2 : 0) + 1);
    }
    for (const slot2 of typeSlots(n)) {
      const rename = renamedLiteral(slot2, renames);
      if (rename === void 0) continue;
      const seen = literals.get(slot2.literal.value);
      literals.set(slot2.literal.value, {
        ...rename,
        count: ((_b = seen == null ? void 0 : seen.count) != null ? _b : 0) + 1
      });
    }
  });
  return { members, literals, backtickSlots };
};
var renameAst = (ast, renames) => {
  const copy = structuredClone(ast);
  walkAst(copy, (n) => {
    const prop5 = dataMemberProperty(n);
    if (prop5 && typeof prop5.name === "string" && prop5.name in renames) {
      prop5.name = renames[prop5.name];
    }
    for (const slot2 of typeSlots(n)) {
      const rename = renamedLiteral(slot2, renames);
      if (rename === void 0) continue;
      slot2.literal.value = rename.nextHead + slot2.literal.value.slice(rename.head.length);
    }
  });
  return copy;
};
var mentionsDataAttr = (s) => s.includes("data-script") || s.includes("data-args");
var normalize = (ast, ignoreText) => {
  const copy = structuredClone(ast);
  walkAst(copy, (n) => {
    if (n.type === "Literal") {
      delete n.raw;
      if (ignoreText && typeof n.value === "string" && mentionsDataAttr(n.value)) {
        delete n.value;
      }
    }
    if (ignoreText && n.type === "TemplateElement") delete n.value;
  });
  return copy;
};
var mentionsAny = (src, renames) => Object.keys(renames).some((name) => src.includes(name));
var fail = (kind, path, message) => left3({ kind, path, message });
var planRename = (src, mode2, renames, path) => {
  var _a2, _b, _c;
  if (!mentionsAny(src, renames)) return right3([]);
  let ast;
  try {
    ast = parseFor(src, mode2);
  } catch (err) {
    return fail(
      "unparseable",
      path,
      `Cannot parse a formula that mentions a renamed type: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  const { tokens, runs } = lex(src, mode2);
  const edits = [];
  if (ast) {
    const targets = collectTargets(ast, renames);
    const memberHits = /* @__PURE__ */ new Map();
    const literalHits = /* @__PURE__ */ new Map();
    tokens.forEach((t30, k) => {
      var _a3, _b2;
      if (t30.kind === "ident" && t30.text in renames) {
        const dot = tokens[k - 1];
        const root = tokens[k - 2];
        if ((dot == null ? void 0 : dot.kind) === "punct" && dot.text === "." && (root == null ? void 0 : root.kind) === "ident" && root.text === DATA_ROOT) {
          memberHits.set(t30.text, ((_a3 = memberHits.get(t30.text)) != null ? _a3 : 0) + 1);
          edits.push({ start: t30.start, end: t30.end, text: renames[t30.text] });
        }
      }
      if (t30.kind === "string") {
        const target = targets.literals.get(t30.value);
        if (!target) return;
        const start = t30.start + 1;
        const end = start + target.head.length;
        if (src.slice(start, end) !== target.head) return;
        literalHits.set(t30.value, ((_b2 = literalHits.get(t30.value)) != null ? _b2 : 0) + 1);
        edits.push({ start, end, text: target.nextHead });
      }
    });
    if (targets.backtickSlots > 0) {
      return fail(
        "unlocated-reference",
        path,
        `A type slot names a renamed type in a backtick string; quote it with ' or " so the rename can place it.`
      );
    }
    for (const [name, count] of targets.members) {
      if (((_a2 = memberHits.get(name)) != null ? _a2 : 0) !== count) {
        return fail(
          "unlocated-reference",
          path,
          `Found ${count} \`$data.${name}\` read(s) in the formula but ${(_b = memberHits.get(name)) != null ? _b : 0} in its source text \u2014 cannot place the rename.`
        );
      }
    }
    for (const [value, { count }] of targets.literals) {
      const hits = (_c = literalHits.get(value)) != null ? _c : 0;
      if (hits !== count) {
        return fail(
          hits > count ? "ambiguous-literal" : "unlocated-reference",
          path,
          `The string "${value}" names a type in ${count} place(s) but appears ${hits} time(s) \u2014 rename it by hand or give the other use a different spelling.`
        );
      }
    }
  }
  const attrEdits = [];
  for (const run of runs) {
    const planned = planAttrBodies(src, run, renames, path);
    if (isLeft3(planned)) return planned;
    attrEdits.push(...planned.right);
  }
  if (ast && !isFaithful(ast, applyEdits(src, [...edits, ...attrEdits]), mode2, renames, {
    ignoreText: attrEdits.length > 0
  })) {
    return fail(
      "equivalence-mismatch",
      path,
      "The renamed formula does not parse to the renamed expression \u2014 the rename would change more than type names."
    );
  }
  return right3([...edits, ...attrEdits]);
};
var isFaithful = (ast, next, mode2, renames, { ignoreText }) => {
  let nextAst;
  try {
    nextAst = parseFor(next, mode2);
  } catch {
    return false;
  }
  return nextAst !== void 0 && R2.isDeepEqual(
    normalize(nextAst, ignoreText),
    normalize(renameAst(ast, renames), ignoreText)
  );
};
var isFaithfulRename = (source, next, renames) => {
  const strip = (s) => s.startsWith("@") ? s.slice(1) : s;
  try {
    const ast = parseFor(strip(source), "expr");
    return ast !== void 0 && isFaithful(ast, strip(next), "expr", renames, { ignoreText: false });
  } catch {
    return false;
  }
};
var planAttrBodies = (src, run, renames, path) => {
  var _a2, _b, _c;
  let text = "";
  const map2 = [];
  run.pieces.forEach((p, idx) => {
    if (idx > 0) text += INTERP_PLACEHOLDER;
    map2.push({ textStart: text.length, srcStart: p.start, length: p.end - p.start });
    text += src.slice(p.start, p.end);
  });
  if (!mentionsDataAttr(text)) return right3([]);
  const edits = [];
  for (const m of text.matchAll(DATA_ATTR_RE)) {
    const raw = (_b = (_a2 = m[2]) != null ? _a2 : m[3]) != null ? _b : "";
    let bodyStart = ((_c = m.index) != null ? _c : 0) + m[0].length - 1 - raw.length;
    let body = raw;
    if (m[1] === "args" && body.startsWith("@")) {
      body = body.slice(1);
      bodyStart++;
    }
    if (!mentionsAny(body, renames)) continue;
    if (body.includes("\\")) {
      return fail(
        "escaped-attribute",
        path,
        `A data-${m[1]} body that mentions a renamed type contains a backslash escape; its source text and its value differ, so the rename cannot be placed.`
      );
    }
    const planned = planRename(body, "expr", renames, `${path} (data-${m[1]})`);
    if (isLeft3(planned)) return planned;
    for (const e of planned.right) {
      const at = bodyStart + e.start;
      const seg = map2.find(
        (s) => at >= s.textStart && at + (e.end - e.start) <= s.textStart + s.length
      );
      if (!seg) {
        return fail(
          "unlocated-reference",
          path,
          `A type reference in a data-${m[1]} body straddles an interpolation.`
        );
      }
      const start = seg.srcStart + (at - seg.textStart);
      edits.push({ start, end: start + (e.end - e.start), text: e.text });
    }
  }
  return right3(edits);
};
var renameTypesInFormula = (value, renames, kind, path = "") => {
  const prefixed = value.startsWith("@");
  if (kind === "formula" && !prefixed) return right3(value);
  const offset = prefixed ? 1 : 0;
  const mode2 = kind === "template" && !prefixed ? "template" : "expr";
  const planned = planRename(value.slice(offset), mode2, renames, path);
  if (isLeft3(planned)) return planned;
  const edits = planned.right.map((e) => ({
    ...e,
    start: e.start + offset,
    end: e.end + offset
  }));
  return right3(applyEdits(value, edits));
};
var renameFieldType = (type14, renames) => {
  const parsed = parseFieldType(type14);
  const rename = (s) => s.kind === "ref" && s.name in renames ? { ...s, name: renames[s.name] } : s;
  switch (parsed.kind) {
    case "ref":
      return parsed.name in renames ? renames[parsed.name] : type14;
    case "list":
    case "map":
      return parsed.elem.kind === "ref" && parsed.elem.name in renames ? formatFieldType({ ...parsed, elem: rename(parsed.elem) }) : type14;
    default:
      return type14;
  }
};
var renameParameters = (params, renames) => params.split(",").map((entry) => {
  const [spec] = parseParameters(entry);
  if (!(spec == null ? void 0 : spec.type)) return entry;
  const next = renameFieldType(spec.type, renames);
  if (next === spec.type) return entry;
  const colon = entry.indexOf(":");
  return entry.slice(0, colon + 1) + entry.slice(colon + 1).replace(spec.type, next);
}).join(",");
var TYPE_NAME_KEYS = /* @__PURE__ */ new Set([
  "dataType",
  "submitObjectType",
  "resultObjectType",
  "publishDataType",
  "rootType",
  "onPublishType",
  "watchType",
  "targetType",
  "privateType",
  "archiveType",
  "smsPrivateType",
  "moveType"
]);
var OBJECT_TYPE_ID_PREFIX = "objectType-";
var renameTypesInConfig = (config, renames) => {
  const errors = [];
  const section2 = (path) => path[0];
  const formula = (value, kind, path) => {
    const out = renameTypesInFormula(value, renames, kind, pathString(path));
    if (isLeft3(out)) {
      errors.push(out.left);
      return value;
    }
    return out.right;
  };
  const walk8 = (value, path) => {
    if (Array.isArray(value)) return value.map((v, i) => walk8(v, [...path, i]));
    if (!isRecord(value)) return value;
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [
        key,
        renameEntry(value, key, v, [...path, key])
      ])
    );
  };
  const renameEntry = (node2, key, v, at) => {
    const inSection = section2(at);
    if (typeof v === "string") {
      if (TYPE_NAME_KEYS.has(key) && v in renames) return renames[v];
      if (key === "type") {
        if (inSection === "objectInstances" && v in renames) return renames[v];
        if (inSection === "objectTypes" || inSection === "plugins") {
          return renameFieldType(v, renames);
        }
        return v;
      }
      if (inSection === "objectTypes" && node2.type === "objectType") {
        if (key === "name" && v in renames) return renames[v];
        if (key === "id" && v.startsWith(OBJECT_TYPE_ID_PREFIX)) {
          const old = v.slice(OBJECT_TYPE_ID_PREFIX.length);
          return old in renames ? OBJECT_TYPE_ID_PREFIX + renames[old] : v;
        }
      }
      if (node2.type === "function") {
        if (key === "parameters") return renameParameters(v, renames);
        if (key === "returns" || key === "returnTypeHint") {
          return renameFieldType(v, renames);
        }
      }
      if (key === "htmlTemplate") {
        if (!v.startsWith("@") && node2.runtime === "sandboxed") return v;
        return formula(v, "template", at);
      }
      if (key === "script" && (isScriptStep(node2) || at[at.length - 3] === "actions")) {
        return formula(v, "script", at);
      }
      return formula(v, "formula", at);
    }
    if (key === "allowedChildTypes" && Array.isArray(v)) {
      return v.map((t30) => typeof t30 === "string" && t30 in renames ? renames[t30] : t30);
    }
    if (key === "nodeTypes" && isRecord(v)) {
      return Object.fromEntries(
        Object.entries(v).map(([name, def]) => [
          name in renames ? renames[name] : name,
          walk8(def, [...at, name])
        ])
      );
    }
    return walk8(v, at);
  };
  const result = walk8(config, []);
  return errors.length > 0 ? left3(errors) : right3(result);
};

// src/orgicore/types/nodes.ts
import * as t9 from "io-ts";
var nodeType = t9.union([
  t9.literal(TEXT_INPUT),
  t9.literal(BOOLEAN_INPUT),
  t9.literal(NUMBER_INPUT),
  t9.literal(TEMPORAL_INPUT),
  t9.literal(SELECT_INPUT),
  t9.literal(FILE_INPUT),
  t9.literal(PLUGIN_LEAF),
  t9.literal(FORM),
  t9.literal(FLOW),
  t9.literal(FLOW_STEP),
  t9.literal(REPEAT),
  t9.literal(CALLABLE_BUTTON),
  t9.literal(APP),
  t9.literal(APP_NODE),
  t9.literal(SCREEN),
  t9.literal(TREE),
  t9.literal(VARIABLE),
  t9.literal(FUNCTION),
  t9.literal(CONST),
  t9.literal(CONTENT),
  t9.literal(SUBSECTION),
  t9.literal(MEDIA_ROOM),
  // Permanent alias, not a transitional one: RTDB version history carries this
  // literal forever, so an old published version must still decode. Dispatch on
  // `resolveNodeType(type)` rather than matching this directly.
  t9.literal(LEGACY_VIDEO_ROOM)
]);
var node = t9.recursion(
  "node",
  () => t9.intersection([
    t9.record(t9.string, t9.unknown),
    t9.partial({ children: t9.array(node) })
  ])
);

// src/orgicore/types/appState.ts
var DEFAULT_APP_DEF = {
  type: "app",
  name: ""
};
var DEFAULT_APP_INSTANCE = {};
var isScope = (p) => {
  return "index" in p;
};
var defaultTraverseOptions = {
  include: isTrue,
  traverseChildren: isTrue,
  traverseParent: isTrue
};
var defaultShiftOptions = {
  ...defaultTraverseOptions,
  direction: "forward"
};
var createShiftOptions = (options) => {
  return {
    ...defaultShiftOptions,
    ...options
  };
};
var createTraverseOptions = (options) => {
  return {
    ...defaultTraverseOptions,
    ...options
  };
};

// src/orgicore/types/ui.ts
import * as t10 from "io-ts";
var moveNodeType = t10.union([
  t10.literal("before"),
  t10.literal("after"),
  t10.literal("into")
]);
var dropPosition = t10.union([
  t10.literal("top"),
  t10.literal("bottom"),
  t10.literal("center")
]);
var dropZone = t10.type({
  target: t10.string,
  action: moveNodeType,
  position: dropPosition
});
var dragAndDropState = t10.type({
  currentDropZone: t10.union([t10.undefined, dropZone]),
  src: t10.string
});

// src/orgicore/types/plugins.ts
import * as t11 from "io-ts";
var pluginRuntime = t11.keyof({ html: null, sandboxed: null });
var connectionProvider = t11.keyof({ spotify: null });
var pluginParameter = t11.partial({
  name: t11.string,
  type: t11.string,
  description: t11.string,
  exampleValue: t11.string,
  default: t11.string
});
var htmlPlugin = t11.intersection([
  t11.type({
    name: t11.string,
    parameters: t11.array(pluginParameter),
    htmlTemplate: t11.string
  }),
  t11.partial({
    icon: t11.string,
    color: t11.string,
    description: t11.string,
    image: fileUploadValue,
    /**
     * How the plugin's htmlTemplate is rendered.
     * - "html" (default / absent): formula-evaluated to an HTML string, injected via morphdom.
     * - "sandboxed": htmlTemplate is the literal document of an opaque-origin <iframe>; the
     *   platform talks to it over the typed postMessage bridge (see widgetDescriptor / WidgetHost).
     */
    runtime: pluginRuntime,
    /**
     * `runtime: "sandboxed"` only. Grants the widget's iframe `allow-same-origin` plus the
     * media permissions policy (`autoplay; encrypted-media; fullscreen; picture-in-picture`),
     * so it can host a third-party player SDK — YouTube's IFrame API being the case this
     * exists for. Default (absent) is the ordinary opaque-origin sandbox; this is opt-in per
     * plugin and never a blanket `WidgetHost` change.
     *
     * Why a capability at all: a player SDK talks to its own nested iframe by `postMessage`
     * with a real target origin, which an opaque-origin document has none of. It fails
     * SILENTLY — the SDK loads, its constructor returns a player, and the ready callback
     * simply never arrives with no error event.
     *
     * Why granting it is contained: `allow-scripts allow-same-origin` is a sandbox escape
     * only when the frame is same-origin as its EMBEDDER, which would let it reach in and
     * drop its own sandbox attribute. Widgets are served from the Storage origin, never the
     * app's, so the pair yields a document that is same-origin with a static file host and
     * still cross-origin to the app. `WidgetHost` asserts that rather than assuming it.
     */
    embedsThirdPartyMedia: t11.boolean,
    /**
     * `runtime: "sandboxed"` only. The connected accounts this widget asks to borrow (epic
     * #2432). `WidgetHost` then pushes the viewer's short-lived ACCESS token for each over the
     * bridge (`connection` message) and refreshes it while mounted; the refresh token never
     * crosses. Declaring is not granting: only widgets on `WIDGET_CONNECTION_ALLOWLIST` ever
     * receive a token, so an org config cannot route a user's account to an arbitrary widget.
     */
    connections: t11.array(connectionProvider),
    /**
     * `runtime: "sandboxed"` only. The Firebase uid of a plugin user whose OWN widget this is
     * (#2458): the iframe then loads `public/widgets/{widgetOwner}/{name}/index.html`, which only
     * that invited user can write, instead of the first-party widget of the same name. An owned
     * widget always gets the strict sandbox and never borrows a connection, whatever
     * `embedsThirdPartyMedia` / `connections` say: both are first-party-only.
     */
    widgetOwner: t11.string
  })
]);
var WIDGET_OWNER_MIN_CLIENT_VERSION = 5;
var WIDGET_OWNER_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
var PLUGIN_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
var WIDGET_NAME_PATTERN = /^[a-z][a-z0-9]{0,39}$/;

// src/orgicore/types/metamodel.ts
import * as t12 from "io-ts";
var partialField = objectTypeField;
var completeField = t12.intersection([
  t12.type({
    name: t12.string,
    type: t12.string
  }),
  objectTypeField
]);

// src/orgicore/types/parentRelation.ts
var readRelation = (ot) => {
  const child = ot.name;
  const rel = ot.parent;
  if (typeof child !== "string" || child.length === 0) return void 0;
  if (rel === null || typeof rel !== "object") return void 0;
  const { type: type14, idField } = rel;
  if (typeof type14 !== "string" || type14.length === 0) return void 0;
  if (typeof idField !== "string" || idField.length === 0) return void 0;
  return { child, parent: type14, idField };
};
var childrenOf = (objectTypes) => {
  var _a2, _b;
  const out = {};
  for (const ot of objectTypes) {
    const rel = readRelation(ot);
    if (!rel || rel.child === rel.parent) continue;
    ((_b = out[_a2 = rel.parent]) != null ? _b : out[_a2] = []).push({ type: rel.child, idField: rel.idField });
  }
  return out;
};
var parentCycles = (objectTypes) => {
  const parentByChild = /* @__PURE__ */ new Map();
  for (const ot of objectTypes) {
    const rel = readRelation(ot);
    if (rel) parentByChild.set(rel.child, rel.parent);
  }
  const cycles = [];
  const seenCycle = /* @__PURE__ */ new Set();
  const settled = /* @__PURE__ */ new Set();
  for (const start of parentByChild.keys()) {
    if (settled.has(start)) continue;
    const path = [];
    const onPath = /* @__PURE__ */ new Map();
    let node2 = start;
    while (node2 !== void 0 && !settled.has(node2)) {
      const at = onPath.get(node2);
      if (at !== void 0) {
        const chain = [...path.slice(at), node2];
        const ring = chain.slice(0, -1);
        const pivot = ring.indexOf([...ring].sort()[0]);
        const key = [...ring.slice(pivot), ...ring.slice(0, pivot)].join(">");
        if (!seenCycle.has(key)) {
          seenCycle.add(key);
          cycles.push(chain);
        }
        break;
      }
      onPath.set(node2, path.length);
      path.push(node2);
      node2 = parentByChild.get(node2);
    }
    for (const n of path) settled.add(n);
  }
  return cycles;
};
var extractObjectTypes = (config, out = [], seen = /* @__PURE__ */ new WeakSet()) => {
  if (Array.isArray(config)) {
    config.forEach((c) => extractObjectTypes(c, out, seen));
    return out;
  }
  if (config === null || typeof config !== "object") return out;
  if (seen.has(config)) return out;
  seen.add(config);
  const node2 = config;
  if (node2.type === "objectType" && typeof node2.name === "string") {
    out.push({ name: node2.name, parent: node2.parent });
  }
  for (const v of Object.values(node2)) extractObjectTypes(v, out, seen);
  return out;
};

// src/orgicore/types/writeAccess.ts
var typeWriteAccess = (config, typeName, seen = /* @__PURE__ */ new WeakSet()) => {
  if (config === null || typeof config !== "object") return void 0;
  if (seen.has(config)) return void 0;
  seen.add(config);
  if (Array.isArray(config)) {
    for (const c of config) {
      const found = typeWriteAccess(c, typeName, seen);
      if (found) return found;
    }
    return void 0;
  }
  const node2 = config;
  if (node2.type === "objectType" && node2.name === typeName) {
    const raw = node2.writeAccess;
    if (raw === void 0) return "anyone";
    return isKnownWriteAccess(raw) ? raw : "admins";
  }
  for (const v of Object.values(node2)) {
    const found = typeWriteAccess(v, typeName, seen);
    if (found) return found;
  }
  return void 0;
};
var isKnownWriteAccess = (v) => typeof v === "string" && ENUM_VALUES.objectTypeWriteAccess.includes(v);
var adminOnlyTypes = (config) => {
  const out = {};
  const seen = /* @__PURE__ */ new WeakSet();
  const walk8 = (node2) => {
    if (node2 === null || typeof node2 !== "object" || seen.has(node2)) return;
    seen.add(node2);
    if (Array.isArray(node2)) {
      node2.forEach(walk8);
      return;
    }
    const obj = node2;
    if (obj.type === "objectType" && typeof obj.name === "string") {
      const raw = obj.writeAccess;
      if (raw !== void 0 && (!isKnownWriteAccess(raw) || raw === "admins")) {
        out[obj.name] = "admins";
      }
      return;
    }
    Object.values(obj).forEach(walk8);
  };
  walk8(config);
  return out;
};
var writePolicyPath = (orgId) => `orgiggly/orgs/${orgId}/writePolicy`;

// src/orgicore/types/recordMeta.ts
var RECORD_META_FIELDS = new Set(
  ENUM_VALUES.recordMetaField
);
var tier = (...fields) => new Set(fields);
var RECORD_META_IDENTITY = tier("id");
var RECORD_META_AUDIT = tier(
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy"
);
var RECORD_META_AUDIT_UID = tier("createdByUid", "updatedByUid");
var RECORD_META_SYSTEM = tier("type", "_scope", "isDeleted");
var RECORD_META_PLACEMENT = tier("_parent");
var RECORD_META_TIERS = {
  identity: RECORD_META_IDENTITY,
  audit: RECORD_META_AUDIT,
  auditUid: RECORD_META_AUDIT_UID,
  system: RECORD_META_SYSTEM,
  placement: RECORD_META_PLACEMENT
};
var STAMPED_META_FIELDS = /* @__PURE__ */ new Set([
  ...RECORD_META_IDENTITY,
  ...RECORD_META_AUDIT,
  ...RECORD_META_AUDIT_UID
]);

// src/orgicore/builder/factories.ts
var nodeDefWithName = ([key, nodeDef2]) => {
  return {
    ...nodeDef2,
    name: key
  };
};
var isDefined = (value) => {
  return !!value[1];
};
var createNodeSet = (children2) => {
  return Object.entries(children2).filter(isDefined).map(nodeDefWithName);
};
var createLeafBuildFn = (type14, staticOptions, staticChildren) => (options = {}) => ({
  name: "",
  type: type14,
  ...options,
  ...staticOptions,
  children: staticChildren ? createNodeSet(staticChildren) : void 0
});
var createCompositeBuildFn = (type14, staticOptions) => {
  return (children2 = {}, options = {}) => {
    const explicit = createNodeSet(children2);
    const implicit = options.submitObjectType ? createImplicitSubmitNodes() : [];
    const all = [...explicit, ...implicit];
    return {
      name: "",
      type: type14,
      ...staticOptions,
      ...options,
      children: all.length > 0 ? all : void 0
    };
  };
};
var variable = createLeafBuildFn(VARIABLE);
var createImplicitSubmitNodes = () => {
  return createNodeSet({
    id: variable(),
    createdAt: variable(),
    createdBy: variable()
  });
};

// src/orgicore/builder/builder.generated.ts
var app = createCompositeBuildFn(APP);
var appNode = createCompositeBuildFn(APP_NODE);
var tabGroup = createCompositeBuildFn(APP_NODE, {
  variant: "tabGroup"
});
var tab = createCompositeBuildFn(APP_NODE, {
  variant: "tab"
});
var folder = createCompositeBuildFn(APP_NODE, {
  variant: "folder"
});
var screen = createCompositeBuildFn(SCREEN);
var subsection = createCompositeBuildFn(SUBSECTION);
var form = createCompositeBuildFn(FORM);
var flow2 = createCompositeBuildFn(FLOW);
var repeat = createCompositeBuildFn(REPEAT, {
  isRepeating: true
});
var flowStep = createLeafBuildFn(FLOW_STEP);
var aiQueryStep = createLeafBuildFn(FLOW_STEP, {
  variant: "aiQuery"
});
var aiChatStep = createLeafBuildFn(FLOW_STEP, {
  variant: "aiChat"
});
var fetchDataStep = createLeafBuildFn(FLOW_STEP, {
  variant: "fetchData"
});
var imageGenerationStep = createLeafBuildFn(FLOW_STEP, {
  variant: "imageGeneration"
});
var audioGenerationStep = createLeafBuildFn(FLOW_STEP, {
  variant: "audioGeneration"
});
var transcribeStep = createLeafBuildFn(FLOW_STEP, {
  variant: "transcribe"
});
var publishDataStep = createLeafBuildFn(FLOW_STEP, {
  variant: "publishData"
});
var scriptStep = createLeafBuildFn(FLOW_STEP, {
  variant: "script"
});
var callableButton = createLeafBuildFn(CALLABLE_BUTTON);
var textInput = createLeafBuildFn(TEXT_INPUT);
var numberInput = createLeafBuildFn(NUMBER_INPUT);
var booleanInput = createLeafBuildFn(BOOLEAN_INPUT);
var temporalInput = createLeafBuildFn(TEMPORAL_INPUT);
var selectInput = createLeafBuildFn(SELECT_INPUT);
var fileInput = createLeafBuildFn(FILE_INPUT);
var content = createLeafBuildFn(CONTENT);
var variable2 = createLeafBuildFn(VARIABLE);
var functionBuilder = createLeafBuildFn(FUNCTION);
var constBuilder = createLeafBuildFn(CONST);
var pluginLeaf = createLeafBuildFn(PLUGIN_LEAF);
var mediaRoom = createLeafBuildFn(MEDIA_ROOM);
var list = createCompositeBuildFn(SUBSECTION, {
  isRepeating: true,
  variant: "masterDetail"
});
var record5 = createCompositeBuildFn(SUBSECTION, {
  isRepeating: true,
  isRecord: true,
  variant: "masterDetail"
});
var tree = createLeafBuildFn(
  TREE,
  { isRepeating: true },
  {
    value: variable2(),
    expanded: variable2(),
    dragAndDropState: variable2(),
    isDrawerClosed: variable2(),
    leftPanelWidth: variable2()
  }
);
var b = {
  aiChatStep,
  aiQueryStep,
  app,
  appNode,
  audioGenerationStep,
  booleanInput,
  callableButton,
  const: constBuilder,
  content,
  fetchDataStep,
  fileInput,
  flow: flow2,
  flowStep,
  folder,
  /** @deprecated Legacy since 2026-10. Write inputs on a screen or flow screen, saved by `publishData` / `updateData` in a script instead. */
  form,
  function: functionBuilder,
  imageGenerationStep,
  list,
  mediaRoom,
  numberInput,
  pluginLeaf,
  publishDataStep,
  record: record5,
  repeat,
  screen,
  scriptStep,
  selectInput,
  subsection,
  tab,
  tabGroup,
  temporalInput,
  textInput,
  transcribeStep,
  tree,
  variable: variable2
};

// src/orgicore/selectors/appdef/basicSelectors.ts
import * as t13 from "io-ts";
import * as R4 from "remeda";
import { createSelector } from "reselect";

// src/orgicore/utils/string/strings.ts
import * as R3 from "remeda";
var toStemAndLeaf = (key, delimiter = ".") => {
  const i = key.lastIndexOf(delimiter);
  const stem = i === -1 ? "" : key.slice(0, i);
  const leaf = i === -1 ? key : key.slice(i + 1);
  return [stem, leaf];
};
var toHeadAndTail = (key, delimiter = ".") => {
  const i = key.indexOf(delimiter);
  const head2 = i === -1 ? key : key.slice(0, i);
  const tail = i === -1 ? "" : key.slice(i + 1);
  return [head2, tail];
};
var compactJoin = (parts, delimiter = ".") => {
  return parts.filter(Boolean).join(delimiter);
};
var splitCamelCase = (str) => {
  return R3.split(str, /(?=[A-Z])/).map((part) => R3.capitalize(part));
};
var camelCaseToLabel = (str) => {
  return splitCamelCase(str).join(" ");
};
var labelToVariableName = (label2) => {
  if (!label2) return "";
  const parts = label2.trim().split(/[^a-zA-Z0-9]+/g).filter(Boolean);
  if (parts.length === 0) return "";
  const [first, ...rest] = parts.map((p) => p.toLowerCase());
  const capitalizedRest = rest.map((p) => p[0].toUpperCase() + p.slice(1));
  return [first, ...capitalizedRest].join("");
};

// src/orgicore/selectors/helpers/nodeType.ts
var isInputFieldType = (nodeType2) => {
  switch (nodeType2) {
    case TEXT_INPUT:
    case NUMBER_INPUT:
    case BOOLEAN_INPUT:
    case TEMPORAL_INPUT:
    case SELECT_INPUT:
    case FILE_INPUT:
    case PLUGIN_LEAF:
      return true;
    default:
      return false;
  }
};
var isCompositeNodeType = (nodeType2) => {
  switch (nodeType2) {
    case APP:
    case APP_NODE:
    case SCREEN:
    case TREE:
    case FORM:
    case FLOW:
    case REPEAT:
    case SUBSECTION:
      return true;
    default:
      return false;
  }
};

// src/orgicore/selectors/helpers/nodeDef.ts
var enumerateAll = (nodeDef2) => {
  const result = [];
  result.push(nodeDef2);
  result.push(...getChildNodes(nodeDef2).flatMap(enumerateAll));
  return result;
};
var getChildNodes = (nodeDef2) => {
  return nodeDef2.children || [];
};
var getDescendantsInSameScopeWithName = (nodeDef2, descendantName) => {
  return getDescendantsInSameScope(nodeDef2).filter(
    (descendant) => descendant.name === descendantName
  );
};
var buildParentMap = (root) => {
  const map2 = /* @__PURE__ */ new Map();
  const traverse = (node2, parent) => {
    map2.set(node2, parent);
    for (const child of getChildNodes(node2)) {
      traverse(child, node2);
    }
  };
  traverse(root, null);
  return map2;
};
var getDescendantsInSameScope = (nodeDef2) => {
  if (isLeaf(nodeDef2)) {
    return [nodeDef2];
  }
  if (nodeDef2.isOwnScope || nodeDef2.isRepeating) {
    return [nodeDef2];
  }
  return [
    nodeDef2,
    ...getChildNodes(nodeDef2).flatMap(getDescendantsInSameScope)
  ];
};
var getUniqueKey = (byName, parentMap, node2) => {
  const itemsWithId = byName[node2.name];
  if (!itemsWithId || itemsWithId.length === 1) {
    return node2.name;
  }
  const parent = parentMap.get(node2);
  if (!parent) {
    throw new Error(`Node with name ${node2.name} has no parent`);
  }
  return `${getUniqueKey(byName, parentMap, parent)}$${node2.name}`;
};
var isComposite = (node2) => {
  return isCompositeNodeType(node2.type);
};
var isSubmitContextNode = (node2) => {
  return node2.type === "form" || !!node2.submitObjectType;
};
var isLeaf = (node2) => {
  return !isComposite(node2);
};
var isScoped = (node2) => {
  return !!(node2.isRepeating || node2.isOwnScope);
};

// src/orgicore/utils/selectors.ts
var stringProp0 = (a) => a;
var stringProp1 = (_a2, b2) => b2;
var stringProp2 = (_a2, _b, c) => c;
var optionalStringProp2 = (_a2, _b, c) => c;
var numberProp3 = (_a2, _b, _c, d) => d;
var optionalStringArrayProp2 = (_a2, _b, c) => c;

// src/orgicore/selectors/selectorHelpers.ts
var nodeProp = (_a2, b2) => b2;
var pointerPredicate2 = (_a2, _b, c) => c;
var traverseOptions2 = (_a2, _b, c) => c;
var directionProp1 = (_a2, b2) => b2;

// src/orgicore/selectors/appdef/basicSelectors.ts
var nodeDef = (nodeDef2) => nodeDef2;
var selectNodes = createSelector(
  nodeDef,
  enumerateAll
);
var selectCompute = createSelector(
  nodeDef,
  R4.prop("compute")
);
var selectName = createSelector(
  nodeDef,
  R4.prop("name")
);
var selectNodesByName = createSelector(selectNodes, (nodeDefs) => {
  return R4.groupBy(nodeDefs, R4.prop("name"));
});
var selectParentMap = createSelector(
  nodeDef,
  buildParentMap
);
var selectByUniqueName = createSelector(
  selectNodes,
  selectNodesByName,
  selectParentMap,
  (nodeDefs, byName, parentMap) => {
    return R4.indexBy(nodeDefs, (node2) => getUniqueKey(byName, parentMap, node2));
  }
);
var selectParentNodeDef = createSelector(
  selectParentMap,
  nodeProp,
  (parentMap, node2) => {
    var _a2;
    return (_a2 = parentMap.get(node2)) != null ? _a2 : void 0;
  }
);
var selectUniqueKey = createSelector(
  selectNodesByName,
  selectParentMap,
  nodeProp,
  (byName, parentMap, node2) => {
    return getUniqueKey(byName, parentMap, node2);
  }
);
var selectMatchingNodes = createSelector(
  selectNodesByName,
  selectByUniqueName,
  stringProp1,
  (byName, byUniqueName, nodeId) => {
    const a = byUniqueName[nodeId];
    if (a) {
      return [a];
    }
    return byName[nodeId] || [];
  }
);
var safeSelectByNodeId = createSelector(
  nodeDef,
  selectNodesByName,
  selectByUniqueName,
  stringProp1,
  (_a2, _b, item) => item,
  (nodeDef2, byName, byUniqueName, nodeId, disambiguationContext) => {
    const a = byUniqueName[nodeId];
    if (a) {
      return right(a);
    }
    const matches = byName[nodeId];
    if (!matches) {
      const [, tail] = toHeadAndTail(nodeId, "$");
      if (tail) {
        return safeSelectByNodeId(nodeDef2, tail, disambiguationContext);
      }
      return error(`Node with id ${nodeId} not found`);
    }
    if (matches.length === 1) {
      return right(matches[0]);
    }
    if (!disambiguationContext) {
      const child = getChildNodes(nodeDef2).find((c) => c.name === nodeId);
      if (child) {
        return right(child);
      }
      const descendants2 = getDescendantsInSameScopeWithName(nodeDef2, nodeId);
      if (descendants2.length === 1) {
        return right(descendants2[0]);
      }
      return error({
        matches: matches.map((m) => selectUniqueKey(nodeDef2, m))
      });
    }
    return safeSelectByNodeId(disambiguationContext, nodeId);
  }
);
var selectByNodeId = createSelector(
  safeSelectByNodeId,
  getRight
);
var selectDefaultLabel = createSelector(
  selectName,
  camelCaseToLabel
);
var createSelectPropDef = (targetCodec, propName, defaultValue) => createSelector(nodeDef, (node2) => {
  let value = node2[propName];
  if (value === void 0 && defaultValue !== void 0) {
    value = provide(defaultValue, node2);
  }
  return parsePropDef(value, targetCodec, propName);
});
var getNounLabel = createSelectPropDef(t13.string, "nounLabel", "Item");
var generateDefaultIterationLabelFormula = (node2) => {
  const { value, isFormula } = getNounLabel(node2);
  const nounLabelArg = isFormula ? value : `'${value}'`;
  return `${nounLabelArg} + ' ' + ($index + 1)`;
};
var selectDefaultIterationLabelFormula = createSelector(nodeDef, generateDefaultIterationLabelFormula);
var getMultiline = createSelectPropDef(optionalBoolean, "multiline");
var selectDatasourceObjTypes = createSelector(
  selectNodes,
  (_a2, _b) => _b,
  (nodes2, source) => {
    const types2 = nodes2.flatMap((node2) => {
      var _a2;
      if (dataOptions.is(node2.data) && node2.data.source === source) {
        return (_a2 = node2.data.dataType) != null ? _a2 : [];
      }
      return [];
    });
    return R4.unique(types2);
  }
);
var selectCallableDataNodes = createSelector(selectNodes, (nodes2) => {
  var _a2;
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const node2 of nodes2) {
    if (!dataOptions.is(node2.data) || node2.data.source !== "callable") {
      continue;
    }
    const typeName = node2.data.dataType;
    const callable = node2.data.callable;
    if (!typeName || !(callable == null ? void 0 : callable.functionName)) continue;
    const key = `${typeName}::${callable.functionName}::${(_a2 = callable.args) != null ? _a2 : ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      typeName,
      functionName: callable.functionName,
      ...callable.args ? { args: callable.args } : {}
    });
  }
  return out;
});
var scopedTypeNames = (node2) => {
  if (node2.type === TREE) {
    const nodeTypes = node2.nodeTypes;
    return nodeTypes && typeof nodeTypes === "object" ? Object.keys(nodeTypes) : [];
  }
  const dataType = dataOptions.is(node2.data) ? node2.data.dataType : void 0;
  return dataType ? [dataType] : [];
};
var selectScopedDatasourceObjTypes = createSelector(
  selectNodes,
  (_a2, _b) => _b,
  (nodes2, source) => {
    var _a2, _b;
    const seen = /* @__PURE__ */ new Map();
    for (const node2 of nodes2) {
      if (dataOptions.is(node2.data) && node2.data.source === source) {
        const scope = (_a2 = node2.data.scope) != null ? _a2 : "global";
        for (const typeName of scopedTypeNames(node2)) {
          const scopes = (_b = seen.get(typeName)) != null ? _b : /* @__PURE__ */ new Set();
          if (scope === "all") {
            scopes.add("global");
            scopes.add("user");
          } else {
            scopes.add(scope);
          }
          seen.set(typeName, scopes);
        }
      }
    }
    const result = [];
    for (const [typeName, scopes] of seen) {
      for (const scope of scopes) {
        result.push({ typeName, scope });
      }
    }
    return result;
  }
);

// src/orgicore/selectors/appstate/basicSelectors.ts
import * as R5 from "remeda";
import { createSelector as createSelector2 } from "reselect";
var emptyArray = [];
var selectAppState = (appState) => appState;
var selectOptionalAppInstance = (appState) => appState.instance;
var selectAppInstance = (appState) => {
  var _a2;
  return (_a2 = appState.instance) != null ? _a2 : DEFAULT_APP_INSTANCE;
};
var selectAppContext = (appState) => appState.context;
var selectAppDef = createSelector2(
  selectAppState,
  R5.prop("def")
);
var selectData = createSelector2(
  selectAppInstance,
  (ai) => {
    var _a2;
    return (_a2 = ai == null ? void 0 : ai.data) != null ? _a2 : emptyArray;
  }
);
var selectNavStack = createSelector2(
  selectAppInstance,
  (ai) => {
    var _a2;
    return (_a2 = ai == null ? void 0 : ai.navStack) != null ? _a2 : emptyArray;
  }
);
var selectLengths = createSelector2(
  selectAppInstance,
  (ai) => {
    var _a2;
    return (_a2 = ai == null ? void 0 : ai.lengths) != null ? _a2 : emptyArray;
  }
);
var selectDataByPointer = createSelector2(selectData, (data) => {
  return R5.indexBy(data, R5.prop("pointer"));
});
var selectLengthsByPointer = createSelector2(selectLengths, (lengths) => {
  return R5.indexBy(lengths, R5.prop("pointer"));
});
var selectCursor = createSelector2(
  selectAppInstance,
  (ai) => ai.cursor || ""
);
var selectCursorArgs = createSelector2(
  selectAppInstance,
  (ai) => ai.cursorArgs
);
var selectNavStackArgs = createSelector2(selectAppInstance, (ai) => {
  var _a2;
  return (_a2 = ai.navStackArgs) != null ? _a2 : emptyArray;
});
var DEFAULT_REPO = { objects: [] };
var selectRepo = createSelector2(
  selectAppState,
  (appState) => {
    var _a2;
    return (_a2 = appState.repo) != null ? _a2 : DEFAULT_REPO;
  }
);

// src/orgicore/selectors/appstate/scopeSelectors.ts
import * as R7 from "remeda";
import { createSelector as createSelector3 } from "reselect";

// src/orgicore/utils/tree.ts
var createPath = (node2) => {
  const { parent } = node2;
  return [...parent ? createPath(parent) : [], node2];
};

// src/orgicore/selectors/helpers/scope.ts
import * as R6 from "remeda";
var parseScopePart = (s) => {
  const r = s.match(/^([\w\s()$]+)(?:\[(\d+)\])?$/);
  if (!r) {
    throw new Error(`part "${s}" invalid syntax`);
  }
  const [, nodeId, index] = r;
  return { nodeId, index: index !== void 0 ? Number(index) : 0 };
};
var getScopeKey = (scope) => {
  return compactJoin(createScopePath(scope).map(getScopePartKey));
};
var createPointerKey = (pointer) => {
  const { scope, node: node2, isIteration } = pointer;
  const scopeKey = getScopeKey(scope);
  if (isIteration) {
    return scopeKey;
  }
  return compactJoin([scopeKey, selectUniqueKey(scope.node, node2)]);
};
var getPointerKeyOfScope = (scope) => {
  const { parent, node: node2 } = scope;
  if (!parent) {
    return "";
  }
  const nodeName = selectUniqueKey(parent.node, node2);
  return compactJoin([getScopeKey(parent), nodeName]);
};
var getScopePartKey = (scope) => {
  const { parent, node: node2, index } = scope;
  const nodeName = !parent ? "" : selectUniqueKey(parent.node, node2);
  return isScoped(node2) ? `${nodeName}[${index}]` : nodeName;
};
var createGlobalScope = (node2) => ({
  node: node2,
  index: 0,
  key: ""
});
var isGlobalScope = (scope) => {
  return scope.parent === void 0;
};
var createScopePath = (scope) => {
  const { parent } = scope;
  return [...parent ? createScopePath(parent) : [], scope];
};
var isOwningScope = (s1, s2) => {
  return s1.parent === s2.parent && s1.node === s2.node;
};
var getIndex = (s1, path) => {
  const scope = path.find((s) => isOwningScope(s1, s));
  return scope ? scope.index : -1;
};
var createScopeKeyWithIndexAt = (s1, path, index) => {
  const newPath = path.map((s) => {
    if (isOwningScope(s1, s)) {
      return { ...s, index };
    }
    return s;
  });
  return compactJoin(newPath.map(getScopePartKey));
};
var getPointerKey = R6.prop("key");
var getBestPointerKey = (scope, nodeName) => {
  const { parent, node: node2, key } = scope;
  const nodes2 = selectMatchingNodes(node2, nodeName);
  if (nodes2.length) {
    if (node2 === nodes2[0]) {
      return { key: compactJoin([parent == null ? void 0 : parent.key, nodeName]), isDeclared: true };
    }
    return { key: compactJoin([key, nodeName]), isDeclared: true };
  }
  if (nodeName.includes("$")) {
    const baseName = nodeName.slice(nodeName.lastIndexOf("$") + 1);
    const baseNodes = selectMatchingNodes(node2, baseName);
    if (baseNodes.length) {
      return { key: compactJoin([key, nodeName]), isDeclared: true };
    }
  }
  return { key: compactJoin([key, nodeName]), isDeclared: false };
};
var convertVarNameToUniqueNodeName = (root, context, varName) => {
  const node2 = findMatchingNode(root, context, varName);
  return node2 ? selectUniqueKey(root, node2) : varName;
};
var findMatchingNode = (root, context, varName) => {
  const matches = selectMatchingNodes(root, varName);
  if (matches.length === 0) {
    return void 0;
  }
  if (matches.length === 1) {
    return matches[0];
  }
  return getMatchingNode(root, context, varName);
};
var getMatchingNode = (root, context, varName) => {
  const matches = selectMatchingNodes(context, varName);
  if (matches.length) {
    return matches[0];
  }
  const parentNodeDef = selectParentNodeDef(root, context);
  if (!parentNodeDef) {
    return void 0;
  }
  return getMatchingNode(root, parentNodeDef, varName);
};

// src/orgicore/selectors/appstate/scopeSelectors.ts
var parseScopeKey = (appDef, key) => {
  if (key === "") {
    return right(createGlobalScope(appDef));
  }
  const [stem, leaf] = toStemAndLeaf(key);
  const { nodeId, index } = parseScopePart(leaf);
  const r1 = safeSelectScope({ def: appDef }, stem);
  if (isLeft(r1)) {
    return r1;
  }
  const parent = r1.right;
  const r2 = safeSelectByNodeId(appDef, nodeId, parent.node);
  if (isLeft(r2)) {
    return r2;
  }
  const node2 = r2.right;
  const scope = { parent, node: node2, index, key };
  const canonicalKey = getScopeKey(scope);
  if (canonicalKey !== key) {
    const r = safeSelectScope({ def: appDef }, canonicalKey);
    if (isLeft(r)) {
      return right(scope);
    }
    return r;
  }
  return right(scope);
};
var parsePointerKey = (appDef, key) => {
  const [stem, leaf] = toStemAndLeaf(key);
  const isIteration = leaf.endsWith("]");
  const scopeKey = isIteration ? key : stem;
  const r1 = safeSelectScope({ def: appDef }, scopeKey);
  if (isLeft(r1)) {
    return r1;
  }
  const scope = r1.right;
  const r = isIteration ? right(scope.node) : safeSelectByNodeId(appDef, leaf, scope.node);
  if (isLeft(r)) {
    return r;
  }
  const node2 = r.right;
  const pointer = { scope, node: node2, isIteration, key };
  const canonicalKey = createPointerKey(pointer);
  if (canonicalKey !== key) {
    const r2 = safeSelectPointer({ def: appDef }, canonicalKey);
    if (isLeft(r2)) {
      return right(pointer);
    }
    return r2;
  }
  return right(pointer);
};
var safeSelectScope = createSelector3(selectAppDef, stringProp1, parseScopeKey);
var selectScope = createSelector3(
  safeSelectScope,
  (r) => {
    if (isLeft(r)) {
      throw new Error(`Invalid scope key: ${r.left}`);
    }
    return r.right;
  }
);
var safeSelectPointer = createSelector3(selectAppDef, stringProp1, parsePointerKey);
var selectPointer = createSelector3(
  safeSelectPointer,
  (r) => {
    if (isLeft(r)) {
      throw new Error(`Invalid pointer key: ${r.left}`);
    }
    return r.right;
  }
);
var selectPointer2 = createSelector3(
  selectAppDef,
  stringProp2,
  (def, key) => {
    return selectPointer({ def }, key);
  }
);
var selectPointerNode = createSelector3(
  selectPointer,
  R7.prop("node")
);
var selectRootPointer = createSelector3(
  selectAppState,
  () => "",
  selectPointer
);
var selectScopePath = createSelector3(
  selectScope,
  createPath
);
var selectScopeIndex = createSelector3(
  selectAppState,
  selectScope,
  stringProp2,
  (def, s1, prop22) => getIndex(s1, selectScopePath(def, prop22))
);
var selectScopeKeyWithIndexAt = createSelector3(
  selectAppState,
  selectScope,
  stringProp2,
  numberProp3,
  (def, s1, prop22, index) => {
    const path = selectScopePath(def, prop22);
    return createScopeKeyWithIndexAt(s1, path, index);
  }
);
var selectBestPointerKey = createSelector3(selectScope, stringProp2, getBestPointerKey);
var selectDeclaredPointerKey = (appState, fromPointerKey, name) => {
  const r = safeSelectPointer(appState, fromPointerKey);
  if (isLeft(r)) return void 0;
  let scope = r.right.scope;
  while (scope) {
    const { key, isDeclared } = selectBestPointerKey(appState, scope.key, name);
    if (isDeclared) {
      return isLeft(safeSelectPointer(appState, key)) ? void 0 : key;
    }
    scope = scope.parent;
  }
  return void 0;
};

// src/orgicore/selectors/appstate/evaluation/createSelectValue.ts
import * as t17 from "io-ts";
import { createSelector as createSelector7 } from "reselect";

// src/orgicore/utils/diag.ts
var diag = (entry) => {
  var _a2;
  const g = globalThis;
  (_a2 = g.__orgigglyDiag) == null ? void 0 : _a2.push({ at: Date.now(), ...entry });
};

// src/orgicore/utils/react/tools.ts
import { useContext } from "react";
import { createContext } from "react";
var createContextTools = (name, defaultValue) => {
  const Context = createContext(defaultValue);
  const Provider2 = Context.Provider;
  const useHook = () => {
    const context = useContext(Context);
    if (context === void 0) {
      if (defaultValue) {
        return defaultValue;
      }
      throw new Error(`${name}Context not found`);
    }
    return context;
  };
  return [Provider2, useHook, Context];
};

// src/orgicore/utils/string/fuzzyMatch.ts
var levenshteinDistance = (a, b2) => {
  const aLen = a.length;
  const bLen = b2.length;
  if (aLen === 0) return bLen;
  if (bLen === 0) return aLen;
  const matrix = [];
  for (let i = 0; i <= aLen; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= bLen; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= aLen; i++) {
    for (let j = 1; j <= bLen; j++) {
      const cost = a[i - 1] === b2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        // deletion
        matrix[i][j - 1] + 1,
        // insertion
        matrix[i - 1][j - 1] + cost
        // substitution
      );
    }
  }
  return matrix[aLen][bLen];
};
var normalizeForComparison = (str) => {
  return str.toLowerCase().trim().replace(/^the\s+/i, "").replace(/\s+/g, " ");
};
var fuzzyMatchAnswer = (userAnswer, correctAnswer, options = {}) => {
  if (userAnswer.length === 0) {
    return "none";
  }
  const { correctThreshold = 1, closeThreshold = 3 } = options;
  const normalizedUser = normalizeForComparison(userAnswer);
  const normalizedCorrect = normalizeForComparison(correctAnswer);
  if (normalizedUser === normalizedCorrect) {
    return "correct";
  }
  if (normalizedUser.length === 0) {
    return "wrong";
  }
  const distance = levenshteinDistance(normalizedUser, normalizedCorrect);
  const lengthFactor = Math.max(1, Math.floor(normalizedCorrect.length / 10));
  const scaledCorrectThreshold = correctThreshold * lengthFactor;
  const scaledCloseThreshold = closeThreshold * lengthFactor;
  if (distance <= scaledCorrectThreshold) {
    return "correct";
  }
  if (distance <= scaledCloseThreshold) {
    return "close";
  }
  return "wrong";
};
var fuzzyMatchScore = (userAnswer, correctAnswer) => {
  const normalizedUser = normalizeForComparison(userAnswer);
  const normalizedCorrect = normalizeForComparison(correctAnswer);
  if (normalizedUser === normalizedCorrect) {
    return 100;
  }
  if (normalizedUser.length === 0) {
    return 0;
  }
  const distance = levenshteinDistance(normalizedUser, normalizedCorrect);
  const maxLength = Math.max(normalizedUser.length, normalizedCorrect.length);
  const similarity = Math.max(0, (maxLength - distance) / maxLength);
  return Math.round(similarity * 100);
};

// src/orgicore/utils/cast.ts
import * as t14 from "io-ts";
var castToString = (value) => {
  if (t14.string.is(value)) {
    return value;
  }
  return void 0;
};

// src/orgicore/utils/coerce.ts
import { Temporal as Temporal5 } from "@js-temporal/polyfill";
import * as t15 from "io-ts";

// src/orgicore/utils/evaluation/formula/safeHtml.ts
var SafeHtml = class {
  constructor(html) {
    this.html = html;
  }
  toString() {
    return this.html;
  }
  toJSON() {
    return this.html;
  }
};
var isSafeHtml = (value) => value instanceof SafeHtml;
var escapeHtmlString = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// src/orgicore/utils/coerce.ts
var isPlainDate2 = (value) => value instanceof Temporal5.PlainDate;
var isZonedDateTime = (value) => value instanceof Temporal5.ZonedDateTime;
var isInstant = (value) => value instanceof Temporal5.Instant;
var coerceToString = (value) => {
  if (t15.string.is(value)) {
    return value;
  }
  if (isSafeHtml(value)) {
    return value.html;
  }
  if (isPlainDate2(value)) {
    return value.toString();
  }
  if (isZonedDateTime(value)) {
    return value.toPlainDate().toString();
  }
  if (isInstant(value)) {
    return value.toZonedDateTimeISO("UTC").toPlainDate().toString();
  }
  if (Array.isArray(value)) {
    return value.join("");
  }
  return value === void 0 ? "" : String(value);
};

// src/orgicore/utils/decode.ts
import * as t16 from "io-ts";
var decode = (r, codec, ctx = "no context") => {
  if (isRight(r)) {
    return decodeUnknown(r.right, codec, ctx);
  }
  return r;
};
var decodeUnknown = (r, codec, ctx = "no context") => {
  if (codec.is(r)) {
    return right(r);
  }
  return error(
    `Type mismatch: ${JSON.stringify(r)} is not a ${codec.name} (${ctx})`
  );
};
var decodeWithStringCoercion = (r, codec, ctx = "no context") => {
  if (!isRight(r)) {
    return r;
  }
  const value = r.right;
  if (codec.is(value)) {
    return right(value);
  }
  const codecAny = codec;
  if (codecAny === t16.string) {
    return right(coerceToString(value));
  }
  if (codecAny === optionalString && value !== void 0) {
    return right(coerceToString(value));
  }
  return error(
    `Type mismatch: ${JSON.stringify(value)} is not a ${codec.name} (${ctx})`
  );
};

// src/orgicore/utils/props.ts
var unwrapProps = (props) => {
  const values = {};
  const errors = [];
  for (const key in props) {
    const item = props[key];
    if (isLeft(item)) {
      errors.push(item.left);
    } else {
      values[key] = item.right;
    }
  }
  return errors.length ? { left: errors } : { right: values };
};
var getErrors = (props) => {
  const r = {};
  for (const key in props) {
    const item = props[key];
    if (isLeft(item)) {
      r[key] = item.left;
    }
  }
  return r;
};
var getUnwrappedProps = (props) => {
  const r = unwrapProps(props);
  if (isLeft(r)) {
    throw new Error(r.left.join("\n"));
  }
  return r.right;
};
var isAnyError = (props) => {
  return Object.values(props).some(isLeft);
};

// src/orgicore/utils/persist/helpers.ts
var notFoundError = (key) => left(`NOT_FOUND: Key "${key}" not found`);
var serializationError = (message) => left(`SERIALIZATION_ERROR: ${message}`);
var storageError = (message) => left(`STORAGE_ERROR: ${message}`);
var unknownError = (message) => left(`UNKNOWN_ERROR: ${message}`);
var success2 = (value) => right(value);
var safeStringify = (obj) => {
  try {
    return right(JSON.stringify(obj));
  } catch (error2) {
    return left(
      `SERIALIZATION_ERROR: Failed to serialize object: ${error2 instanceof Error ? error2.message : String(error2)}`
    );
  }
};
var safeParse = (json) => {
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed === "object" && parsed !== null) {
      return right(parsed);
    }
    return left("SERIALIZATION_ERROR: Parsed value is not an object");
  } catch (error2) {
    return left(
      `SERIALIZATION_ERROR: Failed to parse JSON: ${error2 instanceof Error ? error2.message : String(error2)}`
    );
  }
};
var isPersistErrorType = (error2, type14) => {
  return isLeft(error2) && error2.left.startsWith(type14);
};

// src/orgicore/utils/persist/debouncedPersistence.ts
var DebouncedPersistence = class {
  constructor(provider) {
    this.pendingSaves = /* @__PURE__ */ new Map();
    this.persistence = provider;
  }
  async save(key, value, debounceDelay = 300) {
    const existing = this.pendingSaves.get(key);
    if (existing) {
      clearTimeout(existing.timeout);
    }
    return new Promise((resolve2) => {
      const timeout = setTimeout(async () => {
        this.pendingSaves.delete(key);
        const result = await this.persistence.save(key, value);
        resolve2(result);
      }, debounceDelay);
      this.pendingSaves.set(key, { timeout, value });
    });
  }
  /**
   * Immediate save (bypasses debouncing)
   */
  async saveImmediate(key, value) {
    const existing = this.pendingSaves.get(key);
    if (existing) {
      clearTimeout(existing.timeout);
      this.pendingSaves.delete(key);
    }
    return this.persistence.save(key, value);
  }
  /**
   * Flush all pending saves immediately
   */
  async flushPendingSaves() {
    const pendingSaveEntries = Array.from(this.pendingSaves.entries());
    this.pendingSaves.forEach(({ timeout }) => clearTimeout(timeout));
    this.pendingSaves.clear();
    await Promise.all(
      pendingSaveEntries.map(
        ([key, { value }]) => this.persistence.save(key, value)
      )
    );
  }
  /**
   * Flush pending saves for a specific key
   */
  async flushPendingSave(key) {
    const pending = this.pendingSaves.get(key);
    if (!pending) {
      return false;
    }
    clearTimeout(pending.timeout);
    this.pendingSaves.delete(key);
    await this.persistence.save(key, pending.value);
    return true;
  }
  /**
   * Get the number of pending saves
   */
  getPendingSaveCount() {
    return this.pendingSaves.size;
  }
  /**
   * Check if a specific key has a pending save
   */
  hasPendingSave(key) {
    return this.pendingSaves.has(key);
  }
  /**
   * Get all pending save keys
   */
  getPendingSaveKeys() {
    return Array.from(this.pendingSaves.keys());
  }
  // Pass through all other methods unchanged
  async find(key, defaultValue) {
    return this.persistence.find(key, defaultValue);
  }
  async remove(key) {
    const existing = this.pendingSaves.get(key);
    if (existing) {
      clearTimeout(existing.timeout);
      this.pendingSaves.delete(key);
    }
    return this.persistence.remove(key);
  }
  async list() {
    return this.persistence.list();
  }
  async clear() {
    this.pendingSaves.forEach(({ timeout }) => clearTimeout(timeout));
    this.pendingSaves.clear();
    return this.persistence.clear();
  }
  /**
   * Clean up all timers (useful for component unmounting)
   */
  destroy() {
    this.pendingSaves.forEach(({ timeout }) => clearTimeout(timeout));
    this.pendingSaves.clear();
  }
};

// src/orgicore/utils/persist/namespacedPersistence.ts
var createNamespacedPersistence = (keyPrefix, persistence) => {
  const getFullKey = (key) => {
    return `${keyPrefix}${key}`;
  };
  return {
    save: (key, value, debounceDelay) => persistence.save(getFullKey(key), value, debounceDelay),
    saveImmediate: (key, value) => persistence.saveImmediate(getFullKey(key), value),
    find: (key) => persistence.find(getFullKey(key)),
    remove: (key) => persistence.remove(getFullKey(key)),
    list: async () => {
      try {
        const result = await persistence.list();
        if (isLeft(result)) {
          return result;
        }
        return right(
          result.right.filter((key) => key.startsWith(keyPrefix)).map((key) => key.substring(keyPrefix.length))
        );
      } catch (error2) {
        return left(
          `STORAGE_ERROR: Failed to list localStorage keys: ${error2 instanceof Error ? error2.message : String(error2)}`
        );
      }
    },
    clear: async () => {
      const result = await persistence.list();
      if (isLeft(result)) {
        return result;
      }
      try {
        const keys = result.right;
        keys.filter((key) => key.startsWith(keyPrefix)).forEach((key) => localStorage.removeItem(key));
        return right(void 0);
      } catch (error2) {
        return left(
          `STORAGE_ERROR: Failed to clear localStorage: ${error2 instanceof Error ? error2.message : String(error2)}`
        );
      }
    }
  };
};

// src/orgicore/utils/persist/hooks.ts
import { useEffect, useMemo } from "react";
var [PersistenceProvider, usePersistence] = createContextTools("persistence");
var [PersistenceKeyProvider, usePersistenceKey] = createContextTools("PersistenceKey", "");
var useCreateDebouncedPersistence = (provider) => {
  const debouncedProvider = useMemo(
    () => new DebouncedPersistence(provider),
    [provider]
  );
  useEffect(() => {
    return () => {
      debouncedProvider.destroy();
    };
  }, [debouncedProvider]);
  useAutoFlush(debouncedProvider);
  return debouncedProvider;
};
var useAutoFlush = (debouncedProvider) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        debouncedProvider.flushPendingSaves();
      }
    };
    const handleBeforeUnload = () => {
      debouncedProvider.flushPendingSaves();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [debouncedProvider]);
};

// src/orgicore/utils/evaluation/types.ts
var isFormulaParseError = (error2) => {
  return typeof error2 === "object" && error2 !== null && "message" in error2 && "source" in error2;
};
var isFormulaEvaluationError = (error2) => {
  return typeof error2 === "object" && error2 !== null && "message" in error2 && "failingExpression" in error2;
};
function assertFormulaEvaluationError(error2) {
  if (!isFormulaEvaluationError(error2)) {
    throw new Error("Expected a formula evaluation error");
  }
}
var getSource = (e) => {
  return isFormulaParseError(e) ? e.source : getRaw(e.failingExpression);
};
var getEvalErrorMessage = (e) => {
  if (typeof e === "string") {
    return e;
  }
  return e.message;
};

// src/orgicore/utils/evaluation/validateEnumValue.ts
var ValueOrFormulaError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "ValueOrFormulaError";
  }
};
var validateEnumValue = (value, allowed, fieldLabel) => {
  if (typeof value !== "string" || !allowed.includes(value)) {
    throw new ValueOrFormulaError(
      `${fieldLabel} resolved to ${JSON.stringify(value)}, expected one of: ${allowed.join(", ")}.`
    );
  }
  return value;
};

// src/orgicore/utils/evaluation/formula/index.ts
import * as R8 from "remeda";
import { createSelector as createSelector4 } from "reselect";

// src/orgicore/utils/secure/idGen.ts
var createSeededRandom = (seed) => {
  let state = seed;
  return () => {
    state = state * 1103515245 + 12345 & 2147483647;
    return state / 2147483647;
  };
};
var ALPHANUMERIC_CHARS = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
var UNBIASED_BYTE_CEILING = 248;
var cryptoRandomIndex = () => {
  const webCrypto = globalThis.crypto;
  if (!(webCrypto == null ? void 0 : webCrypto.getRandomValues)) {
    return Math.floor(Math.random() * ALPHANUMERIC_CHARS.length);
  }
  const buf = new Uint8Array(1);
  for (; ; ) {
    webCrypto.getRandomValues(buf);
    const byte = buf[0];
    if (byte < UNBIASED_BYTE_CEILING) return byte % ALPHANUMERIC_CHARS.length;
  }
};
var generateId = (options = {}) => {
  const { length = 8, seed } = options;
  if (length <= 0) {
    throw new Error("Length must be greater than 0");
  }
  const nextIndex = seed !== void 0 ? (() => {
    const random = createSeededRandom(seed);
    return () => Math.floor(random() * ALPHANUMERIC_CHARS.length);
  })() : cryptoRandomIndex;
  let result = "";
  for (let i = 0; i < length; i++) {
    result += ALPHANUMERIC_CHARS[nextIndex()];
  }
  return result;
};

// src/orgicore/utils/evaluation/formula/handRank.ts
var RANK_CODES = "23456789TJQKA";
var SUIT_CODES = "shdc";
var CARD_ID_RE = /^card_[shdc][2-9TJQKA]$/;
var HAND_CATEGORY_NAMES = [
  "high card",
  "pair",
  "two pair",
  "three of a kind",
  "straight",
  "flush",
  "full house",
  "four of a kind",
  "straight flush"
];
var parseCardId = (value) => {
  if (typeof value !== "string" || !CARD_ID_RE.test(value)) {
    throw new Error(
      `handRank: bad card id ${JSON.stringify(value)} \u2014 expected card_{s|h|d|c}{2-9|T|J|Q|K|A}`
    );
  }
  return {
    id: value,
    suit: SUIT_CODES.indexOf(value[5]),
    rank: RANK_CODES.indexOf(value[6]) + 2
  };
};
var RANK_WORDS = {
  2: "two",
  3: "three",
  4: "four",
  5: "five",
  6: "six",
  7: "seven",
  8: "eight",
  9: "nine",
  10: "ten",
  11: "jack",
  12: "queen",
  13: "king",
  14: "ace"
};
var plural = (rank) => rank === 6 ? "sixes" : `${RANK_WORDS[rank]}s`;
var encodeRank = (category, tiebreak) => {
  var _a2;
  let out = category;
  for (let i = 0; i < 5; i++) out = out * 15 + ((_a2 = tiebreak[i]) != null ? _a2 : 0);
  return out;
};
var evaluate5 = (cards) => {
  var _a2;
  const byRankDesc = [...cards].sort((a, b2) => b2.rank - a.rank);
  const counts = /* @__PURE__ */ new Map();
  for (const c of byRankDesc) counts.set(c.rank, ((_a2 = counts.get(c.rank)) != null ? _a2 : 0) + 1);
  const groups = [...counts.entries()].sort((a, b2) => b2[1] - a[1] || b2[0] - a[0]);
  const isFlush = cards.every((c) => c.suit === cards[0].suit);
  const distinct = groups.map(([r]) => r);
  let straightHigh = 0;
  if (distinct.length === 5) {
    if (distinct[0] - distinct[4] === 4) straightHigh = distinct[0];
    else if (distinct.join(",") === "14,5,4,3,2") straightHigh = 5;
  }
  const orderByGroups = () => groups.flatMap(([r]) => byRankDesc.filter((c) => c.rank === r));
  if (straightHigh > 0) {
    const ordered = straightHigh === 5 ? [...byRankDesc.slice(1), byRankDesc[0]] : byRankDesc;
    const category2 = isFlush ? 8 : 4;
    return { category: category2, rank: encodeRank(category2, [straightHigh]), tiebreak: [straightHigh], ordered };
  }
  const shape = groups.map(([, n]) => n).join("");
  let category;
  if (shape === "41") category = 7;
  else if (shape === "32") category = 6;
  else if (isFlush) category = 5;
  else if (shape === "311") category = 3;
  else if (shape === "221") category = 2;
  else if (shape === "2111") category = 1;
  else category = 0;
  const tiebreak = distinct;
  return { category, rank: encodeRank(category, tiebreak), tiebreak, ordered: orderByGroups() };
};
var labelFor = (five) => {
  const t30 = five.tiebreak;
  switch (five.category) {
    case 8:
      return t30[0] === 14 ? "royal flush" : `straight flush, ${RANK_WORDS[t30[0]]} high`;
    case 7:
      return `four of a kind, ${plural(t30[0])}`;
    case 6:
      return `full house, ${plural(t30[0])} over ${plural(t30[1])}`;
    case 5:
      return `flush, ${RANK_WORDS[t30[0]]} high`;
    case 4:
      return `straight, ${RANK_WORDS[t30[0]]} high`;
    case 3:
      return `three of a kind, ${plural(t30[0])}`;
    case 2:
      return `two pair, ${plural(t30[0])} and ${plural(t30[1])}`;
    case 1:
      return `pair of ${plural(t30[0])}`;
    default:
      return `${RANK_WORDS[t30[0]]}-high`;
  }
};
var fiveSubsets = (cards) => {
  const n = cards.length;
  if (n === 5) return [cards];
  if (n === 6) return cards.map((_, a) => cards.filter((_2, i) => i !== a));
  const out = [];
  for (let a = 0; a < n; a++)
    for (let b2 = a + 1; b2 < n; b2++)
      out.push(cards.filter((_, i) => i !== a && i !== b2));
  return out;
};
var handRank = (cardIds) => {
  if (!Array.isArray(cardIds) || cardIds.length < 5 || cardIds.length > 7) {
    const got = Array.isArray(cardIds) ? `${cardIds.length} cards` : typeof cardIds;
    throw new Error(`handRank: expected an array of 5\u20137 card ids, got ${got}`);
  }
  const cards = cardIds.map(parseCardId);
  const seen = /* @__PURE__ */ new Set();
  for (const c of cards) {
    if (seen.has(c.id)) throw new Error(`handRank: duplicate card id ${c.id}`);
    seen.add(c.id);
  }
  let best;
  for (const subset of fiveSubsets(cards)) {
    const five = evaluate5(subset);
    if (!best || five.rank > best.rank) best = five;
  }
  const result = best != null ? best : evaluate5(cards.slice(0, 5));
  return {
    category: result.category,
    categoryName: HAND_CATEGORY_NAMES[result.category],
    rank: result.rank,
    best: result.ordered.map((c) => c.id),
    label: labelFor(result)
  };
};

// src/orgicore/utils/evaluation/formula/arrowSugar.ts
var HOF_SLOTS = {
  map: ["it", "i"],
  filter: ["it", "i"],
  forEach: ["it", "i", "arr"],
  reduce: ["acc", "it", "i"],
  times: ["i"],
  every: ["it", "i"],
  some: ["it", "i"],
  find: ["it", "i"],
  findIndex: ["it", "i"],
  count: ["it", "i"],
  groupBy: ["it", "i"],
  sumBy: ["it", "i"]
};
var getArrowParamNames = (arrow) => {
  if (!arrow.params) return null;
  return arrow.params.filter(isIdentifier).map((p) => p.name);
};

// src/orgicore/utils/visual/meters.ts
var ACCENT = "var(--og-accent, currentColor)";
var TRACK = "var(--og-border, rgba(127,127,127,.25))";
var TEXT = "var(--og-text, currentColor)";
var isOpts = (v) => v != null && typeof v === "object" && !Array.isArray(v);
var optsOf = (opts) => isOpts(opts) ? opts : {};
var toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : void 0;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : void 0;
  }
  return void 0;
};
var positive = (v, fallback) => {
  const n = toNumber(v);
  return n != null && n > 0 ? n : fallback;
};
var fraction = (amount, max) => {
  const a = toNumber(amount);
  const m = max === void 0 ? 1 : toNumber(max);
  if (a == null || m == null || m <= 0) return 0;
  return Math.min(1, Math.max(0, a / m));
};
var UNSAFE_COLOR = /[;{}<>"'\\]|url\(|expression\(/i;
var cleanColor = (v) => {
  if (typeof v !== "string") return void 0;
  const c = v.trim();
  return c !== "" && c.length <= 100 && !UNSAFE_COLOR.test(c) ? c : void 0;
};
var colorOf = (v, fallback) => {
  var _a2;
  return (_a2 = cleanColor(v)) != null ? _a2 : fallback;
};
var safeCssColor = (value, fallback) => {
  var _a2, _b;
  return (_b = (_a2 = cleanColor(value)) != null ? _a2 : cleanColor(fallback)) != null ? _b : "";
};
var round2 = (n) => Math.round(n * 100) / 100;
var ariaProgress = (pct) => `role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"`;
var progressBarMarkup = (amount, opts) => {
  const o = optsOf(opts);
  const pct = Math.round(fraction(amount, o.max) * 100);
  const height = positive(o.height, 8);
  const radius = positive(o.radius, height / 2);
  const color = colorOf(o.color, ACCENT);
  const track = colorOf(o.track, TRACK);
  return `<div ${ariaProgress(pct)} style="height:${height}px;border-radius:${radius}px;background:${track};overflow:hidden;"><div style="width:${pct}%;height:100%;background:${color};"></div></div>`;
};
var progressRingMarkup = (amount, opts) => {
  const o = optsOf(opts);
  const f = fraction(amount, o.max);
  const pct = Math.round(f * 100);
  const size = positive(o.size, 64);
  const stroke = Math.min(positive(o.stroke, 8), size / 2);
  const color = colorOf(o.color, ACCENT);
  const track = colorOf(o.track, TRACK);
  const caption = o.caption == null ? `${pct}%` : escapeHtmlString(String(o.caption));
  const c = size / 2;
  const r = round2((size - stroke) / 2);
  const circumference = round2(2 * Math.PI * r);
  const circle = (style, extra = "") => `<circle cx="${c}" cy="${c}" r="${r}"${extra} style="fill:none;stroke-width:${stroke};${style}"/>`;
  const fill = f > 0 ? circle(
    `stroke:${color};stroke-linecap:round;stroke-dasharray:${round2(f * circumference)} ${circumference};`,
    ` transform="rotate(-90 ${c} ${c})"`
  ) : "";
  return `<svg ${ariaProgress(pct)} width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block;">` + circle(`stroke:${track};`) + fill + `<text x="${c}" y="${c}" text-anchor="middle" dominant-baseline="central" style="fill:${TEXT};font-size:${round2(size * 0.24)}px;font-weight:600;">${caption}</text></svg>`;
};
var sparklineMarkup = (values, opts) => {
  if (!Array.isArray(values)) return "";
  const nums = values.flatMap((v) => {
    const n = toNumber(v);
    return n == null ? [] : [n];
  });
  if (nums.length < 2) return "";
  const o = optsOf(opts);
  const fixedWidth = toNumber(o.width);
  const width = positive(o.width, 120);
  const height = positive(o.height, 32);
  const strokeWidth = positive(o.strokeWidth, 2);
  const color = colorOf(o.color, ACCENT);
  const min = Math.min(...nums);
  const range = Math.max(...nums) - min;
  const pad = strokeWidth / 2;
  const points = nums.map((n, i) => {
    const x = round2(i * width / (nums.length - 1));
    const y = range === 0 ? height / 2 : round2(pad + (1 - (n - min) / range) * (height - 2 * pad));
    return `${x},${y}`;
  }).join(" ");
  const label2 = `trend: ${nums[0]} to ${nums[nums.length - 1]}`;
  return `<svg role="img" aria-label="${label2}" width="${fixedWidth != null && fixedWidth > 0 ? width : "100%"}" height="${height}" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" style="display:block;overflow:visible;"><polyline points="${points}" vector-effect="non-scaling-stroke" style="fill:none;stroke:${color};stroke-width:${strokeWidth};stroke-linejoin:round;stroke-linecap:round;"/></svg>`;
};

// src/orgicore/utils/evaluation/formula/index.ts
var parseFormulaFn = (formula) => {
  var _a2, _b, _c;
  try {
    return right(parseExpressionWithMeta(formula));
  } catch (e) {
    const parseError2 = e;
    const message = (_a2 = parseError2.message) != null ? _a2 : "Parse error";
    const description = (_b = parseError2.description) != null ? _b : message;
    const index = (_c = parseError2.index) != null ? _c : -1;
    return left({
      message,
      description,
      source: formula,
      index
    });
  }
};
var parseFormula = createSelector4(
  stringProp0,
  parseFormulaFn
);
var isOrgFunction = (value) => R8.isPlainObject(value) && "__orgFunction" in value && value.__orgFunction === true;
var orgFunctionCallStack = /* @__PURE__ */ new Set();
var createEvalMemo = () => ({
  map: /* @__PURE__ */ new Map(),
  ids: /* @__PURE__ */ new WeakMap(),
  next: { n: 0 }
});
var memoId = (memo, v) => {
  if (v === null) return "null";
  const t30 = typeof v;
  if (t30 === "object" || t30 === "function") {
    let id = memo.ids.get(v);
    if (id === void 0) {
      id = memo.next.n++;
      memo.ids.set(v, id);
    }
    return "#" + id;
  }
  return t30 + ":" + String(v);
};
var flushEvalMemo = (memo) => {
  memo == null ? void 0 : memo.map.clear();
};
var isClosure = (value) => R8.isPlainObject(value) && "__closure" in value && value.__closure === true;
var makeClosure = (arrow, ctx) => {
  var _a2;
  return {
    __closure: true,
    params: (_a2 = getArrowParamNames(arrow)) != null ? _a2 : [],
    body: arrow.body,
    ctx
  };
};
var closureCallStack = /* @__PURE__ */ new Set();
var invokeClosure = (c, args) => {
  if (closureCallStack.has(c)) return right(void 0);
  const scope = {};
  for (let k = 0; k < c.params.length; k++) {
    scope[c.params[k]] = args[k];
  }
  closureCallStack.add(c);
  try {
    return evalExpr(c.body, childCtx(c.ctx, scope), emptyLookupContext);
  } finally {
    closureCallStack.delete(c);
  }
};
var invokeOrgFunction = (fn, args, ctx, site) => {
  const computeRaw = fn.compute;
  const formula = typeof computeRaw === "string" && computeRaw.startsWith("@") ? computeRaw.slice(1).trim() : String(computeRaw != null ? computeRaw : "");
  if (orgFunctionCallStack.has(fn.key)) {
    return right(void 0);
  }
  const paramNames = parameterNames(fn.parameters);
  const scope = {};
  for (let i = 0; i < paramNames.length; i++) {
    scope[paramNames[i]] = args[i];
  }
  const parsed = parseFormula(formula);
  if (isLeft(parsed)) {
    return left(createFormulaError(`Error in function body: ${formula}`, site));
  }
  const memo = fn.memoSafe === true ? ctx.memo : void 0;
  let memoKey;
  if (memo) {
    const dataVal = ctx.dc("$data", emptyLookupContext);
    memoKey = fn.key + "|" + memoId(memo, dataVal) + "|" + args.map((a) => memoId(memo, a)).join(",");
    const cached = memo.map.get(memoKey);
    if (cached !== void 0) {
      return cached;
    }
  }
  orgFunctionCallStack.add(fn.key);
  try {
    const bodyCtx = childCtx(ctx, scope);
    bodyCtx.letBound = void 0;
    const result = evalExpr(parsed.right, bodyCtx, emptyLookupContext);
    if (memo && memoKey !== void 0) {
      memo.map.set(memoKey, result);
    }
    return result;
  } finally {
    orgFunctionCallStack.delete(fn.key);
  }
};
var evalArgs = (args, ctx, lc) => {
  const out = [];
  for (const arg of args) {
    const argResult = evalExpr(arg, ctx, lc);
    if (isLeft(argResult)) return argResult;
    out.push(argResult.right);
  }
  return right(out);
};
var createFormulaError = (message, ast) => ({
  message,
  failingExpression: ast
});
var getCalleeName = (callee) => {
  if (isIdentifier(callee)) {
    return callee.name;
  }
  if (isMemberExpression(callee)) {
    const prop5 = callee.property;
    if (isIdentifier(prop5)) {
      return prop5.name;
    }
  }
  return "function";
};
var assignTarget = (target, value, ctx, lc) => {
  var _a2;
  const targetWithMeta = target;
  flushEvalMemo(ctx.memo);
  if (isIdentifier(target)) {
    if ((_a2 = ctx.letBound) == null ? void 0 : _a2.has(target.name)) {
      return left(
        createFormulaError(
          `Cannot reassign let binding '${target.name}'`,
          targetWithMeta
        )
      );
    }
    if (!ctx.setter) {
      return left(
        createFormulaError(
          "Cannot assign: no mutable context available",
          targetWithMeta
        )
      );
    }
    ctx.setter(target.name, value);
    return right(void 0);
  }
  if (isMemberExpression(target)) {
    const objectResult = evalExpr(target.object, ctx, lc);
    if (isLeft(objectResult)) return objectResult;
    let obj = objectResult.right;
    if (obj == null || typeof obj !== "object" && typeof obj !== "function") {
      if (isIdentifier(target.object)) {
        if (!ctx.setter) {
          return left(
            createFormulaError(
              "Cannot auto-create object: no mutable context available",
              targetWithMeta
            )
          );
        }
        obj = {};
        ctx.setter(target.object.name, obj);
      } else {
        return left(
          createFormulaError(
            "Cannot assign property of non-object",
            targetWithMeta
          )
        );
      }
    }
    const mlcResult = getMemberLookupContext(target, ctx.dc);
    if (isLeft(mlcResult)) return mlcResult;
    const property = mlcResult.right.property;
    obj[property] = value;
    return right(void 0);
  }
  return left(
    createFormulaError(
      "Invalid assignment target: must be identifier or property access",
      targetWithMeta
    )
  );
};
var evalExpr = (ast, ctx, lc = emptyLookupContext) => {
  var _a2, _b, _c;
  const astWithMeta = ast;
  if (isLiteral(ast)) {
    return right(ast.value);
  }
  if (isBinaryExpression(ast)) {
    if (ast.operator === "->") {
      return left(
        createFormulaError(
          "`->` is only valid inside a `match { }` block (as `condition -> expression`)",
          ast
        )
      );
    }
    if (ast.operator === ";") {
      const leftResult2 = evalExpr(ast.left, ctx, lc);
      if (isLeft(leftResult2)) return leftResult2;
      return evalExpr(ast.right, ctx, lc);
    }
    if (ast.operator === "=") {
      const rightResult2 = evalExpr(ast.right, ctx, lc);
      if (isLeft(rightResult2)) return rightResult2;
      const value = rightResult2.right;
      const assignResult = assignTarget(ast.left, value, ctx, lc);
      if (isLeft(assignResult)) return assignResult;
      return right(value);
    }
    if (ast.operator === "&&" || ast.operator === "||" || ast.operator === "??") {
      const leftResult2 = evalExpr(ast.left, ctx, lc);
      if (isLeft(leftResult2)) return leftResult2;
      const l = leftResult2.right;
      const decided = ast.operator === "&&" ? !l : ast.operator === "||" ? !!l : l != null;
      return decided ? leftResult2 : evalExpr(ast.right, ctx, lc);
    }
    const leftResult = evalExpr(ast.left, ctx, lc);
    if (isLeft(leftResult)) return leftResult;
    const rightResult = evalExpr(ast.right, ctx, lc);
    if (isLeft(rightResult)) return rightResult;
    try {
      return right(
        getBinaryOperation(ast.operator)(leftResult.right, rightResult.right)
      );
    } catch (e) {
      return left(createFormulaError(String(e), ast));
    }
  }
  if (isUnaryExpression(ast)) {
    const argResult = evalExpr(ast.argument, ctx, lc);
    if (isLeft(argResult)) return argResult;
    try {
      return right(getUnaryOperation(ast.operator)(argResult.right));
    } catch (e) {
      return left(createFormulaError(String(e), ast));
    }
  }
  if (isConditionalExpression(ast)) {
    const testResult = evalExpr(ast.test, ctx, lc);
    if (isLeft(testResult)) return testResult;
    return testResult.right ? evalExpr(ast.consequent, ctx, lc) : evalExpr(ast.alternate, ctx, lc);
  }
  if (isLetExpression(ast)) {
    if ((_a2 = ctx.letBound) == null ? void 0 : _a2.has(ast.id)) {
      return left(
        createFormulaError(`Cannot redeclare let binding '${ast.id}'`, ast)
      );
    }
    const initResult = evalExpr(ast.init, ctx, lc);
    if (isLeft(initResult)) return initResult;
    if (!ast.body) return right(initResult.right);
    return evalExpr(ast.body, letScope(ctx, ast.id, initResult.right), lc);
  }
  if (isMatchExpression(ast)) {
    let scrutinee;
    const hasScrutinee = ast.scrutinee !== void 0;
    if (ast.scrutinee !== void 0) {
      const scrutineeResult = evalExpr(ast.scrutinee, ctx, lc);
      if (isLeft(scrutineeResult)) return scrutineeResult;
      scrutinee = scrutineeResult.right;
    }
    for (const arm of ast.arms) {
      if (isIdentifier(arm.test) && arm.test.name === "_") {
        return evalExpr(arm.consequent, ctx, lc);
      }
      const testResult = evalExpr(arm.test, ctx, lc);
      if (isLeft(testResult)) return testResult;
      const wins = hasScrutinee ? looseEquals(scrutinee, testResult.right) : Boolean(testResult.right);
      if (wins) {
        return evalExpr(arm.consequent, ctx, lc);
      }
    }
    return right(void 0);
  }
  if (isCallExpression(ast)) {
    const callLc = { ...lc, memberLookupContexts: [] };
    if (isMemberExpression(ast.callee)) {
      const objectResult = evalExpr(
        ast.callee.object,
        ctx,
        setIsCall(callLc, false)
      );
      if (isLeft(objectResult)) return objectResult;
      const obj = objectResult.right;
      const mlcResult = getMemberLookupContext(ast.callee, ctx.dc);
      if (isLeft(mlcResult)) return mlcResult;
      const methodName = mlcResult.right.property;
      const fnFromContext = lookupIdentifier(
        methodName,
        ctx.dc,
        setIsCall(callLc, true)
      );
      if (isAstHof(fnFromContext)) {
        if (ast.arguments.length > 0) {
          return fnFromContext(obj, ast.arguments[0], ctx);
        }
        return right(void 0);
      }
      if (fnFromContext === reduceFn) {
        if (ast.arguments.length > 0) {
          const initialResult = ast.arguments.length > 1 ? evalExpr(ast.arguments[1], ctx, callLc) : right(void 0);
          if (isLeft(initialResult)) return initialResult;
          return reduceFn(obj, ast.arguments[0], ctx, initialResult.right);
        }
        return right(void 0);
      }
      let method;
      if (obj != null) {
        method = obj[methodName];
        if (isClosure(method)) {
          const argsResult = evalArgs(ast.arguments, ctx, callLc);
          if (isLeft(argsResult)) return argsResult;
          return invokeClosure(method, argsResult.right);
        }
        if (typeof method === "function") {
          const boundMethod = method.bind(
            obj
          );
          const evaluatedArgs = [];
          for (const arg of ast.arguments) {
            const argResult = evalExpr(arg, ctx, callLc);
            if (isLeft(argResult)) return argResult;
            evaluatedArgs.push(argResult.right);
          }
          try {
            return right(boundMethod(...evaluatedArgs));
          } catch (e) {
            const errorMessage2 = e instanceof Error ? e.message : String(e);
            return left(
              createFormulaError(
                `Error invoking ${methodName}(): ${errorMessage2}`,
                astWithMeta
              )
            );
          }
        }
      }
      if (typeof fnFromContext === "function") {
        const evaluatedArgs = [obj];
        for (const arg of ast.arguments) {
          const argResult = evalExpr(arg, ctx, callLc);
          if (isLeft(argResult)) return argResult;
          evaluatedArgs.push(argResult.right);
        }
        try {
          return right(fnFromContext(...evaluatedArgs));
        } catch (e) {
          const errorMessage2 = e instanceof Error ? e.message : String(e);
          return left(
            createFormulaError(
              `Error invoking ${methodName}(): ${errorMessage2}`,
              astWithMeta
            )
          );
        }
      }
      return right(void 0);
    }
    const fnResult = evalExpr(ast.callee, ctx, setIsCall(callLc, true));
    if (isLeft(fnResult)) return fnResult;
    const fn = fnResult.right;
    if (isClosure(fn)) {
      const argsResult = evalArgs(ast.arguments, ctx, callLc);
      if (isLeft(argsResult)) return argsResult;
      return invokeClosure(fn, argsResult.right);
    }
    if (isOrgFunction(fn)) {
      if (orgFunctionCallStack.has(fn.key)) {
        return right(void 0);
      }
      const argsResult = evalArgs(ast.arguments, ctx, callLc);
      if (isLeft(argsResult)) return argsResult;
      return invokeOrgFunction(fn, argsResult.right, ctx, ast);
    }
    if (typeof fn === "function") {
      if (isAstHof(fn)) {
        const arg1Result = evalExpr(ast.arguments[0], ctx, callLc);
        if (isLeft(arg1Result)) return arg1Result;
        return fn(arg1Result.right, ast.arguments[1], ctx);
      }
      if (fn === reduceFn) {
        const arg1Result = evalExpr(ast.arguments[0], ctx, callLc);
        if (isLeft(arg1Result)) return arg1Result;
        const initialResult = ast.arguments.length > 2 ? evalExpr(ast.arguments[2], ctx, callLc) : right(void 0);
        if (isLeft(initialResult)) return initialResult;
        return reduceFn(
          arg1Result.right,
          ast.arguments[1],
          ctx,
          initialResult.right
        );
      }
      const evaluatedArgs = [];
      for (const arg of ast.arguments) {
        const argResult = evalExpr(arg, ctx, callLc);
        if (isLeft(argResult)) return argResult;
        evaluatedArgs.push(argResult.right);
      }
      try {
        return right(fn(...evaluatedArgs));
      } catch (e) {
        const calleeName4 = getCalleeName(ast.callee);
        const errorMessage2 = e instanceof Error ? e.message : String(e);
        return left(
          createFormulaError(
            `Error invoking ${calleeName4}(): ${errorMessage2}`,
            astWithMeta
          )
        );
      }
    }
    return right(void 0);
  }
  if (isThisExpression(ast)) {
    return right(ctx.thisContext);
  }
  if (isIdentifier(ast)) {
    return right(lookupIdentifier(ast.name, ctx.dc, lc));
  }
  if (isMemberExpression(ast)) {
    const { object } = ast;
    const mlcResult = getMemberLookupContext(ast, ctx.dc);
    if (isLeft(mlcResult)) return mlcResult;
    const mlc = mlcResult.right;
    const eObjectResult = evalExpr(
      object,
      ctx,
      prependMemberLookupContext(setIsCall(lc, false), mlc)
    );
    if (isLeft(eObjectResult)) return eObjectResult;
    const eObject = eObjectResult.right;
    if (R8.isArray(eObject)) {
      if (mlc.type === "computed") {
        const numProp = Number(mlc.property);
        if (!isNaN(numProp)) {
          return right(eObject[numProp]);
        }
        return right(void 0);
      }
      return right(
        eObject.map(
          (item) => R8.isPlainObject(item) ? item[mlc.property] : void 0
        )
      );
    }
    if (R8.isPlainObject(eObject)) {
      return right(eObject[mlc.property]);
    }
    if (R8.isString(eObject) && mlc.type !== "computed" && mlc.property === "length") {
      return right(eObject.length);
    }
    if (eObject != null && typeof eObject === "object") {
      const prop5 = eObject[mlc.property];
      if (typeof prop5 === "function") {
        return right(prop5.bind(eObject));
      }
      return right(prop5);
    }
    return right(void 0);
  }
  if (isTemplateLiteral(ast)) {
    const parts = [];
    const { quasis, expressions } = ast;
    for (let i = 0; i < quasis.length; i++) {
      const elem = quasis[i];
      const rawVal = (_c = typeof elem.value === "string" ? elem.value : (_b = elem.value) == null ? void 0 : _b.cooked) != null ? _c : "";
      parts.push(rawVal);
      if (i < expressions.length) {
        const evResult = evalExpr(expressions[i], ctx, lc);
        if (isLeft(evResult)) return evResult;
        parts.push(coerceToString(evResult.right));
      }
    }
    return right(parts.join(""));
  }
  if (isTaggedTemplateExpression(ast)) {
    const tagFnResult = evalExpr(
      ast.tag,
      ctx,
      setIsCall(lc, true)
    );
    if (isLeft(tagFnResult)) return tagFnResult;
    const tagFn = tagFnResult.right;
    if (typeof tagFn !== "function") {
      return right(void 0);
    }
    const { quasis, expressions } = ast.quasi;
    const cooked = quasis.map(
      (q) => {
        var _a3, _b2;
        return (_b2 = typeof q.value === "string" ? q.value : (_a3 = q.value) == null ? void 0 : _a3.cooked) != null ? _b2 : "";
      }
    );
    const strings = Object.assign([...cooked], { raw: cooked.slice() });
    const args = [strings];
    for (const e of expressions) {
      const eResult = evalExpr(e, ctx, lc);
      if (isLeft(eResult)) return eResult;
      args.push(eResult.right);
    }
    return right(tagFn(...args));
  }
  if (isArrayExpression(ast)) {
    const elements = [];
    for (const element2 of ast.elements) {
      if (element2 === null) {
        elements.push(void 0);
      } else if (isSpreadElement(element2)) {
        const spreadResult = evalExpr(element2.argument, ctx, lc);
        if (isLeft(spreadResult)) return spreadResult;
        const spreadVal = spreadResult.right;
        if (R8.isArray(spreadVal)) {
          elements.push(...spreadVal);
        }
      } else {
        const elemResult = evalExpr(element2, ctx, lc);
        if (isLeft(elemResult)) return elemResult;
        elements.push(elemResult.right);
      }
    }
    return right(elements);
  }
  if (isObjectExpression(ast)) {
    const obj = {};
    const { properties } = ast;
    for (const prop5 of properties) {
      if (isSpreadElement(prop5)) {
        const spreadResult = evalExpr(prop5.argument, ctx, lc);
        if (isLeft(spreadResult)) return spreadResult;
        const spreadVal = spreadResult.right;
        if (R8.isPlainObject(spreadVal)) {
          Object.assign(obj, spreadVal);
        } else if (R8.isArray(spreadVal)) {
          spreadVal.forEach((v, i) => {
            obj[String(i)] = v;
          });
        }
        continue;
      }
      const keyNode = prop5.key;
      const computed = Boolean(prop5.computed);
      let key;
      if (!computed && isIdentifier(keyNode)) {
        key = keyNode.name;
      } else if (!computed && isLiteral(keyNode)) {
        key = String(keyNode.value);
      } else {
        const keyResult = evalExpr(keyNode, ctx, lc);
        if (isLeft(keyResult)) return keyResult;
        key = String(keyResult.right);
      }
      if (prop5.shorthand) {
        obj[key] = lookupIdentifier(key, ctx.dc, lc);
      } else {
        const valueResult = evalExpr(
          prop5.value,
          ctx,
          lc
        );
        if (isLeft(valueResult)) return valueResult;
        obj[key] = valueResult.right;
      }
    }
    return right(obj);
  }
  if (isArrowExpression(ast)) {
    return right(makeClosure(ast, ctx));
  }
  return left(
    createFormulaError(`Unsupported expression: ${ast.type}`, astWithMeta)
  );
};
var getMemberLookupContext = (ast, dc) => {
  const { computed } = ast;
  const propResult = getMemberPropName(ast, dc);
  if (isLeft(propResult)) return propResult;
  return right({
    type: computed ? "computed" : "static",
    property: propResult.right
  });
};
var getMemberPropName = (ast, dc) => {
  const { property, computed } = ast;
  if (!computed && isIdentifier(property)) {
    return right(property.name);
  }
  const result = evalExpr(property, { dc, setter: void 0 });
  if (isLeft(result)) return result;
  return right(String(result.right));
};
var lookupIdentifier = (name, ctx, lookupContext) => {
  const value = ctx(name, lookupContext);
  if (value !== void 0) {
    if (!lookupContext.isCall || R8.isFunction(value) || isOrgFunction(value) || // A closure is a plain object, so without this it would be discarded in
    // call position and fall through to the builtin/HOF lookup — making
    // `double = (x) => x * 2; double(21)` silently `undefined`.
    isClosure(value)) {
      return value;
    }
  }
  if (lookupContext.isCall) {
    const hof = AST_HOFS.get(name);
    if (hof) {
      return hof;
    }
    if (name === "reduce") {
      return reduceFn;
    }
  }
  if (!(name in FUNCTIONS)) return void 0;
  if (!lookupContext.isCall && CALL_ONLY_FUNCTIONS.has(name)) {
    return void 0;
  }
  return FUNCTIONS[name];
};
var CALL_ONLY_FUNCTIONS = /* @__PURE__ */ new Set([
  "html",
  // #2313/#2314: plausible node names (a `values` input, a `first` variable).
  // (`count` is an AST HOF, which resolves only in call position anyway.)
  "keys",
  "values",
  "entries",
  "first",
  "last",
  // #2647: a node called `sparkline` is plausible.
  "progressBar",
  "progressRing",
  "sparkline"
]);
var letScope = (ctx, id, value) => {
  var _a2;
  const child = childCtx(ctx, { [id]: value });
  child.letBound = /* @__PURE__ */ new Set([...(_a2 = ctx.letBound) != null ? _a2 : [], id]);
  return child;
};
var childCtx = (ctx, scope) => {
  let letBound = ctx.letBound;
  if (letBound) {
    const shadowed = Object.keys(scope).filter((k) => letBound.has(k));
    if (shadowed.length > 0) {
      const next = new Set(letBound);
      for (const k of shadowed) next.delete(k);
      letBound = next;
    }
  }
  return {
    dc: pushScope(ctx.dc, scope),
    setter: ctx.setter,
    thisContext: ctx.thisContext,
    memo: ctx.memo,
    asyncHosts: ctx.asyncHosts,
    syncHosts: ctx.syncHosts,
    letBound
  };
};
var resolveCallback = (expr, ctx, hofName) => {
  var _a2;
  const slots = (_a2 = HOF_SLOTS[hofName]) != null ? _a2 : [];
  if (isArrowExpression(expr)) {
    const closure = makeClosure(expr, ctx);
    return (slotValues) => invokeClosure(closure, slotValues);
  }
  if (isIdentifier(expr) && !slots.includes(expr.name)) {
    const referenced = evalExpr(
      expr,
      ctx,
      setIsCall(emptyLookupContext, true)
    );
    if (!isLeft(referenced) && isClosure(referenced.right)) {
      const closure = referenced.right;
      return (slotValues) => invokeClosure(closure, slotValues);
    }
    if (!isLeft(referenced) && isOrgFunction(referenced.right)) {
      const fn = referenced.right;
      return (slotValues) => invokeOrgFunction(fn, slotValues, ctx, expr);
    }
  }
  return (slotValues, legacyExtra) => {
    const scope = {};
    for (let k = 0; k < slots.length; k++) {
      scope[slots[k]] = slotValues[k];
    }
    if (legacyExtra) Object.assign(scope, legacyExtra);
    return evalExpr(expr, childCtx(ctx, scope), emptyLookupContext);
  };
};
var forEachFn = (value, templateExpr, ctx) => {
  if (!R8.isString(value)) return right("");
  const invoke = resolveCallback(templateExpr, ctx, "forEach");
  const results = [];
  const lines = value.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const result = invoke([lines[i], i, lines]);
    if (isLeft(result)) return result;
    results.push(String(result.right));
  }
  return right(results.join(""));
};
var filterFn = (arr, predicateExpr, ctx) => {
  if (!R8.isArray(arr)) return right([]);
  const invoke = resolveCallback(predicateExpr, ctx, "filter");
  const results = [];
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i];
    const result = invoke([it, i]);
    if (isLeft(result)) return result;
    if (result.right) {
      results.push(it);
    }
  }
  return right(results);
};
var mapFn = (arr, mapExpr, ctx) => {
  if (!R8.isArray(arr)) return right([]);
  const invoke = resolveCallback(mapExpr, ctx, "map");
  const results = [];
  for (let i = 0; i < arr.length; i++) {
    const result = invoke([arr[i], i]);
    if (isLeft(result)) return result;
    results.push(result.right);
  }
  return right(results);
};
var reduceFn = (arr, reduceExpr, ctx, initial) => {
  if (!R8.isArray(arr)) return right(initial);
  const invoke = resolveCallback(reduceExpr, ctx, "reduce");
  let acc = initial;
  for (let i = 0; i < arr.length; i++) {
    const result = invoke([acc, arr[i], i]);
    if (isLeft(result)) return result;
    acc = result.right;
  }
  return right(acc);
};
var predicateHof = (arr, predicateExpr, ctx, hofName, decide, emptyResult) => {
  if (!R8.isArray(arr)) return right(emptyResult);
  const invoke = resolveCallback(predicateExpr, ctx, hofName);
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i];
    const result = invoke([it, i]);
    if (isLeft(result)) return result;
    const decision = decide(Boolean(result.right), it, i);
    if (decision) return right(decision.value);
  }
  return right(emptyResult);
};
var everyFn = (arr, predicateExpr, ctx) => predicateHof(
  arr,
  predicateExpr,
  ctx,
  "every",
  (truthy) => truthy ? void 0 : { value: false },
  true
);
var someFn = (arr, predicateExpr, ctx) => predicateHof(
  arr,
  predicateExpr,
  ctx,
  "some",
  (truthy) => truthy ? { value: true } : void 0,
  false
);
var findFn = (arr, predicateExpr, ctx) => predicateHof(
  arr,
  predicateExpr,
  ctx,
  "find",
  (truthy, it) => truthy ? { value: it } : void 0,
  void 0
);
var findIndexFn = (arr, predicateExpr, ctx) => predicateHof(
  arr,
  predicateExpr,
  ctx,
  "findIndex",
  (truthy, _it, i) => truthy ? { value: i } : void 0,
  -1
);
var resolveKeyCallback = (expr, ctx, hofName) => {
  if (!expr) return void 0;
  if (isLiteral(expr) && R8.isString(expr.value)) {
    const field2 = expr.value;
    return ([it]) => right(R8.isPlainObject(it) ? it[field2] : void 0);
  }
  return resolveCallback(expr, ctx, hofName);
};
var countFn = (arr, predicateExpr, ctx) => {
  if (!R8.isArray(arr)) return right(0);
  if (!predicateExpr) return right(arr.length);
  const invoke = resolveCallback(predicateExpr, ctx, "count");
  let n = 0;
  for (let i = 0; i < arr.length; i++) {
    const result = invoke([arr[i], i]);
    if (isLeft(result)) return result;
    if (result.right) n++;
  }
  return right(n);
};
var groupByFn = (arr, keyExpr, ctx) => {
  var _a2;
  const invoke = resolveKeyCallback(keyExpr, ctx, "groupBy");
  if (!R8.isArray(arr) || !invoke) return right({});
  const groups = {};
  for (let i = 0; i < arr.length; i++) {
    const result = invoke([arr[i], i]);
    if (isLeft(result)) return result;
    if (result.right == null) continue;
    const key = coerceToString(result.right);
    ((_a2 = groups[key]) != null ? _a2 : groups[key] = []).push(arr[i]);
  }
  return right(groups);
};
var sumByFn = (arr, valueExpr, ctx) => {
  if (!R8.isArray(arr)) return right(0);
  const invoke = resolveKeyCallback(valueExpr, ctx, "sumBy");
  let total = 0;
  for (let i = 0; i < arr.length; i++) {
    const result = invoke ? invoke([arr[i], i]) : right(arr[i]);
    if (isLeft(result)) return result;
    if (result.right != null) total += Number(result.right);
  }
  return right(total);
};
var timesFn = (count, templateExpr, ctx) => {
  const n = Number(count);
  if (!R8.isNumber(n) || isNaN(n) || n < 0) return right([]);
  const invoke = resolveCallback(templateExpr, ctx, "times");
  const results = [];
  for (let i = 0; i < n; i++) {
    const result = invoke([i], { it: i });
    if (isLeft(result)) return result;
    results.push(result.right);
  }
  return right(results);
};
var AST_HOFS = /* @__PURE__ */ new Map([
  ["forEach", forEachFn],
  ["filter", filterFn],
  ["map", mapFn],
  ["times", timesFn],
  ["every", everyFn],
  ["some", someFn],
  ["find", findFn],
  ["findIndex", findIndexFn],
  ["count", countFn],
  ["groupBy", groupByFn],
  ["sumBy", sumByFn]
]);
var AST_HOF_FNS = new Set(AST_HOFS.values());
var isAstHof = (fn) => typeof fn === "function" && AST_HOF_FNS.has(fn);
var CSS_AT_RULES = /* @__PURE__ */ new Set([
  "media",
  "keyframes",
  "import",
  "charset",
  "supports",
  "layer",
  "container",
  "page",
  "namespace",
  "property",
  "scope",
  "font",
  "counter",
  "starting"
]);
var AT_REF_PATTERN = /(?<!\w)@([a-zA-Z_]\w*)/g;
var evaluate = (formula, ctx = {}, thisContext) => {
  const parseResult = parseFormula(formula);
  if (isLeft(parseResult)) {
    return parseResult;
  }
  const mutableCtx = createMutableContext(ctx);
  if (thisContext !== void 0) {
    mutableCtx.thisContext = thisContext;
  }
  return evalExpr(parseResult.right, mutableCtx, emptyLookupContext);
};
var evaluateAndThrow = (formula, ctx = {}, thisContext) => {
  return getRight(evaluate(formula, ctx, thisContext));
};
var emptyLookupContext = {
  memberLookupContexts: [],
  isCall: false
};
var prependMemberLookupContext = (lc, mlc) => {
  return {
    ...lc,
    memberLookupContexts: [mlc, ...lc.memberLookupContexts]
  };
};
var setIsCall = (lc, isCall) => {
  return {
    ...lc,
    isCall
  };
};
var getBinaryOperation = (operator) => {
  const op = BINARY_OPERATORS[operator];
  if (!op) {
    return () => `<<UNSUPPORTED_OPERATOR: ${operator}>>`;
  }
  return op;
};
var getUnaryOperation = (operator) => {
  const op = UNARY_OPERATORS[operator];
  if (!op) {
    return () => `<<UNSUPPORTED_OPERATOR: ${operator}>>`;
  }
  return op;
};
var equals = (a, b2) => {
  if (isPlainDate(a) && isPlainDate(b2)) {
    return a.equals(b2);
  }
  return a === b2;
};
var notEquals = (a, b2) => !equals(a, b2);
var looseEquals = (a, b2) => {
  if (a == null && b2 == null) return true;
  return equals(a, b2);
};
var looseNotEquals = (a, b2) => !looseEquals(a, b2);
var isOrderable = (a, b2) => a != null && b2 != null;
var BINARY_OPERATORS = {
  "+": (a, b2) => {
    if (isPlainDate(a) && R8.isNumber(b2)) {
      return a.add({ days: b2 });
    }
    if (R8.isNumber(a) && isPlainDate(b2)) {
      return b2.add({ days: a });
    }
    if (R8.isString(a) || R8.isString(b2)) {
      return (a == null ? "" : String(a)) + (b2 == null ? "" : String(b2));
    }
    if (isPlainDate(a) || isPlainDate(b2)) {
      return String(a) + String(b2);
    }
    return Number(a) + Number(b2);
  },
  "-": (a, b2) => {
    if (isPlainDate(a) && R8.isNumber(b2)) {
      return a.subtract({ days: b2 });
    }
    if (isPlainDate(a) && isPlainDate(b2)) {
      return a.since(b2).total("days");
    }
    return Number(a) - Number(b2);
  },
  "*": (a, b2) => Number(a) * Number(b2),
  "/": (a, b2) => Number(a) / Number(b2),
  "%": (a, b2) => Number(a) % Number(b2),
  "==": looseEquals,
  "===": equals,
  "!=": looseNotEquals,
  "!==": notEquals,
  ">": (a, b2) => isOrderable(a, b2) && compareValues(a, b2) > 0,
  ">=": (a, b2) => isOrderable(a, b2) && compareValues(a, b2) >= 0,
  "<": (a, b2) => isOrderable(a, b2) && compareValues(a, b2) < 0,
  "<=": (a, b2) => isOrderable(a, b2) && compareValues(a, b2) <= 0
  // `&&` / `||` / `??` short-circuit in evalExpr's binary branch (#2152) and
  // never reach this table.
};
var UNARY_OPERATORS = {
  "-": (a) => -Number(a),
  "!": (a) => !a,
  "@": (a) => a
};
var mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = a + 1831565813 | 0;
    let t30 = Math.imul(a ^ a >>> 15, 1 | a);
    t30 = t30 + Math.imul(t30 ^ t30 >>> 7, 61 | t30) ^ t30;
    return ((t30 ^ t30 >>> 14) >>> 0) / 4294967296;
  };
};
var toKeyList = (keys) => {
  if (R8.isArray(keys)) return keys.map(coerceToString);
  if (keys == null) return [];
  return [coerceToString(keys)];
};
var htmlInterpolation = (v) => {
  if (v == null) return "";
  if (isSafeHtml(v)) return v.html;
  if (R8.isArray(v)) return v.map(htmlInterpolation).join("");
  return escapeHtmlString(coerceToString(v));
};
var numbersOf = (args) => {
  const values = args.length === 1 && R8.isArray(args[0]) ? args[0] : args;
  return values.filter((v) => v != null).map(Number);
};
var toCallback = (fn, allowField) => {
  if (isClosure(fn)) {
    return (...args) => {
      const r = invokeClosure(fn, args);
      if (isLeft(r)) throw new Error(r.left.message);
      return r.right;
    };
  }
  if (allowField && typeof fn === "string" && fn.length > 0) {
    return (item) => R8.isPlainObject(item) ? item[fn] : void 0;
  }
  return void 0;
};
var ownEntries = (obj) => {
  if (R8.isPlainObject(obj)) return Object.entries(obj);
  if (R8.isArray(obj))
    return obj.flatMap(
      (v, i) => v == null ? [] : [[String(i), v]]
    );
  return [];
};
var FUNCTIONS = {
  if: (condition, trueValue, falseValue) => {
    return condition ? trueValue : falseValue;
  },
  and: (...args) => {
    return args.every((arg) => arg);
  },
  or: (...args) => {
    return args.some((arg) => arg);
  },
  length: (value) => {
    if (R8.isString(value)) {
      return value.length;
    }
    if (R8.isArray(value)) {
      return value.length;
    }
    return 0;
  },
  sum: (arr) => {
    if (!R8.isArray(arr)) return 0;
    return arr.reduce((acc, val) => acc + Number(val), 0);
  },
  avg: (arr) => {
    if (!R8.isArray(arr) || arr.length === 0) return 0;
    const total = arr.reduce((acc, val) => acc + Number(val), 0);
    return total / arr.length;
  },
  // One list, or variadic numbers (#2314): `max(0, x)` as well as
  // `max([0, x])`. Nothing is skipped rather than read as NaN (undefined) or
  // 0 (null); no values at all gives nothing.
  min: (...args) => {
    const ns = numbersOf(args);
    return ns.length === 0 ? void 0 : Math.min(...ns);
  },
  max: (...args) => {
    const ns = numbersOf(args);
    return ns.length === 0 ? void 0 : Math.max(...ns);
  },
  // Collection helpers (#2314) — null-safe: nothing in, nothing/[] out.
  first: (list3) => R8.isArray(list3) ? list3[0] : void 0,
  last: (list3) => R8.isArray(list3) && list3.length > 0 ? list3[list3.length - 1] : void 0,
  flatten: (list3) => R8.isArray(list3) ? list3.flat(1) : [],
  clamp: (n, lo, hi) => {
    if (n == null) return void 0;
    let v = Number(n);
    if (lo != null) v = Math.max(v, Number(lo));
    if (hi != null) v = Math.min(v, Number(hi));
    return v;
  },
  split: (str, separator) => {
    if (!R8.isString(str)) return [];
    if (separator === void 0) return [str];
    return str.split(String(separator));
  },
  join: (arr, separator) => {
    if (!R8.isArray(arr)) return "";
    const sep = separator !== void 0 ? String(separator) : ",";
    return arr.map(String).join(sep);
  },
  upper: (str) => coerceToString(str).toUpperCase(),
  lower: (str) => coerceToString(str).toLowerCase(),
  concat: (...arrays) => {
    return arrays.reduce(
      (acc, arr) => R8.isArray(arr) ? acc.concat(arr) : acc,
      []
    );
  },
  today,
  dateFrom: (arg) => {
    try {
      return dateFrom(coerceToString(arg));
    } catch (error2) {
      return `<<INVALID_DATE: ${arg}>>`;
    }
  },
  dateCompare: (a, b2) => {
    return dateCompare(a, b2);
  },
  formatDate: (date, format) => {
    try {
      if (dateFormat.is(format)) {
        return formatDate2(date, format);
      }
      return formatFriendly(date);
    } catch (error2) {
      return `<<INVALID_DATE: ${date}>>`;
    }
  },
  formatPeriod: (start, end) => {
    return formatPeriod(start, end);
  },
  generateId: (length, seed) => {
    return generateId({
      length: Number(length),
      seed: R8.isNumber(seed) ? Number(seed) : void 0
    });
  },
  regex: (pattern, flags) => {
    return new RegExp(String(pattern), flags ? String(flags) : void 0);
  },
  fuzzyMatch: (userAnswer, correctAnswer) => {
    return fuzzyMatchAnswer(
      coerceToString(userAnswer),
      coerceToString(correctAnswer)
    );
  },
  toJson: (value, indent) => {
    const indentValue = indent !== void 0 ? Number(indent) : void 0;
    return JSON.stringify(value, null, indentValue);
  },
  dodgy: (_value) => {
    throw new Error("dodgy function failed!!");
  },
  rand: (max) => {
    const n = Number(max);
    if (!R8.isNumber(n) || isNaN(n) || n <= 0) return 0;
    return Math.floor(Math.random() * n);
  },
  floor: (value) => Math.floor(Number(value)),
  ceil: (value) => Math.ceil(Number(value)),
  round: (value) => Math.round(Number(value)),
  abs: (value) => Math.abs(Number(value)),
  uniq: (arr, field2) => {
    if (!R8.isArray(arr)) return [];
    const values = field2 !== void 0 ? arr.map(
      (item) => R8.isPlainObject(item) ? item[String(field2)] : void 0
    ) : arr;
    return [...new Set(values)].filter((v) => v !== void 0);
  },
  sort: (arr, field2, desc) => {
    if (!R8.isArray(arr)) return [];
    const useField = typeof field2 === "string" && field2.length > 0;
    const keyOf = (item) => useField && R8.isPlainObject(item) ? item[field2] : item;
    const allNumbers = arr.every((item) => typeof keyOf(item) === "number");
    const sign = desc ? -1 : 1;
    return [...arr].sort((a, b2) => {
      const ka = keyOf(a);
      const kb = keyOf(b2);
      const base = allNumbers ? ka - kb : String(ka).localeCompare(String(kb));
      return sign * base;
    });
  },
  shuffle: (arr, seed) => {
    if (!R8.isArray(arr)) return [];
    const rng = mulberry32(Number(seed));
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  },
  handRank: (cardIds) => handRank(cardIds),
  replace: (s, needle, replacement) => {
    const str = s == null ? "" : coerceToString(s);
    if (needle === void 0 || needle === null) return str;
    const rep = replacement == null ? "" : coerceToString(replacement);
    return str.split(coerceToString(needle)).join(rep);
  },
  omit: (obj, keys) => {
    if (!R8.isPlainObject(obj)) return {};
    const drop = new Set(toKeyList(keys));
    return Object.fromEntries(
      Object.entries(obj).filter(([k]) => !drop.has(k))
    );
  },
  pick: (obj, keys) => {
    if (!R8.isPlainObject(obj)) return {};
    const keep = new Set(toKeyList(keys));
    return Object.fromEntries(
      Object.entries(obj).filter(([k]) => keep.has(k))
    );
  },
  // Object builtins (#2313) — all null-safe: nothing in, empty out.
  toMap: (list3, keyFn, valueFn) => {
    var _a2;
    if (!R8.isArray(list3)) return {};
    const keyOf = toCallback(keyFn, true);
    if (!keyOf) return {};
    const valueOf = (_a2 = toCallback(valueFn, true)) != null ? _a2 : (item) => item;
    const out = {};
    list3.forEach((item, i) => {
      const key = keyOf(item, i);
      if (key != null) out[coerceToString(key)] = valueOf(item, i);
    });
    return out;
  },
  keys: (obj) => ownEntries(obj).map(([k]) => k),
  values: (obj) => ownEntries(obj).map(([, v]) => v),
  entries: (obj) => ownEntries(obj),
  mapValues: (obj, fn) => {
    const f = toCallback(fn, false);
    if (!f) return {};
    return Object.fromEntries(ownEntries(obj).map(([k, v]) => [k, f(v, k)]));
  },
  escapeHtml: (s) => {
    return escapeHtmlString(s == null ? "" : coerceToString(s));
  },
  // The `html` tagged template (#1859): html`<b>${name}</b>` escapes every
  // interpolation via htmlInterpolation above and returns a SafeHtml, which
  // unwraps to its markup at any string boundary (template `${}` slots via
  // coerceToString, `+` concat via toString) and passes through a wrapping
  // html`…` unescaped. Also callable directly as a function with a
  // strings-array first argument — the tagged form is the intended use.
  html: (strings, ...values) => {
    const parts = R8.isArray(strings) ? strings.map(coerceToString) : [coerceToString(strings)];
    let out = "";
    for (let i = 0; i < parts.length; i++) {
      out += parts[i];
      if (i < values.length) {
        out += htmlInterpolation(values[i]);
      }
    }
    return new SafeHtml(out);
  },
  // Meter builtins (#2647): one pure drawing core (utils/visual/meters.ts),
  // wrapped as SafeHtml so the markup nests inside html`…` unescaped.
  progressBar: (amount, opts) => new SafeHtml(progressBarMarkup(amount, opts)),
  progressRing: (amount, opts) => new SafeHtml(progressRingMarkup(amount, opts)),
  sparkline: (values, opts) => new SafeHtml(sparklineMarkup(values, opts)),
  // #2763: shares the meters' colour check, so the two cannot drift.
  safeCssColor: (value, fallback) => safeCssColor(value, fallback),
  urlEncode: (s) => {
    return encodeURIComponent(s == null ? "" : coerceToString(s));
  },
  // Point-in-time clock read, NOT reactive — `$now` is the reactive variant
  // (re-read ~every 15s). Same second-precision UTC ISO shape as `$now` so
  // the two compare lexicographically. Impure: registered in
  // `IMPURE_OR_CONTEXT_BUILTINS` (memoSafety.ts) so a function calling it is
  // never memoised.
  now: () => (/* @__PURE__ */ new Date()).toISOString().slice(0, 19) + "Z",
  // Pure — `after` is passed in, never read from the clock — so it is safe in
  // a transition rule's `next`/`seed`, and cacheable like any other builtin.
  nextOccurrence,
  // `byId` needs access to the repo, so the real implementation is injected
  // via createEvalContext.ts. This stub is only reached if the eval context
  // did not provide a `byId` binding (e.g. formulas evaluated outside of an
  // AppState) — in which case there's nothing to look up.
  byId: () => void 0
};
var HOF_NAMES = {
  forEach: true,
  filter: true,
  map: true,
  reduce: true,
  times: true,
  every: true,
  some: true,
  find: true,
  findIndex: true,
  count: true,
  groupBy: true,
  sumBy: true
};
var CALLABLE_FUNCTION_NAMES = /* @__PURE__ */ new Set([
  ...Object.keys(FUNCTIONS),
  ...Object.keys(HOF_NAMES)
]);
var BARE_BUILTIN_NAMES = new Set(
  Object.keys(FUNCTIONS).filter((name) => !CALL_ONLY_FUNCTIONS.has(name))
);
var toDynamicContext = (ctx) => {
  if (typeof ctx === "function") {
    return ctx;
  }
  return (name) => ctx[name];
};
var pushScope = (dc, scope) => {
  return (name, lookupContext) => {
    if (Object.prototype.hasOwnProperty.call(scope, name)) {
      return scope[name];
    }
    return dc(name, lookupContext);
  };
};
var createMutableContext = (baseContext) => {
  const mutableScope = {};
  const dc = pushScope(toDynamicContext(baseContext), mutableScope);
  const memo = createEvalMemo();
  const setter = (name, value) => {
    mutableScope[name] = value;
    flushEvalMemo(memo);
  };
  return { dc, setter, memo };
};

// src/orgicore/utils/evaluation/formula/evalScriptAsync.ts
var evalScriptAsync = async (formula, ctx, hosts) => {
  const parseResult = parseFormula(formula);
  if (isLeft(parseResult)) return parseResult;
  const mutableCtx = createMutableContext(ctx);
  mutableCtx.asyncHosts = hosts.asyncHosts;
  mutableCtx.syncHosts = hosts.syncHosts;
  return evalAsyncRec(parseResult.right, mutableCtx);
};
var evalAsyncRec = async (ast, ctx) => {
  var _a2;
  if (isBinaryExpression(ast) && ast.operator === ";") {
    const leftResult = await evalAsyncRec(ast.left, ctx);
    if (isLeft(leftResult)) return leftResult;
    return evalAsyncRec(ast.right, ctx);
  }
  if (isBinaryExpression(ast) && ast.operator === "=") {
    const rhsResult = await evalAsyncRec(ast.right, ctx);
    if (isLeft(rhsResult)) return rhsResult;
    const value = getRight(rhsResult);
    const assignResult = assignTarget(
      ast.left,
      value,
      ctx,
      emptyLookupContext
    );
    if (isLeft(assignResult)) return assignResult;
    return right(value);
  }
  if (isLetExpression(ast)) {
    if ((_a2 = ctx.letBound) == null ? void 0 : _a2.has(ast.id)) {
      return left(
        createFormulaError(`Cannot redeclare let binding '${ast.id}'`, ast)
      );
    }
    const initResult = await evalAsyncRec(ast.init, ctx);
    if (isLeft(initResult)) return initResult;
    const value = getRight(initResult);
    if (!ast.body) return right(value);
    return evalAsyncRec(ast.body, letScope(ctx, ast.id, value));
  }
  if (isCallExpression(ast) && isIdentifier(ast.callee) && ctx.asyncHosts && ast.callee.name in ctx.asyncHosts) {
    const calleeName4 = ast.callee.name;
    for (const arg of ast.arguments) {
      if (containsAsyncHostCall(arg, ctx.asyncHosts)) {
        return left(
          createFormulaError(
            `Async host call '${calleeName4}' has an argument that itself contains an async host call. Async host calls must appear at statement top-level or as the RHS of an assignment, not nested inside other expressions. Bind the inner result to a variable first.`,
            ast
          )
        );
      }
    }
    const evaluatedArgs = [];
    for (const arg of ast.arguments) {
      const argResult = evalExpr(arg, ctx, emptyLookupContext);
      if (isLeft(argResult)) return argResult;
      evaluatedArgs.push(getRight(argResult));
    }
    try {
      const result = await ctx.asyncHosts[calleeName4](...evaluatedArgs);
      return right(result);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return left(
        createFormulaError(
          `Error invoking async host '${calleeName4}': ${message}`,
          ast
        )
      );
    }
  }
  if (ctx.asyncHosts && containsAsyncHostCall(ast, ctx.asyncHosts)) {
    return left(
      createFormulaError(
        "Async host call appears nested inside another expression. Async host calls must appear at statement top-level or as the RHS of an assignment. Bind the inner result to a variable first.",
        ast
      )
    );
  }
  return evalExpr(ast, ctx, emptyLookupContext);
};
var NON_CHILD_KEYS = /* @__PURE__ */ new Set(["parent"]);
var containsAsyncHostCall = (ast, asyncHosts) => {
  if (isCallExpression(ast) && isIdentifier(ast.callee) && ast.callee.name in asyncHosts) {
    return true;
  }
  for (const key of Object.keys(ast)) {
    if (NON_CHILD_KEYS.has(key)) continue;
    const value = ast[key];
    if (isExpressionLike(value)) {
      if (containsAsyncHostCall(value, asyncHosts)) {
        return true;
      }
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (isExpressionLike(item) && containsAsyncHostCall(item, asyncHosts)) {
          return true;
        }
      }
    }
  }
  return false;
};
var isExpressionLike = (v) => typeof v === "object" && v !== null && typeof v.type === "string";

// src/orgicore/utils/layout/layout.ts
var AXIS_X = "x";
var AXIS_Y = "y";
var GRID = "grid";
var COLUMNS = "columns";
var arrangeIndexesInto2D = (n, colCount, axis2, mode2, breaks = []) => {
  return arrangeInto2D(
    Array.from(Array(n).keys()),
    colCount,
    axis2,
    mode2,
    breaks
  );
};
var arrangeInto2D = (input, colCount, axis2, mode2, breaks = []) => {
  const isManual = breaks.length > 0;
  const arrange = isManual ? arrangeManually : arrangeAutomatically;
  const r = arrange(input, colCount, axis2, breaks);
  return mode2 === "columns" ? r : transpose2DArray(r);
};
var arrangeAutomatically = (arr, colCount, axis2) => {
  const rowCount = Math.ceil(arr.length / colCount);
  const result = fill2DArray(colCount, rowCount);
  for (let i = 0; i < arr.length; i++) {
    const x = axis2 === "y" ? Math.floor(i / rowCount) : i % colCount;
    const y = axis2 === "x" ? Math.floor(i / colCount) : i % rowCount;
    result[x][y] = arr[i];
  }
  return result;
};
var arrangeManually = (arr, colCount, axis2, breaks) => {
  if (axis2 !== "y") {
    throw new Error(`axis ${axis2} not yet supported`);
  }
  const allBreaks = getAllBreaks(breaks, arr.length, colCount);
  const rowCount = getBiggestGap(allBreaks);
  const result = fill2DArray(colCount, rowCount);
  let x = 0, y = 0;
  for (let i = 0; i < arr.length; i++, y++) {
    if (i === allBreaks[x]) {
      x++;
      y = 0;
    }
    result[x][y] = arr[i];
  }
  return result;
};
var fill2DArray = (colCount, rowCount) => {
  return Array.from(Array(colCount), (_) => Array(rowCount).fill(void 0));
};
var getAllBreaks = (breaks, length, colCount) => {
  const validBreaks = breaks.slice(0, colCount - 1).filter((b2) => b2 < length).sort();
  return [...validBreaks, length];
};
var getBiggestGap = (allBreaks) => {
  const gaps = allBreaks.map((b2, i) => {
    if (i === 0) {
      return b2;
    }
    return b2 - allBreaks[i - 1];
  });
  return Math.max(...gaps);
};
var transpose2DArray = (arr) => {
  const result = fill2DArray(arr[0].length, arr.length);
  for (let i = 0; i < arr.length; i++) {
    for (let j = 0; j < arr[0].length; j++) {
      result[j][i] = arr[i][j];
    }
  }
  return result;
};

// src/orgicore/utils/layout/spatial.ts
var gridPlacementStyle = (place2) => {
  if (place2.kind === "auto") return {};
  return {
    gridColumn: `${place2.x + 1} / span ${place2.w}`,
    gridRow: `${place2.y + 1} / span ${place2.h}`
  };
};
var spatialTemplate = (grid) => {
  const min = `${grid.minColumnWidth}px`;
  const rowTrack = grid.rowHeight !== void 0 ? `${grid.rowHeight}px` : "auto";
  const style = {
    display: "grid",
    gridTemplateColumns: `repeat(${grid.cols}, minmax(${min}, 1fr))`,
    gridAutoRows: rowTrack,
    minWidth: `${grid.cols * grid.minColumnWidth}px`
  };
  if (grid.rows !== void 0) {
    style.gridTemplateRows = `repeat(${grid.rows}, ${rowTrack})`;
  }
  return style;
};
var LANE_HEIGHT_PX = 22;
var LANE_STRIP_GUTTER_PX = 20;
var laneStripStyle = (grid, y) => ({
  gridColumn: `1 / span ${grid.cols}`,
  gridRow: `${y + 1}`,
  display: "grid",
  gridTemplateColumns: `repeat(${grid.cols}, minmax(${grid.minColumnWidth}px, 1fr))`,
  gridAutoRows: `${LANE_HEIGHT_PX}px`,
  alignContent: "start",
  rowGap: "2px",
  paddingTop: `${LANE_STRIP_GUTTER_PX}px`,
  pointerEvents: "none",
  minWidth: 0
});
var laneSegmentStyle = (segment2) => ({
  gridColumn: `${segment2.startCol + 1} / span ${segment2.span}`,
  gridRow: `${segment2.lane + 1}`,
  pointerEvents: "auto",
  minWidth: 0
});

// src/orgicore/utils/layout/spanSegments.ts
var runsFor = (slots, cellByIndex) => {
  var _a2;
  const byRow = /* @__PURE__ */ new Map();
  for (const index of slots) {
    const cell = cellByIndex.get(index);
    if (!cell) continue;
    const row = byRow.get(cell.y);
    if (row) row.add(cell.x);
    else byRow.set(cell.y, /* @__PURE__ */ new Set([cell.x]));
  }
  const runs = [];
  for (const y of [...byRow.keys()].sort((a, b2) => a - b2)) {
    const cols = [...(_a2 = byRow.get(y)) != null ? _a2 : []].sort((a, b2) => a - b2);
    let startCol = cols[0];
    let prev = cols[0];
    for (const col of cols.slice(1)) {
      if (col === prev + 1) {
        prev = col;
        continue;
      }
      runs.push({ y, startCol, span: prev - startCol + 1 });
      startCol = col;
      prev = col;
    }
    if (cols.length > 0) {
      runs.push({ y, startCol, span: prev - startCol + 1 });
    }
  }
  return runs;
};
var laneIsFree = (lane, run) => {
  for (let c = run.startCol; c < run.startCol + run.span; c += 1) {
    if (lane.has(c)) return false;
  }
  return true;
};
var occupy = (lane, run) => {
  for (let c = run.startCol; c < run.startCol + run.span; c += 1) lane.add(c);
};
var assignLane = (occupancy, run) => {
  let lanes = occupancy.get(run.y);
  if (!lanes) {
    lanes = [];
    occupancy.set(run.y, lanes);
  }
  for (let i = 0; i < lanes.length; i += 1) {
    if (laneIsFree(lanes[i], run)) {
      occupy(lanes[i], run);
      return i;
    }
  }
  const lane = /* @__PURE__ */ new Set();
  occupy(lane, run);
  lanes.push(lane);
  return lanes.length - 1;
};
var layoutSpanSegments = (records, slots) => {
  const cellByIndex = /* @__PURE__ */ new Map();
  for (const slot2 of slots) cellByIndex.set(slot2.index, { x: slot2.x, y: slot2.y });
  const occupancy = /* @__PURE__ */ new Map();
  const segments = [];
  for (const record10 of records) {
    for (const run of runsFor(record10.slots, cellByIndex)) {
      segments.push({
        recordKey: record10.recordKey,
        ...run,
        lane: assignLane(occupancy, run)
      });
    }
  }
  return segments;
};
var laneCountsByRow = (segments) => {
  var _a2;
  const counts = /* @__PURE__ */ new Map();
  for (const s of segments) {
    counts.set(s.y, Math.max((_a2 = counts.get(s.y)) != null ? _a2 : 0, s.lane + 1));
  }
  return counts;
};

// src/orgicore/utils/outbox/outboxQueue.ts
var enqueue = (queue, entry) => [...queue, entry];
var nextPending = (queue) => queue.find((e) => e.status === "pending");
var markSent = (queue, entryId) => queue.filter((e) => e.entryId !== entryId);
var markFailed = (queue, entryId, error2, maxAttempts) => queue.map((e) => {
  if (e.entryId !== entryId) return e;
  const attempts = e.attempts + 1;
  return {
    ...e,
    attempts,
    lastError: error2,
    status: attempts >= maxAttempts ? "failed" : "pending"
  };
});
var retryEntry = (queue, entryId) => queue.map(
  (e) => e.entryId === entryId ? { ...e, status: "pending", attempts: 0, lastError: void 0 } : e
);
var discardEntry = (queue, entryId) => queue.filter((e) => e.entryId !== entryId);
var summarize = (queue) => ({
  pending: queue.filter((e) => e.status === "pending").length,
  failed: queue.filter((e) => e.status === "failed").length,
  total: queue.length
});
var hasPendingWrite = (queue, recordId) => queue.some((e) => e.recordId === recordId);
var isEntryShaped = (value) => {
  if (typeof value !== "object" || value === null) return false;
  const e = value;
  return typeof e.entryId === "string" && typeof e.recordId === "string" && typeof e.orgId === "string" && typeof e.typeName === "string" && typeof e.userId === "string" && typeof e.enqueuedAt === "string" && typeof e.attempts === "number" && (e.status === "pending" || e.status === "failed") && typeof e.obj === "object" && e.obj !== null;
};
var parseQueue = (value) => {
  if (!Array.isArray(value)) return [];
  return value.filter(isEntryShaped);
};

// src/orgicore/utils/outbox/outboxDrain.ts
var DEFAULT_MAX_ATTEMPTS = 4;
var RETRY_BASE_MS = 2e3;
var nextRetryDelayMs = (attempts) => RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1);
var errorText = (err) => err instanceof Error ? err.message : String(err);
var drainOutbox = async ({
  read,
  save,
  send,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  isPermanent
}) => {
  var _a2;
  let sent = 0;
  let parked = 0;
  const handled = /* @__PURE__ */ new Set();
  for (; ; ) {
    const entry = nextPending(read());
    if (!entry) return { sent, parked, stoppedEarly: false, stalled: false };
    if (handled.has(entry.entryId)) {
      return { sent, parked, stoppedEarly: true, stalled: true };
    }
    handled.add(entry.entryId);
    let sendError;
    try {
      await send(entry);
    } catch (err) {
      sendError = { err };
    }
    if (!sendError) {
      save(markSent(read(), entry.entryId));
      sent += 1;
      continue;
    }
    const permanent = (isPermanent == null ? void 0 : isPermanent(sendError.err)) === true;
    const updated = markFailed(
      read(),
      entry.entryId,
      errorText(sendError.err),
      permanent ? 1 : maxAttempts
    );
    save(updated);
    const didPark = ((_a2 = updated.find((e) => e.entryId === entry.entryId)) == null ? void 0 : _a2.status) === "failed";
    if (!didPark) {
      return { sent, parked, stoppedEarly: true, stalled: false };
    }
    parked += 1;
  }
};

// src/orgicore/utils/typeDescription.ts
var BUILTIN_TS_TYPE = {
  variable: "string",
  text: "string",
  number: "number",
  boolean: "boolean",
  temporal: "string",
  parentRef: "string",
  image: "string",
  imageUpload: "string"
};
var exampleUrl = (index) => `https://example.com/image${index + 1}.png`;
var exampleId = (field2, index) => `example_${field2.name}_${index + 1}`;
var BUILTIN_EXAMPLE = {
  variable: exampleId,
  text: (field2, index) => `Example ${field2.name} ${index + 1}`,
  number: (_field, index) => (index + 1) * 10,
  boolean: (_field, index) => index % 2 === 0,
  temporal: (_field, index) => `2025-01-${String(index + 1).padStart(2, "0")}`,
  parentRef: exampleId,
  image: (_field, index) => exampleUrl(index),
  imageUpload: (_field, index) => exampleUrl(index)
};
var scalarTypeToTypeString = (type14, metaModel2, indent) => {
  if (isFieldTypeBuiltin(type14)) return BUILTIN_TS_TYPE[type14];
  if (metaModel2) {
    const objectType3 = metaModel2.types.find((t30) => t30.name === type14);
    if (objectType3) {
      return objectTypeToTypeDescription(objectType3, metaModel2, indent);
    }
  }
  return "string";
};
var fieldTypeToTypeString = (field2, metaModel2, indent) => {
  const type14 = field2.type;
  if (!type14) return "string";
  const parsed = parseFieldType(type14, field2.isList);
  if (parsed.kind === "list") {
    return `${scalarTypeToTypeString(parsed.elem.name, metaModel2, indent)}[]`;
  }
  if (parsed.kind === "map") {
    return `Record<string, ${scalarTypeToTypeString(parsed.elem.name, metaModel2, indent)}>`;
  }
  return scalarTypeToTypeString(type14, metaModel2, indent);
};
var scalarExampleValue = (type14, field2, index, metaModel2) => {
  if (isFieldTypeBuiltin(type14)) return BUILTIN_EXAMPLE[type14](field2, index);
  if (metaModel2) {
    const objectType3 = metaModel2.types.find((t30) => t30.name === type14);
    if (objectType3) {
      return objectTypeToMockInstance(objectType3, index, metaModel2);
    }
  }
  return exampleId(field2, index);
};
var fieldToExampleValue = (field2, index, metaModel2) => {
  const type14 = field2.type;
  if (!type14) return exampleId(field2, index);
  const parsed = parseFieldType(type14, field2.isList);
  if (parsed.kind === "list") {
    return [scalarExampleValue(parsed.elem.name, field2, index, metaModel2)];
  }
  if (parsed.kind === "map") {
    return {
      [`key${index + 1}`]: scalarExampleValue(
        parsed.elem.name,
        field2,
        index,
        metaModel2
      )
    };
  }
  return scalarExampleValue(type14, field2, index, metaModel2);
};
var objectTypeToMockInstance = (objectType3, index, metaModel2) => {
  const instance = {};
  for (const field2 of objectType3.fields) {
    if (!field2.name || !field2.type) continue;
    const value = fieldToExampleValue(field2, index, metaModel2);
    instance[field2.name] = value;
  }
  return instance;
};
var objectTypeToMockInstances = (objectType3, count, metaModel2) => {
  return Array.from(
    { length: count },
    (_, i) => objectTypeToMockInstance(objectType3, i, metaModel2)
  );
};
var objectTypeToTypeDescription = (objectType3, metaModel2, indent) => {
  const parts = [];
  for (const field2 of objectType3.fields) {
    if (!field2.name || !field2.type) continue;
    const typeStr = fieldTypeToTypeString(
      field2,
      metaModel2,
      indent !== void 0 ? indent + 1 : void 0
    );
    const desc = field2.description;
    if (indent !== void 0 && desc) {
      parts.push({ line: `${field2.name}: ${typeStr}`, desc });
    } else {
      parts.push({ line: `${field2.name}: ${typeStr}`, desc: void 0 });
    }
  }
  if (indent === void 0) {
    return `{ ${parts.map((p) => p.line).join("; ")} }`;
  }
  const pad = "  ".repeat(indent);
  const innerPad = "  ".repeat(indent + 1);
  return "{\n" + parts.map(
    (p) => p.desc ? `${innerPad}${p.line}; // ${p.desc}` : `${innerPad}${p.line};`
  ).join("\n") + `
${pad}}`;
};

// src/orgicore/utils/mediaExts.ts
var VIDEO_EXTS = /* @__PURE__ */ new Set([
  "mp4",
  "mov",
  "webm",
  "m4v",
  "ogv"
]);
var AUDIO_EXTS = /* @__PURE__ */ new Set([
  "mp3",
  "wav",
  "webm",
  "m4a",
  "aac",
  "ogg",
  "flac"
]);
var isVideoExt = (ext) => !!ext && VIDEO_EXTS.has(ext.toLowerCase());
var isAudioExt = (ext) => !!ext && AUDIO_EXTS.has(ext.toLowerCase());
var isNonImageExt = (ext) => isVideoExt(ext) || isAudioExt(ext);

// src/orgicore/utils/imageSources.ts
var MAX_VISION_IMAGES = 8;
var MAX_DEPTH = 2;
var urlFromString = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return void 0;
  return /^(https?:|data:)/i.test(trimmed) ? trimmed : void 0;
};
var isRecord2 = (value) => typeof value === "object" && value !== null;
var stringAt = (obj, key) => {
  const v = obj[key];
  return typeof v === "string" ? v : void 0;
};
var extFromUrl = (url) => {
  if (/^data:/i.test(url)) return void 0;
  const path = url.split(/[?#]/)[0];
  const lastSegment = path.slice(path.lastIndexOf("/") + 1);
  const dot = lastSegment.lastIndexOf(".");
  if (dot <= 0 || dot === lastSegment.length - 1) return void 0;
  const ext = lastSegment.slice(dot + 1).toLowerCase();
  return /^[a-z0-9]{1,5}$/.test(ext) ? ext : void 0;
};
var collect = (value, out, depth) => {
  var _a2;
  if (depth > MAX_DEPTH) return;
  if (Array.isArray(value)) {
    for (const item of value) collect(item, out, depth + 1);
    return;
  }
  if (typeof value === "string") {
    const url2 = urlFromString(value);
    if (url2) out.push(url2);
    return;
  }
  if (!isRecord2(value)) return;
  if ("result" in value) {
    collect(value.result, out, depth + 1);
    return;
  }
  const url = stringAt(value, "url");
  if (!url) return;
  const ext = (_a2 = stringAt(value, "ext")) != null ? _a2 : extFromUrl(url);
  if (isNonImageExt(ext)) return;
  const accepted = urlFromString(url);
  if (accepted) out.push(accepted);
};
var resolveImageSources = (value, cap = MAX_VISION_IMAGES) => {
  const limit = Number.isFinite(cap) && cap > 0 ? Math.floor(cap) : 0;
  if (limit === 0) return [];
  const found = [];
  collect(value, found, 0);
  const unique4 = [];
  const seen = /* @__PURE__ */ new Set();
  for (const url of found) {
    if (seen.has(url)) continue;
    seen.add(url);
    unique4.push(url);
    if (unique4.length === limit) break;
  }
  return unique4;
};

// src/orgicore/integration/metamodel/repoSelectors.ts
import * as R9 from "remeda";
import { createSelector as createSelector5 } from "reselect";
var repo = (repo2) => repo2;
var selectObjects = createSelector5(
  repo,
  (repo2) => repo2.objects
);
var selectObjectsByType = createSelector5(selectObjects, (objects) => R9.groupBy(objects, (o) => o.type));
var selectObjectsById = createSelector5(
  selectObjects,
  (objects) => R9.mapValues(
    R9.groupBy(objects, (o) => o.type),
    (objs) => R9.indexBy(objs, (o) => o.id)
  )
);
var typeIdKey = (o) => `${o.type}\0${o.id}`;
var updateObjects = (oldObjs, newObjs) => {
  const tombstones = newObjs.filter((o) => o.isDeleted === true);
  const deletedKeys = new Set(
    tombstones.filter((o) => hasScope(o)).map(typeIdKey)
  );
  const unscopedDeletedKeys = new Set(
    tombstones.filter((o) => !hasScope(o)).map(typeIdKey)
  );
  const live = newObjs.filter((o) => o.isDeleted !== true);
  const upserted = R9.uniqueBy([...live, ...oldObjs], typeIdKey);
  return upserted.filter((o) => {
    const key = typeIdKey(o);
    if (deletedKeys.has(key)) return false;
    return !(unscopedDeletedKeys.has(key) && !hasScope(o));
  });
};
var hasScope = (o) => typeof o._scope === "string";

// src/orgicore/integration/query/span.ts
import { Temporal as Temporal6 } from "@js-temporal/polyfill";
var SPAN_META_KEY = "__span";
var readSpanMeta = (value) => {
  if (typeof value !== "object" || value === null) return void 0;
  const meta = value[SPAN_META_KEY];
  if (typeof meta !== "object" || meta === null) return void 0;
  return meta;
};
var ISO_DATE_PREFIX_RE = /^\d{4}-\d{2}-\d{2}([T ]|$)/;
var resolveSpan = (span2) => {
  if (!(span2 == null ? void 0 : span2.startField) || !(span2 == null ? void 0 : span2.axis)) return void 0;
  return {
    startField: span2.startField,
    axis: span2.axis,
    endField: span2.endField,
    mode: span2.mode === "once" ? "once" : span2.mode === "grid" ? "grid" : "repeat",
    slots: span2.slots
  };
};
var compareAxis = (a, b2) => {
  if (isPlainDate(a) && !isPlainDate(b2))
    return compareValues(a, toPlainDate2(b2));
  if (isPlainDate(b2) && !isPlainDate(a))
    return compareValues(toPlainDate2(a), b2);
  return compareValues(a, b2);
};
var toPlainDate2 = (value) => {
  if (typeof value !== "string" || !ISO_DATE_PREFIX_RE.test(value))
    return value;
  try {
    return Temporal6.PlainDate.from(value);
  } catch {
    return value;
  }
};
var spanBounds = (record10, span2) => {
  const start = record10[span2.startField];
  if (start === void 0 || start === null || start === "") return void 0;
  const rawEnd = span2.endField ? record10[span2.endField] : void 0;
  const hasEnd = rawEnd !== void 0 && rawEnd !== null && rawEnd !== "";
  if (!hasEnd) return { start, end: start };
  return compareAxis(rawEnd, start) < 0 ? { start, end: start } : { start, end: rawEnd };
};
var covers = (bounds, slot2) => compareAxis(bounds.start, slot2) <= 0 && compareAxis(bounds.end, slot2) >= 0;
var startsAt = (bounds, slot2) => compareAxis(bounds.start, slot2) === 0;
var matchesSlot = (bounds, slot2, mode2) => mode2 === "once" ? startsAt(bounds, slot2) : covers(bounds, slot2);
var spanPosition = (bounds, slot2) => {
  const atStart = compareAxis(bounds.start, slot2) === 0;
  const atEnd = compareAxis(bounds.end, slot2) === 0;
  if (atStart && atEnd) return "single";
  if (atStart) return "start";
  if (atEnd) return "end";
  return "middle";
};

// src/orgicore/integration/query/query.ts
var runDataQuery = (repo2, query, evaluator) => {
  const { dataType, sort, whereClause, scope, source, span: span2 } = query;
  let candidates = selectObjectsByType(repo2)[dataType] || [];
  if ((scope === "user" || scope === "global") && source === "storage") {
    candidates = candidates.filter(
      (r) => r._scope === scope
    );
  }
  const spanned = applySpan(candidates, resolveSpan(span2), evaluator);
  const records = filterRecords(spanned, whereClause, evaluator);
  if (!sort) return records;
  const sorts = (Array.isArray(sort) ? sort : [sort]).filter(
    (s) => typeof s.field === "string" && s.field.length > 0
  );
  if (sorts.length === 0) return records;
  const sorted = [...records].sort((left4, right4) => {
    for (const rule of sorts) {
      const leftValue = left4[rule.field];
      const rightValue = right4[rule.field];
      const leftMissing = leftValue === void 0 || leftValue === null;
      const rightMissing = rightValue === void 0 || rightValue === null;
      if (leftMissing && rightMissing) continue;
      if (leftMissing) return 1;
      if (rightMissing) return -1;
      const directionMultiplier = rule.direction === "desc" ? -1 : 1;
      const result = compareValues(leftValue, rightValue);
      if (result !== 0) return directionMultiplier * result;
    }
    return 0;
  });
  return sorted;
};
var applySpan = (records, span2, evaluator) => {
  if (!span2) return records;
  if (span2.mode === "grid") {
    const out2 = [];
    for (const record10 of records) {
      const bounds = spanBounds(record10, span2);
      if (!bounds) continue;
      out2.push({
        ...record10,
        [SPAN_META_KEY]: { start: bounds.start, end: bounds.end }
      });
    }
    return out2;
  }
  const axis2 = evaluator(span2.axis, {});
  if (!isRight(axis2) || axis2.right === void 0) return [];
  const slot2 = axis2.right;
  const out = [];
  for (const record10 of records) {
    const bounds = spanBounds(record10, span2);
    if (!bounds) continue;
    if (!matchesSlot(bounds, slot2, span2.mode)) continue;
    out.push({
      ...record10,
      [SPAN_META_KEY]: {
        start: bounds.start,
        end: bounds.end,
        position: spanPosition(bounds, slot2)
      }
    });
  }
  return out;
};
var filterRecords = (records, whereClause, evaluator) => {
  if (!whereClause) {
    return records;
  }
  return records.filter((record10) => {
    const result = evaluator(whereClause, record10);
    return isRight(result) && !!result.right;
  });
};

// src/orgicore/selectors/appstate/parentSelectors.ts
import { createSelector as createSelector6 } from "reselect";
var selectParent = createSelector6(selectAppState, selectPointer, (s, p) => {
  const { scope, isIteration, node: node2 } = p;
  const { parent } = scope;
  const parentNodeDef = selectParentNodeDef(s.def, node2);
  if (!parentNodeDef) {
    return void 0;
  }
  if (isIteration && parent) {
    return selectPointer(
      s,
      compactJoin([parent.key, selectUniqueKey(parent.node, node2)])
    );
  }
  if (parentNodeDef.isRepeating) {
    return selectPointer(s, `${scope.key}`);
  } else {
    return selectPointer(
      s,
      compactJoin([scope.key, selectUniqueKey(scope.node, parentNodeDef)])
    );
  }
});

// src/orgicore/selectors/appstate/evaluation/createSelectValue.ts
var getCompute = createSelectPropDef(t17.unknown, "compute");
var getMaxLength = createSelectPropDef(
  t17.union([t17.number, t17.undefined]),
  "maxLength"
);
var getPageSize = createSelectPropDef(
  t17.union([t17.number, t17.undefined]),
  "pageSize"
);
var evaluatePropAndDecode = (s, p, propDef, evaluate2) => {
  const result = propDef.isFormula ? evaluate2(s, propDef.value, p.key) : right(propDef.value);
  return decode(result, propDef.targetCodec, propDef.name);
};
var createSelectors = (evaluate2) => {
  const selectComputedValue2 = createSelector7(
    selectAppState,
    selectRepo,
    selectPointer,
    (appState, repo2, p) => {
      const { node: node2, scope } = p;
      const dataOptions2 = node2.data;
      if (isDataQuery(dataOptions2)) {
        if (p.isIteration) {
          const parent = selectParent(appState, p.key);
          if (parent) {
            const r = selectValue2(appState, parent.key);
            if (isRight(r) && Array.isArray(r.right)) {
              return right(r.right[scope.index]);
            }
          }
        } else {
          const evaluator = (formula, ctx) => {
            return evaluate2(appState, formula, scope.key, ctx);
          };
          return right(runDataQuery(repo2, dataOptions2, evaluator));
        }
      }
      const compute = getCompute(node2);
      return evaluatePropAndDecode(appState, p, compute, evaluate2);
    }
  );
  const selectValue2 = createSelector7(
    selectAppState,
    selectPointer,
    (appState, p) => {
      const { key } = p;
      if (p.node.type === CONST) return right(p.node.value);
      const data = selectDataByPointer(appState);
      const pointerData = data[key];
      if (pointerData) {
        return right(pointerData == null ? void 0 : pointerData.value);
      }
      return selectComputedValue2(appState, p.key);
    }
  );
  const selectLength2 = createSelector7(
    selectAppState,
    selectPointer,
    selectLengthsByPointer,
    (appState, p, lengths) => {
      var _a2;
      const { key, node: node2 } = p;
      const storedLength = (_a2 = lengths[key]) == null ? void 0 : _a2.length;
      const maxLengthProp = getMaxLength(node2);
      const maxLength = getRight(
        evaluatePropAndDecode(appState, p, maxLengthProp, evaluate2)
      );
      const dataOptions2 = node2.data;
      if (isDataQuery(dataOptions2)) {
        const r = selectValue2(appState, p.key);
        if (isRight(r) && Array.isArray(r.right)) {
          const dataLength = r.right.length;
          const totalLength = maxLength !== void 0 ? Math.min(dataLength, maxLength) : dataLength;
          const pageSizeProp2 = getPageSize(node2);
          const pageSize2 = getRight(
            evaluatePropAndDecode(appState, p, pageSizeProp2, evaluate2)
          );
          if (storedLength !== void 0) {
            return right(Math.min(totalLength, storedLength));
          }
          if (pageSize2 !== void 0) {
            return right(Math.min(totalLength, pageSize2));
          }
          return right(totalLength);
        }
      }
      const pageSizeProp = getPageSize(node2);
      const pageSize = getRight(
        evaluatePropAndDecode(appState, p, pageSizeProp, evaluate2)
      );
      if (pageSize !== void 0) {
        if (storedLength !== void 0) {
          const capped = maxLength !== void 0 ? Math.min(storedLength, maxLength) : storedLength;
          return right(capped);
        }
        return right(pageSize);
      }
      if (storedLength !== void 0) {
        return right(storedLength);
      }
      return right(maxLength != null ? maxLength : 0);
    }
  );
  return {
    selectValue: selectValue2,
    selectLength: selectLength2,
    selectComputedValue: selectComputedValue2
  };
};

// src/orgicore/selectors/appstate/evaluation/index.ts
import * as t18 from "io-ts";
import { createSelector as createSelector10 } from "reselect";

// src/orgicore/selectors/appstate/evaluation/createEvalContext.ts
import * as R10 from "remeda";

// src/orgicore/utils/evaluation/formula/memoSafety.ts
var IMPURE_OR_CONTEXT_BUILTINS = /* @__PURE__ */ new Set([
  "rand",
  "generateId",
  "today",
  "dodgy",
  "byId",
  // Point-in-time clock read (#1853): two calls straddling a second boundary
  // differ, so a function calling it must never be memoised.
  "now"
]);
var HOF_NAMES2 = new Set(Object.keys(HOF_SLOTS));
var PURE_METHOD_WHITELIST = /* @__PURE__ */ new Set([
  "includes",
  "indexOf",
  "lastIndexOf",
  "slice",
  "join",
  "concat",
  "split",
  "toLowerCase",
  "toUpperCase",
  "trim",
  "startsWith",
  "endsWith",
  "replace",
  "replaceAll",
  "substring",
  "charAt",
  "padStart",
  "padEnd",
  "repeat",
  "toFixed",
  "localeCompare"
]);
var isAstNode2 = (v) => typeof v === "object" && v !== null && "type" in v && typeof v.type === "string";
var stripAt = (compute) => {
  if (typeof compute !== "string") return void 0;
  return compute.startsWith("@") ? compute.slice(1).trim() : compute;
};
var paramNamesOf = (parameters) => {
  const paramStr = typeof parameters === "string" ? parameters : "";
  return paramStr.split(",").map((s) => s.trim()).filter(Boolean);
};
var arrowParamNames = (arrow) => {
  const params = arrow.params;
  if (!Array.isArray(params)) return [];
  return params.filter(isAstNode2).filter((p) => p.type === "Identifier" && typeof p.name === "string").map((p) => p.name);
};
var callbackBindings = (hof, cb) => {
  var _a2;
  return isAstNode2(cb) && cb.type === "ArrowFunctionExpression" ? arrowParamNames(cb) : (_a2 = HOF_SLOTS[hof]) != null ? _a2 : [];
};
var analyzeOne = (body, params, orgFnNames, constNames = /* @__PURE__ */ new Set()) => {
  const deps = /* @__PURE__ */ new Set();
  let ast;
  try {
    ast = jsep(body);
  } catch {
    return { selfSafe: false, deps };
  }
  let safe = true;
  const unsafe = () => {
    safe = false;
  };
  const checkIdentifier = (name, bound) => {
    if (bound.has(name)) return;
    if (name === "$data") return;
    if (constNames.has(name)) return;
    unsafe();
  };
  const walk8 = (n, bound) => {
    if (!safe) return;
    if (Array.isArray(n)) {
      for (const c of n) walk8(c, bound);
      return;
    }
    if (!isAstNode2(n)) return;
    switch (n.type) {
      case "Literal":
        return;
      case "Identifier": {
        if (typeof n.name === "string") checkIdentifier(n.name, bound);
        return;
      }
      case "ThisExpression":
        unsafe();
        return;
      case "TaggedTemplateExpression": {
        const tag = n.tag;
        const isHtmlTag = isAstNode2(tag) && tag.type === "Identifier" && tag.name === "html" && !bound.has("html") && !orgFnNames.has("html");
        if (!isHtmlTag) {
          unsafe();
          return;
        }
        const quasi = n.quasi;
        const expressions = isAstNode2(quasi) ? quasi.expressions : void 0;
        if (Array.isArray(expressions)) {
          for (const e of expressions) walk8(e, bound);
        }
        return;
      }
      case "BinaryExpression": {
        const op = n.operator;
        if (op === ";" || op === "=") {
          unsafe();
          return;
        }
        walk8(n.left, bound);
        walk8(n.right, bound);
        return;
      }
      case "UnaryExpression":
        walk8(n.argument, bound);
        return;
      case "ConditionalExpression":
        walk8(n.test, bound);
        walk8(n.consequent, bound);
        walk8(n.alternate, bound);
        return;
      case "MatchExpression": {
        const scrutinee = n.scrutinee;
        if (scrutinee !== void 0) walk8(scrutinee, bound);
        const arms = n.arms;
        if (!Array.isArray(arms)) return;
        for (const arm of arms) {
          if (!isAstNode2(arm)) continue;
          const test = arm.test;
          const isDefault = isAstNode2(test) && test.type === "Identifier" && test.name === "_";
          if (!isDefault) walk8(test, bound);
          walk8(arm.consequent, bound);
        }
        return;
      }
      case "MemberExpression": {
        walk8(n.object, bound);
        if (n.computed) walk8(n.property, bound);
        return;
      }
      case "ArrayExpression": {
        const elements = n.elements;
        if (Array.isArray(elements)) {
          for (const el of elements) {
            if (el === null) continue;
            if (isAstNode2(el) && el.type === "SpreadElement") {
              walk8(el.argument, bound);
            } else {
              walk8(el, bound);
            }
          }
        }
        return;
      }
      case "ObjectExpression": {
        const properties = n.properties;
        if (!Array.isArray(properties)) return;
        for (const prop5 of properties) {
          if (!isAstNode2(prop5)) continue;
          if (prop5.type === "SpreadElement") {
            walk8(prop5.argument, bound);
            continue;
          }
          const computed = Boolean(prop5.computed);
          const key = prop5.key;
          if (computed) walk8(key, bound);
          if (prop5.shorthand) {
            if (isAstNode2(key) && key.type === "Identifier" && typeof key.name === "string") {
              checkIdentifier(key.name, bound);
            }
            continue;
          }
          walk8(prop5.value, bound);
        }
        return;
      }
      case "TemplateLiteral": {
        const { expressions } = n;
        if (Array.isArray(expressions)) {
          for (const e of expressions) walk8(e, bound);
        }
        return;
      }
      case "LetExpression": {
        const id = n.id;
        walk8(n.init, bound);
        const body2 = n.body;
        if (body2 !== void 0) {
          walk8(
            body2,
            typeof id === "string" ? /* @__PURE__ */ new Set([...bound, id]) : bound
          );
        }
        return;
      }
      case "ArrowFunctionExpression":
        walk8(
          n.body,
          /* @__PURE__ */ new Set([...bound, ...arrowParamNames(n)])
        );
        return;
      case "CallExpression": {
        const callee = n.callee;
        const rawArgs = n.arguments;
        const args = Array.isArray(rawArgs) ? rawArgs : [];
        if (isAstNode2(callee) && callee.type === "Identifier") {
          const name = typeof callee.name === "string" ? callee.name : "";
          if (bound.has(name)) {
            unsafe();
            return;
          }
          if (HOF_NAMES2.has(name)) {
            args.forEach((arg, i) => {
              if (i === 1) {
                const inner = /* @__PURE__ */ new Set([...bound, ...callbackBindings(name, arg)]);
                const cbBody = isAstNode2(arg) && arg.type === "ArrowFunctionExpression" ? arg.body : arg;
                walk8(cbBody, inner);
              } else {
                walk8(arg, bound);
              }
            });
            return;
          }
          if (orgFnNames.has(name)) {
            deps.add(name);
          } else if (!CALLABLE_FUNCTION_NAMES.has(name) || IMPURE_OR_CONTEXT_BUILTINS.has(name)) {
            unsafe();
            return;
          }
          for (const arg of args) walk8(arg, bound);
          return;
        }
        if (isAstNode2(callee) && callee.type === "MemberExpression") {
          if (callee.computed) {
            unsafe();
            return;
          }
          const prop5 = callee.property;
          const method = isAstNode2(prop5) && typeof prop5.name === "string" ? prop5.name : "";
          walk8(callee.object, bound);
          if (HOF_NAMES2.has(method)) {
            args.forEach((arg, i) => {
              if (i === 0) {
                const inner = /* @__PURE__ */ new Set([
                  ...bound,
                  ...callbackBindings(method, arg)
                ]);
                const cbBody = isAstNode2(arg) && arg.type === "ArrowFunctionExpression" ? arg.body : arg;
                walk8(cbBody, inner);
              } else {
                walk8(arg, bound);
              }
            });
            return;
          }
          if (!PURE_METHOD_WHITELIST.has(method)) {
            unsafe();
            return;
          }
          for (const arg of args) walk8(arg, bound);
          return;
        }
        unsafe();
        return;
      }
      default:
        unsafe();
        return;
    }
  };
  walk8(ast, new Set(params));
  return { selfSafe: safe, deps };
};
var analyzeMemoSafety = (fns, constNames = /* @__PURE__ */ new Set()) => {
  const byName = /* @__PURE__ */ new Map();
  const duplicated = /* @__PURE__ */ new Set();
  for (const f of fns) {
    if (byName.has(f.name)) duplicated.add(f.name);
    byName.set(f.name, f);
  }
  const uniqueNames = new Set(
    [...byName.keys()].filter((n) => !duplicated.has(n))
  );
  const analyses = /* @__PURE__ */ new Map();
  for (const name of uniqueNames) {
    const f = byName.get(name);
    const body = stripAt(f.compute);
    if (body === void 0) {
      analyses.set(name, { selfSafe: false, deps: /* @__PURE__ */ new Set() });
      continue;
    }
    analyses.set(name, analyzeOne(body, paramNamesOf(f.parameters), uniqueNames, constNames));
  }
  const safe = /* @__PURE__ */ new Set();
  let changed = true;
  while (changed) {
    changed = false;
    for (const [name, a] of analyses) {
      if (safe.has(name) || !a.selfSafe) continue;
      if ([...a.deps].every((d) => safe.has(d))) {
        safe.add(name);
        changed = true;
      }
    }
  }
  return safe;
};
var defCache = /* @__PURE__ */ new WeakMap();
var collectFunctionNodes = (node2, out) => {
  if (node2.type === FUNCTION) {
    out.push({
      name: node2.name,
      compute: node2.compute,
      parameters: node2.parameters
    });
  }
  if (Array.isArray(node2.children)) {
    for (const child of node2.children) collectFunctionNodes(child, out);
  }
};
var collectConstNames = (node2, out) => {
  if (node2.type === CONST && typeof node2.name === "string") out.add(node2.name);
  if (Array.isArray(node2.children)) {
    for (const child of node2.children) collectConstNames(child, out);
  }
};
var memoSafeFunctionNames = (def) => {
  const cached = defCache.get(def);
  if (cached) return cached;
  const fns = [];
  collectFunctionNodes(def, fns);
  const constNames = /* @__PURE__ */ new Set();
  collectConstNames(def, constNames);
  const safe = analyzeMemoSafety(fns, constNames);
  defCache.set(def, safe);
  return safe;
};

// src/orgicore/selectors/appstate/evaluation/createEvalContext.ts
var EMPTY_REPO = { objects: [] };
var evalTracker = /* @__PURE__ */ new Set();
var createEvalContext = (appState, mainContext, selectValue2, selectOutput2, evaluateAt) => {
  const mainScope = mainContext.scope;
  const getValue = (key) => {
    const value = selectValue2(appState, key);
    if (isRight(value)) {
      return value.right;
    }
    return void 0;
  };
  const getDataFallback = (name) => {
    var _a2;
    if (isGlobalScope(mainScope)) {
      return void 0;
    }
    const canonicalKey = compactJoin([mainScope.key, name]);
    const stored = (_a2 = selectDataByPointer(appState)[canonicalKey]) == null ? void 0 : _a2.value;
    if (stored !== void 0) {
      return stored;
    }
    const scopeValue = getValue(mainScope.key);
    if (R10.isPlainObject(scopeValue)) {
      const dataObj = scopeValue;
      if (name in dataObj) {
        return dataObj[name];
      }
    }
    return void 0;
  };
  const findSpanMeta = () => {
    let scope = mainScope;
    while (scope) {
      const meta = readSpanMeta(getValue(scope.key));
      if (meta) return meta;
      scope = scope.parent;
    }
    return void 0;
  };
  const resolveSpanLength = () => {
    let scope = mainScope;
    while (scope) {
      const meta = readSpanMeta(getValue(scope.key));
      if (meta) return countCoveredSlots(scope, meta);
      scope = scope.parent;
    }
    return void 0;
  };
  const countCoveredSlots = (rowScope, meta) => {
    var _a2;
    const hoistedResult = safeSelectPointer(appState, rowScope.key);
    if (!isRight(hoistedResult)) return void 0;
    const hoisted = hoistedResult.right;
    const span2 = resolveSpan((_a2 = hoisted.node.data) == null ? void 0 : _a2.span);
    if (!span2 || span2.mode !== "grid" || !span2.slots) return void 0;
    let supplierKey;
    try {
      const nodeName = convertVarNameToUniqueNodeName(
        appState.def,
        hoisted.node,
        span2.slots
      );
      supplierKey = selectBestPointerKey(appState, rowScope.key, nodeName).key;
    } catch {
      return void 0;
    }
    const slots = selectOutput2(appState, supplierKey);
    if (!Array.isArray(slots)) return void 0;
    const bounds = { start: meta.start, end: meta.end };
    let count = 0;
    for (let i = 0; i < slots.length; i += 1) {
      const axis2 = evaluateAt(appState, span2.axis, `${supplierKey}[${i}]`);
      if (!isRight(axis2) || axis2.right === void 0) continue;
      if (covers(bounds, axis2.right)) count += 1;
    }
    return count;
  };
  const tryResolveSpecialVariable = (name) => {
    var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
    if (name === "$index") {
      const index = isGlobalScope(mainScope) ? void 0 : mainScope.index;
      return right(index);
    }
    if (name === "$parentIndex") {
      return right((_a2 = mainScope.parent) == null ? void 0 : _a2.index);
    }
    if (name === "$scopeKey") {
      return right(mainScope.key);
    }
    if (name === "$state") {
      return right(appState);
    }
    if (name === "$data") {
      return right(
        selectObjectsByType((_b = appState.repo) != null ? _b : EMPTY_REPO)
      );
    }
    if (name === "$spanStart" || name === "$spanEnd" || name === "$spanPosition") {
      const meta = findSpanMeta();
      if (name === "$spanStart") return right(meta == null ? void 0 : meta.start);
      if (name === "$spanEnd") return right(meta == null ? void 0 : meta.end);
      return right(meta == null ? void 0 : meta.position);
    }
    if (name === "$spanLength") {
      return right(resolveSpanLength());
    }
    if (name === "$rows") {
      const rowsFn = (subsectionName) => {
        if (!R10.isString(subsectionName)) {
          return void 0;
        }
        let output;
        let records;
        try {
          const nodeName = convertVarNameToUniqueNodeName(
            appState.def,
            mainContext.node,
            subsectionName
          );
          const key = selectBestPointerKey(
            appState,
            mainScope.key,
            nodeName
          ).key;
          output = selectOutput2(appState, key);
          records = getValue(key);
        } catch {
          return void 0;
        }
        if (!Array.isArray(output)) {
          return void 0;
        }
        return output.map((input, i) => {
          const rec = Array.isArray(records) ? records[i] : void 0;
          return R10.isPlainObject(rec) && R10.isPlainObject(input) ? { ...rec, ...input } : input != null ? input : rec;
        });
      };
      return right(rowsFn);
    }
    if (name === "$args") {
      return right((_c = selectCursorArgs(appState)) != null ? _c : {});
    }
    if (name === "$speech") {
      const ctx = selectAppContext(appState);
      const speech = ctx == null ? void 0 : ctx.speech;
      return right(speech != null ? speech : { text: null, lang: null });
    }
    if (name === "$variant") {
      const ctx = selectAppContext(appState);
      return right(ctx == null ? void 0 : ctx.variant);
    }
    if (name === "$now") {
      return right((_d = selectAppContext(appState)) == null ? void 0 : _d.now);
    }
    if (name === "$email") {
      return right((_e = selectAppContext(appState)) == null ? void 0 : _e["$email"]);
    }
    if (name === "$username") {
      return right((_f = selectAppContext(appState)) == null ? void 0 : _f["$username"]);
    }
    if (name === "$userKey") {
      return right((_g = selectAppContext(appState)) == null ? void 0 : _g["$userKey"]);
    }
    if (name === "$authKind") {
      return right((_h = selectAppContext(appState)) == null ? void 0 : _h["$authKind"]);
    }
    if (name === "$authUid") {
      return right((_i = selectAppContext(appState)) == null ? void 0 : _i["$authUid"]);
    }
    if (name === "$connections") {
      return right((_j = selectAppContext(appState)) == null ? void 0 : _j["$connections"]);
    }
    if (name === "$social") {
      return right((_k = selectAppContext(appState)) == null ? void 0 : _k["$social"]);
    }
    if (name === "$isOrgAdmin") {
      return right((_l = selectAppContext(appState)) == null ? void 0 : _l["$isOrgAdmin"]);
    }
    if (name === "$orgUser") {
      return right((_m = selectAppContext(appState)) == null ? void 0 : _m["$orgUser"]);
    }
    return left("not_resolved");
  };
  const tryResolveValueFromContext = (name) => {
    const context = selectAppContext(appState);
    const valueFromCtx = context == null ? void 0 : context[name];
    if (valueFromCtx !== void 0) {
      return right(valueFromCtx);
    }
    return left("not_resolved");
  };
  const tryResolveFromScope = (name, lookupContext, pointerKey) => {
    var _a2, _b, _c;
    if (evalTracker.has(pointerKey)) {
      const data = selectDataByPointer(appState);
      const storedValue = (_a2 = data[pointerKey]) == null ? void 0 : _a2.value;
      if (storedValue !== void 0) {
        return storedValue;
      }
      return getDataFallback(name);
    }
    evalTracker.add(pointerKey);
    try {
      const r = safeSelectPointer(appState, pointerKey);
      if (isLeft(r)) {
        return getDataFallback(name);
      }
      const { key, node: node2 } = r.right;
      if (node2.type === FUNCTION && lookupContext.isCall) {
        const orgFn = {
          __orgFunction: true,
          compute: node2.compute,
          parameters: node2.parameters,
          key,
          memoSafe: memoSafeFunctionNames(appState.def).has(node2.name)
        };
        return orgFn;
      }
      const value = getValue(key);
      if (isComposite(node2)) {
        const { memberLookupContexts } = lookupContext;
        const usePath = node2.isRepeating ? ((_b = memberLookupContexts[0]) == null ? void 0 : _b.type) === "computed" : ((_c = memberLookupContexts[0]) == null ? void 0 : _c.type) !== "computed";
        const path = usePath ? memberLookupContexts.map((c) => c.property) : void 0;
        const output = selectOutput2(appState, key, path);
        if (value === void 0) {
          return output;
        }
        if (Array.isArray(value) && Array.isArray(output)) {
          return value.map((v, i) => {
            const base = output[i];
            return R10.isPlainObject(base) && R10.isPlainObject(v) ? { ...base, ...v } : v != null ? v : base;
          });
        }
        if (R10.isPlainObject(value) && R10.isPlainObject(output)) {
          return { ...output, ...value };
        }
        return value;
      } else {
        if (value === void 0) {
          const fallback = getDataFallback(name);
          if (fallback !== void 0) {
            return fallback;
          }
        }
      }
      return value;
    } finally {
      evalTracker.delete(pointerKey);
    }
  };
  const tryResolveFromScopeHierarchy = (name, lookupContext, scope = mainScope) => {
    var _a2;
    return (_a2 = resolveFromScopeHierarchyRec(name, lookupContext, scope)) == null ? void 0 : _a2.value;
  };
  const resolveFromScopeHierarchyRec = (name, lookupContext, scope) => {
    const { key, isDeclared } = selectBestPointerKey(appState, scope.key, name);
    const storedAt = (k) => {
      var _a2;
      return (_a2 = selectDataByPointer(appState)[k]) == null ? void 0 : _a2.value;
    };
    if (isDeclared) {
      const value = tryResolveFromScope(name, lookupContext, key);
      if (value !== void 0) {
        return { value, isStored: storedAt(key) !== void 0 };
      }
    } else if (scope.parent) {
      const storedAtScopedKey = storedAt(key);
      if (storedAtScopedKey !== void 0) {
        return { value: storedAtScopedKey, isStored: true };
      }
      const parentResolution = resolveFromScopeHierarchyRec(
        name,
        lookupContext,
        scope.parent
      );
      if (parentResolution == null ? void 0 : parentResolution.isStored) {
        return parentResolution;
      }
      const value = tryResolveFromScope(name, lookupContext, key);
      if (value !== void 0) {
        return { value, isStored: false };
      }
      if (parentResolution !== void 0) {
        return parentResolution;
      }
    } else {
      const value = tryResolveFromScope(name, lookupContext, key);
      if (value !== void 0) {
        return { value, isStored: storedAt(key) !== void 0 };
      }
    }
    return void 0;
  };
  const repoByIdFn = (typeName, id) => {
    var _a2, _b;
    if (!R10.isString(typeName) || !R10.isString(id)) return void 0;
    const index = selectObjectsById((_a2 = appState.repo) != null ? _a2 : EMPTY_REPO);
    return (_b = index[typeName]) == null ? void 0 : _b[id];
  };
  return (varName, lookupContext) => {
    const specialVariable = tryResolveSpecialVariable(varName);
    if (isRight(specialVariable)) {
      return specialVariable.right;
    }
    const valueFromCtx = tryResolveValueFromContext(varName);
    if (isRight(valueFromCtx)) {
      return valueFromCtx.right;
    }
    const nodeName = convertVarNameToUniqueNodeName(
      appState.def,
      mainContext.node,
      varName
    );
    const scopeValue = tryResolveFromScopeHierarchy(nodeName, lookupContext);
    if (scopeValue !== void 0) {
      return scopeValue;
    }
    if (varName === "byId") {
      return repoByIdFn;
    }
    return void 0;
  };
};

// src/orgicore/selectors/appstate/evaluation/createSelectChildren.ts
import * as R11 from "remeda";
import { createSelector as createSelector8 } from "reselect";
var createSelectChildren = (selectLength2) => {
  const getVirtualChildNodes = (node2) => {
    if (node2.type === TREE && node2.isRepeating) {
      return [node2];
    }
    return [];
  };
  const getChildNodes2 = (node2) => {
    return getChildNodes(node2).concat(getVirtualChildNodes(node2));
  };
  const selectChildNodes = createSelector8(selectPointer, (p) => {
    return getChildNodes2(p.node);
  });
  const selectChildIterationPointers2 = createSelector8(selectAppState, selectPointer, (s, p) => {
    if (p.node.isOwnScope) {
      return [selectPointer(s, `${p.key}[0]`)];
    }
    const len = getRight(selectLength2(s, p.key));
    return R11.times(len, (i) => selectPointer(s, `${p.key}[${i}]`));
  });
  const selectChildNodePointers2 = createSelector8(
    selectAppState,
    selectPointer,
    (s, p) => {
      return selectChildNodes(s, p.key).map((c) => {
        const key = selectUniqueKey(s.def, c);
        return selectPointer(s, `${p.scope.key}.${key}`);
      });
    }
  );
  return {
    selectChildIterationPointers: selectChildIterationPointers2,
    selectChildNodePointers: selectChildNodePointers2
  };
};

// src/orgicore/selectors/appstate/evaluation/createSelectOutput.ts
import { createSelector as createSelector9 } from "reselect";

// src/orgicore/selectors/helpers/pointer.ts
var isLoop = (p) => {
  return !!(p.node.isRepeating && !p.isIteration);
};
var getTreeOptions = (p) => {
  if (p.node.type === TREE) {
    return p.node;
  }
  return { nodeTypes: {}, rootType: "" };
};
var isScopedPointer = (p) => {
  return isScoped(p.node) && !p.isIteration;
};
var isOwnScopePointer = (p) => {
  return !!p.node.isOwnScope && !p.isIteration;
};
var isNonRootTreeNode = (p) => {
  const { isIteration, node: node2 } = p;
  return isIteration && node2.type === TREE;
};
var isRootTreeNode = (p) => {
  return p.node.type === TREE && p.scope.node !== p.node;
};
var isTreeNode = (p) => {
  return isRootTreeNode(p) || isNonRootTreeNode(p);
};
var isInputField = (p) => {
  return isInputFieldType(p.node.type);
};
var getTreeNodeChildrenKey = (p) => {
  const { key, node: node2 } = p;
  if (isRootTreeNode(p)) {
    return key;
  }
  return `${key}.${node2.name}`;
};
var isSubmitContextPointer = (p) => {
  return isSubmitContextNode(p.node) && !p.isIteration;
};

// src/orgicore/selectors/appstate/evaluation/createSelectOutput.ts
var isObj = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var isFlatten = (p) => {
  if (!isComposite(p.node)) {
    return false;
  }
  if (p.node.isRepeating) {
    return false;
  }
  if (p.node.isOwnScope) {
    return false;
  }
  return true;
};
var createOutputSelector = (selectValue2, selectChildIterationPointers2, selectChildNodePointers2) => {
  const getOutputValue = (s, p, isTreeRoot, path = []) => {
    var _a2;
    const { key, node: node2 } = p;
    const childPath = path.length ? path.slice(1) : [];
    if (node2.isTransient) {
      return void 0;
    }
    if (isLeaf(node2)) {
      const value = selectValue2(s, key);
      return isRight(value) ? value.right : void 0;
    }
    if (isScopedPointer(p) && !isTreeRoot) {
      if (p.node.isOwnScope) {
        const obj = getOutputObject(s, selectPointer(s, `${key}[0]`), path);
        if (path.length === 0 && Object.keys(obj).length === 0) {
          return void 0;
        }
        return obj;
      }
      if (path.length && /^\d+$/.test(path[0])) {
        const el = path[0];
        const iteration = selectPointer(s, `${key}[${el}]`);
        const obj = getOutputObject(s, iteration, childPath);
        return { [el]: obj };
      } else if (p.node.isRecord) {
        const keyField = (_a2 = p.node.recordKey) != null ? _a2 : "key";
        const valueField = typeof p.node.recordValue === "string" ? p.node.recordValue : void 0;
        const iterations = selectChildIterationPointers2(s, key);
        if (valueField !== void 0 && iterations.length === 0) {
          return void 0;
        }
        return iterations.reduce((acc, ip) => {
          const { [keyField]: k, ...rest } = getOutputObject(s, ip, childPath);
          if (typeof k !== "string") return acc;
          if (valueField === void 0) {
            acc[k] = rest;
            return acc;
          }
          const scalar = rest[valueField];
          if (scalar !== void 0) acc[k] = scalar;
          return acc;
        }, {});
      } else {
        const iterations = selectChildIterationPointers2(s, key);
        return iterations.map((p2) => getOutputObject(s, p2, childPath));
      }
    }
    return getOutputObject(s, p, path);
  };
  const getOutputObject = (s, p, path) => {
    const output = {};
    const childPath = path.length ? path.slice(1) : [];
    const assignChild = (child, valuePath) => {
      const { node: node2 } = child;
      const { name } = node2;
      const isRoot = isRootTreeNode(child) && p.node !== child.node;
      const value = getOutputValue(s, child, isRoot, valuePath);
      if (value !== void 0) {
        if (isFlatten(child)) {
          Object.assign(output, value);
        } else {
          output[name] = value;
        }
      }
    };
    const nodePointers = selectChildNodePointers2(s, p.key);
    if (!path.length) {
      nodePointers.forEach((child) => assignChild(child, childPath));
      return output;
    }
    nodePointers.forEach((child) => {
      if (child.node.name === path[0]) {
        assignChild(child, childPath);
      } else if (isFlatten(child)) {
        assignChild(child, path);
      }
    });
    return output;
  };
  const getOutput = (s, p, path) => {
    return getOutputValue(s, p, isRootTreeNode(p), path);
  };
  const selectOutput2 = createSelector9(
    selectAppState,
    selectPointer,
    optionalStringArrayProp2,
    getOutput
  );
  const selectOutputObject2 = createSelector9(
    selectAppState,
    selectPointer,
    (s, p) => {
      const output = getOutput(s, p);
      return isObj(output) ? output : {};
    }
  );
  return {
    selectOutput: selectOutput2,
    selectOutputObject: selectOutputObject2
  };
};

// src/orgicore/selectors/appstate/evaluation/appContextNames.ts
var APP_CONTEXT_NAMES = [
  "orgName",
  "user",
  "email",
  "username",
  "displayName",
  "role",
  "timeAgo",
  "speech",
  "variant",
  "now"
];

// src/orgicore/selectors/appstate/evaluation/index.ts
var optionalStaticContextProp3 = (_a2, _b, _c, d) => d;
var selectEvaluation = createSelector10(
  selectAppState,
  stringProp1,
  optionalStringProp2,
  optionalStaticContextProp3,
  (appState, formula, pointerKey = "", staticContext) => {
    const evalContext = selectEvalContext(appState, pointerKey);
    const ctx = staticContext ? pushScope(toDynamicContext(evalContext), staticContext) : evalContext;
    return evaluate(formula, ctx, staticContext);
  }
);
var selectEvalContext = createSelector10(
  selectAppState,
  selectPointer,
  (appState, p) => {
    return createEvalContext(
      appState,
      p,
      selectValue,
      selectOutput,
      (state, formula, pointerKey) => selectEvaluation(state, formula, pointerKey)
    );
  }
);
var evaluateScriptInState = (appState, pointerKey, script, hosts, staticContext) => {
  const evalContext = selectEvalContext(appState, pointerKey);
  const ctx = staticContext ? pushScope(toDynamicContext(evalContext), staticContext) : evalContext;
  return evalScriptAsync(script, ctx, hosts);
};
var { selectValue, selectComputedValue, selectLength } = createSelectors(selectEvaluation);
var { selectChildIterationPointers, selectChildNodePointers } = createSelectChildren(selectLength);
var { selectOutput, selectOutputObject } = createOutputSelector(
  selectValue,
  selectChildIterationPointers,
  selectChildNodePointers
);
var createTypedValueSelector = (codec) => {
  return createSelector10(selectValue, (value) => {
    return decode(value, codec, "value");
  });
};
var createTypedValueSelectorWithStringCoercion = (codec) => {
  return createSelector10(selectValue, (value) => {
    return decodeWithStringCoercion(value, codec, "value");
  });
};
var createTypedComputedValueSelector = (codec) => {
  return createSelector10(selectComputedValue, (value) => {
    return decode(value, codec, "value");
  });
};
var selectOptionalStringValue = createTypedValueSelectorWithStringCoercion(optionalString);
var selectOptionalStringArrayValue = createTypedValueSelector(
  t18.union([t18.undefined, t18.array(t18.string)])
);
var selectOptionalNumberArrayValue = createTypedValueSelector(
  t18.union([t18.undefined, t18.array(t18.number)])
);
var selectStringValue = createTypedValueSelectorWithStringCoercion(
  t18.string
);
var selectOptionalNumberValue = createTypedValueSelector(optionalNumber);
var selectOptionalBooleanValue = createTypedValueSelector(optionalBoolean);
var selectOptionalObjectValue = createTypedValueSelector(
  t18.union([t18.undefined, unknownRecord])
);
var selectOptionalObjectComputedValue = createTypedComputedValueSelector(t18.union([t18.undefined, unknownRecord]));
var selectOptionalTreeNodeValue = createTypedValueSelector(
  t18.union([t18.undefined, treeNodeValue])
);
var selectFileUploadValue = createTypedValueSelector(
  fileUploadValueOrArray
);
var evalAndDecode = (s, p, formula, targetCodec) => {
  return decode(
    selectEvaluation(s, formula, p.scope.key),
    targetCodec,
    formula
  );
};

// src/orgicore/selectors/appstate/structureSelectors.ts
import { createSelector as createSelector11 } from "reselect";
var selectChildPointers = createSelector11(
  selectAppState,
  selectPointer,
  (s, p) => {
    if (isScopedPointer(p)) {
      return selectChildIterationPointers(s, p.key);
    } else {
      return selectChildNodePointers(s, p.key);
    }
  }
);
var selectChildPointersToRender = createSelector11(selectAppState, selectPointer, (s, p) => {
  const pKey = isOwnScopePointer(p) ? `${p.key}[0]` : p.key;
  return selectChildPointers(s, pKey);
});
var selectClosestAncestor = createSelector11(
  selectAppState,
  selectParent,
  pointerPredicate2,
  (s, parent, predicate = isTrue) => {
    if (!parent) {
      return void 0;
    }
    if (predicate == null ? void 0 : predicate(parent)) {
      return parent;
    }
    return selectClosestAncestor(s, parent.key, predicate);
  }
);
var selectSiblings = createSelector11(
  selectAppState,
  selectPointer,
  selectParent,
  (s, p, parent) => {
    if (parent) {
      return selectChildPointers(s, parent.key);
    }
    return [p];
  }
);
var selectIsSameOrAncestorOf = createSelector11(
  selectAppState,
  selectPointer,
  selectPointer2,
  (s, p1, p2) => {
    if (p1.key === p2.key) {
      return true;
    }
    const parent = selectParent(s, p2.key);
    if (!parent) {
      return false;
    }
    return selectIsSameOrAncestorOf(s, p1.key, parent.key);
  }
);

// src/orgicore/selectors/appstate/labelSelectors.ts
import * as t20 from "io-ts";
import { createSelector as createSelector13 } from "reselect";

// src/orgicore/selectors/appstate/utils.ts
import * as t19 from "io-ts";
import { createSelector as createSelector12 } from "reselect";
var createPropSelector = (targetCodec, propName, defaultValue) => {
  const propGetter = createSelectPropDef(targetCodec, propName, defaultValue);
  const propFromPointer = createSelector12(
    selectPointerNode,
    TEMPLATE_PROPS.has(propName) ? (node2) => inferTemplateFormula(propGetter(node2)) : propGetter
  );
  return createSelector12(
    selectAppState,
    selectPointer,
    propFromPointer,
    (s, p, prop5) => evaluatePropAndDecode(s, p, prop5, selectEvaluation)
  );
};
var TEMPLATE_PROPS = /* @__PURE__ */ new Set(["htmlTemplate"]);
var inferTemplateFormula = (prop5) => !prop5.isFormula && t19.string.is(prop5.value) && needsTemplateWrapper(prop5.value) ? {
  name: prop5.name,
  isFormula: true,
  value: tryStripFormulaPrefix(asTemplateFormula(prop5.value)),
  targetCodec: prop5.targetCodec
} : prop5;

// src/orgicore/selectors/appstate/labelSelectors.ts
var selectNonIterationLabel = createPropSelector(
  t20.string,
  "label",
  (node2) => camelCaseToLabel(node2.name)
);
var selectOptionalIterationLabel = createPropSelector(
  t20.unknown,
  "iterationLabel"
);
var selectIterationLabel = createSelector13(
  selectAppState,
  selectPointer,
  selectOptionalIterationLabel,
  (appState, pointer, iterationLabel) => {
    const { node: node2 } = pointer;
    if (isLeft(iterationLabel)) {
      return iterationLabel;
    }
    const label2 = iterationLabel.right;
    if (label2 !== void 0 && label2 !== null && label2 !== "") {
      return right(String(label2));
    }
    const formula = selectDefaultIterationLabelFormula(node2);
    return evalAndDecode(appState, pointer, formula, t20.string);
  }
);
var selectLabel = createSelector13(
  selectAppState,
  selectPointer,
  (appState, pointer) => {
    const { key, isIteration } = pointer;
    if (isIteration) {
      return selectIterationLabel(appState, key);
    } else {
      return selectNonIterationLabel(appState, key);
    }
  }
);

// src/orgicore/selectors/appstate/navSelectors.ts
import * as R12 from "remeda";
import { createSelector as createSelector14 } from "reselect";

// src/orgicore/selectors/helpers/mergeShiftOptions.ts
var andPredicates = (...predicates) => (p) => predicates.every((predicate) => predicate(p));
var mergePredicates = (predicate1, predicate2) => {
  if (predicate1 === predicate2) {
    return predicate1;
  }
  return andPredicates(predicate1, predicate2);
};
var mergeShiftOptions = (o1, o2) => {
  return {
    direction: o1.direction,
    include: mergePredicates(o1.include, o2.include),
    traverseChildren: mergePredicates(o1.traverseChildren, o2.traverseChildren),
    traverseParent: mergePredicates(o1.traverseParent, o2.traverseParent)
  };
};

// src/orgicore/selectors/appstate/navSelectors.ts
var createGetSibling = (direction) => (s, p) => {
  const parent = selectParent(s, p.key);
  if (!parent) {
    return void 0;
  }
  const siblings = selectChildPointers(s, parent.key);
  const index = siblings.indexOf(p);
  if (index === -1) {
    return void 0;
  }
  const offset = direction === "back" ? -1 : 1;
  const sibling = siblings[index + offset];
  if (sibling) {
    return sibling;
  }
};
var getNextSibling = createGetSibling("forward");
var getPreviousSibling = createGetSibling("back");
var getDeepestLastChild = (s, p, options) => {
  if (!options.traverseChildren(p)) {
    return p;
  }
  const children2 = selectChildPointers(s, p.key);
  const lastChild = R12.last(children2);
  if (!lastChild) {
    return p;
  }
  return getDeepestLastChild(s, lastChild, options);
};
var getPreviousSiblingOrPop = (s, p, options) => {
  const previousSibling = getPreviousSibling(s, p);
  if (previousSibling) {
    return getDeepestLastChild(s, previousSibling, options);
  }
  return selectParent(s, p.key);
};
var getNextSiblingOrPop = (s, p, options) => {
  const nextSibling = getNextSibling(s, p);
  if (nextSibling) {
    return nextSibling;
  }
  const parent = selectParent(s, p.key);
  if (parent && options.traverseParent(parent)) {
    return getNextSiblingOrPop(s, parent, options);
  }
  return void 0;
};
var selectPreviousNode = createSelector14(
  selectAppState,
  selectPointer,
  traverseOptions2,
  (s, p, options = defaultShiftOptions) => {
    return getPreviousSiblingOrPop(s, p, options);
  }
);
var selectNextNode = createSelector14(
  selectAppState,
  selectPointer,
  traverseOptions2,
  (s, p, options = defaultShiftOptions) => {
    if (options.traverseChildren(p)) {
      const children2 = selectChildPointers(s, p.key);
      if (children2.length > 0) {
        return children2[0];
      }
    }
    return getNextSiblingOrPop(s, p, options);
  }
);
var isNavRoute = (pointer) => {
  const { node: node2 } = pointer;
  switch (node2.type) {
    case "screen":
      return true;
    default:
      return false;
  }
};
var selectTopLevelNavRoutes = createSelector14(
  selectAppState,
  (s) => selectChildPointers(s, "").filter(isNavRoute)
);
var shift = (s, pKey, options) => {
  const next = options.direction === "back" ? selectPreviousNode(s, pKey, options) : selectNextNode(s, pKey, options);
  if (!next) {
    return void 0;
  }
  if (options.include(next)) {
    return next;
  }
  return shift(s, next.key, options);
};
var shiftCollect = (s, pKey, options) => {
  const collected = [];
  let currentKey = pKey;
  while (currentKey !== void 0) {
    const next = options.direction === "back" ? selectPreviousNode(s, currentKey, options) : selectNextNode(s, currentKey, options);
    if (!next) {
      break;
    }
    if (options.include(next)) {
      collected.push(next);
    }
    currentKey = next.key;
  }
  return collected;
};
var createShiftSelector = (options) => {
  return createSelector14(selectAppState, stringProp1, (s, pKey) => {
    return shift(s, pKey, options);
  });
};
var selectPreviousNavRoute = createShiftSelector(
  createShiftOptions({ include: isNavRoute, direction: "back" })
);
var selectNextNavRoute = createShiftSelector(
  createShiftOptions({ include: isNavRoute, direction: "forward" })
);
var selectParentNavRoute = createSelector14(
  selectAppState,
  stringProp1,
  (s, pKey) => {
    const ancestor = selectClosestAncestor(s, pKey, isNavRoute);
    if (!ancestor) {
      const firstAppNode = selectNextNode(s, "", defaultShiftOptions);
      if (!firstAppNode) {
        return selectRootPointer(s);
      }
      return firstAppNode;
    }
    return ancestor;
  }
);
var selectCurrentPointer = createSelector14(
  selectAppState,
  selectCursor,
  (s, cursor) => {
    const r = safeSelectPointer(s, cursor);
    if (isLeft(r)) {
      console.warn(
        `[orgicore] cursor "${cursor}" does not resolve in the current app def \u2014 falling back to the home screen (${r.left}).`
      );
      return selectRootPointer(s);
    }
    return r.right;
  }
);
var selectCurrentNavRoute = createSelector14(
  selectAppState,
  selectCurrentPointer,
  (s, p) => {
    if (isNavRoute(p)) {
      return p;
    }
    const ancestorNavRoute = selectClosestAncestor(s, p.key, isNavRoute);
    if (ancestorNavRoute) {
      return ancestorNavRoute;
    }
    return selectNextNavRoute(s, p.key) || selectRootPointer(s);
  }
);
var selectCursorToPop = createSelector14(
  selectNavStack,
  (n) => R12.last(n)
);
var selectDefaultPopTarget = createSelector14(
  selectAppState,
  selectCurrentNavRoute,
  (s, currentNavRoute) => {
    return selectParentNavRoute(s, currentNavRoute.key).key;
  }
);
var createSubTreeTraverseOptions = (p1) => {
  return createShiftOptions({
    traverseParent: (p2) => p1 !== p2
  });
};
var selectSubTreeTraverseOptions = createSelector14(selectPointer, createSubTreeTraverseOptions);
var selectVisibleInputTraverseOptions = createSelector14(selectSubTreeTraverseOptions, (subTreeOptions) => {
  return mergeShiftOptions(
    subTreeOptions,
    createShiftOptions({
      include: isInputField
    })
  );
});

// src/orgicore/selectors/appstate/treeSelectors.ts
import { createSelector as createSelector17 } from "reselect";

// src/orgicore/selectors/appstate/generated/propsSelectors.generated.ts
import * as t22 from "io-ts";
import { createSelector as createSelector16 } from "reselect";

// src/orgicore/selectors/appstate/propsSelectors.ts
import * as t21 from "io-ts";
import { createSelector as createSelector15 } from "reselect";
var selectTextVariant = createPropSelector(
  textVariant,
  "variant",
  "basic"
);
var selectNumberVariant = createPropSelector(
  optionalNumberVariant,
  "variant"
);
var selectPageSize = createPropSelector(optionalNumber, "pageSize");
var selectMaxLength = createPropSelector(optionalNumber, "maxLength");
var selectRequired = createPropSelector(optionalBoolean, "required");
var selectHiddenProp = createPropSelector(optionalBoolean, "hidden");
var selectSize = createPropSelector(optional(inputSize), "size");
var selectValidation = createPropSelector(
  optional(many(validationRule)),
  "validation"
);
var selectImageOutputOptions = createPropSelector(
  optional(imageOptions),
  "imageOutputOptions"
);
var selectPluginName = createPropSelector(
  optionalString,
  "pluginName"
);
var defaultLayout = {
  maxColumns: 1
};
var selectLayoutProp = createPropSelector(layout, "layout", defaultLayout);
var selectHidden = createSelector15(
  selectPointer,
  selectHiddenProp,
  (p, hidden) => {
    const {
      node: { type: type14 }
    } = p;
    if ((type14 === VARIABLE || type14 === FUNCTION || type14 === CONST) && isRight(hidden) && hidden.right === void 0) {
      return right(true);
    }
    return hidden;
  }
);
var selectLayout = createSelector15(
  selectPointer,
  selectLayoutProp,
  (p, layout2) => {
    if (p.isIteration) {
      return right(defaultLayout);
    }
    return layout2;
  }
);
var selectFormConfig = createPropSelector(
  formConfigOptions,
  "formConfig"
);
var selectContainerStyle = createPropSelector(
  containerStyle,
  "containerStyle",
  emptyObj
);
var selectDataOptions = createPropSelector(
  dataOptions,
  "data",
  emptyObj
);
var selectPluginParams = createPropSelector(
  optional(t21.array(pluginParam)),
  "pluginParams"
);
var selectCommonNode = createSelector15(
  selectLabel,
  selectHidden,
  (label2, hidden) => {
    return { label: label2, hidden };
  }
);
var selectMissingRequired = createSelector15(
  selectValue,
  selectRequired,
  (value, required) => {
    if (isLeft(required)) {
      return required;
    }
    if (isLeft(value)) {
      return right(false);
    }
    return right(!!required.right && isBlank(value.right));
  }
);
var selectValidationError = createSelector15(
  selectAppState,
  selectPointer,
  selectValidation,
  selectValue,
  (s, p, validation, value) => {
    if (isLeft(validation)) {
      return validation;
    }
    if (isLeft(value) || isBlank(value.right)) {
      return right(void 0);
    }
    const rules = toArray(validation.right);
    for (const rule of rules) {
      const r = selectEvaluation(s, rule.expression, p.scope.key);
      if (isLeft(r)) {
        return left(
          `error evaluating validation rule "${rule.expression}": ${r.left}`
        );
      }
      if (!r.right) {
        return right(resolveErrorMessage(s, rule.errorMessage, p.scope.key));
      }
    }
    return right(void 0);
  }
);
var resolveErrorMessage = (s, message, scopeKey) => {
  if (!message.startsWith(FORMULA_PREFIX)) return message;
  const formula = tryStripFormulaPrefix(message);
  const r = selectEvaluation(s, formula, scopeKey);
  if (isLeft(r) || r.right == null || r.right === "") return formula;
  return String(r.right);
};
var selectHelp = createPropSelector(optionalString, "help");
var selectPlaceholder = createPropSelector(optionalString, "placeholder");
var selectDeepHelp = createPropSelector(optionalString, "deepHelp");
var selectExamples = createPropSelector(
  optional(t21.array(t21.string)),
  "examples"
);
var selectAiEnabled = createPropSelector(optionalBoolean, "aiEnabled");
var isBlank = (value) => {
  if (typeof value === "string") {
    return value.trim() === "";
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value === null || value === void 0;
};
var selectCommonInputNode = createSelector15(
  selectCommonNode,
  selectMissingRequired,
  selectRequired,
  selectValidationError,
  selectHelp,
  selectPlaceholder,
  selectDeepHelp,
  selectExamples,
  selectSize,
  selectAiEnabled,
  (common, missingRequired, required, errorMessage2, help, placeholder2, deepHelp, examples, size, aiEnabled) => {
    return {
      ...common,
      missingRequired,
      required,
      errorMessage: errorMessage2,
      help,
      placeholder: placeholder2,
      deepHelp,
      examples,
      size,
      aiEnabled
    };
  }
);
var selectNumberArrayInputNode = createSelector15(
  selectCommonInputNode,
  selectOptionalNumberArrayValue,
  (common, value) => {
    return { ...common, value };
  }
);
var selectStringArrayInputNode = createSelector15(
  selectCommonInputNode,
  selectOptionalStringArrayValue,
  (common, value) => {
    return { ...common, value };
  }
);
var selectFormNode = createSelector15(
  selectCommonNode,
  selectOptionalObjectValue,
  selectOptionalObjectComputedValue,
  selectFormConfig,
  (common, currentValue, computedValue, config) => {
    return { ...common, currentValue, computedValue, config };
  }
);
var selectPickerVariant = createPropSelector(
  pickerVariant,
  "variant",
  "single"
);
var selectPickerIdiom = createPropSelector(pickerIdiom, "idiom", "select");
var selectPickerOptions = createPropSelector(
  t21.array(stringOrPickerOption),
  "pickerValues"
);
var normalisePickerOption = (opt) => {
  if (typeof opt === "string") {
    return { id: opt, label: opt };
  }
  return opt;
};
var selectPickerValues = createSelector15(
  selectPickerOptions,
  (pickerOptions) => {
    if (isLeft(pickerOptions)) {
      return pickerOptions;
    }
    return right(pickerOptions.right.map(normalisePickerOption));
  }
);
var selectCommonPicker = createSelector15(
  selectCommonInputNode,
  selectPickerVariant,
  selectPickerValues,
  (common, variant, pickerValues) => {
    return { ...common, variant, pickerValues };
  }
);
var selectSinglePickerInputNode = createSelector15(
  selectCommonPicker,
  selectOptionalStringValue,
  selectPickerIdiom,
  (common, value, idiom) => {
    return { ...common, value, idiom };
  }
);
var selectMultiPickerInputNode = createSelector15(
  selectCommonPicker,
  selectOptionalStringArrayValue,
  (common, value) => {
    return { ...common, value };
  }
);
var getTreeVarSuffix = (p) => {
  if (isRootTreeNode(p)) {
    return "";
  }
  return `${p.key}.`;
};
var treeNodePropKey = (propType) => (p) => {
  return `${getTreeVarSuffix(p)}${propType}`;
};
var valueKey = treeNodePropKey("value");
var expandedKey = treeNodePropKey("expanded");
var selectValueKey = createSelector15(selectPointer, valueKey);
var selectExpandedKey = createSelector15(selectPointer, expandedKey);
var selectTreeNodeValue = createSelector15(
  selectAppState,
  selectValueKey,
  selectOptionalTreeNodeValue
);
var selectTreeNodeExpanded = createSelector15(
  selectAppState,
  selectExpandedKey,
  selectOptionalBooleanValue
);
var selectTreeNode = createSelector15(
  selectPointer,
  selectCommonNode,
  selectTreeNodeValue,
  selectTreeNodeExpanded,
  (p, common, value, expanded) => {
    const isRoot = isRootTreeNode(p);
    return { ...common, value, expanded, isRoot: right(isRoot) };
  }
);
var selectCommonProps = createSelector15(
  selectCommonNode,
  getUnwrappedProps
);
var selectCommonInputProps = createSelector15(selectCommonInputNode, getUnwrappedProps);
var selectFormProps = createSelector15(
  selectFormNode,
  getUnwrappedProps
);
var selectNumberArrayInputProps = createSelector15(selectNumberArrayInputNode, getUnwrappedProps);
var selectStringArrayInputProps = createSelector15(selectStringArrayInputNode, getUnwrappedProps);
var selectCommonPickerProps = createSelector15(selectCommonPicker, getUnwrappedProps);
var selectSinglePickerInputProps = createSelector15(selectSinglePickerInputNode, getUnwrappedProps);
var selectMultiPickerInputProps = createSelector15(selectMultiPickerInputNode, getUnwrappedProps);
var selectTreeProps = createSelector15(
  selectTreeNode,
  getUnwrappedProps
);
var selectPluginNode = createSelector15(
  selectPluginName,
  selectPluginParams,
  (name, params) => {
    return { name, params };
  }
);
var selectPluginProps = createSelector15(
  selectPluginNode,
  getUnwrappedProps
);
var selectCalculatedPluginProps = createSelector15(
  selectAppState,
  selectPointer,
  selectPluginProps,
  (s, p, { name, params = [] }) => {
    const obj = {};
    for (const param of params) {
      if (param.type === "static") {
        obj[param.name] = param.value;
      }
      if (param.type === "dynamic") {
        const r = selectEvaluation(s, param.formula, p.scope.key);
        if (isLeft(r)) {
          return left(
            `Error evaluating plugin "${name}" param "${param.name}": ${r.left}`
          );
        }
        obj[param.name] = r.right;
      }
    }
    return right(obj);
  }
);
var createTypedPluginPropsSelector = (codec) => {
  return createSelector15(selectCalculatedPluginProps, (r) => {
    return decode(r, codec, "pluginProps");
  });
};
var selectPaginationInfo = createSelector15(
  selectAppState,
  selectPointer,
  selectPageSize,
  selectMaxLength,
  (appState, p, pageSizeOption, maxLengthOption) => {
    const { node: node2, key, isIteration } = p;
    if (isIteration) {
      return void 0;
    }
    const pageSize = getRight(pageSizeOption);
    const maxLength = getRight(maxLengthOption);
    if (pageSize === void 0) {
      return void 0;
    }
    let totalLength = maxLength != null ? maxLength : Infinity;
    const dataOptions2 = node2.data;
    if (isDataQuery(dataOptions2)) {
      const valueResult = selectValue(appState, key);
      if (isRight(valueResult) && Array.isArray(valueResult.right)) {
        const dataLength = valueResult.right.length;
        totalLength = maxLength !== void 0 ? Math.min(dataLength, maxLength) : dataLength;
      }
    }
    const currentLength = getRight(selectLength(appState, key));
    return {
      currentLength,
      totalLength,
      hasMore: currentLength < totalLength,
      pageSize
    };
  }
);

// src/orgicore/selectors/appstate/selectorOverrides.ts
var selectorOverrides = {
  // A bare `{ type: "appNode" }` with no variant (only reachable from legacy
  // data — every authored form sets one, #2090) is a plain container, which
  // is exactly what `folder` renders as. The renderer dispatches on
  // `"tabGroup"` alone.
  selectAppNodeVariant: createPropSelector(appNodeVariant, "variant", "folder"),
  selectFlowStepVariant: createPropSelector(
    flowStepVariant,
    "variant",
    "aiQuery"
  ),
  selectHidden,
  selectLabel,
  selectMissingRequired,
  selectValidationError
};

// src/orgicore/selectors/appstate/generated/propsSelectors.generated.ts
var selectName2 = createPropSelector(optionalString, "name");
var selectLabel2 = createPropSelector(optionalString, "label");
var selectIsRepeating = createPropSelector(optionalBoolean, "isRepeating");
var selectIsOwnScope = createPropSelector(optionalBoolean, "isOwnScope");
var selectIsRecord = createPropSelector(optionalBoolean, "isRecord");
var selectRecordKey = createPropSelector(optionalString, "recordKey");
var selectRecordValue = createPropSelector(optionalString, "recordValue");
var selectCompute2 = createPropSelector(t22.unknown, "compute");
var selectIsDerived = createPropSelector(optionalBoolean, "isDerived");
var selectIsTransient = createPropSelector(optionalBoolean, "isTransient");
var selectMaxLength2 = createPropSelector(optionalNumber, "maxLength");
var selectPageSize2 = createPropSelector(optionalNumber, "pageSize");
var selectPlace = createPropSelector(optional(place), "place");
var selectLayout2 = createPropSelector(optional(layout), "layout");
var selectContainerStyle2 = createPropSelector(optional(containerStyle), "containerStyle");
var selectSubmitObjectType = createPropSelector(optionalString, "submitObjectType");
var selectValidation2 = createPropSelector(optional(many(validationRule)), "validation");
var selectRequired2 = createPropSelector(optionalBoolean, "required");
var selectHelp2 = createPropSelector(optionalString, "help");
var selectPlaceholder2 = createPropSelector(optionalString, "placeholder");
var selectDeepHelp2 = createPropSelector(optionalString, "deepHelp");
var selectExamples2 = createPropSelector(optional(t22.array(t22.string)), "examples");
var selectSize2 = createPropSelector(optional(inputSize), "size");
var selectAiEnabled2 = createPropSelector(optionalBoolean, "aiEnabled");
var selectIterationLabel2 = createPropSelector(optionalString, "iterationLabel");
var selectNounLabel = createPropSelector(optionalString, "nounLabel");
var selectIcon = createPropSelector(optionalString, "icon");
var selectColor = createPropSelector(optionalString, "color");
var selectAuthMode = createPropSelector(optional(authMode), "authMode");
var selectOrgLogin = createPropSelector(optionalBoolean, "orgLogin");
var selectAppLogo = createPropSelector(optionalString, "appLogo");
var selectNotifications = createPropSelector(optional(many(pushRule)), "notifications");
var selectTransitions = createPropSelector(optional(many(transitionRule)), "transitions");
var selectBotActors = createPropSelector(optional(many(botActor)), "botActors");
var selectTheme = createPropSelector(optional(themePreset), "theme");
var selectThemeColors = createPropSelector(optional(themeColors), "themeColors");
var selectCustomCss = createPropSelector(optionalString, "customCss");
var selectContentMaxWidth = createPropSelector(optionalNumber, "contentMaxWidth");
var selectHeaderLogo = createPropSelector(optionalString, "headerLogo");
var selectNavigation = createPropSelector(optional(navigationConfig), "navigation");
var selectLibrary = createPropSelector(optional(libraryRef), "library");
var selectFeature = createPropSelector(optional(featureRef), "feature");
var selectHideHeader = createPropSelector(optionalBoolean, "hideHeader");
var selectVariant = createPropSelector(optional(subsectionVariant), "variant");
var selectShowHeader = createPropSelector(optionalBoolean, "showHeader");
var selectCollapsible = createPropSelector(optionalBoolean, "collapsible");
var selectCollapsedByDefault = createPropSelector(optionalBoolean, "collapsedByDefault");
var selectActionMenu = createPropSelector(optionalBoolean, "actionMenu");
var selectReorderable = createPropSelector(optionalBoolean, "reorderable");
var selectOrderField = createPropSelector(optionalString, "orderField");
var selectHideTopBar = createPropSelector(optionalBoolean, "hideTopBar");
var selectHideNav = createPropSelector(optionalBoolean, "hideNav");
var selectItemLabel = createPropSelector(optionalString, "itemLabel");
var selectSummary = createPropSelector(optionalString, "summary");
var selectMin = createPropSelector(optionalNumber, "min");
var selectMax = createPropSelector(optionalNumber, "max");
var selectAutoAdvance = createPropSelector(optionalBoolean, "autoAdvance");
var selectActionLabel = createPropSelector(optionalString, "actionLabel");
var selectIdleTitle = createPropSelector(optionalString, "idleTitle");
var selectIdleDescription = createPropSelector(optionalString, "idleDescription");
var selectRunningTitle = createPropSelector(optionalString, "runningTitle");
var selectRunningDescription = createPropSelector(optionalString, "runningDescription");
var selectAuthTitle = createPropSelector(optionalString, "authTitle");
var selectAuthDescription = createPropSelector(optionalString, "authDescription");
var selectNoCreditsTitle = createPropSelector(optionalString, "noCreditsTitle");
var selectNoCreditsDescription = createPropSelector(optionalString, "noCreditsDescription");
var selectPromptTemplate = createPropSelector(optionalString, "promptTemplate");
var selectImageSource = createPropSelector(optionalString, "imageSource");
var selectResultObjectType = createPropSelector(optionalString, "resultObjectType");
var selectTypeDescription = createPropSelector(optionalString, "typeDescription");
var selectModel = createPropSelector(optional(t22.union([textGenerationModel, t22.string])), "model");
var selectMockEnabled = createPropSelector(optionalBoolean, "mockEnabled");
var selectMockResultCount = createPropSelector(optionalNumber, "mockResultCount");
var selectMockResults = createPropSelector(optionalString, "mockResults");
var selectMaxUserTurns = createPropSelector(optionalNumber, "maxUserTurns");
var selectSteeringChips = createPropSelector(optionalString, "steeringChips");
var selectVoiceInput = createPropSelector(optionalBoolean, "voiceInput");
var selectSpeakReplies = createPropSelector(optionalBoolean, "speakReplies");
var selectSpeechLang = createPropSelector(optionalString, "speechLang");
var selectInputPlaceholder = createPropSelector(optionalString, "inputPlaceholder");
var selectMockReplies = createPropSelector(optionalString, "mockReplies");
var selectFetchUrl = createPropSelector(optionalString, "fetchUrl");
var selectPrompt = createPropSelector(optionalString, "prompt");
var selectMockImageUrl = createPropSelector(optionalString, "mockImageUrl");
var selectLyrics = createPropSelector(optionalString, "lyrics");
var selectDurationSec = createPropSelector(optionalNumber, "durationSec");
var selectMockAudioUrl = createPropSelector(optionalString, "mockAudioUrl");
var selectAudioSourceUrl = createPropSelector(optionalString, "audioSourceUrl");
var selectLanguage = createPropSelector(optionalString, "language");
var selectMockTranscript = createPropSelector(optionalString, "mockTranscript");
var selectSourceExpression = createPropSelector(optionalString, "sourceExpression");
var selectPublishDataType = createPropSelector(optionalString, "publishDataType");
var selectPublishScope = createPropSelector(optionalString, "publishScope");
var selectPublishVariant = createPropSelector(optionalString, "publishVariant");
var selectScript = createPropSelector(optionalString, "script");
var selectFunctionName = createPropSelector(optional(callableButtonFunction), "functionName");
var selectArgs = createPropSelector(optionalString, "args");
var selectRefreshCallables = createPropSelector(optional(t22.array(t22.string)), "refreshCallables");
var selectOnSuccessNavigate = createPropSelector(optionalString, "onSuccessNavigate");
var selectSuccessMessage = createPropSelector(optionalString, "successMessage");
var selectNodeTypes = createPropSelector(optional(t22.record(t22.string, treeNodeOptions)), "nodeTypes");
var selectRootType = createPropSelector(optionalString, "rootType");
var selectCanAdd = createPropSelector(optionalBoolean, "canAdd");
var selectCanEdit = createPropSelector(optionalBoolean, "canEdit");
var selectTextTransform = createPropSelector(optional(textTransform), "textTransform");
var selectStep = createPropSelector(optionalNumber, "step");
var selectFormat = createPropSelector(optional(t22.union([temporalVariant, t22.string])), "format");
var selectIdiom = createPropSelector(optional(t22.union([pickerIdiom, t22.string])), "idiom");
var selectPlaceholderIcon = createPropSelector(optionalString, "placeholderIcon");
var selectImageOutputOptions2 = createPropSelector(optional(imageOptions), "imageOutputOptions");
var selectAiSystemPromptFormula = createPropSelector(optionalString, "aiSystemPromptFormula");
var selectUserAvatarUid = createPropSelector(optionalString, "userAvatarUid");
var selectShowCamera = createPropSelector(optionalBoolean, "showCamera");
var selectShowFilePicker = createPropSelector(optionalBoolean, "showFilePicker");
var selectShowSearch = createPropSelector(optionalBoolean, "showSearch");
var selectShowEdit = createPropSelector(optionalBoolean, "showEdit");
var selectShowCommit = createPropSelector(optionalBoolean, "showCommit");
var selectMultiple = createPropSelector(optionalBoolean, "multiple");
var selectMaxSizeMb = createPropSelector(optionalNumber, "maxSizeMb");
var selectHtmlTemplate = createPropSelector(optionalString, "htmlTemplate");
var selectPurpose = createPropSelector(optionalString, "purpose");
var selectActions = createPropSelector(optional(t22.record(t22.string, contentAction)), "actions");
var selectParameters = createPropSelector(optionalString, "parameters");
var selectReturns = createPropSelector(optionalString, "returns");
var selectReturnTypeHint = createPropSelector(optionalString, "returnTypeHint");
var selectPluginName2 = createPropSelector(optionalString, "pluginName");
var selectPluginParams2 = createPropSelector(optional(t22.array(pluginParam)), "pluginParams");
var selectRoomKey = createPropSelector(optionalString, "roomKey");
var selectDisplayName = createPropSelector(optionalString, "displayName");
var selectIdentity = createPropSelector(optionalString, "identity");
var selectMediaMode = createPropSelector(optional(mediaMode), "mediaMode");
var selectDisplayMode = createPropSelector(optional(videoDisplayMode), "displayMode");
var selectMaxTiles = createPropSelector(optionalNumber, "maxTiles");
var selectFocusIdentity = createPropSelector(optionalString, "focusIdentity");
var selectSubsectionVariant = createPropSelector(subsectionVariant, "variant", "inline");
var selectAiQueryStepImageSource = createPropSelector(t22.unknown, "imageSource");
var selectAiQueryStepModel = createPropSelector(optional(t22.union([textGenerationModel, t22.string])), "model");
var selectAiChatStepModel = createPropSelector(optional(t22.union([textGenerationModel, t22.string])), "model");
var selectImageGenerationStepModel = createPropSelector(optional(t22.union([imageGenerationModel, t22.string])), "model");
var selectAudioGenerationStepModel = createPropSelector(optional(t22.union([audioGenerationModel, t22.string])), "model");
var selectTranscribeStepModel = createPropSelector(optional(t22.union([transcribeModel, t22.string])), "model");
var selectPublishDataStepSourceExpression = createPropSelector(t22.unknown, "sourceExpression");
var selectCallableButtonVariant = createPropSelector(callableButtonVariant, "variant", "contained");
var selectCallableButtonFunctionName = createPropSelector(callableButtonFunction, "functionName");
var selectCallableButtonArgs = createPropSelector(t22.unknown, "args");
var selectCallableButtonSize = createPropSelector(inputSize, "size", "medium");
var selectTextInputVariant = createPropSelector(textVariant, "variant", "basic");
var selectTextInputTextTransform = createPropSelector(textTransform, "textTransform", "none");
var selectNumberInputVariant = createPropSelector(optional(t22.union([numberVariant, t22.string])), "variant");
var selectBooleanInputVariant = createPropSelector(optional(t22.union([t22.literal("checkbox"), t22.literal("toggle")])), "variant");
var selectTemporalInputFormat = createPropSelector(temporalVariant, "format", "date");
var selectFileInputVariant = createPropSelector(optional(t22.union([fileVariant, t22.string])), "variant");
var selectFileInputPlaceholderIcon = createPropSelector(t22.string, "placeholderIcon", "person");
var selectMediaRoomRoomKey = createPropSelector(t22.string, "roomKey");
var selectMediaRoomMediaMode = createPropSelector(mediaMode, "mediaMode", "video");
var selectCommonNode2 = createSelector16(
  selectorOverrides.selectLabel,
  selectorOverrides.selectHidden,
  selectPlace,
  (label2, hidden, place2) => ({
    label: label2,
    hidden,
    place: place2
  })
);
var selectContainerNode = createSelector16(
  selectLayout2,
  selectContainerStyle2,
  selectSubmitObjectType,
  (layout2, containerStyle2, submitObjectType) => ({
    layout: layout2,
    containerStyle: containerStyle2,
    submitObjectType
  })
);
var selectInputNode = createSelector16(
  selectCommonNode2,
  selectValidation2,
  selectRequired2,
  selectHelp2,
  selectPlaceholder2,
  selectSize2,
  selectAiEnabled2,
  selectorOverrides.selectMissingRequired,
  selectorOverrides.selectValidationError,
  (base0, validation, required, help, placeholder2, size, aiEnabled, missingRequired, errorMessage2) => ({
    ...base0,
    validation,
    required,
    help,
    placeholder: placeholder2,
    size,
    aiEnabled,
    missingRequired,
    errorMessage: errorMessage2
  })
);
var selectIterationNode = createSelector16(
  selectIterationLabel2,
  selectNounLabel,
  selectIcon,
  selectColor,
  (iterationLabel, nounLabel, icon, color) => ({
    iterationLabel,
    nounLabel,
    icon,
    color
  })
);
var selectAppNodeNode = createSelector16(
  selectCommonNode2,
  selectContainerNode,
  selectorOverrides.selectAppNodeVariant,
  (base0, base1, variant) => ({
    ...base0,
    ...base1,
    variant
  })
);
var selectTabGroupNode = createSelector16(
  selectAppNodeNode,
  (base0) => ({
    ...base0,
    variant: right("tabGroup")
  })
);
var selectTabNode = createSelector16(
  selectAppNodeNode,
  (base0) => ({
    ...base0,
    variant: right("tab")
  })
);
var selectFolderNode = createSelector16(
  selectAppNodeNode,
  (base0) => ({
    ...base0,
    variant: right("folder")
  })
);
var selectScreenNode = createSelector16(
  selectCommonNode2,
  selectContainerNode,
  selectIcon,
  selectHideHeader,
  (base0, base1, icon, hideHeader) => ({
    ...base0,
    ...base1,
    icon,
    hideHeader
  })
);
var selectSubsectionNode = createSelector16(
  selectCommonNode2,
  selectContainerNode,
  selectIterationNode,
  selectSubsectionVariant,
  selectShowHeader,
  selectCollapsible,
  selectCollapsedByDefault,
  selectActionMenu,
  selectReorderable,
  selectOrderField,
  (base0, base1, base2, variant, showHeader, collapsible, collapsedByDefault, actionMenu, reorderable, orderField) => ({
    ...base0,
    ...base1,
    ...base2,
    variant,
    showHeader,
    collapsible,
    collapsedByDefault,
    actionMenu,
    reorderable,
    orderField
  })
);
var selectFlowNode = createSelector16(
  selectCommonNode2,
  selectHideTopBar,
  selectHideNav,
  (base0, hideTopBar, hideNav) => ({
    ...base0,
    hideTopBar,
    hideNav
  })
);
var selectRepeatNode = createSelector16(
  selectCommonNode2,
  selectItemLabel,
  selectSummary,
  selectMin,
  selectMax,
  (base0, itemLabel, summary, min, max) => ({
    ...base0,
    itemLabel,
    summary,
    min,
    max
  })
);
var selectFlowStepNode = createSelector16(
  selectCommonNode2,
  selectorOverrides.selectFlowStepVariant,
  selectAutoAdvance,
  selectActionLabel,
  selectIdleTitle,
  selectIdleDescription,
  selectRunningTitle,
  selectRunningDescription,
  selectAuthTitle,
  selectAuthDescription,
  selectNoCreditsTitle,
  selectNoCreditsDescription,
  (base0, variant, autoAdvance, actionLabel, idleTitle, idleDescription, runningTitle, runningDescription, authTitle, authDescription, noCreditsTitle, noCreditsDescription) => ({
    ...base0,
    variant,
    autoAdvance,
    actionLabel,
    idleTitle,
    idleDescription,
    runningTitle,
    runningDescription,
    authTitle,
    authDescription,
    noCreditsTitle,
    noCreditsDescription
  })
);
var selectAiQueryStepNode = createSelector16(
  selectFlowStepNode,
  selectPromptTemplate,
  selectAiQueryStepImageSource,
  selectResultObjectType,
  selectTypeDescription,
  selectAiQueryStepModel,
  selectMockEnabled,
  selectMockResultCount,
  selectMockResults,
  (base0, promptTemplate, imageSource, resultObjectType, typeDescription, model, mockEnabled, mockResultCount, mockResults) => ({
    ...base0,
    variant: right("aiQuery"),
    promptTemplate,
    imageSource,
    resultObjectType,
    typeDescription,
    model,
    mockEnabled,
    mockResultCount,
    mockResults
  })
);
var selectAiChatStepNode = createSelector16(
  selectFlowStepNode,
  selectPromptTemplate,
  selectResultObjectType,
  selectTypeDescription,
  selectAiChatStepModel,
  selectMaxUserTurns,
  selectSteeringChips,
  selectVoiceInput,
  selectSpeakReplies,
  selectSpeechLang,
  selectInputPlaceholder,
  selectMockEnabled,
  selectMockReplies,
  (base0, promptTemplate, resultObjectType, typeDescription, model, maxUserTurns, steeringChips, voiceInput, speakReplies, speechLang, inputPlaceholder, mockEnabled, mockReplies) => ({
    ...base0,
    variant: right("aiChat"),
    promptTemplate,
    resultObjectType,
    typeDescription,
    model,
    maxUserTurns,
    steeringChips,
    voiceInput,
    speakReplies,
    speechLang,
    inputPlaceholder,
    mockEnabled,
    mockReplies
  })
);
var selectFetchDataStepNode = createSelector16(
  selectFlowStepNode,
  selectFetchUrl,
  (base0, fetchUrl) => ({
    ...base0,
    variant: right("fetchData"),
    fetchUrl
  })
);
var selectImageGenerationStepNode = createSelector16(
  selectFlowStepNode,
  selectPrompt,
  selectImageGenerationStepModel,
  selectMockEnabled,
  selectMockImageUrl,
  (base0, prompt, model, mockEnabled, mockImageUrl) => ({
    ...base0,
    variant: right("imageGeneration"),
    prompt,
    model,
    mockEnabled,
    mockImageUrl
  })
);
var selectAudioGenerationStepNode = createSelector16(
  selectFlowStepNode,
  selectPrompt,
  selectLyrics,
  selectAudioGenerationStepModel,
  selectDurationSec,
  selectMockEnabled,
  selectMockAudioUrl,
  (base0, prompt, lyrics, model, durationSec, mockEnabled, mockAudioUrl) => ({
    ...base0,
    variant: right("audioGeneration"),
    prompt,
    lyrics,
    model,
    durationSec,
    mockEnabled,
    mockAudioUrl
  })
);
var selectTranscribeStepNode = createSelector16(
  selectFlowStepNode,
  selectAudioSourceUrl,
  selectTranscribeStepModel,
  selectLanguage,
  selectMockEnabled,
  selectMockTranscript,
  (base0, audioSourceUrl, model, language, mockEnabled, mockTranscript) => ({
    ...base0,
    variant: right("transcribe"),
    audioSourceUrl,
    model,
    language,
    mockEnabled,
    mockTranscript
  })
);
var selectPublishDataStepNode = createSelector16(
  selectFlowStepNode,
  selectPublishDataStepSourceExpression,
  selectPublishDataType,
  selectPublishScope,
  selectPublishVariant,
  (base0, sourceExpression, publishDataType, publishScope, publishVariant) => ({
    ...base0,
    variant: right("publishData"),
    sourceExpression,
    publishDataType,
    publishScope,
    publishVariant
  })
);
var selectScriptStepNode = createSelector16(
  selectFlowStepNode,
  selectScript,
  (base0, script) => ({
    ...base0,
    variant: right("script"),
    script
  })
);
var selectCallableButtonNode = createSelector16(
  selectCommonNode2,
  selectCallableButtonVariant,
  selectCallableButtonFunctionName,
  selectCallableButtonArgs,
  selectActionLabel,
  selectRefreshCallables,
  selectOnSuccessNavigate,
  selectSuccessMessage,
  selectCallableButtonSize,
  selectIcon,
  (base0, variant, functionName, args, actionLabel, refreshCallables, onSuccessNavigate, successMessage, size, icon) => ({
    ...base0,
    variant,
    functionName,
    args,
    actionLabel,
    refreshCallables,
    onSuccessNavigate,
    successMessage,
    size,
    icon
  })
);
var selectTextInputNode = createSelector16(
  selectInputNode,
  selectTextInputVariant,
  selectTextInputTextTransform,
  selectOptionalStringValue,
  (base0, variant, textTransform2, value) => ({
    ...base0,
    variant,
    textTransform: textTransform2,
    value
  })
);
var selectNumberInputNode = createSelector16(
  selectInputNode,
  selectNumberInputVariant,
  selectMin,
  selectMax,
  selectStep,
  selectOptionalNumberValue,
  (base0, variant, min, max, step, value) => ({
    ...base0,
    variant,
    min,
    max,
    step,
    value
  })
);
var selectBooleanInputNode = createSelector16(
  selectInputNode,
  selectBooleanInputVariant,
  selectOptionalBooleanValue,
  (base0, variant, value) => ({
    ...base0,
    variant,
    value
  })
);
var selectTemporalInputNode = createSelector16(
  selectInputNode,
  selectTemporalInputFormat,
  selectOptionalStringValue,
  (base0, format, value) => ({
    ...base0,
    format,
    value
  })
);
var selectFileInputNode = createSelector16(
  selectInputNode,
  selectFileInputVariant,
  selectFileInputPlaceholderIcon,
  selectImageOutputOptions2,
  selectAiSystemPromptFormula,
  selectUserAvatarUid,
  selectShowCamera,
  selectShowFilePicker,
  selectShowSearch,
  selectShowEdit,
  selectShowCommit,
  selectMultiple,
  selectMaxSizeMb,
  selectFileUploadValue,
  (base0, variant, placeholderIcon, imageOutputOptions, aiSystemPromptFormula, userAvatarUid, showCamera, showFilePicker, showSearch, showEdit, showCommit, multiple, maxSizeMb, value) => ({
    ...base0,
    variant,
    placeholderIcon,
    imageOutputOptions,
    aiSystemPromptFormula,
    userAvatarUid,
    showCamera,
    showFilePicker,
    showSearch,
    showEdit,
    showCommit,
    multiple,
    maxSizeMb,
    value
  })
);
var selectContentNode = createSelector16(
  selectCommonNode2,
  selectHtmlTemplate,
  selectPurpose,
  selectActions,
  selectValue,
  (base0, htmlTemplate, purpose, actions, value) => ({
    ...base0,
    htmlTemplate,
    purpose,
    actions,
    value
  })
);
var selectVariableNode = createSelector16(
  selectCommonNode2,
  selectValue,
  (base0, value) => ({
    ...base0,
    value
  })
);
var selectFunctionNode = createSelector16(
  selectCommonNode2,
  selectParameters,
  selectReturns,
  selectReturnTypeHint,
  selectValue,
  (base0, parameters, returns, returnTypeHint, value) => ({
    ...base0,
    parameters,
    returns,
    returnTypeHint,
    value
  })
);
var selectConstNode = createSelector16(
  selectCommonNode2,
  selectValue,
  (base0, value) => ({
    ...base0,
    value
  })
);
var selectMediaRoomNode = createSelector16(
  selectCommonNode2,
  selectMediaRoomRoomKey,
  selectDisplayName,
  selectIdentity,
  selectMediaRoomMediaMode,
  selectDisplayMode,
  selectMaxTiles,
  selectFocusIdentity,
  (base0, roomKey, displayName4, identity4, mediaMode2, displayMode, maxTiles, focusIdentity) => ({
    ...base0,
    roomKey,
    displayName: displayName4,
    identity: identity4,
    mediaMode: mediaMode2,
    displayMode,
    maxTiles,
    focusIdentity
  })
);

// src/orgicore/selectors/appstate/treeSelectors.ts
var resolveTypeKey = (value, nodeTypes) => {
  if (value.type.includes(":")) return value.type;
  if (value.type in nodeTypes) return value.type;
  const authored = toRuntimeType(value.type);
  if (authored.variant !== void 0)
    return `${authored.type}:${authored.variant}`;
  if (value.variant) {
    const variant = value.variant.includes(":") ? value.variant.split(":")[0] : value.variant;
    return `${value.type}:${variant}`;
  }
  return value.type;
};
var parseTypeKey = (key) => {
  const colonIdx = key.indexOf(":");
  if (colonIdx === -1) return { type: key };
  return { type: key.slice(0, colonIdx), variant: key.slice(colonIdx + 1) };
};
var typeKeyToAuthored = (key) => {
  const parsed = parseTypeKey(key);
  const authored = authoredTypeOf(parsed.type, parsed.variant);
  return authored === void 0 ? parsed : { type: authored };
};
var initialisedNodeValue = (value, typeKey, newId) => {
  const { type: type14, variant } = typeKeyToAuthored(typeKey);
  const typeMatches = (value == null ? void 0 : value.type) === type14 && (variant === void 0 || value.variant === variant);
  if ((value == null ? void 0 : value.id) && typeMatches) return void 0;
  const isRawSpelling = variant === void 0 && typeof (value == null ? void 0 : value.type) === "string" && authoredTypeOf(value.type, value.variant) === type14;
  const next = {
    ...value,
    type: type14,
    id: (value == null ? void 0 : value.id) || newId(type14)
  };
  if (variant !== void 0) next.variant = variant;
  else if (isRawSpelling) delete next.variant;
  return next;
};
var selectTreeNodeTypeName = createSelector17(selectAppState, selectPointer, (s, p) => {
  const { rootType, nodeTypes } = getTreeOptions(p);
  if (isRootTreeNode(p)) {
    return rootType;
  }
  const r = selectTreeNodeValue(s, p.key);
  if (isRight(r) && r.right) {
    return resolveTypeKey(r.right, nodeTypes);
  }
  return void 0;
});
var treeTypeState = (t30) => t30;
var selectTreeType = createSelector17(treeTypeState, stringProp1, (s, name) => {
  const { nodeTypes } = s;
  const type14 = nodeTypes[name];
  if (type14) {
    const { allowedChildTypes, category = "", icon, color, label: label2 } = type14;
    return { allowedChildTypes, name, category, icon, color, label: label2 };
  }
  return { allowedChildTypes: [], name, category: "" };
});
var selectTreeNodeType = createSelector17(selectPointer, selectTreeNodeTypeName, (p, typeName = "") => {
  return selectTreeType(getTreeOptions(p), typeName);
});
var selectAllowedChildTypeNames = createSelector17(selectTreeNodeType, (type14) => {
  return type14.allowedChildTypes;
});
var selectAllowedChildTypes = createSelector17(selectPointer, selectAllowedChildTypeNames, (p, typeNames2) => {
  return typeNames2.map(
    (typeName) => selectTreeType(getTreeOptions(p), typeName)
  );
});
var selectTreeNodeIcon = createSelector17(
  selectTreeNodeTypeName,
  selectTreeNodeType,
  selectIcon,
  (typeName, type14, icon) => {
    if (isRight(icon) && icon.right) {
      return icon.right;
    }
    return type14.icon || typeName || "objects";
  }
);
var selectTreeNodeIconColor = createSelector17(
  selectTreeNodeType,
  selectColor,
  (type14, color) => {
    if (isRight(color) && color.right) {
      return color.right;
    }
    return type14.color || "#616161";
  }
);
var selectChildTreeNodes = createSelector17(
  selectAppState,
  selectPointer,
  (s, p) => {
    if (isRootTreeNode(p)) {
      return selectChildPointers(s, p.key);
    } else if (isNonRootTreeNode(p)) {
      return selectChildPointers(s, `${p.key}.${p.node.name}`);
    }
    return [];
  }
);
var selectHasChildTreeNodes = createSelector17(
  selectChildTreeNodes,
  (childTreeNodes) => {
    return childTreeNodes.length > 0;
  }
);
var selectIsDeleteAllowed = createSelector17(
  selectPointer,
  selectHasChildTreeNodes,
  (p, hasChildTreeNodes) => {
    return !hasChildTreeNodes && !isRootTreeNode(p);
  }
);
var selectFirstTreeNode = createSelector17(selectChildPointers, (childPointers) => {
  return childPointers.find((c) => c.node.type === TREE);
});
var selectTreeDepth = createSelector17(
  selectAppState,
  selectPointer,
  (s, p) => {
    const { scope } = p;
    if (scope.node.type !== TREE) {
      return 0;
    }
    return 1 + selectTreeDepth(s, getPointerKeyOfScope(scope));
  }
);
var selectParentTreeNode = createSelector17(selectAppState, stringProp1, (s, p) => {
  return selectClosestAncestor(s, p, isTreeNode);
});

// src/orgicore/selectors/appstate/treeUiSelectors.ts
import { createSelector as createSelector18 } from "reselect";
var selectDropOptions = createSelector18(selectAppState, selectPointer, selectPointer2, (s, p1, p2) => {
  const srcTypeName = selectTreeNodeTypeName(s, p1.key);
  if (!srcTypeName) {
    return [];
  }
  const destNodeType = selectTreeNodeType(s, p2.key);
  const result = [];
  if (destNodeType.allowedChildTypes.includes(srcTypeName)) {
    result.push("into");
  }
  const parentTreeNode = selectParentTreeNode(s, p2.key);
  if (parentTreeNode) {
    const parentNodeType = selectTreeNodeType(s, parentTreeNode.key);
    if (parentNodeType.allowedChildTypes.includes(srcTypeName)) {
      result.push("before");
      result.push("after");
    }
  }
  return result;
});
var selectDropZone = createSelector18(
  selectAppState,
  selectPointer,
  selectPointer2,
  (_s, _p1, _p2, mouseParams) => mouseParams,
  (s, _p1, _p2, mouseParams) => {
    const dropOptions = selectDropOptions(s, _p1.key, _p2.key);
    const { mouseY, elementTop, elementBottom } = mouseParams;
    const target = _p2.key;
    if (dropOptions.length === 0) return void 0;
    const elementHeight = elementBottom - elementTop;
    const isNearTop = mouseY - elementTop <= 10;
    const isNearBottom = elementBottom - mouseY <= 10;
    const isNearMiddle = !isNearTop && !isNearBottom;
    const isTopHalf = mouseY - elementTop <= elementHeight / 2;
    const isBottomHalf = elementBottom - mouseY <= elementHeight / 2;
    const hasBefore = dropOptions.includes("before");
    const hasAfter = dropOptions.includes("after");
    const hasInto = dropOptions.includes("into");
    if (hasInto && isNearMiddle) {
      return { target, action: "into", position: "center" };
    }
    if (isTopHalf && hasBefore) {
      return { target, action: "before", position: "top" };
    }
    if (isBottomHalf && hasAfter) {
      return { target, action: "after", position: "bottom" };
    }
    return void 0;
  }
);
var selectNext = createShiftSelector(
  createShiftOptions({ include: isTreeNode, direction: "forward" })
);
var selectPrevious = createShiftSelector(
  createShiftOptions({ include: isTreeNode, direction: "back" })
);
var selectTreeTraverseOptions = createSelector18(
  selectAppState,
  directionProp1,
  (s, direction) => {
    return createShiftOptions({
      include: isTreeNode,
      direction,
      traverseChildren: (p) => {
        if (!isTreeNode(p)) {
          return true;
        }
        const expanded = selectTreeNodeExpanded(s, p.key);
        return isRight(expanded) && !!expanded.right;
      }
    });
  }
);
var selectNextExpanded = createSelector18(selectAppState, stringProp1, (s, p) => {
  return shift(s, p, selectTreeTraverseOptions(s, "forward"));
});
var selectPreviousExpanded = createSelector18(selectAppState, stringProp1, (s, p) => {
  return shift(s, p, selectTreeTraverseOptions(s, "back"));
});

// src/orgicore/selectors/appstate/outputSelectors.ts
import { createSelector as createSelector19 } from "reselect";
var selectRootOutputObject = createSelector19(
  selectAppState,
  (s) => selectOutputObject(s, "")
);
var createTypedOutputSelector = (codec) => {
  return createSelector19(selectOutputObject, (output) => {
    return decodeUnknown(output, codec, "output");
  });
};
var selectClosestSubmitContext = createSelector19(selectAppState, selectPointer, (s, p) => {
  if (isSubmitContextPointer(p)) {
    return p;
  }
  return selectClosestAncestor(s, p.key, isSubmitContextPointer);
});
var selectSubmitContextObject = createSelector19(selectAppState, selectClosestSubmitContext, (s, p) => {
  if (!p || !p.node.submitObjectType) {
    return void 0;
  }
  const { scope, node: node2, key } = p;
  const obj = selectOutputObject(s, key);
  const uniqueNodeId = selectUniqueKey(s.def, node2);
  const createPointerKey2 = (suffix) => {
    return compactJoin([scope.key, `${uniqueNodeId}$${suffix}`]);
  };
  const idKey = createPointerKey2("id");
  const createdAtKey = createPointerKey2("createdAt");
  const createdByKey = createPointerKey2("createdBy");
  return {
    pointer: p,
    type: p.node.submitObjectType,
    idKey,
    createdAtKey,
    createdByKey,
    obj
  };
});

// src/orgicore/selectors/appstate/formSelectors.ts
import { createSelector as createSelector20 } from "reselect";
var selectInputInErrorShiftOptions = createSelector20(
  selectAppState,
  selectVisibleInputTraverseOptions,
  (s, options) => {
    return mergeShiftOptions(
      options,
      createShiftOptions({
        include: (p) => {
          const missingRequired = getRightOr(
            selectMissingRequired(s, p.key),
            false
          );
          const validationError = !!getRightOr(
            selectValidationError(s, p.key),
            void 0
          );
          return missingRequired || validationError;
        }
      })
    );
  }
);
var selectFirstError = createSelector20(
  selectAppState,
  stringProp1,
  selectInputInErrorShiftOptions,
  shift
);
var selectFirstErrorSafe = (s, key) => isLeft(safeSelectPointer(s, key)) ? void 0 : selectFirstError(s, key);
var selectAllErrors = createSelector20(
  selectAppState,
  stringProp1,
  selectInputInErrorShiftOptions,
  shiftCollect
);
var selectUncommittedFileUploadPointers = createSelector20(selectAppState, stringProp1, (s, p) => {
  return shiftCollect(
    s,
    p,
    createShiftOptions({
      traverseParent: isTrue,
      include: (p2) => {
        if (p2.node.type !== FILE_INPUT) {
          return false;
        }
        const r = selectFileUploadValue(s, p2.key);
        if (isLeft(r)) {
          return false;
        }
        if (Array.isArray(r.right)) {
          return false;
        }
        const { url, previewUrl } = r.right;
        return !url && !!previewUrl;
      }
    })
  );
});
var selectUncommittedFileUploads = createSelector20(
  selectAppState,
  selectUncommittedFileUploadPointers,
  (s, pointers) => {
    return pointers.flatMap((p) => {
      const value = getRight(selectFileUploadValue(s, p.key));
      if (Array.isArray(value)) {
        return [];
      }
      return [
        {
          pointerKey: p.key,
          value,
          imageOutputOptions: getRightOr(
            selectImageOutputOptions(s, p.key),
            void 0
          )
        }
      ];
    });
  }
);

// src/orgicore/selectors/appstate/inputCompositeSelectors.ts
import { createSelector as createSelector22 } from "reselect";

// src/orgicore/selectors/appstate/generated/propsWrappers.generated.ts
import { createSelector as createSelector21 } from "reselect";
var selectAppProps = createSelector21(selectAppNodeNode, getUnwrappedProps);
var selectTabGroupProps = createSelector21(selectTabGroupNode, getUnwrappedProps);
var selectTabProps = createSelector21(selectTabNode, getUnwrappedProps);
var selectFolderProps = createSelector21(selectFolderNode, getUnwrappedProps);
var selectSubsectionProps = createSelector21(selectSubsectionNode, getUnwrappedProps);
var selectFlowProps = createSelector21(selectFlowNode, getUnwrappedProps);
var selectRepeatProps = createSelector21(selectRepeatNode, getUnwrappedProps);
var selectFlowStepProps = createSelector21(selectFlowStepNode, getUnwrappedProps);
var selectAiQueryStepProps = createSelector21(selectAiQueryStepNode, getUnwrappedProps);
var selectAiChatStepProps = createSelector21(selectAiChatStepNode, getUnwrappedProps);
var selectFetchDataStepProps = createSelector21(selectFetchDataStepNode, getUnwrappedProps);
var selectImageGenerationStepProps = createSelector21(selectImageGenerationStepNode, getUnwrappedProps);
var selectAudioGenerationStepProps = createSelector21(selectAudioGenerationStepNode, getUnwrappedProps);
var selectTranscribeStepProps = createSelector21(selectTranscribeStepNode, getUnwrappedProps);
var selectPublishDataStepProps = createSelector21(selectPublishDataStepNode, getUnwrappedProps);
var selectScriptStepProps = createSelector21(selectScriptStepNode, getUnwrappedProps);
var selectCallableButtonProps = createSelector21(selectCallableButtonNode, getUnwrappedProps);
var selectTextInputProps = createSelector21(selectTextInputNode, getUnwrappedProps);
var selectNumberInputProps = createSelector21(selectNumberInputNode, getUnwrappedProps);
var selectBooleanInputProps = createSelector21(selectBooleanInputNode, getUnwrappedProps);
var selectTemporalInputProps = createSelector21(selectTemporalInputNode, getUnwrappedProps);
var selectFileInputProps = createSelector21(selectFileInputNode, getUnwrappedProps);
var selectContentProps = createSelector21(selectContentNode, getUnwrappedProps);
var selectVariableProps = createSelector21(selectVariableNode, getUnwrappedProps);
var selectFunctionProps = createSelector21(selectFunctionNode, getUnwrappedProps);
var selectConstProps = createSelector21(selectConstNode, getUnwrappedProps);
var selectMediaRoomProps = createSelector21(selectMediaRoomNode, getUnwrappedProps);

// src/orgicore/selectors/appstate/inputCompositeSelectors.ts
var selectRecordTreeNode = createSelector22(
  selectCommonNode,
  selectNodeTypes,
  selectRootType,
  selectCanAdd,
  selectCanEdit,
  (common, nodeTypes, rootType, canAdd, canEdit) => ({
    ...common,
    nodeTypes,
    rootType,
    canAdd,
    canEdit
  })
);
var selectRecordTreeProps = createSelector22(selectRecordTreeNode, getUnwrappedProps);
var selectNode = createSelector22(
  selectAppState,
  selectPointer,
  (s, p) => {
    const { node: node2, key } = p;
    switch (resolveNodeType(node2.type)) {
      case TEXT_INPUT:
        const textVariant2 = selectTextVariant(s, key);
        if (isRight(textVariant2) && textVariant2.right === "stringArray") {
          return selectStringArrayInputNode(s, key);
        }
        return selectTextInputNode(s, key);
      case BOOLEAN_INPUT:
        return selectBooleanInputNode(s, key);
      case NUMBER_INPUT:
        const variant = selectNumberVariant(s, key);
        if (isRight(variant)) {
          if (variant.right === "intArray") {
            return selectNumberArrayInputNode(s, key);
          }
        }
        return selectNumberInputNode(s, key);
      case TEMPORAL_INPUT:
        return selectTemporalInputNode(s, key);
      case FILE_INPUT:
        return selectFileInputNode(s, key);
      case SELECT_INPUT:
        return selectCommonPicker(s, key);
      case TREE:
        return node2.isRepeating ? selectTreeNode(s, key) : selectRecordTreeNode(s, key);
      case CONTENT:
        return selectContentNode(s, key);
      case FORM:
        return selectFormNode(s, key);
      case VARIABLE:
        return selectVariableNode(s, key);
      case FUNCTION:
        return selectFunctionNode(s, key);
      case CONST:
        return selectConstNode(s, key);
      case FLOW_STEP:
        return selectFlowStepNode(s, key);
      case CALLABLE_BUTTON:
        return selectCallableButtonNode(s, key);
      case MEDIA_ROOM:
        return selectMediaRoomNode(s, key);
      default:
        return selectCommonNode(s, key);
    }
  }
);

// src/orgicore/selectors/appstate/visibilitySelectors.ts
import { createSelector as createSelector23 } from "reselect";
var selectCurrentScreenHidesHeader = createSelector23(
  selectAppState,
  selectCurrentNavRoute,
  (s, screen2) => {
    const result = selectHideHeader(s, screen2.key);
    return isRight(result) && result.right === true;
  }
);
var selectVisibleChildPointersToRender = createSelector23(
  selectAppState,
  selectChildPointersToRender,
  (s, childPointers) => {
    return childPointers.filter((childPointer) => {
      const hiddenResult = selectHidden(s, childPointer.key);
      return !(isRight(hiddenResult) && hiddenResult.right);
    });
  }
);

// src/orgicore/selectors/appstate/spatialSelectors.ts
import { createSelector as createSelector24 } from "reselect";
var AUTO_PLACE = { kind: "auto" };
var SPATIAL_DEFAULT_MIN_CELL_WIDTH = 40;
var isSpatialLayout = (layout2) => (layout2 == null ? void 0 : layout2.mode) === "spatial";
var evaluateSpatialMember = (s, pointerKey, raw) => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : void 0;
  if (typeof raw !== "string") return void 0;
  if (!raw.startsWith("@")) {
    if (raw.trim() === "") return void 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : void 0;
  }
  const result = selectEvaluation(s, tryStripFormulaPrefix(raw), pointerKey);
  if (!isRight(result)) return void 0;
  const v = result.right;
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
};
var selectSpatialGrid = createSelector24(
  selectAppState,
  selectPointer,
  selectLayout,
  (s, p, layoutResult) => {
    if (!isRight(layoutResult)) return void 0;
    const layout2 = layoutResult.right;
    if (!isSpatialLayout(layout2)) return void 0;
    const colsRaw = evaluateSpatialMember(s, p.key, layout2.cols);
    if (colsRaw === void 0) return void 0;
    const cols = Math.floor(colsRaw);
    if (cols < 1) return void 0;
    const rowsRaw = evaluateSpatialMember(s, p.key, layout2.rows);
    const rows = rowsRaw !== void 0 && Math.floor(rowsRaw) >= 1 ? Math.floor(rowsRaw) : void 0;
    const rowHeight = typeof layout2.rowHeight === "number" && Number.isFinite(layout2.rowHeight) && layout2.rowHeight > 0 ? layout2.rowHeight : void 0;
    const minColumnWidth = typeof layout2.minColumnWidth === "number" && layout2.minColumnWidth > 0 ? layout2.minColumnWidth : SPATIAL_DEFAULT_MIN_CELL_WIDTH;
    return { cols, rows, rowHeight, minColumnWidth };
  }
);
var asSpan = (n) => n === void 0 ? 1 : Math.max(1, Math.floor(n));
var clampPlace = (raw, grid) => {
  const x = Math.min(Math.max(0, Math.floor(raw.x)), grid.cols - 1);
  let y = Math.max(0, Math.floor(raw.y));
  let w = Math.min(asSpan(raw.w), grid.cols - x);
  let h = asSpan(raw.h);
  if (grid.rows !== void 0) {
    y = Math.min(y, grid.rows - 1);
    h = Math.min(h, grid.rows - y);
  }
  w = Math.max(1, w);
  h = Math.max(1, h);
  return { kind: "placed", x, y, w, h };
};
var selectEvaluatedPlace = createSelector24(
  selectAppState,
  selectPointer,
  (_s, _key, grid) => grid,
  (s, p, grid) => {
    const raw = p.node.place;
    const place2 = place.is(raw) ? raw : void 0;
    if (!place2) return AUTO_PLACE;
    const x = evaluateSpatialMember(s, p.key, place2.x);
    const y = evaluateSpatialMember(s, p.key, place2.y);
    if (x === void 0 || y === void 0) return AUTO_PLACE;
    const w = evaluateSpatialMember(s, p.key, place2.w);
    const h = evaluateSpatialMember(s, p.key, place2.h);
    return clampPlace({ x, y, w, h }, grid);
  }
);
var selectSpatialItemPointers = createSelector24(
  selectAppState,
  selectVisibleChildPointersToRender,
  (s, children2) => children2.flatMap((child) => {
    if (!isLoop(child)) return [child];
    if (isGridSpanNode(child)) return [];
    return selectChildPointers(s, child.key).filter((iteration) => {
      const hidden = selectHidden(s, iteration.key);
      return !(isRight(hidden) && hidden.right);
    });
  })
);
var isGridSpanNode = (pointer) => {
  var _a2, _b;
  return ((_b = resolveSpan((_a2 = pointer.node.data) == null ? void 0 : _a2.span)) == null ? void 0 : _b.mode) === "grid";
};
var selectGridSpanChildren = createSelector24(
  selectVisibleChildPointersToRender,
  (children2) => children2.filter((child) => isLoop(child) && isGridSpanNode(child))
);
var selectSpatialSlots = createSelector24(
  selectAppState,
  selectVisibleChildPointersToRender,
  (_s, _key, slotsName) => slotsName,
  (_s, _k, _n, grid) => grid,
  (s, children2, slotsName, grid) => {
    const supplier = children2.find(
      (child) => isLoop(child) && child.node.name === slotsName
    );
    if (!supplier) return [];
    const slots = [];
    selectChildPointers(s, supplier.key).forEach((iteration, index) => {
      const hidden = selectHidden(s, iteration.key);
      if (isRight(hidden) && hidden.right) return;
      const place2 = selectEvaluatedPlace(s, iteration.key, grid);
      if (place2.kind !== "placed") return;
      slots.push({ index, x: place2.x, y: place2.y });
    });
    return slots;
  }
);
var selectSpanSegments = createSelector24(
  selectAppState,
  (_s, containerKey) => containerKey,
  (_s, _c, hoistedKey) => hoistedKey,
  (_s, _c, _h, grid) => grid,
  (s, containerKey, hoistedKey, grid) => {
    var _a2;
    const hoisted = selectPointer(s, hoistedKey);
    const span2 = resolveSpan((_a2 = hoisted.node.data) == null ? void 0 : _a2.span);
    if (!span2 || span2.mode !== "grid") return [];
    const slots = selectSpatialSlots(s, containerKey, span2.slots, grid);
    if (slots.length === 0) return [];
    const supplierKeys = supplierIterationKeys(s, containerKey, span2.slots);
    const axisBySlot = slots.map((slot2) => {
      const key = supplierKeys[slot2.index];
      if (key === void 0) return void 0;
      const value = selectEvaluation(s, span2.axis, key);
      return isRight(value) ? value.right : void 0;
    });
    const coverage = [];
    for (const row of selectChildPointers(s, hoistedKey)) {
      const hidden = selectHidden(s, row.key);
      if (isRight(hidden) && hidden.right) continue;
      const rowValue = selectValue(s, row.key);
      const meta = isRight(rowValue) ? readSpanMeta(rowValue.right) : void 0;
      if (!meta) continue;
      const bounds = { start: meta.start, end: meta.end };
      const covered = [];
      slots.forEach((slot2, i) => {
        const axis2 = axisBySlot[i];
        if (axis2 === void 0) return;
        if (covers(bounds, axis2)) covered.push(slot2.index);
      });
      if (covered.length > 0) {
        coverage.push({ recordKey: row.key, slots: covered });
      }
    }
    return layoutSpanSegments(coverage, slots);
  }
);
var supplierIterationKeys = (s, containerKey, slotsName) => {
  const supplier = selectVisibleChildPointersToRender(s, containerKey).find(
    (child) => isLoop(child) && child.node.name === slotsName
  );
  if (!supplier) return [];
  return selectChildPointers(s, supplier.key).map((p) => p.key);
};

// src/orgicore/selectors/appstate/repeatSelectors.ts
var isHidden = (s, key) => {
  const r = selectHidden(s, key);
  return isRight(r) && r.right === true;
};
var selectVisibleInputErrors = (s, key) => selectAllErrors(s, key).filter((p) => {
  let at = p;
  while (at) {
    if (isHidden(s, at.key)) return false;
    if (at.key === key) return true;
    at = selectParent(s, at.key);
  }
  return true;
});
var selectPassScreenKeys = (s, passKey) => selectVisibleChildPointersToRender(s, passKey).filter((p) => p.node.type === SCREEN).map((p) => p.key);
var selectHasStoredValues = (s, key) => selectData(s).some(
  (d) => d.pointer === key || d.pointer.startsWith(`${key}.`) || d.pointer.startsWith(`${key}[`)
);
var selectPassStatus = (s, passKey) => {
  if (!selectHasStoredValues(s, passKey)) return "empty";
  return selectPassScreenKeys(s, passKey).every(
    (k) => selectVisibleInputErrors(s, k).length === 0
  ) ? "complete" : "partial";
};
var selectRepeatPasses = (s, repeatKey) => {
  const lengthResult = selectLength(s, repeatKey);
  const length = isRight(lengthResult) && typeof lengthResult.right === "number" ? lengthResult.right : 0;
  const passKeys = Array.from({ length }, (_, i) => `${repeatKey}[${i}]`);
  return {
    passScreens: passKeys.map((k) => selectPassScreenKeys(s, k)),
    newPassScreens: selectPassScreenKeys(s, `${repeatKey}[${length}]`),
    status: passKeys.map((k) => selectPassStatus(s, k))
  };
};

// src/orgicore/selectors/appstate/runningSlot.ts
var isRunningValue = (v) => typeof v === "object" && v !== null && "status" in v && v.status === "running";
var selectRunningSlotChildKeys = (state, stepKey) => isRunningValue(getRight(selectValue(state, stepKey))) ? selectChildPointers(state, stepKey).map((p) => p.key) : [];
var escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
var readsViewsCache = /* @__PURE__ */ new WeakMap();
var defReadsViews = (def, name) => {
  var _a2;
  let byName = readsViewsCache.get(def);
  if (!byName) {
    byName = /* @__PURE__ */ new Map();
    readsViewsCache.set(def, byName);
  }
  const cached = byName.get(name);
  if (cached !== void 0) return cached;
  let reads;
  try {
    const pattern = new RegExp(
      `(^|[^\\w$.])${escapeRegExp(name)}\\s*\\??\\.\\s*(partial|ready)\\b`
    );
    reads = pattern.test((_a2 = JSON.stringify(def)) != null ? _a2 : "");
  } catch {
    reads = true;
  }
  byName.set(name, reads);
  return reads;
};
var selectShowsAnswerProgress = (state, stepKey) => {
  if (selectChildPointers(state, stepKey).length > 0) return true;
  const { name } = selectPointer(state, stepKey).node;
  return name ? defReadsViews(state.def, name) : false;
};

// src/orgicore/selectors/helpers/createAppInstance.ts
import * as R13 from "remeda";

// src/orgicore/reducers/actions.ts
var INIT_APP = "@@orgicore/INIT_APP";
var INIT_APP_INSTANCE = "@@orgicore/INIT_APP_INSTANCE";
var UPDATE_DEF = "@@orgicore/UPDATE_DEF";
var UPDATE_CONTEXT_DATA = "@@orgicore/UPDATE_CONTEXT_DATA";
var UPDATE_REPO = "@@orgicore/UPDATE_REPO";
var UPDATE_VALUE = "@@orgicore/UPDATE_VALUE";
var CLEAR_VALUE = "@@orgicore/CLEAR_VALUE";
var INSERT_SLOT = "@@orgicore/INSERT_SLOT";
var SWAP_SLOT = "@@orgicore/SWAP_SLOT";
var MOVE_SLOT = "@@orgicore/MOVE_SLOT";
var SET_CURSOR = "@@orgicore/SET_CURSOR";
var PUSH_CURSOR = "@@orgicore/PUSH_CURSOR";
var POP_CURSOR = "@@orgicore/POP_CURSOR";
var MOVE_NODE = "@@orgicore/MOVE_NODE";
var REMOVE_NODE = "@@orgicore/REMOVE_NODE";
var CLEAR_SUBTREE = "@@orgicore/CLEAR_SUBTREE";
var BULK_ACTION = "@@orgicore/BULK_ACTION";
var LOAD_MORE = "@@orgicore/LOAD_MORE";
var SET_PAGE = "@@orgicore/SET_PAGE";
var initApp = (appDef, appInstance4) => ({
  type: INIT_APP,
  payload: { appDef, appInstance: appInstance4 }
});
var initAppInstance = (appInstance4) => ({
  type: INIT_APP_INSTANCE,
  payload: { appInstance: appInstance4 }
});
var updateDef = (appDef) => ({
  type: UPDATE_DEF,
  payload: { appDef }
});
var updateContextData = (data) => ({
  type: UPDATE_CONTEXT_DATA,
  payload: { data }
});
var updateRepo = (repo2) => ({
  type: UPDATE_REPO,
  payload: { repo: repo2 }
});
var updateValue = (pointerKey, value) => ({
  type: UPDATE_VALUE,
  payload: { pointerKey, value }
});
var clearValue = (pointerKey) => ({
  type: CLEAR_VALUE,
  payload: { pointerKey }
});
var insertSlot = (pointerKey, index) => ({
  type: INSERT_SLOT,
  payload: { pointerKey, index }
});
var swapSlot = (pointerKey, fromIndex, toIndex) => ({
  type: SWAP_SLOT,
  payload: { pointerKey, fromIndex, toIndex }
});
var moveSlot = (pointerKey, toIndex) => ({
  type: MOVE_SLOT,
  payload: { pointerKey, toIndex }
});
var setCursor = (pointerKey, args) => ({
  type: SET_CURSOR,
  payload: { pointerKey, args }
});
var pushCursor = (pointerKey, args) => ({
  type: PUSH_CURSOR,
  payload: { pointerKey, args }
});
var popCursor = () => ({
  type: POP_CURSOR
});
var moveNode = (pointerKey, toPointerKey, target) => ({
  type: MOVE_NODE,
  payload: { pointerKey, toPointerKey, target }
});
var removeNode = (pointerKey) => ({
  type: REMOVE_NODE,
  payload: { pointerKey }
});
var clearSubtree = (pointerKey) => ({
  type: CLEAR_SUBTREE,
  payload: { pointerKey }
});
var bulkAction = (actions) => ({
  type: BULK_ACTION,
  payload: { actions }
});
var loadMore = (pointerKey) => ({
  type: LOAD_MORE,
  payload: { pointerKey }
});
var setPage = (pointerKey, page) => ({
  type: SET_PAGE,
  payload: { pointerKey, page }
});
var bulkActions = {
  insertSlot,
  removeNode,
  updateValue,
  clearValue,
  clearSubtree,
  setCursor
};
var actionCreators = {
  initApp,
  initAppInstance,
  updateDef,
  updateContextData,
  updateRepo,
  updateValue,
  clearValue,
  clearSubtree,
  insertSlot,
  removeNode,
  swapSlot,
  moveSlot,
  setCursor,
  pushCursor,
  popCursor,
  moveNode,
  bulkAction,
  loadMore,
  setPage
};
var createActionApi = (dispatch) => {
  const actionApi = {};
  for (const key in actionCreators) {
    const actionCreator = actionCreators[key];
    actionApi[key] = (...args) => dispatch(actionCreator(...args));
  }
  return actionApi;
};

// src/orgicore/reducers/helpers.ts
var updateAppInstance = (s, appInstance4) => {
  const prevAppInstance = selectAppInstance(s);
  return {
    ...s,
    instance: {
      ...prevAppInstance,
      ...appInstance4
    }
  };
};

// src/orgicore/reducers/reducer.ts
var reducer = (state, action) => {
  switch (action.type) {
    case INIT_APP:
      return initApp2(state, action);
    case INIT_APP_INSTANCE:
      return initAppInstance2(state, action);
    case UPDATE_DEF:
      return updateDef2(state, action);
    case UPDATE_CONTEXT_DATA:
      return updateContextData2(state, action);
    case UPDATE_REPO:
      return updateRepo2(state, action);
    case UPDATE_VALUE:
      return updateValue2(state, action.payload);
    case CLEAR_VALUE:
      return clearValue2(state, action);
    case CLEAR_SUBTREE:
      return clearSubtree2(state, action.payload);
    case INSERT_SLOT:
      return insertSlot2(state, action);
    case REMOVE_NODE:
      return removeNode2(state, action.payload);
    case SWAP_SLOT:
      return swapSlot2(state, action.payload);
    case MOVE_NODE:
      return moveNode2(state, action);
    case MOVE_SLOT:
      return moveSlot2(state, action.payload);
    case SET_CURSOR:
      return setCursor2(state, action);
    case PUSH_CURSOR:
      return pushCursor2(state, action);
    case POP_CURSOR:
      return popCursor2(state);
    case LOAD_MORE:
      return loadMore2(state, action);
    case SET_PAGE:
      return setPage2(state, action);
    case BULK_ACTION:
      return action.payload.actions.reduce(
        (acc, action2) => reducer(acc, action2),
        state
      );
  }
};
var getPointer = (s, action) => {
  const pointerKey = action.payload.pointerKey;
  return selectPointer(s, pointerKey);
};
var resolvePointerOrWarn = (s, pointerKey) => {
  const r = safeSelectPointer(s, pointerKey);
  if (isLeft(r)) {
    console.warn(
      `[orgicore] ignoring write to unresolvable pointer "${pointerKey}" \u2014 the node is not declared on the current app def (${r.left}).`
    );
    return void 0;
  }
  return r.right;
};
var updateDataValue = (newValue) => (prev) => {
  if (prev.pointer === newValue.pointer) {
    return newValue;
  }
  return prev;
};
var initApp2 = (_s, action) => {
  const { appDef, appInstance: appInstance4 } = action.payload;
  return { ..._s, def: appDef, instance: appInstance4 };
};
var initAppInstance2 = (state, action) => {
  const { appInstance: appInstance4 } = action.payload;
  return { ...state, instance: appInstance4 };
};
var updateDef2 = (state, action) => {
  const { appDef } = action.payload;
  return { ...state, def: appDef };
};
var updateContextData2 = (state, action) => {
  const { data } = action.payload;
  return { ...state, context: { ...state.context, ...data } };
};
var updateRepo2 = (state, action) => {
  const { repo: repo2 } = action.payload;
  return { ...state, repo: repo2 };
};
var updateLoopLength = (s, loopKey, newLength) => {
  const prevLengths = selectLengths(s);
  const prevLengthsByPointer = selectLengthsByPointer(s);
  const newLengthValue = { pointer: loopKey, length: newLength };
  const prevLength = prevLengthsByPointer[loopKey];
  if (!prevLength) {
    return updateAppInstance(s, { lengths: [...prevLengths, newLengthValue] });
  }
  const lengths = prevLengths.map((length) => {
    if (length.pointer === loopKey) {
      return newLengthValue;
    }
    return length;
  });
  return updateAppInstance(s, { lengths });
};
var updateValue2 = (s, action) => {
  const { value, pointerKey } = action;
  const pointer = resolvePointerOrWarn(s, pointerKey);
  if (!pointer) {
    return s;
  }
  const { key, scope, node: node2 } = pointer;
  if (node2.isDerived) {
    return s;
  }
  const prevData = selectData(s);
  const newValue = { pointer: key, value };
  const dataByPointer = selectDataByPointer(s);
  const prev = dataByPointer[key];
  if (!prev) {
    const scopePath = selectScopePath(s, scope.key);
    const s2 = scopePath.reduce((acc, item) => {
      if (isScoped(item.node)) {
        const newLength = item.index + 1;
        const loopKey = getPointerKeyOfScope(item);
        const length = getRight(selectLength(s, loopKey));
        if (newLength > length) {
          return updateLoopLength(acc, loopKey, newLength);
        }
      }
      return acc;
    }, s);
    return updateAppInstance(s2, {
      data: [...prevData, newValue]
    });
  }
  if (prev.value === value) {
    return s;
  }
  const data = prevData.map(updateDataValue(newValue));
  return updateAppInstance(s, { data });
};
var createUpdatePointerKey = (s) => (loopKey, pointerKey, index) => {
  return selectScopeKeyWithIndexAt(s, loopKey, pointerKey, index);
};
var clearValue2 = (s, action) => {
  const pointer = resolvePointerOrWarn(s, action.payload.pointerKey);
  if (!pointer) {
    return s;
  }
  const prevData = selectData(s);
  const data = prevData.filter((data2) => data2.pointer !== pointer.key);
  return updateAppInstance(s, { data });
};
var clearSubtree2 = (s, { pointerKey }) => {
  const keys = collectSubtreeKeys(s, pointerKey);
  const prevData = selectData(s);
  const prevLengths = selectLengths(s);
  const data = prevData.filter((dv) => !keys.has(dv.pointer));
  const lengths = prevLengths.filter((lv) => !keys.has(lv.pointer));
  if (data.length === prevData.length && lengths.length === prevLengths.length) {
    return s;
  }
  return updateAppInstance(s, { data, lengths });
};
var collectSubtreeKeys = (s, rootKey) => {
  const keys = /* @__PURE__ */ new Set();
  const walk8 = (key) => {
    if (keys.has(key)) return;
    keys.add(key);
    for (const child of selectChildPointers(s, key)) {
      walk8(child.key);
    }
  };
  walk8(rootKey);
  return keys;
};
var findSubtreePointers = (s, rootKey, predicate) => {
  const matches = [];
  const visited = /* @__PURE__ */ new Set();
  const walk8 = (pointer) => {
    if (visited.has(pointer.key)) return;
    visited.add(pointer.key);
    if (predicate(pointer)) {
      matches.push(pointer.key);
    }
    for (const child of selectChildPointers(s, pointer.key)) {
      walk8(child);
    }
  };
  const root = safeSelectPointer(s, rootKey);
  if (isRight(root)) {
    walk8(root.right);
  }
  return matches;
};
var insertSlot2 = (s, action) => {
  const { node: node2, isIteration, key: loopKey } = getPointer(s, action);
  const { index: slotIndex } = action.payload;
  const prevData = selectData(s);
  const prevLengths = selectLengths(s);
  const prevLengthsByPointer = selectLengthsByPointer(s);
  if (!node2.isRepeating || isIteration) {
    return s;
  }
  const updatePointerKey = createUpdatePointerKey(s);
  const data = prevData.map((dv) => {
    const { pointer } = dv;
    const index = selectScopeIndex(s, loopKey, pointer);
    if (index >= slotIndex) {
      const updatedPointer = updatePointerKey(loopKey, pointer, index + 1);
      return { ...dv, pointer: updatedPointer };
    }
    return dv;
  });
  const lengths = prevLengths.map((lv) => {
    const { pointer, length } = lv;
    if (loopKey === pointer) {
      return { pointer, length: length + 1 };
    }
    const index = selectScopeIndex(s, loopKey, pointer);
    if (index >= slotIndex) {
      const updatedPointer = updatePointerKey(loopKey, pointer, index + 1);
      const updatedScope = selectScope(s, updatedPointer);
      return { length, pointer: getPointerKeyOfScope(updatedScope) };
    }
    return lv;
  });
  const s2 = updateAppInstance(s, { data, lengths });
  if (!prevLengthsByPointer[loopKey]) {
    const lengths2 = selectLengths(s2);
    return updateAppInstance(s2, {
      lengths: [...lengths2, { pointer: loopKey, length: 1 }]
    });
  }
  return s2;
};
var moveSlot2 = (s, { pointerKey, toIndex }) => {
  const scope = selectScope(s, pointerKey);
  const scopeKey = scope.key;
  if (!scope.node.isRepeating) {
    return s;
  }
  const prevData = selectData(s);
  const prevLengths = selectLengths(s);
  const updatePointerKey = createUpdatePointerKey(s);
  const fromIndex = scope.index;
  const minAffectedIndex = Math.min(fromIndex, toIndex);
  const maxAffectedIndex = Math.max(fromIndex, toIndex);
  const data = prevData.map((dv) => {
    const { pointer } = dv;
    const i = selectScopeIndex(s, scopeKey, pointer);
    if (i >= minAffectedIndex && i <= maxAffectedIndex) {
      const destIndex = i === fromIndex ? toIndex : i > fromIndex ? i - 1 : i + 1;
      const updatedPointer = updatePointerKey(scopeKey, pointer, destIndex);
      return { ...dv, pointer: updatedPointer };
    }
    return dv;
  });
  const lengths = prevLengths.map((lv) => {
    const { pointer } = lv;
    const i = selectScopeIndex(s, scopeKey, pointer);
    if (i >= minAffectedIndex && i <= maxAffectedIndex) {
      const destIndex = i === fromIndex ? toIndex : i > fromIndex ? i - 1 : i + 1;
      const updatedPointer = updatePointerKey(scopeKey, pointer, destIndex);
      const updatedScope = selectScope(s, updatedPointer);
      return { ...lv, pointer: getPointerKeyOfScope(updatedScope) };
    }
    return lv;
  });
  return updateAppInstance(s, { data, lengths });
};
var swapSlot2 = (s, { fromIndex, toIndex, pointerKey }) => {
  const scope = selectScope(s, pointerKey);
  const scopeKey = scope.key;
  const prevData = selectData(s);
  if (!scope.node.isRepeating) {
    return s;
  }
  const updatePointerKey = createUpdatePointerKey(s);
  const data = prevData.map((dv) => {
    const { pointer } = dv;
    const i = selectScopeIndex(s, scopeKey, pointer);
    if (i === fromIndex || i === toIndex) {
      const newIndex = i === fromIndex ? toIndex : fromIndex;
      const updatedPointer = updatePointerKey(scopeKey, pointer, newIndex);
      return { ...dv, pointer: updatedPointer };
    }
    return dv;
  });
  return updateAppInstance(s, { data });
};
var removeNode2 = (s, { pointerKey }) => {
  var _a2;
  const { scope, isIteration, key } = selectPointer(s, pointerKey);
  if (!isIteration) {
    const prefix = `${pointerKey}[`;
    const data2 = selectData(s).filter(
      (dv) => !dv.pointer.startsWith(prefix)
    );
    const lengths2 = selectLengths(s).filter(
      (lv) => lv.pointer !== pointerKey && !lv.pointer.startsWith(prefix)
    );
    return updateAppInstance(s, { data: data2, lengths: lengths2 });
  }
  const loopKey = ((_a2 = selectParent(s, key)) == null ? void 0 : _a2.key) || "";
  const prevData = selectData(s);
  const prevLengths = selectLengths(s);
  const slotIndex = scope.index;
  const updatePointerKey = createUpdatePointerKey(s);
  const data = prevData.flatMap((dv) => {
    const { pointer } = dv;
    const index = selectScopeIndex(s, loopKey, pointer);
    if (index === slotIndex) {
      return [];
    }
    if (index > slotIndex) {
      const updatedPointer = updatePointerKey(loopKey, pointer, index - 1);
      return { ...dv, pointer: updatedPointer };
    }
    return dv;
  });
  const lengths = prevLengths.flatMap((lv) => {
    const { pointer, length } = lv;
    if (loopKey === pointer) {
      const length2 = lv.length - 1;
      if (length2 === 0) {
        return [];
      }
      return { pointer, length: length2 };
    }
    const index = selectScopeIndex(s, loopKey, pointer);
    if (index === slotIndex) {
      return [];
    }
    if (index > slotIndex) {
      const updatedPointer = updatePointerKey(loopKey, pointer, index - 1);
      const updatedScope = selectScope(s, updatedPointer);
      return { length, pointer: getPointerKeyOfScope(updatedScope) };
    }
    return lv;
  });
  return updateAppInstance(s, { data, lengths });
};
var moveNode2 = (s, action) => {
  const { pointerKey, toPointerKey, target } = action.payload;
  const src = selectPointer(s, pointerKey);
  const srcParent = selectParent(s, src.key);
  if (!srcParent) {
    return s;
  }
  const dest = selectPointer(s, toPointerKey);
  if (!isTreeNode(src) || !isTreeNode(dest)) {
    return s;
  }
  if (src.key === dest.key) {
    return s;
  }
  if (target === "into") {
    const destSearchKey = getTreeNodeChildrenKey(dest);
    const destLoop = selectPointer(s, destSearchKey);
    if (srcParent.key === destLoop.key) {
      return s;
    }
    const [s2] = moveToNewParent(s, src, srcParent, destLoop);
    return s2;
  }
  if (target === "before" || target === "after") {
    if (isRootTreeNode(dest)) {
      return s;
    }
    const destLoop = selectParent(s, dest.key);
    if (!destLoop) {
      return s;
    }
    const fromIndex = src.scope.index;
    const destIndex = dest.scope.index;
    const forwardMoveOffset = target === "before" ? -1 : 0;
    const backwardMoveOffset = target === "after" ? 1 : 0;
    if (srcParent.key === destLoop.key) {
      if (fromIndex === destIndex) {
        return s;
      }
      const offset = fromIndex < destIndex ? forwardMoveOffset : backwardMoveOffset;
      const toIndex = destIndex + offset;
      if (toIndex === fromIndex) {
        return s;
      }
      return moveSlot2(s, { pointerKey: src.key, toIndex });
    }
    const [s2, newPointerKey] = moveToNewParent(s, src, srcParent, destLoop);
    if (s !== s2) {
      const toIndex = destIndex + backwardMoveOffset;
      return moveSlot2(s2, { pointerKey: newPointerKey, toIndex });
    }
  }
  return s;
};
var moveToNewParent = (s, src, srcLoop, destLoop) => {
  const { key: srcKey, scope: srcScope } = src;
  if (selectIsSameOrAncestorOf(s, srcKey, destLoop.key)) {
    return [s, srcKey];
  }
  const srcSlotIndex = srcScope.index;
  const destLength = getRight(selectLength(s, destLoop.key));
  const prevLengthsByPointer = selectLengthsByPointer(s);
  const prevData = selectData(s);
  const prevLengths = selectLengths(s);
  const newPointerKey = `${destLoop.key}[${destLength}]`;
  const data = prevData.flatMap((dv) => {
    const index = selectScopeIndex(s, srcLoop.key, dv.pointer);
    if (index === srcSlotIndex) {
      const pointer = dv.pointer.replace(srcKey, newPointerKey);
      return [dv, { ...dv, pointer }];
    }
    return dv;
  });
  const lengths = prevLengths.flatMap((lv) => {
    if (destLoop.key === lv.pointer) {
      const length = lv.length + 1;
      return { pointer: lv.pointer, length };
    }
    if (lv.pointer.startsWith(srcKey)) {
      const pointer = lv.pointer.replace(
        srcKey,
        `${destLoop.key}[${destLength}]`
      );
      return [lv, { pointer, length: lv.length }];
    }
    return lv;
  });
  let s2 = updateAppInstance(s, { data, lengths });
  if (!prevLengthsByPointer[destLoop.key]) {
    const lengths2 = selectLengths(s2);
    s2 = updateAppInstance(s2, {
      lengths: [...lengths2, { pointer: destLoop.key, length: 1 }]
    });
  }
  const s3 = removeNode2(s2, { pointerKey: srcKey });
  return [s3, newPointerKey];
};
var setCursor2 = (s, action) => {
  const { pointerKey, args } = action.payload;
  return updateAppInstance(
    s,
    args === void 0 ? { cursor: pointerKey } : { cursor: pointerKey, cursorArgs: args }
  );
};
var NON_IDEMPOTENT_STEP_VARIANTS = /* @__PURE__ */ new Set(["script", "publishData"]);
var hasRunningNonIdempotentStep = (s, flowKey) => {
  const dataByPointer = selectDataByPointer(s);
  return findSubtreePointers(
    s,
    flowKey,
    (p) => p.node.type === FLOW_STEP && typeof p.node.variant === "string" && NON_IDEMPOTENT_STEP_VARIANTS.has(p.node.variant)
  ).some((stepKey) => {
    var _a2;
    const value = (_a2 = dataByPointer[stepKey]) == null ? void 0 : _a2.value;
    return typeof value === "object" && value !== null && value.status === "running";
  });
};
var pushCursor2 = (s, action) => {
  const { pointerKey, args } = action.payload;
  if (!pointerKey) return s;
  const cursor = selectCursor(s);
  const currentArgs = selectCursorArgs(s);
  const clearFlowsUnderTarget = (state) => findSubtreePointers(state, pointerKey, (p) => p.node.type === FLOW).filter((flowKey) => !hasRunningNonIdempotentStep(state, flowKey)).reduce((acc, key) => clearSubtree2(acc, { pointerKey: key }), state);
  if (pointerKey === cursor && shallowEqualArgs(currentArgs, args)) {
    return clearFlowsUnderTarget(s);
  }
  const navStack = selectNavStack(s);
  const navStackArgs = selectNavStackArgs(s);
  const afterNav = updateAppInstance(s, {
    navStack: cursor ? [...navStack, cursor] : navStack,
    navStackArgs: cursor ? [...navStackArgs, currentArgs] : navStackArgs,
    cursor: pointerKey,
    cursorArgs: args
  });
  return clearFlowsUnderTarget(afterNav);
};
var shallowEqualArgs = (a, b2) => {
  const ae = a != null ? a : {};
  const be = b2 != null ? b2 : {};
  const aKeys = Object.keys(ae);
  const bKeys = Object.keys(be);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => ae[k] === be[k]);
};
var popCursor2 = (s) => {
  const prevNavStack = selectNavStack(s);
  const prevNavStackArgs = selectNavStackArgs(s);
  const navStack = prevNavStack.slice(0, -1);
  const navStackArgs = prevNavStackArgs.slice(0, -1);
  const cursor = prevNavStack[navStack.length] || selectDefaultPopTarget(s);
  const cursorArgs = prevNavStackArgs[navStack.length];
  return updateAppInstance(s, {
    navStack,
    navStackArgs,
    cursor,
    cursorArgs
  });
};
var getPaginationContext = (s, pointerKey) => {
  const pointer = selectPointer(s, pointerKey);
  const { node: node2, key, scope } = pointer;
  const pageSize = node2.pageSize;
  if (pageSize === void 0) {
    return void 0;
  }
  const pageSizeValue = typeof pageSize === "number" ? pageSize : getRight(selectEvaluation(s, pageSize, scope.key)) || 0;
  if (pageSizeValue <= 0) {
    return void 0;
  }
  const maxLengthOption = node2.maxLength;
  const maxLengthValue = maxLengthOption === void 0 ? void 0 : typeof maxLengthOption === "number" ? maxLengthOption : getRight(selectEvaluation(s, maxLengthOption, scope.key));
  let totalLength = maxLengthValue != null ? maxLengthValue : Infinity;
  const dataOptions2 = node2.data;
  if (isDataQuery(dataOptions2)) {
    const valueResult = selectValue(s, key);
    if (isRight(valueResult) && Array.isArray(valueResult.right)) {
      const dataLength = valueResult.right.length;
      totalLength = maxLengthValue !== void 0 ? Math.min(dataLength, maxLengthValue) : dataLength;
    }
  }
  const currentLength = getRight(selectLength(s, key));
  return { key, pageSizeValue, currentLength, totalLength };
};
var setPage2 = (s, action) => {
  const { pointerKey, page } = action.payload;
  const ctx = getPaginationContext(s, pointerKey);
  if (!ctx) {
    return s;
  }
  const newLength = (page + 1) * ctx.pageSizeValue;
  return updateLoopLength(s, ctx.key, newLength);
};
var loadMore2 = (s, action) => {
  const { pointerKey } = action.payload;
  const ctx = getPaginationContext(s, pointerKey);
  if (!ctx) {
    return s;
  }
  const { currentLength, totalLength, pageSizeValue } = ctx;
  if (currentLength >= totalLength) {
    return s;
  }
  const currentPage = Math.ceil(currentLength / pageSizeValue) - 1;
  return setPage2(s, {
    type: SET_PAGE,
    payload: { pointerKey, page: currentPage + 1 }
  });
};

// src/orgicore/utils/test/testApi.ts
var createTestApi = (appDef = DEFAULT_APP_DEF, context = {}, repo2) => {
  let state = { def: appDef, context, repo: repo2 };
  const dispatch = (action) => {
    state = reducer(state, action);
  };
  const api = createActionApi(dispatch);
  const getState = () => state;
  api.getState = getState;
  const sel = (selector) => (...args) => selector(state, ...args);
  return { api, getState, sel };
};

// src/orgicore/selectors/helpers/createAppInstance.ts
var initWith = (app2, main = {}) => {
  const { api, sel } = app2;
  const getPointer2 = sel(safeSelectPointer);
  api.initAppInstance();
  const initAtScope = (obj, scopeKey = "") => {
    Object.entries(obj).forEach(([pointer, value]) => {
      var _a2;
      if (value !== void 0) {
        const pKey = compactJoin([scopeKey, pointer]);
        const r = getPointer2(pKey);
        if (isLeft(r)) {
          if (String(r.left).endsWith("not found")) {
            return;
          }
          throw new Error(String(r.left));
        }
        const p = r.right;
        if (p.node.isRecord && unknownRecord.is(value)) {
          const keyField = (_a2 = p.node.recordKey) != null ? _a2 : "key";
          const valueField = p.node.recordValue;
          Object.entries(value).forEach(([k, v], index) => {
            initAtScope(
              typeof valueField === "string" ? { [keyField]: k, [valueField]: v } : { [keyField]: k, ...unknownRecord.is(v) ? v : {} },
              `${pKey}[${index}]`
            );
          });
        } else if (p.node.isRepeating && unknownRecordArray.is(value)) {
          value.forEach((v, index) => {
            if (R13.isEmpty(v)) {
              api.insertSlot(p.key, index);
            } else {
              initAtScope(v, `${pKey}[${index}]`);
            }
          });
        } else if (p.node.isOwnScope) {
          if (unknownRecord.is(value)) {
            initAtScope(value, `${pKey}[0]`);
          }
        } else {
          api.updateValue(p.key, value);
        }
      }
    });
  };
  initAtScope(main);
};
var createAppInstance = (obj, appDef) => {
  const app2 = createTestApi(appDef);
  initWith(app2, obj);
  return app2.getState().instance;
};
var sanitizeAppInstance = (instance, appDef) => {
  var _a2, _b, _c, _d;
  if (!instance) return instance;
  const isValidKey = (key) => !isLeft(safeSelectPointer({ def: appDef }, key));
  const cursorValid = instance.cursor ? isValidKey(instance.cursor) : true;
  const prevNav = (_a2 = instance.navStack) != null ? _a2 : [];
  const prevArgs = (_b = instance.navStackArgs) != null ? _b : [];
  const firstBad = prevNav.findIndex((k) => !isValidKey(k));
  const navTruncated = firstBad !== -1;
  const navStack = navTruncated ? prevNav.slice(0, firstBad) : prevNav;
  const navStackArgs = navTruncated ? prevArgs.slice(0, firstBad) : prevArgs;
  const prevData = (_c = instance.data) != null ? _c : [];
  const data = prevData.filter((d) => isValidKey(d.pointer));
  const dataChanged = data.length !== prevData.length;
  const prevLengths = (_d = instance.lengths) != null ? _d : [];
  const lengths = prevLengths.filter((l) => isValidKey(l.pointer));
  const lengthsChanged = lengths.length !== prevLengths.length;
  if (cursorValid && !navTruncated && !dataChanged && !lengthsChanged) {
    return instance;
  }
  return {
    ...instance,
    cursor: cursorValid ? instance.cursor : void 0,
    navStack,
    navStackArgs,
    data,
    lengths
  };
};

// src/orgicore/ui/hooks/hooks.ts
import {
  useCallback,
  useContext as useContext2,
  useEffect as useEffect2,
  useMemo as useMemo2,
  useReducer,
  useRef,
  useState
} from "react";
import * as R16 from "remeda";

// src/orgicore/utils/inFlight.ts
import * as R14 from "remeda";
var isRecord3 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var isDataEntry = (v) => isRecord3(v) && typeof v.pointer === "string";
var isRunningValue2 = (v) => isRecord3(v) && v.status === "running" && ("partial" in v || "ready" in v);
var stripEntry = (entry) => {
  if (!isDataEntry(entry) || !isRunningValue2(entry.value)) return entry;
  const { partial: _partial, ready: _ready, ...rest } = entry.value;
  return { ...entry, value: rest };
};
var withoutInFlightStepValues = (instance) => {
  const data = "data" in instance ? instance.data : void 0;
  if (!Array.isArray(data)) return instance;
  const next = data.map(stripEntry);
  if (next.every((entry, i) => entry === data[i])) return instance;
  return { ...instance, data: next };
};
var sameEntries = (a, b2) => a.length === b2.length && a.every((entry, i) => entry === b2[i] || R14.isDeepEqual(entry, b2[i]));
var isSameOutput = (prev, next) => {
  if (prev === next) return true;
  if (!isRecord3(prev) || !isRecord3(next)) return false;
  const keys = Object.keys(next);
  if (keys.length !== Object.keys(prev).length) return false;
  return keys.every((k) => {
    const a = prev[k];
    const b2 = next[k];
    if (a === b2) return true;
    return k === "data" && Array.isArray(a) && Array.isArray(b2) ? sameEntries(a, b2) : false;
  });
};

// src/orgicore/ui/types/context.ts
var [AppProvider, useImmediateAppContext, AppContext] = createContextTools("App");
var [PointerProvider, useImmediatePointerContext, PointerContext] = createContextTools("Pointer");

// src/orgicore/ui/types/pointer.ts
import * as R15 from "remeda";
var getNamespaceAndPointerKey = (param = {}) => {
  if (R15.isString(param)) {
    return { pointerKey: param };
  }
  return param;
};

// src/orgicore/ui/hooks/bulkHelpers.ts
var createTreeNode = (api, p, data) => {
  const key = getTreeNodeChildrenKey(p);
  const newChildKey = `${key}[0]`;
  const newChildValueKey = `${newChildKey}.value`;
  let cleanData = data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const { id: _stripId, ...rest } = data;
    cleanData = rest;
  }
  api.bulkAction([
    bulkActions.insertSlot(key, 0),
    bulkActions.updateValue(expandedKey(p), true),
    bulkActions.updateValue(newChildValueKey, cleanData),
    bulkActions.setCursor(newChildKey)
  ]);
};
var buildTreePasteActions = (parentChildrenKey, treeName, data, actions, index = 0) => {
  const newNodeKey = `${parentChildrenKey}[${index}]`;
  const newNodeValueKey = `${newNodeKey}.value`;
  const { _children, id: _stripId, ...valueWithoutChildren } = data;
  actions.push(bulkActions.insertSlot(parentChildrenKey, index));
  actions.push(bulkActions.updateValue(newNodeValueKey, valueWithoutChildren));
  if (_children && _children.length > 0) {
    const expandedKeyForNode = `${newNodeKey}.expanded`;
    actions.push(bulkActions.updateValue(expandedKeyForNode, true));
    const childrenKey = `${newNodeKey}.${treeName}`;
    for (let i = _children.length - 1; i >= 0; i--) {
      buildTreePasteActions(childrenKey, treeName, _children[i], actions, 0);
    }
  }
  return newNodeKey;
};
var createTreeNodeWithChildren = (api, p, data) => {
  const key = getTreeNodeChildrenKey(p);
  const treeName = p.node.name;
  const actions = [];
  actions.push(bulkActions.updateValue(expandedKey(p), true));
  const newNodeKey = buildTreePasteActions(key, treeName, data, actions, 0);
  actions.push(bulkActions.setCursor(newNodeKey));
  api.bulkAction(actions);
};
var insertListItem = (api, p, index) => {
  const newChildKey = `${p.key}[${index}]`;
  api.bulkAction([
    bulkActions.insertSlot(p.key, index),
    bulkActions.setCursor(newChildKey)
  ]);
};
var buildTreeStructure = (state, pointer) => {
  const valueResult = selectTreeNodeValue(state, pointer.key);
  if (!isRight(valueResult) || valueResult.right === void 0) {
    return void 0;
  }
  const value = valueResult.right;
  const childPointers = selectChildTreeNodes(state, pointer.key);
  if (childPointers.length === 0) {
    return { ...value };
  }
  const children2 = [];
  for (const childPointer of childPointers) {
    const childTree = buildTreeStructure(state, childPointer);
    if (childTree) {
      children2.push(childTree);
    }
  }
  return {
    ...value,
    _children: children2
  };
};

// src/orgicore/ui/hooks/hooks.ts
import { createSelector as createSelector25 } from "reselect";
var [AppDataProvider, useAppData] = createContextTools(
  "AppData",
  { ctx: {} }
);
var defaultCodec = {
  createAppInstance: R16.identity(),
  toOutputData: selectAppInstance
};
var useCreateAppContext = (props) => {
  const parentAppContext = useContext2(AppContext);
  const {
    name,
    def,
    initialData,
    data,
    onDataChange,
    persistenceKey,
    fallbackPersistenceKey,
    debounceMs = 500,
    codec = defaultCodec,
    repo: repo2
  } = props;
  if (parentAppContext && !name) {
    console.log("Name is required when there is a parent app context", def);
    throw new Error("Name is required when there is a parent app context");
  }
  const { ctx } = useAppData();
  const [isInitialized, setIsInitialized] = useState(false);
  const initialState = useMemo2(() => {
    return {
      def,
      instance: codec.createAppInstance(initialData, def),
      context: ctx,
      repo: repo2
    };
  }, [def, initialData, codec, ctx, repo2]);
  const [state, dispatch] = useReducer(reducer, initialState);
  const listenersRef = useRef([]);
  const stateRef = useRef(state);
  stateRef.current = state;
  const subscribe = useCallback(
    (selector, listener) => {
      const entry = {
        selector,
        listener,
        prev: selector(stateRef.current),
        prevState: stateRef.current
      };
      listenersRef.current.push(entry);
      return () => {
        listenersRef.current = listenersRef.current.filter((l) => l !== entry);
      };
    },
    []
  );
  const api = useMemo2(() => createActionApi(dispatch), [dispatch]);
  const isFirstContextRender = useRef(true);
  useEffect2(() => {
    if (isFirstContextRender.current) {
      isFirstContextRender.current = false;
      return;
    }
    if (ctx) {
      api.updateContextData(ctx);
    }
  }, [ctx, api]);
  const isFirstRepoRender = useRef(true);
  useEffect2(() => {
    if (isFirstRepoRender.current) {
      isFirstRepoRender.current = false;
      return;
    }
    if (repo2) {
      api.updateRepo(repo2);
    }
  }, [repo2, api]);
  useEffect2(() => {
    listenersRef.current.forEach((listener) => {
      const newVal = listener.selector(state);
      if (!Object.is(newVal, listener.prev)) {
        listener.listener(newVal, listener.prev, state, listener.prevState);
        listener.prev = newVal;
        listener.prevState = state;
      }
    });
  }, [state]);
  const persistence = usePersistence();
  useEffect2(() => {
    let lastEmitted;
    const unsubscribe = subscribe(
      codec.toOutputData,
      (next, _prev, _s1, _s2) => {
        const obj = withoutInFlightStepValues(next);
        if (lastEmitted !== void 0 && isSameOutput(lastEmitted, obj)) return;
        lastEmitted = obj;
        if (persistenceKey) {
          persistence.save(persistenceKey, obj, debounceMs);
        }
        onDataChange == null ? void 0 : onDataChange(obj, _s1);
      }
    );
    return unsubscribe;
  }, [
    persistenceKey,
    subscribe,
    persistence,
    def,
    api,
    codec,
    onDataChange,
    debounceMs
  ]);
  const defRef = useRef(def);
  defRef.current = def;
  const nameRef = useRef(name);
  nameRef.current = name;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const codecRef = useRef(codec);
  codecRef.current = codec;
  const prevPersistenceKeyRef = useRef(persistenceKey);
  const fallbackPersistenceKeyRef = useRef(fallbackPersistenceKey);
  fallbackPersistenceKeyRef.current = fallbackPersistenceKey;
  useEffect2(() => {
    const isKeyChange = prevPersistenceKeyRef.current !== persistenceKey;
    let cancelled = false;
    if (isKeyChange) {
      diag({
        kind: "persistence-key-change",
        from: prevPersistenceKeyRef.current,
        to: persistenceKey,
        name: nameRef.current
      });
      setIsInitialized(false);
    }
    prevPersistenceKeyRef.current = persistenceKey;
    (async () => {
      let loaded = false;
      if (persistenceKey) {
        const obj = await persistence.find(persistenceKey);
        if (!cancelled && isRight(obj)) {
          const initial = obj.right;
          api.initApp(
            defRef.current,
            sanitizeAppInstance(
              codecRef.current.createAppInstance(initial, defRef.current),
              defRef.current
            )
          );
          loaded = true;
        }
      }
      if (cancelled) return;
      if (isKeyChange && !loaded) {
        let fallbackLoaded = false;
        const fallbackKey = fallbackPersistenceKeyRef.current;
        if (fallbackKey) {
          const fallbackObj = await persistence.find(fallbackKey);
          if (!cancelled && isRight(fallbackObj)) {
            api.initApp(
              defRef.current,
              sanitizeAppInstance(
                codecRef.current.createAppInstance(
                  fallbackObj.right,
                  defRef.current
                ),
                defRef.current
              )
            );
            fallbackLoaded = true;
          }
        }
        if (!fallbackLoaded && !cancelled) {
          api.initApp(
            defRef.current,
            sanitizeAppInstance(
              codecRef.current.createAppInstance(
                initialDataRef.current,
                defRef.current
              ),
              defRef.current
            )
          );
        }
      }
      setIsInitialized(true);
    })();
    return () => {
      cancelled = true;
      if (persistenceKey) {
        persistence.flushPendingSave(persistenceKey);
      }
    };
  }, [persistenceKey, persistence, api]);
  const isFirstDefRender = useRef(true);
  useEffect2(() => {
    if (isFirstDefRender.current) {
      isFirstDefRender.current = false;
      return;
    }
    api.updateDef(def);
  }, [def, api]);
  useEffect2(() => {
    if (data !== void 0) {
      log("initializing app instance from external data", data);
      api.initAppInstance(codec.createAppInstance(data, def));
    }
  }, [data, def, api, codec]);
  return {
    name,
    isInitialized,
    state,
    api,
    subscribe,
    persistenceKey,
    parentAppContext
  };
};
var findAppContext = (appContext, searchParam) => {
  switch (searchParam) {
    case "@root":
      return getRootAppContext(appContext);
    case "@parent":
      return appContext.parentAppContext;
    case "@self":
      return appContext;
  }
  return getAppContext(appContext, searchParam);
};
var getAppContext = (appContext, namespace) => {
  if (appContext.name === namespace) {
    return appContext;
  }
  if (appContext.parentAppContext) {
    return getAppContext(appContext.parentAppContext, namespace);
  }
  return null;
};
var getPointerContext = (pointerContext, namespace) => {
  if (namespace === void 0 || pointerContext.namespace === namespace) {
    return pointerContext;
  }
  if (pointerContext.parent) {
    return getPointerContext(pointerContext.parent, namespace);
  }
  return null;
};
var useAppContext = (searchParam = "@self") => {
  const appContext = useImmediateAppContext();
  const ctx = findAppContext(appContext, searchParam);
  if (!ctx) {
    throw new Error(`App context not found for search param ${searchParam}`);
  }
  return ctx;
};
var usePointerContext = (param) => {
  const { namespace, pointerKey } = getNamespaceAndPointerKey(param);
  const { name } = useAppContext(namespace);
  const pointerContext = useImmediatePointerContext();
  if (pointerKey) {
    return {
      namespace: name,
      pointerKey
    };
  }
  const ctx = getPointerContext(pointerContext, name);
  if (!ctx) {
    throw new Error(`Pointer context not found for namespace ${name}`);
  }
  return ctx;
};
var useStateSelector = (selector, searchParam) => {
  const { state } = useAppContext(searchParam);
  return selector(state);
};
var createStateSelHook = (selector) => (searchParam) => {
  return useStateSelector(selector, searchParam);
};
var usePointerKey = (param) => {
  return usePointerContext(param).pointerKey;
};
var usePointerSelector = (selector, param) => {
  const pKey = usePointerKey(param);
  const { state } = useAppContext(getNamespaceAndPointerKey(param).namespace);
  return selector(state, pKey);
};
var createPointerSelHook = (selector) => (param) => {
  return usePointerSelector(selector, param);
};
var usePointer = createPointerSelHook(selectPointer);
var useChildPointers = createPointerSelHook(
  selectChildPointersToRender
);
var useVisibleChildPointers = createPointerSelHook(
  selectVisibleChildPointersToRender
);
var useSpatialGrid = createPointerSelHook(selectSpatialGrid);
var useSpatialItemPointers = createPointerSelHook(
  selectSpatialItemPointers
);
var useEvaluatedPlace = (grid, param) => {
  const pKey = usePointerKey(param);
  const { state } = useAppContext(getNamespaceAndPointerKey(param).namespace);
  return selectEvaluatedPlace(state, pKey, grid);
};
var useGridSpanChildren = createPointerSelHook(selectGridSpanChildren);
var useSpanSegments = (hoistedKey, grid, param) => {
  const pKey = usePointerKey(param);
  const { state } = useAppContext(getNamespaceAndPointerKey(param).namespace);
  return selectSpanSegments(state, pKey, hoistedKey, grid);
};
var useLength = createPointerSelHook(
  createSelector25(selectLength, getRight)
);
var useHasChildren = () => {
  const childPointers = useChildPointers();
  return childPointers.length > 0;
};
var useCursor = createStateSelHook(selectCursor);
var useCursorArgs = createStateSelHook(selectCursorArgs);
var useCurrentPointer = createStateSelHook(selectCurrentPointer);
var useCurrentNavRoute = createStateSelHook(selectCurrentNavRoute);
var useCurrentScreenHidesHeader = createStateSelHook(
  selectCurrentScreenHidesHeader
);
var useTopLevelNavRoutes = createStateSelHook(selectTopLevelNavRoutes);
var useRemoveNode = (param) => {
  const { api } = useAppContext();
  const { key } = usePointer(param);
  return useCallback(() => api.removeNode(key), [api, key]);
};
var usePaginationInfo = () => {
  return usePointerSelector(selectPaginationInfo);
};
var useLoadMore = () => {
  const { api } = useAppContext();
  const { key } = usePointer();
  return useCallback(() => api.loadMore(key), [api, key]);
};
var useSetPage = () => {
  const { api } = useAppContext();
  const { key } = usePointer();
  return useCallback((page) => api.setPage(key, page), [api, key]);
};
var useCreateNode = () => {
  const { api } = useAppContext();
  const p = usePointer();
  return useCallback(
    (typeKey) => {
      const { type: type14, variant } = typeKeyToAuthored(typeKey);
      createTreeNode(api, p, variant ? { type: type14, variant } : { type: type14 });
    },
    [api, p]
  );
};
var useInsertListItem = (index) => {
  const { api } = useAppContext();
  const p = usePointer();
  return useCallback(() => insertListItem(api, p, index), [api, p, index]);
};
var useNavToParent = () => {
  const { api, state } = useAppContext();
  const cursor = useCursor();
  const nextNavRoute = selectParentNavRoute(state, cursor);
  return useCallback(() => {
    api.setCursor(nextNavRoute.key);
  }, [api, nextNavRoute]);
};
var useNavPop = (namespace) => {
  const { api } = useAppContext(namespace);
  return useCallback(() => api.popCursor(), [api]);
};
var useNavNext = () => {
  const { api, state } = useAppContext();
  const nextNavRoute = selectNextNavRoute(state, selectCurrentPointer(state).key);
  return useCallback(() => {
    if (nextNavRoute) {
      api.setCursor(nextNavRoute.key);
    }
  }, [api, nextNavRoute]);
};
var useNavPrevious = () => {
  const { api, state } = useAppContext();
  const previousNavRoute = selectPreviousNavRoute(
    state,
    selectCurrentPointer(state).key
  );
  return useCallback(() => {
    if (previousNavRoute) {
      api.setCursor(previousNavRoute.key);
    }
  }, [api, previousNavRoute]);
};
var useTreeNavNext = () => {
  const { api, state } = useAppContext();
  const cursor = useCursor();
  return useCallback(() => {
    const nextNode = selectNextExpanded(state, cursor);
    if (nextNode) {
      api.setCursor(nextNode.key);
    }
  }, [api, state, cursor]);
};
var useTreeNavPrevious = () => {
  const { api, state } = useAppContext();
  const cursor = useCursor();
  return useCallback(() => {
    const previousNode = selectPreviousExpanded(state, cursor);
    if (previousNode) {
      api.setCursor(previousNode.key);
    }
  }, [api, state, cursor]);
};
var useNavTo = () => {
  const { api } = useAppContext();
  const pKey = usePointerKey();
  return useCallback(() => {
    api.setCursor(pKey);
  }, [api, pKey]);
};
var useSetCursor = (namespace) => {
  const { api } = useAppContext(namespace);
  return useCallback(
    (pKey, args) => api.setCursor(pKey, args),
    [api]
  );
};
var useSelectSelf = () => {
  const { api } = useAppContext();
  const pKey = usePointerKey();
  return useCallback(() => api.setCursor(pKey), [api, pKey]);
};
var useIsSelected = () => {
  const pKey = usePointerKey();
  const cursor = useCursor();
  return pKey === cursor;
};
var usePushCursor = () => {
  const { api } = useAppContext();
  return useCallback(
    (pKey, args) => {
      api.pushCursor(pKey, args);
    },
    [api]
  );
};
var useLabel = createPointerSelHook(selectLabel);
var useNodeType = () => {
  const {
    node: { type: type14 }
  } = usePointer();
  return type14;
};
var useNodeProps = createPointerSelHook(selectNode);
var useRecordTreeProps = createPointerSelHook(selectRecordTreeProps);
var useIsNodePropsError = () => isAnyError(useNodeProps());
var useNodeErrors = () => getErrors(useNodeProps());
var useNodeName = () => usePointer().node.name;
var useCommonProps = () => {
  return usePointerSelector(selectCommonProps);
};
var useCommonInputProps = () => {
  return usePointerSelector(selectCommonInputProps);
};
var useValue = () => {
  return getRight(usePointerSelector(selectValue));
};
var usePluginProps = () => {
  return usePointerSelector(selectPluginProps);
};
var useNumberArrayInputProps = () => {
  return usePointerSelector(selectNumberArrayInputProps);
};
var useStringArrayInputProps = () => {
  return usePointerSelector(selectStringArrayInputProps);
};
var useNumberInputVariant = () => {
  return getRight(usePointerSelector(selectNumberVariant)) || "integer";
};
var usePickerInputProps = () => {
  return usePointerSelector(selectCommonPickerProps);
};
var useSinglePickerInputProps = () => {
  return usePointerSelector(selectSinglePickerInputProps);
};
var useMultiPickerInputProps = () => {
  return usePointerSelector(selectMultiPickerInputProps);
};
var useFormProps = createPointerSelHook(selectFormProps);
var useUncommittedFileUploads = (namespace, pKey) => {
  const { state } = useAppContext(namespace);
  return selectUncommittedFileUploads(state, pKey);
};
var useUpdateValue = (key) => {
  const { api } = useAppContext();
  return useCallback(
    (v) => {
      if (v === void 0) {
        api.clearValue(key);
      } else {
        api.updateValue(key, v);
      }
    },
    [api, key]
  );
};
function useAppState(pointerKey, codec, defaultValue, namespace) {
  const { state, api } = useAppContext(namespace);
  const selector = useMemo2(() => createTypedValueSelector(codec), [codec]);
  const rawValue = selector(state, pointerKey);
  const decoded = isRight(rawValue) ? rawValue.right : void 0;
  const value = defaultValue !== void 0 ? decoded != null ? decoded : defaultValue : getRight(rawValue);
  const setValue = useCallback(
    (v) => {
      if (v === void 0) {
        api.clearValue(pointerKey);
      } else {
        api.updateValue(pointerKey, v);
      }
    },
    [api, pointerKey]
  );
  return [value, setValue];
}
var useUpdateBoolean = (key) => {
  const setValue = useUpdateValue(key);
  return useCallback(
    (expanded) => {
      setValue(expanded || void 0);
    },
    [setValue]
  );
};
var useSetValue = () => useUpdateValue(usePointerKey());
var useClearValue = (param) => {
  const { api } = useAppContext(getNamespaceAndPointerKey(param).namespace);
  const key = usePointerKey(param);
  return useCallback(() => {
    api.clearValue(key);
  }, [api, key]);
};
var useSetTreeNodeValue = () => useUpdateValue(usePointerSelector(selectValueKey));
var useSetTreeNodeExpanded = () => useUpdateBoolean(usePointerSelector(selectExpandedKey));
var useFirstTreeNode = createPointerSelHook(selectFirstTreeNode);
var useTreeDepth = createPointerSelHook(selectTreeDepth);
var useChildTreeNodes = createPointerSelHook(selectChildTreeNodes);
var useHasChildTreeNodes = createPointerSelHook(
  selectHasChildTreeNodes
);
var useAllowedChildTypes = createPointerSelHook(
  selectAllowedChildTypes
);
var useIsDeleteAllowed = createPointerSelHook(selectIsDeleteAllowed);
var useTreeNodeIcon = createPointerSelHook(selectTreeNodeIcon);
var useTreeNodeIconColor = createPointerSelHook(
  selectTreeNodeIconColor
);
var useTreeNodeTypeName = createPointerSelHook(selectTreeNodeTypeName);
var useTreeProps = createPointerSelHook(selectTreeProps);
var useFirstError = createPointerSelHook(selectFirstErrorSafe);
var useLayout = () => {
  return getRight(usePointerSelector(selectLayout));
};
var useContainerStyle = () => {
  return getRight(usePointerSelector(selectContainerStyle));
};
var useDataOptions = () => {
  return getRight(usePointerSelector(selectDataOptions));
};
var useCalculatedPluginProps = () => {
  return getRight(usePointerSelector(selectCalculatedPluginProps));
};
var createPluginPropsHook = (codec) => {
  return createPointerSelHook(createTypedPluginPropsSelector(codec));
};
var useEvalFormula = (formula) => {
  const { state } = useAppContext();
  const stateRef = useRef(state);
  stateRef.current = state;
  return getRight(selectEvaluation(state, formula));
};
var useFormulaEvaluator = (param) => {
  const { state } = useAppContext(getNamespaceAndPointerKey(param).namespace);
  const stateRef = useRef(state);
  const { scope } = usePointer(param);
  stateRef.current = state;
  return useCallback(
    (formula, ctx = {}) => {
      return getRight(
        selectEvaluation(stateRef.current, formula, scope.key, ctx)
      );
    },
    [scope.key]
  );
};
var getRootAppContext = (appContext) => {
  const { parentAppContext } = appContext;
  if (!parentAppContext) {
    return appContext;
  }
  return getRootAppContext(parentAppContext);
};
var useRootAppContext = () => {
  return useAppContext("@root");
};
var useBooleanState = (pointerKey) => {
  const [value, setValue] = useAppState(pointerKey, optionalBoolean, false);
  const toggle = createToggle(value, setValue);
  return { value, toggle };
};
var createBooleanStateHook = (pointerKey) => () => {
  return useBooleanState(pointerKey);
};
function createAppStateHook({
  pointerKey,
  codec,
  defaultValue,
  namespace
}) {
  return () => {
    const p = usePointerKey({ namespace, pointerKey });
    return useAppState(p, codec, defaultValue, namespace);
  };
}

// src/orgicore/ui/hooks/generated/propsHooks.generated.ts
var useAppProps = createPointerSelHook(selectAppProps);
var useTabGroupProps = createPointerSelHook(selectTabGroupProps);
var useTabProps = createPointerSelHook(selectTabProps);
var useFolderProps = createPointerSelHook(selectFolderProps);
var useSubsectionProps = createPointerSelHook(selectSubsectionProps);
var useFlowProps = createPointerSelHook(selectFlowProps);
var useRepeatProps = createPointerSelHook(selectRepeatProps);
var useFlowStepProps = createPointerSelHook(selectFlowStepProps);
var useAiQueryStepProps = createPointerSelHook(selectAiQueryStepProps);
var useAiChatStepProps = createPointerSelHook(selectAiChatStepProps);
var useFetchDataStepProps = createPointerSelHook(selectFetchDataStepProps);
var useImageGenerationStepProps = createPointerSelHook(selectImageGenerationStepProps);
var useAudioGenerationStepProps = createPointerSelHook(selectAudioGenerationStepProps);
var useTranscribeStepProps = createPointerSelHook(selectTranscribeStepProps);
var usePublishDataStepProps = createPointerSelHook(selectPublishDataStepProps);
var useScriptStepProps = createPointerSelHook(selectScriptStepProps);
var useCallableButtonProps = createPointerSelHook(selectCallableButtonProps);
var useTextInputProps = createPointerSelHook(selectTextInputProps);
var useNumberInputProps = createPointerSelHook(selectNumberInputProps);
var useBooleanInputProps = createPointerSelHook(selectBooleanInputProps);
var useTemporalInputProps = createPointerSelHook(selectTemporalInputProps);
var useFileInputProps = createPointerSelHook(selectFileInputProps);
var useContentProps = createPointerSelHook(selectContentProps);
var useVariableProps = createPointerSelHook(selectVariableProps);
var useFunctionProps = createPointerSelHook(selectFunctionProps);
var useConstProps = createPointerSelHook(selectConstProps);
var useMediaRoomProps = createPointerSelHook(selectMediaRoomProps);

// src/orgicore/ui/components/utility.tsx
import { Fragment, useContext as useContext3 } from "react";
import { jsx } from "react/jsx-runtime";
var ForEachChild = ({
  pointerKey,
  children: children2
}) => {
  const childPointers = useChildPointers({ pointerKey });
  return /* @__PURE__ */ jsx(ForEach, { pointers: childPointers, children: children2 });
};
var ForEachChildTreeNode = ({ children: children2 }) => {
  const childPointers = useChildTreeNodes();
  return /* @__PURE__ */ jsx(ForEach, { pointers: childPointers, children: children2 });
};
var ForEach = ({
  pointers,
  children: children2
}) => {
  return /* @__PURE__ */ jsx(Fragment, { children: pointers.map(({ key }, index) => /* @__PURE__ */ jsx(
    PointerWrapper,
    {
      pointerKey: key,
      length: pointers.length,
      index,
      children: children2
    },
    key
  )) });
};
var CursorView = ({ children: children2 }) => {
  const { key } = useCurrentNavRoute();
  return /* @__PURE__ */ jsx(PointerWrapper, { pointerKey: key, children: children2 });
};
var StateTreeView = ({ children: children2 }) => {
  return /* @__PURE__ */ jsx(PointerWrapper, { pointerKey: "", children: children2 });
};
var PointerWrapper = ({
  children: children2,
  ...rest
}) => {
  const { name } = useAppContext();
  const parent = useContext3(PointerContext);
  return /* @__PURE__ */ jsx(PointerProvider, { value: { ...rest, namespace: name, parent }, children: children2 });
};

// src/orgicore/ui/components/AppStateProvider.tsx
import { Fragment as Fragment2, jsx as jsx2, jsxs } from "react/jsx-runtime";
var createAppWrapper = () => {
  return ({ children: children2, footer, ...rest }) => {
    const ctx = useCreateAppContext(rest);
    return /* @__PURE__ */ jsx2(AppProvider, { value: ctx, children: ctx.isInitialized && /* @__PURE__ */ jsxs(Fragment2, { children: [
      /* @__PURE__ */ jsx2(StateTreeView, { children: children2 }),
      footer
    ] }) });
  };
};

// src/orgicore/ui/components/FormWrapper.tsx
import { useCallback as useCallback2, useState as useState2 } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var [FormContextProvider, useFormContext] = createContextTools("form");
var useCreateFormContext = () => {
  const [blockedByErrors, setBlockedByErrors] = useState2(false);
  const firstError = useFirstError();
  const navNext = useNavNext();
  const next = useCallback2(() => {
    if (firstError) {
      setBlockedByErrors(true);
    } else {
      navNext();
    }
  }, [firstError, navNext]);
  return {
    next,
    blockedByErrors,
    firstError
  };
};
var FormWrapper = ({ children: children2 }) => {
  const ctx = useCreateFormContext();
  return /* @__PURE__ */ jsx3(FormContextProvider, { value: ctx, children: children2 });
};

// src/orgicore/integration/metamodel/pluginSelectors.ts
import * as R17 from "remeda";
import { createSelector as createSelector26 } from "reselect";
var plugins = (plugins3) => plugins3;
var selectPluginsByName = createSelector26(plugins, (plugins3) => R17.indexBy(plugins3, (p) => p.name));
var selectPluginNodeTypes = createSelector26(plugins, (plugins3) => {
  return plugins3.reduce((acc, plugin2) => {
    acc[plugin2.name] = {
      allowedChildTypes: [],
      icon: plugin2.icon || "extension",
      color: plugin2.color || "#9E9E9E",
      category: "plugins"
    };
    return acc;
  }, {});
});
var selectPluginNames = createSelector26(
  plugins,
  (plugins3) => plugins3.map((p) => p.name)
);
var selectPluginByName = createSelector26(
  selectPluginsByName,
  stringProp1,
  (plugins3, name) => plugins3[name]
);

// src/orgicore/integration/transforms/designDef.ts
var inputTypes = [
  TEXT_INPUT,
  NUMBER_INPUT,
  BOOLEAN_INPUT,
  FILE_INPUT,
  TEMPORAL_INPUT,
  SELECT_INPUT,
  //BUTTON,
  VARIABLE,
  FUNCTION,
  CONST
];
var createTreeNodeTypes = (plugins3) => {
  const generalContentTypes = [
    ...inputTypes,
    CONTENT,
    "textContent",
    "subsection",
    CALLABLE_BUTTON,
    //"image",
    ...selectPluginNames(plugins3)
  ];
  const pluginNodeTypes = selectPluginNodeTypes(plugins3);
  const topLevelNodeTypes = ["appNode:folder", SCREEN, FUNCTION, CONST];
  return {
    app: {
      allowedChildTypes: topLevelNodeTypes,
      icon: "dashboard",
      color: "#3468b2"
    },
    screen: {
      allowedChildTypes: [
        "appNode:tabGroup",
        "form",
        "flow",
        TREE,
        ...generalContentTypes
      ],
      icon: "laptop_mac",
      color: "#6c46b9",
      category: "layout"
    },
    form: {
      allowedChildTypes: [...generalContentTypes],
      icon: "edit_square",
      color: "#ba6d36",
      category: "layout"
    },
    "appNode:folder": {
      allowedChildTypes: topLevelNodeTypes,
      icon: "folder",
      color: "#b8853d",
      label: "Folder"
    },
    "appNode:tabGroup": {
      allowedChildTypes: ["appNode:tab"],
      icon: "tab_group",
      color: "#4650b9",
      category: "layout",
      label: "Tab Group"
    },
    "appNode:tab": {
      allowedChildTypes: [...generalContentTypes, TREE],
      icon: "tab",
      color: "#5e66ba",
      label: "Tab"
    },
    content: {
      allowedChildTypes: [],
      icon: "html",
      color: "#a6593f",
      category: "content",
      label: "Html Content"
    },
    textContent: {
      allowedChildTypes: [],
      icon: "newsmode",
      color: "#a14563",
      category: "content"
    },
    subsection: {
      allowedChildTypes: [
        "appNode:tabGroup",
        "form",
        "flow",
        TREE,
        ...generalContentTypes
      ],
      icon: "mobile_layout",
      color: "#3686a1",
      category: "layout",
      label: "Container"
    },
    tree: {
      allowedChildTypes: [...generalContentTypes],
      icon: "account_tree",
      color: "#00695c",
      category: "layout"
    },
    flow: {
      allowedChildTypes: [
        "flowStep:aiQuery",
        "flowStep:aiChat",
        "flowStep:fetchData",
        "flowStep:imageGeneration",
        "flowStep:audioGeneration",
        "flowStep:transcribe",
        "flowStep:publishData",
        "flowStep:script",
        "repeat",
        "appNode:tabGroup",
        "form",
        ...generalContentTypes
      ],
      icon: "route",
      color: "#2e7d32",
      category: "layout",
      label: "Flow"
    },
    // A flow child asking its screens once per item (#2383): screens only.
    repeat: {
      allowedChildTypes: [SCREEN],
      icon: "repeat",
      color: "#558b2f",
      category: "layout",
      label: "Repeat"
    },
    // The running slot: content the web draws in place of the spinner while
    // the step runs, reading `step.partial` / `step.ready`.
    "flowStep:aiQuery": {
      allowedChildTypes: [...generalContentTypes],
      icon: "smart_toy",
      color: "#1565c0",
      label: "AI Query Step"
    },
    "flowStep:aiChat": {
      allowedChildTypes: [],
      icon: "forum",
      color: "#0277bd",
      label: "AI Chat Step"
    },
    "flowStep:fetchData": {
      allowedChildTypes: [],
      icon: "cloud_download",
      color: "#00838f",
      label: "Fetch Data Step"
    },
    "flowStep:imageGeneration": {
      allowedChildTypes: [],
      icon: "image",
      color: "#7b1fa2",
      label: "Image Generation Step"
    },
    "flowStep:audioGeneration": {
      allowedChildTypes: [],
      icon: "music_note",
      color: "#b6488d",
      label: "Audio Generation Step"
    },
    "flowStep:transcribe": {
      allowedChildTypes: [],
      icon: "mic",
      color: "#00695c",
      label: "Transcribe Step"
    },
    "flowStep:publishData": {
      allowedChildTypes: [],
      icon: "cloud_upload",
      color: "#4a148c",
      label: "Publish Data Step"
    },
    "flowStep:script": {
      allowedChildTypes: [],
      icon: "code",
      color: "#5d4037",
      label: "Script Step"
    },
    callableButton: {
      allowedChildTypes: [],
      icon: "smart_button",
      color: "#0277bd",
      category: "input",
      label: "Callable Button"
    },
    textInput: {
      allowedChildTypes: [],
      icon: "text_fields",
      color: "#339199",
      category: "input"
    },
    numberInput: {
      allowedChildTypes: [],
      icon: "numbers",
      color: "#389475",
      category: "input"
    },
    booleanInput: {
      allowedChildTypes: [],
      icon: "check_box",
      color: "#ac3973",
      category: "input"
    },
    fileInput: {
      allowedChildTypes: [],
      icon: "attach_file",
      color: "#a18636",
      category: "input"
    },
    temporalInput: {
      allowedChildTypes: [],
      icon: "schedule",
      color: "#8c47ae",
      category: "input"
    },
    selectInput: {
      allowedChildTypes: [],
      icon: "arrow_drop_down_circle",
      color: "#ac4d39",
      category: "input"
    },
    //button: { allowedChildTypes: [], category: "action" },
    variable: {
      allowedChildTypes: [],
      icon: "data_object",
      color: "#5c8599",
      category: "special"
    },
    function: {
      allowedChildTypes: [],
      icon: "function",
      color: "#7b1fa2",
      category: "special"
    },
    const: {
      allowedChildTypes: [],
      icon: "pin",
      color: "#455a64",
      category: "special"
    },
    ...pluginNodeTypes
  };
};
var createDesignDef = (nodeTypes) => {
  return app({
    t: tree({
      rootType: "app",
      nodeTypes,
      iterationLabel: "@(value.label || value.name || 'New ' + (value.variant || value.type)) + (value.isRepeating ? '[...]' : '')",
      nounLabel: "@value.variant || value.type",
      label: "App Config"
    })
  });
};

// src/orgicore/integration/transforms/designToAppDef.ts
import * as t23 from "io-ts";
var LEGACY_AUTH_MODE = {
  anonOnly: "public",
  hybrid: "public",
  userOnly: "private"
};
var DEGRADED_NODE_ATTR = "data-degraded-node";
var DEGRADED_NODE_HTML = `<div ${DEGRADED_NODE_ATTR}="true" role="alert" style="margin:8px 0;padding:8px 12px;border:1px solid #e6b800;border-radius:6px;background:#fff8e1;color:#8a6d00;font-size:0.875rem;">\u26A0\uFE0F This part of the page couldn't be displayed.</div>`;
var degradedNode = (name) => ({
  type: CONTENT,
  name,
  htmlTemplate: DEGRADED_NODE_HTML
});
var designNode = t23.intersection([
  t23.partial({
    value: treeNodeValue
  }),
  unknownRecord
]);
var selectDesignNode = createTypedOutputSelector(designNode);
var designToAppDef = (design, plugins3, opts = {}) => {
  const designNodeToAppDef = (designNode3) => {
    var _a2;
    const { t: t30 = [], value: value2 } = designNode3;
    const children2 = t30.length > 0 ? t30.map(designNodeToAppDef) : void 0;
    let options;
    try {
      options = valueToOptions(value2);
    } catch (error2) {
      if (!opts.lenient) {
        throw error2;
      }
      return degradedNode((_a2 = value2.name) != null ? _a2 : "unnamed");
    }
    const nodeDef2 = { ...options, children: children2 };
    if (nodeDef2.submitObjectType) {
      if (!nodeDef2.children) {
        nodeDef2.children = [];
      }
      nodeDef2.children = [...nodeDef2.children, ...createImplicitSubmitNodes()];
    }
    return nodeDef2;
  };
  const valueToOptions = (value2) => {
    const transformed = transformValue(value2);
    const decoded = nodeOptionsCodec.decode(transformed);
    if (isRight3(decoded)) {
      return decoded.right;
    }
    const errorSummary = summarizeErrors(decoded.left);
    const errorPath = getErrorPath(decoded.left);
    const hasColumnBreaksError = decoded.left.some(
      (e) => e.context.some((ctx) => ctx.key === "columnBreaks")
    );
    let suggestion = 'Check that your node has a valid "type" property.';
    if (hasColumnBreaksError) {
      suggestion = "columnBreaks format mismatch. System expects number[] (e.g., [2, 5, 7]) but you provided responsive objects. Convert to legacy format or update types.";
    }
    throw new Error(
      `\u{1F6A8} Design Node Error - ${errorSummary}
\u{1F4CD} Location: ${errorPath || "root"}
\u{1F4A1} Fix: ${suggestion}
\u{1F4CB} Node: ${JSON.stringify(transformed, null, 2).substring(0, 500)}`
    );
  };
  const transformValue = (value2) => {
    const v2 = transformNode(value2);
    const { name = "unnamed", id, ...rest2 } = v2;
    return { name, ...rest2 };
  };
  const transformNode = (value2) => {
    const { type: type14, ...rest2 } = value2;
    const plugin2 = selectPluginByName(plugins3, type14);
    if (plugin2) {
      const { parameters = {}, description, icon, image, ...rest22 } = rest2;
      if (stringRecord.is(parameters)) {
        return {
          type: CONTENT,
          htmlTemplate: `@${type14}(${plugin2.parameters.map((p) => parameters[p.name || ""] || "undefined").join(",")})`,
          pluginName: type14,
          ...rest22
        };
      }
    }
    const runtime = toRuntimeType(type14);
    if (runtime.variant !== void 0) {
      return {
        type: runtime.type,
        variant: runtime.variant,
        ...rest2
      };
    }
    if (type14 === "image" || type14 === "textContent") {
      return {
        type: CONTENT,
        ...rest2
      };
    }
    if (!nodeType.is(type14)) {
      throw new Error(`Invalid node type: ${type14}`);
    }
    if (type14 === REPEAT) {
      return { ...value2, isRepeating: true };
    }
    if (type14 === APP) {
      const { appLogo: _ignored, ...rest22 } = value2;
      void _ignored;
      let v = { type: type14, ...rest22 };
      const am = v.authMode;
      if (am && LEGACY_AUTH_MODE[am]) {
        v = { ...v, authMode: LEGACY_AUTH_MODE[am] };
      }
      return v;
    }
    return value2;
  };
  const s = {
    def: createDesignDef(createTreeNodeTypes(plugins3)),
    instance: design
  };
  const r = selectDesignNode(s, "t");
  if (isLeft(r)) {
    return left(`Invalid design node: ${r.left}`);
  }
  const incoming = r.right;
  const { value = { type: "app", name: "" }, ...rest } = incoming;
  const designNode2 = { value: { ...value, name: "" }, ...rest };
  try {
    return right(designNodeToAppDef(designNode2));
  } catch (error2) {
    return left(getErrorMessage(error2));
  }
};

// src/orgicore/integration/transforms/rawOrgDataToTree.ts
var isInformativeValue = (value) => value !== void 0 && !(typeof value === "object" && value !== null && !Array.isArray(value) && Object.keys(value).length === 0);
var appInstanceToTree = (instance) => {
  var _a2;
  if (!instance || !instance.data || instance.data.length === 0) {
    return void 0;
  }
  const rootValue = (_a2 = instance.data.find((d) => d.pointer === "value")) == null ? void 0 : _a2.value;
  const hasRootValue = isInformativeValue(rootValue);
  const rootNodeDef = app({
    t: tree({
      rootType: "app",
      nodeTypes: {}
    })
  });
  const state = {
    def: rootNodeDef,
    instance
  };
  if (hasRootValue) {
    const pointer = selectPointer(state, "t");
    return buildTreeStructure(state, pointer);
  } else {
    const pointer = selectPointer(state, "t");
    const childPointers = selectChildTreeNodes(state, pointer.key);
    if (childPointers.length === 0) {
      return { type: "folder", name: "" };
    }
    const children2 = [];
    for (const childPointer of childPointers) {
      const childTree = buildTreeStructure(state, childPointer);
      if (childTree) {
        children2.push(childTree);
      }
    }
    return {
      type: "folder",
      _children: children2
    };
  }
};
var rawOrgDataToTree = (data) => {
  return {
    versionId: data.versionId,
    apiVersion: data.apiVersion,
    minClientVersion: data.minClientVersion,
    appConfig: appInstanceToTree(data.appConfig),
    users: appInstanceToTree(data.users),
    objectTypes: appInstanceToTree(data.objectTypes),
    objectInstances: appInstanceToTree(data.objectInstances),
    plugins: appInstanceToTree(data.plugins)
  };
};
var buildAppInstanceData = (tree2, baseKey, data, lengths) => {
  const { _children, ...valueWithoutChildren } = tree2;
  data.push({
    pointer: `${baseKey}.value`,
    value: valueWithoutChildren
  });
  if (_children && _children.length > 0) {
    data.push({
      pointer: `${baseKey}.expanded`,
      value: true
    });
    const childrenKey = `${baseKey}.t`;
    lengths.push({
      pointer: childrenKey,
      length: _children.length
    });
    _children.forEach((child, index) => {
      const childKey = `${childrenKey}[${index}]`;
      buildAppInstanceData(child, childKey, data, lengths);
    });
  }
};
var treeToAppInstance = (tree2, cursor, navStack) => {
  if (!tree2) {
    return {};
  }
  const data = [];
  const lengths = [];
  const { _children, ...valueWithoutChildren } = tree2;
  const isSyntheticRoot = valueWithoutChildren.type === "folder" && Object.keys(valueWithoutChildren).length === 1;
  if (!isSyntheticRoot) {
    data.push({
      pointer: "value",
      value: valueWithoutChildren
    });
  }
  if (_children && _children.length > 0) {
    data.push({
      pointer: "expanded",
      value: true
    });
    lengths.push({
      pointer: "t",
      length: _children.length
    });
    _children.forEach((child, index) => {
      buildAppInstanceData(child, `t[${index}]`, data, lengths);
    });
  }
  const instance = {
    data,
    lengths
  };
  if (cursor) {
    instance.cursor = cursor;
  }
  if (navStack) {
    instance.navStack = navStack;
  }
  return instance;
};
var treeToRawOrgData = (tree2) => {
  return {
    versionId: tree2.versionId,
    apiVersion: tree2.apiVersion,
    minClientVersion: tree2.minClientVersion,
    appConfig: treeToAppInstance(
      tree2.appConfig,
      tree2.appConfig ? "t[0]" : void 0
    ),
    users: treeToAppInstance(tree2.users, tree2.users ? "t[0]" : void 0),
    objectTypes: treeToAppInstance(
      tree2.objectTypes,
      tree2.objectTypes ? "t[0]" : void 0
    ),
    objectInstances: treeToAppInstance(
      tree2.objectInstances,
      tree2.objectInstances ? "t[0].t[0]" : void 0
    ),
    plugins: treeToAppInstance(tree2.plugins, tree2.plugins ? "t[0]" : void 0)
  };
};

// src/orgicore/integration/plugins/widgetDescriptor.ts
import * as t24 from "io-ts";
var WIDGET_DESCRIPTOR_MARKER = "\0ORGIGGLY_SANDBOXED_WIDGET\0";
var widgetDescriptor = t24.intersection([
  t24.type({
    kind: t24.literal("sandboxed-widget"),
    pluginName: t24.string,
    params: t24.UnknownRecord
  }),
  t24.partial({
    /**
     * The plugin's `embedsThirdPartyMedia` capability, carried through so `WidgetHost` knows
     * which sandbox to build. The plugin's identity is erased before render, so the descriptor
     * is the only place this can travel.
     */
    embedsThirdPartyMedia: t24.boolean,
    /** The plugin's DECLARED `connections` — `WidgetHost` intersects them with the allowlist. */
    connections: t24.array(connectionProvider),
    /**
     * The plugin's `widgetOwner` uid (#2536): `WidgetHost` loads that user's widget folder and
     * clamps the frame and connections to first-party-free defaults.
     */
    widgetOwner: t24.string
  })
]);
var makeWidgetDescriptor = (pluginName, params, { embedsThirdPartyMedia, connections, widgetOwner } = {}) => {
  const descriptor = {
    kind: "sandboxed-widget",
    pluginName,
    params,
    // Omitted rather than `false` / `[]` so an ordinary widget's descriptor is byte-identical
    // to what it was before the capabilities existed.
    ...embedsThirdPartyMedia ? { embedsThirdPartyMedia: true } : {},
    ...(connections == null ? void 0 : connections.length) ? { connections } : {},
    ...widgetOwner !== void 0 ? { widgetOwner } : {}
  };
  return WIDGET_DESCRIPTOR_MARKER + JSON.stringify(descriptor);
};
var tryParseWidgetDescriptor = (value) => {
  if (!value.startsWith(WIDGET_DESCRIPTOR_MARKER)) {
    return null;
  }
  const payload = value.slice(WIDGET_DESCRIPTOR_MARKER.length);
  let parsed;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null;
  }
  const decoded = widgetDescriptor.decode(parsed);
  return isRight3(decoded) ? decoded.right : null;
};

// src/orgicore/integration/plugins/createPluginFn.ts
var createHtmlPluginFn = ({
  name,
  htmlTemplate,
  parameters,
  runtime,
  embedsThirdPartyMedia,
  connections,
  widgetOwner
}) => {
  return (...params) => {
    const context = parameters.reduce(
      (acc, param, index) => {
        acc[param.name || `param${index}`] = params[index];
        return acc;
      },
      {}
    );
    if (runtime === "sandboxed") {
      return makeWidgetDescriptor(name, context, {
        embedsThirdPartyMedia,
        connections,
        widgetOwner
      });
    }
    return String(evaluateAndThrow(asTemplateFormula(htmlTemplate), context));
  };
};
var createHtmlPluginFns = (plugins3) => {
  return plugins3.reduce((acc, plugin2) => {
    acc[plugin2.name] = createHtmlPluginFn(plugin2);
    return acc;
  }, {});
};

// src/orgicore/integration/plugins/createPluginParamForm.ts
var createPluginParamForm = (type14, createInput) => {
  const paramInputs = type14.parameters.reduce((acc, param) => {
    if (param.name) {
      acc[param.name] = createInput({
        help: param.description
      });
    }
    return acc;
  }, {});
  return {
    name: textInput({ compute: "@id" }),
    parameters: subsection(paramInputs, {
      isOwnScope: true,
      showHeader: true
    })
  };
};

// src/orgicore/integration/plugins/widgetConnections.ts
var WIDGET_CONNECTION_ALLOWLIST = {
  spotify: ["spotifyplayer"]
};
var grantedConnections = (pluginName, declared, owned) => owned ? [] : (declared != null ? declared : []).filter(
  (provider) => WIDGET_CONNECTION_ALLOWLIST[provider].includes(pluginName)
);

// src/orgicore/integration/createEditor.ts
var getTextVariant = (field2) => {
  const { type: type14, variant, multiline } = field2;
  if (type14 === "text" && !variant && multiline) {
    return "multiline";
  }
  return variant;
};
var BUILTIN_FIELD_BUILDERS = {
  variable: ({ field: field2, options }) => [field2.name, variable2(options)],
  text: ({ field: field2, options }) => [
    field2.name,
    textInput({ ...options, variant: getTextVariant(field2) })
  ],
  number: ({ field: field2, options }) => [field2.name, numberInput(options)],
  boolean: ({ field: field2, options }) => [field2.name, booleanInput(options)],
  temporal: ({ field: field2, options }) => [field2.name, temporalInput(options)],
  // The site passes the parent id through untouched; the admin lets it be typed.
  parentRef: ({ field: field2, options, useCase }) => [
    field2.name,
    useCase === "site" ? variable2({ ...options }) : textInput({ ...options })
  ],
  image: ({ field: field2, options }) => {
    const { name, description, searchFormula } = field2;
    const pluginParams = searchFormula ? [{ type: "dynamic", name: "searchTerm", formula: searchFormula }] : [];
    return [
      name,
      pluginLeaf({
        pluginName: "ImageInput",
        pluginParams,
        ...options,
        help: description || (searchFormula ? `@!${name} ? 'Paste an image URL or use Search to find one' : ''` : `@!${name} ? 'Enter the URL of an image. Preview will be shown below' : ''`)
      })
    ];
  },
  imageUpload: ({ field: field2, options }) => {
    const { name, fileNameFormula = `'${name}_' + generateId(4)`, aiEnabled, aiSystemPromptFormula } = field2;
    const compute = `@{name: ${fileNameFormula}}`;
    return [
      name,
      fileInput({
        variant: "image",
        ...options,
        compute,
        aiEnabled,
        ...aiSystemPromptFormula ? { aiSystemPromptFormula: `@${aiSystemPromptFormula}` } : {}
      })
    ];
  }
};
var readOnlyPair = (name, options, caption, body) => [
  [name, variable2(options)],
  [
    `${name}Preview`,
    content({
      htmlTemplate: `@\`<div><div style="font-size:0.8em;opacity:0.7">${caption}</div>${body}</div>\``
    })
  ]
];
var createFieldDef = (field2, useCase, lockedFields) => {
  const { name, type: type14, compute, isTransient, description } = field2;
  const options = { compute, isTransient, label: name, help: description };
  const parsed = parseFieldType(type14, field2.isList);
  if (parsed.kind === "list" || parsed.kind === "map") {
    return readOnlyPair(
      name,
      options,
      `${name} (${parsed.kind}, read-only)`,
      `<pre style="margin:0;white-space:pre-wrap">\${toJson(${name}, 2)}</pre>`
    );
  }
  const hiddenAnyway = type14 === "parentRef" && useCase === "site";
  if (lockedFields.has(name) && !hiddenAnyway) {
    const body = type14 === "imageUpload" ? `<pre style="margin:0;white-space:pre-wrap">\${toJson(${name}, 2)}</pre>` : `<div style="white-space:pre-wrap">\${escapeHtml(${name} ?? '')}</div>`;
    return readOnlyPair(name, options, `${name} (read-only)`, body);
  }
  if (isFieldTypeBuiltin(type14)) {
    return [BUILTIN_FIELD_BUILDERS[type14]({ field: field2, options, useCase })];
  }
  return [[name, selectInput({ pickerValues: `@${type14}` })]];
};
var NO_LOCKED_FIELDS = /* @__PURE__ */ new Set();
var createEditor = (objectType3, useCase, imageInput, opts = {}) => {
  var _a2;
  void imageInput;
  const lockedFields = (_a2 = opts.lockedFields) != null ? _a2 : NO_LOCKED_FIELDS;
  const fieldDefs = objectType3.fields.filter((f) => completeField.is(f));
  const fields = fieldDefs.flatMap(
    (f) => createFieldDef(f, useCase, lockedFields)
  );
  const passThrough = (names2) => [...names2].map((name) => [name, variable2()]);
  return screen(
    Object.fromEntries([
      ...passThrough(RECORD_META_IDENTITY),
      // Admin instance edits persist as tree nodes, which must satisfy the
      // `treeNodeValue` codec (requires `type`). The site form intentionally
      // omits `type` so it doesn't leak into published data — the `typeName`
      // publish parameter is authoritative there. See issue #368.
      ...useCase === "admin" ? passThrough(["type"]) : [],
      ...passThrough(RECORD_META_AUDIT),
      ...passThrough(RECORD_META_PLACEMENT),
      ...fields,
      [
        "isDeleted",
        booleanInput({
          label: "Delete?",
          help: "If checked, this object will be deleted when you publish",
          hidden: useCase === "site" ? "@!id" : true
        })
      ]
    ])
  );
};

// src/orgicore/integration/reorderWrites.ts
var clamp = (n, max) => Math.max(0, Math.min(max, Math.trunc(n)));
var reorderWrites = (records, fromIdx, toIdx, orderField) => {
  const n = records.length;
  if (n === 0) return [];
  const from = clamp(fromIdx, n - 1);
  const to = clamp(toIdx, n - 1);
  if (from === to) return [];
  const next = [...records];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  const writes = [];
  next.forEach((record10, position) => {
    if (record10[orderField] === position) return;
    writes.push({ id: record10.id, patch: { [orderField]: position } });
  });
  return writes;
};
var DEFAULT_ORDER_FIELD = "sortIndex";

// src/orgicore/integration/metamodel/types.ts
import * as t25 from "io-ts";
var configNode = t25.intersection([
  t25.type({
    name: t25.string
  }),
  treeNodeValue
]);
var objInstance = t25.intersection([treeNodeValue, unknownRecord]);
var basicType = t25.union([
  t25.literal("text"),
  t25.literal("number"),
  t25.literal("boolean"),
  t25.literal("temporal")
]);
var basicTypes = basicType.types.map((codec) => codec.value);
var orgDataKeys = [
  "appConfig",
  "users",
  "objectTypes",
  "objectInstances",
  "plugins"
];
var dataValue = t25.type({ pointer: t25.string, value: t25.unknown });
var lengthValue = t25.type({ pointer: t25.string, length: t25.number });
var appInstance = t25.partial({
  cursor: t25.string,
  navStack: t25.array(t25.string),
  data: t25.array(dataValue),
  lengths: t25.array(lengthValue)
});
var orgData = t25.intersection([
  t25.type({
    // empty versionId means it's a draft
    versionId: t25.string,
    appConfig: appInstance,
    users: appInstance,
    objectTypes: appInstance,
    objectInstances: appInstance,
    plugins: appInstance
  }),
  t25.partial({
    // apiVersion tracks the schema version of the data
    // If missing or empty, assume "0.0"
    apiVersion: t25.string,
    // minClientVersion is the minimum CLIENT CAPABILITY VERSION required to
    // render this config safely — the deliberate lever for a genuinely breaking
    // config change (#1082). A client below it self-updates or shows an
    // "update required" screen instead of attempting a render it can't finish.
    // Absent means "no minimum" and never gates. Deliberately NOT apiVersion,
    // which means data-schema version and is overwritten on every publish.
    minClientVersion: t25.number
  })
]);
var userNode = t25.intersection([
  t25.type({
    id: t25.string,
    pointer: t25.string,
    type: t25.union([t25.literal("user"), t25.literal("group")])
  }),
  t25.partial({
    name: t25.string,
    email: t25.string,
    isAdmin: t25.boolean,
    /**
     * Orgiggly usernames this person signs in as, comma- or space-separated
     * ("dave, david_webber"). What `$orgUser` matches on. Prefer it to
     * `email`: the published config is world-readable, so an email here can be
     * scraped (and a listed email is also made an org admin on publish).
     */
    usernames: t25.string
  })
]);

// src/orgicore/integration/metamodel/modelSelectors.ts
import * as R20 from "remeda";
import { createSelector as createSelector28 } from "reselect";

// src/orgicore/integration/metamodel/hydration.ts
var isPlainObject3 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var INT_KEY = /^(0|[1-9]\d*)$/;
var densify = (obj) => {
  const keys = Object.keys(obj);
  if (!keys.every((k) => INT_KEY.test(k))) return void 0;
  const max = keys.reduce((m, k) => Math.max(m, Number(k)), -1);
  const out = new Array(max + 1).fill(null);
  for (const k of keys) out[Number(k)] = obj[k];
  return out;
};
var toListShape = (v) => {
  var _a2;
  if (v === void 0 || v === null) return [];
  if (Array.isArray(v)) return v;
  if (isPlainObject3(v)) return (_a2 = densify(v)) != null ? _a2 : v;
  return v;
};
var toMapShape = (v) => {
  if (v === void 0 || v === null) return {};
  if (Array.isArray(v)) {
    const out = {};
    v.forEach((item, i) => {
      if (item !== null && item !== void 0) out[String(i)] = item;
    });
    return out;
  }
  return v;
};
var shapersFor = (objectType3) => {
  const out = [];
  for (const f of objectType3.fields) {
    if (typeof f.name !== "string" || typeof f.type !== "string") continue;
    const parsed = parseFieldType(f.type, f.isList);
    if (parsed.kind === "list") out.push([f.name, toListShape]);
    else if (parsed.kind === "map") out.push([f.name, toMapShape]);
  }
  return out;
};
var normalizeRecordShape = (obj, objectType3) => {
  if (!objectType3 || obj.isDeleted === true) return obj;
  const shapers = shapersFor(objectType3);
  if (shapers.length === 0) return obj;
  const out = { ...obj };
  for (const [name, shape] of shapers) out[name] = shape(out[name]);
  return out;
};
var normalizeRepoObjects = (objects, metaModel2) => {
  if (!metaModel2 || metaModel2.types.length === 0) return objects;
  const byName = new Map(metaModel2.types.map((t30) => [t30.name, t30]));
  return objects.map((o) => normalizeRecordShape(o, byName.get(o.type)));
};

// src/orgicore/integration/metamodel/helpers.ts
import * as R19 from "remeda";

// src/orgicore/integration/metamodel/typeSelectors.ts
import * as R18 from "remeda";
import { createSelector as createSelector27 } from "reselect";
var type10 = (type14) => type14;
var selectFields = createSelector27(
  type10,
  (type14) => type14.fields.filter((f) => completeField.is(f))
);
var selectFieldsByName = createSelector27(
  selectFields,
  (fields) => R18.indexBy(fields, (f) => f.name)
);
var selectFieldByName = createSelector27(
  selectFieldsByName,
  stringProp1,
  (fieldsByName, name) => fieldsByName[name]
);

// src/orgicore/integration/metamodel/helpers.ts
var generateNameFormula = (types2) => {
  const bestFields = types2.map(getBestNameField).filter(R19.isDefined);
  const uniqueFields = R19.unique(["name", ...bestFields]);
  return uniqueFields.map((f) => `value.${f}`).join(" || ");
};
var getBestNameField = (type14) => {
  const byName = selectFieldsByName(type14);
  const nameField = byName.name || byName.Name;
  if (nameField) {
    return nameField.name;
  }
  const firstField = selectFields(type14)[0];
  if (firstField && (firstField.type === "text" || firstField.type === "number")) {
    return firstField.name;
  }
  return "id";
};
var storageObjToRepoObj = (type14, obj, _scope, objectType3, _groupKey) => normalizeRecordShape(
  { ...obj, type: type14, _scope, ..._groupKey !== void 0 ? { _groupKey } : {} },
  objectType3
);
var REPO_READ_META = ["type", "_scope", "_groupKey"];
var REPO_READ_META_SET = new Set(REPO_READ_META);
var stripRepoMeta = (record10) => {
  const rest = {};
  for (const key of Object.keys(record10)) {
    if (!REPO_READ_META_SET.has(key)) rest[key] = record10[key];
  }
  return rest;
};
var isRepoObject = (obj) => {
  return R19.isDefined(obj.type) && R19.isDefined(obj.id);
};

// src/orgicore/integration/metamodel/modelSelectors.ts
var metaModel = (metaModel2) => metaModel2;
var types = createSelector28(
  metaModel,
  (metaModel2) => metaModel2.types
);
var selectNameFormula = createSelector28(
  types,
  generateNameFormula
);
var selectTypesByName = createSelector28(
  types,
  (types2) => R20.indexBy(types2, (t30) => t30.name)
);
var selectTypeByName = createSelector28(selectTypesByName, stringProp1, (types2, name) => types2[name]);
var selectBestNameField = createSelector28(selectTypeByName, (type14) => type14 && getBestNameField(type14));

// src/orgicore/integration/metamodel/recordTree.ts
import * as R21 from "remeda";
var discoverParentRefField = (objectType3) => {
  if (!objectType3) return void 0;
  const refField = objectType3.fields.find(
    (f) => f.type === "parentRef" && typeof f.name === "string"
  );
  return refField == null ? void 0 : refField.name;
};
var parentLinkOf = (objectType3) => {
  const name = discoverParentRefField(objectType3);
  return name ? { kind: "field", name } : { kind: "implicit" };
};
var isRecordLike = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var readParentId = (link, record10) => {
  const implicit = record10._parent;
  const raw = link.kind === "field" ? record10[link.name] : isRecordLike(implicit) ? implicit.id : void 0;
  return typeof raw === "string" && raw.length > 0 ? raw : void 0;
};
var parentLinkPatch = (objectType3, parent) => {
  const link = parentLinkOf(objectType3);
  return link.kind === "field" ? { [link.name]: parent.id } : { _parent: { id: parent.id, type: parent.type } };
};
var compareNullsLast = (a, b2, direction) => {
  const aMissing = a === void 0 || a === null;
  const bMissing = b2 === void 0 || b2 === null;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return direction * compareValues(a, b2);
};
var buildRecordForest = (objectsByType, opts) => {
  var _a2, _b;
  const { objectTypes, nodeTypes, rootType, whereClause, sort, evaluator } = opts;
  const nodeTypeKeys = Object.keys(nodeTypes);
  const declarationIndex = new Map(nodeTypeKeys.map((k, i) => [k, i]));
  const objectTypesByName = R21.indexBy(objectTypes, (t30) => t30.name);
  const parentLinkByType = new Map(
    nodeTypeKeys.map((k) => [k, parentLinkOf(objectTypesByName[k])])
  );
  const allowedChildTypes = (parentType) => {
    var _a3, _b2;
    return (_b2 = (_a3 = nodeTypes[parentType]) == null ? void 0 : _a3.allowedChildTypes) != null ? _b2 : [];
  };
  const candidates = nodeTypeKeys.flatMap(
    (k) => {
      var _a3;
      return ((_a3 = objectsByType[k]) != null ? _a3 : []).filter((r) => r.isDeleted !== true);
    }
  );
  const parentIdOf = (record10) => {
    const link = parentLinkByType.get(record10.type);
    return link ? readParentId(link, record10) : void 0;
  };
  const childCandidatesByParent = /* @__PURE__ */ new Map();
  for (const record10 of candidates) {
    const pid = parentIdOf(record10);
    if (pid === void 0) continue;
    const list3 = (_a2 = childCandidatesByParent.get(pid)) != null ? _a2 : [];
    list3.push(record10);
    childCandidatesByParent.set(pid, list3);
  }
  const sortRules = (Array.isArray(sort) ? sort : sort ? [sort] : []).filter(
    (s) => typeof s.field === "string" && s.field.length > 0
  );
  const orderSiblings = (records) => [...records].sort((a, b2) => {
    var _a3, _b2;
    const ai = (_a3 = declarationIndex.get(a.type)) != null ? _a3 : Number.MAX_SAFE_INTEGER;
    const bi = (_b2 = declarationIndex.get(b2.type)) != null ? _b2 : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    if (sortRules.length > 0) {
      for (const rule of sortRules) {
        const cmp = compareNullsLast(
          a[rule.field],
          b2[rule.field],
          rule.direction === "desc" ? -1 : 1
        );
        if (cmp !== 0) return cmp;
      }
      return 0;
    }
    return compareNullsLast(
      a.createdAt,
      b2.createdAt,
      1
    );
  });
  const walk8 = (rootRecords) => {
    var _a3;
    const visited = /* @__PURE__ */ new Set();
    const childrenOf6 = {};
    const parentOf = {};
    const orderedRoots = orderSiblings(rootRecords);
    const queue = [];
    for (const root of orderedRoots) {
      if (visited.has(root.id)) continue;
      visited.add(root.id);
      queue.push(root);
    }
    while (queue.length > 0) {
      const parent = queue.shift();
      const legalChildren = ((_a3 = childCandidatesByParent.get(parent.id)) != null ? _a3 : []).filter(
        (child) => allowedChildTypes(parent.type).includes(child.type) && !visited.has(child.id)
      );
      const orderedChildren = orderSiblings(legalChildren);
      if (orderedChildren.length > 0) childrenOf6[parent.id] = orderedChildren;
      for (const child of orderedChildren) {
        visited.add(child.id);
        parentOf[child.id] = parent.id;
        queue.push(child);
      }
    }
    return { visited, childrenOf: childrenOf6, parentOf, orderedRoots };
  };
  const rootCandidates = ((_b = objectsByType[rootType]) != null ? _b : []).filter(
    (r) => r.isDeleted !== true && parentIdOf(r) === void 0
  );
  const fullReach = walk8(rootCandidates).visited;
  const orphanCount = candidates.filter(
    (r) => parentIdOf(r) !== void 0 && !fullReach.has(r.id)
  ).length;
  if (orphanCount > 0) {
    console.warn(
      `buildRecordForest: excluded ${orphanCount} orphaned record(s) with missing, deleted, or illegally-placed parents.`
    );
  }
  const survivingRoots = whereClause && evaluator ? rootCandidates.filter((r) => {
    const result = evaluator(whereClause, r);
    return isRight(result) && !!result.right;
  }) : rootCandidates;
  const view = walk8(survivingRoots);
  const byId = {};
  for (const record10 of candidates) {
    if (view.visited.has(record10.id)) byId[record10.id] = record10;
  }
  return {
    roots: view.orderedRoots,
    childrenOf: view.childrenOf,
    byId,
    parentOf: view.parentOf,
    orphanCount
  };
};

// src/orgicore/integration/metamodel/appConfigSelectors.ts
import * as R22 from "remeda";
import { createSelector as createSelector29 } from "reselect";
var appConfig = (appConfig3) => appConfig3;
var nodes = createSelector29(
  appConfig,
  (appConfig3) => appConfig3.nodes
);
var plugins2 = createSelector29(
  appConfig,
  (appConfig3) => appConfig3.plugins
);
var selectUniqueNodeNames = createSelector29(
  nodes,
  plugins2,
  (nodes2, plugins3) => R22.unique([...nodes2.map((n) => n.name), ...plugins3.map((p) => p.name)])
);

// src/orgicore/integration/metamodel/createDb.ts
var createDbForObjEditing = (metaModel2, objs) => {
  const objects = objs.filter(isRepoObject).flatMap((node2) => {
    const { type: type14, id } = node2;
    const nameField = selectBestNameField(metaModel2, type14);
    if (nameField) {
      const label2 = node2[nameField] || id;
      return { ...node2, label: label2, id };
    }
    return [];
  });
  return createDb(metaModel2, objects);
};
var createDb = (metaModel2, objects) => {
  return { metaModel: metaModel2, repo: { objects } };
};

// src/orgicore/integration/metamodel/userSelectors.ts
import * as R24 from "remeda";
import { createSelector as createSelector31 } from "reselect";

// src/orgicore/integration/metamodel/orgData.ts
import * as R23 from "remeda";
import { createSelector as createSelector30 } from "reselect";
var appInstance2 = (state) => state;
var createNodeSelector = (codec) => {
  return createSelector30(appInstance2, (appInstance4) => {
    const { data = [] } = appInstance4 != null ? appInstance4 : {};
    return data.map((d) => {
      return R23.isObjectType(d.value) ? { ...d.value, pointer: d.pointer } : d.value;
    }).filter(codec.is);
  });
};
var selectObjectTypes = createNodeSelector(objectType);
var selectObjectInstances = createNodeSelector(objInstance);
var selectPlugins = createNodeSelector(htmlPlugin);
var selectAppConfigNodes = createNodeSelector(configNode);
var selectUserNodes = createNodeSelector(userNode);
var rawOrgData = (state) => state;
var buildOrgData = (state, { lenient } = {}) => {
  const versionId = state.versionId;
  const apiVersion = state.apiVersion;
  const plugins3 = selectPlugins(state.plugins);
  const objectInstances = selectObjectInstances(state.objectInstances).filter(
    isRepoObject
  );
  const r = designToAppDef(state.appConfig, plugins3, { lenient });
  if (isLeft(r)) {
    return r;
  }
  return right({
    appConfig: r.right,
    users: state.users,
    objectTypes: selectObjectTypes(state.objectTypes),
    objectInstances,
    plugins: plugins3,
    versionId,
    apiVersion
  });
};
var selectOrgData = createSelector30(rawOrgData, (state) => buildOrgData(state));
var selectOrgDataLenient = createSelector30(rawOrgData, (state) => buildOrgData(state, { lenient: true }));
var orgData2 = (state) => state;
var selectMetaModel = createSelector30(
  orgData2,
  (s) => ({ types: s.objectTypes })
);
var selectDb = createSelector30(
  orgData2,
  selectMetaModel,
  (s, metaModel2) => createDb(metaModel2, s.objectInstances)
);

// src/orgicore/integration/metamodel/userSelectors.ts
var appInstance3 = (state) => state;
var getParentKey = (path) => {
  const [stem] = toStemAndLeaf(path);
  return stem;
};
var getNodeKey = (path) => {
  return path.replace(/\.?value$/, "");
};
var selectUserNodesByKey = createSelector31(
  selectUserNodes,
  (nodes2) => R24.indexBy(nodes2, (node2) => getNodeKey(node2.pointer))
);
var isAdmin = (gu) => {
  if (gu.isAdmin) return true;
  if (gu.parent) return isAdmin(gu.parent);
  return false;
};
var selectGroup = createSelector31(
  appInstance3,
  selectUserNodesByKey,
  stringProp1,
  (s, byKey2, pointerKey) => {
    const node2 = byKey2[pointerKey];
    if (!node2) return void 0;
    const { id, name = id, type: type14, isAdmin: isAdmin2 } = node2;
    if (type14 !== "group") return void 0;
    const parent = pointerKey ? selectGroup(s, getParentKey(pointerKey)) : void 0;
    return {
      id,
      name,
      parent,
      isAdmin: isAdmin2
    };
  }
);
var selectUser = createSelector31(
  appInstance3,
  selectUserNodesByKey,
  stringProp1,
  (s, byKey2, pointerKey) => {
    const node2 = byKey2[pointerKey];
    if (!node2) return void 0;
    const { id, name = id, email, type: type14, isAdmin: isAdmin2 } = node2;
    if (type14 !== "user" || !email) return void 0;
    const parent = selectGroup(s, getParentKey(pointerKey));
    return {
      id,
      name,
      email,
      parent,
      isAdmin: isAdmin2
    };
  }
);
var selectNodesByType = createSelector31(
  selectUserNodes,
  (nodes2) => R24.groupBy(nodes2, (node2) => node2.type)
);
var selectUsers = createSelector31(
  appInstance3,
  selectNodesByType,
  (s, nodes2) => {
    const userNodes = nodes2.user || [];
    const users = userNodes.flatMap(({ pointer }) => {
      const user = selectUser(s, getNodeKey(pointer));
      return user ? [user] : [];
    });
    return users;
  }
);
var selectGroups = createSelector31(
  appInstance3,
  selectNodesByType,
  (s, nodes2) => {
    const groups = nodes2.group.flatMap(({ pointer }) => {
      const group = selectGroup(s, getNodeKey(pointer));
      return group ? [group] : [];
    });
    return groups;
  }
);
var selectUsersByEmail = createSelector31(
  selectUsers,
  (users) => R24.indexBy(users, (user) => user.email)
);
var selectUsersByName = createSelector31(
  selectUsers,
  (users) => R24.indexBy(users, (user) => user.name)
);
var selectUserByName = createSelector31(
  selectUsersByName,
  stringProp1,
  (usersByName, name) => usersByName[name]
);
var selectUserByEmail = createSelector31(
  selectUsersByEmail,
  stringProp1,
  (usersByEmail, email) => usersByEmail[email]
);
var parseUsernames = (raw) => (raw != null ? raw : "").split(/[\s,]+/).map((u) => u.trim().toLowerCase()).filter((u) => u && u !== "anon");
var selectOrgUserByUsername = createSelector31(selectUserNodes, stringProp1, (nodes2, username) => {
  var _a2;
  const wanted = username.trim().toLowerCase();
  if (!wanted || wanted === "anon") return void 0;
  const node2 = nodes2.find(
    (n) => n.type === "user" && parseUsernames(n.usernames).includes(wanted)
  );
  if (!node2) return void 0;
  return {
    id: node2.id,
    name: (_a2 = node2.name) != null ? _a2 : node2.id,
    usernames: parseUsernames(node2.usernames)
  };
});

// src/orgicore/integration/metamodel/clientVersion.ts
var isClientTooOld = (minClientVersion, clientVersion) => typeof minClientVersion === "number" && Number.isFinite(minClientVersion) && minClientVersion > clientVersion;

// src/orgicore/integration/transforms/outline/emit.ts
var INDENT = "  ";
var isRecord4 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var isEmptyValue = (v) => v === void 0 || v === "" || Array.isArray(v) && v.length === 0 || isRecord4(v) && Object.keys(v).length === 0;
var byKey = (a, b2) => a < b2 ? -1 : a > b2 ? 1 : 0;
var isIdentifier2 = (v) => typeof v === "string" && /^[A-Za-z_][A-Za-z0-9_]*$/.test(v);
var pruneEmpty = (v) => {
  if (!isRecord4(v)) return v;
  const out = {};
  for (const [k, raw] of Object.entries(v)) {
    const pruned = pruneEmpty(raw);
    if (!isEmptyValue(pruned)) out[k] = pruned;
  }
  return out;
};
var encodeAttr = (key, value) => {
  if (isEmptyValue(value)) return void 0;
  if (value === true) return key;
  if (value === false) return `${key}={false}`;
  if (value === null) return `${key}={null}`;
  if (typeof value === "number") return `${key}={${String(value)}}`;
  if (typeof value === "string") return `${key}=${JSON.stringify(value)}`;
  if (typeof value === "object") {
    const pruned = pruneEmpty(value);
    return isEmptyValue(pruned) ? void 0 : `${key}={${JSON.stringify(pruned)}}`;
  }
  return void 0;
};
var renderAttrs = (pairs) => pairs.map(([k, v]) => encodeAttr(k, v)).filter((s) => s !== void 0).map((s) => ` ${s}`).join("");
var element = (indent, tag, attrs, body = []) => body.length === 0 ? [`${indent}<${tag}${attrs}/>`] : [`${indent}<${tag}${attrs}>`, ...body, `${indent}</${tag}>`];
var STUB_WIDTH = 60;
var byteLength = (s) => new TextEncoder().encode(s).length;
var htmlStub = (html) => {
  const firstLine = html.split("\n")[0];
  const clipped = firstLine.slice(0, STUB_WIDTH);
  const more = firstLine.length > STUB_WIDTH || html.includes("\n");
  return `${clipped}${more ? "\u2026" : ""} (${byteLength(html)} bytes)`;
};

// src/orgicore/integration/transforms/outline/nodes.ts
var RESERVED = /* @__PURE__ */ new Set([
  "id",
  "type",
  "name",
  "label",
  "_children",
  "isRepeating",
  "data",
  "htmlTemplate",
  "purpose"
]);
var SUGARED_DATA_KEYS = /* @__PURE__ */ new Set(["dataType", "source"]);
var displayName = (node2) => isEmptyValue(node2.name) ? node2.label : node2.name;
var authoredNode = (node2) => typeof node2.type === "string" ? toAuthoredNode(node2) : node2;
var childrenOf2 = (node2) => Array.isArray(node2._children) ? node2._children.filter(isRecord4) : [];
var nodeAttrPairs = (node2, level, omit2 = /* @__PURE__ */ new Set()) => {
  if (level === 0) {
    return isEmptyValue(node2.name) ? [["label", node2.label]] : [["name", node2.name]];
  }
  const pairs = [["name", node2.name]];
  if (node2.label !== node2.name) pairs.push(["label", node2.label]);
  pairs.push(["purpose", node2.purpose]);
  const data = isRecord4(node2.data) ? node2.data : void 0;
  if (node2.isRepeating === true) pairs.push(["repeating", true]);
  if (data) {
    pairs.push(["of", data.dataType]);
    if (data.source !== "embedded") pairs.push(["source", data.source]);
  }
  if (level === 1 && node2.isRepeating === true && isEmptyValue(data == null ? void 0 : data.dataType)) {
    pairs.push(["maxLength", node2.maxLength]);
  }
  if (level >= 2) {
    const rest = Object.keys(node2).filter((k) => !RESERVED.has(k) && !omit2.has(k)).map((k) => [k, node2[k]]);
    if (data) {
      const residual = Object.fromEntries(
        Object.entries(data).filter(([k]) => !SUGARED_DATA_KEYS.has(k))
      );
      rest.push(["data", residual]);
    }
    rest.sort(([a], [b2]) => byKey(a, b2));
    pairs.push(...rest);
  }
  return pairs;
};
var collapseThreshold = (level) => level === 0 ? 2 : level === 1 ? 3 : Number.POSITIVE_INFINITY;
var variableRunLength = (children2, from) => {
  let j = from;
  while (j < children2.length && children2[j].type === "variable") j++;
  return j - from;
};
var renderChildren = (children2, ctx, depth) => {
  const out = [];
  const threshold = collapseThreshold(ctx.level);
  const indent = INDENT.repeat(depth);
  for (let i = 0; i < children2.length; ) {
    const run = variableRunLength(children2, i);
    if (run >= threshold) {
      const names2 = children2.slice(i, i + run).map((n) => {
        const d = displayName(n);
        return typeof d === "string" ? d : "";
      }).join(", ");
      out.push(
        ...element(
          indent,
          "variables",
          renderAttrs([
            ["n", run],
            ["names", names2]
          ])
        )
      );
      i += run;
      continue;
    }
    out.push(...renderNode(children2[i], ctx, depth));
    i++;
  }
  return out;
};
var renderNode = (raw, ctx, depth, tagOverride, extra = []) => {
  const node2 = tagOverride === void 0 ? authoredNode(raw) : raw;
  const tag = tagOverride != null ? tagOverride : typeof node2.type === "string" ? node2.type : "node";
  const indent = INDENT.repeat(depth);
  const pairs = [...nodeAttrPairs(node2, ctx.level), ...extra];
  const childCtx2 = {
    level: ctx.level,
    insideScreen: ctx.insideScreen || node2.type === "screen"
  };
  const showChildren = ctx.level >= 1 || !ctx.insideScreen;
  const body = showChildren ? renderChildren(childrenOf2(node2), childCtx2, depth + 1) : [];
  const html = typeof node2.htmlTemplate === "string" && node2.htmlTemplate !== "" ? node2.htmlTemplate : void 0;
  if (html !== void 0 && ctx.level === 2) pairs.push(["html", htmlStub(html)]);
  if (html !== void 0 && ctx.level === 3) {
    const lines = html.split("\n");
    const looksLikeChild = lines[0].startsWith(`${indent}${INDENT}<`);
    if (body.length === 0 && !html.includes(`</${tag}>`) && !looksLikeChild) {
      return element(indent, tag, renderAttrs(pairs), lines);
    }
    pairs.push(["htmlTemplate", html]);
  }
  return element(indent, tag, renderAttrs(pairs), body);
};

// src/orgicore/integration/transforms/sectionItems.ts
var isRec = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var sectionItems = (section2) => {
  const out = [];
  const walk8 = (node2, path) => {
    if (!Array.isArray(node2._children)) return;
    node2._children.forEach((child, i) => {
      if (!isRec(child)) return;
      const at = [...path, "_children", i];
      if (child.type === "folder") walk8(child, at);
      else out.push({ node: child, path: at });
    });
  };
  if (isRec(section2)) walk8(section2, []);
  return out;
};
var sectionNodes = (section2) => sectionItems(section2).map((item) => item.node);

// src/orgicore/integration/transforms/outline/slots.ts
var SLOT_DEPTH = 1;
var APP_RULE_KEYS = /* @__PURE__ */ new Set(["notifications", "transitions"]);
var ruleLines = (rules, tag, level, depth) => Array.isArray(rules) ? rules.flatMap(
  (r) => isRecord4(r) ? element(INDENT.repeat(depth), tag, renderAttrs(nodeAttrPairs(r, level))) : []
) : [];
var renderAppConfig = (app2, level, depth = SLOT_DEPTH) => {
  if (!app2) return [];
  const pairs = nodeAttrPairs(app2, level, APP_RULE_KEYS);
  const rules = level >= 1 ? [
    ...ruleLines(app2.notifications, "notification", level, depth + 1),
    ...ruleLines(app2.transitions, "transition", level, depth + 1)
  ] : [];
  const kids5 = renderChildren(childrenOf2(app2), { level, insideScreen: false }, depth + 1);
  return element(INDENT.repeat(depth), "appConfig", renderAttrs(pairs), [...rules, ...kids5]);
};
var renderUsers = (users, level, depth = SLOT_DEPTH) => {
  if (!users || level === 0) return [];
  const body = childrenOf2(users).flatMap(
    (u) => renderNode(u, { level, insideScreen: false }, depth + 1)
  );
  return element(INDENT.repeat(depth), "users", "", body);
};
var OBJECT_TYPE_RESERVED = /* @__PURE__ */ new Set(["id", "type", "name", "fields", "_children"]);
var FIELD_SUGARED = /* @__PURE__ */ new Set(["name", "type", "isList"]);
var renderField = (field2, indent, level) => {
  const rest = level >= 2 ? Object.keys(field2).filter((k) => !FIELD_SUGARED.has(k)).sort(byKey).map((k) => [k, field2[k]]) : [];
  const list3 = field2.isList === true ? [["list", true]] : [];
  const { name, type: type14 } = field2;
  if (isIdentifier2(name) && isIdentifier2(type14)) {
    return [`${indent}<field ${name}:${type14}${renderAttrs([...list3, ...rest])}/>`];
  }
  return element(
    indent,
    "field",
    renderAttrs([["name", name], ["type", type14], ...list3, ...rest])
  );
};
var renderObjectType = (ot, level, depth = SLOT_DEPTH + 1) => {
  const indent = INDENT.repeat(depth);
  const { name } = ot;
  const tag = isIdentifier2(name) ? name : typeof ot.type === "string" ? ot.type : "objectType";
  const pairs = tag === name ? [] : [["name", name]];
  if (level === 0) return element(indent, tag, renderAttrs(pairs));
  const rest = level >= 2 ? Object.keys(ot).filter((k) => !OBJECT_TYPE_RESERVED.has(k)).sort(byKey).map((k) => [k, ot[k]]) : [["parent", ot.parent]];
  const fields = Array.isArray(ot.fields) ? ot.fields.flatMap((f) => isRecord4(f) ? renderField(f, indent + INDENT, level) : []) : [];
  return element(indent, tag, renderAttrs([...pairs, ...rest]), fields);
};
var renderObjectTypes = (slot2, level, depth = SLOT_DEPTH) => {
  if (!slot2) return [];
  const body = sectionNodes(slot2).flatMap((ot) => renderObjectType(ot, level, depth + 1));
  return element(INDENT.repeat(depth), "objectTypes", "", body);
};
var SAMPLE_MAX = 3;
var SAMPLE_WIDTH = 40;
var ROW_META = /* @__PURE__ */ new Set(["id", "type", "_children", "name"]);
var isIdLike = (key) => key === "id" || /Id$/.test(key);
var collectRows = (nodes2, rows) => {
  for (const n of nodes2) {
    if (n.type === "folder") collectRows(childrenOf2(n), rows);
    else rows.push(n);
  }
};
var clip = (s) => s.length > SAMPLE_WIDTH ? `${s.slice(0, SAMPLE_WIDTH)}\u2026` : s;
var fieldOrderOf = (objectTypes) => {
  const order = /* @__PURE__ */ new Map();
  if (!objectTypes) return order;
  for (const ot of sectionNodes(objectTypes)) {
    if (typeof ot.name !== "string" || !Array.isArray(ot.fields)) continue;
    order.set(
      ot.name,
      ot.fields.flatMap((f) => isRecord4(f) && typeof f.name === "string" ? [f.name] : [])
    );
  }
  return order;
};
var displayValue = (row, fields) => {
  const usable = (k) => !ROW_META.has(k) && !isIdLike(k);
  const rest = Object.keys(row).filter((k) => usable(k) && !fields.includes(k)).sort(byKey);
  const candidates = [row.name, ...[...fields.filter(usable), ...rest].map((k) => row[k])];
  const found = candidates.find((v) => typeof v === "string" && v !== "");
  return typeof found === "string" ? clip(found) : void 0;
};
var samples = (rows, fields) => rows.slice(0, SAMPLE_MAX).map((r) => displayValue(r, fields)).filter((s) => s !== void 0);
var renderInstanceGroup = (type14, rows, level, fields, depth = SLOT_DEPTH + 1) => {
  const indent = INDENT.repeat(depth);
  const tag = isIdentifier2(type14) ? type14 : "instances";
  const pairs = tag === type14 ? [] : [["of", type14]];
  pairs.push(["count", rows.length]);
  if (level === 1 || level === 2) pairs.push(["eg", samples(rows, fields)]);
  const body = level === 3 ? rows.flatMap((r) => renderNode(r, { level, insideScreen: false }, depth + 1, "row")) : [];
  return element(indent, tag, renderAttrs(pairs), body);
};
var instanceGroups = (slot2) => {
  const rows = [];
  collectRows(childrenOf2(slot2), rows);
  const groups = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const type14 = typeof row.type === "string" ? row.type : "node";
    const group = groups.get(type14);
    if (group) group.push(row);
    else groups.set(type14, [row]);
  }
  return groups;
};
var renderObjectInstances = (slot2, level, fieldOrder = /* @__PURE__ */ new Map(), depth = SLOT_DEPTH) => {
  if (!slot2) return [];
  const body = [...instanceGroups(slot2)].flatMap(
    ([type14, list3]) => {
      var _a2;
      return renderInstanceGroup(type14, list3, level, (_a2 = fieldOrder.get(type14)) != null ? _a2 : [], depth + 1);
    }
  );
  return element(INDENT.repeat(depth), "objectInstances", "", body);
};
var parameterNames2 = (parameters) => Array.isArray(parameters) ? parameters.flatMap((p) => isRecord4(p) && typeof p.name === "string" ? [p.name] : []) : [];
var renderPlugin = (entry, level, depth = SLOT_DEPTH + 1) => {
  const indent = INDENT.repeat(depth);
  if (typeof entry === "string") {
    return element(indent, "pluginRef", renderAttrs([["name", entry]]));
  }
  if (!isRecord4(entry)) return [];
  const { name } = entry;
  const tag = isIdentifier2(name) ? name : "plugin";
  const named = tag === name;
  if (level === 0) {
    return element(indent, tag, renderAttrs(named ? [] : [["name", name]]));
  }
  const shaped = { ...entry };
  if (named) delete shaped.name;
  const extra = [];
  if (level < 3) {
    const params = parameterNames2(shaped.parameters);
    delete shaped.parameters;
    if (level === 1) extra.push(["params", params]);
    else shaped.params = params;
  }
  return renderNode(shaped, { level, insideScreen: false }, depth, tag, extra);
};
var pluginEntries = (node2) => (Array.isArray(node2._children) ? node2._children : []).flatMap(
  (p) => isRecord4(p) && p.type === "folder" ? pluginEntries(p) : [p]
);
var renderPlugins = (slot2, level, depth = SLOT_DEPTH) => {
  if (!slot2) return [];
  const body = pluginEntries(slot2).flatMap((p) => renderPlugin(p, level, depth + 1));
  return element(INDENT.repeat(depth), "plugins", "", body);
};

// src/orgicore/integration/transforms/outline/at.ts
var OUTLINE_SLOTS = [
  "appConfig",
  "users",
  "objectTypes",
  "objectInstances",
  "plugins"
];
var isSlot = (s) => OUTLINE_SLOTS.includes(s);
var displayName2 = (node2) => isEmptyValue(node2.name) ? node2.label : node2.name;
var nameOf = (v) => typeof v === "string" ? v : void 0;
var listNames = (names2) => {
  const real = names2.filter((n) => n !== void 0 && n !== "");
  return real.length === 0 ? "(none)" : real.map((n) => JSON.stringify(n)).join(", ");
};
var notFound = (segment2, prefix, siblings) => left(`no "${segment2}" under ${prefix}; available: ${listNames(siblings)}`);
var throughFolders = (entries) => entries.flatMap(
  (e) => isRecord4(e) && e.type === "folder" && Array.isArray(e._children) ? throughFolders(e._children) : [e]
);
var walkNodes = (root, slot2, segments) => {
  var _a2;
  let node2 = root;
  let prefix = slot2;
  for (const segment2 of segments) {
    const kids5 = childrenOf2(node2);
    const nested = throughFolders(kids5).filter(isRecord4);
    const hit = (_a2 = kids5.find((k) => displayName2(k) === segment2)) != null ? _a2 : nested.find((k) => displayName2(k) === segment2);
    if (!hit) {
      const names2 = [...kids5, ...nested].map((k) => nameOf(displayName2(k)));
      return notFound(segment2, prefix, [...new Set(names2)]);
    }
    node2 = hit;
    prefix = `${prefix}.${segment2}`;
  }
  return right({ kind: "node", slot: slot2, node: node2 });
};
var leafOnly = (path) => left(`"${path}" has no children to descend into`);
var resolveOutlinePath = (tree2, at) => {
  const segments = at.split(".");
  const [head2, ...rest] = segments;
  if (head2 === void 0 || head2 === "" || segments.some((s) => s === "")) {
    return left(`invalid path "${at}"; expected dotted names like appConfig.home or objectTypes.Recipe`);
  }
  if (!isSlot(head2)) {
    return left(`unknown slot "${head2}"; expected one of ${OUTLINE_SLOTS.join(", ")}`);
  }
  const root = tree2[head2];
  if (!isRecord4(root)) return left(`the org has no ${head2} slot`);
  if (rest.length === 0) return right({ kind: "slot", slot: head2, root });
  switch (head2) {
    case "appConfig":
    case "users":
      return walkNodes(root, head2, rest);
    case "objectTypes": {
      const [name, ...more] = rest;
      const types2 = throughFolders(childrenOf2(root)).filter(isRecord4);
      const hit = types2.find((t30) => t30.name === name);
      if (!hit) return notFound(name, head2, types2.map((t30) => nameOf(t30.name)));
      if (more.length > 0) return leafOnly(`${head2}.${name}`);
      return right({ kind: "objectType", node: hit });
    }
    case "objectInstances": {
      const [name, ...more] = rest;
      const groups = instanceGroups(root);
      const rows = groups.get(name);
      if (!rows) return notFound(name, head2, [...groups.keys()]);
      if (more.length > 0) return leafOnly(`${head2}.${name}`);
      return right({ kind: "instances", type: name, rows });
    }
    case "plugins": {
      const [name, ...more] = rest;
      const entries = throughFolders(Array.isArray(root._children) ? root._children : []);
      const pluginName = (e) => typeof e === "string" ? e : isRecord4(e) ? nameOf(e.name) : void 0;
      const hit = entries.find((e) => pluginName(e) === name);
      if (hit === void 0) return notFound(name, head2, entries.map(pluginName));
      if (more.length > 0) return leafOnly(`${head2}.${name}`);
      return right({ kind: "plugin", entry: hit });
    }
  }
};
var renderOutlineTarget = (tree2, target, level) => {
  var _a2;
  switch (target.kind) {
    case "slot":
      switch (target.slot) {
        case "appConfig":
          return renderAppConfig(target.root, level, 0);
        case "users":
          return renderUsers(target.root, level, 0);
        case "objectTypes":
          return renderObjectTypes(target.root, level, 0);
        case "objectInstances":
          return renderObjectInstances(target.root, level, fieldOrderOf(tree2.objectTypes), 0);
        case "plugins":
          return renderPlugins(target.root, level, 0);
      }
      break;
    case "node":
      return renderNode(target.node, { level, insideScreen: false }, 0);
    case "objectType":
      return renderObjectType(target.node, level, 0);
    case "instances":
      return renderInstanceGroup(
        target.type,
        target.rows,
        level,
        (_a2 = fieldOrderOf(tree2.objectTypes).get(target.type)) != null ? _a2 : [],
        0
      );
    case "plugin":
      return renderPlugin(target.entry, level, 0);
  }
};

// src/orgicore/integration/transforms/outline/orgOutline.ts
var orgOutline = (tree2, opts) => {
  const { level, at } = opts;
  if (at !== void 0) {
    const target = resolveOutlinePath(tree2, at);
    if (isLeft(target)) throw new Error(target.left);
    return renderOutlineTarget(tree2, target.right, level).join("\n");
  }
  const rootAttrs = renderAttrs([
    ["versionId", tree2.versionId],
    ["apiVersion", tree2.apiVersion],
    ["minClientVersion", tree2.minClientVersion]
  ]);
  const body = [
    ...renderAppConfig(tree2.appConfig, level),
    ...renderUsers(tree2.users, level),
    ...renderObjectTypes(tree2.objectTypes, level),
    ...renderObjectInstances(tree2.objectInstances, level, fieldOrderOf(tree2.objectTypes)),
    ...renderPlugins(tree2.plugins, level)
  ];
  return element("", "org", rootAttrs, body).join("\n");
};

// src/orgicore/integration/transforms/outline/parse/tokens.ts
var formatParseError = (e) => `line ${e.line}, col ${e.column}: ${e.message}`;
var parseError = (line, column, message) => left({ message, line, column });
var isSpace = (c) => c === " " || c === "	";
var KEY_STOP = /* @__PURE__ */ new Set([" ", "	", "=", "/", ">", "<", '"', "{", "}"]);
var readKey = (s, from) => {
  let j = from;
  while (j < s.length && !KEY_STOP.has(s[j])) j++;
  return j;
};
var FIELD_SUGAR_KEY = /:[A-Za-z_]\w*$/;
var GENERIC_ARG = /^<[A-Za-z_]\w*>/;
var readFieldSugarKey = (s, from, end) => {
  if (!FIELD_SUGAR_KEY.test(s.slice(from, end))) return end;
  const m = GENERIC_ARG.exec(s.slice(end));
  return m ? end + m[0].length : end;
};
var scanString = (s, i) => {
  let j = i + 1;
  while (j < s.length) {
    const c = s[j];
    if (c === "\\") {
      j += 2;
      continue;
    }
    if (c === '"') return j + 1;
    j++;
  }
  return void 0;
};
var scanBraces = (s, i) => {
  let depth = 0;
  let inString = false;
  let j = i;
  while (j < s.length) {
    const c = s[j];
    if (inString) {
      if (c === "\\") j++;
      else if (c === '"') inString = false;
    } else if (c === '"') {
      inString = true;
    } else if (c === "{") {
      depth++;
    } else if (c === "}") {
      depth--;
      if (depth === 0) return j + 1;
    }
    j++;
  }
  return void 0;
};
var decodeJson = (text, line, column) => {
  try {
    return right(JSON.parse(text));
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    return parseError(line, column, `invalid JSON value ${text}: ${why}`);
  }
};
var CLOSE_TAG = /^<\/([^\s/<>"={}]+)>\s*$/;
var parseTagLine = (text, line) => {
  let i = 0;
  while (i < text.length && text[i] === " ") i++;
  const indent = text.slice(0, i);
  if (text[i] !== "<") {
    return parseError(line, i + 1, "expected an element: <tag \u2026/>, <tag \u2026> or </tag>");
  }
  i++;
  if (text[i] === "/") {
    const m = CLOSE_TAG.exec(text.slice(indent.length));
    if (!m) return parseError(line, i + 1, "malformed closing tag; expected </tag>");
    return right({ kind: "close", indent, tag: m[1] });
  }
  const tagEnd = readKey(text, i);
  if (tagEnd === i) return parseError(line, i + 1, "expected a tag name after <");
  const tag = text.slice(i, tagEnd);
  i = tagEnd;
  const attrs = [];
  const seen = /* @__PURE__ */ new Set();
  const finish = (selfClosing, end) => text.slice(end).trim() === "" ? right({ kind: "open", indent, tag, attrs, selfClosing }) : parseError(line, end + 1, `unexpected text after <${tag} \u2026${selfClosing ? "/>" : ">"}`);
  for (; ; ) {
    while (i < text.length && isSpace(text[i])) i++;
    if (i >= text.length) {
      return parseError(line, i + 1, `unclosed <${tag}>: expected > or /> before the end of the line`);
    }
    if (text.startsWith("/>", i)) return finish(true, i + 2);
    if (text[i] === ">") return finish(false, i + 1);
    const keyEnd = tag === "field" ? readFieldSugarKey(text, i, readKey(text, i)) : readKey(text, i);
    if (keyEnd === i) {
      return parseError(line, i + 1, `unexpected character "${text[i]}" in <${tag}>`);
    }
    const key = text.slice(i, keyEnd);
    const column = i + 1;
    if (seen.has(key)) return parseError(line, column, `duplicate attribute "${key}"`);
    seen.add(key);
    i = keyEnd;
    if (text[i] !== "=") {
      attrs.push({ key, value: true, bare: true, column });
      continue;
    }
    i++;
    const valueColumn = i + 1;
    if (text[i] === '"') {
      const end = scanString(text, i);
      if (end === void 0) {
        return parseError(line, valueColumn, `unterminated string value for "${key}"`);
      }
      const decoded = decodeJson(text.slice(i, end), line, valueColumn);
      if (isLeft(decoded)) return decoded;
      attrs.push({ key, value: decoded.right, bare: false, column });
      i = end;
      continue;
    }
    if (text[i] === "{") {
      const end = scanBraces(text, i);
      if (end === void 0) {
        return parseError(line, valueColumn, `unbalanced braces in the value of "${key}"`);
      }
      const inner = text.slice(i + 1, end - 1);
      if (inner.trim() === "") {
        return parseError(line, valueColumn, `empty braces for "${key}"; an empty value is simply omitted`);
      }
      const decoded = decodeJson(inner, line, valueColumn + 1);
      if (isLeft(decoded)) return decoded;
      attrs.push({ key, value: decoded.right, bare: false, column });
      i = end;
      continue;
    }
    return parseError(line, valueColumn, `expected "\u2026" or {\u2026} after ${key}=`);
  }
};

// src/orgicore/integration/transforms/outline/parse/document.ts
var isBlank2 = (s) => s.trim() === "";
var dedent = (body) => {
  const indents = body.filter((l) => !isBlank2(l)).map((l) => l.length - l.trimStart().length);
  const n = indents.length > 0 ? Math.min(...indents) : 0;
  return body.map((l) => isBlank2(l) ? l : l.slice(n));
};
var parseElement = (lines, cursor, expectedIndent, end, opts, parentTag) => {
  const lineNo = cursor.i + 1;
  const tagLine = parseTagLine(lines[cursor.i], lineNo);
  if (isLeft(tagLine)) return tagLine;
  const t30 = tagLine.right;
  if (t30.kind === "close") {
    return parseError(lineNo, t30.indent.length + 1, `unexpected </${t30.tag}>; no <${t30.tag}> is open here`);
  }
  if (t30.indent !== expectedIndent) {
    return parseError(
      lineNo,
      1,
      `<${t30.tag}> is indented ${t30.indent.length} spaces; expected ${expectedIndent.length}`
    );
  }
  cursor.i++;
  const el = { tag: t30.tag, attrs: t30.attrs, line: lineNo, indent: t30.indent, children: [] };
  if (t30.selfClosing) return right(el);
  const closer = `${t30.indent}</${t30.tag}>`;
  const lenient = opts.lenientBody === true;
  const isCloser = (line) => line === closer || lenient && line.trim() === `</${t30.tag}>`;
  const childIndent = `${t30.indent}${INDENT}`;
  const unclosed = () => parseError(lineNo, 1, `unclosed <${t30.tag}>: no closing </${t30.tag}> line found`);
  if (cursor.i >= end) return unclosed();
  const first = lines[cursor.i];
  if (isCloser(first)) {
    cursor.i++;
    return right(el);
  }
  const alwaysBody = lenient && (t30.tag === "content" || parentTag === "plugins");
  if (!alwaysBody && first.startsWith(`${childIndent}<`)) {
    for (; ; ) {
      if (cursor.i >= end) return unclosed();
      const cur = lines[cursor.i];
      if (isCloser(cur)) {
        cursor.i++;
        return right(el);
      }
      if (cur.trimStart().startsWith("</")) {
        return parseError(
          cursor.i + 1,
          1,
          `unexpected ${cur.trim()}; expected </${t30.tag}> to close the <${t30.tag}> opened on line ${lineNo}`
        );
      }
      const child = parseElement(lines, cursor, childIndent, end, opts, t30.tag);
      if (isLeft(child)) return child;
      el.children.push(child.right);
    }
  }
  const body = [];
  while (cursor.i < end) {
    const cur = lines[cursor.i];
    cursor.i++;
    if (isCloser(cur)) {
      el.body = (lenient ? dedent(body) : body).join("\n");
      return right(el);
    }
    body.push(cur);
  }
  return unclosed();
};
var parseDocument = (text, opts = {}) => {
  const lines = text.split("\n");
  let end = lines.length;
  while (end > 0 && isBlank2(lines[end - 1])) end--;
  let start = 0;
  while (start < end && isBlank2(lines[start])) start++;
  if (start >= end) return parseError(1, 1, "empty outline");
  const cursor = { i: start };
  const root = parseElement(lines, cursor, "", end, opts, void 0);
  if (isLeft(root)) return root;
  if (cursor.i < end) {
    return parseError(cursor.i + 1, 1, `unexpected content after </${root.right.tag}>`);
  }
  return root;
};

// src/orgicore/integration/transforms/outline/parse/interpret.ts
var LOSSY_KEYS = {
  eg: "eg= is the L1/L2 sample list; L3 lists every <row \u2026/>",
  html: "html= is the L2 stub; L3 carries the template as the element body or htmlTemplate=",
  params: "params= is the L1/L2 parameter-name list; L3 carries parameters={[\u2026]}"
};
var IDENT2 = /^([A-Za-z_][A-Za-z0-9_]*):([A-Za-z_][A-Za-z0-9_]*(?:<[A-Za-z_][A-Za-z0-9_]*>)?)$/;
var noBody = (el, what) => el.body === void 0 ? right(void 0) : parseError(el.line + 1, 1, `${what} cannot have a body`);
var noChildren = (el, what) => el.children.length === 0 ? right(void 0) : parseError(el.children[0].line, 1, `${what} cannot have children`);
var nodeFromAttrs = (type14, attrs, line, ctx) => {
  const node2 = { type: type14 };
  let explicitData;
  const sugar = {};
  for (const a of attrs) {
    const lossy = LOSSY_KEYS[a.key];
    if (lossy) {
      if (ctx.lenient && a.key === "html") continue;
      return parseError(line, a.column, lossy);
    }
    switch (a.key) {
      case "repeating":
        if (typeof a.value !== "boolean") {
          return parseError(line, a.column, "repeating is a bare flag (or {false})");
        }
        node2.isRepeating = a.value;
        break;
      case "of":
        if (typeof a.value !== "string") {
          return parseError(line, a.column, 'of= names the data type and must be a string, e.g. of="Recipe"');
        }
        sugar.dataType = a.value;
        break;
      case "source":
        if (typeof a.value !== "string") {
          return parseError(line, a.column, 'source= must be a string, e.g. source="storage"');
        }
        sugar.source = a.value;
        break;
      case "data":
        if (!isRecord4(a.value)) {
          return parseError(line, a.column, "data= must be a {json} object holding the members not covered by of=/source=");
        }
        explicitData = a.value;
        break;
      default:
        node2[a.key] = a.value;
    }
  }
  if (explicitData !== void 0 || Object.keys(sugar).length > 0) {
    node2.data = { ...explicitData, ...sugar };
  }
  return right(node2);
};
var VARIABLES_ERROR = "<variables \u2026/> is the L0/L1 collapsed run; L3 lists every <variable \u2026/>";
var expandVariables = (el) => {
  var _a2, _b;
  const leaf = noChildren(el, "<variables>");
  if (isLeft(leaf)) return leaf;
  const body = noBody(el, "<variables>");
  if (isLeft(body)) return body;
  const n = el.attrs.find((a) => a.key === "n");
  const names2 = el.attrs.find((a) => a.key === "names");
  const other = el.attrs.find((a) => a.key !== "n" && a.key !== "names");
  if (other) return parseError(el.line, other.column, `unexpected attribute "${other.key}" on <variables> (only n= and names=)`);
  if (!n || typeof n.value !== "number") return parseError(el.line, (_a2 = n == null ? void 0 : n.column) != null ? _a2 : el.indent.length + 2, "<variables> needs n={count}");
  if (!names2 || typeof names2.value !== "string") {
    return parseError(el.line, (_b = names2 == null ? void 0 : names2.column) != null ? _b : el.indent.length + 2, '<variables> needs names="a, b, c"');
  }
  const list3 = names2.value.split(",").map((s) => s.trim());
  if (list3.length !== n.value) {
    return parseError(el.line, n.column, `<variables> says n={${n.value}} but names= lists ${list3.length}`);
  }
  return right(list3.map((name) => name === "" ? { type: "variable" } : { type: "variable", name }));
};
var treeChildren = (els, ctx) => {
  const kids5 = [];
  for (const child of els) {
    if (child.tag === "variables" && ctx.lenient) {
      const run = expandVariables(child);
      if (isLeft(run)) return run;
      kids5.push(...run.right);
      continue;
    }
    const k = treeNode(child, ctx);
    if (isLeft(k)) return k;
    kids5.push(k.right);
  }
  return right(kids5);
};
var treeNode = (el, ctx, typeOverride) => {
  if (el.tag === "variables") {
    return parseError(el.line, el.indent.length + 2, VARIABLES_ERROR);
  }
  const built = nodeFromAttrs(typeOverride != null ? typeOverride : el.tag, el.attrs, el.line, ctx);
  if (isLeft(built)) return built;
  const node2 = built.right;
  if (el.body !== void 0) {
    if ("htmlTemplate" in node2) {
      return parseError(el.line, 1, `<${el.tag}> has both an htmlTemplate= attribute and a body`);
    }
    node2.htmlTemplate = el.body;
  }
  const kids5 = treeChildren(el.children, ctx);
  if (isLeft(kids5)) return kids5;
  if (kids5.right.length > 0) node2._children = kids5.right;
  return right(node2);
};
var withoutType = (node2) => Object.fromEntries(Object.entries(node2).filter(([k]) => k !== "type"));
var RULE_TAGS = {
  notification: "notifications",
  transition: "transitions"
};
var appConfig2 = (el, ctx) => {
  const body = noBody(el, "<appConfig>");
  if (isLeft(body)) return body;
  const built = nodeFromAttrs("app", el.attrs, el.line, ctx);
  if (isLeft(built)) return built;
  const app2 = built.right;
  const rules = {
    notifications: [],
    transitions: []
  };
  const nodeEls = [];
  for (const child of el.children) {
    const arrayKey = RULE_TAGS[child.tag];
    if (arrayKey) {
      const leaf = noChildren(child, `<${child.tag}>`);
      if (isLeft(leaf)) return leaf;
      const ruleBody = noBody(child, `<${child.tag}>`);
      if (isLeft(ruleBody)) return ruleBody;
      const rule = nodeFromAttrs("rule", child.attrs, child.line, ctx);
      if (isLeft(rule)) return rule;
      rules[arrayKey].push(withoutType(rule.right));
      continue;
    }
    nodeEls.push(child);
  }
  const kids5 = treeChildren(nodeEls, ctx);
  if (isLeft(kids5)) return kids5;
  if (rules.notifications.length > 0) app2.notifications = rules.notifications;
  if (rules.transitions.length > 0) app2.transitions = rules.transitions;
  if (kids5.right.length > 0) app2._children = kids5.right;
  return right(app2);
};
var slotFolder = (kids5) => kids5.length > 0 ? { type: "folder", _children: kids5 } : { type: "folder" };
var slot = (el, item) => {
  const body = noBody(el, `<${el.tag}>`);
  if (isLeft(body)) return body;
  if (el.attrs.length > 0) {
    return parseError(el.line, el.attrs[0].column, `<${el.tag}> is a synthetic folder and takes no attributes`);
  }
  const kids5 = [];
  for (const child of el.children) {
    const k = item(child);
    if (isLeft(k)) return k;
    kids5.push(k.right);
  }
  return right(slotFolder(kids5));
};
var field = (el, owner, ctx) => {
  if (el.tag !== "field") {
    return parseError(el.line, el.indent.length + 2, `expected <field \u2026/> inside <${owner}>, found <${el.tag}>`);
  }
  const leaf = noChildren(el, "<field>");
  if (isLeft(leaf)) return leaf;
  const body = noBody(el, "<field>");
  if (isLeft(body)) return body;
  const out = {};
  let rest = el.attrs;
  const first = el.attrs[0];
  const sugar = first && first.bare ? IDENT2.exec(first.key) : null;
  if (sugar) {
    if (sugar[2].includes("<") && !ctx.lenient) {
      return parseError(el.line, first.column, `a list<T>/map<T> field is written <field name="${sugar[1]}" type="${sugar[2]}"/>; the name:type sugar takes plain identifiers`);
    }
    out.name = sugar[1];
    out.type = sugar[2];
    rest = el.attrs.slice(1);
  }
  for (const a of rest) {
    if (a.key === "list") {
      if (typeof a.value !== "boolean") return parseError(el.line, a.column, "list is a bare flag");
      out.isList = a.value;
    } else {
      out[a.key] = a.value;
    }
  }
  if (out.name === void 0 || out.type === void 0) {
    return parseError(el.line, el.indent.length + 2, '<field> needs name:type sugar or name="\u2026" type="\u2026" attributes');
  }
  return right(out);
};
var objectType2 = (el, ctx) => {
  const body = noBody(el, `<${el.tag}>`);
  if (isLeft(body)) return body;
  const node2 = { type: "objectType" };
  if (el.tag !== "objectType") node2.name = el.tag;
  for (const a of el.attrs) {
    if (a.key === "name" && el.tag !== "objectType") {
      return parseError(el.line, a.column, `<${el.tag}> is already the object type's name; drop name=`);
    }
    node2[a.key] = a.value;
  }
  const fields = [];
  for (const child of el.children) {
    const f = field(child, el.tag, ctx);
    if (isLeft(f)) return f;
    fields.push(f.right);
  }
  if (fields.length > 0) node2.fields = fields;
  return right(node2);
};
var instanceGroup = (el, ctx) => {
  const body = noBody(el, `<${el.tag}>`);
  if (isLeft(body)) return body;
  let type14 = el.tag === "instances" ? void 0 : el.tag;
  let count;
  for (const a of el.attrs) {
    if (a.key === "of") {
      if (typeof a.value !== "string") return parseError(el.line, a.column, "of= must be a string type name");
      type14 = a.value;
    } else if (a.key === "count") {
      if (typeof a.value !== "number") return parseError(el.line, a.column, "count= must be a number");
      count = a.value;
    } else if (a.key === "eg") {
      if (ctx.lenient) continue;
      return parseError(el.line, a.column, LOSSY_KEYS.eg);
    } else {
      return parseError(el.line, a.column, `unexpected attribute "${a.key}" on an instance group (only of= and count=)`);
    }
  }
  if (type14 === void 0) return parseError(el.line, el.indent.length + 2, '<instances> needs of="Type"');
  const rows = [];
  for (const child of el.children) {
    if (child.tag !== "row") {
      return parseError(child.line, child.indent.length + 2, `expected <row \u2026/> inside <${el.tag}>, found <${child.tag}>`);
    }
    const row = treeNode(child, ctx, type14);
    if (isLeft(row)) return row;
    rows.push(row.right);
  }
  const countOnly = ctx.lenient && rows.length === 0;
  if (count !== void 0 && count !== rows.length && !countOnly) {
    return parseError(el.line, 1, `<${el.tag}> says count={${count}} but ${rows.length} row(s) follow`);
  }
  const folder2 = { type: "folder", name: type14 };
  if (rows.length > 0) folder2._children = rows;
  return right(folder2);
};
var parametersFromParams = (a, line) => {
  const names2 = typeof a.value === "string" ? a.value.split(",").map((s) => s.trim()) : Array.isArray(a.value) ? a.value : [];
  if (names2.length === 0 || !names2.every((n) => typeof n === "string" && n !== "")) {
    return parseError(line, a.column, 'params= lists parameter names: params="a, b" or params={["a","b"]}');
  }
  return right(names2.map((name) => ({ name })));
};
var plugin = (el, ctx) => {
  if (el.tag === "pluginRef") {
    return parseError(el.line, el.indent.length + 2, "<pluginRef/> is a bare string plugin reference, which has no tree representation; no committed org uses one");
  }
  let attrs = el.attrs;
  let name = el.tag;
  const nameAttr = attrs.find((a) => a.key === "name");
  if (el.tag === "plugin") {
    name = nameAttr == null ? void 0 : nameAttr.value;
    attrs = attrs.filter((a) => a !== nameAttr);
  } else if (nameAttr) {
    return parseError(el.line, nameAttr.column, `<${el.tag}> is already the plugin's name; drop name=`);
  }
  let parameters;
  const paramsAttr = attrs.find((a) => a.key === "params");
  if (paramsAttr && ctx.lenient) {
    const fromParams = parametersFromParams(paramsAttr, el.line);
    if (isLeft(fromParams)) return fromParams;
    parameters = fromParams.right;
    attrs = attrs.filter((a) => a !== paramsAttr);
  }
  const built = treeNode({ ...el, attrs }, ctx, "plugin");
  if (isLeft(built)) return built;
  if (name !== void 0) built.right.name = name;
  if (parameters !== void 0) {
    if ("parameters" in built.right) {
      return parseError(el.line, paramsAttr.column, "a plugin takes params= or parameters={[\u2026]}, not both");
    }
    built.right.parameters = parameters;
  }
  return right(built.right);
};
var SLOT_PARSERS = {
  appConfig: appConfig2,
  users: (el, ctx) => slot(el, (c) => treeNode(c, ctx)),
  objectTypes: (el, ctx) => slot(el, (c) => objectType2(c, ctx)),
  objectInstances: (el, ctx) => slot(el, (c) => instanceGroup(c, ctx)),
  plugins: (el, ctx) => slot(el, (c) => plugin(c, ctx))
};
var isSlotKey = (tag) => tag in SLOT_PARSERS;
var interpretRoot = (root, ctx = { lenient: false }) => {
  if (root.tag !== "org") {
    return parseError(root.line, 2, `expected <org \u2026> at the root, found <${root.tag}>`);
  }
  const body = noBody(root, "<org>");
  if (isLeft(body)) return body;
  const tree2 = {
    versionId: "",
    appConfig: void 0,
    users: void 0,
    objectTypes: void 0,
    objectInstances: void 0,
    plugins: void 0
  };
  for (const a of root.attrs) {
    if (a.key === "versionId" || a.key === "apiVersion") {
      if (typeof a.value !== "string") return parseError(root.line, a.column, `${a.key}= must be a string`);
      tree2[a.key] = a.value;
    } else if (a.key === "minClientVersion") {
      if (typeof a.value !== "number") return parseError(root.line, a.column, "minClientVersion= must be a number");
      tree2.minClientVersion = a.value;
    } else {
      return parseError(root.line, a.column, `unknown <org> attribute "${a.key}" (versionId, apiVersion, minClientVersion)`);
    }
  }
  const seen = /* @__PURE__ */ new Set();
  for (const child of root.children) {
    if (!isSlotKey(child.tag)) {
      return parseError(child.line, child.indent.length + 2, `unknown slot <${child.tag}>; expected appConfig, users, objectTypes, objectInstances or plugins`);
    }
    if (seen.has(child.tag)) {
      return parseError(child.line, child.indent.length + 2, `duplicate slot <${child.tag}>`);
    }
    seen.add(child.tag);
    const parsed = SLOT_PARSERS[child.tag](child, ctx);
    if (isLeft(parsed)) return parsed;
    tree2[child.tag] = parsed.right;
  }
  return right(tree2);
};

// src/orgicore/integration/transforms/outline/parse/parseOutline.ts
var parseOutline = (text, options = {}) => {
  const doc = parseDocument(text, { lenientBody: options.lenientBody });
  if (isLeft(doc)) return doc;
  return interpretRoot(doc.right, { lenient: options.lenient === true });
};

// src/orgicore/integration/transforms/outline/canonical.ts
var jsonView = (v) => v === void 0 ? void 0 : JSON.parse(JSON.stringify(v));
var canonicalValue = (raw) => {
  const v = jsonView(raw);
  if (isEmptyValue(v)) return void 0;
  if (typeof v === "object" && v !== null) {
    const pruned = pruneEmpty(v);
    return isEmptyValue(pruned) ? void 0 : pruned;
  }
  return v;
};
var canonicalData = (data) => {
  const out = {};
  const residual = Object.fromEntries(
    Object.entries(data).filter(([k]) => k !== "dataType" && k !== "source")
  );
  const rest = canonicalValue(residual);
  if (isRecord4(rest)) Object.assign(out, rest);
  const dataType = canonicalValue(data.dataType);
  if (dataType !== void 0) out.dataType = dataType;
  if (data.source !== "embedded") {
    const source = canonicalValue(data.source);
    if (source !== void 0) out.source = source;
  }
  return Object.keys(out).length > 0 ? out : void 0;
};
var NODE_SKIP = /* @__PURE__ */ new Set(["id", "type", "_children"]);
var canonicalNode = (raw, type14) => {
  const node2 = type14 === void 0 ? authoredNode(raw) : raw;
  const out = { type: type14 != null ? type14 : typeof node2.type === "string" ? node2.type : "node" };
  for (const [key, raw2] of Object.entries(node2)) {
    if (NODE_SKIP.has(key)) continue;
    if (key === "label" && raw2 === node2.name) continue;
    if (key === "isRepeating") {
      if (raw2 === true) out.isRepeating = true;
      continue;
    }
    if (key === "data") {
      if (!isRecord4(raw2)) continue;
      const data = canonicalData(raw2);
      if (data) out.data = data;
      continue;
    }
    if (key === "htmlTemplate") {
      if (typeof raw2 === "string" && raw2 !== "") out.htmlTemplate = raw2;
      continue;
    }
    const v = canonicalValue(raw2);
    if (v !== void 0) out[key] = v;
  }
  const kids5 = childrenOf2(node2).map((c) => canonicalNode(c));
  if (kids5.length > 0) out._children = kids5;
  return out;
};
var omitKeys = (obj, keys) => Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.has(k)));
var RULE_SKIP = /* @__PURE__ */ new Set(["type", "_children"]);
var APP_SPECIAL = /* @__PURE__ */ new Set(["notifications", "transitions", "htmlTemplate"]);
var canonicalApp = (app2) => {
  const out = canonicalNode(omitKeys(app2, APP_SPECIAL), "app");
  for (const key of ["notifications", "transitions"]) {
    const rules = Array.isArray(app2[key]) ? app2[key].filter(isRecord4) : [];
    const canon = rules.map((r) => omitKeys(canonicalNode(r), RULE_SKIP));
    if (canon.length > 0) out[key] = canon;
  }
  return out;
};
var OBJECT_TYPE_SKIP = /* @__PURE__ */ new Set(["id", "type", "name", "fields", "_children"]);
var FIELD_SKIP = /* @__PURE__ */ new Set(["name", "type", "isList"]);
var canonicalField = (f) => {
  const out = {};
  const name = canonicalValue(f.name);
  if (name !== void 0) out.name = name;
  const type14 = canonicalValue(f.type);
  if (type14 !== void 0) out.type = type14;
  if (f.isList === true) out.isList = true;
  for (const [key, raw] of Object.entries(f)) {
    if (FIELD_SKIP.has(key)) continue;
    const v = canonicalValue(raw);
    if (v !== void 0) out[key] = v;
  }
  return out;
};
var canonicalObjectType = (ot) => {
  const out = { type: "objectType" };
  const name = canonicalValue(ot.name);
  if (name !== void 0) out.name = name;
  for (const [key, raw] of Object.entries(ot)) {
    if (OBJECT_TYPE_SKIP.has(key)) continue;
    const v = canonicalValue(raw);
    if (v !== void 0) out[key] = v;
  }
  const fields = Array.isArray(ot.fields) ? ot.fields.filter(isRecord4).map(canonicalField) : [];
  if (fields.length > 0) out.fields = fields;
  return out;
};
var canonicalInstances = (slot2) => {
  const rows = [];
  collectRows(childrenOf2(slot2), rows);
  const groups = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const type14 = typeof row.type === "string" ? row.type : "node";
    const group = groups.get(type14);
    const canon = canonicalNode(row, type14);
    if (group) group.push(canon);
    else groups.set(type14, [canon]);
  }
  return [...groups].map(([name, kids5]) => ({ type: "folder", name, _children: kids5 }));
};
var canonicalPlugin = (p) => {
  const named = isIdentifier2(p.name);
  const shaped = { ...p };
  if (named) delete shaped.name;
  const out = canonicalNode(shaped, "plugin");
  if (named) out.name = p.name;
  return out;
};
var canonicalSlot = (slot2, items) => {
  if (!slot2) return void 0;
  const kids5 = isRecord4(slot2) ? items(slot2) : [];
  return kids5.length > 0 ? { type: "folder", _children: kids5 } : { type: "folder" };
};
var canonicalOrgTree = (tree2) => {
  const out = {
    versionId: typeof tree2.versionId === "string" ? tree2.versionId : "",
    appConfig: tree2.appConfig ? canonicalApp(tree2.appConfig) : void 0,
    users: canonicalSlot(tree2.users, (root) => childrenOf2(root).map((u) => canonicalNode(u))),
    objectTypes: canonicalSlot(tree2.objectTypes, (root) => sectionNodes(root).map(canonicalObjectType)),
    objectInstances: canonicalSlot(tree2.objectInstances, canonicalInstances),
    plugins: canonicalSlot(tree2.plugins, (root) => sectionNodes(root).map(canonicalPlugin))
  };
  const apiVersion = canonicalValue(tree2.apiVersion);
  if (typeof apiVersion === "string") out.apiVersion = apiVersion;
  const minClientVersion = canonicalValue(tree2.minClientVersion);
  if (typeof minClientVersion === "number") out.minClientVersion = minClientVersion;
  return out;
};

// src/orgicore/transitions/evalRule.ts
var functionMarkers = (functions, consts) => {
  const out = {};
  for (const [name, fn] of Object.entries(functions != null ? functions : {})) {
    if (!fn || typeof fn !== "object") continue;
    if (name in consts) continue;
    out[name] = {
      __orgFunction: true,
      compute: fn.compute,
      parameters: fn.parameters,
      key: name
    };
  }
  return out;
};
var decodeConsts = (consts, ruleLabel) => {
  const out = {};
  const unbound = (name) => console.warn(
    `[transition] rule ${ruleLabel != null ? ruleLabel : "(unlabelled)"}: mirrored const ${name} is not a JSON string \u2014 left unbound (reads undefined)`
  );
  for (const [name, encoded] of Object.entries(consts != null ? consts : {})) {
    if (typeof encoded !== "string") {
      unbound(name);
      continue;
    }
    try {
      out[name] = JSON.parse(encoded);
    } catch {
      unbound(name);
    }
  }
  return out;
};
var sharedScope = (input) => {
  var _a2;
  const consts = decodeConsts(input.consts, input.ruleLabel);
  return {
    ...consts,
    ...functionMarkers(input.functions, consts),
    $target: input.target,
    $records: (_a2 = input.records) != null ? _a2 : [],
    $params: input.params,
    $now: input.now,
    $seed: input.seed,
    $private: input.private
  };
};
var buildRuleContext = (input) => ({
  $record: input.record,
  $recordId: input.recordId,
  ...sharedScope(input)
});
var buildTimerRuleContext = (input) => sharedScope(input);
var evalRuleField = (source, ctx) => {
  try {
    const parsed = parseFormula(tryStripFormulaPrefix(source));
    if (isLeft(parsed)) {
      return { ok: false, outcome: "formula-error", detail: parsed.left.message };
    }
    const evaluated = evalExpr(parsed.right, createMutableContext(ctx));
    if (isLeft(evaluated)) {
      return {
        ok: false,
        outcome: "formula-error",
        detail: getEvalErrorMessage(evaluated.left)
      };
    }
    return { ok: true, value: evaluated.right };
  } catch (e) {
    return { ok: false, outcome: "formula-error", detail: String(e) };
  }
};
var evalTargetId = (source, ctx) => {
  const r = evalRuleField(source, ctx);
  if (!r.ok) return r;
  if (typeof r.value === "string" && r.value.length > 0) {
    return { ok: true, value: r.value };
  }
  return {
    ok: false,
    outcome: "bad-target-id",
    detail: `targetId did not evaluate to a non-empty string (got ${typeof r.value === "string" ? "empty string" : typeof r.value})`
  };
};
var isPlainObject4 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var pruneForRtdb = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => pruneForRtdb(item));
  }
  if (isPlainObject4(value)) {
    const out = {};
    for (const [key, raw] of Object.entries(value)) {
      if (raw === void 0) continue;
      const pruned = pruneForRtdb(raw);
      if (pruned === void 0) continue;
      if (isPlainObject4(pruned) && Object.keys(pruned).length === 0) continue;
      out[key] = pruned;
    }
    return out;
  }
  return value;
};

// src/orgicore/transitions/commit.ts
var evalGuards = (guards, ctx) => {
  for (const src of guards) {
    const r = evalRuleField(src, ctx);
    if (!r.ok) return { ok: false, detail: r.detail };
    if (!r.value) return { ok: true, passed: false };
  }
  return { ok: true, passed: true };
};
var ILLEGAL_KEY_CHARS = /[.#$/[\]]/;
var buildPrivateUpdates = (value, args) => {
  if (value === null || value === void 0) {
    return {
      ok: false,
      detail: "`nextPrivate` evaluated to null/undefined; return an object keyed by auth uid, or `@{}` to write no private records."
    };
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return {
      ok: false,
      detail: `\`nextPrivate\` must evaluate to an object keyed by auth uid, got ${Array.isArray(value) ? "an array" : typeof value}.`
    };
  }
  const updates = {};
  for (const [uid, record10] of Object.entries(value)) {
    if (!uid || ILLEGAL_KEY_CHARS.test(uid)) {
      return {
        ok: false,
        detail: `\`nextPrivate\` key \`${uid}\` is not a usable RTDB path segment (empty, or contains one of . # $ / [ ]). Keys must be Firebase auth uids.`
      };
    }
    if (record10 === void 0) continue;
    updates[`realtimePrivate/${uid}/${args.privateType}/${args.targetId}`] = pruneForRtdb(record10);
  }
  return { ok: true, updates };
};
var commitGuarded = async (args) => {
  const { targetRef, snapshot, guards, nextSrc, contextFor, privateWrite } = args;
  let evalErr = null;
  let privateUpdates = null;
  let live = void 0;
  const tx = await targetRef.transaction((current) => {
    const cur = current != null ? current : snapshot;
    const ctx = contextFor(cur);
    live = void 0;
    const guard = evalGuards(guards, ctx);
    if (!guard.ok) {
      evalErr = { detail: guard.detail };
      return void 0;
    }
    if (!guard.passed) {
      live = cur;
      return void 0;
    }
    const nextR = evalRuleField(nextSrc, ctx);
    if (!nextR.ok) {
      evalErr = { detail: nextR.detail };
      return void 0;
    }
    if (privateWrite) {
      const privR = evalRuleField(privateWrite.nextPrivateSrc, ctx);
      if (!privR.ok) {
        evalErr = { detail: privR.detail };
        return void 0;
      }
      const built = buildPrivateUpdates(privR.value, privateWrite);
      if (!built.ok) {
        evalErr = { detail: built.detail };
        return void 0;
      }
      privateUpdates = built.updates;
    }
    return pruneForRtdb(nextR.value);
  });
  if (evalErr) {
    return {
      outcome: "formula-error",
      detail: evalErr.detail
    };
  }
  if (!tx.committed) return { outcome: "stale", live };
  if (privateUpdates !== null) {
    const updates = privateUpdates;
    if (Object.keys(updates).length === 0) {
      return { outcome: "applied", committed: tx.snapshot.val() };
    }
    try {
      await privateWrite.orgRootRef.update(updates);
    } catch (e) {
      return { outcome: "private-write-failed", detail: String(e) };
    }
  }
  return { outcome: "applied", committed: tx.snapshot.val() };
};

// src/orgicore/transitions/privateRead.ts
var PRIVATE_READ_LIMIT = 64;
var normalizePrivateReadKeys = (value) => {
  if (!Array.isArray(value)) {
    return {
      ok: false,
      detail: `\`privateReadUids\` must evaluate to an array of keys, got ${value === null ? "null" : typeof value}; use \`@[]\` to read none.`
    };
  }
  if (value.length > PRIVATE_READ_LIMIT) {
    return {
      ok: false,
      detail: `\`privateReadUids\` produced ${value.length} keys, over the ${PRIVATE_READ_LIMIT} limit. Each key is one read; narrow the formula.`
    };
  }
  const keys = [];
  for (const raw of value) {
    if (typeof raw !== "string" || raw.length === 0) {
      return {
        ok: false,
        detail: `\`privateReadUids\` produced a key that is not a non-empty string (got ${raw === null ? "null" : typeof raw}). Keys must be Firebase auth uids, or a reserved engine-only segment.`
      };
    }
    if (ILLEGAL_KEY_CHARS.test(raw)) {
      return {
        ok: false,
        detail: `\`privateReadUids\` key \`${raw}\` is not a usable RTDB path segment (contains one of . # $ / [ ]).`
      };
    }
    if (!keys.includes(raw)) keys.push(raw);
  }
  return { ok: true, keys };
};
var loadPrivateRecords = async (db, base, privateType, targetId, keys) => {
  const loaded = await Promise.all(
    keys.map(async (key) => {
      const snap = await db.ref(`${base}/realtimePrivate/${key}/${privateType}/${targetId}`).once("value");
      return [key, snap.val()];
    })
  );
  const out = {};
  for (const [key, val] of loaded) {
    if (val !== null && val !== void 0) out[key] = val;
  }
  return out;
};

// src/orgicore/transitions/mirror.ts
var MIRROR_BOT_ACTOR_FORMULA_FIELDS = [
  "isBotTurn",
  "privateKey",
  "privateReady",
  "state",
  "questions",
  "moveId",
  "move",
  "fallbackMove",
  "delayMs"
];
var appNodeOf = (config) => {
  var _a2, _b;
  return Array.isArray((_a2 = config.appConfig) == null ? void 0 : _a2.data) ? (_b = config.appConfig.data.find((e) => {
    var _a3;
    return ((_a3 = e == null ? void 0 : e.value) == null ? void 0 : _a3.type) === "app";
  })) == null ? void 0 : _b.value : void 0;
};
var extractBotActors = (config) => {
  var _a2, _b;
  if (!config) return [];
  const raw = (_b = (_a2 = appNodeOf(config)) == null ? void 0 : _a2.botActors) != null ? _b : config.botActors;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
};
var extractTransitionRules = (config) => {
  var _a2, _b, _c;
  if (!config) return [];
  const appValue = Array.isArray((_a2 = config.appConfig) == null ? void 0 : _a2.data) ? (_b = config.appConfig.data.find((e) => {
    var _a3;
    return ((_a3 = e == null ? void 0 : e.value) == null ? void 0 : _a3.type) === "app";
  })) == null ? void 0 : _b.value : void 0;
  const raw = (_c = appValue == null ? void 0 : appValue.transitions) != null ? _c : config.transitions;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
};
var astReferencesRecords = (node2, seen) => {
  if (Array.isArray(node2)) return node2.some((c) => astReferencesRecords(c, seen));
  if (node2 === null || typeof node2 !== "object") return false;
  if (seen.has(node2)) return false;
  seen.add(node2);
  const n = node2;
  if (n.type === "Identifier" && n.name === "$records") return true;
  return Object.values(n).some((c) => astReferencesRecords(c, seen));
};
var ruleFormulaFields = (rule) => [
  rule.targetId,
  rule.condition,
  rule.next,
  rule.nextPrivate,
  rule.privateReadUids,
  // A reason like `@length($records) + " moves so far"` must fetch `$records`
  // and see the functions and consts it calls/reads (#1371).
  rule.rejectionReason,
  rule.dueAt,
  // A perpetual rule's publish-time first state (#1521): the reconcile
  // evaluates it with this rule's closure, so a helper or const read only
  // from `seed` must be stamped too. The gate bars `$records` from it (it
  // runs before the record exists), so the `referencesRecords` probe can't
  // fire for a valid config — listed anyway so the guard stays "every field".
  rule.seed,
  // The list push (#2325) is evaluated after the commit in the same scope as
  // `next`, so it needs the same records, functions and consts.
  rule.notifyTo,
  rule.notifyTitle,
  rule.notifyBody,
  rule.notifyKey,
  rule.notifyClickVariant,
  // The SMS channel (#2254) shares the list push's post-commit scope.
  rule.smsTo,
  rule.smsBody,
  rule.smsReadUids,
  // The archive projection and id (#2324), evaluated by timerFire in the
  // same scope as `next` just before the end-of-life delete.
  rule.archiveData,
  rule.archiveId
];
var referencesRecords = (rule) => ruleFormulaFields(rule).some((src) => {
  if (typeof src !== "string" || src.length === 0) return false;
  const parsed = parseFormula(tryStripFormulaPrefix(src));
  if (isLeft(parsed)) return false;
  return astReferencesRecords(parsed.right, /* @__PURE__ */ new WeakSet());
});
var extractFunctionNodes = (config, out = /* @__PURE__ */ new Map()) => {
  if (Array.isArray(config)) {
    config.forEach((c) => extractFunctionNodes(c, out));
    return out;
  }
  if (config === null || typeof config !== "object") return out;
  const node2 = config;
  if (node2.type === "function" && typeof node2.name === "string" && node2.name.length > 0 && typeof node2.compute === "string" && !out.has(node2.name)) {
    out.set(node2.name, {
      parameters: typeof node2.parameters === "string" ? node2.parameters : "",
      compute: node2.compute
    });
  }
  for (const v of Object.values(node2)) extractFunctionNodes(v, out);
  return out;
};
var isRtdbSafeKey = (name) => !/[.$#[\]/]/.test(name);
var extractConstNodes = (config, out = /* @__PURE__ */ new Map()) => {
  if (Array.isArray(config)) {
    config.forEach((c) => extractConstNodes(c, out));
    return out;
  }
  if (config === null || typeof config !== "object") return out;
  const node2 = config;
  if (node2.type === "const" && typeof node2.name === "string" && node2.name.length > 0 && isRtdbSafeKey(node2.name) && node2.value !== void 0 && !out.has(node2.name)) {
    out.set(node2.name, JSON.stringify(node2.value));
  }
  for (const v of Object.values(node2)) extractConstNodes(v, out);
  return out;
};
var astIdentifiers = (node2, seen, out) => {
  if (Array.isArray(node2)) {
    node2.forEach((c) => astIdentifiers(c, seen, out));
    return;
  }
  if (node2 === null || typeof node2 !== "object") return;
  if (seen.has(node2)) return;
  seen.add(node2);
  const n = node2;
  if (n.type === "Identifier") {
    if (typeof n.name === "string" && n.name.length > 0) out.add(n.name);
    return;
  }
  if (n.type === "MemberExpression" && n.computed !== true) {
    astIdentifiers(n.object, seen, out);
    return;
  }
  if (n.type === "Property" && n.computed !== true) {
    astIdentifiers(n.value, seen, out);
    return;
  }
  for (const v of Object.values(n)) astIdentifiers(v, seen, out);
};
var identifiersIn = (src) => {
  const out = /* @__PURE__ */ new Set();
  if (typeof src !== "string" || src.length === 0) return out;
  const parsed = parseFormula(tryStripFormulaPrefix(src));
  if (isLeft(parsed)) return out;
  astIdentifiers(parsed.right, /* @__PURE__ */ new WeakSet(), out);
  return out;
};
var reachableFunctions = (rule, all) => reachableFunctionsFor(ruleFormulaFields(rule), all);
var reachableFunctionsFor = (formulas, all) => {
  const out = {};
  const queue = [];
  const seed = /* @__PURE__ */ new Set();
  for (const src of formulas) {
    for (const name of identifiersIn(src)) seed.add(name);
  }
  queue.push(...seed);
  while (queue.length > 0) {
    const name = queue.shift();
    if (name in out) continue;
    const fn = all.get(name);
    if (!fn) continue;
    out[name] = fn;
    for (const next of identifiersIn(fn.compute)) {
      if (!(next in out)) queue.push(next);
    }
  }
  return out;
};
var reachableConsts = (rule, allConsts, reachableFns) => reachableConstsFor(ruleFormulaFields(rule), allConsts, reachableFns);
var reachableConstsFor = (formulas, allConsts, reachableFns) => {
  const out = {};
  if (allConsts.size === 0) return out;
  const sources = [
    ...formulas,
    ...Object.values(reachableFns).map((fn) => fn.compute)
  ];
  for (const src of sources) {
    for (const name of identifiersIn(src)) {
      const encoded = allConsts.get(name);
      if (encoded !== void 0) out[name] = encoded;
    }
  }
  return out;
};
var buildMirror = (rules, versionId, allFunctions = /* @__PURE__ */ new Map(), objectTypes = [], actors = [], allConsts = /* @__PURE__ */ new Map()) => {
  var _a2, _b, _c, _d, _e;
  const cascade = childrenOf(objectTypes);
  const byWatchType = {};
  const byTargetType = {};
  const byTimerType = {};
  const botActors = buildBotActorIndex(actors, allFunctions, allConsts);
  const privateOf = {};
  for (const rule of rules) {
    if (rule.enabled === false) continue;
    if (typeof rule.targetType === "string" && rule.targetType.length > 0 && typeof rule.privateType === "string" && rule.privateType.length > 0) {
      const types2 = (_b = privateOf[_a2 = rule.targetType]) != null ? _b : privateOf[_a2] = [];
      if (!types2.includes(rule.privateType)) types2.push(rule.privateType);
    }
    const functions = reachableFunctions(rule, allFunctions);
    const consts = reachableConsts(rule, allConsts, functions);
    const mirrorRule = {
      ...rule,
      needsRecords: referencesRecords(rule),
      ...Object.keys(functions).length > 0 ? { functions } : {},
      ...Object.keys(consts).length > 0 ? { consts } : {}
    };
    const targetType = rule.targetType;
    const hasTargetType = typeof targetType === "string" && targetType.length > 0;
    if (rule.trigger === "onTimer") {
      if (hasTargetType) ((_c = byTimerType[targetType]) != null ? _c : byTimerType[targetType] = []).push(mirrorRule);
      continue;
    }
    const watchType = rule.watchType;
    if (typeof watchType !== "string" || watchType.length === 0) continue;
    ((_d = byWatchType[watchType]) != null ? _d : byWatchType[watchType] = []).push(mirrorRule);
    if (hasTargetType && typeof rule.notifyTurnUidField === "string" && rule.notifyTurnUidField.length > 0) {
      ((_e = byTargetType[targetType]) != null ? _e : byTargetType[targetType] = []).push(mirrorRule);
    }
  }
  if (Object.keys(byWatchType).length === 0 && Object.keys(byTimerType).length === 0 && Object.keys(cascade).length === 0 && Object.keys(botActors).length === 0) {
    return null;
  }
  return {
    versionId,
    byWatchType,
    byTargetType,
    byTimerType,
    childrenOf: cascade,
    botActors,
    privateOf
  };
};
var buildBotActorIndex = (actors, allFunctions, allConsts = /* @__PURE__ */ new Map()) => {
  var _a2, _b;
  const out = {};
  for (const actor of actors) {
    if (actor.enabled === false) continue;
    const { watchType, moveType } = actor;
    if (typeof watchType !== "string" || watchType.length === 0) continue;
    if (typeof moveType !== "string" || moveType.length === 0) continue;
    if (moveType === watchType) {
      console.warn(
        `[transitionRulesMirror] botActor ${(_a2 = actor.label) != null ? _a2 : "(unlabelled)"} dropped: moveType equals watchType (${watchType}) \u2014 it would loop`
      );
      continue;
    }
    const formulas = MIRROR_BOT_ACTOR_FORMULA_FIELDS.map((f) => actor[f]);
    const functions = reachableFunctionsFor(formulas, allFunctions);
    const consts = reachableConstsFor(formulas, allConsts, functions);
    ((_b = out[watchType]) != null ? _b : out[watchType] = []).push({
      ...actor,
      ...Object.keys(functions).length > 0 ? { functions } : {},
      ...Object.keys(consts).length > 0 ? { consts } : {}
    });
  }
  return out;
};

// src/orgicore/transitions/engineCore.ts
var buildLastOutcome = (event, result, now) => {
  const isNoRulesOnly = result.results.length === 1 && result.results[0].outcome === "no-rules";
  if (isNoRulesOnly) return null;
  return {
    typeName: event.typeName,
    recordId: event.recordId,
    at: now,
    results: result.results.map((r) => ({
      outcome: r.outcome,
      ...r.rule !== void 0 ? { rule: r.rule } : {},
      ...r.detail !== void 0 ? { detail: r.detail } : {}
    }))
  };
};
var inputRail = (base, event) => event.inboxUid ? `${base}/realtimeInbox/${event.inboxUid}` : `${base}/realtime`;
var single = (outcome) => ({
  results: [{ rule: void 0, outcome }]
});
var hasFormula = (v) => typeof v === "string" && v.length > 0;
var GENERIC_REJECTION = "That move isn't allowed right now.";
var MOVE_ACK_SUBKEY = "_moveOutcome";
var CASCADE_RULE = "cascade:parent-delete";
var evalRejectionReason = (rule, ctx) => {
  if (!hasFormula(rule.rejectionReason)) return void 0;
  const r = evalRuleField(rule.rejectionReason, ctx);
  return r.ok && typeof r.value === "string" && r.value.length > 0 ? r.value : void 0;
};
var decideMoveAck = (results, now) => {
  var _a2;
  if (results.some((r) => r.outcome === "applied")) return { action: "clear" };
  const rejected = results.find(
    (r) => r.outcome === "condition-false" || r.outcome === "stale"
  );
  if (!rejected) return { action: "skip" };
  return {
    action: "set",
    value: {
      ...rejected.rule !== void 0 ? { rule: rejected.rule } : {},
      reason: (_a2 = rejected.reason) != null ? _a2 : GENERIC_REJECTION,
      at: now
    }
  };
};
var sameAck = (current, next) => current !== null && current.rule === next.rule && current.reason === next.reason;
var applyMoveAck = async (db, rail, typeName, recordId, results, now) => {
  var _a2;
  const decision = decideMoveAck(results, now);
  if (decision.action === "skip") return;
  const ackRef = db.ref(`${rail}/${typeName}/${recordId}/${MOVE_ACK_SUBKEY}`);
  const current = (_a2 = (await ackRef.once("value")).val()) != null ? _a2 : null;
  if (decision.action === "clear") {
    if (current === null) return;
    await ackRef.set(null);
    return;
  }
  if (sameAck(current, decision.value)) return;
  await ackRef.set(decision.value);
};
var runTransition = async (deps, params, event) => {
  const { db } = deps;
  const base = `orgiggly/orgs/${params.username}/${params.orgName}`;
  const isDelete = event.after === null || event.after === void 0;
  const cascaded = isDelete ? await cascadeDelete(db, base, event) : [];
  if (isDelete && !event.inboxUid) await reapNotifyMarkers(db, base, event);
  const mirrorSnap = await db.ref(`${base}/transitionRules/byWatchType/${event.typeName}`).once("value");
  const mirrorRules = mirrorSnap.val();
  if (!Array.isArray(mirrorRules) || mirrorRules.length === 0) {
    return cascaded.length > 0 ? { results: cascaded } : single("no-rules");
  }
  let rules = mirrorRules.filter((r) => r.enabled !== false);
  if (isDelete) rules = rules.filter((r) => r.onDelete === true);
  if (rules.length === 0) {
    if (cascaded.length > 0) return { results: cascaded };
    return single(isDelete ? "ignored" : "no-rules");
  }
  const now = deps.now();
  const seed = deps.seed();
  const rail = inputRail(base, event);
  const record10 = event.inboxUid && event.after !== null && typeof event.after === "object" ? { ...event.after, ownerUid: event.inboxUid } : event.after;
  const { recordId } = event;
  const results = [];
  for (const rule of rules) {
    results.push(
      await runRule(db, base, rail, event.typeName, { record: record10, recordId, now, seed }, params, rule, deps.afterApplied)
    );
  }
  await applyMoveAck(db, rail, event.typeName, recordId, results, now);
  return { results: [...cascaded, ...results] };
};
var cascadeDelete = async (db, base, event) => {
  let links = null;
  let privateTypes = null;
  try {
    const [linksSnap, privateSnap] = await Promise.all([
      db.ref(`${base}/transitionRules/childrenOf/${event.typeName}`).once("value"),
      db.ref(`${base}/transitionRules/privateOf/${event.typeName}`).once("value")
    ]);
    links = linksSnap.val();
    privateTypes = privateSnap.val();
  } catch (err) {
    return [
      { rule: CASCADE_RULE, outcome: "cascade-failed", detail: String(err) }
    ];
  }
  const results = [];
  if (Array.isArray(privateTypes) && privateTypes.length > 0) {
    results.push(
      ...await reapPrivate(db, base, event.recordId, privateTypes)
    );
  }
  if (!Array.isArray(links) || links.length === 0) return results;
  let inbox = null;
  try {
    inbox = (await db.ref(`${base}/realtimeInbox`).once("value")).val();
  } catch (err) {
    results.push({
      rule: CASCADE_RULE,
      outcome: "cascade-failed",
      detail: `inbox: ${String(err)}`
    });
  }
  for (const link of links) {
    if (!link || typeof link.type !== "string" || typeof link.idField !== "string") {
      continue;
    }
    if (inbox && typeof inbox === "object") {
      results.push(...await reapInboxChildren(db, base, inbox, link, event.recordId));
    }
    try {
      const coll = (await db.ref(`${base}/realtime/${link.type}`).once("value")).val();
      if (!coll) continue;
      let deleted = 0;
      for (const [childId, child] of Object.entries(coll)) {
        if (child === null || typeof child !== "object") continue;
        if (child[link.idField] !== event.recordId) {
          continue;
        }
        await db.ref(`${base}/realtime/${link.type}/${childId}`).remove();
        deleted += 1;
      }
      if (deleted > 0) {
        results.push({
          rule: CASCADE_RULE,
          outcome: "cascaded",
          detail: `${link.type} x${deleted}`
        });
      }
    } catch (err) {
      results.push({
        rule: CASCADE_RULE,
        outcome: "cascade-failed",
        detail: `${link.type}: ${String(err)}`
      });
    }
  }
  return results;
};
var reapNotifyMarkers = async (db, base, event) => {
  try {
    const ref = db.ref(`${base}/pushSent/notify/${event.typeName}/${event.recordId}`);
    if ((await ref.once("value")).val() !== null) await ref.remove();
  } catch (err) {
    console.error(`[realtimeTransition] notify-marker reap failed: ${String(err)}`);
  }
};
var reapInboxChildren = async (db, base, inbox, link, parentId) => {
  let deleted = 0;
  try {
    for (const [uid, byType] of Object.entries(inbox)) {
      if (byType === null || typeof byType !== "object") continue;
      const coll = byType[link.type];
      if (coll === null || typeof coll !== "object") continue;
      for (const [childId, child] of Object.entries(coll)) {
        if (child === null || typeof child !== "object") continue;
        if (child[link.idField] !== parentId) continue;
        await db.ref(`${base}/realtimeInbox/${uid}/${link.type}/${childId}`).remove();
        deleted += 1;
      }
    }
  } catch (err) {
    return [
      { rule: CASCADE_RULE, outcome: "cascade-failed", detail: `inbox ${link.type}: ${String(err)}` }
    ];
  }
  return deleted > 0 ? [{ rule: CASCADE_RULE, outcome: "cascaded", detail: `inbox ${link.type} x${deleted}` }] : [];
};
var reapPrivate = async (db, base, recordId, privateTypes) => {
  var _a2;
  try {
    const rail = (await db.ref(`${base}/realtimePrivate`).once("value")).val();
    if (!rail || typeof rail !== "object") return [];
    const counts = {};
    for (const [key, byType] of Object.entries(rail)) {
      if (byType === null || typeof byType !== "object") continue;
      for (const type14 of privateTypes) {
        if (typeof type14 !== "string") continue;
        const recs = byType[type14];
        if (recs === null || typeof recs !== "object") continue;
        if (!(recordId in recs)) continue;
        await db.ref(`${base}/realtimePrivate/${key}/${type14}/${recordId}`).remove();
        counts[type14] = ((_a2 = counts[type14]) != null ? _a2 : 0) + 1;
      }
    }
    return Object.entries(counts).map(([type14, n]) => ({
      rule: CASCADE_RULE,
      outcome: "cascaded",
      detail: `private ${type14} x${n}`
    }));
  } catch (err) {
    return [
      {
        rule: CASCADE_RULE,
        outcome: "cascade-failed",
        detail: `private: ${String(err)}`
      }
    ];
  }
};
var runRule = async (db, base, rail, typeName, hoisted, params, rule, afterApplied) => {
  var _a2, _b;
  const { record: record10, recordId, now, seed } = hoisted;
  const label2 = rule.label;
  if (!hasFormula(rule.targetId)) {
    return { rule: label2, outcome: "bad-target-id", detail: "rule has no targetId" };
  }
  const functions = (_a2 = rule.functions) != null ? _a2 : {};
  const consts = (_b = rule.consts) != null ? _b : {};
  const preCtx = buildRuleContext({ record: record10, recordId, target: null, records: [], params, now, seed, functions, consts, private: {}, ruleLabel: label2 });
  const tid = evalTargetId(rule.targetId, preCtx);
  if (!tid.ok) return { rule: label2, outcome: tid.outcome, detail: tid.detail };
  const targetRef = db.ref(`${base}/realtime/${rule.targetType}/${tid.value}`);
  const targetVal = (await targetRef.once("value")).val();
  if (targetVal === null || targetVal === void 0) {
    return { rule: label2, outcome: "missing-target" };
  }
  let records = [];
  if (rule.needsRecords) {
    const coll = (await db.ref(`${rail}/${typeName}`).once("value")).val();
    records = coll ? Object.values(coll) : [];
  }
  let privateRecords = {};
  if (hasFormula(rule.privateReadUids)) {
    if (!hasFormula(rule.privateType)) {
      return {
        rule: label2,
        outcome: "formula-error",
        detail: "`privateReadUids` requires `privateType`"
      };
    }
    const keysCtx = buildRuleContext({ record: record10, recordId, target: targetVal, records, params, now, seed, functions, consts, private: {}, ruleLabel: label2 });
    const keysRes = evalRuleField(rule.privateReadUids, keysCtx);
    if (!keysRes.ok) {
      return { rule: label2, outcome: keysRes.outcome, detail: keysRes.detail };
    }
    const keys = normalizePrivateReadKeys(keysRes.value);
    if (!keys.ok) {
      return { rule: label2, outcome: "formula-error", detail: keys.detail };
    }
    privateRecords = await loadPrivateRecords(db, base, rule.privateType, tid.value, keys.keys);
  }
  const contextFor = (target) => buildRuleContext({ record: record10, recordId, target, records, params, now, seed, functions, consts, private: privateRecords, ruleLabel: label2 });
  const guards = hasFormula(rule.condition) ? [rule.condition] : [];
  const preGuardCtx = contextFor(targetVal);
  const pre = evalGuards(guards, preGuardCtx);
  if (!pre.ok) {
    return { rule: label2, outcome: "formula-error", detail: pre.detail };
  }
  if (!pre.passed) {
    const reason = evalRejectionReason(rule, preGuardCtx);
    return {
      rule: label2,
      outcome: "condition-false",
      ...reason !== void 0 ? { reason } : {}
    };
  }
  if (!hasFormula(rule.next)) {
    return { rule: label2, outcome: "formula-error", detail: "rule has no next" };
  }
  const privateWrite = hasFormula(rule.nextPrivate) && hasFormula(rule.privateType) ? {
    nextPrivateSrc: rule.nextPrivate,
    orgRootRef: db.ref(base),
    privateType: rule.privateType,
    // The public target's own id: `Game/g1` ↔ each player's `Hand/g1`.
    targetId: tid.value
  } : void 0;
  const committed = await commitGuarded({
    targetRef,
    snapshot: targetVal,
    guards,
    nextSrc: rule.next,
    contextFor,
    ...privateWrite ? { privateWrite } : {}
  });
  if (committed.outcome === "stale") {
    const reason = evalRejectionReason(rule, contextFor(committed.live));
    return {
      rule: label2,
      outcome: "stale",
      ...reason !== void 0 ? { reason } : {}
    };
  }
  if (committed.outcome !== "applied") return { rule: label2, ...committed };
  if (!afterApplied) return { rule: label2, outcome: "applied" };
  const committedCtx = buildRuleContext({
    record: record10,
    recordId,
    target: committed.committed,
    records,
    params,
    now,
    seed,
    functions,
    consts,
    private: {},
    ruleLabel: label2
  });
  const detail = await afterApplied({
    db,
    params,
    rule,
    // Present by now: the target read above used it to build its path.
    targetType: String(rule.targetType),
    targetId: tid.value,
    ctx: committedCtx,
    now
  });
  return { rule: label2, outcome: "applied", ...detail ? { detail } : {} };
};

// src/orgicore/transitions/arming.ts
var PAST_REARM_FLOOR_MS = 6e4;
var ARM_HORIZON_MS = 29 * 24 * 60 * 60 * 1e3;
var armedMarkerPath = (base, typeName, recordId, ruleKey) => `${base}/transitionRules/armed/${typeName}/${recordId}/${ruleKey}`;
var buildLastArm = (event, result, at) => {
  const noteworthy = result.results.some(
    (r) => r.outcome === "armed" || r.outcome === "formula-error" || r.outcome === "enqueue-error" || r.outcome === "marker-read-error"
  );
  if (!noteworthy) return null;
  return {
    typeName: event.typeName,
    recordId: event.recordId,
    at,
    results: result.results.map((r) => ({
      ruleKey: r.ruleKey,
      outcome: r.outcome,
      ...r.rule !== void 0 ? { rule: r.rule } : {},
      ...r.dueAt !== void 0 ? { dueAt: r.dueAt } : {},
      ...r.detail !== void 0 ? { detail: r.detail } : {}
    }))
  };
};
var hasFormula2 = (v) => typeof v === "string" && v.length > 0;
var collectionRecords = (coll) => coll && typeof coll === "object" ? Object.values(coll).filter((v) => v !== null && v !== void 0) : [];
var armRecordTimers = async (deps, params, event, rules, readRecords, knownMarkers) => {
  var _a2, _b;
  const { db } = deps;
  if (event.after === null || event.after === void 0) {
    return { results: [] };
  }
  const base = `orgiggly/orgs/${params.username}/${params.orgName}`;
  const now = deps.now();
  const seed = deps.seed();
  const results = [];
  for (let i = 0; i < rules.length; i += 1) {
    const rule = rules[i];
    if (!rule || typeof rule !== "object" || rule.enabled === false) continue;
    const ruleKey = String(i);
    const label2 = rule.label;
    const tag = (outcome, extra = {}) => ({
      ruleKey,
      ...label2 !== void 0 ? { rule: label2 } : {},
      outcome,
      ...extra.dueAt !== void 0 ? { dueAt: extra.dueAt } : {},
      ...extra.detail !== void 0 ? { detail: extra.detail } : {}
    });
    if (rule.perpetual === true && event.recordId !== rule.targetId) {
      results.push(tag("skipped", { detail: "not this schedule's record" }));
      continue;
    }
    if (!hasFormula2(rule.dueAt)) {
      results.push(tag("formula-error", { detail: "timer rule has no dueAt" }));
      continue;
    }
    const ctx = buildTimerRuleContext({
      target: event.after,
      records: rule.needsRecords ? await readRecords() : [],
      params,
      // The private READ path (#1369) is onWrite-only in v1; the closing gate
      // rejects `privateReadUids` on an onTimer rule, so an empty map here can
      // never silently starve a rule that asked for private state.
      private: {},
      now,
      seed,
      // #1546 — a `dueAt` may call an org function, so arming must bind the
      // rule's stamped closure or the deadline silently evaluates to
      // `undefined` and G1 refuses to arm, with nothing logged.
      functions: (_a2 = rule.functions) != null ? _a2 : {},
      // #2317 — the same for a const read in `dueAt` (`$now + skipAfterMs`).
      consts: (_b = rule.consts) != null ? _b : {},
      ruleLabel: rule.label
    });
    const evaluated = evalRuleField(rule.dueAt, ctx);
    if (!evaluated.ok) {
      results.push(tag("formula-error", { detail: evaluated.detail }));
      continue;
    }
    const value = evaluated.value;
    if (value === null || value === void 0 || value === false) {
      results.push(tag("skipped"));
      continue;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
      results.push(
        tag("formula-error", {
          detail: `dueAt must evaluate to a finite number (epoch ms) or null/undefined/false; got ${typeof value === "number" ? String(value) : typeof value}`
        })
      );
      continue;
    }
    const dueAt = value;
    const markerRef = db.ref(
      armedMarkerPath(base, event.typeName, event.recordId, ruleKey)
    );
    let marker;
    if (knownMarkers) {
      marker = ruleKey in knownMarkers ? knownMarkers[ruleKey] : null;
    } else {
      try {
        marker = (await markerRef.once("value")).val();
      } catch (err) {
        results.push(tag("marker-read-error", { dueAt, detail: String(err) }));
        continue;
      }
    }
    if (marker === dueAt) {
      results.push(tag("already-armed", { dueAt }));
      continue;
    }
    const isReArm = marker !== null && marker !== void 0;
    const scheduleAtMs = dueAt <= now && isReArm ? now + PAST_REARM_FLOOR_MS : Math.max(now, Math.min(dueAt, now + ARM_HORIZON_MS));
    try {
      await deps.enqueue(
        {
          username: params.username,
          orgName: params.orgName,
          typeName: event.typeName,
          recordId: event.recordId,
          ruleKey,
          dueAtMs: dueAt
        },
        scheduleAtMs
      );
    } catch (err) {
      results.push(tag("enqueue-error", { dueAt, detail: String(err) }));
      continue;
    }
    try {
      await markerRef.set(dueAt);
      results.push(tag("armed", { dueAt }));
    } catch (err) {
      results.push(tag("armed", { dueAt, detail: `marker write failed: ${String(err)}` }));
    }
  }
  return { results };
};
var writeLastArm = async (db, params, last2) => {
  const base = `orgiggly/orgs/${params.username}/${params.orgName}`;
  try {
    await db.ref(`${base}/transitionRules/lastArm`).set(last2);
  } catch (err) {
    console.error(
      `[armTimers] lastArm write failed for ${params.username}/${params.orgName}: ${String(err)}`
    );
  }
};
var armTimers = async (deps, params, event) => {
  const { db } = deps;
  if (event.after === null || event.after === void 0) {
    return { results: [] };
  }
  const base = `orgiggly/orgs/${params.username}/${params.orgName}`;
  const indexSnap = await db.ref(`${base}/transitionRules/byTimerType/${event.typeName}`).once("value");
  const timerRules = indexSnap.val();
  if (!Array.isArray(timerRules) || timerRules.length === 0) {
    return { results: [] };
  }
  let records = null;
  const readRecords = async () => {
    if (records !== null) return records;
    const coll = (await db.ref(`${base}/realtime/${event.typeName}`).once("value")).val();
    records = collectionRecords(coll);
    return records;
  };
  const result = await armRecordTimers(
    deps,
    params,
    event,
    timerRules,
    readRecords
  );
  const last2 = buildLastArm(event, result, deps.now());
  if (last2) await writeLastArm(db, params, last2);
  return result;
};

// src/orgicore/transitions/timerFire.ts
var buildLastFire = (payload, result, at) => {
  if (result.outcome === "stale-drop") return null;
  return {
    typeName: payload.typeName,
    recordId: payload.recordId,
    ruleKey: payload.ruleKey,
    at,
    outcome: result.outcome,
    ...result.rule !== void 0 ? { rule: result.rule } : {},
    ...result.dueAt !== void 0 ? { dueAt: result.dueAt } : {},
    ...result.detail !== void 0 ? { detail: result.detail } : {}
  };
};
var hasFormula3 = (v) => typeof v === "string" && v.length > 0;
var isPathSafe = (v) => typeof v === "string" && v.length > 0 && !/[/.#$[\]]/.test(v);
var isTimerTaskPayload = (v) => {
  if (v === null || typeof v !== "object") return false;
  const p = v;
  return isPathSafe(p.username) && isPathSafe(p.orgName) && isPathSafe(p.typeName) && isPathSafe(p.recordId) && isPathSafe(p.ruleKey) && typeof p.dueAtMs === "number" && Number.isFinite(p.dueAtMs);
};
var buildArchiveCopy = (rule, ctx, record10, recordId) => {
  let data = record10;
  if (hasFormula3(rule.archiveData)) {
    const r = evalRuleField(rule.archiveData, ctx);
    if (!r.ok) return { ok: false, detail: `archiveData: ${r.detail}` };
    const v = r.value;
    if (typeof v !== "object" || v === null || Array.isArray(v)) {
      return {
        ok: false,
        detail: `archiveData must evaluate to an object, got ${Array.isArray(v) ? "an array" : v === null ? "null" : typeof v}`
      };
    }
    data = v;
  }
  let id = recordId;
  if (hasFormula3(rule.archiveId)) {
    const r = evalRuleField(rule.archiveId, ctx);
    if (!r.ok) return { ok: false, detail: `archiveId: ${r.detail}` };
    if (typeof r.value !== "string" || r.value.length === 0 || r.value.includes("/")) {
      return {
        ok: false,
        detail: `archiveId must evaluate to a non-empty string with no "/", got ${JSON.stringify(r.value)}`
      };
    }
    id = r.value;
  }
  return { ok: true, record: { ...data, id } };
};
var fireTimer = async (deps, payload) => {
  const { db } = deps;
  const base = `orgiggly/orgs/${payload.username}/${payload.orgName}`;
  const markerRef = db.ref(
    armedMarkerPath(base, payload.typeName, payload.recordId, payload.ruleKey)
  );
  const now = deps.now();
  const seed = deps.seed();
  const ruleSnap = await db.ref(
    `${base}/transitionRules/byTimerType/${payload.typeName}/${payload.ruleKey}`
  ).once("value");
  const rule = ruleSnap.val();
  const ruleGone = !rule || typeof rule !== "object" || rule.enabled === false;
  const ruleLabel = ruleGone ? void 0 : rule.label;
  const settle = async (partial12, marker) => {
    if (marker === "delete") {
      await markerRef.set(null);
    }
    const result = {
      outcome: partial12.outcome,
      ...ruleLabel !== void 0 ? { rule: ruleLabel } : {},
      ...partial12.dueAt !== void 0 ? { dueAt: partial12.dueAt } : {},
      ...partial12.detail !== void 0 ? { detail: partial12.detail } : {}
    };
    const last2 = buildLastFire(payload, result, now);
    if (last2) {
      try {
        await db.ref(`${base}/transitionRules/lastFire`).set(last2);
      } catch (err) {
        console.error(
          `[timerFire] lastFire write failed for ${payload.username}/${payload.orgName}: ${String(err)}`
        );
      }
    }
    return result;
  };
  if (ruleGone) {
    return settle({ outcome: "no-rule" }, "delete");
  }
  const targetRef = db.ref(
    `${base}/realtime/${payload.typeName}/${payload.recordId}`
  );
  const record10 = (await targetRef.once("value")).val();
  if (record10 === null || record10 === void 0) {
    return settle({ outcome: "record-gone" }, "delete");
  }
  let records = [];
  if (rule.needsRecords) {
    const coll = (await db.ref(`${base}/realtime/${payload.typeName}`).once("value")).val();
    records = collectionRecords(coll);
  }
  const contextFor = (target) => {
    var _a2, _b;
    return buildTimerRuleContext({
      target,
      records,
      params: { username: payload.username, orgName: payload.orgName },
      // onWrite-only in v1 — see arming.ts and the `transition-private-read-timer`
      // gate check.
      private: {},
      now,
      seed,
      // #1546 — the fire handler RE-DERIVES dueAt to compare against the
      // payload's discriminator, so it must resolve the same functions arming
      // did; otherwise a helper-built deadline differs and every task
      // stale-drops.
      functions: (_a2 = rule.functions) != null ? _a2 : {},
      // #2317 — and the same consts, for the same re-derivation reason.
      consts: (_b = rule.consts) != null ? _b : {},
      ruleLabel: rule.label
    });
  };
  const ctx = contextFor(record10);
  if (!hasFormula3(rule.dueAt)) {
    return settle(
      { outcome: "formula-error", detail: "timer rule has no dueAt" },
      "delete"
    );
  }
  const evaluated = evalRuleField(rule.dueAt, ctx);
  if (!evaluated.ok) {
    return settle(
      { outcome: "formula-error", detail: evaluated.detail },
      "delete"
    );
  }
  const value = evaluated.value;
  if (value === null || value === void 0 || value === false) {
    return settle({ outcome: "due-null" }, "delete");
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return settle(
      {
        outcome: "formula-error",
        detail: `dueAt must evaluate to a finite number (epoch ms) or null/undefined/false; got ${typeof value === "number" ? String(value) : typeof value}`
      },
      "delete"
    );
  }
  const dueAt = value;
  if (dueAt !== payload.dueAtMs) {
    return {
      outcome: "stale-drop",
      ...ruleLabel !== void 0 ? { rule: ruleLabel } : {},
      dueAt
    };
  }
  if (now < dueAt) {
    await deps.enqueue(payload, Math.min(dueAt, now + ARM_HORIZON_MS));
    return settle({ outcome: "hopped", dueAt }, "keep");
  }
  const guards = hasFormula3(rule.condition) ? [rule.condition] : [];
  const pre = evalGuards(guards, ctx);
  if (!pre.ok) {
    return settle(
      { outcome: "formula-error", detail: pre.detail, dueAt },
      "delete"
    );
  }
  if (!pre.passed) {
    return settle({ outcome: "condition-false", dueAt }, "delete");
  }
  if (!hasFormula3(rule.next)) {
    return settle(
      { outcome: "formula-error", detail: "rule has no next", dueAt },
      "delete"
    );
  }
  const archives = rule.archive === true && rule.endOfLife === true;
  if (archives) {
    if (!deps.archive) {
      return settle(
        { outcome: "formula-error", detail: "archive: no storage client", dueAt },
        "delete"
      );
    }
    const copy = buildArchiveCopy(
      rule,
      ctx,
      record10,
      payload.recordId
    );
    if (!copy.ok) {
      return settle(
        { outcome: "formula-error", detail: copy.detail, dueAt },
        "delete"
      );
    }
    try {
      await deps.archive({
        orgId: `${payload.username}/${payload.orgName}`,
        typeName: hasFormula3(rule.archiveType) ? rule.archiveType : payload.typeName,
        record: copy.record
      });
    } catch (err) {
      await settle(
        {
          outcome: "archive-failed",
          detail: err instanceof Error ? err.message : String(err),
          dueAt
        },
        "keep"
      );
      throw err;
    }
  }
  const committed = await commitGuarded({
    targetRef,
    snapshot: record10,
    guards,
    nextSrc: rule.next,
    contextFor
  });
  if (committed.outcome === "applied") {
    const sideEffects = deps.afterApplied ? await deps.afterApplied({
      db,
      params: { username: payload.username, orgName: payload.orgName },
      rule,
      targetType: payload.typeName,
      targetId: payload.recordId,
      ctx: contextFor(committed.committed),
      now
    }) : void 0;
    const detail = [archives ? "archived" : void 0, sideEffects].filter(Boolean).join("; ");
    return settle({ outcome: "applied", dueAt, ...detail ? { detail } : {} }, "keep");
  }
  if (committed.outcome === "stale") {
    return settle({ outcome: "stale", dueAt }, "delete");
  }
  return settle(
    { outcome: "formula-error", detail: committed.detail, dueAt },
    "delete"
  );
};

// src/orgicore/transitions/reconcile.ts
var emptyReconcileSummary = () => ({
  scanned: 0,
  armed: 0,
  alreadyArmed: 0,
  skipped: 0,
  errors: 0,
  swept: 0,
  seeded: 0,
  failedTypes: []
});
var countMarkers = (node2) => {
  if (!node2 || typeof node2 !== "object") return 0;
  return Object.values(node2).reduce(
    (n, child) => n + (child && typeof child === "object" ? Object.keys(child).length : 0),
    0
  );
};
var entriesOf = (node2) => node2 && typeof node2 === "object" ? Object.entries(node2) : [];
var childOf = (node2, key) => node2 && typeof node2 === "object" ? node2[key] : void 0;
var markersFor = (armedForType, recordId) => {
  const node2 = childOf(armedForType, recordId);
  return node2 && typeof node2 === "object" ? node2 : {};
};
var seedPerpetual = async (db, base, params, typeName, rule, now, seed, summary) => {
  var _a2, _b, _c;
  const id = rule.targetId;
  if (typeof id !== "string" || id.length === 0) return null;
  const fail3 = (detail) => {
    summary.errors += 1;
    console.error(
      `[transitionRulesMirror] seed failed for ${params.username}/${params.orgName} ${typeName}/${id}: ${detail}`
    );
    return null;
  };
  if (typeof rule.seed !== "string" || rule.seed.length === 0) {
    return fail3("perpetual rule has no seed");
  }
  const evaluated = evalRuleField(
    rule.seed,
    buildTimerRuleContext({
      target: void 0,
      records: [],
      params,
      private: {},
      now,
      seed,
      functions: (_a2 = rule.functions) != null ? _a2 : {},
      consts: (_b = rule.consts) != null ? _b : {},
      ruleLabel: rule.label
    })
  );
  if (!evaluated.ok) return fail3((_c = evaluated.detail) != null ? _c : "formula error");
  const value = pruneForRtdb(evaluated.value);
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return fail3(`seed must evaluate to an object; got ${Array.isArray(value) ? "an array" : typeof value}`);
  }
  const record10 = value;
  try {
    await db.ref(`${base}/realtime/${typeName}/${id}`).set(record10);
  } catch (err) {
    return fail3(String(err));
  }
  summary.seeded += 1;
  return { id, record: record10 };
};
var tally = (summary, result) => {
  for (const r of result.results) {
    if (r.outcome === "armed") summary.armed += 1;
    else if (r.outcome === "already-armed") summary.alreadyArmed += 1;
    else if (r.outcome === "skipped") summary.skipped += 1;
    else summary.errors += 1;
  }
};
var reconcileTimers = async (deps, params, mirror) => {
  var _a2;
  const { db } = deps;
  const base = `orgiggly/orgs/${params.username}/${params.orgName}`;
  const summary = emptyReconcileSummary();
  const now = deps.now();
  const seed = deps.seed();
  const armDeps = { ...deps, now: () => now, seed: () => seed };
  let armedTree = null;
  let armedReadOk = true;
  try {
    armedTree = (await db.ref(`${base}/transitionRules/armed`).once("value")).val();
  } catch (err) {
    armedReadOk = false;
    console.error(
      `[transitionRulesMirror] armed/ read failed for ${params.username}/${params.orgName}: ${String(err)}`
    );
  }
  const liveIds = /* @__PURE__ */ new Map();
  let last2 = null;
  for (const [typeName, rules] of Object.entries(mirror.byTimerType)) {
    let collection;
    try {
      collection = (await db.ref(`${base}/realtime/${typeName}`).once("value")).val();
    } catch (err) {
      summary.failedTypes.push(typeName);
      console.error(
        `[transitionRulesMirror] reconcile read failed for ${params.username}/${params.orgName} type=${typeName}: ${String(err)}`
      );
      continue;
    }
    const records = entriesOf(collection);
    for (const rule of rules) {
      if (!rule || rule.perpetual !== true) continue;
      const present = records.some(
        ([id, v]) => id === rule.targetId && v != null
      );
      if (present) continue;
      const seeded = await seedPerpetual(
        db,
        base,
        params,
        typeName,
        rule,
        now,
        seed,
        summary
      );
      if (seeded) records.push([seeded.id, seeded.record]);
    }
    liveIds.set(
      typeName,
      new Set(records.filter(([, v]) => v != null).map(([id]) => id))
    );
    const siblings = collectionRecords(collection);
    const readRecords = async () => siblings;
    const armedForType = armedReadOk ? childOf(armedTree, typeName) : void 0;
    for (const [recordId, record10] of records) {
      if (record10 === null || record10 === void 0) continue;
      summary.scanned += 1;
      const event = { typeName, recordId, after: record10 };
      try {
        const result = await armRecordTimers(
          armDeps,
          params,
          event,
          rules,
          readRecords,
          armedReadOk ? markersFor(armedForType, recordId) : void 0
        );
        tally(summary, result);
        last2 = (_a2 = buildLastArm(event, result, now)) != null ? _a2 : last2;
      } catch (err) {
        summary.errors += 1;
        console.error(
          `[transitionRulesMirror] reconcile arm failed for ${params.username}/${params.orgName} type=${typeName} record=${recordId}: ${String(err)}`
        );
      }
    }
  }
  if (armedReadOk) {
    for (const [typeName, byRecord] of entriesOf(armedTree)) {
      const rules = mirror.byTimerType[typeName];
      if (!Array.isArray(rules) || rules.length === 0) {
        const n = countMarkers(byRecord);
        if (n === 0) continue;
        try {
          await db.ref(`${base}/transitionRules/armed/${typeName}`).set(null);
          summary.swept += n;
        } catch (err) {
          console.error(
            `[transitionRulesMirror] sweep failed for ${params.username}/${params.orgName} type=${typeName}: ${String(err)}`
          );
        }
        continue;
      }
      const live = liveIds.get(typeName);
      if (!live) continue;
      for (const [recordId, byRule] of entriesOf(byRecord)) {
        if (byRule === null || byRule === void 0) continue;
        const keys = Object.keys(byRule);
        const gone = !live.has(recordId);
        const stale = gone ? keys : keys.filter((k) => !/^\d+$/.test(k) || Number(k) >= rules.length);
        if (stale.length === 0) continue;
        const paths = gone ? [`${base}/transitionRules/armed/${typeName}/${recordId}`] : stale.map(
          (k) => `${base}/transitionRules/armed/${typeName}/${recordId}/${k}`
        );
        for (const p of paths) {
          try {
            await db.ref(p).set(null);
            summary.swept += gone ? stale.length : 1;
          } catch (err) {
            console.error(
              `[transitionRulesMirror] sweep failed for ${params.username}/${params.orgName} at ${p}: ${String(err)}`
            );
          }
        }
      }
    }
  }
  if (last2) await writeLastArm(db, params, last2);
  return summary;
};

// src/orgicore/utils/test/rtdbCoerce.ts
var rtdbCoerce = (v) => {
  if (v === null || typeof v !== "object") return v;
  const entries = Object.entries(v).filter(([, val]) => !(Array.isArray(v) && val === null)).map(([k, val]) => [k, rtdbCoerce(val)]);
  if (Array.isArray(v) && entries.length === 0) return [];
  const keys = entries.map(([k]) => k);
  const allInt = keys.length > 0 && keys.every((k) => /^(0|[1-9]\d*)$/.test(k));
  if (allInt && Math.max(...keys.map(Number)) < 2 * keys.length) {
    const arr = [];
    for (const [k, val] of entries) arr[Number(k)] = val;
    return arr;
  }
  return Object.fromEntries(entries);
};

// src/orgicore/utils/test/rtdbFake.ts
var splitPath = (path) => path.split("/").filter(Boolean);
var incrementOf = (value) => {
  if (!value || typeof value !== "object") return void 0;
  const sv = value[".sv"];
  if (!sv || typeof sv !== "object") return void 0;
  const n = sv.increment;
  return typeof n === "number" ? n : void 0;
};
var makeFakeDb = (initial = {}, options = {}) => {
  const root = JSON.parse(JSON.stringify(initial));
  const reads = [];
  let pushCount = 0;
  const writes = [];
  const hooks = {};
  const getAt = (segs) => {
    let cur = root;
    for (const s of segs) {
      if (!cur || typeof cur !== "object") return void 0;
      cur = cur[s];
    }
    return cur;
  };
  const setAt = (segs, value) => {
    let cur = root;
    for (const s of segs.slice(0, -1)) {
      if (!cur[s] || typeof cur[s] !== "object") cur[s] = {};
      cur = cur[s];
    }
    cur[segs[segs.length - 1]] = value;
  };
  const removeAt = (segs) => {
    const parent = getAt(segs.slice(0, -1));
    if (parent && typeof parent === "object") {
      delete parent[segs[segs.length - 1]];
    }
    if (options.faithfulDeletes && segs.length > 1) {
      const up = segs.slice(0, -1);
      const node2 = getAt(up);
      if (node2 && typeof node2 === "object" && Object.keys(node2).length === 0) {
        removeAt(up);
      }
    }
  };
  const normalize3 = (value) => {
    if (value === null || value === void 0) return null;
    if (typeof value !== "object") return value;
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const n = normalize3(v);
      if (n !== null) out[k] = n;
    }
    return Object.keys(out).length === 0 ? null : out;
  };
  const writeAt = (segs, raw) => {
    const step = incrementOf(raw);
    const current = step === void 0 ? void 0 : getAt(segs);
    const value = step === void 0 ? raw : (typeof current === "number" ? current : 0) + step;
    if (!options.faithfulDeletes) {
      setAt(segs, value);
      return;
    }
    const n = normalize3(value);
    if (n === null) removeAt(segs);
    else setAt(segs, n);
  };
  const readOut = (v) => options.coerceReads && v !== void 0 ? rtdbCoerce(JSON.parse(JSON.stringify(v))) : v;
  const ref = (path) => {
    const segs = splitPath(path);
    return {
      once: async () => {
        var _a2;
        reads.push(path);
        if ((_a2 = hooks.failRead) == null ? void 0 : _a2.call(hooks, path)) {
          throw new Error(`fake read failure at ${path}`);
        }
        return {
          val: () => {
            const v = readOut(getAt(segs));
            return v === void 0 ? null : v;
          },
          exists: () => getAt(segs) !== void 0
        };
      },
      // KNOWN INFIDELITY: real RTDB treats `set(null)` as a DELETE (the key
      // goes away; `Object.keys` on the parent no longer lists it), whereas
      // this stores a literal `null` tombstone. Harmless where a caller only
      // ever re-reads via `.val()` — which returns `null` either way, so the
      // timer engine's `set(null)` marker deletes are faithfully covered —
      // but a test that ENUMERATES the parent's keys sees the tombstone. Use
      // `remove()` when the deletion needs to be faithful at that level (the
      // video meter's hold release does, since the solvency sum walks the
      // payer's holds). Not "fixed" here because several suites' assertions
      // pin the tombstone (`toBeNull()` on the raw store). (#1555)
      set: async (value) => {
        var _a2;
        writes.push(path);
        if ((_a2 = hooks.failWrite) == null ? void 0 : _a2.call(hooks, path)) {
          throw new Error(`fake write failure at ${path}`);
        }
        writeAt(segs, value);
      },
      // A child under a fresh, chronologically sortable key (the AI spend
      // log, #469). Real `push()` also works without a value; no caller here
      // needs that form.
      push: async (value) => {
        var _a2;
        const key = `-fake${String(++pushCount).padStart(6, "0")}`;
        const childPath = `${path}/${key}`;
        writes.push(childPath);
        if ((_a2 = hooks.failWrite) == null ? void 0 : _a2.call(hooks, childPath)) {
          throw new Error(`fake write failure at ${childPath}`);
        }
        writeAt([...segs, key], value);
        return { key };
      },
      remove: async () => {
        var _a2;
        writes.push(path);
        if ((_a2 = hooks.failWrite) == null ? void 0 : _a2.call(hooks, path)) {
          throw new Error(`fake write failure at ${path}`);
        }
        removeAt(segs);
      },
      // RTDB `update` is a multi-path set: each KEY of the object is written
      // at `path/key` (a `/`-containing key is a deeper path), and every key
      // NOT mentioned is left untouched. That difference from `set` is the
      // whole point of #1539's mirror-write fix — `set` on the mirror root
      // wiped the sibling `armed/`+`last*` nodes — so the fake models it
      // faithfully, including `null` meaning "remove this child".
      update: async (values) => {
        var _a2;
        writes.push(path);
        if ((_a2 = hooks.failWrite) == null ? void 0 : _a2.call(hooks, path)) {
          throw new Error(`fake write failure at ${path}`);
        }
        for (const [key, value] of Object.entries(values)) {
          const childSegs = [...segs, ...splitPath(key)];
          if (value === null) removeAt(childSegs);
          else writeAt(childSegs, value);
        }
      },
      transaction: async (fn) => {
        const snapshot = () => ({ val: () => readOut(getAt(segs)) });
        if (fn(null) === void 0) {
          return { committed: false, snapshot: snapshot() };
        }
        const hook = hooks.beforeTxCurrent;
        if (hook) {
          hooks.beforeTxCurrent = void 0;
          await hook(path);
        }
        const current = readOut(getAt(segs));
        const next = fn(current === void 0 ? null : current);
        if (next === void 0) {
          return { committed: false, snapshot: snapshot() };
        }
        writes.push(path);
        if (next === null) removeAt(segs);
        else writeAt(segs, next);
        return {
          committed: true,
          snapshot: {
            val: () => {
              var _a2;
              return options.coerceReads ? (_a2 = readOut(getAt(segs))) != null ? _a2 : null : next;
            }
          }
        };
      }
    };
  };
  return { db: { ref }, root, reads, writes, hooks };
};

// src/orgicore/utils/test/transitionSim.ts
var TransitionLoopError = class extends Error {
  constructor(chain) {
    super(
      `transition loop: more than ${chain.length - 1} re-fires
  ` + chain.join("\n  ")
    );
    this.name = "TransitionLoopError";
    this.chain = chain;
  }
};
var TimerLoopError = class extends Error {
  constructor(firings) {
    super(
      `timer loop: more than ${firings.length - 1} firings in one advanceTime
  ` + firings.slice(0, 10).join("\n  ") + (firings.length > 10 ? `
  \u2026 ${firings.length - 10} more` : "")
    );
    this.name = "TimerLoopError";
    this.firings = firings;
  }
};
var describeFirings = (firings) => firings.map(
  (f) => {
    var _a2, _b;
    return `${f.typeName}/${f.recordId}[${f.ruleKey}]: ` + (f.result ? `${(_a2 = f.result.rule) != null ? _a2 : "-"}=${f.result.outcome}` : `threw ${(_b = f.error) != null ? _b : ""}`);
  }
);
var describeEvents = (events) => events.map(
  (e) => `${e.inboxUid ? `inbox:${e.inboxUid}:` : ""}${e.typeName}/${e.recordId}: ` + e.results.map((r) => {
    var _a2;
    return `${(_a2 = r.rule) != null ? _a2 : "-"}=${r.outcome}`;
  }).join(", ")
);
var SIM_EPOCH = 176e10;
var DEFAULT_MAX_REFIRES = 20;
var DEFAULT_MAX_FIRES = 200;
var isTreeFormat = (config) => {
  if (config === null || typeof config !== "object") return false;
  const app2 = config.appConfig;
  return app2 !== null && typeof app2 === "object" && "_children" in app2;
};
var sameJson = (a, b2) => JSON.stringify(a != null ? a : null) === JSON.stringify(b2 != null ? b2 : null);
var createTransitionSim = (config, options) => {
  var _a2, _b, _c, _d, _e, _f;
  const { username, orgName } = options;
  const base = `orgiggly/orgs/${username}/${orgName}`;
  const baseSegs = splitPath(base);
  const maxRefires = (_a2 = options.maxRefires) != null ? _a2 : DEFAULT_MAX_REFIRES;
  const maxFires = (_b = options.maxFires) != null ? _b : DEFAULT_MAX_FIRES;
  const seed = (_c = options.seed) != null ? _c : () => 42;
  let tick = SIM_EPOCH;
  const baseNow = (_d = options.now) != null ? _d : () => tick++;
  let offset = 0;
  let floor = -Infinity;
  let lastNow = null;
  const now = () => {
    let t30 = baseNow() + offset;
    if (t30 < floor) {
      offset += floor - t30;
      t30 = floor;
    }
    lastNow = t30;
    return t30;
  };
  const clock = () => lastNow != null ? lastNow : now();
  const published = isTreeFormat(config) ? treeToRawOrgData(config) : config;
  const mirror = buildMirror(
    extractTransitionRules(published),
    "sim",
    extractFunctionNodes(published),
    extractObjectTypes(published),
    extractBotActors(published),
    extractConstNodes(published)
  );
  const org = {};
  if (mirror) {
    org.transitionRules = {
      versionId: mirror.versionId,
      byWatchType: mirror.byWatchType,
      byTargetType: mirror.byTargetType,
      byTimerType: mirror.byTimerType,
      childrenOf: mirror.childrenOf,
      botActors: mirror.botActors,
      privateOf: (_e = mirror.privateOf) != null ? _e : {}
    };
  }
  if (options.records) org.realtime = options.records;
  if (options.inbox) org.realtimeInbox = options.inbox;
  if (options.private) org.realtimePrivate = options.private;
  const fake = makeFakeDb(
    { orgiggly: { orgs: { [username]: { [orgName]: org } } } },
    { coerceReads: true, faithfulDeletes: true }
  );
  const getAt = (segs) => {
    let cur = fake.root;
    for (const s of segs) {
      if (!cur || typeof cur !== "object") return void 0;
      cur = cur[s];
    }
    return cur;
  };
  const readCoerced = (segs) => {
    const v = getAt(segs);
    return v === void 0 ? null : rtdbCoerce(JSON.parse(JSON.stringify(v)));
  };
  const addressOf = (path) => {
    const segs = splitPath(path);
    if (baseSegs.some((s, i) => segs[i] !== s)) return null;
    const rel = segs.slice(baseSegs.length);
    if (rel[0] === "realtime" && rel.length >= 3) {
      return { typeName: rel[1], recordId: rel[2] };
    }
    if (rel[0] === "realtimeInbox" && rel.length >= 4) {
      return { inboxUid: rel[1], typeName: rel[2], recordId: rel[3] };
    }
    return null;
  };
  const recordSegs = (a) => a.inboxUid ? [...baseSegs, "realtimeInbox", a.inboxUid, a.typeName, a.recordId] : [...baseSegs, "realtime", a.typeName, a.recordId];
  const addressesTouched = (path, keys) => {
    const paths = keys ? keys.map((k) => `${path}/${k}`) : [path];
    const out = [];
    for (const p of paths) {
      const a = addressOf(p);
      if (a && !out.some((o) => sameJson(o, a))) out.push(a);
    }
    return out;
  };
  const queues = [];
  const tracked = async (addresses, op, before) => {
    var _a3;
    let snaps = addresses.map((a) => readCoerced(recordSegs(a)));
    const result = await op();
    snaps = (_a3 = before == null ? void 0 : before()) != null ? _a3 : snaps;
    addresses.forEach((a, i) => {
      var _a4;
      const after = readCoerced(recordSegs(a));
      if (sameJson(snaps[i], after)) return;
      (_a4 = queues[queues.length - 1]) == null ? void 0 : _a4.push({
        typeName: a.typeName,
        recordId: a.recordId,
        after,
        ...a.inboxUid ? { inboxUid: a.inboxUid } : {}
      });
    });
    return result;
  };
  const db = {
    ref: (path) => {
      const inner = fake.db.ref(path);
      const touched = addressesTouched(path);
      return {
        once: inner.once,
        set: (value) => tracked(touched, () => inner.set(value)),
        remove: () => tracked(touched, () => inner.remove()),
        update: (values) => tracked(
          addressesTouched(path, Object.keys(values)),
          () => inner.update(values)
        ),
        transaction: async (fn) => {
          const userHook = fake.hooks.beforeTxCurrent;
          let snaps = null;
          fake.hooks.beforeTxCurrent = async (p) => {
            if (userHook) await userHook(p);
            snaps = touched.map((a) => readCoerced(recordSegs(a)));
          };
          const result = await tracked(
            touched,
            () => inner.transaction(fn),
            () => snaps
          );
          if (snaps === null) fake.hooks.beforeTxCurrent = userHook;
          return result;
        }
      };
    }
  };
  const recordPath = (type14, id, inboxUid) => inboxUid ? `${base}/realtimeInbox/${inboxUid}/${type14}/${id}` : `${base}/realtime/${type14}/${id}`;
  const describeEvent = (e) => `${e.inboxUid ? `inbox:${e.inboxUid}:` : ""}${e.typeName}/${e.recordId}` + (e.after === null ? " (deleted)" : "");
  let tasks = [];
  let taskSeq = 0;
  const enqueue2 = async (payload, scheduleAtMs) => {
    tasks.push({ payload, scheduleAtMs, seq: taskSeq++ });
  };
  const copies = [];
  let archiveFailing = false;
  const archive = async ({ typeName, record: record10 }) => {
    if (archiveFailing) throw new Error("archive: simulated Storage failure");
    copies.push({ typeName, record: JSON.parse(JSON.stringify(record10)) });
  };
  const runChain = async (mutate) => {
    const queue = [];
    queues.push(queue);
    const events = [];
    const chain = [];
    try {
      await mutate();
      while (queue.length > 0) {
        const event = queue.shift();
        chain.push(describeEvent(event));
        if (chain.length > maxRefires + 1) throw new TransitionLoopError(chain);
        const t30 = now();
        const sd = seed();
        const result = await runTransition(
          { db, now: () => t30, seed: () => sd },
          { username, orgName },
          event
        );
        const armed = event.inboxUid ? { results: [] } : await armTimers(
          { db, now: () => t30, seed: () => sd, enqueue: enqueue2 },
          { username, orgName },
          event
        );
        events.push({ ...event, results: result.results, armed: armed.results });
      }
    } finally {
      queues.pop();
    }
    return events;
  };
  const hasTimers = Object.keys((_f = mirror == null ? void 0 : mirror.byTimerType) != null ? _f : {}).length > 0;
  const ready = mirror && hasTimers ? (async () => {
    let summary = emptyReconcileSummary();
    const events = await runChain(async () => {
      summary = await reconcileTimers(
        { db, now, seed, enqueue: enqueue2 },
        { username, orgName },
        mirror
      );
    });
    return { summary, events };
  })() : Promise.resolve(null);
  ready.catch(() => void 0);
  const write = async (type14, id, record10, writeOptions = {}) => {
    const { concurrent } = writeOptions;
    await ready;
    try {
      return await runChain(async () => {
        if (concurrent) {
          fake.hooks.beforeTxCurrent = async (path) => {
            const live = readCoerced(splitPath(path));
            await db.ref(path).set(concurrent(live));
          };
        }
        await db.ref(recordPath(type14, id, writeOptions.inboxUid)).set(record10);
      });
    } finally {
      if (concurrent) fake.hooks.beforeTxCurrent = void 0;
    }
  };
  const remove = async (type14, id, removeOptions = {}) => {
    await ready;
    return runChain(
      () => db.ref(recordPath(type14, id, removeOptions.inboxUid)).remove()
    );
  };
  const race = async (first, second) => {
    await ready;
    let secondEvents = [];
    let ran = false;
    const firstEvents = await runChain(async () => {
      var _a3;
      fake.hooks.beforeTxCurrent = async () => {
        ran = true;
        secondEvents = await write(
          second.type,
          second.id,
          second.record,
          second.options
        );
      };
      await db.ref(recordPath(first.type, first.id, (_a3 = first.options) == null ? void 0 : _a3.inboxUid)).set(first.record);
    });
    if (!ran) {
      fake.hooks.beforeTxCurrent = void 0;
      throw new Error(
        `race: the first write never reached a transaction, so nothing raced (its events: ${describeEvents(firstEvents).join("; ")})`
      );
    }
    return { first: firstEvents, second: secondEvents };
  };
  const addressOfTask = (t30) => ({
    typeName: t30.payload.typeName,
    recordId: t30.payload.recordId,
    ruleKey: t30.payload.ruleKey
  });
  const nextDue = (by) => tasks.filter((t30) => t30.scheduleAtMs <= by).sort((a, b2) => a.scheduleAtMs - b2.scheduleAtMs || a.seq - b2.seq)[0];
  const advanceTime = async (ms) => {
    if (!(ms >= 0)) throw new Error(`advanceTime: ms must be >= 0, got ${ms}`);
    await ready;
    const target = clock() + ms;
    const firings = [];
    for (let task = nextDue(target); task; task = nextDue(target)) {
      const due = task;
      tasks = tasks.filter((t30) => t30 !== due);
      if (firings.length >= maxFires) {
        throw new TimerLoopError([
          ...describeFirings(firings),
          `${due.payload.typeName}/${due.payload.recordId}[${due.payload.ruleKey}]`
        ]);
      }
      floor = due.scheduleAtMs;
      const firedAt = now();
      const sd = seed();
      let result = null;
      let error2;
      const events = await runChain(async () => {
        try {
          result = await fireTimer(
            { db, now: () => firedAt, seed: () => sd, enqueue: enqueue2, archive },
            due.payload
          );
        } catch (err) {
          error2 = err instanceof Error ? err.message : String(err);
        }
      });
      firings.push({
        ...addressOfTask(due),
        dueAtMs: due.payload.dueAtMs,
        firedAt,
        result,
        ...error2 !== void 0 ? { error: error2 } : {},
        events
      });
    }
    floor = Math.max(floor, target);
    now();
    return firings;
  };
  const pendingTimers = () => [...tasks].sort((a, b2) => a.scheduleAtMs - b2.scheduleAtMs || a.seq - b2.seq).map((t30) => {
    var _a3;
    const rule = (_a3 = mirror == null ? void 0 : mirror.byTimerType[t30.payload.typeName]) == null ? void 0 : _a3[Number(t30.payload.ruleKey)];
    return {
      ...addressOfTask(t30),
      ...(rule == null ? void 0 : rule.label) !== void 0 ? { rule: rule.label } : {},
      dueAtMs: t30.payload.dueAtMs,
      scheduleAtMs: t30.scheduleAtMs
    };
  });
  return {
    write,
    remove,
    race,
    advanceTime,
    pendingTimers,
    armedMarker: (type14, id, ruleKey) => {
      var _a3;
      return (_a3 = getAt([
        ...baseSegs,
        "transitionRules",
        "armed",
        type14,
        id,
        String(ruleKey)
      ])) != null ? _a3 : null;
    },
    archived: () => copies.map((c) => ({ ...c })),
    failArchive: (failing) => {
      archiveFailing = failing;
    },
    clock,
    ready,
    read: (type14, id, readOptions = {}) => readCoerced(splitPath(recordPath(type14, id, readOptions.inboxUid))),
    readPrivate: (key, privateType, id) => readCoerced([...baseSegs, "realtimePrivate", key, privateType, id]),
    readAll: (type14) => {
      var _a3;
      return (_a3 = readCoerced([...baseSegs, "realtime", type14])) != null ? _a3 : {};
    },
    raw: () => {
      var _a3;
      return (_a3 = getAt(baseSegs)) != null ? _a3 : {};
    },
    mirror,
    fake
  };
};

// src/prompts/hostFunctions.ts
var HOST_FN_CONTEXTS = [
  "flowStep:script",
  "content:data-script",
  "content:action"
];
var HOST_FUNCTIONS = [
  {
    name: "publishData",
    async: true,
    signature: 'publishData({type: string, data: object | object[], scope?: "global" | "user" | "variant" | "group", variant?: string, groupKey?: string}): {id: string, ...} | [{id: string, ...}, ...]',
    description: 'Publishes one record (when `data` is an object) or N records (when `data` is an array of objects) of the given object type. Returns the created record(s) with server-stamped `id`, `createdAt`, `createdBy` fields populated. Use the returned `id` to link related records \u2014 e.g. stamp a parent\'s id into each child by including it in the child\'s object literal. `scope` must match the partition the type is bound at, and defaults to `"global"`: `"user"` writes the signed-in user\'s own partition, `"variant"` needs a `variant` key, and `"group"` needs a `groupKey` naming which group to write into (a member can be in several at once, so it is never inferred \u2014 a group publish with no key is refused rather than falling back to the shared collection).',
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `post = publishData({type: "TravelPost", data: {AuthorId: whoAmI, Details: comment}})`,
      `publishData({type: "Photo", data: photos.map((p) => ({Image: p.url, postId: post.id}))})`,
      `publishData({type: "Prediction", data: $rows('matchRows').map((r) => ({id: r.id, score: r.score}))})`,
      `publishData({type: "CardState", data: {cardId: card.id, box: 2}, scope: "user"})`,
      `publishData({type: "Expense", data: {amount: total, paidBy: whoAmI}, scope: "group", groupKey: currentTab})`
    ]
  },
  {
    name: "createGroup",
    async: true,
    signature: "createGroup({groupKey: string, inviteCode?: string}): {groupKey: string, changed: boolean, role: string, addedAt: string}",
    description: 'Creates a membership group in this org and enrols the caller as its first `owner`, so they can immediately read and write the group\'s `scope: "group"` data. Pass an `inviteCode` to mint a shareable join credential at the same time. Idempotent for the owner \u2014 calling it again returns the existing membership with `changed: false`, so a flow step the user can re-enter is safe. Another user hitting an existing key is refused. Use this for a "start a new shared thing" step: a trip tab, a household, a team.',
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `tab = createGroup({groupKey: "tab-" + generateId(6), inviteCode: code})`,
      `createGroup({groupKey: slug(tripName)})`
    ]
  },
  {
    name: "joinGroup",
    async: true,
    signature: "joinGroup({inviteCode?: string, groupKey?: string, claimEmail?: string}): {groupKey: string, changed: boolean, role: string, addedAt: string, matchedBy?: string}",
    description: 'Admits the caller to an existing group so its `scope: "group"` data becomes readable and writable for them. Either an `inviteCode` (the code itself is the credential \u2014 enough on its own) or a `groupKey` plus the `claimEmail` the caller was invited under, which is matched against their signed-in identity. Returns `matchedBy` naming which invitation matched, and `changed: false` when they were already a member, so re-running is safe. The caller\'s account is what gets recorded, so they must be signed in \u2014 an invitation can be addressed to an email or handle, but access is keyed on the account that claims it.',
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `joined = joinGroup({inviteCode: enteredCode})`,
      `joinGroup({groupKey: $args.tab, claimEmail: myEmail})`
    ]
  },
  {
    name: "leaveGroup",
    async: true,
    signature: "leaveGroup({groupKey: string}): {groupKey: string, changed: boolean}",
    description: 'Removes the caller from a group, which revokes their access to its `scope: "group"` data \u2014 the group\'s records drop out of `$data` for them. It deletes nothing: the other members keep the group and everything in it, so tombstone or un-claim your own records first if the flow calls for it. Leaving a group you are not in succeeds with `changed: false`, so a re-entered step is safe. Use this for a "leave this tab / household / team" step.',
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`leaveGroup({groupKey: curTabId})`]
  },
  {
    name: "setAppValue",
    async: true,
    signature: "setAppValue(key: string, value: any): void",
    description: "Writes an app-level value to the given pointer key (relative to the app root). The value persists across reloads via the existing localStorage AppInstance rail. Use this for 'remember who I am' / 'remember last choice' identity write-back \u2014 at the end of a flow, or straight from a Content action's script.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`setAppValue("whoAmI", AuthorId)`]
  },
  {
    name: "setPresenceContext",
    async: true,
    signature: "setPresenceContext(value: string | null): void",
    description: "Tells the signed-in user's friends what they are doing in this app, beside the platform's own record of which app they have open \u2014 e.g. the room code of the game they're in, so a friend can join it. `value` is a string of at most 256 characters; `null` clears it. Only friends can read it, and it lasts for this page: opening another app starts without it. It does nothing for a visitor who isn't signed in, signed in with a name and PIN, or has no friends yet, so call it unconditionally. There is no invite or notification: a friend's app reads it and decides what to show.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`setPresenceContext(roomCode)`, `setPresenceContext(null)`]
  },
  {
    name: "clearValues",
    async: true,
    signature: "clearValues(...names: string[]): void",
    description: "Wipes everything the user has entered under each NAMED node \u2014 its own value, every descendant field, and every slot of a repeating section \u2014 in one atomic update. Names resolve like a formula read: this node's scope outward, then any name unique across the app (an ambiguous name throws and nothing is cleared). Use it to reset a section for a fresh round \u2014 e.g. a game's per-round inputs when a new game starts, which otherwise keep last game's typing because the slots reuse the same index.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `clearValues("lineSlots")`,
      `clearValues("answers", "notes"); setAppValue("roomCode", newCode)`
    ]
  },
  {
    name: "sendToWidget",
    async: true,
    signature: "sendToWidget(widgetName: string, command: string, args?: object): void",
    description: "Tells a sandboxed widget on the screen to DO something \u2014 e.g. `spotifyplayer`'s `play` / `stop` \u2014 so a button outside the widget can drive it. `widgetName` is the widget node's name, resolved like a formula read (an unknown or ambiguous name throws); which commands exist, and their args, is up to each widget. The command is delivered at once, BEFORE any value this same script just set reaches the widget as a parameter \u2014 so pass everything the command needs in `args` rather than relying on a `setValues` a line earlier. A widget that isn't showing (hidden, or on another screen) ignores it. No reply comes back. Runs in preview too.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `sendToWidget("player", "play", {trackUri: q.uri, startMs: q.startMs, lengthMs: 3000})`,
      `sendToWidget("player", "stop"); navigate("results")`
    ]
  },
  {
    name: "navigate",
    async: true,
    signature: "navigate(pointerKey: string, args?: object): void",
    description: "Moves the app cursor to the given screen/section pointer key. Both forms push \u2014 a nav-stack entry (Back returns to the caller) plus a reset of any flow state under the target, exactly like an `<a>` link. The optional `args` object additionally exposes its values as `$args.<key>` on the destination screen \u2014 pair with `byId('Type', $args.id)` on the destination to land on a record you just created. From a content action's script, call it as the last statement \u2014 a preceding setAppValue(...) is the preselect-then-navigate idiom.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `navigate("home")`,
      `navigate("matchDetail", {founderId: newFounder.id})`
    ]
  },
  {
    name: "replaceScreen",
    async: true,
    signature: "replaceScreen(pointerKey: string, args?: object): void",
    description: "Swaps the current screen for the given screen/section pointer key WITHOUT adding a back-stack entry and WITHOUT resetting flow state under the target \u2014 Back returns to wherever the caller came from. Use for redirect-style moves (a landing screen that forwards to the right tab). Prefer `navigate` for ordinary drill-down: replacing into a screen that contains a flow re-shows the flow's last completed step instead of a fresh run.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `replaceScreen("home")`,
      `replaceScreen("tabDetail", {id: $params.id})`
    ]
  },
  {
    name: "back",
    async: true,
    signature: "back(): void",
    description: 'Goes back one nav-stack entry \u2014 exactly what the Back button (or a legacy `href="@pop"`) does. Takes no arguments. A Back/Cancel control in a content template is an action whose script is just `back()`.',
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`back()`]
  },
  {
    name: "updateData",
    async: true,
    signature: "updateData({type: string, id: string, patch: object}): {id: string, ...}",
    description: "Partial-updates an existing record by id. Reads the current record from $data, shallow-merges `patch` over it, and writes back via the same path the Form node's publish button uses \u2014 so create/update/edit-by-form/edit-by-script all converge on one backend op. Returns the merged record. Throws if no record of that type+id is currently loaded in $data. Object property shorthand is supported: `{Name, Role}` means `{Name: Name, Role: Role}` (and `id` means `id: id`), so a patch built from same-named scope variables stays concise.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `updateData({type: "Post", id: post.id, patch: {Content: newContent}})`,
      `updateData({type: "Traveller", id, patch: {Name, Role, Bio}})`
    ]
  },
  {
    name: "deleteData",
    async: true,
    signature: "deleteData({type: string, id: string | string[]}): void",
    description: "Soft-deletes one record (when `id` is a string) or N records (when `id` is an array of strings) by setting `isDeleted: true`. Records remain in storage with their fields preserved; org-level filters (a `whereClause` on the subsection, or `filter(..., !it.isDeleted)` in a template) hide them from the UI. Cascades through declared parent relations: every loaded record of an object type whose `parent: {type, idField}` points at a deleted record is soft-deleted too, recursively, children before parents \u2014 so do NOT filter and delete children by hand when the child type declares `parent`. Only children currently loaded in $data are found, so the child type must be declared as a data source by some node. Restoring a parent does not restore its children. To remove records permanently, use `hardDelete`. Throws if any referenced record is not currently loaded in $data.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `deleteData({type: "Post", id: post.id})`,
      `deleteData({type: "Photo", id: photos.map((p) => p.id)})`,
      `deleteData({type: "Post", id: id}) // Photo declares parent: {type: "Post", idField: "postId"}, so its photos go too`
    ]
  },
  {
    name: "deleteFile",
    async: true,
    signature: "deleteFile(url: string | string[]): void",
    description: "Permanently deletes an uploaded file (an image a `fileInput` stored, by its URL) from this org's Storage \u2014 the blob a record field points at, not the record. Opt in for records that are genuinely gone: `deleteData` is a soft delete that keeps the record restorable, so calling `deleteFile` beside it sacrifices restore of the photo. Only files under THIS org's own upload prefix are touched; any other URL (a web-search pick, another org's file, an empty field) is silently skipped, never an error, so it is safe to call unconditionally. A file that is already gone is tolerated. Accepts an array for multi-file fields.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `deleteData({type: "Event", id: id}); deleteFile(Pic); navigate("calendar")`,
      `deleteFile(photos.map((p) => p.url))`
    ]
  },
  {
    name: "hardDelete",
    async: true,
    signature: "hardDelete({type: string, id: string | string[]}): {deleted: {type, id}[], notFound: string[], blobsDeleted: string[], blobsSkippedShared: string[]}",
    description: "PERMANENTLY removes records \u2014 irreversible, unlike `deleteData`'s restorable soft delete. Runs on the server: removes every stored copy (a record and its soft-delete tombstone alike), cascades through declared parent relations across ALL stored records (not just those loaded in $data, so the child type needs no data source), and deletes this org's own uploaded files that no surviving record, realtime record or config still references (a file shared with something that survives is kept and reported in `blobsSkippedShared`). Only an org admin, or the user who created every named record, may call it; cascaded children go with their parent whoever made them. Refuses realtime-rail types, and throws for an embedded type (nothing on the server to delete \u2014 use `deleteData`). Unknown ids are reported in `notFound`, not thrown. Prefer `deleteData` unless the records must really be gone (personal data, a mistaken import).",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `hardDelete({type: "Post", id: post.id})`,
      `r = hardDelete({type: "Game", id: gameIds}); navigate("home")`
    ]
  },
  {
    name: "setRealtime",
    async: true,
    signature: "setRealtime(path: string, value: object): {ownerUid: string, ...}",
    description: "Creates or replaces ONE realtime record at `path` (shaped `{Type}/{recordId}`, relative to the org's realtime root), reflecting live to every subscriber of that type. `value` must be an object; it is stamped with your `ownerUid`, and security rules let you write only records you own. The record's id IS the final path segment, so any `id` (and `type`) field in `value` is ignored \u2014 spreading a record read from `$data` is safe. It REPLACES the whole record: a field you leave out is deleted, so to change some fields of an existing record use `updateRealtime` instead. Use it to create fixed-id records (a game room keyed by its code, a player keyed by identity); it stamps the `createdAt` that cleanup timers key on.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `setRealtime("Game/" + roomCode, {phase: "lobby", hostId: whoAmI})`,
      `setRealtime("Player/" + whoAmI, {name, gameId: roomCode})`
    ]
  },
  {
    name: "updateRealtime",
    async: true,
    signature: "updateRealtime(path: string, patch: object): {ownerUid: string, ...patch}",
    description: "MERGES `patch` into ONE realtime record at `path` (shaped `{Type}/{recordId}`, relative to the org's realtime root), reflecting live to every subscriber of that type. Fields `patch` doesn't mention are KEPT \u2014 the safe way to change some fields of an existing record (a game's `phase`, a player's score) without re-listing the rest. Shallow: each top-level key replaces that field whole (a nested object is not merged into), and a `null` value deletes the field. Keys may not contain `/`, and `patch` must have at least one field; `id`/`type` are ignored. Security rules let you update only records you own. Create the record with `setRealtime` first: a merge onto a missing path does create it, but without the `createdAt` stamp cleanup timers key on. Returns the patch as written (with your `ownerUid`), not the whole merged record.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `updateRealtime("Game/" + roomCode, {phase: "playing", round: 1})`,
      `updateRealtime("Player/" + whoAmI, {score: me.score + 1})`
    ]
  },
  {
    name: "pushRealtime",
    async: true,
    signature: "pushRealtime(path: string, value: object): {id: string, ownerUid: string, ...}",
    description: "Appends a NEW realtime record under the collection `path` (shaped `{Type}`) with a server-generated id, reflecting live to every subscriber of that type. `value` must be an object; it is stamped with your `ownerUid`. Returns the record with its new `id` \u2014 use it to link related records. Use for append-only collections (chat messages, submissions) where you don't choose the id.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `msg = pushRealtime("ChatMessage", {text: comment, gameId: roomCode})`,
      `entry = pushRealtime("Drawing", {url: image.url, gameId: roomCode})`
    ]
  },
  {
    name: "removeRealtimeOnDisconnect",
    async: true,
    signature: "removeRealtimeOnDisconnect(path: string): void",
    description: "Registers a server-side rule that DELETES the realtime record at `path` (shaped `{Type}/{recordId}`, relative to the org's realtime root) when this client's connection drops \u2014 e.g. the tab closes or the network is lost. Call it right after `setRealtime` creates the record you want auto-cleaned (a player keyed by identity), so a player who leaves vanishes from the live roster instead of stranding it. You may only auto-remove records you own. The deletion is queued by the realtime server and fires on disconnect, not immediately \u2014 it is the wrong tool for removing a record right now.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `setRealtime("Player/" + pid, {name, gameId: roomCode}); removeRealtimeOnDisconnect("Player/" + pid)`
    ]
  },
  {
    name: "removeRealtime",
    async: true,
    signature: "removeRealtime(path: string): void",
    description: "Immediately DELETES the realtime record at `path` (shaped `{Type}/{recordId}`, relative to the org's realtime root), reflecting live to every subscriber of that type. Unlike `removeRealtimeOnDisconnect` (which fires only when the connection drops), this removes the record right now. Security rules let you delete a record you own, or \u2014 for the org's creator \u2014 any realtime record in your own org (so an owner can clean up old game rooms). Use for an admin/owner 'delete this room' control.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `removeRealtime("Game/" + roomCode)`,
      `removeRealtime("ChatMessage/" + msgId)`
    ]
  },
  {
    name: "advanceFlow",
    async: true,
    signature: "advanceFlow(): void",
    description: "Advances the enclosing flow to its next step \u2014 and finishes (closes) the flow when called on the last step. Only valid from a Content script (a declared action or a legacy `data-script`) rendered INSIDE a flow; it lets you replace the built-in Next/Finish button with your own control (pair with the flow's `hideNav`/`hideTopBar` options for a chrome-less, custom-driven flow). Advancing into an auto-running step (e.g. a `flowStep:script`) triggers that step, so this is also how a custom 'Submit' button kicks off the flow's final script. Throws if called outside a flow.",
    availableIn: ["content:data-script", "content:action"],
    examples: [`advanceFlow()`]
  },
  {
    name: "leaveVideoRoom",
    async: true,
    signature: "leaveVideoRoom(): void",
    description: "Leaves the app's active video call, if one is active. A `videoRoom` node's call outlives screen navigation (multiple nodes can share one call across screens), so leaving is never automatic \u2014 call this explicitly wherever a script means for video to actually end, e.g. a 'leave game' or 'back to the start' action. Safe to call even when no call is active (no-op, does not throw).",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`setAppValue("roomCode", ""); leaveVideoRoom(); navigate("home")`]
  },
  {
    name: "endVideoRoom",
    async: true,
    signature: "endVideoRoom(): void",
    description: "End the video call for EVERYONE in the room, not just this device. Only whoever started the room may do this \u2014 the platform refuses anyone else \u2014 so gate the control on your own host check too. Unlike leaveVideoRoom, this is final: nobody can rejoin the same room afterwards. Use it for a deliberate 'we're done' at the end of a session; use leaveVideoRoom when one person is stepping out.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`endVideoRoom(); navigate("home")`]
  },
  {
    name: "spotifyIsConnected",
    async: true,
    signature: "spotifyIsConnected(): boolean",
    description: 'True when the person running the script has connected their own Spotify account (at orgiggly.com/connect/spotify). Read-only, so it also works in preview. Scripts have no `if`, so store the result and let a formula decide what to show \u2014 e.g. a "Connect Spotify" prompt.',
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [`ok = spotifyIsConnected(); setAppValue("spotifyReady", ok)`]
  },
  {
    name: "spotifySearchTracks",
    async: true,
    signature: "spotifySearchTracks(songs: {title: string, artist?: string}[], options?: {limit?: number}): {uri: string, id: string, title: string, artist: string, artists: string[], album: string, albumType: string, year: number | null, artUrl: string | null, durationMs: number, explicit: boolean}[][]",
    description: "Looks every song up on the viewer's connected Spotify account and returns ONE candidate list per song, in the same order \u2014 an empty list when nothing matched (a miss never throws). Batch by design: a script has no loops and cannot call an async host inside `map`, so pass the whole list in one call, then pick the best candidate per song with ordinary formulas or a `function` node (prefer the matching artist; avoid live, remix, karaoke and cover versions). `limit` is candidates per song: default 5, at most 10. Throws \"Connect Spotify first (orgiggly.com/connect/spotify)\" when the viewer hasn't connected. Read-only, so it also runs in preview.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `results = spotifySearchTracks(songs.map((s) => ({title: s.title, artist: s.artist})))`,
      `results = spotifySearchTracks([{title: "Mr. Brightside", artist: "The Killers"}], {limit: 3}); setAppValue("firstUri", results[0][0].uri)`
    ]
  },
  {
    name: "spotifyListPlaylists",
    async: true,
    signature: "spotifyListPlaylists(): {id: string, name: string, trackCount: number, imageUrl: string | null}[]",
    description: "The viewer's Spotify playlists that a quiz can be built from: the ones they own or collaborate on (Spotify won't let an app read the tracks of any other playlist, followed ones included). Feed it to a `selectInput`'s `pickerValues` (ids are strings) and pass the chosen id to `spotifyPlaylistTracks`. Throws \"Connect Spotify first (orgiggly.com/connect/spotify)\" when the viewer hasn't connected, and \"Reconnect Spotify to use your playlists\" when they connected before playlists were supported. Read-only, so it also runs in preview.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `spotifyListPlaylists()`,
      `lists = spotifyListPlaylists(); setAppValue("playlistCount", length(lists))`
    ]
  },
  {
    name: "spotifyPlaylistTracks",
    async: true,
    signature: "spotifyPlaylistTracks(playlistId: string): {uri: string, id: string, title: string, artist: string, artists: string[], album: string, albumType: string, year: number | null, artUrl: string | null, durationMs: number, explicit: boolean}[]",
    description: "The tracks of one of the viewer's playlists, in playlist order and in the same shape as a `spotifySearchTracks` candidate (podcast episodes and local files are left out; up to 500 tracks). `playlistId` is an id from `spotifyListPlaylists`, a `spotify:playlist:\u2026` URI or an open.spotify.com link. Shuffle with `shuffle(tracks, now())` to pick at random. Throws the same Connect / Reconnect errors as `spotifyListPlaylists`, and a clear error for a playlist the viewer neither owns nor collaborates on. Read-only, so it also runs in preview.",
    availableIn: ["flowStep:script", "content:data-script", "content:action"],
    examples: [
      `tracks = spotifyPlaylistTracks(playlistId); picks = shuffle(tracks, now()).slice(0, 10)`
    ]
  },
  {
    name: "speak",
    async: true,
    signature: "speak(text: string, lang?: string, rate?: number): void",
    description: 'Speaks `text` aloud via the device\'s speech synthesis (the same Web Speech path as the legacy `data-speak` attribute, plus error handling). `lang` accepts a BCP-47 code ("fr-FR") or a common English language name ("French"); `rate` is playback speed 0.1\u201310. Toggle-aware: calling it with the SAME text while it is still speaking stops playback instead.',
    availableIn: ["content:action"],
    examples: [
      `speak($params.text, "fr-FR")`,
      `speak(story.Original, story.Language, 0.9)`
    ]
  },
  {
    name: "stopSpeech",
    async: true,
    signature: "stopSpeech(): void",
    description: 'Stops any in-progress speech synthesis immediately (the legacy `data-speak-action="stop"` behaviour). Safe to call when nothing is speaking \u2014 no-op, does not throw.',
    availableIn: ["content:action"],
    examples: [`stopSpeech()`]
  },
  {
    name: "copyText",
    async: true,
    signature: "copyText(text: string): void",
    description: "Copies `text` to the device's clipboard and shows a brief \"Copied\" note. Content actions only, because a browser lets a page write the clipboard only in response to a tap or click. Throws (so the action's error snackbar reports it) where the clipboard isn't available, e.g. a page not served over HTTPS.",
    availableIn: ["content:action"],
    examples: [`copyText(rosterText)`, `copyText($params.link)`]
  },
  {
    name: "enablePush",
    async: true,
    signature: 'enablePush(): "subscribed" | "denied" | "unavailable" | "ios-not-installed" | "preview"',
    description: "Asks for notification permission (only if it hasn't been asked) and registers this device for the org's pushes \u2014 the header bell's subscribe, as a button the org can place where it matters (\u{1F514} Remind me). The device is registered against the visitor's auth uid, which is what a transition rule's `notifyTo` names. Content actions only, because the permission prompt needs a tap. Returns what happened, so store it and word the button from it: `subscribed`; `denied` (blocked \u2014 they must re-enable it in browser or phone settings); `unavailable` (this browser has no web push); `ios-not-installed` (iPhone/iPad in a browser tab \u2014 web push works only once the site is added to the home screen); `preview` (a design preview, nothing registered).",
    availableIn: ["content:action"],
    examples: [`r = enablePush(); setAppValue("pushState", r)`]
  },
  {
    name: "setValue",
    async: true,
    signature: "setValue(value: any): void",
    description: "Writes the content node's OWN value \u2014 the node whose action is running \u2014 so formulas elsewhere can read it back via the node's name (the legacy `setValue=` attribute's behaviour). Use it for local UI state a content node carries itself (selected tab, filter toggle); for app-level state use `setAppValue` instead.",
    availableIn: ["content:action"],
    examples: [`setValue($params.id)`, `setValue("")`]
  },
  {
    name: "setValues",
    async: true,
    signature: "setValues(values: {[fieldName: string]: any}): void",
    description: "Writes SEVERAL fields at once, by name, as ONE atomic state update (#407). Each key is a node name resolved from this content node's scope outward \u2014 exactly the names a formula here could read \u2014 and every value is evaluated against the same state snapshot before any write lands, so `setValues({a: b, b: a})` is a genuine swap with no half-applied render in between. A name nothing in scope declares throws (nothing is written). Use it for any control that must move two or more fields together \u2014 swap, reset-to-defaults, apply-a-preset; for one field use `setValue` (own value) or `setAppValue` (app variable).",
    availableIn: ["content:action"],
    examples: [
      `setValues({myLanguage: otherLanguage, otherLanguage: myLanguage})`,
      `setValues({minPrice: 0, maxPrice: 1000, sortBy: "newest"})`
    ]
  },
  {
    name: "submit",
    async: true,
    signature: "submit(): {id: string, ...}",
    description: "Publishes the content node's enclosing submit context object (the legacy `@submit` href behaviour): resolves the surrounding form-like object, publishes it as a record, then writes the server-stamped `id`/`createdAt`/`createdBy` back into the source fields and shows a success snackbar. Returns the published record. Throws when there is no submit context or the publish fails \u2014 so a failing `submit()` aborts the action's `navigate` step.",
    availableIn: ["content:action"],
    examples: [`submit()`],
    status: "legacy",
    use: "a declared action or a flow `script` step that calls `publishData({type, data: {...}})` with the inputs named explicitly"
  }
];
var ASYNC_HOST_NAMES = HOST_FUNCTIONS.filter(
  (h) => h.async
).map((h) => h.name);
var SYNC_HOST_NAMES = HOST_FUNCTIONS.filter(
  (h) => !h.async
).map((h) => h.name);
var isHostFnContext = (value) => HOST_FN_CONTEXTS.some((c) => c === value);
var hostNamesForContext = (context) => isHostFnContext(context) ? HOST_FUNCTIONS.filter((h) => h.availableIn.includes(context)).map(
  (h) => h.name
) : [];

// src/orgicore/utils/test/orgPackApi.ts
var unsuppliedHosts = Object.fromEntries(
  HOST_FUNCTIONS.map((h) => [
    h.name,
    async () => {
      throw new Error(
        `${h.name} isn't available in a test pack: stub it in hosts.asyncHosts`
      );
    }
  ])
);
var printableLeft = (r) => {
  if (!isLeft(r)) return r;
  const e = r.left;
  return {
    left: "failingExpression" in e ? { message: e.message, expression: getRaw(e.failingExpression) } : { message: e.message, expression: e.source }
  };
};
var withNow = (context) => {
  if (!("$now" in context)) return context;
  const { $now, ...rest } = context;
  if ("now" in rest && rest.now !== $now) {
    throw new Error(
      `orgPack: context sets both $now (${String($now)}) and now (${String(rest.now)}) \u2014 pass one.`
    );
  }
  return { ...rest, now: $now };
};
var createOrgPackApi = (tree2, opts = {}) => {
  var _a2, _b, _c, _d;
  const od = selectOrgData(treeToRawOrgData(tree2));
  if (isLeft(od)) {
    throw new Error(`org config failed to decode: ${String(od.left)}`);
  }
  const def = od.right.appConfig;
  const declaredTypes = new Set(
    ["storage", "realtime", "callable"].flatMap(
      (source) => selectScopedDatasourceObjTypes(def, source).map((t30) => t30.typeName)
    )
  );
  const undeclared = [
    ...new Set(
      ((_a2 = opts.records) != null ? _a2 : []).map((r) => r.type).filter((t30) => !declaredTypes.has(t30))
    )
  ];
  if (undeclared.length) {
    throw new Error(
      `orgPack: record type(s) ${undeclared.join(", ")} are not declared as a datasource by any node in this config \u2014 the live app would never load them. Declare a data node (e.g. a subsection with data: {dataType, source: "storage"}) or remove the fixture records.`
    );
  }
  const records = ((_b = opts.records) != null ? _b : []).filter((r) => r.isDeleted !== true);
  const repo2 = {
    objects: [...od.right.objectInstances, ...records]
  };
  const context = withNow(
    opts.plugins ? { ...createHtmlPluginFns(od.right.plugins), ...opts.context } : (_c = opts.context) != null ? _c : {}
  );
  const app2 = createTestApi(def, context, repo2);
  initWith(app2, (_d = opts.init) != null ? _d : {});
  const state = () => app2.getState();
  const widgetCommands = [];
  const hiddenItself = (key) => {
    const r = selectHidden(state(), key);
    return isRight(r) && r.right === true;
  };
  const isVisible = (at) => {
    let p = selectPointer(state(), at);
    while (p) {
      if (hiddenItself(p.key)) return false;
      p = selectParent(state(), p.key);
    }
    return true;
  };
  return {
    ...app2,
    /** Evaluate a formula (`@...` prefix optional) at a pointer key. */
    evalAt: (formula, at = "") => getRight(
      selectEvaluation(
        state(),
        formula.startsWith("@") ? formula : `@${formula}`,
        at
      )
    ),
    /** The node's current value (stored or computed) at a pointer key. */
    value: (key) => getRight(selectValue(state(), key)),
    /** Iteration count of a repeating node. */
    lengthOf: (key) => getRight(selectLength(state(), key)),
    /** Store a value at a canonical pointer key (as a user input would). */
    setValue: (key, v) => {
      app2.api.updateValue(key, v);
    },
    /**
     * Clear a pointer's stored value (and its descendants) so its `compute` default shows again —
     * what the web's `setValues({x: undefined})` and `clearValues("x")` do. A `setValues` stub in a
     * test should route `undefined` here, or it stores `undefined` where the app would clear.
     */
    clearValue: (key) => {
      app2.api.clearSubtree(key);
    },
    /**
     * Move the cursor — the programmatic analogue of navigating.
     *
     * `args` are nav args, which is the ONLY way to reach `$args` in a
     * formula: it resolves from `instance.cursorArgs` (`selectCursorArgs`),
     * not from the app `context`. So a detail screen — whose whole shape is
     * `byId('Type', $args.id)` — is untestable without them, and passing the
     * id via `context` silently yields an empty record instead of failing.
     */
    setCursor: (key, args) => {
      app2.api.setCursor(key, args);
    },
    /**
     * Whether the node at `at` would be on the page: its own `hidden` and every
     * ancestor's, evaluated exactly as the renderer does (#2278). `renderHtml`
     * never looks at `hidden`, so a pack that renders every surface green can
     * still ship a screen whose content nobody sees — on a phase-gated
     * realtime screen "who sees what" IS the logic, so test it with this.
     * Says nothing about navigation: a node on a screen the cursor isn't on
     * is still "visible" if nothing hides it.
     */
    isVisible,
    /**
     * Pointer keys of the children of `at` the renderer would draw — `[]` when
     * `at` itself is hidden (by its own `hidden` or an ancestor's).
     */
    visibleChildren: (at) => isVisible(at) ? selectVisibleChildPointersToRender(state(), at).map((p) => p.key) : [],
    /**
     * Pointer keys of the running-slot children of the aiQuery step named
     * `step` while its value is `{status: "running", …}`, else `[]`. Set the
     * in-flight value with `setValue(stepKey, {status: "running", partial})`,
     * then `evalAt("step.partial.x", slotKey)` / `renderHtml(slotKey)`.
     */
    runningSlot: (step) => {
      const key = selectDeclaredPointerKey(state(), "", step);
      if (key === void 0) {
        throw new Error(`runningSlot: no node named "${step}" in this org`);
      }
      return selectRunningSlotChildKeys(state(), key);
    },
    /**
     * Whether the web asks for 100 ms progress for this aiQuery step: it has
     * a running slot, or a formula reads `<step>.partial` / `.ready` (#2924).
     */
    showsAnswerProgress: (step) => {
      const key = selectDeclaredPointerKey(state(), "", step);
      if (key === void 0) {
        throw new Error(`showsAnswerProgress: no node named "${step}"`);
      }
      return selectShowsAnswerProgress(state(), key);
    },
    /** Child pointer keys of a node — for walking/iterating. */
    childKeys: (key) => selectChildPointers(state(), key).map((p) => p.key),
    /**
     * Render a content node's `htmlTemplate` at its pointer — interpolations
     * resolve against the current state, exactly as the renderer would: a
     * plain `${…}` template is inferred as a formula (#2129), and any other
     * plain string is the HTML itself.
     */
    renderHtml: (at) => {
      const tpl = selectPointer(state(), at).node.htmlTemplate;
      if (typeof tpl !== "string") {
        throw new Error(`no htmlTemplate at pointer "${at}"`);
      }
      const formula = asTemplateFormula(tpl);
      if (!formula.startsWith("@")) return formula;
      const html = getRight(selectEvaluation(state(), formula, at));
      if (html !== void 0 && typeof html !== "string") {
        throw new Error(
          `htmlTemplate at "${at}" evaluated to a non-string (${JSON.stringify(html)}); the renderer rejects it. Wrap a html\`\u2026\` result in a template: @\`\${html\`\u2026\`}\``
        );
      }
      return String(html);
    },
    /**
     * Every `sendToWidget` a `runScriptAt` script made, in order — the
     * widget's node name, the pointer key it resolved to, the command and its
     * args. Never cleared by the pack; a test splices it if it needs to.
     */
    widgetCommands,
    /**
     * The NodeDef at a pointer key — for reading declarative config a test
     * needs to drive by hand, notably a content node's `actions` block
     * (#1752: an action's `script` is the thing a `data-action` tap runs).
     */
    nodeAt: (at) => selectPointer(state(), at).node,
    /**
     * Run the `script` of the flowStep/node at `at` (or an explicit script
     * body) against the current state, with the given stub hosts. Returns the
     * script's Either result.
     *
     * `staticContext` supplies per-invocation special variables the same way
     * the runtime dispatcher does — a content action's script is run with
     * `{ $params }` (the clicked element's `data-param-*` literals), so a
     * pack test can execute the real declared script rather than a
     * paraphrase of it (#1752).
     *
     * `clearValues`, `sendToWidget`, `setValue` and `setValues` run for real;
     * every other host must be stubbed in `hosts.asyncHosts`, or the script
     * returns a `left` naming it. A test's own stub always wins (#2866).
     */
    runScriptAt: (at, hosts, script, staticContext) => {
      const body = script != null ? script : selectPointer(state(), at).node.script;
      if (typeof body !== "string" || body.length === 0) {
        throw new Error(`no script found at pointer "${at}"`);
      }
      const clearValues = async (...args) => {
        const names2 = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
        const keys = names2.map((name) => {
          const k = selectDeclaredPointerKey(state(), at, String(name));
          if (k === void 0) {
            throw new Error(`clearValues: no node named "${String(name)}" is in scope here`);
          }
          return k;
        });
        keys.forEach((k) => app2.api.clearSubtree(k));
        return void 0;
      };
      const sendToWidget = async (widget, command, args) => {
        const key = selectDeclaredPointerKey(state(), at, String(widget));
        if (key === void 0) {
          throw new Error(`sendToWidget: no node named "${String(widget)}" is in scope here`);
        }
        if (!selectPointer(state(), key).node.pluginName) {
          throw new Error(`sendToWidget: "${String(widget)}" is not a widget node`);
        }
        widgetCommands.push({
          widget: String(widget),
          key,
          command: String(command),
          ...args === void 0 ? {} : { args }
        });
        return void 0;
      };
      const setValue = async (value) => {
        app2.api.updateValue(selectPointer(state(), at).key, value);
        return void 0;
      };
      const setValues = async (values) => {
        if (typeof values !== "object" || values === null || Array.isArray(values)) {
          throw new Error(
            "setValues: expected an object of {fieldName: value}, e.g. setValues({a: b, b: a})"
          );
        }
        const actions = Object.entries(values).map(([name, value]) => {
          const target = selectDeclaredPointerKey(state(), at, name);
          if (target === void 0) {
            throw new Error(`setValues: no field named "${name}" is in scope here`);
          }
          return value === void 0 ? bulkActions.clearValue(target) : bulkActions.updateValue(target, value);
        });
        app2.api.bulkAction(actions);
        return void 0;
      };
      return evaluateScriptInState(
        state(),
        at,
        body,
        {
          ...hosts,
          asyncHosts: {
            ...unsuppliedHosts,
            clearValues,
            sendToWidget,
            setValue,
            setValues,
            ...hosts.asyncHosts
          }
        },
        staticContext
      ).then(printableLeft);
    }
  };
};
var READ_SIDE_FIELDS = ["id", ...REPO_READ_META];
var stripReadSide = (v) => Object.fromEntries(
  Object.entries(v).filter(([k]) => !READ_SIDE_FIELDS.includes(k))
);
var isObject = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var createRealtimeStore = (seed = []) => {
  const store = new Map(
    seed.map(({ id, type: type14, ...rest }) => [`${type14}/${id}`, rest])
  );
  const writes = [];
  const checkPath = (fn, path) => {
    const p = String(path);
    if (typeof path !== "string" || p.split("/").some((s) => s.length === 0)) {
      throw new Error(`${fn}: path "${p}" has an empty segment`);
    }
    return p;
  };
  const setRealtime = async (path, value) => {
    checkPath("setRealtime", path);
    if (!isObject(value)) throw new Error("setRealtime: `value` must be an object");
    const v = stripReadSide(value);
    writes.push({ kind: "set", path: String(path), value: v });
    store.set(String(path), v);
    return v;
  };
  const updateRealtime = async (path, patch) => {
    var _a2;
    checkPath("updateRealtime", path);
    if (!isObject(patch)) throw new Error("updateRealtime: `patch` must be an object");
    const keys = Object.keys(patch);
    if (keys.length === 0) {
      throw new Error("updateRealtime: `patch` must have at least one field");
    }
    if (keys.some((k) => k.includes("/"))) {
      throw new Error("updateRealtime: patch top-level fields only");
    }
    const p = stripReadSide(patch);
    writes.push({ kind: "update", path: String(path), value: p });
    const merged = { ...(_a2 = store.get(String(path))) != null ? _a2 : {} };
    Object.entries(p).forEach(([k, v]) => {
      if (v === null) delete merged[k];
      else merged[k] = v;
    });
    store.set(String(path), merged);
    return p;
  };
  return {
    writes,
    /** The stored record at `{Type}/{id}`, as RTDB would now hold it. */
    record: (path) => store.get(path),
    asyncHosts: { setRealtime, updateRealtime }
  };
};

// src/create/cloneTemplate.ts
var isRecord5 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var isTreeNode2 = (x) => isRecord5(x) && typeof x.type === "string" && (x._children === void 0 || Array.isArray(x._children) && x._children.every(isTreeNode2));
var TYPE_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;
var refuse = (kind, message) => left3({ kind, message });
var children = (node2) => isRecord5(node2) && Array.isArray(node2._children) ? node2._children : [];
var descendants = (root) => isRecord5(root) ? [root, ...children(root).flatMap(descendants)] : [];
var objectTypeNames = (config) => descendants(config.objectTypes).flatMap(
  (n) => n.type === "objectType" && typeof n.name === "string" ? [n.name] : []
);
var displayText = (value) => {
  const trimmed = value.trim();
  return trimmed.length > 0 && !trimmed.startsWith("@") ? trimmed : void 0;
};
var slugOf = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
var checkEntityRenames = (entities, owner, types2, renames) => {
  for (const [from, to] of Object.entries(renames)) {
    if (!entities.includes(from)) {
      return refuse(
        "unknown-entity",
        `"${from}" is not a renameable entity of ${owner} (has: ${entities.join(", ")}).`
      );
    }
    if (!TYPE_NAME.test(to) || FIELD_TYPE_BUILTINS.has(to)) {
      return refuse(
        "invalid-name",
        `"${to}" cannot name a type: use letters, digits and _, starting with a letter, and not a field-type builtin.`
      );
    }
  }
  const finals = types2.map((t30) => {
    var _a2;
    return (_a2 = renames[t30]) != null ? _a2 : t30;
  });
  const duplicate = finals.find((t30, i) => finals.indexOf(t30) !== i);
  if (duplicate !== void 0) {
    return refuse(
      "name-collision",
      `The renames leave two types named "${duplicate}".`
    );
  }
  return right3(void 0);
};
var emptySeedSlots = (instances, slots) => {
  if (!isRecord5(instances) || !Array.isArray(instances._children)) return instances;
  return {
    ...instances,
    _children: instances._children.filter((c) => !(isRecord5(c) && typeof c.type === "string" && slots.has(c.type))).map((c) => emptySeedSlots(c, slots))
  };
};
var cloneTemplateTree = (template, params) => {
  var _a2, _b, _c, _d, _e;
  if (!isRecord5(template.config) || !isRecord5(template.config.appConfig)) {
    return refuse("invalid-template", "The template has no appConfig.");
  }
  const appName = displayText(params.appName);
  if (appName === void 0) {
    return refuse("invalid-params", "The app name must be non-empty text that does not start with @.");
  }
  const types2 = objectTypeNames(template.config);
  const renames = (_a2 = params.entityRenames) != null ? _a2 : {};
  const renameCheck = checkEntityRenames(
    template.manifest.entities,
    `the ${template.manifest.verticalId} template`,
    types2,
    renames
  );
  if (isLeft3(renameCheck)) return renameCheck;
  const slots = (_b = params.seedSlots) != null ? _b : [];
  const unknownSlot = slots.find((s) => !template.manifest.seedSlots.includes(s));
  if (unknownSlot !== void 0) {
    return refuse(
      "unknown-seed-slot",
      `"${unknownSlot}" is not a seed slot of the ${template.manifest.verticalId} template.`
    );
  }
  const seeded = {
    ...template.config,
    objectInstances: emptySeedSlots(template.config.objectInstances, new Set(slots))
  };
  const renamed = renameTypesInConfig(seeded, renames);
  if (isLeft3(renamed)) {
    return left3({
      kind: "rename-failed",
      message: `The entity rename could not be placed in ${renamed.left.length} place(s).`,
      renameErrors: renamed.left
    });
  }
  const config = structuredClone(renamed.right);
  if (!isRecord5(config) || !isRecord5(config.appConfig)) {
    return refuse("invalid-template", "The template has no appConfig.");
  }
  const app2 = config.appConfig;
  app2.label = appName;
  if (typeof app2.name === "string") app2.name = slugOf(appName) || app2.name;
  if (((_c = params.theme) == null ? void 0 : _c.preset) !== void 0) app2.theme = params.theme.preset;
  if (((_d = params.theme) == null ? void 0 : _d.colors) !== void 0) {
    app2.themeColors = {
      ...isRecord5(app2.themeColors) ? app2.themeColors : {},
      ...params.theme.colors
    };
  }
  for (const [nodeName, label2] of Object.entries((_e = params.labels) != null ? _e : {})) {
    if (!template.manifest.labels.includes(nodeName)) {
      return refuse(
        "unknown-label",
        `"${nodeName}" is not a labellable node of the ${template.manifest.verticalId} template.`
      );
    }
    const text = displayText(label2);
    if (text === void 0) {
      return refuse("invalid-params", `The label for "${nodeName}" must be non-empty text that does not start with @.`);
    }
    const matches = descendants(app2).filter((n) => n.name === nodeName);
    if (matches.length !== 1) {
      return refuse(
        "invalid-template",
        `The template has ${matches.length} nodes named "${nodeName}"; a labellable node must be unique.`
      );
    }
    matches[0].label = text;
  }
  return right3(config);
};
var section = (value) => isTreeNode2(value) ? value : void 0;
var cloneTemplate = (template, params) => {
  const tree2 = cloneTemplateTree(template, params);
  if (isLeft3(tree2)) return tree2;
  const t30 = tree2.right;
  const orgTree = {
    versionId: "",
    apiVersion: typeof t30.apiVersion === "string" ? t30.apiVersion : void 0,
    appConfig: section(t30.appConfig),
    users: section(t30.users),
    objectTypes: section(t30.objectTypes),
    objectInstances: section(t30.objectInstances),
    plugins: section(t30.plugins)
  };
  return right3(treeToRawOrgData(orgTree));
};

// src/create/templates/family-shell.json
var family_shell_default = {
  apiVersion: "0.1",
  versionId: "family-shell-template-1",
  appConfig: {
    id: "app-family-shell",
    type: "app",
    authMode: "public",
    notifications: [],
    name: "familyShell",
    label: "Family Hub",
    _children: [
      {
        id: "screen-8",
        type: "screen",
        label: "Home",
        name: "home",
        layout: {
          maxColumns: 1,
          axis: "y",
          mode: "columns",
          minColumnWidth: 350
        },
        data: {
          sort: {},
          source: "embedded"
        },
        containerStyle: {},
        _children: [
          {
            id: "subsection-6",
            type: "subsection",
            label: "Menu",
            name: "menu",
            layout: {
              maxColumns: 3,
              axis: "x",
              mode: "columns",
              columnWeights: [
                1,
                1,
                1
              ],
              columnBreaks: [],
              minColumnWidth: 170
            },
            data: {
              dataType: "AppFeature",
              sort: {},
              source: "embedded"
            },
            isRepeating: true,
            length: "@length(menu)",
            style: {},
            _children: [
              {
                id: "PictureLink-1",
                type: "PictureLink",
                name: "pictureLink",
                parameters: {
                  link: '@"/" + menu[$index].name',
                  picUrl: "@menu[$index].pic.url",
                  title: "@menu[$index].title",
                  param: "",
                  subtitle: "",
                  spanPosition: ""
                },
                containerStyle: {
                  marginBottom: 3,
                  justifySelf: "center"
                }
              }
            ]
          },
          {
            id: "content-signInForMore",
            type: "content",
            name: "signInForMore",
            label: "Sign in for more",
            hidden: "@$authKind != 'anon'",
            htmlTemplate: '@`<div style="max-width:520px;margin:8px auto 24px;padding:0 16px;text-align:center;color:#6b7280;font-size:14px">Family? Sign in (top right) for more.</div>`'
          }
        ]
      }
    ]
  },
  users: {
    type: "folder",
    _children: []
  },
  objectTypes: {
    id: "folder-5",
    type: "folder",
    name: "",
    _children: [
      {
        id: "objectType-4",
        type: "objectType",
        name: "AppFeature",
        icon: "featured_seasonal_and_gifts",
        fields: [
          {
            name: "name",
            type: "text"
          },
          {
            name: "title",
            type: "text"
          },
          {
            name: "pic",
            type: "imageUpload"
          }
        ]
      }
    ]
  },
  objectInstances: {
    id: "folder-1",
    type: "folder",
    name: "",
    _children: [
      {
        id: "folder-11",
        type: "folder",
        name: "Features",
        _children: []
      }
    ]
  },
  plugins: {
    type: "folder",
    id: "folder-12",
    _children: [
      {
        id: "plugin-6",
        type: "plugin",
        name: "PictureLink",
        icon: "image",
        parameters: [
          {
            name: "link",
            type: "text",
            description: "The name of the page you want the navigate to"
          },
          {
            name: "param",
            type: "text",
            description: "expression returning a parameter to send to the page"
          },
          {
            name: "picUrl",
            type: "text",
            description: "expression returning the url of the picture to display"
          },
          {
            name: "title",
            type: "text",
            description: "The main title text to display"
          },
          {
            name: "subtitle",
            type: "text",
            description: "the smaller subtitle to display"
          },
          {
            name: "spanPosition",
            type: "text",
            description: "optional: $spanPosition inside a spanning row (start/middle/end/single), or 'ongoing' for a run already underway. 'middle' and 'end' render a compact continuation chip; the rest render the full card."
          }
        ],
        htmlTemplate: `@\`\${(spanPosition == 'middle' || spanPosition == 'end') ? \`<a 
  rel="\${link}"
  param="\${param}"
  style="
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    text-decoration: none;
    font-family: 'Inter', 'Helvetica Neue', sans-serif;
    background-color: #f5f3ff;
    border-left: 3px solid #8b5cf6;
    border-radius: \${spanPosition == 'end' ? '8px 8px 12px 12px' : '8px'};
    color: #4c1d95;
  "
>
  <span 
    style="
      font-size: 13px;
      font-weight: 600;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    "
  >\${title}</span>
  <span 
    style="
      font-size: 11px;
      font-weight: 500;
      opacity: 0.75;
      white-space: nowrap;
    "
  >\${spanPosition == 'end' ? 'last day' : '\xB7\xB7\xB7'}</span>
</a>\` : \`<a 
  rel="\${link}"
  param="\${param}"
  style="
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    text-decoration: none;
    color: #222;
    font-family: 'Inter', 'Helvetica Neue', sans-serif;
    width: 160px;
    max-width: 100%;
    padding: 14px;
    border-radius: 16px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
    background-color: #fff;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  "
  onmouseover="this.style.transform='translateY(-4px)'; this.style.boxShadow='0 6px 12px rgba(0,0,0,0.15)'"
  onmouseout="this.style.transform='none'; this.style.boxShadow='0 2px 6px rgba(0,0,0,0.1)'"
>
  \${picUrl ? \`<img 
    src="\${picUrl}" 
    alt="\${title}" 
    style="
      width: 100%;
      max-width: 200px;
      max-height: 200px;
      object-fit: cover;
      border-radius: 12px;
      margin-bottom: 10px;
    "
  />\` : ""}
  <div 
    style="
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    "
  >
    <span 
      style="
        font-size: 15px;
        font-weight: 600;
        color: #222;
    "
  >
    \${title}
  </span>
    \${subtitle ? \`
      <span 
        style="
          font-size: 13px;
          font-weight: 400;
          color: #666;
        "
      >
        \${subtitle}
      </span>
    \` : ''}
    \${(spanPosition == 'start' || spanPosition == 'ongoing') ? \`
      <span 
        style="
          font-size: 11px;
          font-weight: 600;
          color: #6d28d9;
          letter-spacing: 0.3px;
        "
      >
        \${spanPosition == 'ongoing' ? 'in progress \u2192' : 'continues \u2192'}
      </span>
    \` : ''}
  </div>
</a>\`}\``
      }
    ]
  }
};

// src/create/templates/placeholder.json
var placeholder_default = {
  apiVersion: "0.1",
  versionId: "placeholder-template-1",
  appConfig: {
    id: "app-placeholder",
    type: "app",
    name: "placeholder",
    label: "Fruit Stand",
    authMode: "public",
    theme: "light",
    _children: [
      {
        id: "variable-lastPicked",
        type: "variable",
        name: "lastPicked"
      },
      {
        id: "function-ripest",
        type: "function",
        name: "ripest",
        parameters: "fruits: list<Fruit>",
        returns: "Fruit",
        compute: "@first(fruits)"
      },
      {
        id: "screen-home",
        type: "screen",
        name: "home",
        label: "Home",
        _children: [
          {
            id: "content-fruitBoard",
            type: "content",
            name: "fruitBoard",
            htmlTemplate: '@`<div style="padding:16px;font-family:system-ui;">\n<h2 style="margin:0 0 8px;">Fruit stand</h2>\n<p style="color:#666;margin:0 0 12px;">Every Fruit on the stand. Tap one for its detail; the ripest pick is ${ripest($data.Fruit) ? ripest($data.Fruit).title : \'not in yet\'}.</p>\n<p style="margin:0 0 12px;">Last picked: ${lastPicked ?? \'none\'}</p>\n<div style="display:flex;flex-direction:column;gap:8px;">${$data.Fruit.map((f) => `<button data-action="openFruit" data-param-id="${f.id}" data-param-title="${f.title}" style="text-align:left;padding:12px 16px;border:1px solid #ccc;border-radius:8px;background:#fff;cursor:pointer;font-size:1rem;">Open ${f.title}</button>`).join(\'\')}</div>\n</div>`',
            actions: {
              openFruit: {
                params: [
                  "id",
                  "title"
                ],
                script: "setAppValue('lastPicked', $params.title); navigate('fruitDetail', {id: $params.id, title: $params.title})"
              }
            }
          },
          {
            id: "subsection-basketList",
            type: "subsection",
            name: "basketList",
            label: "Baskets",
            isRepeating: true,
            data: {
              dataType: "Basket",
              source: "embedded"
            },
            _children: [
              {
                id: "content-basketRow",
                type: "content",
                name: "basketRow",
                htmlTemplate: '@`<div style="padding:8px 16px;font-family:system-ui;"><strong>${name}</strong> \u2014 ${count($data.Fruit)} Fruit on offer</div>`'
              }
            ]
          }
        ]
      },
      {
        id: "screen-fruitDetail",
        type: "screen",
        name: "fruitDetail",
        label: "Fruit detail",
        _children: [
          {
            id: "content-fruitCard",
            type: "content",
            name: "fruitCard",
            htmlTemplate: `@\`<div style="padding:16px;font-family:system-ui;">
<h2 style="margin:0 0 8px;">\${$args.title ?? 'Unknown'}</h2>
<p style="margin:0 0 8px;">\${$args.id ? byId('Fruit', $args.id).tagline : ''}</p>
<p style="color:#666;margin:0;">Last picked: \${lastPicked ?? 'none'}</p>
</div>\``
          }
        ]
      }
    ]
  },
  users: {
    id: "group-root",
    type: "group",
    _children: [
      {
        id: "user-anon",
        type: "user",
        name: "Visitor"
      }
    ]
  },
  objectTypes: {
    id: "folder-types",
    type: "folder",
    name: "",
    _children: [
      {
        id: "objectType-Fruit",
        type: "objectType",
        name: "Fruit",
        icon: "nutrition",
        fields: [
          {
            name: "title",
            type: "text"
          },
          {
            name: "tagline",
            type: "text"
          }
        ]
      },
      {
        id: "objectType-Basket",
        type: "objectType",
        name: "Basket",
        icon: "shopping_basket",
        fields: [
          {
            name: "name",
            type: "text"
          },
          {
            name: "favourite",
            type: "Fruit"
          },
          {
            name: "contents",
            type: "list<Fruit>"
          }
        ]
      }
    ]
  },
  objectInstances: {
    id: "folder-instances",
    type: "folder",
    name: "",
    _children: [
      {
        id: "fruit-kiwi",
        type: "Fruit",
        title: "Kiwi",
        tagline: "Fuzzy and tart."
      },
      {
        id: "fruit-mango",
        type: "Fruit",
        title: "Mango",
        tagline: "Golden and sweet."
      },
      {
        id: "fruit-plum",
        type: "Fruit",
        title: "Plum",
        tagline: "Deep purple snack."
      },
      {
        id: "basket-breakfast",
        type: "Basket",
        name: "Breakfast"
      },
      {
        id: "basket-picnic",
        type: "Basket",
        name: "Picnic"
      }
    ]
  },
  plugins: {
    id: "folder-plugins",
    type: "folder",
    name: "",
    _children: []
  }
};

// src/create/templates/registry.ts
var textAnswer = (answers, key) => {
  const value = answers[key];
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : void 0;
};
var toTypeName = (text) => {
  const name = text.split(/[^A-Za-z0-9]+/).filter((w) => w.length > 0).map((w) => w[0].toUpperCase() + w.slice(1)).join("");
  return /^[A-Za-z]/.test(name) ? name : void 0;
};
var placeholder = {
  manifest: {
    verticalId: "placeholder",
    title: "Item list with detail screen",
    entities: ["Fruit", "Basket"],
    seedSlots: ["Fruit", "Basket"],
    labels: ["home", "fruitDetail", "basketList"]
  },
  config: placeholder_default,
  paramsFromSpec: (spec) => {
    var _a2;
    const item = spec.derived.entities[0];
    const itemType = item === void 0 ? void 0 : toTypeName(item);
    const entityRenames = {};
    if (itemType !== void 0) entityRenames.Fruit = itemType;
    return {
      appName: (_a2 = textAnswer(spec.answers, "appName")) != null ? _a2 : "Fruit Stand",
      entityRenames,
      seedSlots: ["Fruit"]
    };
  }
};
var familyShell = {
  manifest: {
    verticalId: "family-shell",
    title: "Family hub, ready for features",
    entities: [],
    seedSlots: [],
    labels: ["home"]
  },
  config: family_shell_default,
  paramsFromSpec: (spec) => {
    var _a2;
    return {
      appName: (_a2 = textAnswer(spec.answers, "appName")) != null ? _a2 : "Family Hub"
    };
  }
};
var TEMPLATES = [placeholder, familyShell];
var listTemplates = () => TEMPLATES;
var getTemplate = (verticalId) => TEMPLATES.find((t30) => t30.manifest.verticalId === verticalId);

// src/create/renameNodes.ts
import * as R25 from "remeda";

// src/validation/nearMiss.ts
var nearMissHint = (needle, haystack, label2) => {
  if (haystack.size === 0) {
    return `No ${label2}s declared. Add a declaration to satisfy the reference.`;
  }
  const ranked = [...haystack].map((p) => ({ p, d: levenshteinDistance(needle, p) })).sort((a, b2) => a.d - b2.d);
  const closest = ranked[0];
  if (closest && closest.d <= 3) {
    return `Closest ${label2}: \`${closest.p}\`. Either rename the reference or add \`${needle}\` to the org config.`;
  }
  const list3 = ranked.slice(0, 5).map((x) => x.p).join(", ");
  return `Available ${label2}s: ${list3}. Either rename the reference or add \`${needle}\`.`;
};

// src/utils/externalHref.ts
var BROWSER_SCHEMES = /^(https?|mailto|tel|sms|geo):/i;
var isBrowserHref = (href) => BROWSER_SCHEMES.test(href) || href.startsWith("/") || href.startsWith("#");

// src/validation/types.ts
var ERROR_KIND_TO_NODE_TYPE = {
  "missing-required-field": null,
  "unknown-field": null,
  "invalid-type": null,
  "duplicate-id": null,
  "duplicate-sibling-name": null,
  "ambiguous-name-ref": null,
  "section-root-untyped": null,
  "parent-on-placement-field": null,
  "parent-with-tree-placement": null,
  "enum-unknown-value": null,
  "span-incomplete": "subsection",
  "span-not-repeating": "subsection",
  "span-slots-invalid": "subsection",
  // Cross-cutting: `place` sits on `common`, so the conflict can land anywhere.
  "place-span-conflict": null,
  // Cross-cutting: `layout` sits on every container and `place` on `common`.
  "spatial-missing-cols": null,
  "place-incomplete": null,
  "place-outside-spatial": null,
  "spatial-unsupported": null,
  // Cross-cutting: any node carrying a `data` block can trip these.
  "data-scope-user-unsupported-source": null,
  "data-scope-group-unsupported-source": null,
  "data-scope-inbox-unsupported-source": null,
  "data-scope-group-key-missing": null,
  "data-scope-realtime-rail-conflict": null,
  "media-mode-room-conflict": null,
  "vision-model-unsupported": "flowStep:aiQuery",
  // Same `variant`-qualified spelling as the `flowStep:script` host-fn kinds:
  // the sidecar is `node-pitfalls/aiChatStep.md`, keyed by the variant node.
  "aiChat-missing-prompt": "flowStep:aiChat",
  "aiChat-turns-range": "flowStep:aiChat",
  "aiChat-speech-lang-unused": "flowStep:aiChat",
  "repeat-outside-flow": "repeat",
  "repeat-nested": "repeat",
  "repeat-child-not-screen": "repeat",
  "repeat-duplicate-input": "repeat",
  "formula-syntax": null,
  "formula-unknown-var": null,
  "formula-arrow-syntax": null,
  "host-fn-unknown": "flowStep:script",
  "host-fn-out-of-context": "flowStep:script",
  "host-fn-nested": "flowStep:script",
  "host-arg-missing": "flowStep:script",
  "host-arg-type": "flowStep:script",
  "rows-unknown-subsection": "subsection",
  "rows-out-of-context": "subsection",
  "rows-dynamic-name": "subsection",
  "formula-arity": null,
  "formula-unknown-method": null,
  "function-undefined-concat": "function",
  "match-no-default": null,
  "formula-let-rebind": null,
  "formula-unknown-identifier": null,
  "name-shadowed-by-builtin": null,
  "function-param-unknown-field": "function",
  "function-arg-type": null,
  "formula-duplicate-data-scan": null,
  "formula-prefer-nullish": null,
  "formula-prefer-find": null,
  "formula-prefer-some": null,
  "formula-prefer-variadic-concat": null,
  "formula-length-member": null,
  "formula-prefer-value-match": null,
  "missing-objectType": null,
  "missing-screen": "screen",
  "missing-plugin": "plugin",
  "invalid-field-type": null,
  "parent-relation-invalid": "objectType",
  "parent-relation-cycle": "objectType",
  "unknown-field-ref": null,
  "unknown-sort-field": null,
  "arbitrary-sort-field": null,
  "undeclared-data-read": null,
  "span-unknown-field": null,
  // Tree-of-records: the pitfalls sidecar `node-pitfalls/tree.md` lands with
  // the AI-exposure sub-task (#1073) that un-hides `tree`; pointing at the
  // right node type now means the report is correct the moment it exists.
  "tree-roottype-unknown": "tree",
  "tree-allowedchild-unknown": "tree",
  "tree-nodetype-unknown-objecttype": "tree",
  "tree-mixed-parent-spellings": "tree",
  "tree-multiple-parentref-fields": "tree",
  "tree-detail-unknown-child": "tree",
  "tree-unreachable-nodetype": "tree",
  "tree-source-not-writable": "tree",
  "subsection-reorderable-sort-mismatch": "subsection",
  "subsection-reorderable-realtime": "subsection",
  "subsection-reorderable-partial-view": "subsection",
  "vision-source-unresolved": "flowStep:aiQuery",
  "vision-low-fidelity-source": "flowStep:aiQuery",
  "plugin-param-undeclared": "plugin",
  "plugin-param-missing": "plugin",
  "plugin-param-untyped": "plugin",
  "plugin-param-unparsable": "plugin",
  "plugin-name-invalid": "plugin",
  "widget-owner-invalid": "plugin",
  "widget-owner-not-sandboxed": "plugin",
  "widget-owner-capability": "plugin",
  "widget-owner-needs-min-client": "plugin",
  "action-unknown": "content",
  "action-unused": "content",
  "action-param-mismatch": "content",
  "action-replace-into-flow": "content",
  "action-confirm-untitled": "content",
  "action-confirm-noop": "content",
  "action-confirm-mismatch": "content",
  "action-confirm-info-dead-option": "content",
  "legacy-click-channel": "content",
  "template-at-ref": "content",
  "template-missing-formula-wrapper": "content",
  "run-render-error": "content",
  "run-dead-interpolation": "content",
  "run-empty-list": "subsection",
  "run-plugin-error": "plugin",
  "html-unbalanced-tag": "content",
  "homeScreen-missing": "app",
  "repeating-without-data": "subsection",
  "cross-org-link-missing-target-blank": null,
  "empty-container": null,
  "flow-script-only": "flow",
  "flow-outside-screen": "flow",
  "legacy-node": null,
  "legacy-prop": null,
  "orphan-screen": "screen",
  "sparse-instances": null,
  "rubric-missing-objectType": null,
  "unsafe-click-path": "app",
  "notification-unknown-identifier": null,
  "notification-unknown-call": null,
  // Transitions: transitionRule is a helperType, not a node type — no
  // per-type pitfall file yet (the sidecar is T9). Cross-cutting → null.
  "transition-unknown-trigger": null,
  "transition-due-at": null,
  "transition-timer-missing-due": null,
  "transition-timer-unexpected-field": null,
  "transition-timer-missing-eol": null,
  "transition-archive-not-eol": null,
  "transition-perpetual-not-timer": null,
  "transition-perpetual-target-id": null,
  "transition-perpetual-due-not-field": null,
  "transition-perpetual-mixed-type": null,
  "transition-archive-field-without-archive": null,
  "transition-archive-unprojected": null,
  "transition-archive-impure-id": null,
  "transition-timer-record-var": null,
  "transition-timer-impure-due": null,
  "transition-missing-field": null,
  "transition-unknown-type": null,
  "transition-self-target": null,
  "transition-loop": null,
  "transition-dead-rule": null,
  "transition-private-incomplete": null,
  "transition-private-read-incomplete": null,
  "transition-target-inbox": null,
  "transition-notify-incomplete": null,
  "transition-notify-conflict": null,
  "transition-notify-private": null,
  "transition-sms-incomplete": null,
  "transition-sms-private": null,
  "transition-private-read-timer": null,
  "transition-private-type-not-private": null,
  "transition-unknown-call": null,
  "transition-unknown-identifier": null,
  // This one DOES have a node type — the error is on the `function` node whose
  // body is wrong, not on the rule that called it.
  "function-transition-scope-var": "function",
  // Bot actors: `botActor` is a helperType like transitionRule — cross-cutting.
  "botactor-missing-field": null,
  "botactor-unknown-type": null,
  "botactor-self-target": null,
  "botactor-private-incomplete": null,
  "botactor-private-type-not-private": null,
  "botactor-unknown-call": null,
  "botactor-unknown-identifier": null,
  "const-name-clash": "const",
  "const-name-invalid": "const",
  // Library folders are vendored by the CLI and hidden from the org-gen
  // agent, so there is no prompt-side pitfall to point at.
  "library-folder-drift": null,
  "library-name-clash": null,
  "library-override": null,
  // Installed by the CLI or the graph `install` op, never authored inline.
  "feature-folder-drift": null
};
var FORMULA_REFERENCE = {
  path: "prompts/formula-reference.generated.md",
  generated: { upstream: "scripts/codegen/formulaModel.json" }
};
var TYPES_REFERENCE = {
  path: "prompts/types-reference.generated.md",
  generated: { upstream: "scripts/codegen/nodeModel.json" }
};
var ORG_PROMPT = { path: "prompts/orgSystemPromptV2.md" };
var HTML_CONTENT = { path: "prompts/sections/html-content.md" };
var CROSS_CUTTING_PITFALL_TARGETS = {
  // Formulas — the operator/function/special-var reference.
  "formula-syntax": FORMULA_REFERENCE,
  "formula-unknown-var": FORMULA_REFERENCE,
  "formula-arrow-syntax": FORMULA_REFERENCE,
  "formula-arity": FORMULA_REFERENCE,
  "formula-unknown-method": FORMULA_REFERENCE,
  "formula-let-rebind": FORMULA_REFERENCE,
  "formula-unknown-identifier": FORMULA_REFERENCE,
  "formula-duplicate-data-scan": FORMULA_REFERENCE,
  "formula-prefer-nullish": FORMULA_REFERENCE,
  "formula-prefer-find": FORMULA_REFERENCE,
  "formula-prefer-some": FORMULA_REFERENCE,
  "formula-prefer-variadic-concat": FORMULA_REFERENCE,
  "formula-length-member": FORMULA_REFERENCE,
  "formula-prefer-value-match": FORMULA_REFERENCE,
  "match-no-default": FORMULA_REFERENCE,
  "name-shadowed-by-builtin": FORMULA_REFERENCE,
  "function-arg-type": FORMULA_REFERENCE,
  // Structure and references — the node/field schema.
  "missing-required-field": TYPES_REFERENCE,
  "unknown-field": TYPES_REFERENCE,
  "invalid-type": TYPES_REFERENCE,
  "enum-unknown-value": TYPES_REFERENCE,
  "invalid-field-type": TYPES_REFERENCE,
  "missing-objectType": TYPES_REFERENCE,
  "unknown-field-ref": TYPES_REFERENCE,
  "unknown-sort-field": TYPES_REFERENCE,
  "arbitrary-sort-field": TYPES_REFERENCE,
  "span-unknown-field": TYPES_REFERENCE,
  // The reference's "Legacy — recognise, never write" tail names each
  // legacy node/prop with its replacement (#2739).
  "legacy-node": TYPES_REFERENCE,
  "legacy-prop": TYPES_REFERENCE,
  // Tree shape and naming — the JSON/tree structure sections of the prompt.
  "duplicate-id": ORG_PROMPT,
  "duplicate-sibling-name": ORG_PROMPT,
  "ambiguous-name-ref": ORG_PROMPT,
  "section-root-untyped": ORG_PROMPT,
  // Rubric kinds — the generation rules.
  "empty-container": ORG_PROMPT,
  "sparse-instances": ORG_PROMPT,
  "rubric-missing-objectType": ORG_PROMPT,
  // Layout — place/span/spatial live on every container.
  // Layout (place/span/spatial) and data-scope kinds ALSO go to the types
  // reference: `sections/containers.md` never mentions placement and
  // `sections/data-binding.md` never teaches `scope`/`groupKey`, and a
  // target that is silent on the kind reads as `ok` to the size-based status.
  // Re-point them when a section actually teaches the shape.
  "place-span-conflict": TYPES_REFERENCE,
  "spatial-missing-cols": TYPES_REFERENCE,
  "place-incomplete": TYPES_REFERENCE,
  "place-outside-spatial": TYPES_REFERENCE,
  "spatial-unsupported": TYPES_REFERENCE,
  "parent-on-placement-field": TYPES_REFERENCE,
  "parent-with-tree-placement": TYPES_REFERENCE,
  // Data binding — scopes, rails and reads.
  "data-scope-user-unsupported-source": TYPES_REFERENCE,
  "data-scope-group-unsupported-source": TYPES_REFERENCE,
  "data-scope-inbox-unsupported-source": TYPES_REFERENCE,
  "data-scope-group-key-missing": TYPES_REFERENCE,
  "data-scope-realtime-rail-conflict": TYPES_REFERENCE,
  "undeclared-data-read": TYPES_REFERENCE,
  // Template HTML.
  "cross-org-link-missing-target-blank": HTML_CONTENT
};
var pathStr = (segs) => segs.reduce((acc, s) => {
  if (typeof s === "number") return `${acc}[${s}]`;
  if (acc === "") return String(s);
  return `${acc}.${s}`;
}, "");

// src/validation/atRefs.ts
var NAV_PSEUDO_PROTOCOLS = /* @__PURE__ */ new Set(["pop", "submit", "delete", "back"]);
var isAstNode3 = (n) => typeof n === "object" && n !== null && !Array.isArray(n);
var quasiText = (q) => {
  if (!isAstNode3(q)) return "";
  const value = q.value;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const cooked = value.cooked;
    if (typeof cooked === "string") return cooked;
  }
  return "";
};
var collectQuasiTexts = (ast, out) => {
  if (Array.isArray(ast)) {
    for (const c of ast) collectQuasiTexts(c, out);
    return;
  }
  if (!isAstNode3(ast)) return;
  if (ast.type === "TemplateLiteral" && Array.isArray(ast.quasis)) {
    for (const q of ast.quasis) out.push(quasiText(q));
  }
  for (const [k, v] of Object.entries(ast)) {
    if (k === "parent") continue;
    collectQuasiTexts(v, out);
  }
};
var classify = (name, after, knownFields) => {
  if (after.startsWith("(")) return `\`${name}(\u2026)\` reads as a function call`;
  if (after.startsWith("[")) return `\`${name}[\u2026]\` reads as an index`;
  if (/^\.[A-Za-z_]/.test(after)) {
    return `\`${name}.\u2026\` reads as a property access`;
  }
  if (CALLABLE_FUNCTION_NAMES.has(name)) {
    return `\`${name}\` is a built-in formula function`;
  }
  if (knownFields.has(name)) {
    return `\`${name}\` is a field declared on an objectType`;
  }
  return void 0;
};
var checkLiteralAtRefs = (source, knownFields, errors, path, field2) => {
  if (!source.startsWith("@")) return;
  let ast;
  try {
    ast = jsep(source.slice(1).trim());
  } catch {
    return;
  }
  const texts = [];
  collectQuasiTexts(ast, texts);
  const seen = /* @__PURE__ */ new Set();
  for (const text of texts) {
    for (const m of text.matchAll(new RegExp(AT_REF_PATTERN.source, "g"))) {
      const name = m[1];
      if (CSS_AT_RULES.has(name) || NAV_PSEUDO_PROTOCOLS.has(name)) continue;
      if (seen.has(name)) continue;
      const why = classify(
        name,
        text.slice(m.index + m[0].length),
        knownFields
      );
      if (!why) continue;
      seen.add(name);
      errors.push({
        path: pathStr([...path, field2]),
        kind: "template-at-ref",
        message: `\`@${name}\` in template text renders as the literal characters "@${name}" \u2014 ${why}, so this was probably meant as \`\${${name}}\`.`,
        fixHint: `Write \`\${${name}}\` to interpolate it, or leave \`@${name}\` if the literal text is intended.`,
        severity: "warning"
      });
    }
  }
};

// src/validation/treeRefs.ts
var TREE_STATIC_CHILDREN = /* @__PURE__ */ new Set([
  "value",
  "expanded",
  "dragAndDropState",
  "isDrawerClosed",
  "leftPanelWidth"
]);
var validateTreeNode = (node2, declared, errors, path) => {
  const rootType = node2.rootType;
  const nodeTypes = node2.nodeTypes;
  if (typeof rootType !== "string" || !isObj2(nodeTypes)) return;
  const types2 = Object.keys(nodeTypes);
  const typeSet = new Set(types2);
  const optionsOf = (type14) => {
    const opts = nodeTypes[type14];
    return isObj2(opts) ? opts : {};
  };
  const allowedChildTypes = (type14) => {
    const raw = optionsOf(type14).allowedChildTypes;
    return Array.isArray(raw) ? raw.filter((c) => typeof c === "string") : [];
  };
  const children2 = Array.isArray(node2._children) ? node2._children : [];
  const childNames = new Set(
    children2.filter(isObj2).map((child) => child.name).filter((name) => typeof name === "string").filter((name) => !TREE_STATIC_CHILDREN.has(name))
  );
  const tree2 = {
    rootType,
    types: types2,
    optionsOf,
    allowedChildTypes,
    childNames
  };
  const data = node2.data;
  const dataBound = isObj2(data) && typeof data.source === "string";
  const rootTypeKnown = typeSet.has(rootType);
  if (!rootTypeKnown) {
    errors.push({
      path: pathStr([...path, "rootType"]),
      kind: "tree-roottype-unknown",
      message: `Tree rootType \`${rootType}\` is not one of its nodeTypes.`,
      fixHint: nearMissHint(rootType, typeSet, "tree node type"),
      severity: "error"
    });
  }
  for (const type14 of types2) {
    allowedChildTypes(type14).forEach((child, i) => {
      if (typeSet.has(child)) return;
      errors.push({
        path: pathStr([...path, "nodeTypes", type14, "allowedChildTypes", i]),
        kind: "tree-allowedchild-unknown",
        message: `Allowed child type \`${child}\` of \`${type14}\` is not one of the tree's nodeTypes.`,
        fixHint: nearMissHint(child, typeSet, "tree node type"),
        severity: "error"
      });
    });
  }
  if (dataBound) {
    validateBoundTypes(tree2, declared, errors, path);
    const source = data.source;
    if ((source === "realtime" || source === "callable") && !(node2.canAdd === false && node2.canEdit === false)) {
      errors.push({
        path: pathStr([...path, "data", "source"]),
        kind: "tree-source-not-writable",
        message: `Tree is bound to a \`${source}\` data source, which its Add/Edit/Move mutations cannot write to \u2014 it will render read-only.`,
        fixHint: `Set \`canAdd: false\` and \`canEdit: false\` on the tree to make the intent explicit, or bind a \`storage\`/\`embedded\` source if it should be editable.`,
        severity: "warning"
      });
    }
  }
  if (rootTypeKnown) {
    const reachable = reachableFrom(rootType, allowedChildTypes, typeSet);
    for (const type14 of types2) {
      if (reachable.has(type14)) continue;
      errors.push({
        path: pathStr([...path, "nodeTypes", type14]),
        kind: "tree-unreachable-nodetype",
        message: `Tree node type \`${type14}\` is not reachable from rootType \`${rootType}\` via allowedChildTypes.`,
        fixHint: `Add \`${type14}\` to the allowedChildTypes of a reachable type, or remove it from nodeTypes.`,
        severity: "warning"
      });
    }
  }
};
var validateBoundTypes = (tree2, declared, errors, path) => {
  var _a2;
  const { types: types2, allowedChildTypes } = tree2;
  const appearsAsChild = new Set(types2.flatMap(allowedChildTypes));
  for (const type14 of types2) {
    const typePath = pathStr([...path, "nodeTypes", type14]);
    if (!declared.objectTypes.has(type14)) {
      errors.push({
        path: typePath,
        kind: "tree-nodetype-unknown-objecttype",
        message: `Tree node type \`${type14}\` is not a declared objectType. A data-bound tree's nodeTypes keys are ObjectType names.`,
        fixHint: nearMissHint(type14, declared.objectTypes, "object type"),
        severity: "error"
      });
      continue;
    }
    const parentRefFields = (_a2 = declared.objectTypeParentRefFields.get(type14)) != null ? _a2 : [];
    if (declared.objectTypesWithParent.has(type14) && appearsAsChild.has(type14)) {
      errors.push({
        path: typePath,
        kind: "parent-with-tree-placement",
        message: `ObjectType \`${type14}\` appears as a child in this tree AND declares a \`parent\` relation, so it is both PLACED (arranged in the tree, restorable) and OWNED (deleting the parent cascades into it).`,
        fixHint: `Intended for a record that genuinely belongs to its parent \u2014 otherwise drop the \`parent\` relation and let the tree placement stand on its own. See the ownership-vs-placement note on #1989.`,
        severity: "info"
      });
    }
    if (parentRefFields.length > 1) {
      errors.push({
        path: typePath,
        kind: "tree-multiple-parentref-fields",
        message: `ObjectType \`${type14}\` declares ${parentRefFields.length} \`parentRef\` fields (${parentRefFields.join(", ")}) \u2014 parent discovery is ambiguous.`,
        fixHint: `Keep exactly one \`parentRef\` field on \`${type14}\` and change the others to a plain field type.`,
        severity: "error"
      });
    }
  }
  validateDetailSlots(tree2, errors, path);
};
var validateDetailSlots = (tree2, errors, path) => {
  const { types: types2, optionsOf, childNames } = tree2;
  for (const type14 of types2) {
    const detail = optionsOf(type14).detail;
    if (typeof detail !== "string" || detail.length === 0) continue;
    if (childNames.has(detail)) continue;
    errors.push({
      path: pathStr([...path, "nodeTypes", type14, "detail"]),
      kind: "tree-detail-unknown-child",
      message: `Detail view \`${detail}\` for \`${type14}\` is not a child node of the tree.`,
      fixHint: nearMissHint(detail, childNames, "child node of the tree"),
      severity: "error"
    });
  }
};
var reachableFrom = (rootType, allowedChildTypes, known) => {
  const seen = /* @__PURE__ */ new Set([rootType]);
  const queue = [rootType];
  for (let i = 0; i < queue.length; i++) {
    for (const child of allowedChildTypes(queue[i])) {
      if (!known.has(child) || seen.has(child)) continue;
      seen.add(child);
      queue.push(child);
    }
  }
  return seen;
};
var isObj2 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);

// src/validation/reorderable.ts
var isObj3 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var validateReorderableSubsection = (node2, errors, path) => {
  const { reorderable, data } = node2;
  if (reorderable === void 0 || reorderable === false) return;
  if (!isObj3(data)) return;
  const orderField = typeof node2.orderField === "string" && node2.orderField.length > 0 ? node2.orderField : DEFAULT_ORDER_FIELD;
  const sorts = Array.isArray(data.sort) ? data.sort : data.sort !== void 0 ? [data.sort] : [];
  const sortedByOrderField = sorts.some(
    (s) => isObj3(s) && s.field === orderField
  );
  if (!sortedByOrderField) {
    errors.push({
      path: pathStr([...path, "data", "sort"]),
      kind: "subsection-reorderable-sort-mismatch",
      message: `Reorderable subsection writes its order to \`${orderField}\` but its data block is not sorted by that field, so a drop will not stick visually.`,
      fixHint: `Add \`sort: { field: "${orderField}" }\` to this subsection's \`data\` block (or set \`orderField\` to the field it already sorts by).`,
      severity: "warning"
    });
  }
  const filtered = typeof data.whereClause === "string" && data.whereClause.length > 0;
  const paginated = node2.pageSize !== void 0 && node2.pageSize !== null;
  if (filtered || paginated) {
    const why = [filtered ? "`data.whereClause`" : null, paginated ? "`pageSize`" : null].filter(Boolean).join(" and ");
    errors.push({
      path: pathStr([...path, filtered ? "data" : "pageSize", ...filtered ? ["whereClause"] : []]),
      kind: "subsection-reorderable-partial-view",
      message: `Reorderable subsection shows only part of its collection (${why}), but a drop renumbers just the rows on display \u2014 hidden rows keep stale \`${orderField}\` values and interleave with the moved ones on the next read.`,
      fixHint: `A reorderable list must show the whole collection: drop the ${why}, or reorder from an unfiltered, unpaginated view.`,
      severity: "warning"
    });
  }
  if (data.source === "realtime") {
    errors.push({
      path: pathStr([...path, "data", "source"]),
      kind: "subsection-reorderable-realtime",
      message: "Reorderable subsection is bound to a `realtime` source: `updateRealtime` is owner-only, so a user can only reorder records they own \u2014 a host cannot reorder other players' records.",
      fixHint: "Keep the order host-local (an app variable, as dave/squiggly's lobby does) or bind a `storage` source where any signed-in user may write.",
      severity: "warning"
    });
  }
};

// src/prompts/aiPricing.ts
var AI_MODELS = {
  // Anthropic — current generation
  "claude-haiku-5-5": {
    id: "claude-haiku-5-5",
    provider: "anthropic",
    name: "Claude Haiku 5.5",
    apiModelId: "claude-haiku-5-5",
    description: "Fast and efficient for lightweight tasks",
    inputPer1M: 0.1,
    outputPer1M: 0.5,
    // No explicit cache rates: the standard multipliers then follow whichever
    // card the prompt size selects.
    longPromptThreshold: 1e5,
    longInputPer1M: 0.5,
    longOutputPer1M: 2.5,
    contextWindow: 1e6,
    effort: "low"
  },
  "claude-haiku-4-5": {
    id: "claude-haiku-4-5",
    provider: "anthropic",
    name: "Claude Haiku 4.5",
    apiModelId: "claude-haiku-4-5",
    // Alias for claude-haiku-4-5-20251001
    description: "Fast and efficient for lightweight tasks",
    inputPer1M: 1,
    outputPer1M: 5,
    cacheReadPer1M: 0.1,
    cacheWrite5mPer1M: 1.25,
    cacheWrite1hPer1M: 2,
    contextWindow: 2e5,
    retiresNotBefore: "2026-10-15",
    supersededBy: "claude-haiku-5-5"
  },
  "claude-sonnet-5": {
    id: "claude-sonnet-5",
    provider: "anthropic",
    name: "Claude Sonnet 5",
    apiModelId: "claude-sonnet-5",
    description: "Balanced performance for most tasks",
    inputPer1M: 2,
    outputPer1M: 10,
    cacheReadPer1M: 0.2,
    cacheWrite5mPer1M: 2.5,
    cacheWrite1hPer1M: 4,
    contextWindow: 1e6,
    retiresNotBefore: "2027-06-30"
  },
  "claude-opus-5-5": {
    id: "claude-opus-5-5",
    provider: "anthropic",
    name: "Claude Opus 5.5",
    apiModelId: "claude-opus-5-5",
    description: "Most capable for complex reasoning",
    inputPer1M: 4,
    outputPer1M: 20,
    cacheReadPer1M: 0.2,
    cacheWrite5mPer1M: 5,
    cacheWrite1hPer1M: 8,
    contextWindow: 1e6,
    retiresNotBefore: "2027-09-22"
  },
  // Anthropic — legacy, kept so stale clients and explicit configs still
  // bill at list until they retire. Nothing in source should name these.
  "claude-sonnet-4-5": {
    id: "claude-sonnet-4-5",
    provider: "anthropic",
    name: "Claude Sonnet 4.5",
    apiModelId: "claude-sonnet-4-5",
    description: "Previous-generation Sonnet",
    inputPer1M: 3,
    outputPer1M: 15,
    cacheReadPer1M: 0.3,
    cacheWrite5mPer1M: 3.75,
    cacheWrite1hPer1M: 6,
    contextWindow: 1e6,
    retiresNotBefore: "2026-09-29"
  },
  "claude-opus-4-6": {
    id: "claude-opus-4-6",
    provider: "anthropic",
    name: "Claude Opus 4.6",
    apiModelId: "claude-opus-4-6",
    description: "Previous-generation Opus",
    inputPer1M: 5,
    outputPer1M: 25,
    cacheReadPer1M: 0.5,
    cacheWrite5mPer1M: 6.25,
    cacheWrite1hPer1M: 10,
    contextWindow: 1e6,
    retiresNotBefore: "2027-02-05"
  },
  // OpenAI
  "gpt-5-mini": {
    id: "gpt-5-mini",
    provider: "openai",
    name: "GPT-5 Mini",
    apiModelId: "gpt-5-mini",
    description: "Fast and affordable for well-defined tasks",
    inputPer1M: 0.25,
    outputPer1M: 2,
    contextWindow: 4e5
  },
  "gpt-5": {
    id: "gpt-5",
    provider: "openai",
    name: "GPT-5",
    apiModelId: "gpt-5",
    description: "Best for coding, agents, and high-performance tasks",
    inputPer1M: 1.25,
    outputPer1M: 10,
    contextWindow: 4e5
  },
  // Google. NOTE the id/apiModelId split: RTDB can't hold "." in a key, so
  // the pricing key is the dashed id, and billing maps the dotted id the
  // cloud function sends onto it via `textPricingKey`.
  "gemini-2-5-flash": {
    id: "gemini-2-5-flash",
    provider: "google",
    name: "Gemini 2.5 Flash",
    apiModelId: "gemini-2.5-flash",
    description: "Fast and efficient",
    inputPer1M: 0.3,
    outputPer1M: 2.5,
    contextWindow: 1e6
  }
};
var MODEL_ID_HAIKU = "claude-haiku-5-5";
var MODEL_ID_SONNET = "claude-sonnet-5";
var MODEL_ID_OPUS = "claude-opus-5-5";
var MODEL_ALIASES = {
  haiku: MODEL_ID_HAIKU,
  sonnet: MODEL_ID_SONNET,
  opus: MODEL_ID_OPUS
};
var resolveModelAlias = (modelArg) => {
  var _a2;
  return modelArg ? (_a2 = MODEL_ALIASES[modelArg]) != null ? _a2 : modelArg : MODEL_ID_HAIKU;
};
var textPricingKey = (model) => model.replace(/\./g, "-");
var servedModelId = (id) => {
  var _a2, _b;
  return (_b = (_a2 = AI_MODELS[textPricingKey(id)]) == null ? void 0 : _a2.supersededBy) != null ? _b : id;
};
var anthropicRequestExtras = (id) => {
  var _a2;
  const effort = (_a2 = AI_MODELS[textPricingKey(id)]) == null ? void 0 : _a2.effort;
  return effort ? { output_config: { effort } } : {};
};
var textRateCard = (model, promptTokens) => model.longPromptThreshold !== void 0 && model.longInputPer1M !== void 0 && model.longOutputPer1M !== void 0 && promptTokens > model.longPromptThreshold ? {
  inputPer1M: model.longInputPer1M,
  outputPer1M: model.longOutputPer1M,
  long: true
} : { inputPer1M: model.inputPer1M, outputPer1M: model.outputPer1M, long: false };
var IMAGE_MODELS = {
  "flux-schnell": {
    id: "flux-schnell",
    name: "Flux Schnell",
    pricePerImage: 3e-3,
    description: "Fast and cost-effective image generation"
  },
  "flux-dev": {
    id: "flux-dev",
    name: "Flux Dev",
    pricePerImage: 0.055,
    description: "Development-quality image generation"
  },
  "flux-pro": {
    id: "flux-pro",
    name: "Flux Pro",
    pricePerImage: 0.055,
    description: "Professional-quality image generation"
  },
  "nano-banana-2": {
    id: "nano-banana-2",
    name: "Nano Banana 2",
    // Google's GA id. The previous 2.5 Flash Image id shuts down 2026-10-02
    // and the web client sends THIS id through to the cloud function. (#1654)
    apiModelId: "gemini-3.1-flash-image",
    pricePerImage: 0.067,
    description: "High-quality image generation with better text rendering"
  }
};
var CACHE_READ_MULTIPLIER = 0.1;
var CACHE_WRITE_5M_MULTIPLIER = 1.25;
var CACHE_WRITE_1H_MULTIPLIER = 2;
var cacheRates = (model) => {
  var _a2, _b, _c;
  return {
    readPer1M: (_a2 = model.cacheReadPer1M) != null ? _a2 : model.inputPer1M * CACHE_READ_MULTIPLIER,
    write5mPer1M: (_b = model.cacheWrite5mPer1M) != null ? _b : model.inputPer1M * CACHE_WRITE_5M_MULTIPLIER,
    write1hPer1M: (_c = model.cacheWrite1hPer1M) != null ? _c : model.inputPer1M * CACHE_WRITE_1H_MULTIPLIER
  };
};
var calculateCost = (usage) => {
  var _a2, _b, _c;
  const model = AI_MODELS[textPricingKey(usage.model)];
  if (!model) {
    console.warn(`Unknown model: ${usage.model}`);
    return 0;
  }
  const promptTokens = usage.inputTokens + ((_a2 = usage.cacheReadTokens) != null ? _a2 : 0) + ((_b = usage.cacheWrite5mTokens) != null ? _b : 0) + ((_c = usage.cacheWrite1hTokens) != null ? _c : 0);
  const card = textRateCard(model, promptTokens);
  const rates = cacheRates(
    card.long ? { inputPer1M: card.inputPer1M } : model
  );
  const perM = (tokens, rate) => (tokens != null ? tokens : 0) / 1e6 * rate;
  return perM(usage.inputTokens, card.inputPer1M) + perM(usage.outputTokens, card.outputPer1M) + perM(usage.cacheReadTokens, rates.readPer1M) + perM(usage.cacheWrite5mTokens, rates.write5mPer1M) + perM(usage.cacheWrite1hTokens, rates.write1hPer1M);
};
var calculateImageCost = (usage) => {
  const model = IMAGE_MODELS[usage.model];
  if (!model) {
    console.warn(`Unknown image model: ${usage.model}`);
    return 0;
  }
  return usage.imagesGenerated * model.pricePerImage;
};
var getModel = (modelId) => {
  return AI_MODELS[modelId];
};
var getModelsByProvider = (provider) => {
  return Object.values(AI_MODELS).filter((m) => m.provider === provider);
};
var formatCostUSD = (cost) => {
  if (cost < 0.01) {
    return `$${cost.toFixed(4)}`;
  }
  return `$${cost.toFixed(2)}`;
};
var CREDIT_SYMBOL = "\xA2";
var formatCredits = (credits) => `${Math.round(credits * 100) / 100}${CREDIT_SYMBOL}`;

// src/validation/visionRefs.ts
var MIN_VISION_SOURCE_WIDTH = 1024;
var RECOMMENDED_VISION_WIDTH = 1568;
var isObj4 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var isAiQueryStep = (node2) => node2.type === "flowStep:aiQuery" || typeof node2.type === "string" && isVariant({ type: node2.type, variant: node2.variant }, FLOW_STEP, "aiQuery");
var visionSourceRoot = (source) => {
  const trimmed = source.trim();
  if (!trimmed.startsWith("@")) return void 0;
  const expr = trimmed.slice(1);
  const match2 = /^([A-Za-z_][A-Za-z0-9_]*)((?:\.[A-Za-z_][A-Za-z0-9_]*|\[[^\]]*\])*)$/.exec(
    expr
  );
  if (!match2) return void 0;
  return match2[1];
};
var collectVisionDeclarations = (appConfig3) => {
  const nodesByName = /* @__PURE__ */ new Map();
  const walk8 = (value) => {
    if (Array.isArray(value)) {
      value.forEach(walk8);
      return;
    }
    if (!isObj4(value)) return;
    if (typeof value.name === "string" && value.name.length > 0) {
      nodesByName.set(value.name, value);
    }
    if (Array.isArray(value._children)) value._children.forEach(walk8);
  };
  walk8(appConfig3);
  return { nodesByName };
};
var validateVisionSource = (node2, declared, errors, path) => {
  var _a2;
  const source = node2.imageSource;
  if (typeof source !== "string" || source.trim().length === 0) return;
  const root = visionSourceRoot(source);
  if (!root) return;
  const sourcePath = [...path, "imageSource"];
  const target = declared.nodesByName.get(root);
  if (!target) {
    errors.push({
      path: pathStr(sourcePath),
      kind: "vision-source-unresolved",
      message: `\`imageSource\` reads \`${root}\`, but no node called \`${root}\` exists in this org \u2014 the step would run with no image and the AI would answer from the prompt alone.`,
      fixHint: (_a2 = nearMissHint(root, new Set(declared.nodesByName.keys()), "node")) != null ? _a2 : `Point \`imageSource\` at an image \`fileInput\` in the same flow, e.g. \`@photo\`.`,
      severity: "error"
    });
    return;
  }
  if (target.type !== "fileInput") return;
  const options = target.imageOutputOptions;
  const width = isObj4(options) ? options.width : void 0;
  if (typeof width === "number" && width >= MIN_VISION_SOURCE_WIDTH) return;
  if (typeof width === "string") return;
  const detail = typeof width === "number" ? `is only ${width}px wide` : `has no \`imageOutputOptions.width\`, so it uploads at the 800px default`;
  errors.push({
    path: pathStr(sourcePath),
    kind: "vision-low-fidelity-source",
    message: `\`imageSource\` reads \`${root}\`, whose uploaded image ${detail} \u2014 fine print in a receipt or label will not survive that.`,
    fixHint: `Set \`imageOutputOptions.width\` to ${RECOMMENDED_VISION_WIDTH} on \`${root}\` if the image carries text; leave it if you are identifying a whole subject like a plant.`,
    severity: "warning"
  });
};
var VISION_CAPABLE_PROVIDERS = /* @__PURE__ */ new Set(["anthropic"]);
var checkModel = (node2, path, errors) => {
  const source = node2.imageSource;
  if (typeof source !== "string" || source.trim().length === 0) return;
  const model = node2.model;
  if (typeof model !== "string" || model.startsWith("@")) return;
  const catalogued = AI_MODELS[model];
  if (!catalogued) return;
  if (VISION_CAPABLE_PROVIDERS.has(catalogued.provider)) return;
  errors.push({
    path: pathStr([...path, "model"]),
    kind: "vision-model-unsupported",
    message: `This step sends images, but \`model\` names \`${model}\` (${catalogued.provider}). Image blocks are built in Anthropic's request shape, so a ${catalogued.provider} model cannot read them.`,
    fixHint: `Use an Anthropic model \u2014 leave \`model\` unset for the default \`${MODEL_ID_HAIKU}\`, or set \`${MODEL_ID_SONNET}\` for harder extraction.`,
    severity: "error"
  });
};
var validateVisionSteps = (json) => {
  const errors = [];
  if (!isObj4(json)) return errors;
  const walk8 = (value, path) => {
    if (Array.isArray(value)) {
      value.forEach((child, i) => walk8(child, [...path, i]));
      return;
    }
    if (!isObj4(value)) return;
    if (isAiQueryStep(value)) checkModel(value, path, errors);
    if (Array.isArray(value._children)) {
      value._children.forEach(
        (c, i) => walk8(c, [...path, "_children", i])
      );
    }
  };
  walk8(json.appConfig, ["appConfig"]);
  return errors;
};

// src/validation/references.ts
var BUILTIN_PLUGINS = /* @__PURE__ */ new Set(["CardLink", "ItemCard"]);
var unparsableParamReason = (value) => {
  if (typeof value !== "string") {
    return `is a ${value === null ? "null" : typeof value}, not a string (one non-string value makes the whole instance an invalid node)`;
  }
  if (value.trim() === "") return void 0;
  try {
    const ast = jsep(value);
    return ast.type === "Compound" ? "is not a single expression" : void 0;
  } catch {
    return "does not parse as a formula";
  }
};
var quotedParam = (value) => `@'${String(value).replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`;
var META_NODE_TYPES = [
  "objectType",
  "objectInstance",
  "field",
  "plugin",
  "record",
  "user"
];
var STANDARD_NODE_TYPES = /* @__PURE__ */ new Set([
  ...NODE_TYPES,
  ...VARIANT_TYPE_KEYS,
  ...Object.keys(VARIANT_TYPES),
  ...META_NODE_TYPES,
  ...Object.keys(NODE_TYPE_ALIASES)
]);
var AUTO_INJECTED_FIELDS = RECORD_META_FIELDS;
var validateReferences = (json) => {
  if (!isObj5(json)) return [];
  const declared = collectDeclared(json);
  const errors = [];
  const vision = collectVisionDeclarations(json.appConfig);
  walkAppTree(json.appConfig, declared, errors, ["appConfig"], vision);
  for (const { node: plugin2, path: at } of sectionItems(json.plugins)) {
    const { parameters, ...pluginWithoutParams } = plugin2;
    void parameters;
    walkAppTree(pluginWithoutParams, declared, errors, ["plugins", ...at]);
    validatePluginParameters(plugin2, declared, errors, ["plugins", ...at]);
    validatePluginName(plugin2, errors, ["plugins", ...at]);
    validateWidgetOwner(plugin2, json.minClientVersion, errors, [
      "plugins",
      ...at
    ]);
  }
  const objectTypeItems = sectionItems(json.objectTypes);
  for (const { node: ot, path: at } of objectTypeItems) {
    validateObjectTypeFields(ot, declared, errors, ["objectTypes", ...at]);
    validateParentRelation(ot, declared, errors, ["objectTypes", ...at]);
  }
  validateParentAcyclic(objectTypeItems, errors);
  return errors;
};
var collectDeclared = (json) => {
  const objectTypes = /* @__PURE__ */ new Set();
  const screens = /* @__PURE__ */ new Set();
  const navTargets = /* @__PURE__ */ new Set();
  const plugins3 = /* @__PURE__ */ new Set();
  const objectTypeFields = /* @__PURE__ */ new Map();
  const objectTypeFieldTypes = /* @__PURE__ */ new Map();
  const objectTypeParentRefFields = /* @__PURE__ */ new Map();
  const objectTypesWithParent = /* @__PURE__ */ new Set();
  const allFieldNames = /* @__PURE__ */ new Set();
  const objectTypeChildren = sectionNodes(json.objectTypes);
  for (const ot of objectTypeChildren) {
    if (typeof ot.name !== "string") continue;
    objectTypes.add(ot.name);
    if (isObj5(ot.parent)) objectTypesWithParent.add(ot.name);
    const fields = /* @__PURE__ */ new Set();
    const fieldTypes = /* @__PURE__ */ new Map();
    const parentRefFields = [];
    const fieldsArr = ot.fields;
    if (Array.isArray(fieldsArr)) {
      for (const f of fieldsArr) {
        if (!isObj5(f)) continue;
        const fname = f.name;
        if (typeof fname === "string" && fname.length > 0) {
          fields.add(fname);
          allFieldNames.add(fname);
          const ftype = f.type;
          if (typeof ftype === "string") {
            fieldTypes.set(
              fname,
              parseFieldType(ftype, f.isList === true)
            );
          }
          if (ftype === "parentRef") {
            parentRefFields.push(fname);
          }
        }
      }
    }
    objectTypeFields.set(ot.name, fields);
    objectTypeFieldTypes.set(ot.name, fieldTypes);
    objectTypeParentRefFields.set(ot.name, parentRefFields);
  }
  collectNavTargetsInto(json.appConfig, screens, navTargets);
  const pluginChildren = sectionNodes(json.plugins);
  const pluginParams = /* @__PURE__ */ new Map();
  for (const p of pluginChildren) {
    if (typeof p.name === "string") {
      plugins3.add(p.name);
      const names2 = /* @__PURE__ */ new Set();
      if (Array.isArray(p.parameters)) {
        for (const param of p.parameters) {
          if (isObj5(param) && typeof param.name === "string") {
            names2.add(param.name);
          }
        }
      }
      pluginParams.set(p.name, names2);
    }
  }
  return {
    objectTypes,
    screens,
    navTargets,
    plugins: plugins3,
    builtinPlugins: BUILTIN_PLUGINS,
    pluginParams,
    objectTypeFields,
    objectTypeFieldTypes,
    objectTypeParentRefFields,
    objectTypesWithParent,
    allFieldNames
  };
};
var collectNavTargetsInto = (value, screens, navTargets) => {
  if (Array.isArray(value)) {
    value.forEach((c) => collectNavTargetsInto(c, screens, navTargets));
    return;
  }
  if (!isObj5(value)) return;
  const node2 = value;
  if (node2.type === "screen" && typeof node2.name === "string") {
    screens.add(node2.name);
  }
  if (typeof node2.name === "string") {
    navTargets.add(node2.name);
  }
  if (Array.isArray(node2._children)) {
    node2._children.forEach(
      (c) => collectNavTargetsInto(c, screens, navTargets)
    );
  }
};
var collectNavTargets = (appConfig3) => {
  const screens = /* @__PURE__ */ new Set();
  const navTargets = /* @__PURE__ */ new Set();
  collectNavTargetsInto(appConfig3, screens, navTargets);
  return navTargets;
};
var collectFlowBearingNames = (appConfig3) => {
  const names2 = /* @__PURE__ */ new Set();
  const walk8 = (value) => {
    if (Array.isArray(value)) {
      return value.map(walk8).some(Boolean);
    }
    if (!isObj5(value)) return false;
    const node2 = value;
    const childrenBearFlow = walk8(node2._children);
    const bearsFlow = node2.type === "flow" || childrenBearFlow;
    if (bearsFlow && typeof node2.name === "string") names2.add(node2.name);
    return bearsFlow;
  };
  walk8(appConfig3);
  return names2;
};
var walkAppTree = (value, declared, errors, path = [], vision) => {
  if (Array.isArray(value)) {
    value.forEach(
      (child, i) => walkAppTree(child, declared, errors, [...path, i], vision)
    );
    return;
  }
  if (!isObj5(value)) return;
  const node2 = value;
  const data = node2.data;
  if (isObj5(data)) {
    const dataType = data.dataType;
    if (typeof dataType === "string" && !declared.objectTypes.has(dataType)) {
      errors.push({
        path: pathStr([...path, "data", "dataType"]),
        kind: "missing-objectType",
        message: `Referenced dataType \`${dataType}\` not declared in objectTypes.`,
        fixHint: nearMissHint(dataType, declared.objectTypes, "object type"),
        severity: "error"
      });
    }
    const sort = data.sort;
    if (isObj5(sort) && typeof dataType === "string" && declared.objectTypeFields.has(dataType)) {
      const sortField = sort.field;
      const fields = declared.objectTypeFields.get(dataType);
      if (typeof sortField === "string" && sortField.length > 0 && !fields.has(sortField) && !AUTO_INJECTED_FIELDS.has(sortField)) {
        errors.push({
          path: pathStr([...path, "data", "sort", "field"]),
          kind: "unknown-sort-field",
          message: `Sort field \`${sortField}\` is not a field on objectType \`${dataType}\`.`,
          fixHint: nearMissHint(sortField, fields, `field on \`${dataType}\``),
          severity: "error"
        });
      }
      if (sortField === "id") {
        errors.push({
          path: pathStr([...path, "data", "sort", "field"]),
          kind: "arbitrary-sort-field",
          message: `Sorting on \`id\` gives an arbitrary order: \`${dataType}\` ids are \`${dataType}-<random>\` since #1772, so this sorts alphabetically over random suffixes rather than by insertion.`,
          fixHint: "Sort on `createdAt` for oldest-first, or another meaningful field.",
          severity: "warning"
        });
      }
    }
    const span2 = data.span;
    if (isObj5(span2) && typeof dataType === "string" && declared.objectTypeFields.has(dataType)) {
      const fields = declared.objectTypeFields.get(dataType);
      for (const key of ["startField", "endField"]) {
        const fieldName = span2[key];
        if (typeof fieldName === "string" && fieldName.length > 0 && !fields.has(fieldName) && !AUTO_INJECTED_FIELDS.has(fieldName)) {
          errors.push({
            path: pathStr([...path, "data", "span", key]),
            kind: "span-unknown-field",
            message: `Span ${key} \`${fieldName}\` is not a field on objectType \`${dataType}\`.`,
            fixHint: nearMissHint(
              fieldName,
              fields,
              `field on \`${dataType}\``
            ),
            severity: "error"
          });
        }
      }
    }
  }
  if (typeof node2.type === "string" && !STANDARD_NODE_TYPES.has(node2.type) && !declared.plugins.has(node2.type) && !declared.builtinPlugins.has(node2.type)) {
    errors.push({
      path: pathStr([...path, "type"]),
      kind: "missing-plugin",
      message: `Plugin \`${node2.type}\` not declared in plugins.`,
      fixHint: nearMissHint(
        node2.type,
        /* @__PURE__ */ new Set([...declared.plugins, ...declared.builtinPlugins]),
        "plugin"
      ),
      severity: "error"
    });
  }
  if (typeof node2.type === "string" && declared.pluginParams.has(node2.type) && isObj5(node2.parameters)) {
    const declaredParams = declared.pluginParams.get(node2.type);
    for (const key of Object.keys(node2.parameters)) {
      if (!declaredParams.has(key)) {
        errors.push({
          path: pathStr([...path, "parameters", key]),
          kind: "plugin-param-undeclared",
          message: `Plugin instance passes parameter \`${key}\`, which plugin \`${node2.type}\` does not declare \u2014 it is silently dropped and never reaches the widget.`,
          fixHint: nearMissHint(
            key,
            declaredParams,
            `parameter of \`${node2.type}\``
          ),
          severity: "error"
        });
      }
    }
  }
  if (typeof node2.type === "string" && declared.pluginParams.has(node2.type)) {
    const declaredParams = declared.pluginParams.get(node2.type);
    const passed = isObj5(node2.parameters) ? node2.parameters : {};
    const missing = [...declaredParams].filter((n) => !(n in passed));
    if (missing.length > 0) {
      const bare = Object.keys(passed).length === 0;
      errors.push({
        path: pathStr([...path, "parameters"]),
        kind: "plugin-param-missing",
        message: bare ? `Plugin instance of \`${node2.type}\` passes no parameters \u2014 it fails at render with "Error invoking ${node2.type}()".` : `Plugin instance of \`${node2.type}\` omits declared parameter(s) ${missing.map((n) => `\`${n}\``).join(", ")} \u2014 each reaches the widget as \`undefined\`.`,
        fixHint: `Pass every parameter \`${node2.type}\` declares: ${[...declaredParams].map((n) => `\`${n}\``).join(", ")}.`,
        severity: bare ? "error" : "warning"
      });
    }
  }
  if (typeof node2.type === "string" && declared.pluginParams.has(node2.type) && isObj5(node2.parameters)) {
    for (const [key, value2] of Object.entries(node2.parameters)) {
      const reason = unparsableParamReason(value2);
      if (reason) {
        errors.push({
          path: pathStr([...path, "parameters", key]),
          kind: "plugin-param-unparsable",
          message: `Plugin instance of \`${node2.type}\` passes parameter \`${key}\` as ${JSON.stringify(value2)}, which ${reason} \u2014 every parameter is spliced raw into \`@${node2.type}(\u2026)\`, so the widget fails at render with "Error invoking ${node2.type}()".`,
          fixHint: `Write the parameter as a formula: ${JSON.stringify(quotedParam(value2))} for literal text, or \`@<expression>\` for a value. Quote every literal the same way \u2014 a screen name for a link or an emoji too, which parse as identifiers that resolve to nothing.`,
          severity: "error"
        });
      }
    }
  }
  if (node2.type === "tree" && path[0] === "appConfig") {
    validateTreeNode(node2, declared, errors, path);
  }
  if ((node2.type === "subsection" || node2.type === "list") && path[0] === "appConfig") {
    validateReorderableSubsection(node2, errors, path);
  }
  if (vision && isAiQueryStep(node2) && path[0] === "appConfig") {
    validateVisionSource(node2, vision, errors, path);
  }
  if (typeof node2.htmlTemplate === "string") {
    checkRelTargets(node2.htmlTemplate, declared, errors, path);
    const matches = node2.htmlTemplate.matchAll(/href="([^"@${}]+)"/g);
    for (const m of matches) {
      const target = m[1];
      if (isBrowserHref(target) || target.includes("/")) continue;
      if (!declared.navTargets.has(target)) {
        errors.push({
          path: pathStr([...path, "htmlTemplate"]),
          kind: "missing-screen",
          message: `Anchor target \`${target}\` does not match any named node in appConfig.`,
          fixHint: nearMissHint(
            target,
            declared.navTargets,
            "navigation target"
          ),
          severity: "error"
        });
      }
    }
    const fieldRefRe = /(\w+)\[\$index\]\.(\w+)/g;
    const seenInThisTemplate = /* @__PURE__ */ new Set();
    for (const m of node2.htmlTemplate.matchAll(fieldRefRe)) {
      const prefix = m[1];
      const fname = m[2];
      const owner = declared.objectTypeFields.get(prefix);
      const legal = fname === "type" || AUTO_INJECTED_FIELDS.has(fname) || (owner ? owner.has(fname) : declared.allFieldNames.has(fname));
      const seenKey = `${prefix}.${fname}`;
      if (legal || seenInThisTemplate.has(seenKey)) {
        seenInThisTemplate.add(seenKey);
        continue;
      }
      seenInThisTemplate.add(seenKey);
      errors.push({
        path: pathStr([...path, "htmlTemplate"]),
        kind: "unknown-field-ref",
        message: owner ? `Template references field \`${fname}\` not declared on objectType \`${prefix}\`.` : `Template references field \`${fname}\` not declared on any objectType.`,
        fixHint: nearMissHint(fname, owner != null ? owner : declared.allFieldNames, "field"),
        severity: "error"
      });
    }
  }
  for (const [k, v] of Object.entries(node2)) {
    if (typeof v === "string") {
      checkLiteralAtRefs(v, declared.allFieldNames, errors, path, k);
    }
  }
  for (const [k, v] of Object.entries(node2)) {
    walkAppTree(v, declared, errors, [...path, k], vision);
  }
};
var fieldTypeProblem = (type14, isList, declared, what) => {
  const parsed = parseFieldType(type14, isList);
  const candidates = /* @__PURE__ */ new Set([...FIELD_TYPE_BUILTINS, ...declared.objectTypes]);
  if (parsed.kind === "invalid") {
    const nested = /<\s*(list|map)\s*</.test(type14);
    return {
      message: nested ? `${what} \`${type14}\` nests a collection inside a collection \u2014 only one level (\`list<T>\` / \`map<T>\`) is supported.` : `${what} \`${type14}\` is not a legal field type.`,
      fixHint: nested ? "Declare the element as its own objectType and use `list<ThatType>`." : "Use a built-in (text, number, boolean, temporal, \u2026), a declared objectType name, or `list<T>` / `map<T>` over one of those."
    };
  }
  const scalar = scalarOf(parsed);
  if (!scalar || scalar.kind === "builtin") return void 0;
  if (declared.objectTypes.has(scalar.name)) return void 0;
  const isElem = parsed.kind === "list" || parsed.kind === "map";
  return {
    message: isElem ? `${what} \`${type14}\`: element type \`${scalar.name}\` is neither a built-in field type nor a declared object type.` : `${what} \`${type14}\` is neither a built-in field type nor a declared object type.`,
    fixHint: nearMissHint(scalar.name, candidates, "field type")
  };
};
var validatePluginName = (plugin2, errors, basePath) => {
  if (plugin2.widgetOwner !== void 0) return;
  if (typeof plugin2.name !== "string" || PLUGIN_NAME_PATTERN.test(plugin2.name)) return;
  errors.push({
    path: pathStr([...basePath, "name"]),
    kind: "plugin-name-invalid",
    message: `Plugin name \`${plugin2.name}\` isn't a formula identifier. Each instance renders as \`@${plugin2.name}(\u2026)\`, which won't parse as one call, so it never renders.`,
    fixHint: "Use letters, digits and `_` only, starting with a letter (e.g. `tapCounter`), and rename every instance's `type` to match.",
    severity: "error"
  });
};
var validateWidgetOwner = (plugin2, minClientVersion, errors, basePath) => {
  const owner = plugin2.widgetOwner;
  if (owner === void 0) return;
  const at = (field2) => pathStr([...basePath, field2]);
  if (typeof owner !== "string" || !WIDGET_OWNER_PATTERN.test(owner)) {
    errors.push({
      path: at("widgetOwner"),
      kind: "widget-owner-invalid",
      message: "`widgetOwner` must be a Firebase uid (letters, digits, `_` and `-`, at most 128).",
      fixHint: "Use your own uid, as printed by `og widget deploy`.",
      severity: "error"
    });
  }
  if (typeof plugin2.name === "string" && !WIDGET_NAME_PATTERN.test(plugin2.name)) {
    errors.push({
      path: at("name"),
      kind: "widget-owner-invalid",
      message: "An owned widget is loaded by the plugin's name, and `og widget deploy` only accepts lower-case letters and digits, starting with a letter (at most 40), so this name can never resolve.",
      fixHint: "Rename the plugin (and its instances) to the name the widget was deployed under.",
      severity: "error"
    });
  }
  if (plugin2.runtime !== "sandboxed") {
    errors.push({
      path: at("runtime"),
      kind: "widget-owner-not-sandboxed",
      message: '`widgetOwner` names a hosted widget, which only a `runtime: "sandboxed"` plugin loads.',
      fixHint: 'Set `runtime: "sandboxed"` (and `htmlTemplate: ""`), or drop `widgetOwner`.',
      severity: "error"
    });
  }
  const capabilities = [
    ["embedsThirdPartyMedia", plugin2.embedsThirdPartyMedia === true],
    [
      "connections",
      Array.isArray(plugin2.connections) && plugin2.connections.length > 0
    ]
  ];
  for (const [field2, set] of capabilities) {
    if (!set) continue;
    errors.push({
      path: at(field2),
      kind: "widget-owner-capability",
      message: `\`${field2}\` is first-party-only; a user-owned widget never gets it, so it has no effect here.`,
      fixHint: `Remove \`${field2}\` from this plugin.`,
      severity: "error"
    });
  }
  if (typeof minClientVersion !== "number" || minClientVersion < WIDGET_OWNER_MIN_CLIENT_VERSION) {
    errors.push({
      path: at("widgetOwner"),
      kind: "widget-owner-needs-min-client",
      message: "An older app ignores `widgetOwner` and would load the first-party widget of the same name instead.",
      fixHint: `Set the top-level \`minClientVersion\` to at least ${WIDGET_OWNER_MIN_CLIENT_VERSION}, so older apps update first.`,
      severity: "error"
    });
  }
};
var validatePluginParameters = (plugin2, declared, errors, basePath) => {
  const params = plugin2.parameters;
  if (!Array.isArray(params)) return;
  for (const [j, param] of params.entries()) {
    if (!isObj5(param)) continue;
    const type14 = param.type;
    if (typeof type14 !== "string") {
      const name = param.name;
      errors.push({
        path: pathStr([...basePath, "parameters", j, "type"]),
        kind: "plugin-param-untyped",
        message: `Plugin parameter${typeof name === "string" ? ` \`${name}\`` : ""} declares no \`type\`.`,
        fixHint: "Give it a field type (text, number, boolean, \u2026, a declared objectType, or `list<T>`).",
        severity: "warning"
      });
      continue;
    }
    const problem = fieldTypeProblem(type14, false, declared, "Plugin parameter type");
    if (!problem) continue;
    errors.push({
      path: pathStr([...basePath, "parameters", j, "type"]),
      kind: "invalid-field-type",
      ...problem,
      severity: "error"
    });
  }
};
var validateObjectTypeFields = (ot, declared, errors, basePath) => {
  const fields = ot.fields;
  if (!Array.isArray(fields)) return;
  for (const [j, field2] of fields.entries()) {
    if (!isObj5(field2)) continue;
    if (field2.name === "_parent") {
      errors.push({
        path: pathStr([...basePath, "fields", j]),
        kind: "tree-mixed-parent-spellings",
        message: `ObjectType \`${String(ot.name)}\` declares a field named \`_parent\`, which is reserved for the platform's implicit record-tree parent link.`,
        fixHint: 'Rename the field (e.g. `parentId`). To link records to a tree parent, either declare one field with `"type": "parentRef"` or declare none and let the platform write `_parent`.',
        severity: "error"
      });
    }
    const type14 = field2.type;
    if (typeof type14 !== "string") continue;
    const problem = fieldTypeProblem(
      type14,
      field2.isList === true,
      declared,
      "Field type"
    );
    if (!problem) continue;
    errors.push({
      path: pathStr([...basePath, "fields", j, "type"]),
      kind: "invalid-field-type",
      ...problem,
      severity: "error"
    });
  }
};
var PARENT_KEYS = /* @__PURE__ */ new Set(["type", "idField"]);
var validateParentRelation = (ot, declared, errors, basePath) => {
  var _a2;
  const rel = ot.parent;
  if (rel === void 0 || rel === null) return;
  const at = (member) => pathStr(member ? [...basePath, "parent", member] : [...basePath, "parent"]);
  const push = (path, message, fixHint) => {
    errors.push({
      path,
      kind: "parent-relation-invalid",
      message,
      fixHint,
      severity: "error"
    });
  };
  if (!isObj5(rel)) {
    push(
      at(),
      "`parent` must be an object of the form `{ type, idField }`.",
      'Write `"parent": { "type": "Game", "idField": "gameId" }`, or remove it.'
    );
    return;
  }
  for (const key of Object.keys(rel)) {
    if (key === "type" || key === "idField") continue;
    errors.push({
      path: at(key),
      kind: "unknown-field",
      message: `Unknown parent property \`${key}\`.`,
      fixHint: nearMissHint(key, PARENT_KEYS, "parent property"),
      severity: "error"
    });
  }
  const name = typeof ot.name === "string" ? ot.name : void 0;
  const parentType = rel.type;
  const idField = rel.idField;
  if (typeof parentType !== "string" || parentType.length === 0) {
    push(
      at("type"),
      "`parent.type` is missing \u2014 a parent relation needs both `type` and `idField`.",
      'Name the parent object type, e.g. `"type": "Game"`.'
    );
  } else if (!declared.objectTypes.has(parentType)) {
    push(
      at("type"),
      `Parent type \`${parentType}\` is not declared in objectTypes.`,
      nearMissHint(parentType, declared.objectTypes, "object type")
    );
  } else if (name !== void 0 && parentType === name) {
    push(
      at("type"),
      `\`${name}\` declares itself as its own parent \u2014 deleting a record would cascade into its own collection.`,
      "Point `parent.type` at a different object type, or remove the relation."
    );
  }
  if (typeof idField !== "string" || idField.length === 0) {
    push(
      at("idField"),
      "`parent.idField` is missing \u2014 a parent relation needs both `type` and `idField`.",
      'Name the field on this type that holds the parent\'s id, e.g. `"idField": "gameId"`.'
    );
    return;
  }
  const own = name !== void 0 ? declared.objectTypeFields.get(name) : void 0;
  if (own === void 0) return;
  if (own.has(idField) || AUTO_INJECTED_FIELDS.has(idField)) {
    const placement = [
      "_parent",
      ...name !== void 0 ? (_a2 = declared.objectTypeParentRefFields.get(name)) != null ? _a2 : [] : []
    ];
    if (placement.includes(idField)) {
      errors.push({
        path: at("idField"),
        kind: "parent-on-placement-field",
        message: `\`${idField}\` is ${idField === "_parent" ? "the implicit `_parent` link" : "a `parentRef` field"} \u2014 a tree PLACEMENT link \u2014 but \`parent\` declares OWNERSHIP, so deleting the parent record will cascade into \`${name}\`.`,
        fixHint: `If records of \`${name}\` are merely placed in the tree and should survive their parent's deletion, remove the \`parent\` relation. If they really are owned, point \`parent.idField\` at a plain id field instead of the \`parentRef\` one.`,
        severity: "warning"
      });
    }
    return;
  }
  push(
    at("idField"),
    `\`parent.idField\` \`${idField}\` is not a field on objectType \`${name}\`.`,
    nearMissHint(idField, own, "field")
  );
};
var validateParentAcyclic = (objectTypeItems, errors) => {
  var _a2;
  const cycles = parentCycles(
    objectTypeItems.map((item) => item.node)
  );
  for (const chain of cycles) {
    const head2 = chain[0];
    const item = objectTypeItems.find((ot) => ot.node.name === head2);
    errors.push({
      path: pathStr([
        "objectTypes",
        ...(_a2 = item == null ? void 0 : item.path) != null ? _a2 : ["_children", 0],
        "parent",
        "type"
      ]),
      kind: "parent-relation-cycle",
      message: `Parent relations form a cycle: ${chain.join(" \u2192 ")}.`,
      fixHint: "Break the cycle \u2014 deleting a record cascades to its children, so the parent graph must be a tree.",
      severity: "error"
    });
  }
};
var HTML_REL_TOKENS = /* @__PURE__ */ new Set([
  "noopener",
  "noreferrer",
  "nofollow",
  "opener",
  "external",
  "alternate",
  "author",
  "bookmark",
  "help",
  "license",
  "next",
  "prev",
  "search",
  "tag"
]);
var isDynamicAttrValue = (value) => value.includes(INTERP_PLACEHOLDER) || value.includes("$") || value.includes("@") || value.includes("{");
var targetSuffix = (target) => {
  var _a2;
  const noRoot = target.startsWith("/") ? target.slice(1) : target;
  const segments = noRoot.split(".");
  const last2 = (_a2 = segments[segments.length - 1]) != null ? _a2 : "";
  return last2.replace(/\[\d+\]$/g, "");
};
var checkRelTargets = (htmlTemplate, declared, errors, path) => {
  const { text, removed } = substituteInterpolations(htmlTemplate);
  const sources = [text, ...interpolatedHtml(removed)];
  for (const tag of sources.flatMap((s) => scanHtmlTags(s))) {
    if (tag.closing) continue;
    if (tag.name !== "a" && tag.name !== "button") continue;
    if (hasAttr(tag.attrs, "href")) continue;
    const rel = readAttr(tag.attrs, "rel");
    if (rel === void 0 || rel.length === 0) continue;
    if (isDynamicAttrValue(rel)) continue;
    if (rel.includes(" ")) continue;
    if (HTML_REL_TOKENS.has(rel)) continue;
    const suffix = targetSuffix(rel);
    if (suffix.length === 0 || declared.navTargets.has(suffix)) continue;
    errors.push({
      path: pathStr([...path, "htmlTemplate"]),
      kind: "missing-screen",
      message: `Anchor \`rel="${rel}"\` does not match any named node in appConfig \u2014 the click will silently no-op (rel resolves scope-relatively, but its target name must exist somewhere).`,
      fixHint: nearMissHint(suffix, declared.navTargets, "navigation target"),
      severity: "error"
    });
  }
};
var isObj5 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);

// src/validation/hostArgs.ts
var isAstNode4 = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var NAV_HOSTS = /* @__PURE__ */ new Set([
  "navigate",
  "replaceScreen",
  "pushCursor",
  "setCursor"
]);
var REPLACE_HOSTS = /* @__PURE__ */ new Set([
  "replaceScreen",
  "setCursor"
]);
var calleeName = (call) => {
  const callee = call.callee;
  return isAstNode4(callee) && callee.type === "Identifier" && typeof callee.name === "string" ? callee.name : void 0;
};
var argsOf = (call) => Array.isArray(call.arguments) ? call.arguments : [];
var quasiText2 = (q) => {
  if (!isAstNode4(q)) return void 0;
  const value = q.value;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const cooked = value.cooked;
    if (typeof cooked === "string") return cooked;
  }
  return void 0;
};
var stringLiteral = (n) => {
  if (!isAstNode4(n)) return void 0;
  if (n.type === "Literal" && typeof n.value === "string") return n.value;
  if (n.type === "TemplateLiteral" && Array.isArray(n.expressions) && n.expressions.length === 0 && Array.isArray(n.quasis)) {
    return quasiText2(n.quasis[0]);
  }
  return void 0;
};
var checkNavHost = (fn) => (call, ctx) => {
  var _a2;
  const errors = [];
  const [targetNode, argsNode] = argsOf(call);
  if (targetNode === void 0) {
    errors.push({
      path: ctx.path,
      kind: "host-arg-missing",
      message: `\`${fn}()\` needs a target \u2014 the screen (or named node) to move to.`,
      fixHint: `Write \`${fn}('screenName')\`; a Back button is \`back()\`.`,
      severity: "error"
    });
    return errors;
  }
  if (isAstNode4(argsNode) && argsNode.type === "Literal") {
    errors.push({
      path: ctx.path,
      kind: "host-arg-type",
      message: `\`${fn}\`'s second argument must be an object of nav args, not a literal.`,
      fixHint: `e.g. \`${fn}('detail', {id: $params.id})\`.`,
      severity: "error"
    });
  }
  const target = stringLiteral(targetNode);
  if (target !== void 0 && target.length === 0) {
    errors.push({
      path: ctx.path,
      kind: "host-arg-missing",
      message: `\`${fn}()\` needs a target \u2014 the screen (or named node) to move to.`,
      fixHint: `Write \`${fn}('screenName')\`; a Back button is \`back()\`.`,
      severity: "error"
    });
    return errors;
  }
  if (target === void 0 || ctx.navTargets === void 0) return errors;
  if (isDynamicAttrValue(target)) return errors;
  const suffix = targetSuffix(target);
  if (suffix.length === 0) return errors;
  if (!ctx.navTargets.has(suffix)) {
    errors.push({
      path: ctx.path,
      kind: "missing-screen",
      message: `\`${fn}("${target}")\` does not match any named node in appConfig \u2014 the call will fail at runtime.`,
      fixHint: nearMissHint(suffix, ctx.navTargets, "navigation target"),
      severity: "error"
    });
    return errors;
  }
  if (REPLACE_HOSTS.has(fn) && ((_a2 = ctx.flowBearing) == null ? void 0 : _a2.has(suffix)) === true) {
    errors.push({
      path: ctx.path,
      kind: "action-replace-into-flow",
      message: `\`${fn}("${target}")\` replaces into a screen that contains a flow \u2014 replace skips the flow-state reset, so re-entering shows the previous run's completed step instead of a fresh flow.`,
      fixHint: `Use \`navigate("${target}")\` (push resets flow state under the target and gives Back a nav-stack entry).`,
      severity: "warning"
    });
  }
  return errors;
};
var checkBack = (call, ctx) => argsOf(call).length === 0 ? [] : [
  {
    path: ctx.path,
    kind: "host-arg-type",
    message: "`back()` takes no arguments \u2014 it always goes back one nav-stack entry.",
    fixHint: "Use `back()`; to go to a specific screen use `navigate('screen')`.",
    severity: "warning"
  }
];
var propertyValue = (obj, key) => {
  if (!isAstNode4(obj) || obj.type !== "ObjectExpression") return void 0;
  const props = Array.isArray(obj.properties) ? obj.properties : [];
  for (const p of props) {
    if (!isAstNode4(p) || p.type !== "Property" || p.computed === true) continue;
    const k = p.key;
    const name = isAstNode4(k) ? k.type === "Identifier" && typeof k.name === "string" ? k.name : stringLiteral(k) : void 0;
    if (name === key) return p.value;
  }
  return void 0;
};
var writtenLiterals = (value) => {
  if (!isAstNode4(value)) return [];
  if (value.type === "ObjectExpression") return [value];
  if (value.type === "ArrayExpression" && Array.isArray(value.elements)) {
    return value.elements.filter(
      (e) => isAstNode4(e) && e.type === "ObjectExpression"
    );
  }
  return [];
};
var checkParentSpelling = (fn, recordKey) => (call, ctx) => {
  var _a2;
  const fieldTypes = ctx.objectTypeFieldTypes;
  const [arg] = argsOf(call);
  const typeName = stringLiteral(propertyValue(arg, "type"));
  if (fieldTypes === void 0 || typeName === void 0) return [];
  const fields = fieldTypes.get(typeName);
  if (fields === void 0) return [];
  const declared = (_a2 = [...fields].find(
    ([, t30]) => t30.kind === "builtin" && t30.name === "parentRef"
  )) == null ? void 0 : _a2[0];
  if (declared === void 0) return [];
  return writtenLiterals(propertyValue(arg, recordKey)).filter((rec) => propertyValue(rec, "_parent") !== void 0).map(
    () => ({
      path: ctx.path,
      kind: "tree-mixed-parent-spellings",
      message: `${fn} writes \`_parent\` on a \`${typeName}\`, but \`${typeName}\` declares the \`parentRef\` field \`${declared}\`, so the tree reads only \`${declared}\` and ignores \`_parent\`.`,
      fixHint: `Write the parent's id to \`${declared}\` instead (\`${declared}: <parent id>\`), or remove the \`parentRef\` field from \`${typeName}\` to use the implicit \`_parent\` link everywhere.`,
      severity: "error"
    })
  );
};
var HOST_ARG_CHECKS = new Map([
  ...[...NAV_HOSTS].map((name) => [
    name,
    checkNavHost(name)
  ]),
  ["back", checkBack],
  ["publishData", checkParentSpelling("publishData", "data")],
  ["updateData", checkParentSpelling("updateData", "patch")]
]);
var NAV_HOST_CALL_RE = new RegExp(
  `\\b(?:${[...NAV_HOSTS].join("|")})\\(`
);
var NON_AST_CHILD_KEYS = /* @__PURE__ */ new Set(["parent"]);
var walkCalls = (node2, visit) => {
  if (Array.isArray(node2)) {
    node2.forEach((c) => walkCalls(c, visit));
    return;
  }
  if (!isAstNode4(node2)) return;
  if (node2.type === "CallExpression") visit(node2);
  for (const [k, v] of Object.entries(node2)) {
    if (NON_AST_CHILD_KEYS.has(k)) continue;
    if (typeof v === "object" && v !== null) walkCalls(v, visit);
  }
};
var checkHostArgs = (ast, ctx) => {
  const errors = [];
  walkCalls(ast, (call) => {
    const name = calleeName(call);
    const check = name !== void 0 ? HOST_ARG_CHECKS.get(name) : void 0;
    if (check) errors.push(...check(call, ctx));
  });
  return errors;
};
var collectLiteralTargets = (node2, scan) => {
  const lit = stringLiteral(node2);
  if (lit !== void 0) {
    if (lit.length > 0) scan.targets.push(lit);
    return;
  }
  if (isAstNode4(node2)) {
    if (node2.type === "ConditionalExpression") {
      collectLiteralTargets(node2.consequent, scan);
      collectLiteralTargets(node2.alternate, scan);
      return;
    }
    if ((node2.type === "BinaryExpression" || node2.type === "LogicalExpression") && (node2.operator === "&&" || node2.operator === "||")) {
      if (node2.operator !== "&&") collectLiteralTargets(node2.left, scan);
      collectLiteralTargets(node2.right, scan);
      return;
    }
  }
  if (node2 !== void 0) scan.dynamic = true;
};
var scanNavCalls = (source) => {
  let ast;
  try {
    ast = jsep(source);
  } catch {
    return { targets: [], dynamic: false };
  }
  const scan = { targets: [], dynamic: false };
  walkCalls(ast, (call) => {
    const name = calleeName(call);
    if (name === void 0 || !NAV_HOSTS.has(name)) return;
    const [first] = argsOf(call);
    collectLiteralTargets(first, scan);
  });
  return scan;
};

// src/create/renameNodes.ts
var NAV_ATTR_RE = /\b(rel|href)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
var CODE_ATTR_RE = /(?<![\w-])(data-script|data-args|setValue|param)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
var NAV_ATTR_OPEN_RE = /\b(rel|href)\s*=\s*["']?$/;
var NAV_PLAIN_KEYS = /* @__PURE__ */ new Set([
  "clickPath",
  "notifyClickPath",
  "detail",
  "slots"
]);
var RAW_FORMULA_KEYS = /* @__PURE__ */ new Set(["expression"]);
var NAV_FORMULA_KEYS = /* @__PURE__ */ new Set(["link", "onSuccessNavigate"]);
var EXTERNAL_HREF_RE = /^(?:[a-z]+:|\/\/|#|@)/i;
var fail2 = (kind, path, message) => left3({ kind, path, message });
var mentionsAny2 = (src, renames) => Object.keys(renames).some((name) => src.includes(name));
var renameNavTarget = (target, renames) => {
  const lead = target.startsWith("/") ? "/" : "";
  let changed = false;
  const segs = target.slice(lead.length).split(".").map((seg) => {
    var _a2, _b;
    const m = /^([^[]*)(.*)$/.exec(seg);
    const base = (_a2 = m == null ? void 0 : m[1]) != null ? _a2 : seg;
    const rest = (_b = m == null ? void 0 : m[2]) != null ? _b : "";
    if (!(base in renames)) return seg;
    changed = true;
    return renames[base] + rest;
  });
  return changed ? lead + segs.join(".") : void 0;
};
var NAV_RESULT_OPS = /* @__PURE__ */ new Set(["||", "??", "&&"]);
var walkOccurrences = (ast, renames, nodeNames, rootBound, rootNav) => {
  const idents = [];
  const strings = [];
  const suspects = [];
  const reference = (n, bound) => {
    const name = String(n.name);
    if (!(name in renames)) return;
    if (bound.has(name)) {
      idents.push({ name });
      return;
    }
    const next = renames[name];
    idents.push({
      name,
      apply: () => {
        n.name = next;
      },
      edit: (t30) => ({ start: t30.start, end: t30.end, text: next })
    });
  };
  const keep = (name) => {
    if (name in renames) idents.push({ name });
  };
  const chainRoot = (n) => {
    if (!isAstNode(n)) return void 0;
    if (n.type === "Identifier") return String(n.name);
    if (n.type === "MemberExpression") return chainRoot(n.object);
    return void 0;
  };
  const visit = (n, bound, nav) => {
    if (!isAstNode(n)) return;
    switch (n.type) {
      case "Identifier":
        reference(n, bound);
        return;
      case "Literal": {
        if (typeof n.value !== "string") return;
        const value = n.value;
        const next = nav ? renameNavTarget(value, renames) : void 0;
        if (next !== void 0) {
          strings.push({
            value,
            next,
            apply: () => {
              n.value = next;
            }
          });
          return;
        }
        if (value in renames) suspects.push(value);
        strings.push({ value });
        return;
      }
      case "ThisExpression":
        return;
      case "MemberExpression": {
        visit(n.object, bound, false);
        const prop5 = n.property;
        if (n.computed === true) {
          visit(prop5, bound, false);
          return;
        }
        if (!isAstNode(prop5) || prop5.type !== "Identifier") return;
        const root = chainRoot(n.object);
        const isNodePath = root !== void 0 && nodeNames.has(root) && !bound.has(root);
        if (isNodePath) reference(prop5, /* @__PURE__ */ new Set());
        else keep(String(prop5.name));
        return;
      }
      case "CallExpression": {
        visit(n.callee, bound, false);
        const args = Array.isArray(n.arguments) ? n.arguments : [];
        const callee = n.callee;
        const isNav = isAstNode(callee) && callee.type === "Identifier" && NAV_HOSTS.has(String(callee.name));
        args.forEach((a, i) => visit(a, bound, isNav && i === 0));
        return;
      }
      case "BinaryExpression":
      case "LogicalExpression": {
        const op = String(n.operator);
        const navLeft = nav && (op === "||" || op === "??");
        const navRight = nav && (NAV_RESULT_OPS.has(op) || op === ";");
        visit(n.left, bound, navLeft);
        visit(n.right, bound, navRight);
        return;
      }
      case "UnaryExpression":
        visit(n.argument, bound, false);
        return;
      case "ConditionalExpression":
        visit(n.test, bound, false);
        visit(n.consequent, bound, nav);
        visit(n.alternate, bound, nav);
        return;
      case "ArrayExpression":
        (Array.isArray(n.elements) ? n.elements : []).forEach(
          (e) => visit(e, bound, false)
        );
        return;
      case "SpreadElement":
        visit(n.argument, bound, false);
        return;
      case "ObjectExpression":
        (Array.isArray(n.properties) ? n.properties : []).forEach(
          (p) => visitProperty(p, bound)
        );
        return;
      case "TemplateLiteral": {
        const quasis = Array.isArray(n.quasis) ? n.quasis : [];
        const exprs = Array.isArray(n.expressions) ? n.expressions : [];
        exprs.forEach((e, i) => {
          var _a2;
          const q = quasis[i];
          const cooked = isAstNode(q) && isRecord(q.value) ? String((_a2 = q.value.cooked) != null ? _a2 : "") : "";
          visit(e, bound, NAV_ATTR_OPEN_RE.test(cooked));
        });
        return;
      }
      case "TaggedTemplateExpression":
        visit(n.tag, bound, false);
        visit(n.quasi, bound, false);
        return;
      case "ArrowFunctionExpression": {
        const params = Array.isArray(n.params) ? n.params : [];
        const inner = new Set(bound);
        for (const p of params) {
          if (isAstNode(p) && p.type === "Identifier") {
            keep(String(p.name));
            inner.add(String(p.name));
          } else {
            visit(p, bound, false);
          }
        }
        visit(n.body, inner, false);
        return;
      }
      case "LetExpression": {
        const id = String(n.id);
        keep(id);
        visit(n.init, bound, false);
        visit(n.body, /* @__PURE__ */ new Set([...bound, id]), nav);
        return;
      }
      case "MatchExpression":
        visit(n.scrutinee, bound, false);
        for (const arm of Array.isArray(n.arms) ? n.arms : []) {
          if (!isAstNode(arm)) continue;
          visit(arm.test, bound, false);
          visit(arm.consequent, bound, nav);
        }
        return;
      case "Compound":
        (Array.isArray(n.body) ? n.body : []).forEach(
          (b2) => visit(b2, bound, false)
        );
        return;
      default:
        throw new Error(`unknown expression type ${n.type}`);
    }
  };
  const visitProperty = (p, bound) => {
    if (!isAstNode(p)) return;
    if (p.type !== "Property") {
      visit(p, bound, false);
      return;
    }
    const key = p.key;
    if (p.shorthand === true && isAstNode(key) && key.type === "Identifier") {
      const name = String(key.name);
      if (name in renames && !bound.has(name)) {
        const next = renames[name];
        idents.push({
          name,
          apply: () => {
            p.shorthand = false;
            p.value = { type: "Identifier", name: next };
          },
          edit: (t30) => ({
            start: t30.start,
            end: t30.end,
            text: `${name}: ${next}`
          })
        });
      } else {
        keep(name);
      }
      return;
    }
    if (p.computed === true) visit(key, bound, false);
    else if (isAstNode(key) && key.type === "Identifier")
      keep(String(key.name));
    else visit(key, bound, false);
    visit(p.value, bound, false);
  };
  visit(ast, rootBound, rootNav);
  return { idents, strings, suspects };
};
var normalize2 = (ast, ignoreText) => {
  const copy = structuredClone(ast);
  const strip = (n) => {
    if (Array.isArray(n)) return n.forEach(strip);
    if (!isRecord(n)) return;
    if (n.type === "Literal") {
      delete n.raw;
      if (ignoreText && typeof n.value === "string" && /=\s*["']/.test(n.value)) {
        delete n.value;
      }
    }
    if (ignoreText && n.type === "TemplateElement") delete n.value;
    delete n.optional;
    Object.values(n).forEach(strip);
  };
  strip(copy);
  return copy;
};
var planNodeRename = (src, mode2, renames, path, ctx) => {
  var _a2, _b;
  if (!mentionsAny2(src, renames)) return right3({ edits: [], suspects: [] });
  let ast;
  try {
    ast = parseFor(src, mode2);
  } catch (err) {
    return fail2(
      "unparseable",
      path,
      `Cannot parse a formula that mentions a renamed node: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  const { tokens, runs } = lex(src, mode2);
  const edits = [];
  const suspects = [];
  let expected;
  if (ast) {
    expected = structuredClone(ast);
    let walked;
    try {
      walked = walkOccurrences(
        expected,
        renames,
        ctx.nodeNames,
        ctx.bound,
        ctx.nav
      );
    } catch (err) {
      return fail2(
        "unlocated-reference",
        path,
        `Cannot walk a formula that mentions a renamed node: ${err instanceof Error ? err.message : String(err)}`
      );
    }
    const identTokens = tokens.filter(
      (t30) => t30.kind === "ident" && t30.text in renames
    );
    const stringTokens = tokens.filter((t30) => t30.kind === "string");
    if (identTokens.length !== walked.idents.length || stringTokens.length !== walked.strings.length) {
      return fail2(
        "unlocated-reference",
        path,
        `The formula's source and its expression disagree on where a renamed node name sits (${identTokens.length}/${walked.idents.length} identifiers, ${stringTokens.length}/${walked.strings.length} strings) \u2014 rename it by hand.`
      );
    }
    for (const [k, occ] of walked.idents.entries()) {
      const t30 = identTokens[k];
      if (t30.kind !== "ident" || t30.text !== occ.name) {
        return fail2(
          "unlocated-reference",
          path,
          `Identifier ${occ.name} is out of order.`
        );
      }
      (_a2 = occ.apply) == null ? void 0 : _a2.call(occ);
      if (occ.edit) edits.push(occ.edit(t30));
    }
    for (const [k, occ] of walked.strings.entries()) {
      const t30 = stringTokens[k];
      if (t30.kind !== "string" || t30.value !== occ.value) {
        return fail2(
          "unlocated-reference",
          path,
          `String "${occ.value}" is out of order.`
        );
      }
      if (occ.next === void 0) continue;
      if (src.slice(t30.start + 1, t30.end - 1) !== occ.value) {
        return fail2(
          "escaped-attribute",
          path,
          `The nav target "${occ.value}" is spelled with an escape; its source text and its value differ, so the rename cannot be placed.`
        );
      }
      (_b = occ.apply) == null ? void 0 : _b.call(occ);
      edits.push({ start: t30.start + 1, end: t30.end - 1, text: occ.next });
    }
    suspects.push(...walked.suspects.map((value) => ({ path, value })));
  }
  const attrEdits = [];
  for (const run of runs) {
    const planned = planAttrs(src, run, renames, path, ctx);
    if (isLeft3(planned)) return planned;
    attrEdits.push(...planned.right.edits);
    suspects.push(...planned.right.suspects);
  }
  if (ast && expected) {
    const next = applyEdits(src, [...edits, ...attrEdits]);
    let nextAst;
    try {
      nextAst = parseFor(next, mode2);
    } catch {
      nextAst = void 0;
    }
    const ignoreText = attrEdits.length > 0;
    if (nextAst === void 0 || !R25.isDeepEqual(
      normalize2(nextAst, ignoreText),
      normalize2(expected, ignoreText)
    )) {
      return fail2(
        "equivalence-mismatch",
        path,
        "The renamed formula does not parse to the renamed expression \u2014 the rename would change more than node names."
      );
    }
  }
  return right3({ edits: [...edits, ...attrEdits], suspects });
};
var planAttrs = (src, run, renames, path, ctx) => {
  var _a2, _b, _c, _d;
  let text = "";
  const map2 = [];
  run.pieces.forEach((p, idx) => {
    if (idx > 0) text += INTERP_PLACEHOLDER;
    map2.push({
      textStart: text.length,
      srcStart: p.start,
      length: p.end - p.start
    });
    text += src.slice(p.start, p.end);
  });
  if (!mentionsAny2(text, renames)) return right3({ edits: [], suspects: [] });
  const toSrc = (at, length) => {
    const seg = map2.find(
      (s) => at >= s.textStart && at + length <= s.textStart + s.length
    );
    return seg ? seg.srcStart + (at - seg.textStart) : void 0;
  };
  const valueStart = (m, raw) => {
    var _a3;
    return ((_a3 = m.index) != null ? _a3 : 0) + m[0].length - 1 - raw.length;
  };
  const edits = [];
  const suspects = [];
  for (const m of text.matchAll(NAV_ATTR_RE)) {
    const raw = (_b = (_a2 = m[2]) != null ? _a2 : m[3]) != null ? _b : "";
    if (raw.includes(INTERP_PLACEHOLDER) || raw.includes("\\")) continue;
    if (m[1] === "href" && EXTERNAL_HREF_RE.test(raw)) continue;
    const next = renameNavTarget(raw, renames);
    if (next === void 0) continue;
    const start = toSrc(valueStart(m, raw), raw.length);
    if (start === void 0) {
      return fail2(
        "unlocated-reference",
        path,
        `A ${m[1]}= target straddles an interpolation.`
      );
    }
    edits.push({ start, end: start + raw.length, text: next });
  }
  for (const m of text.matchAll(CODE_ATTR_RE)) {
    const raw = (_d = (_c = m[2]) != null ? _c : m[3]) != null ? _d : "";
    let bodyStart = valueStart(m, raw);
    let body = raw;
    if (body.startsWith("@")) {
      body = body.slice(1);
      bodyStart++;
    }
    if (!mentionsAny2(body, renames)) continue;
    if (body.includes("\\")) {
      return fail2(
        "escaped-attribute",
        path,
        `A ${m[1]} body that mentions a renamed node contains a backslash escape, so the rename cannot be placed.`
      );
    }
    const planned = planNodeRename(body, "expr", renames, `${path} (${m[1]})`, {
      ...ctx,
      nav: false
    });
    if (isLeft3(planned)) return planned;
    suspects.push(...planned.right.suspects);
    for (const e of planned.right.edits) {
      const start = toSrc(bodyStart + e.start, e.end - e.start);
      if (start === void 0) {
        return fail2(
          "unlocated-reference",
          path,
          `A node reference in a ${m[1]} body straddles an interpolation.`
        );
      }
      edits.push({ start, end: start + (e.end - e.start), text: e.text });
    }
  }
  return right3({ edits, suspects });
};
var renameNodesInFormula = (value, renames, kind, opts = {}) => {
  var _a2, _b, _c, _d;
  const prefixed = value.startsWith("@");
  if (kind === "formula" && !prefixed) return right3({ value, suspects: [] });
  const offset = prefixed ? 1 : 0;
  const mode2 = kind === "template" && !prefixed ? "template" : "expr";
  const planned = planNodeRename(
    value.slice(offset),
    mode2,
    renames,
    (_a2 = opts.path) != null ? _a2 : "",
    {
      nav: (_b = opts.nav) != null ? _b : false,
      bound: (_c = opts.bound) != null ? _c : /* @__PURE__ */ new Set(),
      nodeNames: (_d = opts.nodeNames) != null ? _d : new Set(Object.keys(renames))
    }
  );
  if (isLeft3(planned)) return planned;
  const edits = planned.right.edits.map((e) => ({
    ...e,
    start: e.start + offset,
    end: e.end + offset
  }));
  return right3({
    value: applyEdits(value, edits),
    suspects: planned.right.suspects
  });
};
var collectNodeNames = (tree2) => {
  const names2 = /* @__PURE__ */ new Set();
  const walk8 = (v) => {
    if (Array.isArray(v)) return v.forEach(walk8);
    if (!isRecord(v)) return;
    if (typeof v.type === "string" && typeof v.name === "string" && v.name) {
      names2.add(v.name);
    }
    Object.values(v).forEach(walk8);
  };
  walk8(tree2);
  return names2;
};
var renameNodesInTree = (tree2, renames) => {
  const errors = [];
  const suspects = [];
  const nodeNames = collectNodeNames(tree2);
  const formula = (value, kind, at, opts = {}) => {
    const out = renameNodesInFormula(value, renames, kind, {
      ...opts,
      path: pathString(at),
      nodeNames
    });
    if (isLeft3(out)) {
      errors.push(out.left);
      return value;
    }
    suspects.push(...out.right.suspects);
    return out.right.value;
  };
  const walk8 = (value, at, bound) => {
    if (Array.isArray(value))
      return value.map((v, i) => walk8(v, [...at, i], bound));
    if (!isRecord(value)) return value;
    const isNode7 = typeof value.type === "string";
    const params = value.type === "function" && typeof value.parameters === "string" ? new Set(parseParameters(value.parameters).map((p) => p.name)) : void 0;
    const inner = params ? /* @__PURE__ */ new Set([...bound, ...params]) : bound;
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => {
        var _a2;
        const here = [...at, key];
        if (typeof v === "string") {
          if (isNode7 && key === "name")
            return [key, v in renames ? renames[v] : v];
          if (key === "htmlTemplate") {
            if (!v.startsWith("@") && value.runtime === "sandboxed")
              return [key, v];
            return [key, formula(v, "template", here, { bound: inner })];
          }
          if (key === "script" && (isScriptStep(value) || at[at.length - 2] === "actions")) {
            return [key, formula(v, "script", here, { bound: inner })];
          }
          if (RAW_FORMULA_KEYS.has(key)) {
            return [key, formula(v, "script", here, { bound: inner })];
          }
          if (NAV_PLAIN_KEYS.has(key) && !v.startsWith("@")) {
            return [key, (_a2 = renameNavTarget(v, renames)) != null ? _a2 : v];
          }
          const nav = NAV_PLAIN_KEYS.has(key) || NAV_FORMULA_KEYS.has(key);
          return [key, formula(v, "formula", here, { nav, bound: inner })];
        }
        if (key === "parameters" && isRecord(v)) {
          return [
            key,
            Object.fromEntries(
              Object.entries(v).map(([pk, pv]) => [
                pk,
                typeof pv === "string" ? formula(pv, "formula", [...here, pk], {
                  nav: true,
                  bound: inner
                }) : pv
              ])
            )
          ];
        }
        return [key, walk8(v, here, inner)];
      })
    );
  };
  const result = walk8(tree2, [], /* @__PURE__ */ new Set());
  return errors.length > 0 ? left3(errors) : right3({ tree: result, suspects });
};

// src/create/provenance.ts
var createProvenance = (spec) => ({
  verticalId: spec.verticalId,
  riskScore: spec.risk.score,
  strategy: spec.risk.strategy,
  factorsTriggered: spec.risk.factors.filter((f) => f.triggered).map((f) => f.id)
});

// src/create/requirementsBlock.ts
var humanise = (key) => {
  const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
var scalarText = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : void 0;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  return void 0;
};
var isRecord6 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var thingDetails = (answers) => {
  const things = answers.things;
  const details = /* @__PURE__ */ new Map();
  if (!Array.isArray(things)) return details;
  for (const thing of things) {
    if (!isRecord6(thing)) continue;
    const name = scalarText(thing.name);
    const detail = scalarText(thing.detail);
    if (name !== void 0 && detail !== void 0) details.set(name, detail);
  }
  return details;
};
var STRATEGY_GUIDANCE = {
  clone: "A standard app of this kind. Keep to the conventional shape.",
  orgGen: "Some custom parts. Build the core of each requested thing and feature.",
  orgGenHighRisk: "An ambitious app. Make the core flow work end to end before adding breadth; the owner expects to edit it afterwards."
};
var formatRequirementsBlock = (spec) => {
  var _a2;
  const details = thingDetails(spec.answers);
  const answerLines = Object.entries(spec.answers).flatMap(([key, value]) => {
    const text = scalarText(value);
    return text === void 0 ? [] : [`- ${humanise(key)}: ${text}`];
  });
  const entityLines = spec.derived.entities.map((name) => {
    const detail = details.get(name);
    return detail === void 0 ? `- ${name}` : `- ${name}: ${detail}`;
  });
  const featureLines = spec.derived.features.map((f) => `- ${f}`);
  const triggered = spec.risk.factors.filter((f) => f.triggered).map((f) => f.label);
  const notes = (_a2 = spec.notes) == null ? void 0 : _a2.trim();
  const sections = [
    `## Requirements

The visitor answered a short wizard; treat these as settled and don't ask about them again.

- Kind of app: ${spec.verticalId}
${answerLines.join("\n")}`.trimEnd(),
    entityLines.length > 0 ? `### Things to track
${entityLines.join("\n")}` : "",
    featureLines.length > 0 ? `### Features
${featureLines.join("\n")}` : "",
    notes ? `### Notes
${notes}` : "",
    `### Scope
${STRATEGY_GUIDANCE[spec.risk.strategy]}${triggered.length > 0 ? ` (Flagged: ${triggered.join("; ")}.)` : ""}`
  ];
  return sections.filter((s) => s.length > 0).join("\n\n");
};
var promptFromRequirements = (spec) => {
  const name = spec.answers.appName;
  return typeof name === "string" && name.trim().length > 0 ? `Build "${name.trim()}", as described below.` : "Build the app described below.";
};

// src/entitlements/index.ts
var ORG_LIMITS = {
  free: 1,
  paid: 5
};
var PAID_STATUSES = /* @__PURE__ */ new Set(["active", "trialing"]);
var GRACE_STATUSES = /* @__PURE__ */ new Set(["past_due"]);
var deriveTier = (sub) => (sub == null ? void 0 : sub.status) && (PAID_STATUSES.has(sub.status) || GRACE_STATUSES.has(sub.status)) ? "paid" : "free";
var LAPSED_STATUSES = /* @__PURE__ */ new Set(["canceled", "unpaid", "paused"]);
var hasLapsedSubscription = (sub) => !!(sub == null ? void 0 : sub.status) && LAPSED_STATUSES.has(sub.status);
var orgLimitForTier = (tier2) => ORG_LIMITS[tier2];
var effectiveOrgLimit = (tier2, override) => {
  if (override === "unlimited") return Infinity;
  if (typeof override === "number" && Number.isFinite(override) && override >= 0) {
    return override;
  }
  return orgLimitForTier(tier2);
};
var canCreateOrg = (tier2, ownedCount, override) => ownedCount < effectiveOrgLimit(tier2, override);
var isPaidOrComped = (tier2, orgLimitOverride) => tier2 === "paid" || orgLimitOverride === "unlimited";
var orgsToKeepLive = (tier2, orgIdsByAgeAsc, keptOverrideId, orgLimitOverride) => {
  const limit = effectiveOrgLimit(tier2, orgLimitOverride);
  const keep = /* @__PURE__ */ new Set();
  if (keptOverrideId && orgIdsByAgeAsc.includes(keptOverrideId)) {
    keep.add(keptOverrideId);
  }
  for (const id of orgIdsByAgeAsc) {
    if (keep.size >= limit) break;
    keep.add(id);
  }
  return keep;
};
var sortOrgsByAgeAsc = (orgs) => {
  const byId = (a, b2) => a.id < b2.id ? -1 : a.id > b2.id ? 1 : 0;
  return [...orgs].sort((a, b2) => {
    if (a.createdAt && b2.createdAt) {
      return a.createdAt < b2.createdAt ? -1 : a.createdAt > b2.createdAt ? 1 : byId(a, b2);
    }
    if (a.createdAt) return -1;
    if (b2.createdAt) return 1;
    return byId(a, b2);
  });
};
var planOrgLocks = (tier2, orgs, keptOverrideId, orgLimitOverride) => {
  const sorted = sortOrgsByAgeAsc(orgs);
  const keep = orgsToKeepLive(
    tier2,
    sorted.map((o) => o.id),
    keptOverrideId,
    orgLimitOverride
  );
  const toLock = [];
  const toUnlock = [];
  for (const o of orgs) {
    const shouldBeLive = keep.has(o.id);
    const isLocked = o.locked === true;
    if (!shouldBeLive && !isLocked) toLock.push(o.id);
    else if (shouldBeLive && isLocked) toUnlock.push(o.id);
  }
  return { toLock, toUnlock };
};

// src/features/manifest.ts
import * as t27 from "io-ts";

// src/features/wellKnown.ts
import * as t26 from "io-ts";
var wellKnownName = t26.keyof({ Person: null });
var WELL_KNOWN_TYPES = {
  Person: {
    description: "Someone the org keeps a record of \u2014 a member, a family member, a client. Not the signed-in user (`$orgUser`), which is an auth identity, not a record.",
    fields: [{ name: "name", type: "text" }]
  }
};
var kids = (node2) => isRecord(node2) && Array.isArray(node2._children) ? node2._children : [];
var objectTypesIn = (section2) => kids(section2).flatMap(
  (n) => !isRecord(n) ? [] : n.type === "objectType" ? [n] : objectTypesIn(n)
);
var fieldNames = (type14) => new Set(
  (Array.isArray(type14.fields) ? type14.fields : []).flatMap(
    (f) => isRecord(f) && typeof f.name === "string" ? [f.name] : []
  )
);
var findWellKnownHost = (objectTypes, name) => {
  var _a2;
  const types2 = objectTypesIn(objectTypes);
  return (_a2 = types2.find((t30) => t30.wellKnown === name)) != null ? _a2 : types2.find((t30) => t30.name === name);
};
var missingWellKnownFields = (type14, name, reads) => {
  const has = fieldNames(type14);
  const need = [...WELL_KNOWN_TYPES[name].fields.map((f) => f.name), ...reads];
  return [...new Set(need)].filter((f) => !has.has(f));
};

// src/features/manifest.ts
var fieldExpectation = t27.intersection([
  t27.type({ name: t27.string }),
  t27.partial({ type: t27.string })
]);
var requiredType = t27.intersection([
  t27.type({ name: t27.string, fields: t27.array(fieldExpectation) }),
  t27.partial({
    description: t27.string,
    /** Bind to the host's type of this well-known kind when it has one
     * (`wellKnown.ts`); `name` is then only the feature's own spelling. */
    wellKnown: wellKnownName
  })
]);
var navKind = t27.keyof({ appFeature: null, none: null });
var featureScope = t27.keyof({
  host: null,
  global: null,
  user: null,
  group: null
});
var featureRequires = t27.partial({
  types: t27.array(requiredType),
  scope: featureScope,
  nav: navKind,
  /** Plugins the feature instantiates but does not ship (webberoonies'
   * `PictureLink` is used by every slice and by the shell's own menu). */
  plugins: t27.array(t27.string)
});
var navContribution = t27.intersection([
  t27.type({ screen: t27.string, title: t27.string }),
  t27.partial({ pic: t27.string })
]);
var featureSlot = t27.intersection([
  t27.type({
    name: t27.string,
    shape: t27.union([t27.string, t27.record(t27.string, t27.string)])
  }),
  t27.partial({ description: t27.string })
]);
var featureProvides = t27.intersection([
  t27.type({
    types: t27.array(t27.string),
    screens: t27.array(t27.string)
  }),
  t27.partial({
    functions: t27.array(t27.string),
    plugins: t27.array(t27.string),
    nav: t27.array(navContribution),
    /** Push notifications the feature registers on the host app, by the
     * type whose publish triggers them. They live on `appConfig`, outside
     * the feature folder, so install must merge them by hand. */
    notifications: t27.array(t27.string),
    slots: t27.array(featureSlot)
  })
]);
var featureParams = t27.partial({
  /** objectType names an install may rename. */
  entities: t27.array(t27.string),
  /** objectType names whose seed records an install may drop. */
  seedSlots: t27.array(t27.string),
  /** appConfig node names whose `label` an install may set. */
  labels: t27.array(t27.string)
});
var featureInstallParams = templateOverrides;
var featureSeam = t27.intersection([
  t27.type({
    name: t27.string,
    hostType: requiredType,
    slot: t27.string,
    fn: t27.string
  }),
  t27.partial({ description: t27.string })
]);
var featureManifest = t27.intersection([
  t27.type({
    /** `<owner>/<slug>`, the same shape as a library id. */
    id: t27.string,
    version: t27.number,
    description: t27.string,
    requires: featureRequires,
    provides: featureProvides
  }),
  t27.partial({
    params: featureParams,
    seams: t27.array(featureSeam)
  })
]);
var featureShell = t27.partial({
  nav: t27.array(t27.unknown),
  /** The `objectInstances` folder the nav rows sat in. Install falls back to
   * it when the host has no registry record left to locate the folder by —
   * the case once every feature has been removed (#2574). */
  navFolder: t27.string,
  notifications: t27.array(t27.unknown)
});
var featureBody = t27.partial({
  appConfig: t27.array(t27.unknown),
  objectTypes: t27.array(t27.unknown),
  objectInstances: t27.array(t27.unknown),
  plugins: t27.array(t27.unknown),
  shell: featureShell
});
var featureBundle = t27.type({
  manifest: featureManifest,
  body: featureBody
});

// src/features/graft.ts
import * as R26 from "remeda";
var FEATURE_SECTIONS = [
  "appConfig",
  "objectTypes",
  "objectInstances",
  "plugins"
];
var SECTION_TAG = {
  appConfig: "screens",
  objectTypes: "types",
  objectInstances: "seeds",
  plugins: "plugins"
};
var featureFolderName = (slug) => `feature-${slug}`;
var featureFolderId = (section2, slug) => `folder-${SECTION_TAG[section2]}-${slug}`;
var SLUG_RE = /^[a-z][a-z0-9-]*$/;
var NAME_RE = /^[A-Za-z_$][\w$]*$/;
var hash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};
var seededId = (kind, length, seedKey, taken) => {
  for (let attempt = 0; ; attempt++) {
    const next = `${kind}-${generateId({ length, seed: hash(`${seedKey}:${attempt}`) })}`;
    if (!taken.has(next)) {
      taken.add(next);
      return next;
    }
  }
};
var wholeWord = (word) => new RegExp(
  `(?<![\\w-])${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\w-])`
);
var walkRecords = (v, visit) => {
  if (Array.isArray(v)) return v.forEach((x) => walkRecords(x, visit));
  if (!isRecord(v)) return;
  visit(v);
  Object.values(v).forEach((x) => walkRecords(x, visit));
};
var mapStrings = (v, f) => {
  if (typeof v === "string") return f(v);
  if (Array.isArray(v)) return v.map((x) => mapStrings(x, f));
  if (!isRecord(v)) return v;
  return R26.mapValues(v, (x) => mapStrings(x, f));
};
var collectIds = (v) => {
  const ids = /* @__PURE__ */ new Set();
  walkRecords(v, (r) => {
    if (typeof r.id === "string") ids.add(r.id);
  });
  return ids;
};
var namesOfType = (v, type14) => {
  const found = /* @__PURE__ */ new Map();
  walkRecords(v, (r) => {
    if (r.type === type14 && typeof r.name === "string") found.set(r.name, r);
  });
  return found;
};
var referencedWords = (roots) => {
  const words = /* @__PURE__ */ new Set();
  const walk8 = (v, inAppConfig) => {
    if (typeof v === "string") {
      for (const m of v.matchAll(/[A-Za-z_$][\w$]*/g)) words.add(m[0]);
      return;
    }
    if (Array.isArray(v)) return v.forEach((x) => walk8(x, inAppConfig));
    if (!isRecord(v)) return;
    for (const [key, x] of Object.entries(v)) {
      const isOwnName = inAppConfig && key === "name" && typeof v.type === "string";
      if (!isOwnName) walk8(x, inAppConfig || key === "appConfig");
    }
  };
  roots.forEach((r) => walk8(r, false));
  return words;
};
var freeName = (name, prefix, taken) => {
  const base = `${prefix}_${name}`;
  let next = base;
  for (let n = 2; taken.has(next); n++) next = `${base}${n}`;
  taken.add(next);
  return next;
};
var graftFeature = (host, body, opts) => {
  var _a2, _b, _c, _d, _e, _f, _g;
  const { slug } = opts;
  if (!SLUG_RE.test(slug)) {
    return left3({
      kind: "invalid-slug",
      message: `"${slug}" is not a feature slug (lowercase letters, digits and dashes).`
    });
  }
  const prefix = ((_a2 = opts.prefix) != null ? _a2 : slug).replace(/-/g, "_");
  if (!NAME_RE.test(prefix)) {
    return left3({
      kind: "invalid-slug",
      message: `"${prefix}" cannot prefix a name.`
    });
  }
  if (!isRecord(host)) {
    return left3({
      kind: "invalid-host",
      message: "The host is not a config object."
    });
  }
  for (const section2 of FEATURE_SECTIONS) {
    if (!((_b = body[section2]) == null ? void 0 : _b.length)) continue;
    const root = host[section2];
    if (!isRecord(root) || !Array.isArray(root._children)) {
      return left3({
        kind: "invalid-host",
        message: `The host has no ${section2} root to graft into.`
      });
    }
  }
  const folderName = featureFolderName(slug);
  let installed = false;
  for (const section2 of FEATURE_SECTIONS) {
    walkRecords(host[section2], (r) => {
      if (r.type === "folder" && r.name === folderName) installed = true;
    });
  }
  if (installed) {
    return left3({
      kind: "already-installed",
      message: `The host already has a ${folderName} folder. Install a second copy under another slug.`
    });
  }
  const report = {
    ids: [],
    nodes: [],
    keptDuplicates: [],
    types: [],
    sharedPlugins: [],
    suspects: []
  };
  const grafted = {
    appConfig: structuredClone((_c = body.appConfig) != null ? _c : []),
    objectTypes: structuredClone((_d = body.objectTypes) != null ? _d : []),
    objectInstances: structuredClone((_e = body.objectInstances) != null ? _e : []),
    plugins: []
  };
  const hostPlugins = namesOfType(host.plugins, "plugin");
  for (const plugin2 of (_f = body.plugins) != null ? _f : []) {
    const existing = isRecord(plugin2) && typeof plugin2.name === "string" ? hostPlugins.get(plugin2.name) : void 0;
    if (!isRecord(plugin2) || existing === void 0) {
      grafted.plugins.push(structuredClone(plugin2));
      continue;
    }
    const name = String(plugin2.name);
    if (!R26.isDeepEqual(R26.omit(plugin2, ["id"]), R26.omit(existing, ["id"]))) {
      return left3({
        kind: "plugin-collision",
        plugin: name,
        message: `The host ships a different plugin named ${name}. Rename one of them before installing.`
      });
    }
    report.sharedPlugins.push(name);
  }
  const hostTypes = new Set(namesOfType(host.objectTypes, "objectType").keys());
  const bodyTypes = [...namesOfType(grafted.objectTypes, "objectType").keys()];
  const takenTypes = /* @__PURE__ */ new Set([...hostTypes, ...bodyTypes]);
  const typeRenames = {};
  for (const name of bodyTypes) {
    if (hostTypes.has(name))
      typeRenames[name] = freeName(name, prefix, takenTypes);
  }
  if (Object.keys(typeRenames).length > 0) {
    const wrapped = Object.fromEntries(
      FEATURE_SECTIONS.map((s) => [
        s,
        { type: "folder", _children: grafted[s] }
      ])
    );
    const renamed = renameTypesInConfig(wrapped, typeRenames);
    if (isLeft3(renamed)) {
      return left3({
        kind: "rename",
        message: "An objectType rename could not be placed.",
        errors: renamed.left
      });
    }
    const out = renamed.right;
    for (const s of FEATURE_SECTIONS) {
      const section2 = isRecord(out) ? out[s] : void 0;
      grafted[s] = isRecord(section2) && Array.isArray(section2._children) ? section2._children : [];
    }
    report.types = Object.entries(typeRenames).map(([from, to]) => ({
      from,
      to
    }));
  }
  const hostNames = collectNodeNames(host.appConfig);
  const bodyNames = collectNodeNames(grafted.appConfig);
  const takenNames = /* @__PURE__ */ new Set([...hostNames, ...bodyNames]);
  const nodeRenames = {};
  const mentioned = referencedWords([host, grafted]);
  for (const name of (_g = opts.referenced) != null ? _g : []) mentioned.add(name);
  for (const name of bodyNames) {
    if (!hostNames.has(name)) continue;
    if (mentioned.has(name))
      nodeRenames[name] = freeName(name, prefix, takenNames);
    else report.keptDuplicates.push(name);
  }
  if (Object.keys(nodeRenames).length > 0) {
    const renamed = renameNodesInTree(grafted.appConfig, nodeRenames);
    if (isLeft3(renamed)) {
      return left3({
        kind: "rename",
        message: "A node rename could not be placed.",
        errors: renamed.left
      });
    }
    const tree3 = renamed.right.tree;
    grafted.appConfig = Array.isArray(tree3) ? tree3 : [];
    report.suspects.push(...renamed.right.suspects);
    report.nodes = Object.entries(nodeRenames).map(([from, to]) => ({
      from,
      to
    }));
  }
  const hostIds = collectIds(host);
  const takenIds = new Set(hostIds);
  const idRenames = /* @__PURE__ */ new Map();
  const mint = (r, section2, oldId) => {
    const isRecordRow = section2 === "objectInstances" && r.type !== "folder";
    const kind = typeof r.type === "string" && r.type ? r.type : oldId.split("-")[0];
    return seededId(kind, isRecordRow ? 12 : 8, `${slug}:${oldId}`, takenIds);
  };
  for (const section2 of FEATURE_SECTIONS) {
    walkRecords(grafted[section2], (r) => {
      if (typeof r.id !== "string" || idRenames.has(r.id)) return;
      if (takenIds.has(r.id)) {
        idRenames.set(r.id, mint(r, section2, r.id));
      } else {
        takenIds.add(r.id);
      }
    });
  }
  if (idRenames.size > 0) {
    for (const section2 of FEATURE_SECTIONS) {
      grafted[section2] = grafted[section2].map(
        (v) => mapStrings(v, (s) => {
          var _a3;
          return (_a3 = idRenames.get(s)) != null ? _a3 : s;
        })
      );
    }
    report.ids = [...idRenames].map(([from, to]) => ({ from, to }));
    for (const section2 of FEATURE_SECTIONS) {
      mapStrings(grafted[section2], (s) => {
        for (const old of idRenames.keys()) {
          if (s !== old && wholeWord(old).test(s)) {
            report.suspects.push({ path: section2, value: old });
          }
        }
        return s;
      });
    }
  }
  const tree2 = { ...host };
  for (const section2 of FEATURE_SECTIONS) {
    const children2 = grafted[section2];
    if (children2.length === 0) continue;
    let folderId = featureFolderId(section2, slug);
    if (takenIds.has(folderId))
      folderId = mint({ type: "folder" }, section2, folderId);
    else takenIds.add(folderId);
    const root = host[section2];
    if (!isRecord(root) || !Array.isArray(root._children)) continue;
    tree2[section2] = {
      ...root,
      _children: [
        ...root._children,
        { id: folderId, type: "folder", name: folderName, _children: children2 }
      ]
    };
  }
  return right3({ tree: tree2, report });
};

// src/features/cut.ts
var prune = (node2, folderName) => {
  if (!isRecord(node2) || !Array.isArray(node2._children)) return { node: node2 };
  const kids5 = node2._children;
  const at = kids5.findIndex(
    (c) => isRecord(c) && c.type === "folder" && c.name === folderName
  );
  if (at !== -1) {
    const folder2 = kids5[at];
    const children2 = isRecord(folder2) && Array.isArray(folder2._children) ? folder2._children : [];
    return {
      node: { ...node2, _children: kids5.filter((_, i) => i !== at) },
      children: children2
    };
  }
  for (const [i, kid] of kids5.entries()) {
    const out = prune(kid, folderName);
    if (out.children !== void 0) {
      const next = [...kids5];
      next[i] = out.node;
      return { node: { ...node2, _children: next }, children: out.children };
    }
  }
  return { node: node2 };
};
var cutFeature = (config, slug) => {
  const folderName = featureFolderName(slug);
  const body = {};
  const rest = { ...config };
  const sections = [];
  for (const section2 of FEATURE_SECTIONS) {
    const out = prune(config[section2], folderName);
    if (out.children === void 0) continue;
    body[section2] = structuredClone(out.children);
    rest[section2] = out.node;
    sections.push(section2);
  }
  return { body, rest, sections };
};

// src/orgicore/utils/string/sha256.ts
var K = new Uint32Array([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]);
var utf8 = (s) => {
  const out = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c >= 55296 && c <= 56319) {
      const lo = i + 1 < s.length ? s.charCodeAt(i + 1) : 0;
      if (lo >= 56320 && lo <= 57343) {
        c = 65536 + (c - 55296 << 10) + (lo - 56320);
        i++;
      } else {
        c = 65533;
      }
    } else if (c >= 56320 && c <= 57343) {
      c = 65533;
    }
    if (c < 128) out.push(c);
    else if (c < 2048) out.push(192 | c >> 6, 128 | c & 63);
    else if (c < 65536) {
      out.push(224 | c >> 12, 128 | c >> 6 & 63, 128 | c & 63);
    } else {
      out.push(
        240 | c >> 18,
        128 | c >> 12 & 63,
        128 | c >> 6 & 63,
        128 | c & 63
      );
    }
  }
  return out;
};
var rotr = (x, n) => x >>> n | x << 32 - n;
var sha256Hex = (input) => {
  const bytes = utf8(input);
  const bitLen = bytes.length * 8;
  const padded = new Uint8Array(bytes.length + 9 + 63 >> 6 << 6);
  padded.set(bytes);
  padded[bytes.length] = 128;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLen / 4294967296));
  view.setUint32(padded.length - 4, bitLen >>> 0);
  const h = new Uint32Array([
    1779033703,
    3144134277,
    1013904242,
    2773480762,
    1359893119,
    2600822924,
    528734635,
    1541459225
  ]);
  const w = new Uint32Array(64);
  for (let off = 0; off < padded.length; off += 64) {
    for (let t30 = 0; t30 < 16; t30++) w[t30] = view.getUint32(off + t30 * 4);
    for (let t30 = 16; t30 < 64; t30++) {
      const s0 = rotr(w[t30 - 15], 7) ^ rotr(w[t30 - 15], 18) ^ w[t30 - 15] >>> 3;
      const s1 = rotr(w[t30 - 2], 17) ^ rotr(w[t30 - 2], 19) ^ w[t30 - 2] >>> 10;
      w[t30] = w[t30 - 16] + s0 + w[t30 - 7] + s1;
    }
    let [a, b2, c, d, e, f, g, hh] = h;
    for (let t30 = 0; t30 < 64; t30++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = e & f ^ ~e & g;
      const t1 = hh + S1 + ch + K[t30] + w[t30] >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = a & b2 ^ a & c ^ b2 & c;
      const t210 = S0 + maj >>> 0;
      hh = g;
      g = f;
      f = e;
      e = d + t1 >>> 0;
      d = c;
      c = b2;
      b2 = a;
      a = t1 + t210 >>> 0;
    }
    h[0] += a;
    h[1] += b2;
    h[2] += c;
    h[3] += d;
    h[4] += e;
    h[5] += f;
    h[6] += g;
    h[7] += hh;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, "0")).join("");
};

// src/validation/libraries.ts
var isObj6 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var canonical = (v) => {
  if (Array.isArray(v)) return v.map(canonical);
  if (!isObj6(v)) return v;
  const out = {};
  for (const k of Object.keys(v).sort()) out[k] = canonical(v[k]);
  return out;
};
var withoutId = (child) => {
  if (!isObj6(child)) return child;
  const { id: _id, ...rest } = child;
  void _id;
  return rest;
};
var libraryChildrenHash = (children2) => sha256Hex(JSON.stringify(canonical(children2.map(withoutId))));
var LIBRARY_ID = /^\w+\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
var LIBRARY_ID_PROBLEM = '`id` must be "<owner>/<slug>": letters, digits and `_` in the owner; lowercase letters, digits and single `-` in the slug';
var nodeProblems = (n, i) => {
  if (!isObj6(n)) return [`functions[${i}] is not a node object`];
  if (n.type !== FUNCTION && n.type !== CONST) {
    return [
      `functions[${i}] is a \`${String(n.type)}\` \u2014 a library holds only \`function\` and \`const\` nodes`
    ];
  }
  return typeof n.name === "string" && n.name !== "" ? [] : [`functions[${i}] has no \`name\``];
};
var duplicateNameProblems = (nodes2) => {
  const at = /* @__PURE__ */ new Map();
  nodes2.forEach((n, i) => {
    var _a2;
    if (isObj6(n) && typeof n.name === "string" && n.name !== "") {
      at.set(n.name, [...(_a2 = at.get(n.name)) != null ? _a2 : [], i]);
    }
  });
  return [...at].filter(([, idx]) => idx.length > 1).map(
    ([name, idx]) => `\`${name}\` is defined ${idx.length} times (${idx.map((i) => `functions[${i}]`).join(", ")}) \u2014 names in a library must be unique`
  );
};
var parseLibrarySource = (raw) => {
  if (!isObj6(raw))
    return { ok: false, problems: ["library.json must be an object"] };
  const problems = [];
  const { id, version, description, functions } = raw;
  if (typeof id !== "string" || !LIBRARY_ID.test(id)) {
    problems.push(LIBRARY_ID_PROBLEM);
  }
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) {
    problems.push("`version` must be a positive integer");
  }
  if (description !== void 0 && typeof description !== "string") {
    problems.push("`description` must be a string");
  }
  if (!Array.isArray(functions)) {
    problems.push("`functions` must be an array");
  } else {
    problems.push(...functions.flatMap(nodeProblems));
    problems.push(...duplicateNameProblems(functions));
  }
  if (problems.length > 0 || typeof id !== "string" || typeof version !== "number" || !Array.isArray(functions)) {
    return { ok: false, problems };
  }
  return {
    ok: true,
    source: {
      id,
      version,
      ...typeof description === "string" ? { description } : {},
      functions: functions.filter(isObj6)
    }
  };
};
var vendorLibrary = (source, omit2) => {
  const skip = new Set(omit2);
  const isOmitted = (n) => typeof n.name === "string" && skip.has(n.name);
  const omitted = source.functions.filter(isOmitted).map((n) => String(n.name)).sort();
  const children2 = source.functions.filter((n) => !isOmitted(n));
  return {
    children: children2,
    library: {
      id: source.id,
      version: source.version,
      hash: libraryChildrenHash(children2),
      ...omitted.length > 0 ? { omitted } : {}
    }
  };
};
var scanAppConfig = (json) => {
  const scan = { folders: [], decls: [] };
  if (!isObj6(json) || !isObj6(json.appConfig)) return scan;
  const walk8 = (v, path, folder2) => {
    if (Array.isArray(v)) {
      v.forEach((c, i) => walk8(c, [...path, i], folder2));
      return;
    }
    if (!isObj6(v)) return;
    const { type: type14, name } = v;
    let inner = folder2;
    if (typeof type14 === "string" && isVariant({ type: type14, variant: v.variant }, APP_NODE, "folder") && libraryRef.is(v.library)) {
      inner = {
        path: [...path],
        library: v.library,
        children: Array.isArray(v._children) ? v._children : []
      };
      scan.folders.push(inner);
    }
    if ((type14 === FUNCTION || type14 === CONST) && typeof name === "string") {
      scan.decls.push({ type: type14, name, path: [...path], folder: folder2 });
    }
    for (const [k, c] of Object.entries(v)) walk8(c, [...path, k], inner);
  };
  walk8(json.appConfig, ["appConfig"], void 0);
  return scan;
};
var collectLibraryFolders = (json) => scanAppConfig(json).folders;
var collectCallableDecls = (json) => scanAppConfig(json).decls;
var label = (lib) => `\`${lib.id}\` v${lib.version}`;
var driftErrors = (folders) => folders.filter((f) => libraryChildrenHash(f.children) !== f.library.hash).map((f) => ({
  path: pathStr([...f.path, "library", "hash"]),
  kind: "library-folder-drift",
  message: `Library folder ${label(f.library)} no longer matches its sync stamp \u2014 its children were edited after \`og library sync\` vendored them, so the org no longer runs the library it claims to.`,
  fixHint: `Never edit inside a library folder: re-run \`og library sync\` to restore it. To change the library, edit \`libraries/${f.library.id}\`, bump its \`version\` and re-sync; to replace one function, define your own outside the folder and re-sync \u2014 sync then lists it in \`library.omitted\`.`,
  severity: "error"
}));
var overrideInfos = (folders) => folders.flatMap(
  (f) => {
    var _a2;
    return ((_a2 = f.library.omitted) != null ? _a2 : []).map(
      (name, i) => ({
        path: pathStr([...f.path, "library", "omitted", i]),
        kind: "library-override",
        message: `Library ${label(f.library)} omits \`${name}\` \u2014 this org supplies its own (or none).`,
        severity: "info"
      })
    );
  }
);
var clashErrors = (decls) => {
  const errors = [];
  const reported = /* @__PURE__ */ new Set();
  for (const d of decls) {
    const { folder: folder2 } = d;
    if (!folder2) continue;
    const key = `${pathStr(folder2.path)}\0${d.name}`;
    if (reported.has(key)) continue;
    const others = decls.filter(
      (o) => o.name === d.name && o.folder !== folder2
    );
    if (others.length === 0) continue;
    reported.add(key);
    const outside = others.every((o) => o.folder === void 0);
    const where = outside ? "outside it" : "in another library folder";
    errors.push({
      path: pathStr([...d.path, "name"]),
      kind: "library-name-clash",
      message: `\`${d.name}\` is a ${d.type} of library ${label(folder2.library)} and is also defined ${where} \u2014 one name, two definitions, and which one a formula reaches is undefined.`,
      // Sync omits a name only when the ORG defines it, so re-running it
      // cannot settle a clash between two libraries (#2359).
      fixHint: outside ? `An override is expressed as \`omitted\`, never a duplicate: re-run \`og library sync\` \u2014 it omits \`${d.name}\` from the folder (recording it in \`library.omitted\`) because the org defines its own \u2014 or rename yours.` : `Two pinned libraries both define \`${d.name}\`, and \`og library sync\` cannot omit between libraries: rename it in one library and bump its \`version\`, or drop one of the pins.`,
      severity: "error",
      relatedPaths: others.map((o) => pathStr(o.path))
    });
  }
  return errors;
};
var validateLibraries = (json) => {
  const { folders, decls } = scanAppConfig(json);
  if (folders.length === 0) return [];
  return [
    ...driftErrors(folders),
    ...clashErrors(decls),
    ...overrideInfos(folders)
  ];
};

// src/features/seams.ts
var slotKind = (slot2) => typeof slot2.shape === "string" ? "value" : "list";
var EMPTY_LIST_BODY = "@[]";
var kids2 = (node2) => isRecord(node2) && Array.isArray(node2._children) ? node2._children : [];
var isSlotPin = (v) => isRecord(v) && typeof v.name === "string" && typeof v.node === "string" && (v.kind === "list" || v.kind === "value");
var isSeamPin = (v) => isRecord(v) && typeof v.name === "string" && typeof v.slot === "string" && typeof v.fn === "string" && typeof v.hostType === "string";
var installedPins = (tree2) => {
  const byId = /* @__PURE__ */ new Map();
  const walk8 = (node2) => {
    var _a2;
    for (const c of kids2(node2)) {
      if (!isRecord(c)) continue;
      const pin = c.feature;
      if (c.type === "folder" && isRecord(pin) && typeof pin.id === "string") {
        if (kids2(c).length === 0) continue;
        const entry = (_a2 = byId.get(pin.id)) != null ? _a2 : { id: pin.id, slots: [], seams: [] };
        if (Array.isArray(pin.slots))
          entry.slots.push(...pin.slots.filter(isSlotPin));
        if (Array.isArray(pin.seams))
          entry.seams.push(...pin.seams.filter(isSeamPin));
        byId.set(pin.id, entry);
        continue;
      }
      walk8(c);
    }
  };
  for (const section2 of FEATURE_SECTIONS) walk8(tree2[section2]);
  return [...byId.values()].sort((a, b2) => a.id.localeCompare(b2.id));
};
var hostTypeNames = (tree2) => {
  const names2 = /* @__PURE__ */ new Set();
  const walk8 = (v) => {
    if (!isRecord(v)) return;
    if (v.type === "objectType" && typeof v.name === "string")
      names2.add(v.name);
    kids2(v).forEach(walk8);
  };
  walk8(tree2.objectTypes);
  return names2;
};
var slotBody = (slot2, fns) => {
  var _a2;
  return slot2.kind === "list" ? fns.length ? `@concat(${fns.map((f) => `${f}()`).join(", ")})` : EMPTY_LIST_BODY : fns.length ? `@${fns[0]}()` : (_a2 = slot2.default) != null ? _a2 : "@null";
};
var planWeave = (tree2) => {
  const installed = installedPins(tree2);
  const types2 = hostTypeNames(tree2);
  return installed.flatMap(
    (provider) => provider.slots.map((slot2) => {
      const matching = installed.filter((c) => c.id !== provider.id).flatMap(
        (c) => c.seams.filter((s) => s.slot === slot2.name).sort((a, b2) => a.name.localeCompare(b2.name)).map((s) => ({ feature: c.id, seam: s.name, fn: s.fn, s }))
      );
      const contributions = matching.filter((m) => types2.has(m.s.hostType)).map(({ feature, seam, fn }) => ({ feature, seam, fn }));
      const waiting = matching.filter((m) => !types2.has(m.s.hostType)).map(({ feature, seam, fn, s }) => ({
        feature,
        seam,
        fn,
        hostType: s.hostType
      }));
      return {
        feature: provider.id,
        slot: slot2.name,
        node: slot2.node,
        kind: slot2.kind === "value" ? "value" : "list",
        contributions,
        waiting,
        body: slotBody(
          slot2,
          contributions.map((c) => c.fn)
        )
      };
    })
  );
};
var setBodies = (node2, bodies) => {
  if (!isRecord(node2)) return node2;
  let next = node2;
  if (node2.type === "function" && typeof node2.name === "string" && bodies.has(node2.name) && node2.compute !== bodies.get(node2.name)) {
    next = { ...node2, compute: bodies.get(node2.name) };
  }
  if (Array.isArray(node2._children)) {
    const children2 = node2._children.map((c) => setBodies(c, bodies));
    if (children2.some((c, i) => c !== kids2(node2)[i]))
      next = { ...next, _children: children2 };
  }
  return next;
};
var weaveSeams = (tree2) => {
  const weaves = planWeave(tree2);
  if (weaves.length === 0) return { tree: tree2, weaves };
  const bodies = new Map(weaves.map((w) => [w.node, w.body]));
  return {
    tree: { ...tree2, appConfig: setBodies(tree2.appConfig, bodies) },
    weaves
  };
};
var featureFolderHash = (children2, slotNodes = []) => {
  const blank = new Map([...slotNodes].map((n) => [n, EMPTY_LIST_BODY]));
  return libraryChildrenHash(
    blank.size ? children2.map((c) => setBodies(c, blank)) : children2
  );
};

// src/features/install.ts
var invalidParams = (message) => left3({ kind: "invalid-params", message });
var kids3 = (node2) => isRecord(node2) && Array.isArray(node2._children) ? node2._children : [];
var toPin = (raw) => isRecord(raw) && typeof raw.id === "string" && typeof raw.version === "number" ? {
  id: raw.id,
  version: raw.version,
  ...typeof raw.hash === "string" ? { hash: raw.hash } : {}
} : void 0;
var featureChildrenHash = (children2, slotNodes = []) => featureFolderHash(children2, slotNodes);
var findFeatureFolders = (config, slug) => {
  const name = featureFolderName(slug);
  const found = [];
  const walk8 = (node2, at, section2) => kids3(node2).some((c, i) => {
    if (isRecord(c) && c.type === "folder" && c.name === name) {
      found.push({
        section: section2,
        at: [...at, i],
        folder: c,
        pin: toPin(c.feature)
      });
      return true;
    }
    return walk8(c, [...at, i], section2);
  });
  for (const section2 of FEATURE_SECTIONS) walk8(config[section2], [], section2);
  return found;
};
var editChildren = (root, at, fn) => {
  if (!isRecord(root)) return root;
  if (at.length === 0) return { ...root, _children: fn(kids3(root)) };
  const [i, ...rest] = at;
  const next = [...kids3(root)];
  next[i] = editChildren(next[i], rest, fn);
  return { ...root, _children: next };
};
var plural2 = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
var objectTypeNames2 = (body) => {
  var _a2;
  const names2 = [];
  const walk8 = (v) => {
    if (Array.isArray(v)) return v.forEach(walk8);
    if (!isRecord(v)) return;
    if (v.type === "objectType" && typeof v.name === "string")
      names2.push(v.name);
    kids3(v).forEach(walk8);
  };
  walk8((_a2 = body.objectTypes) != null ? _a2 : []);
  return names2;
};
var wrapSections = (body) => Object.fromEntries(
  FEATURE_SECTIONS.map((s) => {
    var _a2;
    return [
      s,
      { type: "folder", _children: (_a2 = body[s]) != null ? _a2 : [] }
    ];
  })
);
var unwrapSections = (wrapped, shell) => {
  const body = {};
  for (const s of FEATURE_SECTIONS) {
    const children2 = kids3(isRecord(wrapped) ? wrapped[s] : void 0);
    if (children2.length) body[s] = children2;
  }
  if (shell) body.shell = shell;
  return body;
};
var applyParams = (bundle, params) => {
  var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
  const declared = (_a2 = bundle.manifest.params) != null ? _a2 : {};
  const owner = `the ${bundle.manifest.id} feature`;
  if (params.theme !== void 0) {
    return invalidParams("A feature has no theme to set.");
  }
  let body = structuredClone(bundle.body);
  const slots = (_b = params.seedSlots) != null ? _b : [];
  const unknownSlot = slots.find((s) => {
    var _a3;
    return !((_a3 = declared.seedSlots) == null ? void 0 : _a3.includes(s));
  });
  if (unknownSlot !== void 0) {
    return invalidParams(
      `"${unknownSlot}" is not a seed slot of ${owner} (has: ${((_c = declared.seedSlots) != null ? _c : []).join(", ") || "none"}).`
    );
  }
  if (slots.length && body.objectInstances) {
    body.objectInstances = kids3(
      emptySeedSlots(
        { type: "folder", _children: body.objectInstances },
        new Set(slots)
      )
    );
  }
  for (const [node2, label2] of Object.entries((_d = params.labels) != null ? _d : {})) {
    if (!((_e = declared.labels) == null ? void 0 : _e.includes(node2))) {
      return invalidParams(
        `"${node2}" is not a labellable node of ${owner} (has: ${((_f = declared.labels) != null ? _f : []).join(", ") || "none"}).`
      );
    }
    const text = displayText(label2);
    if (text === void 0) {
      return invalidParams(
        `The label for "${node2}" must be non-empty text that does not start with @.`
      );
    }
    const matches = ((_g = body.appConfig) != null ? _g : []).filter(
      (n) => isRecord(n) && n.name === node2
    );
    if (matches.length !== 1) {
      return invalidParams(
        `${owner} has ${matches.length} top-level nodes named "${node2}"; a labellable node must be unique.`
      );
    }
    matches[0].label = text;
    for (const row of (_i = (_h = body.shell) == null ? void 0 : _h.nav) != null ? _i : []) {
      if (isRecord(row) && row.name === node2 && "title" in row)
        row.title = text;
    }
  }
  const renames = (_j = params.entityRenames) != null ? _j : {};
  const check = checkEntityRenames(
    (_k = declared.entities) != null ? _k : [],
    owner,
    objectTypeNames2(body),
    renames
  );
  if (isLeft3(check)) {
    return invalidParams(check.left.message);
  }
  if (Object.keys(renames).length) {
    const renamed = renameTypesInConfig(wrapSections(body), renames);
    if (isLeft3(renamed)) {
      return invalidParams(
        `The entity rename could not be placed in ${plural2(renamed.left.length, "place")}.`
      );
    }
    body = unwrapSections(renamed.right, body.shell);
  }
  return right3({ body, typeRenames: { ...renames } });
};
var findNavRegistry = (host) => {
  const navTypes = /* @__PURE__ */ new Set();
  const walkTypes = (v) => {
    if (!isRecord(v)) return;
    if (v.type === "objectType" && typeof v.name === "string") {
      const fields = Array.isArray(v.fields) ? v.fields : [];
      const has = (n) => fields.some((f) => isRecord(f) && f.name === n);
      if (has("name") && has("title")) navTypes.add(v.name);
    }
    kids3(v).forEach(walkTypes);
  };
  walkTypes(host.objectTypes);
  let registry;
  const walkApp = (v) => {
    if (registry !== void 0 || !isRecord(v)) return;
    const data = isRecord(v.data) ? v.data : void 0;
    if (v.isRepeating === true && typeof (data == null ? void 0 : data.dataType) === "string" && navTypes.has(data.dataType)) {
      registry = data.dataType;
      return;
    }
    kids3(v).forEach(walkApp);
  };
  walkApp(host.appConfig);
  return registry;
};
var registryFolder = (host, type14, folderName) => {
  var _a2, _b;
  const search = (node2, at, hit) => {
    if (hit(kids3(node2), node2)) return at;
    for (const [i, c] of kids3(node2).entries()) {
      const found = search(c, [...at, i], hit);
      if (found) return found;
    }
    return void 0;
  };
  return (_b = (_a2 = search(
    host.objectInstances,
    [],
    (children2) => children2.some((c) => isRecord(c) && c.type === type14)
  )) != null ? _a2 : folderName === void 0 ? void 0 : search(
    host.objectInstances,
    [],
    (_, node2) => isRecord(node2) && node2.type === "folder" && node2.name === folderName
  )) != null ? _b : [];
};
var recordsOfType = (root, type14) => {
  const out = [];
  const walk8 = (v) => {
    if (!isRecord(v)) return;
    if (v.type === type14) out.push(v);
    kids3(v).forEach(walk8);
  };
  walk8(root);
  return out;
};
var article = (word) => /^[aeiou]/i.test(word) ? "an" : "a";
var triggers = (n) => {
  const on = [
    n.onCreate === true && "added",
    n.onUpdate === true && "updated",
    n.onDelete === true && "deleted"
  ].filter((x) => typeof x === "string");
  if (on.length === 0) return "changed";
  return on.length === 1 ? on[0] : `${on.slice(0, -1).join(", ")} or ${on[on.length - 1]}`;
};
var shellEntries = (body, host) => {
  var _a2, _b, _c, _d;
  const entries = [];
  const used = /* @__PURE__ */ new Set();
  const keyFor = (base) => {
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base}.${n}`;
    used.add(key);
    return key;
  };
  for (const raw of (_b = (_a2 = body.shell) == null ? void 0 : _a2.nav) != null ? _b : []) {
    if (!isRecord(raw) || typeof raw.name !== "string") continue;
    const row = raw;
    const screen2 = raw.name;
    const title = typeof row.title === "string" ? row.title : screen2;
    entries.push({
      option: {
        key: keyFor(`nav.${screen2}`),
        kind: "nav",
        question: `Add "${title}" to the home-screen menu?`,
        default: true
      },
      apply: (ctx) => {
        var _a3, _b2;
        const target = (_a3 = ctx.nodeRenames[screen2]) != null ? _a3 : screen2;
        const registry = findNavRegistry(ctx.tree);
        if (registry === void 0) {
          ctx.report.notes.push(noRegistryNote(host, target));
          return false;
        }
        if (recordsOfType(ctx.tree.objectInstances, registry).some(
          (r) => r.name === target
        )) {
          ctx.report.notes.push(`The menu already links to ${target}.`);
          return false;
        }
        const id = typeof row.id === "string" && !ctx.takenIds.has(row.id) ? row.id : seededId(registry, 12, `${ctx.slug}:nav:${screen2}`, ctx.takenIds);
        ctx.takenIds.add(id);
        const record10 = { ...row, id, type: registry, name: target };
        ctx.tree.objectInstances = editChildren(
          ctx.tree.objectInstances,
          registryFolder(ctx.tree, registry, (_b2 = body.shell) == null ? void 0 : _b2.navFolder),
          (c) => [...c, record10]
        );
        return true;
      }
    });
  }
  for (const raw of (_d = (_c = body.shell) == null ? void 0 : _c.notifications) != null ? _d : []) {
    if (!isRecord(raw) || typeof raw.onPublishType !== "string") continue;
    const n = raw;
    const type14 = raw.onPublishType;
    const label2 = typeof n.label === "string" ? n.label : `${type14} changes`;
    entries.push({
      option: {
        key: keyFor(`notifications.${type14}`),
        kind: "notification",
        question: `Notify users ("${label2}") when ${article(type14)} ${type14} is ${triggers(n)}?`,
        default: true
      },
      apply: (ctx) => {
        var _a3;
        const app2 = isRecord(ctx.tree.appConfig) ? ctx.tree.appConfig : {};
        const existing = Array.isArray(app2.notifications) ? app2.notifications : [];
        const onPublishType = ctx.typeName(type14);
        if (existing.some(
          (e) => isRecord(e) && e.onPublishType === onPublishType && e.label === n.label
        )) {
          ctx.report.notes.push(
            `The "${label2}" notification is already there.`
          );
          return false;
        }
        const clickPath = typeof n.clickPath === "string" ? (_a3 = renameNavTarget(n.clickPath, ctx.nodeRenames)) != null ? _a3 : n.clickPath : void 0;
        const entry = {
          ...n,
          onPublishType,
          ...clickPath !== void 0 ? { clickPath } : {}
        };
        ctx.tree.appConfig = { ...app2, notifications: [...existing, entry] };
        return true;
      }
    });
  }
  return entries;
};
var topScreens = (node2) => kids3(node2).flatMap(
  (n) => !isRecord(n) ? [] : n.type === "folder" ? topScreens(n) : n.type === "screen" && typeof n.name === "string" ? [n] : []
);
var homeLinksTo = (appConfig3, home, target) => {
  const out = renameNodesInTree(appConfig3, { [target]: `${target}__navProbe` });
  if (isLeft3(out)) return true;
  const after = topScreens(out.right.tree).find((s) => s.name === home.name);
  return JSON.stringify(after) !== JSON.stringify(home);
};
var noRegistryNote = (host, screen2) => {
  const [home, ...others] = topScreens(host.appConfig);
  return home && others.some((s) => homeLinksTo(host.appConfig, home, String(s.name))) ? `This org's home menu is hand-authored (${String(home.name)} links its screens directly), so no menu row was added. Add a link to ${screen2} there.` : `This org has no home-menu registry, so no menu row was added. Add a nav entry to reach ${screen2}.`;
};
var withoutType2 = (nodes2, type14) => nodes2.flatMap(
  (n) => !isRecord(n) ? [n] : n.type === "objectType" && n.name === type14 ? [] : Array.isArray(n._children) ? [{ ...n, _children: withoutType2(n._children, type14) }] : [n]
);
var markType = (nodes2, type14, mark) => nodes2.map(
  (n) => !isRecord(n) ? n : n.type === "objectType" && n.name === type14 ? { ...n, wellKnown: mark } : Array.isArray(n._children) ? { ...n, _children: markType(n._children, type14, mark) } : n
);
var bindWellKnown = (bundle, sections, host, paramRenames) => {
  var _a2, _b, _c, _d, _e, _f, _g;
  const { id } = bundle.manifest;
  let body = { ...sections };
  const bindings = [];
  const renames = {};
  for (const required of (_a2 = bundle.manifest.requires.types) != null ? _a2 : []) {
    const wk = required.wellKnown;
    if (wk === void 0) continue;
    const type14 = (_b = paramRenames[required.name]) != null ? _b : required.name;
    const target = findWellKnownHost(host.objectTypes, wk);
    if (target) {
      const reads = required.fields.map((f) => f.name);
      const missing = missingWellKnownFields(target, wk, reads);
      const hostName = String(target.name);
      if (missing.length) {
        return left3({
          kind: "well-known-mismatch",
          missing,
          message: `${id} binds its ${type14} to this org's ${wk} type, ${hostName}, which lacks ${missing.join(", ")}.`
        });
      }
      body = {
        ...body,
        objectTypes: withoutType2((_c = body.objectTypes) != null ? _c : [], type14),
        objectInstances: kids3(
          emptySeedSlots(
            { type: "folder", _children: (_d = body.objectInstances) != null ? _d : [] },
            /* @__PURE__ */ new Set([type14])
          )
        )
      };
      if (hostName !== type14) {
        const renamed = renameTypesInConfig(wrapSections(body), {
          [type14]: hostName
        });
        if (isLeft3(renamed)) {
          return invalidParams(
            `Binding ${type14} to ${hostName} could not be placed in ${plural2(renamed.left.length, "place")}.`
          );
        }
        body = unwrapSections(renamed.right, void 0);
        renames[type14] = hostName;
      }
      bindings.push({ wellKnown: wk, type: type14, host: hostName });
    } else {
      const brings = JSON.stringify(withoutType2((_e = body.objectTypes) != null ? _e : [], type14)) !== JSON.stringify((_f = body.objectTypes) != null ? _f : []);
      if (!brings) {
        return left3({
          kind: "well-known-missing",
          message: `${id} needs a ${wk} and brings none. Mark one of this org's types \`wellKnown: "${wk}"\` and install again.`
        });
      }
      body = {
        ...body,
        objectTypes: markType((_g = body.objectTypes) != null ? _g : [], type14, wk)
      };
      bindings.push({ wellKnown: wk, type: type14, brought: type14 });
    }
  }
  return right3({ body, bindings, renames });
};
var installOptions = (bundle) => shellEntries(bundle.body, {}).map((e) => e.option);
var collectIds2 = (v, into = /* @__PURE__ */ new Set()) => {
  if (Array.isArray(v)) v.forEach((x) => collectIds2(x, into));
  else if (isRecord(v)) {
    if (typeof v.id === "string") into.add(v.id);
    Object.values(v).forEach((x) => collectIds2(x, into));
  }
  return into;
};
var findFunction = (node2, name) => {
  if (!isRecord(node2)) return void 0;
  if (node2.type === "function" && node2.name === name) return node2;
  for (const c of kids3(node2)) {
    const hit = findFunction(c, name);
    if (hit) return hit;
  }
  return void 0;
};
var hasNoParameters = (fn) => typeof fn.parameters !== "string" || fn.parameters.trim() === "";
var seamPins = (bundle, tree2, slug, nodeRenames, typeName) => {
  var _a2, _b;
  const { id } = bundle.manifest;
  const folder2 = kids3(tree2.appConfig).find(
    (c) => isRecord(c) && c.name === featureFolderName(slug)
  );
  const fnFor = (role, name) => {
    var _a3;
    const node2 = (_a3 = nodeRenames[name]) != null ? _a3 : name;
    const fn = findFunction(folder2, node2);
    if (fn === void 0 || !hasNoParameters(fn)) {
      return left3({
        kind: "bad-seam",
        message: `${id} declares ${role} "${name}", but its screens have no zero-parameter function node named ${name}.`
      });
    }
    return right3({ node: node2, fn });
  };
  const slots = [];
  for (const slot2 of (_a2 = bundle.manifest.provides.slots) != null ? _a2 : []) {
    const found = fnFor("the slot", slot2.name);
    if (isLeft3(found)) return found;
    const kind = slotKind(slot2);
    const body = found.right.fn.compute;
    slots.push({
      name: slot2.name,
      node: found.right.node,
      kind,
      ...kind === "value" && typeof body === "string" ? { default: body } : {}
    });
  }
  const seams = [];
  for (const seam of (_b = bundle.manifest.seams) != null ? _b : []) {
    const found = fnFor("the seam", seam.fn);
    if (isLeft3(found)) return found;
    seams.push({
      name: seam.name,
      slot: seam.slot,
      fn: found.right.node,
      hostType: typeName(seam.hostType.name)
    });
  }
  return right3({ slots, seams });
};
var installFeature = (host, bundle, opts = {}) => {
  var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  const { id, version } = bundle.manifest;
  const slug = (_b = (_a2 = opts.slug) != null ? _a2 : id.split("/").pop()) != null ? _b : id;
  const params = applyParams(bundle, (_c = opts.params) != null ? _c : {});
  if (isLeft3(params)) return params;
  const entries = shellEntries(params.right.body, host);
  const options = entries.map((e) => e.option);
  const skip = new Set((_d = opts.skip) != null ? _d : []);
  const unknown10 = [...skip].filter((k) => !options.some((o) => o.key === k));
  if (unknown10.length) {
    return left3({
      kind: "unknown-option",
      message: `${id} has no option ${unknown10.join(", ")} (has: ${options.map((o) => o.key).join(", ") || "none"}).`,
      options: options.map((o) => o.key)
    });
  }
  const existing = findFeatureFolders(host, slug);
  const foreign = existing.find((f) => f.pin && f.pin.id !== id);
  if (foreign) {
    return left3({
      kind: "slug-taken",
      message: `${featureFolderName(slug)} holds ${(_e = foreign.pin) == null ? void 0 : _e.id}. Install ${id} under another slug.`
    });
  }
  const stubs = existing.length > 0 && existing.every((f) => f.pin !== void 0 && kids3(f.folder).length === 0);
  if (existing.length > 0 && !stubs && !opts.force) {
    const pinned = (_f = existing.find((f) => f.pin)) == null ? void 0 : _f.pin;
    return left3({
      kind: "installed",
      message: pinned ? `${id} is already installed here (pinned v${pinned.version}). Pass --force to replace it, discarding local edits.` : `This org already has its own ${featureFolderName(slug)} folders. Pass --force to replace them with ${id}.`,
      ...pinned ? { version: pinned.version } : {}
    });
  }
  const placement = existing.length === 0 ? "appended" : stubs ? "filled-stubs" : "replaced";
  const { shell: _shell, ...sections } = params.right.body;
  void _shell;
  const { rest } = cutFeature(host, slug);
  const bound = bindWellKnown(bundle, sections, rest, params.right.typeRenames);
  if (isLeft3(bound)) return bound;
  const grafted = graftFeature(rest, bound.right.body, {
    slug,
    prefix: opts.prefix,
    referenced: [
      ...((_g = bundle.manifest.provides.slots) != null ? _g : []).map((sl) => sl.name),
      ...((_h = bundle.manifest.seams) != null ? _h : []).map((se) => se.fn)
    ]
  });
  if (isLeft3(grafted)) return grafted;
  const tree2 = { ...grafted.right.tree };
  const paramTypes = params.right.typeRenames;
  const boundTypes = bound.right.renames;
  const graftTypes = Object.fromEntries(
    grafted.right.report.types.map((r) => [r.from, r.to])
  );
  const typeName = (type14) => {
    var _a3, _b2, _c2;
    const named = (_a3 = paramTypes[type14]) != null ? _a3 : type14;
    const bound2 = (_b2 = boundTypes[named]) != null ? _b2 : named;
    return (_c2 = graftTypes[bound2]) != null ? _c2 : bound2;
  };
  const nodeRenames = Object.fromEntries(
    grafted.right.report.nodes.map((r) => [r.from, r.to])
  );
  const pins = seamPins(bundle, tree2, slug, nodeRenames, typeName);
  if (isLeft3(pins)) return pins;
  const { slots, seams } = pins.right;
  const slotNodes = slots.map((sl) => sl.node);
  for (const section2 of FEATURE_SECTIONS) {
    const rootKids = kids3(tree2[section2]);
    const last2 = rootKids[rootKids.length - 1];
    if (!isRecord(last2) || last2.name !== featureFolderName(slug)) continue;
    const children2 = kids3(last2);
    const old = existing.find((f) => f.section === section2);
    const { _children: _old, ...oldKeys } = (_i = old == null ? void 0 : old.folder) != null ? _i : {};
    void _old;
    const { _children: _new, ...newKeys } = last2;
    void _new;
    const pin = {
      id,
      version,
      hash: featureChildrenHash(children2, slotNodes),
      ...section2 === "appConfig" && slots.length ? { slots } : {},
      ...section2 === "appConfig" && seams.length ? { seams } : {}
    };
    const folder2 = {
      ...old ? oldKeys : newKeys,
      feature: pin,
      _children: children2
    };
    let root = editChildren(tree2[section2], [], (c) => c.slice(0, -1));
    const at = (_j = old == null ? void 0 : old.at) != null ? _j : [rootKids.length - 1];
    root = editChildren(root, at.slice(0, -1), (c) => {
      const next = [...c];
      next.splice(at[at.length - 1], 0, folder2);
      return next;
    });
    tree2[section2] = root;
  }
  const report = {
    id,
    version,
    slug,
    placement,
    graft: grafted.right.report,
    bindings: [],
    applied: [],
    skipped: [],
    notes: [],
    seams: []
  };
  report.bindings = bound.right.bindings.map(
    (b2) => {
      var _a3;
      return b2.brought ? { ...b2, brought: (_a3 = graftTypes[b2.brought]) != null ? _a3 : b2.brought } : b2;
    }
  );
  const ctx = {
    tree: tree2,
    nodeRenames,
    typeName,
    takenIds: collectIds2(tree2),
    slug,
    report
  };
  for (const entry of entries) {
    const { key } = entry.option;
    if (skip.has(key)) {
      report.skipped.push(key);
      continue;
    }
    (entry.apply(ctx) ? report.applied : report.skipped).push(key);
  }
  const woven = weaveSeams(ctx.tree);
  report.seams = woven.weaves.filter(
    (w) => w.feature === id || [...w.contributions, ...w.waiting].some((c) => c.feature === id)
  );
  return right3({ tree: woven.tree, report, options });
};

// src/features/uninstall.ts
var kids4 = (node2) => isRecord(node2) && Array.isArray(node2._children) ? node2._children : [];
var FOLDER_PREFIX = "feature-";
var installedSlugs = (host, id) => {
  const slugs = /* @__PURE__ */ new Set();
  const walk8 = (node2) => {
    for (const c of kids4(node2)) {
      if (!isRecord(c)) continue;
      const pin = c.feature;
      if (c.type === "folder" && isRecord(pin) && pin.id === id && typeof c.name === "string" && c.name.startsWith(FOLDER_PREFIX) && kids4(c).length > 0) {
        slugs.add(c.name.slice(FOLDER_PREFIX.length));
        continue;
      }
      walk8(c);
    }
  };
  for (const section2 of FEATURE_SECTIONS) walk8(host[section2]);
  return [...slugs].sort();
};
var namesOf = (nodes2, type14) => {
  const names2 = /* @__PURE__ */ new Set();
  const walk8 = (v) => {
    if (!isRecord(v)) return;
    if (v.type === type14 && typeof v.name === "string") names2.add(v.name);
    kids4(v).forEach(walk8);
  };
  nodes2.forEach(walk8);
  return names2;
};
var withoutRows = (node2, type14, screens, removed) => {
  if (!isRecord(node2) || !Array.isArray(node2._children)) return node2;
  const children2 = node2._children.flatMap((c) => {
    if (isRecord(c) && c.type === type14 && typeof c.name === "string" && screens.has(c.name)) {
      removed.push(c.name);
      return [];
    }
    return [withoutRows(c, type14, screens, removed)];
  });
  return { ...node2, _children: children2 };
};
var uninstallFeature = (host, id, slug) => {
  var _a2, _b;
  const slugs = installedSlugs(host, id);
  if (slug !== void 0 ? !slugs.includes(slug) : slugs.length === 0) {
    return left3({
      kind: "not-installed",
      message: `${id} is not installed here${slug !== void 0 ? ` as ${slug}` : ""}.`
    });
  }
  if (slug === void 0 && slugs.length > 1) {
    return left3({
      kind: "ambiguous",
      message: `${id} is installed ${slugs.length} times (${slugs.join(", ")}). Name the copy to remove.`,
      slugs
    });
  }
  const which = slug != null ? slug : slugs[0];
  const before = planWeave(host).filter(
    (w) => w.feature === id || w.contributions.some((c) => c.feature === id)
  );
  const { body, rest } = cutFeature(host, which);
  const screens = namesOf((_a2 = body.appConfig) != null ? _a2 : [], "screen");
  const types2 = namesOf((_b = body.objectTypes) != null ? _b : [], "objectType");
  const nav = [];
  const registry = findNavRegistry(rest);
  const tree2 = registry === void 0 ? rest : {
    ...rest,
    objectInstances: withoutRows(
      rest.objectInstances,
      registry,
      screens,
      nav
    )
  };
  const notifications = [];
  const app2 = tree2.appConfig;
  if (isRecord(app2) && Array.isArray(app2.notifications)) {
    const kept = app2.notifications.filter((n) => {
      const type14 = isRecord(n) ? n.onPublishType : void 0;
      if (typeof type14 === "string" && types2.has(type14)) {
        notifications.push(type14);
        return false;
      }
      return true;
    });
    tree2.appConfig = { ...app2, notifications: kept };
  }
  return right3({
    tree: weaveSeams(tree2).tree,
    report: { id, slug: which, nav, notifications, seams: before }
  });
};

// src/prompts/outlineNotation.ts
var OUTLINE_NOTATION = `### Reading example orgs (outline notation)

\`request_examples\` returns each org as a compact **outline** in a \`jsx\` code block, not as JSON. The outline is a reading format only: **your response must still be the org JSON described in this prompt.** Never emit outline syntax.

How the outline maps to JSON:

- **Element = node.** The tag is the node's \`type\`, \`name="\u2026"\` is its \`name\`, and nested elements are its \`_children\`. \`<screen name="books" label="Books">\` with \`<content name="hero"/>\` inside is \`{ "type": "screen", "name": "books", "label": "Books", "_children": [{ "type": "content", "name": "hero" }] }\`.
- **Attribute values:** \`"\u2026"\` is a JSON string, \`{\u2026}\` is JSON (number, object, array, \`null\`, \`false\`), a bare key is \`true\`. \`id\`s and empty values are left out of the outline \u2014 your JSON still needs every required field, including \`id\`.
- **Data-binding shorthand:** bare \`repeating\` is \`"isRepeating": true\`; \`of="Book"\` is \`"data": { "dataType": "Book" }\`; \`source="storage"\` is \`data.source\` (\`embedded\` when absent). Any other \`data\` members appear as \`data={\u2026}\`.
- **HTML is abbreviated.** \`html="<div class=\u2026\u2026 (412 bytes)"\` is only the first line of an \`htmlTemplate\`. Write complete templates of your own \u2014 never copy a stub.
- **\`<appConfig \u2026>\`** is the root \`app\` node, with its attributes on the tag.
- **\`<objectTypes>\`:** the tag is the type name; \`<field title:text/>\` is \`{ "name": "title", "type": "text" }\`; a bare \`list\` is \`"isList": true\`; \`parent={\u2026}\` is the parent relation.
- **\`<objectInstances>\`:** \`<Book count={12} eg={["Dune","Emma"]}/>\` summarises the seed rows (how many, sample display values). It is not the instance format.
- **\`<plugins>\`:** the tag is the plugin name; \`params={[\u2026]}\` lists its parameter names only.

Use example orgs for **structure and patterns**: which screens exist, how they nest, how data is bound, which node types do the work. Adapt them to the user's request rather than copying names.`;

// src/prompts/promptStrip.ts
var STRIP_BLOCK = /<!-- BEGIN PROMPT-STRIP: [^\n]*?-->[\s\S]*?<!-- END PROMPT-STRIP: [^\n]*?-->/g;
var stripPromptOnly = (markdown) => markdown.replace(STRIP_BLOCK, "").replace(/\n{3,}/g, "\n\n");

// src/prompts/buildPrompts.ts
var stripDocsOnlySections = stripPromptOnly;
var buildVariablesSection = (context) => {
  const { variableNames = [] } = context;
  return variableNames.length > 0 ? `## Available Variables
The following variables are available in this context and can be referenced in \${} expressions:
${variableNames.map((v) => `- \`${v}\``).join("\n")}

These likely represent data fields or computed values from the app. Use them to make the HTML dynamic.` : `## Variables
No specific variables are defined in this context, but you may have access to:
- \`$index\` - The current iteration index (in repeating sections)
- Data fields from the parent context`;
};
var buildExistingHtmlSection = (context) => {
  const { existingHtml } = context;
  return existingHtml ? `## Existing Content
The user wants to refine or build upon this existing HTML:
\`\`\`html
${existingHtml}
\`\`\`
` : "";
};
var buildHtmlSystemPromptFromTemplate = (template, formulaReference, context) => {
  return template.replace("{{FORMULA_REFERENCE}}", stripDocsOnlySections(formulaReference)).replace("{{VARIABLES_SECTION}}", buildVariablesSection(context)).replace("{{EXISTING_HTML_SECTION}}", buildExistingHtmlSection(context));
};
var buildExistingOrgSection = (context) => {
  const { existingOrgJson } = context;
  return existingOrgJson ? `## Existing Organization Structure
The user wants to refine or build upon this existing org configuration:
\`\`\`json
${existingOrgJson}
\`\`\`
` : "";
};
var buildInspirationOrgSection = (context) => {
  const { inspirationOrg } = context;
  return inspirationOrg ? `## Inspiration Org
The user is creating a new org inspired by the following existing org. This is your **primary reference** \u2014 study its structure, screen layout, data types, plugins, and design patterns carefully. Adapt these patterns to the user's new concept. Do NOT copy it verbatim, but the structural quality and completeness should match or exceed the inspiration.
${inspirationOrg}

Since the user is working from an inspiration org, you **MUST** use \`ask_user\` on your first turn to understand how they want the new org to differ. Ask about: which features to keep, what to change, what to add, and the overall theme/purpose. For example: "I can see this org has X, Y, and Z. How would you like your new org to differ? Are there features you'd like to keep, change, or add?"
` : "";
};
var buildShowcaseMenuSection = (context) => {
  const { showcaseMenu } = context;
  if (!showcaseMenu || showcaseMenu.length === 0) return "";
  const catalogue = showcaseMenu.map((item) => {
    var _a2;
    const features = ((_a2 = item.features) == null ? void 0 : _a2.length) ? ` \u2014 ${item.features.join(", ")}` : "";
    return `- \`${item.id}\` \u2014 ${item.name}${features}`;
  }).join("\n");
  return `## Showcase Examples (available on request)
Every org below is a real, published Orgiggly app. If one would help with the user's request, call \`request_examples\` with 1-3 ids before generating \u2014 the user's first message lists the best matches for this request. Treat a fetched example as patterns to learn from, not a spec to copy.

${catalogue}

${OUTLINE_NOTATION}
`;
};
var buildOrgSystemPromptV2 = (template, sections, examples, context, imagesSection, webSearchSection, videosSection) => {
  const sectionsBlock = sections.map(stripDocsOnlySections).join("\n\n");
  const examplesBlock = `## Pattern Examples

${examples.map((ex) => `\`\`\`json
${ex}
\`\`\``).join("\n\n")}`;
  return template.replace("{{SECTIONS}}", sectionsBlock).replace("{{EXAMPLES}}", examplesBlock).replace("{{EXISTING_ORG_SECTION}}", buildExistingOrgSection(context)).replace(
    "{{INSPIRATION_ORG_SECTION}}",
    buildInspirationOrgSection(context)
  ).replace(
    "{{SHOWCASE_MENU_SECTION}}",
    buildShowcaseMenuSection(context)
  ).replace("{{IMAGES_SECTION}}", imagesSection != null ? imagesSection : "").replace("{{WEB_SEARCH_SECTION}}", webSearchSection != null ? webSearchSection : "").replace("{{VIDEOS_SECTION}}", videosSection != null ? videosSection : "");
};
var buildMarketplaceMenuSection = (context) => {
  const { marketplaceMenu } = context;
  if (!marketplaceMenu || marketplaceMenu.length === 0) return "";
  const menuItems = marketplaceMenu.map((item) => {
    const prefix = item.icon ? `${item.icon} ` : "";
    const parts = [
      `- ${prefix}**${item.name}** (id: \`${item.id}\`)`
    ];
    if (item.description) parts.push(`  ${item.description}`);
    return parts.join("\n");
  }).join("\n");
  return `## Marketplace Plugins (available on request)
The following published plugins are available for reference. If any would be useful as examples for the user's request, use the \`request_examples\` tool to fetch their full JSON configurations before generating. Pick 1-3 of the most relevant ones.

Before generating a new plugin, check these marketplace plugins. If one closely matches the user's request, call \`suggest_existing_plugin\` to recommend it. Only suggest if it's a strong match \u2014 same purpose and similar parameters. If no good match, proceed directly with generation.

${menuItems}
`;
};
var buildPluginSystemPrompt = (template, formulaReference, context, imagesSection, webSearchSection, videosSection) => {
  return template.replace("{{FORMULA_REFERENCE}}", stripDocsOnlySections(formulaReference)).replace(
    "{{MARKETPLACE_MENU_SECTION}}",
    buildMarketplaceMenuSection(context)
  ).replace("{{IMAGES_SECTION}}", imagesSection != null ? imagesSection : "").replace("{{WEB_SEARCH_SECTION}}", webSearchSection != null ? webSearchSection : "").replace("{{VIDEOS_SECTION}}", videosSection != null ? videosSection : "");
};
var turnBudgetInstruction = (turnsRemaining) => {
  if (turnsRemaining <= 0) {
    return "This is the final turn: the user cannot reply again. Bring the conversation to a natural close \u2014 round off warmly and do not ask another question.";
  }
  if (turnsRemaining === 1) {
    return "The user has one reply left in this conversation. Make this turn count, and be ready to close after their next message.";
  }
  return `The user has ${turnsRemaining} replies left in this conversation. When one remains, start bringing it to a natural close.`;
};
var buildChatSystemPrompt = (template, context) => template.replace(
  "{{TURN_BUDGET}}",
  turnBudgetInstruction(context.turnsRemaining)
);
var quantityToInstruction = (quantity) => {
  if (typeof quantity === "number") {
    return quantity === 1 ? "Generate exactly one value of the target type." : `Generate exactly ${quantity} objects.`;
  }
  if (quantity === "some") return "Generate a handful of objects (3-7).";
  return "Generate many objects (10-20).";
};
var outputFormatInstruction = (quantity) => quantity === 1 ? "Return exactly ONE value of the target type, shaped exactly as the type says: an object type is a single JSON object, a list type (`T[]`) is a single JSON array. Do not wrap it in an extra array." : "Return a JSON array of objects. Each must conform to the type above.";
var buildObjectsSystemPrompt = (template, config, imagesSection, webSearchSection, videosSection) => {
  return template.replace("{{TYPE_DESCRIPTION}}", config.typeDescription).replace("{{QUANTITY_INSTRUCTION}}", quantityToInstruction(config.quantity)).replace("{{OUTPUT_FORMAT}}", outputFormatInstruction(config.quantity)).replace(
    "{{ADDITIONAL_CONTEXT}}",
    config.additionalContext ? `## Additional Context

${config.additionalContext}` : ""
  ).replace("{{IMAGES_SECTION}}", imagesSection != null ? imagesSection : "").replace("{{WEB_SEARCH_SECTION}}", webSearchSection != null ? webSearchSection : "").replace("{{VIDEOS_SECTION}}", videosSection != null ? videosSection : "");
};

// src/prompts/extractJsonResponse.ts
var extractJsonResponseSimple = (s) => {
  const firstBrace = s.indexOf("{");
  const lastBrace = s.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace === -1 || firstBrace > lastBrace) {
    return "";
  }
  return s.slice(firstBrace, lastBrace + 1);
};

// src/prompts/generated/nodeSchemaIndex.ts
var NODE_SCHEMA_INDEX = {
  "aiChat": {
    type: "aiChat",
    schema: '{\n  type: "aiChat";\n  /**\n   * The brief the assistant opens from: who it is, who the user is, what to do. It always speaks first.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Written as a template literal so it can interpolate earlier steps\' values (`${lang}`, `${topic}`) and run to many lines. The user never sees this text \u2014 only the assistant\'s opening turn.\n   * Examples: @`You are a warm ${lang} tutor. The learner wants to practise: ${topic}. Keep turns to 2\u20133 sentences and end each with a question.`\n   */\n  promptTemplate?: string;\n  /**\n   * Object type the distilled result is shaped like. Indicative only \u2014 it seeds a default `typeDescription`.\n   * Naming a type here saves writing `typeDescription` by hand. It does not itself constrain the AI: `typeDescription` is what the extraction pass is actually told.\n   */\n  resultObjectType?: string;\n  /**\n   * Shape of the end-of-conversation summary, as a TypeScript-ish type string. Leave unset for a plain `{ text, turnCount }` result.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Setting it costs one extra non-streaming AI call when the user taps Done. Inline `//` comments after each field steer the extraction.\n   * Examples: @`{ summary: string; // 2 sentences\\n  vocabulary: string[]; corrections: { said: string; better: string }[] }`\n   */\n  typeDescription?: string;\n  /**\n   * How many user turns before the conversation ends itself. Defaults to 20.\n   * Bounds cost, stored transcript size and the model\'s context in one number. Hitting it behaves exactly like tapping Done.\n   */\n  maxUserTurns?: number;\n  /**\n   * Canned steers shown as tappable chips under the reply box.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * A JSON array of strings inside a backtick template \u2014 the step parses it, the same idiom as the AI-query step\'s mock results. Tapping a chip sends its text as the user\'s turn; mid-stream it interrupts and re-steers the assistant.\n   * Examples: @`["Slower please", "Explain that in English", "Give me a harder one"]`\n   */\n  steeringChips?: string;\n  /**\n   * `true`: Shows a mic button; recordings are transcribed and sent as the user\'s turn\n   * `false`: Typed replies only (default)\n   * Recorded audio is uploaded through the org\'s public Storage path before transcription, so anyone with the URL can fetch the clip. The transcript, not the audio, is what enters the conversation.\n   */\n  voiceInput?: boolean;\n  /**\n   * `true`: Reads each assistant sentence aloud as it arrives\n   * `false`: Silent (default)\n   * Uses the device\'s own Web Speech voices, so quality varies by phone. Pair with `speechLang` for anything other than the device default language.\n   */\n  speakReplies?: boolean;\n  /**\n   * Language of the spoken voice \u2014 a BCP-47 code or an English language name. Only read when `speakReplies` is on.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: @lang, fr-FR, Mandarin Chinese\n   */\n  speechLang?: string;\n  /**\n   * Placeholder shown in the empty reply box.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: @`Say what comes to mind\u2026`\n   */\n  inputPlaceholder?: string;\n  /**\n   * `true`: Streams `mockReplies` locally instead of calling the AI\n   * `false`: Calls the AI for real\n   * The switch that makes a chat flow testable without spending credits \u2014 the full stop/steer state machine still runs.\n   */\n  mockEnabled?: boolean;\n  /**\n   * Assistant replies to stream while mocking, one per turn.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * A JSON array of strings inside a backtick template, parsed by the step. When the array runs out the mock repeats its last reply.\n   * Examples: @`["Bonjour ! Comment \xE7a va ?", "Tr\xE8s bien. Et maintenant\u2026"]`\n   */\n  mockReplies?: string;\n}',
    examples: [],
    pitfalls: ["An aiChat step is a multi-turn CONVERSATION, not a one-shot query: the assistant speaks first, the user replies (typed, by voice, or by tapping a steering chip), and the two alternate until the user taps Done or `maxUserTurns` is reached. Use it when the interaction is a dialogue (a tutor, an interviewer, a coach, a guided reflection); use `aiQueryStep` when one prompt should produce one structured answer.", "The ONLY valid parent is a `flow`. An aiChat step placed directly on a `screen` or inside a `subsection` does nothing \u2014 put it in a `flow` alongside the `screen`s that collect its inputs and the `script` step that saves its result.", "The assistant opens FIRST, from `promptTemplate`. The user NEVER sees the brief \u2014 only the assistant's opening turn \u2014 so write it as instructions TO the assistant: who it is, who the user is, what to do, how long a turn should be, and how each turn should end. A brief written as a message to the user (\"Welcome! Let's practise French\") reads to the model as a line it must respond to and produces a confused opener.", '`promptTemplate` is a backtick template \u2014 `"@\\`\u2026\\`"` \u2014 so values from earlier steps in the same flow interpolate: `"promptTemplate": "@\\`You are a warm ${lang} tutor. The learner wants to practise: ${topic}. Speak mostly in ${lang}, keep turns to 2\u20133 sentences, and end each turn with a question.\\`"`. A plain string with no `@` is used verbatim and cannot interpolate. An absent or empty `promptTemplate` is a gate warning (`aiChat-missing-prompt`): the step still runs, but on a model improvising its own persona.', '`steeringChips` is a JSON array of strings INSIDE a backtick template, parsed by the step \u2014 `"steeringChips": "@\\`[\\"Slower please\\", \\"Explain that in English\\", \\"Give me a harder one\\"]\\`"`. Do NOT write a bare array formula (`"@[\\"Slower please\\", \u2026]"`) \u2014 the field is a string, so the array fails the decode and the chips silently vanish. This is the same idiom as `aiQueryStep.mockResults`.', "Tapping a chip sends its text as the user's turn; mid-stream it interrupts the assistant and re-steers it. Chips are the cheapest way to give a non-typing user (a language learner, a child, someone on a phone) a way to drive the conversation \u2014 offer three or four short ones, phrased as things the USER would say.", '`mockReplies` uses the same shape as `steeringChips` \u2014 a JSON array of strings inside a backtick template, one assistant reply per turn: `"mockEnabled": true, "mockReplies": "@\\`[\\"Bonjour ! Comment \xE7a va ?\\", \\"Tr\xE8s bien. Et maintenant\u2026\\"]\\`"`. When the array runs out the mock repeats its last reply. `mockEnabled: true` skips the AI entirely (no credits) but runs the full stop/steer/Done machinery, so a flow can be checked end to end before it costs anything. Leave both off for the real thing.', '`speakReplies: true` reads each assistant sentence aloud as it streams, using the device\'s own Web Speech voices. For ANY language other than the device default, also set `speechLang` \u2014 a BCP-47 tag (`"fr-FR"`, `"cmn-Hans-CN"`), an English language name (`"Mandarin Chinese"`), or a formula reading an earlier input (`"@lang"`). Without `speechLang` a French lesson is read in an English voice.', '`speechLang` is only read when `speakReplies` is on. Setting `speechLang` with `speakReplies` absent or `false` is a gate warning (`aiChat-speech-lang-unused`): the author plainly wanted spoken replies and gets silence. Either set `"speakReplies": true` or remove `speechLang`. A formula `speakReplies` (`"@wantsVoice"`) is accepted as is.', "`voiceInput: true` adds a mic button to the reply row: the recording is transcribed and sent as the user's turn. The transcript, not the audio, enters the conversation. Pair it with `speakReplies` for a hands-free spoken lesson; it works fine alone for a user who would rather talk than type.", 'The step stays `running` until the user taps Done (or the turn cap is hit). Do NOT set `autoAdvance: false` expecting a pause \u2014 completion already waits on the user, and the flag only controls whether the flow moves to the NEXT step once this one completes. Leave `autoAdvance` alone on an aiChat step; put `"autoAdvance": false` plus an `actionLabel` on the FOLLOWING `script` step if the user should confirm before the save.', "`maxUserTurns` caps how many user turns the conversation allows \u2014 1 to 100, default 20 \u2014 and is the one number that bounds the step's cost, stored transcript size and the model's context. Hitting the cap behaves exactly like tapping Done. Pick it from the register: a quick check-in is 4\u20136, a lesson 10\u201315; a literal outside 1..100 is a gate warning (`aiChat-turns-range`). A formula (`\"@turns\"`) is allowed and not checked.", '`.result` on Done is `{ text, turnCount }` \u2014 `text` is the full transcript as one string, `turnCount` the number of user turns. Set `typeDescription` to have a final extraction pass distil the finished conversation into that shape instead: `"typeDescription": "@\\`{ summary: string; // 2 sentences, in English\\n  vocabulary: string[]; // new words the learner met\\n  corrections: { said: string; better: string }[] }\\`"`. Inline `//` comments after each field steer the extraction; it costs one extra non-streaming AI call.', '`resultObjectType` names a MetaModel object type and only SEEDS a default `typeDescription` \u2014 it does not constrain the AI by itself. Name the type the result will be saved as (`"resultObjectType": "Session"`) and write `typeDescription` when the shape matters.', 'Save the result from a following `script` step, spreading the distilled object into a record: `{ "type": "script", "name": "save", "autoAdvance": false, "actionLabel": "Save this session", "script": "publishData({type: \\"Session\\", data: {...tutor.result, language: lang, at: $now}})" }`. The step is referenced by NAME (`tutor.result`), so give the aiChat step a short, meaningful `name`.', 'The live conversation is formula-visible while it runs: `<stepName>.chat.turns` is the transcript so far (an array of `{ role, text }`), and `<stepName>.chat.partialText` is the assistant\'s in-flight streaming text. A sibling `content` node in the same flow can render either \u2014 a running word count, a "turn 3 of 12" caption \u2014 but the step renders its own transcript, so do not rebuild the chat UI by hand.', "`model` is admin-only and billing-coupled \u2014 do NOT emit it. The step defaults to the platform's text model; leave the field out of every generated node.", '`inputPlaceholder` is the hint in the empty reply box, a backtick template like the other text fields: `"inputPlaceholder": "@\\`Say what comes to mind\u2026\\`"`. Use it to tell the user what KIND of reply is wanted ("Answer in French, however roughly").', 'A complete, minimal node: `{ "type": "aiChat", "name": "interview", "label": "Reflect", "promptTemplate": "@\\`You are a thoughtful journaling companion. Ask one short question at a time about the user\'s day \u2014 feelings, one highlight, one difficulty. After 3\u20134 exchanges, tell them you have enough for their entry.\\`", "typeDescription": "@\\`{ title: string; entry: string; // first person, warm, ~120 words\\n  mood: string }\\`", "maxUserTurns": 6, "inputPlaceholder": "@\\`Say what comes to mind\u2026\\`" }`. Everything else (`steeringChips`, `voiceInput`, `speakReplies`, `speechLang`, `mockEnabled`) is opt-in.']
  },
  "aiQuery": {
    type: "aiQuery",
    schema: "{\n  type: \"aiQuery\";\n  /**\n   * The prompt sent to the AI. It can interpolate earlier steps' values.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Reference a sibling step's input or result by name (`${qGoal}`, `story.result.title`). Written as a template literal, so it can run to many lines \u2014 the showcase orgs' prompts are full briefs, not one-liners.\n   * Examples: @`You are an inventive home chef. Based on the user's inputs, design ONE complete recipe.`\n   */\n  promptTemplate?: string;\n  /**\n   * The image(s) to analyse. Point it at a sibling image fileInput.\n   * Accepts a fileInput value (`@photo`), its url (`@photo.url`), a multi-file fileInput (`@photos`, an array), or an imageGenerationStep result (`@generated.result.url`). Non-image items in a multi-file value are ignored. Up to 8 images per call. Images are sent to Anthropic and are readable by anyone with the Storage URL \u2014 do not point this at private documents you would not publish. For text-heavy images like receipts, set the source fileInput's `imageOutputOptions.width` to 1568; the 800px default loses fine print.\n   * Examples: @photo, @photos, @receiptPhoto.url\n   */\n  imageSource?: string;\n  /**\n   * Object type the result is shaped like. Indicative only \u2014 it seeds a default `typeDescription`.\n   * Naming a type here saves writing `typeDescription` by hand. It does not itself constrain the AI: `typeDescription` is what the model is actually told.\n   */\n  resultObjectType?: string;\n  /**\n   * The shape the AI must return, as a TypeScript-ish type string.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * This is what actually constrains the output, so it is worth being fussy: inline `//` comments after each field are read by the model and are the cheapest way to steer a value.\n   * Examples: @`{ name: string; // catchy recipe name\\n  prepTime: number; // minutes }`\n   */\n  typeDescription?: string;\n  /**\n   * `true`: Returns `mockResults` instead of calling the AI\n   * `false`: Calls the AI for real\n   * The switch that makes a flow testable without spending credits or waiting on a model.\n   */\n  mockEnabled?: boolean;\n  /**\n   * How many mock objects to generate. Defaults to 1.\n   * Useful for checking a repeating result renders sensibly with more than one row.\n   */\n  mockResultCount?: number;\n  /**\n   * The mock payload to return, as a JSON string.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Leave it empty to have mock objects generated from `typeDescription` instead \u2014 hand-write it only when a specific value matters to what you're checking.\n   */\n  mockResults?: string;\n}",
    examples: [],
    pitfalls: ["An aiQuery step is a ONE-SHOT structured query: one prompt in, one JSON object out, no conversation. Use it to turn inputs (typed answers, a photo) into a record \u2014 a recipe, a receipt, a plant profile. Use `aiChatStep` when the user should talk back and forth with the AI instead.", 'The config type is `"type": "aiQuery"` \u2014 `{ "type": "aiQuery", "name": "extract", \u2026 }`. Do NOT write `"type": "aiQueryStep"`: that is the schema/builder name, not a config type, and it fails the closing gate with a misleading "Plugin `aiQueryStep` not declared in plugins". The long form `{ "type": "flowStep", "variant": "aiQuery" }` also validates but is not how configs are authored \u2014 always write the short form.', "The ONLY valid parent is a `flow`. Put it in a `flow`'s `_children` between the `screen` that collects its inputs and the `screen` that shows its result: `screen (inputs) \u2192 aiQuery \u2192 screen (result) \u2192 script (save)`. Placed directly on a `screen` or in a `subsection` it never runs.", '`promptTemplate` is a backtick template \u2014 `"@`\u2026`"` \u2014 so earlier inputs in the same flow interpolate by bare name: `"promptTemplate": "@`You are an inventive home chef. Design ONE recipe using: ${ingredients}. The cook has ${minutes} minutes.`"`. A plain string with no `@` is sent verbatim and cannot interpolate. Write it as a full brief to the AI (role, task, constraints), not a one-liner.', '`typeDescription` is what actually constrains the output \u2014 a TypeScript-ish shape inside a backtick template, with an inline `//` comment after each field to steer its value: `"typeDescription": "@`{\\n  name: string; // catchy recipe name\\n  minutes: number; // total time\\n  steps: string[]; // one instruction each\\n}`"`. Always write it; without it the AI invents its own shape and the result screen\'s references resolve to nothing.', 'The result (`<step>.result`) is ONE value shaped exactly as `typeDescription`: an object type gives an object, a list type gives the whole list \u2014 `"typeDescription": "@`{\\n  title: string;\\n  artist: string;\\n}[] // 10 songs`"` makes `<step>.result` a 10-item array, no wrapper object needed. An object type may still carry lists in its fields (`{ songs: {...}[]; title: string }`).', '`resultObjectType` names an object type and only SEEDS a default `typeDescription` \u2014 it does not constrain the AI by itself. Set it to the type the result will be saved as (`"resultObjectType": "Recipe"`) and still write `typeDescription` when the shape matters.', "The parsed object is read by the step's NAME plus `.result`: `extract.result.merchant`, `${extract.result.total}` inside a later template. Give the step a short, meaningful `name` \u2014 it is an identifier every later node uses.", 'It inherits these `flowStep` fields, which its own schema does NOT list: `autoAdvance` (default `true` \u2014 move to the next step as soon as the query returns), `actionLabel` (the run button\'s text), `idleTitle` / `idleDescription` (shown before it runs \u2014 worth setting, because the step costs credits), `runningTitle` / `runningDescription` (shown while it runs). All are backtick-template strings except `autoAdvance`: `"idleTitle": "@`Read the receipt`", "idleDescription": "@`Sends your photo to the AI to pull out the details.`", "actionLabel": "Read it"`.', 'VISION: set `imageSource` and the step sends image(s) along with the prompt. It is a formula naming an image `fileInput` in the same flow by bare name \u2014 `"imageSource": "@photo"`. Also accepted: `"@photo.url"`, a `multiple: true` fileInput\'s array (`"@photos"`, up to 8 images; non-image files are skipped), or an earlier imageGenerationStep\'s picture \u2014 a flow step\'s value needs `.result`, so `"@generated.result.url"`, never `"@generated"`.', "VISION: the name in `imageSource` must be a node that exists, or the gate reports `vision-source-unresolved` \u2014 the step would run with no image and the AI would answer from the prompt alone. Match it character for character to the fileInput's `name`.", 'VISION: the image does not replace the output contract \u2014 a vision query still needs `promptTemplate` (what to look for) AND `typeDescription` (what to return). `"promptTemplate": "@`Read this receipt. Report amounts exactly as printed; if a value is illegible, use null rather than guessing.`"`.', 'VISION: pair it with a `fileInput` whose `"variant": "image"`, in a `screen` BEFORE the aiQuery step. The user picks or shoots a photo and taps Commit to upload it; until then `@photo` is empty and the step shows "Add a photo first" instead of running. Leave `showCommit` at its default.', 'VISION: uploads are re-encoded to an 800px-wide JPEG unless the fileInput says otherwise, which loses fine print. For anything with TEXT in it (a receipt, a label, a menu, a whiteboard) set `"imageOutputOptions": { "width": 1568, "format": "jpeg" }` on the fileInput \u2014 the gate warns `vision-low-fidelity-source` otherwise. For identifying a whole subject (a plant, a dish, a dog) the default is fine and the warning can be left.', "`model` is admin-only and billing-coupled \u2014 do NOT emit it. Every query runs on the platform's default text model, which reads images; leave the field out of every generated node.", '`mockEnabled: true` skips the AI (no credits) and returns `mockResults`, a JSON string inside a backtick template: `"mockEnabled": true, "mockResults": "@`[{\\"merchant\\": \\"Corner Caf\xE9\\", \\"total\\": 12.5}]`"`. Leave `mockResults` out to have mocks generated from `typeDescription`. Leave both off for the real thing.', 'Save the result from a following `script` step, spreading the object into a record: `{ "type": "script", "name": "save", "autoAdvance": false, "actionLabel": "Save expense", "script": "publishData({type: \\"Expense\\", data: {...extract.result, at: $now}}); navigate(\\"expenses\\")" }`. Show the result on a `screen` between the two so the user can check it before saving.', 'A complete vision flow: `{ "type": "flow", "name": "scanFlow", "label": "Scan a receipt", "_children": [ { "type": "screen", "name": "shoot", "label": "Photo", "_children": [ { "type": "fileInput", "name": "receiptPhoto", "label": "Receipt", "variant": "image", "imageOutputOptions": { "width": 1568, "format": "jpeg" } } ] }, { "type": "aiQuery", "name": "extract", "label": "Read receipt", "imageSource": "@receiptPhoto", "promptTemplate": "@`Read this receipt. Report amounts exactly as printed; use null for anything illegible.`", "typeDescription": "@`{\\n  merchant: string;\\n  date: string; // YYYY-MM-DD\\n  total: number; // grand total incl. tax\\n  items: { name: string; price: number }[];\\n}`", "resultObjectType": "Expense", "idleTitle": "@`Read the receipt`", "actionLabel": "Read it" }, { "type": "screen", "name": "check", "label": "Check", "_children": [ { "type": "content", "name": "extracted", "htmlTemplate": "@`<h3>${extract.result.merchant}</h3><p>${extract.result.date} \u2014 ${extract.result.total}</p>`" } ] }, { "type": "script", "name": "save", "autoAdvance": false, "actionLabel": "Save expense", "script": "publishData({type: \\"Expense\\", data: {...extract.result, at: $now}}); navigate(\\"expenses\\")" } ] }`.', "While it runs, `<step>.partial` / `<step>.ready` hold the answer so far and child nodes render in place of the spinner. Recipes: `og reference ai-progress`."]
  },
  "app": {
    type: "app",
    schema: '{\n  type: "app";\n  /**\n   * `public`: Anyone can open the site without signing in\n   * `private`: Visitors must sign in before any screen renders\n   */\n  authMode?: "public" | "private";\n  /**\n   * `light`: Light header and tab strip \u2014 the default\n   * `dark`: Dark header and tab strip with a neon accent\n   * Presets the site chrome only (header bar + tab strip), not the page body. Override individual colors with `themeColors`.\n   */\n  theme?: "light" | "dark";\n  /**\n   * Per-color overrides on top of the theme preset. Anything omitted falls back to the preset.\n   * Covers the chrome: `bg`, `surface`, `accent`, `text`, `mutedText` and `border`. Set `accent` alone to brand the org without redesigning it.\n   */\n  themeColors?: ThemeColors;\n  /**\n   * CSS that every screen shares \u2014 one copy here instead of a <style> block copied into each screen.\n   * Injected once at the site root, so it survives navigation (a screen-scoped <style> unmounts when you leave the screen). Scope selectors with a class prefix the org owns, e.g. `.pd-card`, because this styles the whole document. Keep screen-specific rules in that screen\'s own <style> node \u2014 those still win on document order.\n   * Examples: .card { border-radius: 12px; padding: 16px }\n   */\n  customCss?: string;\n  /**\n   * Widest every screen\'s content may get, in px. Centred on a wide window.\n   * Set it for a form or content org (640\u2013760 suits most) so inputs and widgets share one column with the HTML. Leave it unset for a dashboard or grid that wants the whole window, or give one screen `containerStyle: { maxWidth: 0 }` to opt that screen out.\n   * Examples: 720\n   */\n  contentMaxWidth?: number;\n  /**\n   * An image URL or @-formula for the header picture, instead of the org\'s logo.\n   * Use it when the page is about someone or something with its own picture, as the user home does with @$data.UserProfile[0].avatarUrl. The header shows it as a circle, where an org\'s logo keeps its own shape. The formula is evaluated against the org\'s own data, so it can read $data. While it is empty (data still loading) and when the image fails to load, the header shows the org\'s logo as before. It changes the header only: the tab icon, home-screen icon and link preview still use the org\'s logo.\n   * Examples: @$data.UserProfile[0].avatarUrl\n   */\n  headerLogo?: string;\n}\n\n// ThemeColors\n{\n  /**\n   * Page and header background colour.\n   * Examples: #081120, #0a0a0a\n   */\n  bg?: string;\n  /**\n   * Tab strip and elevated surface colour.\n   * Examples: #0e1a30\n   */\n  surface?: string;\n  /**\n   * Selected tab indicator and icon highlight colour.\n   * Examples: #00e676, #0f766e\n   */\n  accent?: string;\n  /**\n   * Primary text colour on the header and tabs.\n   * Examples: #f4f7fb\n   */\n  text?: string;\n  /**\n   * Secondary text colour \u2014 bylines, status lines.\n   * Examples: #93a3b8\n   */\n  mutedText?: string;\n  /**\n   * Bottom border colour of the header and tab bar.\n   * Examples: rgba(255, 255, 255, 0.12)\n   */\n  border?: string;\n}',
    examples: [],
    pitfalls: ["The app's `appLogo` field is admin-managed (uploaded via the design-tab widget). Do not generate it \u2014 it'll be stripped on publish.", '`authMode` accepts only `"public"` or `"private"`. Legacy values (`anonOnly`, `hybrid`, `userOnly`) are mapped at load time but should not be generated.', '`theme` and `themeColors` configure site chrome. The `palette.mode` you pick (`"light"` or `"dark"`) flips MUI dark variants for portal-rendered components \u2014 pair it with `themeColors` overrides if you want a non-default look.', "Shared CSS goes in the app node's `customCss` \u2014 one copy, injected on every screen. Never paste the same `<style>` block into each screen's content node: a screen-scoped `<style>` unmounts on navigation, which is exactly why `customCss` exists. Keep genuinely screen-specific rules in that screen's own `<style>`; they win on document order.", 'Set `contentMaxWidth` (px) on the app node for a form or content org, e.g. `"contentMaxWidth": 720`. Every screen\'s content then sits in one centred column on a wide window, so built-in inputs and widgets line up with the HTML instead of stretching edge to edge. Your HTML then needs no `max-width` / `margin: 0 auto` of its own. Leave it unset for a dashboard, calendar or multi-column grid that wants the full window, or give that one screen `"containerStyle": { "maxWidth": 0 }` to opt it out.']
  },
  "appNode": {
    type: "appNode",
    schema: '{\n  type: "appNode";\n}',
    examples: [],
    pitfalls: []
  },
  "audioGeneration": {
    type: "audioGeneration",
    schema: "{\n  type: \"audioGeneration\";\n  /**\n   * What to generate.\n   * Formula-capable: plain text, or prefix with `@` to compute it.\n   * For `musicgen` this is the whole prompt. For `minimax-music-2.5` it is the style or scene only \u2014 anything meant to be sung goes in `lyrics`.\n   * Examples: @'Upbeat jingle for: ' + intro.result.title\n   */\n  prompt?: string;\n  /**\n   * Words to be sung. Used by `minimax-music-2.5` and ignored by `musicgen`.\n   * Formula-capable: plain text, or prefix with `@` to compute it.\n   * Takes `[Verse]` / `[Chorus]` / `[Bridge]` structure tags with newline-separated lines, and the lyrics' length is what drives the clip's length on this model.\n   * Examples: @lyricsInput, @'[Verse]\\nPop or plop\\n[Chorus]\\nPlop!'\n   */\n  lyrics?: string;\n  /**\n   * `musicgen`: Instrumental only; honours `durationSec`\n   * `minimax-music-2.5`: Sings `lyrics`; length follows the words, and `durationSec` is ignored\n   * The two models take their input differently, so switching between them usually means moving text between `prompt` and `lyrics`. Accepts a literal id or an `@`formula.\n   */\n  model?: \"musicgen\" | \"minimax-music-2.5\" | string;\n  /**\n   * Clip length in seconds. Formula-capable.\n   * Only `musicgen` honours it, clamped to 1\u201330 and defaulting to 8. `minimax-music-2.5` derives length from the lyrics and ignores this field.\n   */\n  durationSec?: number | string;\n  /**\n   * `true`: Returns `mockAudioUrl` instead of generating\n   * `false`: Generates for real\n   */\n  mockEnabled?: boolean;\n  /**\n   * Audio URL to return while mocking. Leave empty for the default stub clip.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   */\n  mockAudioUrl?: string;\n}",
    examples: [],
    pitfalls: []
  },
  "booleanInput": {
    type: "booleanInput",
    schema: '{\n  type: "booleanInput";\n  /**\n   * `checkbox`: A checkbox\n   * `toggle`: A switch\n   */\n  variant?: "checkbox" | "toggle";\n}',
    examples: [],
    pitfalls: []
  },
  "callableButton": {
    type: "callableButton",
    schema: '{\n  type: "callableButton";\n  /**\n   * `setUserProfile`: Writes the signed-in user\'s profile\n   * `triggerWorldcupSync`: Kicks off the World Cup results sync\n   * `socialFollow`: Follows a person, or changes the tier of an existing follow. Args: targetUid and an optional tier of follow or close\n   * `socialUnfollow`: Stops following a person. Args: targetUid\n   * `socialLike`: Likes an app. Args: orgId, the username/orgName pair\n   * `socialUnlike`: Removes the caller\'s like from an app. Args: orgId\n   * `kickFromHome`: Platform admins only: takes an app off the orgiggly.com home feeds, or puts it back. Args: orgId and an optional undo\n   * A server-vetted allow-list, not a free-text name: adding a callable is a code change in both the web dispatcher and the admin picker.\n   */\n  functionName: "setUserProfile" | "triggerWorldcupSync" | "socialFollow" | "socialUnfollow" | "socialLike" | "socialUnlike" | "kickFromHome";\n  /**\n   * Formula producing the arguments object passed to the callable.\n   * Examples: @({ bio, avatarUrl: avatar.url })\n   */\n  args?: string;\n  /**\n   * Button label.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: Save changes\n   */\n  actionLabel?: string;\n  /** Comma-separated callable names whose cached data is re-fetched after this button succeeds \u2014 e.g. listUserHome. */\n  refreshCallables?: string[];\n  /**\n   * Formula resolving to a screen pointer key. When set, the user is taken there on success.\n   * Leave it unset to keep the user where they are \u2014 pair that with `successMessage` so something still acknowledges the click.\n   */\n  onSuccessNavigate?: string;\n  /**\n   * Snackbar message shown on success.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Worth setting whenever `onSuccessNavigate` is not, or a successful call looks like nothing happening.\n   */\n  successMessage?: string;\n  /**\n   * `contained`: A filled button across the full width, for a page\'s main action\n   * `outlined`: An outlined button sized to its label, for secondary actions like Follow\n   * `text`: A borderless button sized to its label, for small inline actions like Like\n   * `menu`: A small \u22EE icon that opens a menu holding the action, for an action on every card that shouldn\'t take a row of its own \u2014 pin it to a corner with containerStyle.overlay\n   * `pill`: A compact rounded button with its own light backing, readable over a picture \u2014 pin it to a card corner with containerStyle.overlay, as for a like\n   */\n  variant?: "contained" | "outlined" | "text" | "menu" | "pill";\n  /**\n   * `small`: A compact button, for actions repeated on every card\n   * `medium`: The standard size\n   */\n  size?: "small" | "medium";\n  /**\n   * Icon shown before the label, by Material Symbols name.\n   * Examples: favorite, person_add, check\n   */\n  icon?: string;\n}',
    examples: [],
    pitfalls: []
  },
  "const": {
    type: "const",
    schema: `{
  type: "const";
  /**
   * The constant's value, as plain JSON. It is stored as written \u2014 never evaluated as a formula and never overridden by user input.
   * Readable by its name from any formula (write the bare name or @name) and from a transition rule's condition/next/dueAt, so a tunable lives in one place instead of a zero-arg function repeated per org. The value is literal JSON: a string stays a string even if it starts with @.
   * Examples: 6, 30000, ["s","h","d","c"], { "mostCards": 2, "sweep": 1 }
   */
  value: unknown;
}`,
    examples: [],
    pitfalls: []
  },
  "content": {
    type: "content",
    schema: "{\n  type: \"content\";\n  /**\n   * The node's HTML, as a template literal. Interpolate data with `${\u2026}`.\n   * Evaluated by the formula engine, not JavaScript \u2014 so there is no `JSON.stringify`, no `typeof`, no optional chaining and no `new Date(...)`. Use `toJson(value)`, `length(arr)`, `formatDate(d, fmt)` and `if(...)` instead; an unknown call silently yields nothing rather than erroring. Clicks are wired through `actions`: a `<button data-action=\"name\">` invokes a declared action, and an action whose script calls `setValue(\u2026)` writes back to the node's own value, which is how a content node holds state.\n   */\n  htmlTemplate?: string;\n  /**\n   * One sentence saying what this block shows or does. Never rendered to visitors.\n   * Read by people and by the org-gen agent rather than by the renderer: the org outline (`docs/OUTLINE.md`) shows it at L1 in place of the HTML, and staged generation emits it before any HTML exists (#2067). Describe intent, not markup \u2014 'lists this week's events with a link to each' rather than 'a <ul> of events'.\n   * Examples: Hero banner with the club's tagline and a join button, Shows the current player's hand as cards, Empty state shown when the member has no tabs yet\n   */\n  purpose?: string;\n  /**\n   * Named click behaviours the template invokes with data-action attributes \u2014 each one a script; to move screens it calls navigate('screen'), usually as the last line.\n   * Each key is an action name; the template invokes it with a data-action attribute on a button (preferred \u2014 a real button role) or anchor, binding param values through render-time interpolated data-param attributes. Click-time dispatch evaluates no formulas: the action runs `script` with $params in scope. To move screens, end the script with navigate('screen', {id: $params.id}), replaceScreen('screen') or back(); a failure earlier in the script aborts the navigation. The structured replacement for the legacy rel=, setValue=, data-script and data-args click attributes. A second dispatch channel exists for a sandboxed-widget node: the widget calls channel.runAction(name, params) and the platform runs the action of that name declared HERE, so the widget can never do more than this config allows. That channel passes only the params the action declares (a widget is deployed separately from the org config, so an undeclared param means the two have drifted) and its params are runtime JSON rather than interpolated string literals.\n   * Examples: { \"openTab\": { \"params\": [\"id\"], \"script\": \"setAppValue('currentTabId', $params.id); navigate('tabDetail', {id: $params.id})\" } }, { \"cancel\": { \"script\": \"back()\" } }\n   */\n  actions?: Record<string, ContentAction>;\n}\n\n// ContentAction\n{\n  /**\n   * Declared parameter names, bound at click time from the element's data-param attributes and exposed to `script`.\n   * They are deliberately not merged into the surrounding scope, so a param can never shadow a field of the record.\n   * Examples: [\"melds\"]\n   */\n  params?: string[];\n  /**\n   * Inline script run through the same host machinery as a script flow step, in the content node's scope, with the declared params injected.\n   * Navigate from it with the host navigate function rather than a link, so the action and the navigation stay one unit.\n   */\n  script?: string;\n  /**\n   * Ask before this action runs. The person sees a modal built from these strings, and `script` runs only if they confirm.\n   * The strings are static \u2014 an action cannot compute its own confirmation text from the record it is about to act on.\n   */\n  confirm?: ActionConfirm;\n}\n\n// ActionConfirm\n{\n  /** The question, as the modal's heading. Keep it to a few words. */\n  title: string;\n  /** One line under the title saying what confirming costs. Omit it when there is nothing to add \u2014 a line of empty reassurance is worse than none. */\n  body?: string;\n  /**\n   * Label for the confirming button; defaults to Confirm.\n   * Prefer the verb \u2014 Leave, Delete \u2014 so the button says what it does rather than restating the question.\n   */\n  ok?: string;\n  /** Label for the dismissing button; defaults to Cancel. */\n  cancel?: string;\n  /**\n   * `true`: Paints the confirming button in the error colour \u2014 for anything that discards data the person cannot get back.\n   * `false`: An ordinary confirmation. This is the default.\n   */\n  destructive?: boolean;\n  /**\n   * `true`: One OK button, no Cancel \u2014 for telling the person something, not asking. The script, if any, runs on OK.\n   * `false`: An ordinary two-button confirmation. This is the default.\n   */\n  info?: boolean;\n}",
    examples: [
      {
        "excerpt": '{\n  "id": "content-1",\n  "type": "content",\n  "name": "welcomeHeader",\n  "htmlTemplate": "@`<div style=\\"padding: 24px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 16px; color: white; margin-bottom: 16px;\\">\\n  <h1 style=\\"margin: 0 0 8px 0; font-family: system-ui; font-size: 28px;\\">My App</h1>\\n  <p style=\\"margin: 0; opacity: 0.9;\\">Welcome to your dashboard</p>\\n</div>`"\n}',
        "source": "dashboard"
      },
      {
        "excerpt": '{\n  "id": "content-2",\n  "type": "content",\n  "name": "addBtn",\n  "htmlTemplate": "@`<a href=\\"editContact\\" style=\\"display: inline-flex; align-items: center; padding: 12px 24px; background: #10b981; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; margin-bottom: 16px;\\">+ Add Contact</a>`"\n}',
        "source": "list-detail"
      },
      {
        "excerpt": '{\n  "id": "content-1",\n  "type": "content",\n  "name": "newRecipeLink",\n  "htmlTemplate": "@`<a href=\\"newRecipe\\" style=\\"display: inline-block; padding: 12px 24px; background: #16a34a; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; margin-bottom: 16px;\\">+ New recipe</a>`"\n}',
        "source": "save-record"
      }
    ],
    pitfalls: ["`htmlTemplate` strings start with `` @` `` (backtick after `@`). Template literals use `${expression}` for formula interpolation. Do not use plain backticks without `@`.", 'For cross-org links (`<a href="orgiggly.com/<slug>">` or `<a href="/<slug>">`), include `target="_blank"` or the SPA router intercepts the click.', 'Raw `<img src="...">` has no onError fallback. For dynamic image URLs, conditionally render or use a default URL when the source might 404.', "Inline `<style>` tags only work at screen level via a content node, not inside per-node htmlTemplates.", 'Clicks have exactly TWO channels: a real `href` for a genuine hyperlink (`http(s):`, `mailto:`, `tel:`, `/`-absolute), and `data-action="<name>"` for every behaviour. Do NOT emit `rel=`, `setValue=`, `param=`, `data-script`, `data-args`, `data-speak*`, `href="@pop"` or `href="@submit"` \u2014 those are frozen legacy channels that you will see in existing orgs but must not write in new ones.', 'The full shape of an `actions` entry: `"actions": { "openTask": { "params": ["id"], "script": "setAppValue(\'lastOpened\', $params.id); navigate(\'taskDetail\', {id: $params.id})" } }`. `params` declares the names bound from `data-param-*`; `script` runs with `$params` in scope. Navigation is a host call in the script \u2014 `navigate(\'screen\', args?)` pushes (adds a back step), `replaceScreen(\'screen\', args?)` swaps the current screen, `back()` goes back \u2014 normally as the LAST statement, so an earlier failure aborts it. A Back or Cancel button is `{ "script": "back()" }` and needs nothing else.', 'Bind params as render-time interpolations: `data-param-id="${id}"`, never `data-param-id="id"`. Click-time dispatch evaluates no formulas, so the attribute must already hold the literal value. HTML lowercases attribute names, so a param declared `taskId` must be bound as `data-param-taskid` \u2014 prefer all-lowercase param names to avoid the mismatch entirely.', "Put `data-action` on a `<button>`, not an `<a>`. Reset the button chrome in the style attribute (`border: none; font: inherit; cursor: pointer;` plus `text-align: left; width: 100%;` for a card) or it will not match the surrounding UI.", 'To hold state in a content node, give it a `compute` for the initial value and have an action\'s script call the `setValue(\u2026)` host function. Read the value back by the NODE\'S OWN NAME, in the template and the script alike \u2014 there is no `value` variable, so a bare `value` is undefined (`value.index` renders `NaN`). E.g. a node named `tagFilter`: `{ "name": "tagFilter", "compute": "@[]", "actions": { "toggle": { "params": ["tag"], "script": "setValue(tagFilter.includes($params.tag) ? tagFilter.filter((x) => x != $params.tag) : [...tagFilter, $params.tag])" } } }`. This is the filter-UI pattern; `setValue`, `speak`, `stopSpeech` and `submit` are host functions available only inside an action\'s script.', "A content node's stored value beats its `compute` for good. Once the visitor types into an `<input>` in its template, or a script calls `setValue`, the value is kept on their device and `compute` is never read again: across reloads and days, and even after what `compute` reads has changed. A daily app's answer box showed yesterday's answer every day this way. So clear the value once it has been used: end the save action's script with `clearValues('<node name>')` (e.g. `publishData({type: 'Answer', data: {text: answer}}); clearValues('answer')`), and `compute` applies again. No screen-enter hook resets it. To drop values already stored on devices after a fix, rename the node; nothing else reaches them.", "Identify records by id, not list position: bind `data-param-id=\"${id}\"`, pass it as `navigate('taskDetail', {id: $params.id})`, and resolve it on the destination with a variable whose compute is `@byId('Task', $args.id)`. A destination that reads the clicked row's scope only works from that one list; one that reads `$args.id` works from every entry point, including a deep link.", 'Inside a spatial container (a parent with `layout: { "mode": "spatial", "cols": N }`) a content node may carry `place` DIRECTLY \u2014 `{ "id": "content-3", "type": "content", "name": "stageLabel", "place": { "x": 2, "y": 1 }, "htmlTemplate": "@`<div>Stage</div>`" }` \u2014 to pin a static label, a legend, a header cell or a board square at a 0-based `(x, y)` cell, with optional `w`/`h` spans (`"w": 3` stretches it across three columns). Both `x` and `y` are required once `place` is present (gate error `place-incomplete`), each may be a number or an `@`-formula evaluated in this node\'s scope, and siblings WITHOUT `place` auto-flow into the next free cell in document order, so a fixed title cell and auto-flowed tiles mix freely in one grid. `place` on a content node whose parent is not spatial does nothing (gate warning `place-outside-spatial`) \u2014 the parent\'s `layout.mode` is what activates it.', "An action name used by `data-action` must exist in `actions`, or the closing gate fails the org. A declared action the template never invokes is a warning, as is a `data-param-*` binding whose name is not in the action's `params`."]
  },
  "fetchData": {
    type: "fetchData",
    schema: `{
  type: "fetchData";
  /**
   * URL to fetch. Formula-capable, so it can be built from earlier steps' values.
   * Examples: @'https://api.example.com/' + itemId
   */
  fetchUrl?: string;
}`,
    examples: [],
    pitfalls: []
  },
  "fileInput": {
    type: "fileInput",
    schema: '{\n  type: "fileInput";\n  /**\n   * `image`: Image upload with preview, cropping and optional AI generation\n   * `file`: Any file type \u2014 no preview or editing\n   * `video`: Video upload, including in-app recording\n   * `audio`: Audio upload, including in-app microphone recording\n   */\n  variant?: "image" | "file" | "video" | "audio" | string;\n  /** Icon shown in place of a preview before anything is uploaded. Defaults to a person icon. */\n  placeholderIcon?: string;\n  /**\n   * Resizes and re-encodes the image on upload. Unset means 800px-wide JPEG, not the original.\n   * Every image upload is re-encoded and has its EXIF stripped: with no options set, `uploadFileToStorage` applies `{width: 800, format: "jpeg"}` to keep page weight down. Only video and audio (the `raw` path) keep their original bytes. So `width`/`height`/`format` here *override* that default rather than switching processing on. Set `width` explicitly for text-heavy images \u2014 1568 is the useful value when the image is going to a vision AI step, because the 800px default loses fine print.\n   * Examples: { "width": 1568, "format": "jpeg" }\n   */\n  imageOutputOptions?: ImageOptions;\n  /**\n   * Prefix prepended to the user\'s prompt when generating an image with AI. Formula-capable.\n   * Use it to pin a house style so every generated image in the org looks related.\n   * Examples: @`An illustration for a short story: title: ${rightTitle} , ${rightText}`\n   */\n  aiSystemPromptFormula?: string;\n  /**\n   * Formula returning a Firebase uid. When set, the upload path becomes the per-user avatar path instead of the org-scoped one.\n   * The path becomes public/{uid}/{fileName}.{ext} \u2014 a mutable canonical location, so a new upload replaces the previous avatar rather than accumulating files.\n   */\n  userAvatarUid?: string;\n  /**\n   * `true`: Offers a \'Take photo\' button\n   * `false`: Hides it\n   * Defaults to true.\n   */\n  showCamera?: boolean;\n  /**\n   * `true`: Offers a \'Choose file\' button\n   * `false`: Hides it\n   * Defaults to true.\n   */\n  showFilePicker?: boolean;\n  /**\n   * `true`: Offers an image \'Search\' button\n   * `false`: Hides it\n   * Defaults to on for the `image` variant only.\n   */\n  showSearch?: boolean;\n  /**\n   * `true`: Offers the crop/edit accordion\n   * `false`: Hides it\n   * Defaults to true.\n   */\n  showEdit?: boolean;\n  /**\n   * `true`: Offers an explicit \'Commit\' button to start the upload\n   * `false`: Uploads without a confirmation step\n   * Defaults to true.\n   */\n  showCommit?: boolean;\n  /**\n   * `true`: Holds an array of files\n   * `false`: Holds a single file\n   * Defaults to false. When true the value is an array of { name, url, ext, takenAt? }.\n   */\n  multiple?: boolean;\n  /**\n   * Largest picked file accepted, in megabytes. Rejected client-side before the upload starts.\n   * Defaults to 10 for images, 30 for video, and 10 for audio. Video/audio recorded in-app is not checked against it \u2014 that is bounded by bitrate x duration instead.\n   */\n  maxSizeMb?: number | string;\n}\n\n// ImageOptions\n{\n  /**\n   * `jpeg`: Smallest files, no transparency. The only format any org currently asks for.\n   * `png`: Lossless with transparency \u2014 logos and flat graphics.\n   * `webp`: Smaller than PNG at similar quality, with transparency.\n   */\n  format?: "jpeg" | "png" | "webp";\n}\n\n// Dimension\n{\n  /** Width in pixels. */\n  width?: number;\n  /** Height in pixels. */\n  height?: number;\n}',
    examples: [
      {
        "excerpt": `{
  "id": "fileInput-1",
  "type": "fileInput",
  "name": "newPic",
  "label": "Photo (optional)",
  "variant": "image",
  "showCamera": true,
  "showFilePicker": true,
  "showCommit": false,
  "compute": "@({ name: 'recipe-' + generateId(6) })"
}`,
        "source": "save-record"
      }
    ],
    pitfalls: []
  },
  "flow": {
    type: "flow",
    schema: '{\n  type: "flow";\n  /**\n   * `true`: The step progress bar is hidden\n   * `false`: The step progress bar is shown (default)\n   */\n  hideTopBar?: boolean | string;\n  /**\n   * `true`: The Cancel/Back/Next/Finish bar is hidden\n   * `false`: The navigation bar is shown (default)\n   */\n  hideNav?: boolean | string;\n}',
    examples: [
      {
        "excerpt": '{\n  "id": "flow-1",\n  "type": "flow",\n  "name": "newRecipeFlow",\n  "hideNav": true,\n  "hideTopBar": true,\n  "_children": [\n    {\n      "id": "screen-3",\n      "type": "screen",\n      "name": "newRecipeStep",\n      "_children": [\n        {\n          "id": "textInput-1",\n          "type": "textInput",\n          "name": "newName",\n          "label": "Name",\n          "required": true\n        },\n        {\n          "id": "textInput-2",\n          "type": "textInput",\n          "name": "newMethod",\n          "label": "Method",\n          "variant": "multiline"\n        },\n        {\n          "id": "fileInput-1",\n          "type": "fileInput",\n          "name": "newPic",\n          "label": "Photo (optional)",\n          "variant": "image",\n          "showCamera": true,\n          "showFilePicker": true,\n          "showCommit": false,\n          "compute": "@({ name: \'recipe-\' + generateId(6) })"\n        },\n        {\n          "id": "content-3",\n          "type": "content",\n          "name": "newRecipeButtons",\n          "htmlTemplate": "@`<div style=\\"display: flex; gap: 12px; margin-top: 16px;\\">\\n  <button data-action=\\"create\\" style=\\"flex: 1; padding: 12px 24px; border: 0; border-radius: 8px; background: #16a34a; color: white; font: inherit; font-weight: 600; cursor: pointer;\\">Save</button>\\n  <button data-action=\\"cancel\\" style=\\"flex: 1; padding: 12px 24px; border: 0; border-radius: 8px; background: #e2e8f0; font: inherit; cursor: pointer;\\">Cancel</button>\\n</div>`",\n          "actions": {\n            "create": {\n              "script": "advanceFlow()"\n            },\n            "cancel": {\n              "script": "back()"\n            }\n          }\n        }\n      ]\n    },\n    {\n      "id": "script-1",\n      "type": "script",\n      "name": "createRecipe",\n      "label": "Save",\n      "idleTitle": "Saving your recipe",\n      "script": "publishData({type: \\"Recipe\\", data: {name: newName, method: newMethod || \\"\\", pic: newPic.url || \\"\\"}});\\nnavigate(\\"recipeBook\\")"\n    }\n  ]\n}',
        "source": "save-record"
      }
    ],
    pitfalls: ['A flow\'s `_children` are its steps, in order: `screen` nodes (a page of inputs), step nodes written by their own type (`{"type": "script"}`, `{"type": "aiQuery"}`, `{"type": "transcribe"}`, \u2026) and `repeat`. Never write `"type": "flowStep"` or a builder name like `scriptStep`: the step\'s type IS its kind, and `scriptStep` fails as an undeclared plugin.', 'Give a flow at least one `screen` step. A flow whose every step is a `script` gives the user nothing to fill in: they tap in, then tap its one button. Run that script from the button itself instead, as a declared content action (`<button data-action="start">` + `actions: {start: {script: "\u2026"}}`, with `confirm` for a destructive one). The gate warns `flow-script-only`.', "Next and Finish refuse to leave a flow screen while a visible `required` input is empty or a `validation` rule fails; the fields turn red. Mark what the step genuinely needs as `required` instead of checking it in the script. Hidden inputs never block.", 'A step whose `hidden` formula is true drops out of the flow entirely: it leaves the stepper and never runs. Use it to gate an action (`hidden: "@!roomExists"` on a `script` step means finishing writes nothing until the room exists) or to give one flow two paths (an AI step and a pick-from-list step, each hidden when the other applies).', 'A `script` step runs as soon as it is entered, and its value is readable later as `<stepName>.result` (the value of its last expression), so a script can load data a following screen shows: `{"type": "script", "name": "loadPlaylists", "script": "spotifyListPlaylists()"}`, then `"pickerValues": "@loadPlaylists.result"` on that screen\'s `selectInput`.', 'To pass a value INTO a flow, open it from a declared action whose script calls `navigate` with an args object (`<button data-action="add" data-param-day="${day}">` + `actions: {add: {script: "navigate(\'newEvent\', {Start: $params.day})"}}`), then read `$args` inside the flow: `"compute": "@($args.Start ? $args.Start : \'\')"` on an input, or `$args.Start` in a `script` step. Never use `param=`, which a flow cannot see.', '`autoAdvance` on a step is a plain `true`/`false`, never a formula: `"autoAdvance": "@true"` passes validation and then fails to publish. `hideTopBar` and `hideNav` on the flow DO accept a formula.', 'Put every flow inside a `screen` of its own, and `navigate()` to that screen, never to the flow: `{"type": "screen", "name": "postNotice", "_children": [{"type": "flow", "name": "postNoticeFlow", \u2026}]}`, then `navigate(\'postNotice\')`. A flow placed straight into a folder and navigated to renders its first screen as a bare page, and its `advanceFlow()` fails with "can only be called from a Content data-script inside a flow".', "With `hideNav: true` the flow has no Next button, so a step advances only when your own content calls `advanceFlow()` (a declared action's script). Pair it with `hideTopBar: true` for a one-screen form that should look like a plain screen.", "A flow's inputs reset every time the flow is navigated to; a plain screen's inputs keep whatever was typed last. So an edit form opened per record (`navigate('editRole', {id: $params.id})`) belongs in a one-step flow with `hideNav` and `hideTopBar`, or record B opens showing record A's typing.", 'To ask the same screens once per item ("Add another guest?"), use a `repeat` step; call `get_node_schema("repeat")` for its shape and rules rather than numbering screens.']
  },
  "flowStep": {
    type: "flowStep",
    schema: "{\n  type: \"flowStep\";\n  /**\n   * `true`: Moves to the next step as soon as this one finishes\n   * `false`: Waits for the user to advance\n   * Defaults to true. Turn it off when the user should read or check the result before moving on.\n   */\n  autoAdvance?: boolean;\n  /**\n   * Label on the step's primary button. Falls back to the variant's own default.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: Save my picks, Save changes\n   */\n  actionLabel?: string;\n  /**\n   * Heading shown before the step runs. Falls back to the variant's own default.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: Ready to save, @`Ready to create your story`\n   */\n  idleTitle?: string;\n  /**\n   * Body copy under the idle heading. Omitted entirely when unset.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * The place to say what the step is about to do \u2014 especially worth setting on a step that costs credits or writes data.\n   */\n  idleDescription?: string;\n  /**\n   * Heading shown while the step runs. Falls back to the variant's own default.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: Saving\u2026, Publishing your expense\u2026\n   */\n  runningTitle?: string;\n  /**\n   * Body copy under the running heading. Omitted entirely when unset.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Worth setting on a step that can take a while on a slow connection \u2014 tell the user what is being saved while they wait.\n   */\n  runningDescription?: string;\n  /**\n   * Heading shown to a visitor who has to sign in before this step can run. Falls back to the variant's own default.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * This is the gateway moment for a new visitor, so say what signing in gets them. The Sign In button's label is fixed.\n   * Examples: Your first story is on us, @`Sign in to create your story`\n   */\n  authTitle?: string;\n  /**\n   * Body copy under the sign-in heading. Falls back to the platform default, which mentions free credits for new users.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: New users get 200 free credits, enough for about 60 stories.\n   */\n  authDescription?: string;\n  /**\n   * Heading shown to a signed-in user with no credits left. Falls back to the variant's own default.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Examples: You're out of story credits\n   */\n  noCreditsTitle?: string;\n  /**\n   * Body copy under the no-credits heading. Falls back to the platform default.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * The Buy Credits button's label is fixed, so the purchase action always reads the same in every org.\n   * Examples: Top up to keep creating stories.\n   */\n  noCreditsDescription?: string;\n}",
    examples: [],
    pitfalls: []
  },
  "folder": {
    type: "folder",
    schema: '{\n  type: "folder";\n}',
    examples: [
      {
        "excerpt": `{
  "id": "folder-1",
  "type": "folder",
  "name": "components",
  "label": "Components",
  "_children": [
    {
      "id": "screen-2",
      "type": "screen",
      "label": "New Recipe",
      "name": "newRecipe",
      "_children": [
        {
          "id": "flow-1",
          "type": "flow",
          "name": "newRecipeFlow",
          "hideNav": true,
          "hideTopBar": true,
          "_children": [
            {
              "id": "screen-3",
              "type": "screen",
              "name": "newRecipeStep",
              "_children": [
                {
                  "id": "textInput-1",
                  "type": "textInput",
                  "name": "newName",
                  "label": "Name",
                  "required": true
                },
                {
                  "id": "textInput-2",
                  "type": "textInput",
                  "name": "newMethod",
                  "label": "Method",
                  "variant": "multiline"
                },
                {
                  "id": "fileInput-1",
                  "type": "fileInput",
                  "name": "newPic",
                  "label": "Photo (optional)",
                  "variant": "image",
                  "showCamera": true,
                  "showFilePicker": true,
                  "showCommit": false,
                  "compute": "@({ name: 'recipe-' + generateId(6) })"
                },
                {
                  "id": "content-3",
                  "type": "content",
                  "name": "newRecipeButtons",
                  "htmlTemplate": "@\`<div style=\\"display: flex; gap: 12px; margin-top: 16px;\\">\\n  <button data-action=\\"create\\" style=\\"flex: 1; padding: 12px 24px; border: 0; border-radius: 8px; background: #16a34a; color: white; font: inherit; font-weight: 600; cursor: pointer;\\">Save</button>\\n  <button data-action=\\"cancel\\" style=\\"flex: 1; padding: 12px 24px; border: 0; border-radius: 8px; background: #e2e8f0; font: inherit; cursor: pointer;\\">Cancel</button>\\n</div>\`",
                  "actions": {
                    "create": {
                      "script": "advanceFlow()"
                    },
                    "cancel": {
                      "script": "back()"
                    }
                  }
                }
              ]
            },
            {
              "id": "script-1",
              "type": "script",
              "name": "createRecipe",
              "label": "Save",
              "idleTitle": "Saving your recipe",
              "script": "publishData({type: \\"Recipe\\", data: {name: newName, method: newMethod || \\"\\", pic: newPic.url || \\"\\"}});\\nnavigate(\\"recipeBook\\")"
            }
          ]
        }
      ]
    },
    {
      "id": "screen-4",
      "type": "screen",
      "label": "Edit Recipe",
      "name": "editRecipe",
      "_children": [
        {
          "id": "variable-1",
          "type": "variable",
          "name": "rec",
          "compute": "@byId('Recipe', $args.id) || ({})"
        },
        {
          "id": "flow-2",
          "type": "flow",
          "name": "editRecipeFlow",
          "hideNav": true,
          "hideTopBar": true,
          "_children": [
            {
              "id": "screen-5",
              "type": "screen",
              "name": "editRecipeStep",
              "_children": [
                {
                  "id": "textInput-3",
                  "type": "textInput",
                  "name": "editName",
                  "label": "Name",
                  "required": true,
                  "compute": "@rec.name"
                },
                {
                  "id": "textInput-4",
                  "type": "textInput",
                  "name": "editMethod",
                  "label": "Method",
                  "variant": "multiline",
                  "compute": "@rec.method"
                },
                {
                  "id": "fileInput-2",
                  "type": "fileInput",
                  "name": "editPic",
                  "label": "Photo (optional)",
                  "variant": "image",
                  "showCamera": true,
                  "showFilePicker": true,
                  "showCommit": false,
                  "compute": "@(rec.pic ? { name: 'recipe-' + generateId(6), url: rec.pic } : { name: 'recipe-' + generateId(6) })"
                },
                {
                  "id": "content-4",
                  "type": "content",
                  "name": "editRecipeButtons",
                  "htmlTemplate": "@\`<div style=\\"display: flex; gap: 12px; margin-top: 16px;\\">\\n  <button data-action=\\"save\\" style=\\"flex: 1; padding: 12px 24px; border: 0; border-radius: 8px; background: #16a34a; color: white; font: inherit; font-weight: 600; cursor: pointer;\\">Save</button>\\n  <button data-action=\\"cancel\\" style=\\"flex: 1; padding: 12px 24px; border: 0; border-radius: 8px; background: #e2e8f0; font: inherit; cursor: pointer;\\">Cancel</button>\\n</div>\`",
                  "actions": {
                    "save": {
                      "script": "advanceFlow()"
                    },
                    "cancel": {
                      "script": "back()"
                    }
                  }
                }
              ]
            },
            {
              "id": "script-2",
              "type": "script",
              "name": "saveRecipe",
              "label": "Save",
              "idleTitle": "Saving your changes",
              "script": "updateData({type: \\"Recipe\\", id: $args.id, patch: {name: editName, method: editMethod || \\"\\", pic: editPic.url || \\"\\"}});\\nback()"
            }
          ]
        }
      ]
    }
  ]
}`,
        "source": "save-record"
      }
    ],
    pitfalls: []
  },
  "form": {
    type: "form",
    legacy: { "since": "2026-10", "use": "inputs on a screen or flow screen, saved by `publishData` / `updateData` in a script" },
    schema: '/** LEGACY since 2026-10. Do not write this in a new org. Use: inputs on a screen or flow screen, saved by `publishData` / `updateData` in a script */\n{\n  type: "form";\n  /**\n   * Which object type the form writes to, and which partition it writes into.\n   * `dataType` names the collection a submit publishes to; `scope` picks the partition, defaulting to `global`. Without a `dataType` the form has nowhere to save.\n   * Examples: { "dataType": "Recipe" }, { "dataType": "Post", "scope": "user" }\n   */\n  formConfig: FormConfigOptions;\n}\n\n// FormConfigOptions\n{\n  /**\n   * The object type this form submits records of.\n   * Examples: Recipe, Post, Todo\n   */\n  dataType?: string;\n  /**\n   * `global`: One shared collection every visitor reads and writes.\n   * `user`: A per-user partition.\n   * `variant`: A per-URL-variant partition, admin-curated and so not form-writable.\n   * `group`: A group partition, which needs `groupKey` to say which one.\n   * `all`: Reads across every partition.\n   * `inbox`: Realtime only: each visitor\'s own write-only inbox, which org admins can read.\n   */\n  scope?: "global" | "user" | "variant" | "group" | "all" | "inbox";\n  /**\n   * Which group partition a submit writes into. Required when `scope` is group.\n   * A member can belong to several groups at once, so the key cannot be inferred from the person.\n   * Examples: A, B, C\n   */\n  groupKey?: string;\n}',
    examples: [],
    pitfalls: ["`form` is LEGACY (since 2026-10): recognise it, never write it. Use instead: inputs on a screen or flow screen, saved by `publishData` / `updateData` in a script (the `save-record` example shows create and edit). The bullets below are for reading or editing an org that already has a form.", "A form must have a `formConfig` whose `dataType` matches an entry in `objectTypes`. The form fields are derived from that type's fields.", '`formConfig.scope` controls where submissions are written: `"global"`, `"user"`, `"variant"`, or `"all"`. Variant-scoped publishing is admin-only \u2014 the form publish path rejects it.', 'Public auth mode (`authMode: "public"`) disables the publish button for `scope: "user"` forms when no user is signed in. Show inline helper text rather than auto-creating an anon user.']
  },
  "function": {
    type: "function",
    schema: "{\n  type: \"function\";\n  /**\n   * Comma-separated parameter names the function body can reference; add `: Type` to have the closing gate check member access against that type's declared fields.\n   * The body itself is the inherited `compute` field. Arguments bind to these names in a child scope, so a function can call another function. A transition rule can call it too, and MUST pass any transition state it needs as an argument \u2014 see the help on the `compute` field (labelled Body in the designer). Annotations are gradual: `game: Game, pid` types only `game`, and an untyped list keeps working unchanged. Typing a parameter opts its body into strictness \u2014 `game.phaze` on a `Game` with no `phaze` field is a publish-time error rather than a silent `undefined` at runtime \u2014 and lets the gate check call sites whose argument type is statically known (a literal, a `$data.Type` element, a typed function's `returns`).\n   * Examples: a, b, record, index, game: Game, move: Move, rows: list<Share>, n: number\n   */\n  parameters?: string;\n  /**\n   * What the function returns, as a field type. Lets the closing gate type-check call sites that pass this function's result to a typed parameter.\n   * Examples: number, Game, list<Card>\n   */\n  returns?: string;\n  /**\n   * Display-only legacy hint \u2014 prefer `returns`, which the closing gate understands.\n   * Examples: number, string[]\n   */\n  returnTypeHint?: string;\n}",
    examples: [],
    pitfalls: []
  },
  "imageGeneration": {
    type: "imageGeneration",
    schema: '{\n  type: "imageGeneration";\n  /**\n   * The complete prompt sent to the image model.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Reference an earlier step\'s result to describe what to draw by interpolating it inside a `${story.result.title}`-style span, written as a backtick template rather than a bare expression like the AI-query step\'s prompt. Unlike the AI-query step there is no separate type description \u2014 this string is the whole instruction.\n   */\n  prompt?: string;\n  /**\n   * `flux-schnell`: Fast and cheap \u2014 the default choice for illustrations\n   * `nano-banana-2`: Slower and stronger, for when quality matters more than cost\n   * Leave it unset and the user picks, seeing a button per model. Set it (a literal id or an `@`formula) and they see one \'Create Image\' button using your choice.\n   */\n  model?: "flux-schnell" | "nano-banana-2" | string;\n  /**\n   * `true`: Returns `mockImageUrl` instead of generating\n   * `false`: Generates for real\n   * Image generation is one of the slower and costlier steps, so this is the one to reach for while building the flow around it.\n   */\n  mockEnabled?: boolean;\n  /**\n   * Image URL to return while mocking. Leave empty for a default placeholder.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   */\n  mockImageUrl?: string;\n}',
    examples: [],
    pitfalls: []
  },
  "numberInput": {
    type: "numberInput",
    schema: '{\n  type: "numberInput";\n  /**\n   * `integer`: Whole numbers only\n   * `slider`: Drag-to-set slider \u2014 pair with `min`, `max` and `step`\n   * `decimal`: Decimal numbers, stepping by 0.01\n   * `money`: Currency amount \u2014 decimal, stepping by 0.01, shown and stored to 2 places. No symbol: format that in the org\'s own formulas\n   * `intArray`: A list of whole numbers the user can add to and remove from\n   */\n  variant?: "integer" | "slider" | "decimal" | "money" | "intArray" | string;\n  /**\n   * Lowest value allowed. Formula-capable.\n   * Every variant honours it: a typed value below `min` is clamped up to it on commit. The slider defaults it to 1; the text variants have no lower bound unless set.\n   * Examples: @0, @1\n   */\n  min?: number | string;\n  /**\n   * Highest value allowed. Formula-capable.\n   * Every variant honours it, the same as `min` \u2014 a typed value above it is clamped down on commit. The slider defaults it to 100; the text variants have no upper bound unless set.\n   * Examples: @100, @10\n   */\n  max?: number | string;\n  /**\n   * Increment the value moves in. Formula-capable.\n   * The slider snaps to it; the text variants use it for the spinner arrows. When unset it follows the variant \u2014 0.01 for `decimal` and `money`, 1 otherwise.\n   * Examples: @1, @5\n   */\n  step?: number | string;\n}',
    examples: [],
    pitfalls: []
  },
  "objectType": {
    type: "objectType",
    schema: '{\n  /**\n   * Names the record type that `dataType`, foreign-key field types and `parent.type` refer to; unique across the org.\n   * Storage keys each collection by this name, so renaming a type does not move its existing records.\n   * Examples: Post, Game, Submission\n   */\n  name: string;\n  /**\n   * Declares the record\'s own fields; `id`, `createdAt`, `createdBy`, `updatedAt` and `updatedBy` are stamped automatically and must not be listed.\n   * Examples: [{"name":"postId","type":"text"},{"name":"url","type":"text"}]\n   */\n  fields: ObjectTypeField[];\n  /**\n   * Shows this Material icon for the type in the admin Object Types tab and on record-tree rows.\n   * Examples: article, image, fitness_center\n   */\n  icon?: string;\n  /**\n   * Makes records of this type belong to a record of another type, so deleting that parent record deletes these too (declared on the CHILD).\n   * Cascade runs on both rails: the realtime rail removes children in the cloud function; the Storage rail tombstones them (`isDeleted: true`) when a script calls `deleteData` or a form saves the parent with its delete toggle on \u2014 only children some node loads as a data source. Restoring a parent does not restore its children. This is ownership; a record tree\'s `parentRef` field is placement and never cascades.\n   * Examples: {"type":"Game","idField":"gameId"}, {"type":"Post","idField":"postId"}\n   */\n  parent?: ParentRelation;\n  /**\n   * `anyone`: Anyone using the app can save records of this type. This is the default, and the right choice for forms, submissions and per-visitor records.\n   * `admins`: Only the app\'s creator and its admins can save records of this type; everyone can still read them. Use it for an owner\'s public collection, portfolio or results.\n   * An admin is the app\'s creator or a user listed in its admins. The server checks it on every save the app makes. Until a later release locks direct storage writes, an out-of-date copy of the app or a direct storage write can still get past it, so don\'t rely on it for anything secret yet. Hiding the matching controls is a separate step: give them a hidden formula on $isOrgAdmin. Guest (demo) apps refuse `admins` outright, because their admin list isn\'t protected.\n   * Examples: admins\n   */\n  writeAccess?: "anyone" | "admins";\n}',
    examples: [],
    pitfalls: []
  },
  "objectTypeField": {
    type: "objectTypeField",
    schema: '{\n  /**\n   * Sets the key the value is stored under on every record, and the name formulas read it by (`it.Content`, `$data.Post[0].AuthorId`).\n   * Examples: Content, postId, gameId\n   */\n  name?: string;\n  /**\n   * Chooses the value type: a builtin (`text`, `number`, `boolean`, `temporal`, `image`, `imageUpload`, `variable`, `parentRef`), a declared object type name for a foreign key, or `list<T>` / `map<T>` of either.\n   * A declared `list<T>` reads back as `[]` when empty and dense when sparse; declare arrays here rather than inlining them. `parentRef` is the record-tree placement link (exactly one per child type) and is unrelated to `parent`. Multiline text is a `variant`, not a type.\n   * Examples: text, number, list<text>, map<number>, SoloRun, parentRef\n   */\n  type?: string;\n  /**\n   * Picks the input widget for a `text` field: `basic` (default), `multiline` or `stringArray`. Ignored on every other field type.\n   * Only the text editor reads it; `variant: "integer"` on a number field has no effect.\n   * Examples: multiline, basic\n   */\n  variant?: string;\n  /**\n   * Fills the field from an @-formula whenever the form renders, instead of user input, and stores the result on the record.\n   * Evaluated in the form\'s scope, so it can reference sibling fields by name. Same mechanism as a `variable` node\'s `compute`.\n   */\n  compute?: string;\n  /**\n   * `true`: Computed for the form but never written onto the record; pair with `compute` for a derived display value.\n   * `false`: The value is saved (default).\n   */\n  isTransient?: boolean;\n  /**\n   * Shows as help text under the field in every form that edits this type, and tells the org-gen agent what the field means.\n   * Examples: Currency the tab is kept in, Short shareable code others join with\n   */\n  description?: string;\n  /**\n   * imageUpload only: formula producing the stored file name; defaults to `\'<name>_\' + generateId(4)`.\n   * Examples: @name\n   */\n  fileNameFormula?: string;\n  /**\n   * `true`: imageUpload only; offers "Generate with AI" beside the upload button.\n   * `false`: Upload or camera only (default).\n   */\n  aiEnabled?: boolean;\n  /**\n   * imageUpload with `aiEnabled`: formula for the system-prompt prefix sent with the user\'s image prompt; can reference other fields of the record.\n   * Default is "safe for work, high quality".\n   * Examples: @`An illustration for a short story: title: ${rightTitle} , ${rightText}`\n   */\n  aiSystemPromptFormula?: string;\n  /**\n   * image only: formula that pre-fills the image search box, e.g. from the record\'s name.\n   * Examples: @Name\n   */\n  searchFormula?: string;\n  // LEGACY, recognise but never write: isList (write instead: `type: "list<T>"`); multiline (write instead: `variant: "multiline"` on a text field)\n}',
    examples: [],
    pitfalls: []
  },
  "parentRelation": {
    type: "parentRelation",
    schema: "{\n  /**\n   * Names the parent object type whose records own records of this type.\n   * Must be a declared object type; the closing gate rejects an unknown name, a self-parent and any cycle, because the cascade walks the graph as a tree.\n   * Examples: Game, Post\n   */\n  type?: string;\n  /**\n   * Names the field on THIS type that holds the parent record's id.\n   * Must be one of this type's declared fields (the gate hints at near-misses). Any field type works; jumbly's `gameId` and feta-compli-sim's `postId` are plain `text`. The value links, not the type.\n   * Examples: gameId, postId\n   */\n  idField?: string;\n}",
    examples: [],
    pitfalls: []
  },
  "plugin": {
    type: "plugin",
    schema: "{\n  name: string;\n  parameters: PluginParameter[];\n  htmlTemplate: string;\n  icon?: string;\n}\n\n// PluginParameter\n{\n  name: string;\n  type?: string;\n  description?: string;\n}",
    examples: [
      {
        "excerpt": '{\n  "id": "plugin-1",\n  "type": "plugin",\n  "name": "CardLink",\n  "icon": "link",\n  "parameters": [\n    {\n      "name": "link",\n      "type": "text",\n      "description": "Target screen name"\n    },\n    {\n      "name": "title",\n      "type": "text",\n      "description": "Card title"\n    },\n    {\n      "name": "icon",\n      "type": "text",\n      "description": "Emoji or icon"\n    }\n  ],\n  "htmlTemplate": "@`<a href=\\"${link}\\" style=\\"display: flex; flex-direction: column; align-items: center; padding: 24px; background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); text-decoration: none; color: #333;\\">\\n  <span style=\\"font-size: 32px; margin-bottom: 8px;\\">${icon}</span>\\n  <span style=\\"font-weight: 600; font-family: system-ui;\\">${title}</span>\\n</a>`"\n}',
        "source": "dashboard"
      }
    ],
    pitfalls: ['A plugin\'s `name` becomes the node `type` for invocations. Reference plugins as `{ type: "<name>", parameters: { ... } }`.', "`parameters` declared on the plugin definition must match the keys passed at invocation. Missing parameters render as `undefined`.", "Inside a plugin's `htmlTemplate`, reference parameters as `${parameters.<name>}`.", 'Cross-org link plugins need `target="_blank"` in their anchor; without it the SPA intercepts.']
  },
  "publishData": {
    type: "publishData",
    schema: "{\n  type: \"publishData\";\n  /**\n   * Formula resolving to the object to publish.\n   * Usually assembles the record from earlier steps' results, which is why an object spread is the common shape \u2014 merging an AI result with a generated image, say.\n   * Examples: @({ ...askai.result, illustration: imageGen.result }), @({ ...askai.result, image: imageGen.result.url })\n   */\n  sourceExpression?: string;\n  /**\n   * Object type to publish the record as.\n   * Must be one of the org's declared object types \u2014 it decides which collection the record joins, and so where `$data.<Type>` will find it.\n   */\n  publishDataType?: string;\n  /**\n   * Which partition to publish into. Defaults to `global`.\n   * `global` for shared records, `user` for records belonging to the signed-in user, `variant` for per-URL-variant content (which also needs `publishVariant`).\n   */\n  publishScope?: string;\n  /**\n   * Which variant key to publish under. Formula-capable; only read when `publishScope` is `variant`.\n   * Variant records are stored per key rather than filtered at read time, so this decides the storage path the record lands on.\n   */\n  publishVariant?: string;\n}",
    examples: [],
    pitfalls: []
  },
  "repeat": {
    type: "repeat",
    schema: "{\n  type: \"repeat\";\n  /**\n   * The noun for one item, as people should read it in the buttons and headers.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Lower case reads best: the platform capitalises it where a sentence starts. A formula is evaluated in the flow's scope, so an earlier step can set it (\"stall\", \"station\").\n   * Examples: guest, @roleSingular\n   */\n  itemLabel?: string;\n  /**\n   * The line that stands for one pass on the summary screen. Plain text, evaluated in the pass's scope.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   * Every input on the pass's screens is in scope by its bare name, and $index is the pass number. Keep it to one line at phone width.\n   * Examples: @name + ' \xB7 ' + email, @emoji + ' ' + name + ' \xB7 ' + hhmmFn(from) + '\u2013' + hhmmFn(until)\n   */\n  summary?: string;\n  /**\n   * How many items are needed before Done is allowed. Default 0.\n   * Examples: 1, @seats > 0 ? 1 : 0\n   */\n  min?: number | string;\n  /**\n   * The most items allowed; Add another disappears at this count. Leave empty for no limit.\n   * Examples: 20, @seats\n   */\n  max?: number | string;\n}",
    examples: [],
    pitfalls: ['Use a `repeat` when a flow must ask the SAME questions once per item and the user decides how many ("Add another guest?"). Never ask for a count up front and never fake it with numbered screens: the repeat draws the summary (one row per item with Edit and Remove, "Add another", Done) for you.', "A `repeat` is a DIRECT child of a `flow`, and its children are `screen` nodes, one per page of a pass. Inputs go inside those screens. No repeat inside a repeat. The gate rejects each of these shapes.", `Worked example, a guest list inside a booking flow: \`{"type": "flow", "name": "booking", "_children": [{"type": "screen", "name": "who", "_children": [{"type": "textInput", "name": "lead", "label": "Your name", "required": true}]}, {"type": "repeat", "name": "guests", "label": "Guests", "itemLabel": "guest", "summary": "@guestName + (diet ? ' \xB7 ' + diet : '')", "min": 1, "max": 8, "_children": [{"type": "screen", "name": "guestScreen", "_children": [{"type": "textInput", "name": "guestName", "label": "Guest's name", "required": true}, {"type": "textInput", "name": "diet", "label": "Dietary needs (optional)"}]}]}, {"type": "script", "name": "save", "script": "publishData({type: \\"Booking\\", data: {lead: lead, guests: guests}}); navigate(\\"thanks\\")"}]}\``, "After the repeat, its NAME reads as an array of per-pass objects with every screen's inputs merged flat: `guests` is `[{guestName, diet}, \u2026]`. Build the record from it in a later `script` step (pure `map`/`reduce`, then ONE host call \u2014 host calls inside a loop are rejected).", "Input names must be unique across ALL of one repeat's screens, because a pass's screens merge into one object. They may repeat names used outside the repeat.", "`summary` is plain text evaluated in each pass's scope, so the pass's inputs are bare names (`@guestName`), and `$index` is the pass number (0-based). Keep it to one short line.", '`itemLabel` is the lower-case noun ("guest"); the platform capitalises it where needed ("Guest 2 \xB7 1 of 1", "Add another guest"). It may be a formula reading an earlier step.', '`min` gates Done (a pass only counts once every `required` input on its screens is filled and every `validation` rule passes); `max` hides "Add another". Both take a number or a formula.', "Next refuses to leave a screen (any flow screen, including a pass's) while a `required` input is empty, marking it red, so mark the fields a pass genuinely needs as `required`."]
  },
  "screen": {
    type: "screen",
    schema: "{\n  type: \"screen\";\n  /** Shown beside this screen in the nav menu or tab strip. */\n  icon?: string;\n  /**\n   * `true`: The site header is hidden while this screen is showing\n   * `false`: The site header is shown (default)\n   * Examples: true, @curPhase == 'playing'\n   */\n  hideHeader?: boolean | string;\n}",
    examples: [
      {
        "excerpt": '{\n  "id": "screen-1",\n  "type": "screen",\n  "label": "Home",\n  "name": "home",\n  "layout": {\n    "maxColumns": 1,\n    "axis": "y",\n    "mode": "columns",\n    "minColumnWidth": 350\n  },\n  "data": {\n    "source": "embedded"\n  },\n  "_children": [\n    {\n      "id": "content-1",\n      "type": "content",\n      "name": "welcomeHeader",\n      "htmlTemplate": "@`<div style=\\"padding: 24px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 16px; color: white; margin-bottom: 16px;\\">\\n  <h1 style=\\"margin: 0 0 8px 0; font-family: system-ui; font-size: 28px;\\">My App</h1>\\n  <p style=\\"margin: 0; opacity: 0.9;\\">Welcome to your dashboard</p>\\n</div>`"\n    },\n    {\n      "id": "subsection-1",\n      "type": "subsection",\n      "label": "Menu",\n      "name": "menu",\n      "isRepeating": true,\n      "layout": {\n        "maxColumns": 2,\n        "axis": "x",\n        "mode": "columns",\n        "minColumnWidth": 150\n      },\n      "data": {\n        "dataType": "MenuItem",\n        "source": "embedded"\n      },\n      "_children": [\n        {\n          "id": "CardLink-1",\n          "type": "CardLink",\n          "name": "menuCard",\n          "parameters": {\n            "link": "@screen",\n            "title": "@title",\n            "icon": "@icon"\n          }\n        }\n      ]\n    }\n  ]\n}',
        "source": "dashboard"
      },
      {
        "excerpt": '{\n  "id": "screen-2",\n  "type": "screen",\n  "label": "Contacts",\n  "name": "contacts",\n  "layout": {\n    "maxColumns": 1,\n    "axis": "y",\n    "mode": "columns",\n    "minColumnWidth": 350\n  },\n  "data": {\n    "source": "embedded"\n  },\n  "_children": [\n    {\n      "id": "content-2",\n      "type": "content",\n      "name": "addBtn",\n      "htmlTemplate": "@`<a href=\\"editContact\\" style=\\"display: inline-flex; align-items: center; padding: 12px 24px; background: #10b981; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; margin-bottom: 16px;\\">+ Add Contact</a>`"\n    },\n    {\n      "id": "subsection-2",\n      "type": "subsection",\n      "label": "All Contacts",\n      "name": "contactList",\n      "isRepeating": true,\n      "layout": {\n        "maxColumns": 2,\n        "axis": "x",\n        "mode": "columns",\n        "minColumnWidth": 250\n      },\n      "data": {\n        "dataType": "Contact",\n        "source": "embedded",\n        "sort": {\n          "field": "name",\n          "direction": "asc"\n        }\n      },\n      "_children": [\n        {\n          "id": "content-3",\n          "type": "content",\n          "name": "contactCard",\n          "htmlTemplate": "@`<button data-action=\\"openContact\\" data-param-id=\\"${id}\\" style=\\"display: flex; gap: 12px; padding: 16px; background: white; border: none; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); color: #333; font: inherit; text-align: left; width: 100%; cursor: pointer;\\">\\n  <div style=\\"width: 48px; height: 48px; border-radius: 50%; background: #e2e8f0; display: flex; align-items: center; justify-content: center;\\">\u{1F464}</div>\\n  <div>\\n    <div style=\\"font-weight: 600; font-family: system-ui;\\">${name}</div>\\n    <div style=\\"font-size: 14px; color: #666;\\">${email}</div>\\n  </div>\\n</button>`",\n          "actions": {\n            "openContact": {\n              "params": [\n                "id"\n              ],\n              "script": "navigate(\'contactDetail\', {id: $params.id})"\n            }\n          }\n        }\n      ]\n    }\n  ]\n}',
        "source": "list-detail"
      },
      {
        "excerpt": '{\n  "id": "screen-1",\n  "type": "screen",\n  "label": "Recipes",\n  "name": "recipeBook",\n  "data": {\n    "source": "embedded"\n  },\n  "_children": [\n    {\n      "id": "content-1",\n      "type": "content",\n      "name": "newRecipeLink",\n      "htmlTemplate": "@`<a href=\\"newRecipe\\" style=\\"display: inline-block; padding: 12px 24px; background: #16a34a; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; margin-bottom: 16px;\\">+ New recipe</a>`"\n    },\n    {\n      "id": "subsection-1",\n      "type": "subsection",\n      "name": "recipeList",\n      "isRepeating": true,\n      "data": {\n        "dataType": "Recipe",\n        "source": "embedded",\n        "sort": {\n          "field": "name",\n          "direction": "asc"\n        }\n      },\n      "_children": [\n        {\n          "id": "content-2",\n          "type": "content",\n          "name": "recipeRow",\n          "htmlTemplate": "@`<div style=\\"display: flex; align-items: center; gap: 12px; padding: 12px; background: white; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); margin-bottom: 8px;\\">\\n  ${pic ? `<img src=\\"${pic}\\" alt=\\"\\" style=\\"width: 56px; height: 56px; object-fit: cover; border-radius: 8px;\\">` : ``}\\n  <div style=\\"flex: 1; font-weight: 600;\\">${name}</div>\\n  <button data-action=\\"edit\\" data-param-id=\\"${id}\\" style=\\"padding: 8px 16px; border: 0; border-radius: 8px; background: #e2e8f0; font: inherit; cursor: pointer;\\">Edit</button>\\n</div>`",\n          "actions": {\n            "edit": {\n              "params": [\n                "id"\n              ],\n              "script": "navigate(\'editRecipe\', {id: $params.id})"\n            }\n          }\n        }\n      ]\n    }\n  ]\n}',
        "source": "save-record"
      }
    ],
    pitfalls: ["The first screen child of `appConfig._children` is the home screen. Always ensure a screen sits at index 0.", '`data.dataType` must reference a name declared under `objectTypes._children`. Singular form ("Recipe", not "Recipes") is the convention.', '`layout` is optional; if set, `axis` is `"x"` or `"y"` and `mode` is `"columns"` or `"rows"`. Don\'t invent values.', 'Screens placed under a `Components` folder are detail/edit screens (master-detail pattern). Navigate to one from a list row with a declared action that passes the record\'s id \u2014 `<button data-action="open" data-param-id="${id}">` plus `"actions": { "open": { "params": ["id"], "script": "navigate(\'taskDetail\', {id: $params.id})" } }` \u2014 and resolve the record on the destination with a variable computing `@byId(\'Task\', $args.id)`.']
  },
  "script": {
    type: "script",
    schema: '{\n  type: "script";\n  /**\n   * Imperative script body \u2014 statements separated by `;`. Not an `@`formula: written raw.\n   * Parenthesise any ternary before a following statement (`x = (a ? b : c);`) \u2014 the parser otherwise swallows the next statement into the ternary\'s else branch. Async host calls are only legal at statement top level or as the right-hand side of an assignment.\n   */\n  script?: string;\n}',
    examples: [
      {
        "excerpt": '{\n  "id": "script-1",\n  "type": "script",\n  "name": "createRecipe",\n  "label": "Save",\n  "idleTitle": "Saving your recipe",\n  "script": "publishData({type: \\"Recipe\\", data: {name: newName, method: newMethod || \\"\\", pic: newPic.url || \\"\\"}});\\nnavigate(\\"recipeBook\\")"\n}',
        "source": "save-record"
      }
    ],
    pitfalls: ["The `script` field is a RAW statement sequence \u2014 no `@` prefix. Statements are `;`-separated; assignment (`x = expr`) is supported.", "**A ternary as a whole STATEMENT swallows the statements after it** \u2014 `cond ? a : b; publishData(...)` parses the publish into the alternate (nested \u2192 rejected). Assign it instead: `x = cond ? a : b;` binds the whole ternary to `x` and ends at the `;`, as does `x = a || b` / `x = a ?? b`.", "Async host calls (`publishData`, `updateData`, `deleteData`, `setAppValue`, `navigate`) are only legal as a whole statement or as the RHS of an assignment \u2014 never nested inside another expression or another host call's arguments. Bind intermediate results to variables first.", "There are no `if` statements \u2014 but you cannot branch around host calls with ternaries either (that nests them). Use separate flows for create vs edit (publishData vs updateData).", 'End with `navigate("<screenName>")` or the flow\'s last step stays selected after saving.']
  },
  "selectInput": {
    type: "selectInput",
    schema: '{\n  type: "selectInput";\n  /**\n   * `single`: The user picks one option\n   * `multiple`: The user can pick several options\n   */\n  variant?: "single" | "multiple" | string;\n  /**\n   * `select`: Dropdown menu \u2014 the default\n   * `radio`: Every option visible as a radio button; best for two to four choices\n   * `combobox`: Type-to-filter box; best when the list is long\n   * On touch devices a combobox renders as the plain dropdown instead, so focusing it never pops the virtual keyboard. Type-ahead is a desktop affordance.\n   */\n  idiom?: "select" | "radio" | "combobox" | string;\n  /**\n   * The options offered \u2014 a literal array, or a formula producing one.\n   * An option is either a plain string, or an object with `id` and `label` (plus an optional `group` to divide the list) when the stored value differs from the text shown.\n   * Examples: ["English (British)", "French", "Spanish", "German"], @concat([({ id: \'\', label: \'All teams\' })], filter($data.Team, true).map(({ id: it.id, label: it.name, group: \'Group \' + it.group })))\n   */\n  pickerValues?: string | StringOrPickerOption[];\n}\n\n// StringOrPickerOption\n{\n\n}\n\n// PickerOption\n{\n  /**\n   * The value stored when this option is chosen.\n   * Examples: trip, ongoing\n   */\n  id: string;\n  /**\n   * The text the person sees.\n   * Examples: A trip \u2014 settles at the end, Ongoing \u2014 runs indefinitely\n   */\n  label: string;\n  /**\n   * Optional group label. Options sharing a group render together under that heading.\n   * Insertion order of the options decides the order the headings appear in.\n   * Examples: A, B\n   */\n  group?: string;\n}',
    examples: [],
    pitfalls: ['The choices go in `pickerValues`, never `options`. Each is a plain string (`["Small", "Medium", "Large"]`), or an object `{id, label}` when the stored value differs from the text shown; add `group` to divide a long list. The input\'s value is the string, or the picked option\'s `id`.', 'A select input has no `dataType`. To offer records, map them in a formula: `"pickerValues": "@filter($data.Team, true).map((t) => ({id: t.id, label: t.name}))"`, which stores the picked record\'s `id`.', 'It picks ONE value. There is no `multi` field, and `variant: "multiple"` passes validation but still renders a pick-one dropdown, so for "pick any of these" write one `booleanInput` per choice.', '`idiom` sets the look: `"select"` (the default dropdown), `"radio"` for two to four choices, `"combobox"` (type to filter) for a long list.']
  },
  "subsection": {
    type: "subsection",
    schema: '{\n  type: "subsection";\n  /**\n   * `inline`: Every item rendered in a row, laid out by the subsection\'s layout\n   * `masterDetail`: A list of the items beside a detail pane for the selected one, with Add, Delete and drag-to-reorder controls\n   * Only read when the subsection is repeating. Master/detail is the editing shape: the master list captions each row with iterationLabel (falling back to nounLabel + index), and the detail pane shows the row\'s children. Inline is the display shape most orgs use for data-bound collections.\n   * Examples: masterDetail\n   */\n  variant?: "inline" | "masterDetail";\n  /**\n   * `true`: The subsection will have a header showing its title and a border\n   * `false`: The subsection will not have a header, title or border\n   */\n  showHeader?: boolean | string;\n  /**\n   * `true`: The subsection will be collapsible\n   * `false`: The subsection will not be collapsible\n   */\n  collapsible?: boolean | string;\n  /**\n   * `true`: The subsection will be collapsed by default\n   * `false`: The subsection will not be collapsed by default\n   */\n  collapsedByDefault?: boolean | string;\n  /**\n   * `true`: The children sit behind a \u22EE menu; each callable button inside is one menu item\n   * `false`: The children render in place (default)\n   * Put the subsection beside the card inside a wrapper subsection, and give it containerStyle.overlay (topRight) so the \u22EE pins to the card\'s corner, as for a like pill. Any callableButton inside renders as a menu item whatever its variant, and reports a failure in a snackbar since the menu has closed. Hidden buttons drop out of the menu.\n   * Examples: true\n   */\n  actionMenu?: boolean | string;\n  /**\n   * `true`: Each item gets a drag handle; dropping it writes the new order\n   * `false`: Items render in query order with no drag handle\n   * Takes a formula, so one list can be reorderable for the host only (@isHost). A data-bound list persists the order on the records: every displayed row whose position changed gets orderField written as 0..N-1, through updateData on the storage rail or updateRealtime on the realtime rail (owner-only there, so a host cannot reorder other players\' records). Pair it with sort: {field: <orderField>} on the same data block or the drop will not stick visually.\n   * Examples: true, @isHost\n   */\n  reorderable?: boolean | string;\n  /**\n   * The record field that holds each item\'s position; defaults to sortIndex.\n   * Declare it on the ObjectType as a number field and sort the same data block by it, so the persisted order is what renders. The platform writes a dense 0..N-1 on every drop and only touches records whose value changed.\n   * Examples: sortIndex, position\n   */\n  orderField?: string;\n}',
    examples: [
      {
        "excerpt": '{\n  "id": "subsection-1",\n  "type": "subsection",\n  "label": "Menu",\n  "name": "menu",\n  "isRepeating": true,\n  "layout": {\n    "maxColumns": 2,\n    "axis": "x",\n    "mode": "columns",\n    "minColumnWidth": 150\n  },\n  "data": {\n    "dataType": "MenuItem",\n    "source": "embedded"\n  },\n  "_children": [\n    {\n      "id": "CardLink-1",\n      "type": "CardLink",\n      "name": "menuCard",\n      "parameters": {\n        "link": "@screen",\n        "title": "@title",\n        "icon": "@icon"\n      }\n    }\n  ]\n}',
        "source": "dashboard"
      },
      {
        "excerpt": '{\n  "id": "subsection-2",\n  "type": "subsection",\n  "label": "All Contacts",\n  "name": "contactList",\n  "isRepeating": true,\n  "layout": {\n    "maxColumns": 2,\n    "axis": "x",\n    "mode": "columns",\n    "minColumnWidth": 250\n  },\n  "data": {\n    "dataType": "Contact",\n    "source": "embedded",\n    "sort": {\n      "field": "name",\n      "direction": "asc"\n    }\n  },\n  "_children": [\n    {\n      "id": "content-3",\n      "type": "content",\n      "name": "contactCard",\n      "htmlTemplate": "@`<button data-action=\\"openContact\\" data-param-id=\\"${id}\\" style=\\"display: flex; gap: 12px; padding: 16px; background: white; border: none; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); color: #333; font: inherit; text-align: left; width: 100%; cursor: pointer;\\">\\n  <div style=\\"width: 48px; height: 48px; border-radius: 50%; background: #e2e8f0; display: flex; align-items: center; justify-content: center;\\">\u{1F464}</div>\\n  <div>\\n    <div style=\\"font-weight: 600; font-family: system-ui;\\">${name}</div>\\n    <div style=\\"font-size: 14px; color: #666;\\">${email}</div>\\n  </div>\\n</button>`",\n      "actions": {\n        "openContact": {\n          "params": [\n            "id"\n          ],\n          "script": "navigate(\'contactDetail\', {id: $params.id})"\n        }\n      }\n    }\n  ]\n}',
        "source": "list-detail"
      },
      {
        "excerpt": '{\n  "id": "subsection-1",\n  "type": "subsection",\n  "name": "recipeList",\n  "isRepeating": true,\n  "data": {\n    "dataType": "Recipe",\n    "source": "embedded",\n    "sort": {\n      "field": "name",\n      "direction": "asc"\n    }\n  },\n  "_children": [\n    {\n      "id": "content-2",\n      "type": "content",\n      "name": "recipeRow",\n      "htmlTemplate": "@`<div style=\\"display: flex; align-items: center; gap: 12px; padding: 12px; background: white; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); margin-bottom: 8px;\\">\\n  ${pic ? `<img src=\\"${pic}\\" alt=\\"\\" style=\\"width: 56px; height: 56px; object-fit: cover; border-radius: 8px;\\">` : ``}\\n  <div style=\\"flex: 1; font-weight: 600;\\">${name}</div>\\n  <button data-action=\\"edit\\" data-param-id=\\"${id}\\" style=\\"padding: 8px 16px; border: 0; border-radius: 8px; background: #e2e8f0; font: inherit; cursor: pointer;\\">Edit</button>\\n</div>`",\n      "actions": {\n        "edit": {\n          "params": [\n            "id"\n          ],\n          "script": "navigate(\'editRecipe\', {id: $params.id})"\n        }\n      }\n    }\n  ]\n}',
        "source": "save-record"
      }
    ],
    pitfalls: ["`isRepeating: true` requires a `data: { dataType, source }` binding. Without it the subsection renders nothing.", "Default vertical margin between repeating subsections is set by the platform; do not add `marginBottom` manually unless you want to override.", "WHERE clauses on a subsection's `data` use `this` to refer to the iterating record (NOT `it` \u2014 that's for filter/map/reduce inside formulas).", 'When a record occupies a RANGE of the parent\'s rows \u2014 a multi-day event across a day list, a stint across laps \u2014 use `data.span` instead of writing an equality filter. It replaces the `whereClause` you would otherwise write: `{ "type": "subsection", "isRepeating": true, "data": { "dataType": "Event", "source": "storage", "span": { "startField": "Start", "endField": "End", "axis": "@day", "mode": "repeat" } } }`. Here `axis` is a formula for the CURRENT row\'s position, normally the variable the parent repeating node computes per row (e.g. a `variable` named `day` whose compute is `@today() + $index`); `startField`/`endField` are fields on the bound objectType. Both must be real declared fields or the org fails validation, and a span on a node without `isRepeating: true` is rejected because there are no rows to cover.', "`data.span` is one facet of the `data` block, so it sits beside `dataType`/`source`/`sort` \u2014 NOT at the top level of the node. Do not also write a `whereClause` that matches a single slot (`@dateCompare(day, Start) == 0`): span owns row matching, and an equality clause on top would cancel it out. A `whereClause` that filters on something else entirely (`@this.published`) is fine and is applied as well.", 'With `mode: "repeat"` (the default) the record renders in EVERY row it covers; read `$spanPosition` (`"start"` / `"middle"` / `"end"` / `"single"`) to style the run so it reads as one continuous element, plus `$spanStart` and `$spanEnd` for the range itself. With `mode: "once"` it renders only on the row where its run starts, which is what a plain equality filter does \u2014 pick that when one card per record is wanted, and show the range with `${$spanStart} \u2013 ${$spanEnd}`.', '`layout: { "mode": "spatial", "cols": N }` turns a subsection (or screen/app) into a 0-based CELL GRID \u2014 a seating plan, a month view, a board, a piano roll. `cols` is REQUIRED in spatial mode (the closing gate fails the org with `spatial-missing-cols` without it); `rows` is optional (omit and the grid grows downward to fit whatever is placed) and `rowHeight` (px) is optional (omit for content-sized rows; fix it when cell geometry must line up, as in a month grid). Both `cols` and `rows` accept a number or an `@`-formula evaluated in the container\'s scope (`"cols": "@length(sections)"`, `"cols": "@bars * 16"`). Unlike `mode: "columns"` / `mode: "grid"`, NOTHING flows by default here \u2014 each child picks its own cell through its `place` field; a child without `place` drops into the next free cell in document order, two children resolving to the same cell overlap (later paints on top, deliberately \u2014 a board square and the piece on it), and a screen narrower than the grid scrolls sideways instead of squashing.', '`place` goes on the CHILD, never on the spatial container: `"place": { "x": 2, "y": 1 }` with optional `"w"` / `"h"` cell spans (default 1). `x` and `y` are 0-based (origin top-left) and BOTH are required once `place` is present (gate error `place-incomplete`); each member is a number or an `@`-formula evaluated in the child\'s own scope. For a repeating child (`isRepeating: true` + `data`) EVERY ITERATION becomes its own grid item and `place` is re-evaluated per iteration from that record\'s fields or `$index` \u2014 so ONE repeating child carrying `"place": { "x": "@Col", "y": "@Row" }` over `SeatAssignment` records draws the entire seating plan, and `{ "x": "@$index % 7", "y": "@floor($index / 7)" }` lays 42 day cells into a 7-wide month. Out-of-range values clamp to the edge and a non-numeric result falls back to auto-flow, silently. Do NOT put `showHeader`, `collapsible` or `pageSize` on a repeating child inside a spatial parent \u2014 that chrome does not render in spatial mode and the gate warns `spatial-unsupported`; `place` on a child whose parent is NOT spatial is ignored with a `place-outside-spatial` warning.', 'Complete seating plan \u2014 the smallest spatial org, one repeating child placed by its own record\'s fields (`SeatAssignment` records shaped `{ "Guest": "Ada", "Row": 2, "Col": 4 }`; an empty seat is simply an unoccupied cell): `{ "id": "subsection-1", "type": "subsection", "name": "seatMap", "layout": { "mode": "spatial", "cols": 10, "rows": 6, "rowHeight": 48, "minColumnWidth": 40 }, "_children": [ { "id": "subsection-2", "type": "subsection", "name": "seats", "isRepeating": true, "place": { "x": "@Col", "y": "@Row" }, "data": { "dataType": "SeatAssignment", "source": "storage", "sort": { "field": "Row", "direction": "asc" } }, "_children": [ { "id": "content-1", "type": "content", "name": "seatChip", "htmlTemplate": "@`<div style=\\"height: 100%; display: flex; align-items: center; justify-content: center; border-radius: 6px; background: #e3f2fd; font-size: 12px;\\">${Guest}</div>`" } ] } ] }`. Note the shape: `layout` (with `cols`) on the OUTER subsection, `place` + `data` on the INNER repeating subsection, and the visual in a `content` child that reads the record\'s fields (`${Guest}`) as bare identifiers.', "To read per-row user edits back out of a repeating subsection (a save script, a live tally), use **`$rows('subsectionName')`** \u2014 it returns each row's record merged with its inputs, input-over-record. A bare reference to the subsection name merges record-over-input, so a per-row input named like a bound field is silently shadowed (the \"edited value reads back as the original / live tally reads 0\" trap). Bulk-save the whole set in one step: `publishData({type: \"X\", data: $rows('rows').map((r) => ({...}))})`."]
  },
  "tab": {
    type: "tab",
    schema: '{\n  type: "tab";\n}',
    examples: [],
    pitfalls: []
  },
  "tabGroup": {
    type: "tabGroup",
    schema: '{\n  type: "tabGroup";\n}',
    examples: [],
    pitfalls: []
  },
  "temporalInput": {
    type: "temporalInput",
    schema: '{\n  type: "temporalInput";\n  /**\n   * `date`: Date only\n   * `datetime`: Date and time together\n   * `time`: Time of day only\n   * Maps onto the native browser input, so the picker a visitor sees is their own platform\'s. `datetime` uses `datetime-local`, which stores a naive wall-clock time carrying no timezone.\n   */\n  format?: "date" | "datetime" | "time" | string;\n}',
    examples: [],
    pitfalls: []
  },
  "textInput": {
    type: "textInput",
    schema: '{\n  type: "textInput";\n  /**\n   * `basic`: Single-line text field\n   * `multiline`: Text area that grows with the content\n   * `stringArray`: A list of strings the user can add to and remove from\n   */\n  variant?: "basic" | "multiline" | "stringArray" | string;\n  /**\n   * `none`: Leaves the text exactly as typed\n   * `uppercase`: Forces UPPERCASE as the user types\n   * `lowercase`: Forces lowercase as the user types\n   * Useful for codes a user reads off a screen or a card \u2014 forcing a room or booking code to uppercase means the entry always matches a case-insensitive lookup.\n   */\n  textTransform?: "none" | "uppercase" | "lowercase";\n}',
    examples: [
      {
        "excerpt": '{\n  "id": "textInput-1",\n  "type": "textInput",\n  "name": "newName",\n  "label": "Name",\n  "required": true\n}',
        "source": "save-record"
      }
    ],
    pitfalls: ['`variant` is `"basic"` (default) or `"multiline"`. Number input is a separate node type (`numberInput`).', "`placeholder` and `label` are static strings or formulas. Do not put HTML in either.", "Input values stored under the iteration's pointer key; reference them with `value.<fieldName>` from sibling nodes."]
  },
  "transcribe": {
    type: "transcribe",
    schema: '{\n  type: "transcribe";\n  /**\n   * The audio clip to transcribe. Formula-capable \u2014 usually a sibling fileInput\'s uploaded url.\n   * Point it at the `url` of a fileInput with `variant: "audio"`. A fileInput is an input node, not a flow step, so its uploaded value is read directly off its name \u2014 a field named `recordAudio` gives `@recordAudio.url`, with no `.result` in between.\n   * Examples: @recordAudio.url\n   */\n  audioSourceUrl?: string;\n  /**\n   * `gpt-4o-transcribe`: OpenAI\'s speech-to-text model \u2014 the default and only supported model today.\n   * The only supported model today; the field exists so a future second model is additive, not a breaking rename.\n   */\n  model?: "gpt-4o-transcribe" | string;\n  /** Optional language hint (e.g. `en`) to improve accuracy. Leave unset to let the model detect it. */\n  language?: string;\n  /**\n   * `true`: Returns `mockTranscript` instead of transcribing\n   * `false`: Transcribes for real\n   */\n  mockEnabled?: boolean;\n  /**\n   * Transcript text to return while mocking.\n   * Template string: an `@` prefix makes the value a backtick template, so `${...}` interpolates; a plain string with no `@` is used verbatim.\n   */\n  mockTranscript?: string;\n}',
    examples: [],
    pitfalls: []
  },
  "tree": {
    type: "tree",
    schema: "{\n  type: \"tree\";\n  /**\n   * The kinds of row the tree can hold, keyed by type name.\n   * Each entry declares `allowedChildTypes` \u2014 which is what makes the tree a tree rather than a list \u2014 plus optional `icon`, `color`, `label`, `labelField` (which record field titles the row) and `detail` (a child node rendered as that type's detail view).\n   */\n  nodeTypes: Record<string, TreeNodeOptions>;\n  /**\n   * Which of the `nodeTypes` sits at the top level.\n   * Only rows of this type appear as roots; everything else has to be reachable through some type's `allowedChildTypes`.\n   * Examples: Project, group\n   */\n  rootType: string;\n  /**\n   * `true`: The user can create rows\n   * `false`: The tree is read-only for creation\n   * Defaults to true, and takes a formula \u2014 so creation can depend on who is signed in or on the record being viewed.\n   */\n  canAdd?: boolean | string;\n  /**\n   * `true`: The user can edit and delete rows\n   * `false`: Existing rows are read-only\n   * Defaults to true, and takes a formula. Governs deletion as well as editing, so it is the one to gate on ownership.\n   */\n  canEdit?: boolean | string;\n}\n\n// TreeNodeOptions\n{\n  /**\n   * The node types that may be created under this one. An empty list makes the type a leaf.\n   * Controls what the add-child menu offers on a row of this type, so it is how a tree's shape is enforced \u2014 a type absent from every list can never be created below the root.\n   * Examples: [], [\"Task\"], [\"Epic\", \"Task\"]\n   */\n  allowedChildTypes: string[];\n  /**\n   * Material icon name shown on every row of this type.\n   * Examples: task_alt, folder, flag, sticky_note_2\n   */\n  icon?: string;\n  /**\n   * CSS color for this type's icon, distinguishing types at a glance in a mixed tree.\n   * Leave unset to inherit the tree's default icon color.\n   */\n  color?: string;\n  /**\n   * Groups this type with others in the add-child menu, for a tree with enough types that one flat list is unhelpful.\n   * Purely an editor-side grouping \u2014 it has no effect on what may be created, which is `allowedChildTypes`.\n   */\n  category?: string;\n  /** Display name for this node type in the designer and the add-child menu. Falls back to the type name. */\n  label?: string;\n  /**\n   * The record field used as each row's label.\n   * Defaults to the best name field of the bound ObjectType, so it is only worth setting when that guess is wrong or when a different field reads better in a tree.\n   * Examples: title, body, name, text\n   */\n  labelField?: string;\n  /**\n   * Name of a child node of the tree rendered as this type's detail view, with the row's record as its initial data.\n   * Lets one tree show a different detail screen per node type.\n   * Examples: taskDetail\n   */\n  detail?: string;\n  /**\n   * Small chip on each row of this type. A value starting with @ is a formula with the record's fields in scope; anything else is a literal.\n   * An empty or undefined result renders no chip, which is how a badge shows only when it has something to say.\n   * Examples: @status\n   */\n  badge?: string;\n  /**\n   * CSS color for the badge chip. A value starting with @ is a formula with the record's fields in scope; anything else is used literally.\n   * Returning an empty string falls back to the default chip color, so one formula can tint only the states worth calling out.\n   * Examples: @status == 'done' ? '#E6F6EC' : status == 'in-progress' ? '#FFF4E0' : status == 'blocked' ? '#FDE8E8' : status == 'ready' || status == 'assigned' ? '#E8F0FE' : ''\n   */\n  badgeColor?: string;\n  /**\n   * Fields that render read-only in this type's detail form. A literal array locks them for every record; a value starting with @ is a formula with the record's fields in scope returning the list per record.\n   * A locked field still round-trips on save, so its value is never lost; it just cannot be typed into. Nothing is locked on a new record. Use it when some fields are owned by an external sync (a mirror, an import) and editing them would silently revert.\n   * Examples: [\"status\", \"priority\"], @origin == 'gh-mirror' ? ['title', 'status'] : []\n   */\n  lockedFields?: string | string[];\n  /**\n   * `true`: Rows of this type can be edited (subject to the tree's canEdit)\n   * `false`: Rows of this type are read-only\n   * Takes a formula with the record's fields in scope, so one type can be editable for some records and read-only for others: ownership, status, origin. The tree-level canEdit still wins when false.\n   * Examples: @origin != 'gh-mirror'\n   */\n  canEdit?: boolean | string;\n}",
    examples: [],
    pitfalls: ["A data-bound `tree` renders stored RECORDS, not authored child nodes: `nodeTypes` keys are **ObjectType names** and the hierarchy is linked by each child record's parent link (below), never by `_children`. `rootType` must itself be one of the `nodeTypes` keys.", 'Always set the data block explicitly \u2014 `"data": { "source": "storage", "scope": "global" }`. With no `data.source` the tree is UNBOUND: it renders "This tree isn\'t connected to a data source yet" and the record-mode checks below are skipped rather than reported, so the mistake is silent. `"source": "embedded"` renders the seeded `objectInstances` and IS writable: add/edit lands in a per-browser local overlay, so rows reappear and survive reload on that device but are never shared with anyone else. Use `storage` + `scope` when records must be visible across devices or users. (An earlier version of this bullet said embedded writes "never appear" \u2014 that was WRONG since the local rail landed.)', '`nodeTypes` keys are ObjectType names and each value is a `TreeNodeOptions` (shape above) \u2014 e.g. `"nodeTypes": { "Project": { "allowedChildTypes": ["Epic", "Task"], "icon": "folder" }, "Epic": { "allowedChildTypes": ["Task"] }, "Task": { "allowedChildTypes": [], "labelField": "title" } }`. A leaf type declares `allowedChildTypes: []`.', 'Child records link to their parent through the platform-written `_parent: { "id": \u2026, "type": \u2026 }` member \u2014 declare NOTHING for it. A child ObjectType is just its own fields, e.g. `{ "type": "objectType", "name": "Task", "fields": [{ "name": "title", "type": "text" }] }`. The tree\'s Add and Move controls write `_parent` for you; never declare a field named `_parent` (it is reserved and rejected).', 'Seed data and scripts write the link as an object: a seeded child is `{ "id": "Task-1", "title": "Ship it", "_parent": { "id": "Epic-1", "type": "Epic" } }`, a script creates one with `publishData({type: "Task", data: {title: t, _parent: {id: id, type: type}}})` from the parent\'s scope (`id` and `type` there are the parent record\'s own). With only a bare id, pass the parent\'s type along with it \u2014 `byId` needs the type name, so it cannot recover one; a `_parent` with no `type` still links, but `t._parent.type` then reads nothing. A root record has no `_parent` at all.', "Formulas read the link as `t._parent.id` and `t._parent.type` \u2014 children of the record in scope: `filter($data.Task, (t) => t._parent.id == id)`; Tasks placed directly under an Epic: `filter($data.Task, (t) => t._parent.type == 'Epic')`. Reading `_parent.id` on a root record is `undefined`, not an error.", 'The explicit alternative, still supported: declare ONE field whose **`type` is the literal `"parentRef"`** \u2014 e.g. `"fields": [{ "name": "title", "type": "text" }, { "name": "parentId", "type": "parentRef" }]` \u2014 and that type\'s records then link through `parentId` (a plain id string) INSTEAD of `_parent`. The field\'s TYPE is what counts, not its name. Pick one spelling per type: on a type with a `parentRef` field a written `_parent` is ignored (and a script literal doing it is rejected), and two `parentRef` fields on one type are ambiguous and rejected.', "Every declared `nodeTypes` key must be reachable from `rootType` by walking `allowedChildTypes` \u2014 an unreachable type is dead config and is flagged.", "Never emit `value` or `expanded` children on a tree. Those are builder-injected admin-designer state; a data-bound tree holds no node values and reads every row from records.", "`data.whereClause` filters ROOT records only. Descendants are always reached through their parent link, so a clause cannot prune a subtree.", "`labelField` names the record field used as the row label; omit it and the ObjectType's best name field is used. A typo degrades silently to that fallback instead of erroring, so check the name against the ObjectType's fields.", '`detail` names a node nested INSIDE the tree\'s own `_children` array \u2014 e.g. `{ "type": "tree", "nodeTypes": { "Task": { "detail": "taskDetail" } }, "_children": [{ "type": "content", "name": "taskDetail", "htmlTemplate": "@`\u2026`" }] }`. This is NOT the list master-detail pattern: a screen declared elsewhere in the app (say under a `components` folder) will NOT be found, and the tree needs no cursor-linked detail screen. Omit `detail` entirely and a default form is generated from the ObjectType\'s fields \u2014 prefer that unless a custom layout is actually wanted.', 'One level can mix types: `"allowedChildTypes": ["Epic", "Task"]` offers the user a type picker when adding a child.']
  },
  "variable": {
    type: "variable",
    schema: '{\n  type: "variable";\n}',
    examples: [
      {
        "excerpt": `{
  "id": "variable-1",
  "type": "variable",
  "name": "currentContact",
  "compute": "@byId('Contact', $args.id)"
}`,
        "source": "list-detail"
      },
      {
        "excerpt": `{
  "id": "variable-1",
  "type": "variable",
  "name": "rec",
  "compute": "@byId('Recipe', $args.id) || ({})"
}`,
        "source": "save-record"
      }
    ],
    pitfalls: []
  }
};

// src/validation/aiChatSteps.ts
var isObj7 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var TURNS_MIN = 1;
var TURNS_MAX = 100;
var TURNS_DEFAULT = 20;
var isAiChatStep = (value) => typeof value.type === "string" && isVariant({ type: value.type, variant: value.variant }, FLOW_STEP, "aiChat");
var checkStep = (value, path, errors) => {
  const name = typeof value.name === "string" ? value.name : "(unnamed)";
  const prompt = value.promptTemplate;
  if (typeof prompt !== "string" || prompt.trim() === "") {
    errors.push({
      path: pathStr([...path, "promptTemplate"]),
      kind: "aiChat-missing-prompt",
      message: `AI chat step \`${name}\` has no \`promptTemplate\`. The assistant opens FIRST, from the brief \u2014 with nothing to open from, the conversation starts on a model improvising its own persona.`,
      fixHint: `Write the brief as instructions TO the assistant (who it is, who the user is, what to do) in a backtick template so earlier steps' values interpolate: \`"promptTemplate": "@\`You are a warm \${lang} tutor. Keep turns to 2\u20133 sentences and end each with a question.\`"\`. The user never sees this text, only the assistant's opening turn.`,
      severity: "warning"
    });
  }
  const turns = value.maxUserTurns;
  if (typeof turns === "number" && !(turns >= TURNS_MIN && turns <= TURNS_MAX)) {
    errors.push({
      path: pathStr([...path, "maxUserTurns"]),
      kind: "aiChat-turns-range",
      message: `AI chat step \`${name}\` sets \`maxUserTurns\` to ${turns}; it must be between ${TURNS_MIN} and ${TURNS_MAX}. The cap ends the conversation after that many user turns and is what bounds the step's cost, transcript size and model context.`,
      fixHint: `Set a literal between ${TURNS_MIN} and ${TURNS_MAX}, or omit the field for the default of ${TURNS_DEFAULT}. Hitting the cap behaves exactly like the user tapping Done.`,
      severity: "warning"
    });
  }
  const speechLang = value.speechLang;
  const speak = value.speakReplies;
  if (typeof speechLang === "string" && speechLang.trim() !== "" && (speak === void 0 || speak === false)) {
    errors.push({
      path: pathStr([...path, "speechLang"]),
      kind: "aiChat-speech-lang-unused",
      message: `AI chat step \`${name}\` sets \`speechLang\` to \`${speechLang}\` but \`speakReplies\` is ${speak === false ? "false" : "not set"}, so the language is never read \u2014 replies are not spoken at all.`,
      fixHint: `Set \`"speakReplies": true\` to have replies spoken in that language, or remove \`speechLang\` if spoken replies were not intended. A formula \`speakReplies\` (\`@\u2026\`) is accepted as is.`,
      severity: "warning"
    });
  }
};
var walk2 = (value, path, errors) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walk2(c, [...path, i], errors));
    return;
  }
  if (!isObj7(value)) return;
  if (isAiChatStep(value)) checkStep(value, path, errors);
  for (const [key, child] of Object.entries(value)) {
    if (key === "data") continue;
    walk2(child, [...path, key], errors);
  }
};
var validateAiChatSteps = (json) => {
  if (!isObj7(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj7(appConfig3)) return [];
  const errors = [];
  walk2(appConfig3, ["appConfig"], errors);
  return errors;
};

// src/validation/flowRepeat.ts
var isObj8 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var REPEAT2 = "repeat";
var FLOW2 = "flow";
var SCREEN2 = "screen";
var typeOf = (v) => typeof v.type === "string" ? resolveNodeType(v.type) : "";
var nameOf2 = (v) => typeof v.name === "string" ? v.name : "(unnamed)";
var childrenOf3 = (v) => Array.isArray(v._children) ? v._children : [];
var inputsUnder = (v, path) => childrenOf3(v).flatMap((c, i) => {
  if (!isObj8(c)) return [];
  const p = [...path, "_children", i];
  const own = isInputFieldType(typeOf(c)) && typeof c.name === "string" ? [{ name: c.name, path: p }] : [];
  return [...own, ...inputsUnder(c, p)];
});
var checkRepeat = (value, path, errors) => {
  const name = nameOf2(value);
  const seen = /* @__PURE__ */ new Map();
  childrenOf3(value).forEach((c, i) => {
    if (!isObj8(c)) return;
    const p = [...path, "_children", i];
    if (typeOf(c) !== SCREEN2) {
      errors.push({
        path: pathStr(p),
        kind: "repeat-child-not-screen",
        message: `Repeat \`${name}\` has a \`${String(c.type)}\` child \`${nameOf2(c)}\`. A repeat's children are the screens of one pass; anything else has no screen to sit on and is never shown.`,
        fixHint: `Put inputs and content inside a \`screen\` child of the repeat (one screen per page of the pass). Steps that run after every pass belong after the repeat, as siblings in the flow.`,
        severity: "error"
      });
      return;
    }
    for (const input of inputsUnder(c, p)) {
      const first = seen.get(input.name);
      if (first !== void 0) {
        errors.push({
          path: pathStr(input.path),
          kind: "repeat-duplicate-input",
          message: `Repeat \`${name}\` has two inputs named \`${input.name}\` (on screens \`${first}\` and \`${nameOf2(c)}\`). A pass's screens merge into ONE object per pass, so one answer would silently overwrite the other.`,
          fixHint: `Give each input in the repeat's screens its own name.`,
          severity: "error"
        });
      } else {
        seen.set(input.name, nameOf2(c));
      }
    }
  });
};
var walk3 = (value, path, parentType, insideRepeat, errors) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walk3(c, [...path, i], parentType, insideRepeat, errors));
    return;
  }
  if (!isObj8(value)) return;
  const type14 = typeOf(value);
  const isRepeat = type14 === REPEAT2;
  if (isRepeat) {
    const name = nameOf2(value);
    if (insideRepeat) {
      errors.push({
        path: pathStr(path),
        kind: "repeat-nested",
        message: `Repeat \`${name}\` sits inside another repeat. Repeats don't nest yet: a pass inside a pass has no way to navigate.`,
        fixHint: `Flatten it: ask for the inner items in a later step of the flow, or collect them as a list input on the pass's screen.`,
        severity: "error"
      });
    } else if (parentType !== FLOW2) {
      errors.push({
        path: pathStr(path),
        kind: "repeat-outside-flow",
        message: `Repeat \`${name}\` is not a direct child of a flow. The flow is what walks a pass's screens and draws the "Add another" summary; anywhere else nothing ever adds a pass.`,
        fixHint: `Move it to be a step of a \`flow\` ({"type": "flow", "_children": [\u2026, {"type": "repeat", \u2026}, \u2026]}). For a list that is shown rather than asked, use a repeating subsection.`,
        severity: "error"
      });
    }
    checkRepeat(value, path, errors);
  }
  for (const [key, child] of Object.entries(value)) {
    if (key === "data") continue;
    walk3(
      child,
      [...path, key],
      key === "_children" ? type14 : parentType,
      insideRepeat || isRepeat,
      errors
    );
  }
};
var validateFlowRepeats = (json) => {
  if (!isObj8(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj8(appConfig3)) return [];
  const errors = [];
  walk3(appConfig3, ["appConfig"], "", false, errors);
  return errors;
};

// src/validation/seedRecords.ts
var isObj9 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var forEachSeedRecord = (objectInstances, visit) => {
  const walk8 = (node2) => {
    if (Array.isArray(node2)) {
      for (const c of node2) walk8(c);
      return;
    }
    if (!isObj9(node2) || typeof node2.type !== "string") return;
    if (node2.type === "folder") walk8(node2._children);
    else visit(node2);
  };
  walk8(isObj9(objectInstances) ? objectInstances._children : objectInstances);
};

// src/validation/scopeModel.ts
var JS_GLOBALS = ["undefined", "NaN", "Infinity"];
var ENGINE_CALLEES = ["reduce", "byId"];
var HOF_MAGIC_VARS = ["it", "i", "acc", "arr"];
var HOF_NAMES3 = /* @__PURE__ */ new Set([
  "map",
  "filter",
  "forEach",
  "reduce",
  "times",
  "every",
  "some",
  "find",
  "findIndex",
  "count",
  "groupBy",
  "sumBy"
]);
var AUTO_INJECTED_FIELDS2 = [...RECORD_META_FIELDS];
var isObj10 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var collectNodeNames2 = (root, out) => {
  if (Array.isArray(root)) {
    for (const c of root) collectNodeNames2(c, out);
    return;
  }
  if (!isObj10(root)) return;
  if (typeof root.type === "string" && typeof root.name === "string") {
    out.add(root.name);
  }
  const children2 = root._children;
  if (Array.isArray(children2)) collectNodeNames2(children2, out);
};
var collectConstNodes = (json) => {
  const out = /* @__PURE__ */ new Map();
  const walk8 = (v, path) => {
    if (Array.isArray(v)) {
      v.forEach((c, i) => walk8(c, [...path, i]));
      return;
    }
    if (!isObj10(v)) return;
    const { type: type14, name } = v;
    if (type14 === CONST && typeof name === "string" && name.length > 0 && !out.has(name)) {
      out.set(name, { name, value: v.value, path: [...path] });
    }
    for (const [k, c] of Object.entries(v)) walk8(c, [...path, k]);
  };
  walk8(json, []);
  return out;
};
var collectKeysDeep = (v, out, depth = 0) => {
  if (depth > 8) return;
  if (Array.isArray(v)) {
    for (const c of v) collectKeysDeep(c, out, depth + 1);
    return;
  }
  if (!isObj10(v)) return;
  for (const [k, c] of Object.entries(v)) {
    out.add(k);
    collectKeysDeep(c, out, depth + 1);
  }
};
var collectKnownNames = (json) => {
  const root = isObj10(json) ? json : {};
  const known = /* @__PURE__ */ new Set([
    ...CALLABLE_FUNCTION_NAMES,
    ...HOF_NAMES3,
    ...ENGINE_CALLEES,
    ...JS_GLOBALS,
    ...APP_CONTEXT_NAMES,
    ...AUTO_INJECTED_FIELDS2,
    ...HOST_FUNCTIONS.map((h) => h.name)
  ]);
  collectNodeNames2(root.appConfig, known);
  for (const ot of sectionNodes(root.objectTypes)) {
    const fields = ot.fields;
    if (!Array.isArray(fields)) continue;
    for (const f of fields) {
      if (isObj10(f) && typeof f.name === "string") known.add(f.name);
    }
  }
  forEachSeedRecord(root.objectInstances, (rec) => collectKeysDeep(rec, known));
  for (const p of sectionNodes(root.plugins)) {
    if (typeof p.name === "string") known.add(p.name);
    if (Array.isArray(p.parameters)) {
      for (const param of p.parameters) {
        if (isObj10(param) && typeof param.name === "string") {
          known.add(param.name);
        }
      }
    }
  }
  return known;
};
var isAstNode5 = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var arrowParamNames2 = (arrow) => {
  const params = arrow.params;
  if (!Array.isArray(params)) return [];
  return params.filter(isAstNode5).filter((p) => p.type === "Identifier" && typeof p.name === "string").map((p) => String(p.name));
};
var hofNameOfCall = (call) => {
  const callee = call.callee;
  if (!isAstNode5(callee)) return null;
  if (callee.type === "Identifier" && typeof callee.name === "string") {
    return HOF_NAMES3.has(callee.name) ? callee.name : null;
  }
  if (callee.type === "MemberExpression" && !callee.computed) {
    const prop5 = callee.property;
    if (isAstNode5(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string" && HOF_NAMES3.has(prop5.name)) {
      return prop5.name;
    }
  }
  return null;
};
var collectUnknownIdentifiers = (ast, known, initialScope = []) => {
  const out = [];
  const read = (node2, scope) => {
    const name = node2.name;
    if (typeof name !== "string") return;
    if (name.startsWith("$")) return;
    if (known.has(name) || scope.has(name)) return;
    out.push({ name, node: node2 });
  };
  const walk8 = (n, scope) => {
    if (!isAstNode5(n)) return;
    switch (n.type) {
      case "Identifier":
        read(n, scope);
        return;
      case "Literal":
      case "ThisExpression":
        return;
      case "MemberExpression":
        walk8(n.object, scope);
        if (n.computed) walk8(n.property, scope);
        return;
      case "LetExpression": {
        walk8(n.init, scope);
        const inner = new Set(scope);
        if (typeof n.id === "string") inner.add(n.id);
        walk8(n.body, inner);
        return;
      }
      case "BinaryExpression": {
        if (n.operator === "=") {
          walk8(n.right, scope);
          const target = n.left;
          if (isAstNode5(target) && target.type === "Identifier" && typeof target.name === "string") {
            scope.add(target.name);
          } else {
            walk8(target, scope);
          }
          return;
        }
        walk8(n.left, scope);
        walk8(n.right, scope);
        return;
      }
      case "ArrowFunctionExpression": {
        const inner = new Set(scope);
        for (const p of arrowParamNames2(n)) inner.add(p);
        walk8(n.body, inner);
        return;
      }
      case "CallExpression": {
        walk8(n.callee, scope);
        const hof = hofNameOfCall(n);
        const args = Array.isArray(n.arguments) ? n.arguments : [];
        for (const arg of args) {
          if (hof && isAstNode5(arg) && arg.type !== "ArrowFunctionExpression") {
            const inner = new Set(scope);
            for (const v of HOF_MAGIC_VARS) inner.add(v);
            walk8(arg, inner);
          } else {
            walk8(arg, scope);
          }
        }
        return;
      }
      case "MatchExpression": {
        if (n.scrutinee !== void 0) walk8(n.scrutinee, scope);
        const arms = Array.isArray(n.arms) ? n.arms : [];
        for (const arm of arms) {
          if (!isAstNode5(arm)) continue;
          const test = arm.test;
          const isDefault = isAstNode5(test) && test.type === "Identifier" && test.name === "_";
          if (!isDefault) walk8(test, scope);
          walk8(arm.consequent, scope);
        }
        return;
      }
      case "ObjectExpression": {
        const props = Array.isArray(n.properties) ? n.properties : [];
        for (const prop5 of props) {
          if (!isAstNode5(prop5)) continue;
          if (prop5.type === "SpreadElement") {
            walk8(prop5.argument, scope);
            continue;
          }
          if (prop5.computed) walk8(prop5.key, scope);
          if (prop5.shorthand) {
            if (isAstNode5(prop5.key)) read(prop5.key, scope);
          } else {
            walk8(prop5.value, scope);
          }
        }
        return;
      }
      case "TaggedTemplateExpression":
        walk8(n.tag, scope);
        walk8(n.quasi, scope);
        return;
      default: {
        for (const [k, v] of Object.entries(n)) {
          if (k === "parent") continue;
          if (Array.isArray(v)) {
            for (const c of v) walk8(c, scope);
          } else if (isAstNode5(v)) {
            walk8(v, scope);
          }
        }
      }
    }
  };
  walk8(ast, new Set(initialScope));
  return out;
};
var collectFunctionNodes2 = (json, path, out) => {
  if (Array.isArray(json)) {
    json.forEach((c, i) => collectFunctionNodes2(c, [...path, i], out));
    return;
  }
  if (!isObj10(json)) return;
  const { type: type14, name, compute, parameters } = json;
  if (type14 === "function" && typeof name === "string" && name.length > 0 && typeof compute === "string" && !out.has(name)) {
    out.set(name, {
      name,
      parameters: typeof parameters === "string" ? parameters : "",
      compute,
      path: [...path]
    });
  }
  for (const [k, v] of Object.entries(json)) {
    collectFunctionNodes2(v, [...path, k], out);
  }
};
var parseBody = (src) => {
  const body = src.startsWith("@") ? src.slice(1) : src;
  try {
    return jsep(body);
  } catch {
    return void 0;
  }
};
var calleeName2 = (node2) => {
  const callee = node2.callee;
  if (!isAstNode5(callee) || callee.type !== "Identifier") return void 0;
  return typeof callee.name === "string" ? callee.name : void 0;
};
var unknownCallsIn = (src, callable) => {
  const found = /* @__PURE__ */ new Set();
  const seen = /* @__PURE__ */ new WeakSet();
  const walk8 = (node2) => {
    if (Array.isArray(node2)) {
      node2.forEach(walk8);
      return;
    }
    if (!isAstNode5(node2)) return;
    if (seen.has(node2)) return;
    seen.add(node2);
    if (node2.type === "CallExpression") {
      const called = calleeName2(node2);
      if (called !== void 0 && !callable.has(called)) found.add(called);
    }
    for (const [k, v] of Object.entries(node2)) {
      if (k === "parent") continue;
      walk8(v);
    }
  };
  const ast = parseBody(src);
  if (ast !== void 0) walk8(ast);
  return [...found].sort();
};
var ruleScopeMissText = (miss, field2, owner) => {
  const { noun } = owner;
  const article2 = noun === "actor" ? "an" : "a";
  const where = miss.viaFn === void 0 ? "" : ` It is read inside function \`${miss.viaFn}\`, reached from this ${noun}'s \`${field2}\` \u2014 a function body sees only its own parameters, builtins, functions and consts.`;
  return {
    message: `${owner.prefix}\`${miss.name}\` is not in ${noun} scope \u2014 ${noun}s see no repo; a value ${article2} ${noun} needs must be a \`const\` node or a function.${where}`,
    fixHint: `If \`${miss.name}\` is a fixed value, declare it as a \`const\` node; if it is derived, compute it in a \`function\` and pass record state in as arguments; otherwise read it off ${owner.recordVars}.`
  };
};
var calleeNodes = (ast) => {
  const nodes2 = /* @__PURE__ */ new WeakSet();
  const seen = /* @__PURE__ */ new WeakSet();
  const walk8 = (node2) => {
    if (Array.isArray(node2)) {
      node2.forEach(walk8);
      return;
    }
    if (!isAstNode5(node2) || seen.has(node2)) return;
    seen.add(node2);
    if (node2.type === "CallExpression" && calleeName2(node2) !== void 0) {
      if (isAstNode5(node2.callee)) nodes2.add(node2.callee);
    }
    for (const [k, v] of Object.entries(node2)) {
      if (k === "parent") continue;
      walk8(v);
    }
  };
  walk8(ast);
  return nodes2;
};
var functionRefsIn = (ast, functions) => {
  const out = /* @__PURE__ */ new Set();
  const seen = /* @__PURE__ */ new WeakSet();
  const walk8 = (node2) => {
    if (Array.isArray(node2)) {
      node2.forEach(walk8);
      return;
    }
    if (!isAstNode5(node2) || seen.has(node2)) return;
    seen.add(node2);
    if (node2.type === "Identifier") {
      if (typeof node2.name === "string" && functions.has(node2.name)) {
        out.add(node2.name);
      }
      return;
    }
    for (const [k, v] of Object.entries(node2)) {
      if (k === "parent") continue;
      const isName = node2.computed !== true && (k === "property" && node2.type === "MemberExpression" || k === "key" && node2.type === "Property");
      if (!isName) walk8(v);
    }
  };
  walk8(ast);
  return out;
};
var ruleScopeMissesIn = (src, known, functions) => {
  const out = [];
  const reported = /* @__PURE__ */ new Set();
  const walkedFns = /* @__PURE__ */ new Set();
  const visit = (source, params, viaFn) => {
    const ast = parseBody(source);
    if (ast === void 0) return;
    const callees = calleeNodes(ast);
    for (const u of collectUnknownIdentifiers(ast, known, params)) {
      if (callees.has(u.node)) continue;
      const key = `${viaFn != null ? viaFn : ""}\0${u.name}`;
      if (reported.has(key)) continue;
      reported.add(key);
      out.push(
        viaFn === void 0 ? { name: u.name } : { name: u.name, viaFn }
      );
    }
    for (const called of functionRefsIn(ast, functions)) {
      const fn = functions.get(called);
      if (fn === void 0 || walkedFns.has(called)) continue;
      walkedFns.add(called);
      visit(fn.compute, parameterNames(fn.parameters), called);
    }
  };
  visit(src, [], void 0);
  return out;
};

// src/validation/typeModel.ts
var isAstNode6 = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var isObj11 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var collectObjectTypeFieldTypes = (json) => {
  const out = /* @__PURE__ */ new Map();
  if (!isObj11(json)) return out;
  for (const ot of sectionNodes(json.objectTypes)) {
    if (typeof ot.name !== "string") continue;
    const fields = /* @__PURE__ */ new Map();
    if (Array.isArray(ot.fields)) {
      for (const f of ot.fields) {
        if (!isObj11(f) || typeof f.name !== "string") continue;
        if (typeof f.type !== "string") continue;
        fields.set(f.name, parseFieldType(f.type, f.isList === true));
      }
    }
    out.set(ot.name, fields);
  }
  return out;
};
var collectOrgFunctionSignatures = (json) => {
  const out = /* @__PURE__ */ new Map();
  const walk8 = (v) => {
    if (Array.isArray(v)) {
      v.forEach(walk8);
      return;
    }
    if (!isObj11(v)) return;
    if (v.type === "function" && typeof v.name === "string" && !out.has(v.name)) {
      const returns = typeof v.returns === "string" ? v.returns.trim() : "";
      out.set(v.name, {
        params: parseParameters(v.parameters),
        returns: returns ? parseFieldType(returns) : void 0
      });
    }
    for (const c of Object.values(v)) walk8(c);
  };
  walk8(json);
  return out;
};
var IMPLICIT_RECORD_FIELDS = RECORD_META_FIELDS;
var sameType = (a, b2) => a.kind !== "invalid" && b2.kind !== "invalid" && formatFieldType(a) === formatFieldType(b2);
var refOf = (t30) => t30.kind === "ref" ? t30 : void 0;
var checkMemberChain = (chain, rootType, fieldTypes) => {
  let current = rootType;
  let written = "";
  for (const node2 of chain) {
    if (!current) return void 0;
    const obj = node2.object;
    if (written === "" && isAstNode6(obj) && obj.type === "Identifier") {
      written = String(obj.name);
    }
    if (node2.computed) {
      const prop6 = node2.property;
      const keyText = isAstNode6(prop6) && prop6.type === "Identifier" ? String(prop6.name) : isAstNode6(prop6) && prop6.type === "Literal" ? JSON.stringify(prop6.value) : "\u2026";
      written += `[${keyText}]`;
      if (current.kind === "list" || current.kind === "map") {
        current = current.elem;
        continue;
      }
      return void 0;
    }
    const prop5 = node2.property;
    if (!isAstNode6(prop5) || prop5.type !== "Identifier") return void 0;
    const field2 = String(prop5.name);
    written += `.${field2}`;
    if (current.kind === "list" || current.kind === "map") {
      return void 0;
    }
    const ref = refOf(current);
    if (!ref) return void 0;
    const fields = fieldTypes.get(ref.name);
    if (!fields) return void 0;
    if (IMPLICIT_RECORD_FIELDS.has(field2)) return void 0;
    const declared = fields.get(field2);
    if (!declared) {
      return {
        chain: written,
        field: field2,
        onType: ref.name,
        candidates: new Set(fields.keys())
      };
    }
    current = declared;
  }
  return void 0;
};
var dataTypeOf = (n) => {
  if (isAstNode6(n) && n.type === "MemberExpression" && !n.computed && isAstNode6(n.object) && n.object.type === "Identifier" && n.object.name === "$data" && isAstNode6(n.property) && n.property.type === "Identifier") {
    return String(n.property.name);
  }
  return void 0;
};
var calleeName3 = (call) => {
  const c = call.callee;
  if (!isAstNode6(c)) return void 0;
  if (c.type === "Identifier") return String(c.name);
  if (c.type === "MemberExpression" && !c.computed && isAstNode6(c.property)) {
    return String(c.property.name);
  }
  return void 0;
};
var ELEMENT_HOFS = /* @__PURE__ */ new Set(["find"]);
var LIST_HOFS = /* @__PURE__ */ new Set(["filter", "sort", "reverse", "slice", "concat"]);
var staticArgType = (arg, signatures) => {
  if (!isAstNode6(arg)) return void 0;
  if (arg.type === "Literal") {
    const v = arg.value;
    if (typeof v === "string") return { kind: "builtin", name: "text" };
    if (typeof v === "number") return { kind: "builtin", name: "number" };
    if (typeof v === "boolean") return { kind: "builtin", name: "boolean" };
    return void 0;
  }
  if (arg.type === "TemplateLiteral") return { kind: "builtin", name: "text" };
  const scan = dataTypeOf(arg);
  if (scan) return { kind: "list", elem: { kind: "ref", name: scan } };
  if (arg.type === "MemberExpression" && arg.computed) {
    const t30 = dataTypeOf(arg.object);
    if (t30) return { kind: "ref", name: t30 };
    return void 0;
  }
  if (arg.type === "CallExpression") {
    const name = calleeName3(arg);
    if (!name) return void 0;
    const callee = arg.callee;
    const args = Array.isArray(arg.arguments) ? arg.arguments : [];
    const subject = callee.type === "MemberExpression" ? dataTypeOf(callee.object) : dataTypeOf(args[0]);
    if (subject && ELEMENT_HOFS.has(name))
      return { kind: "ref", name: subject };
    if (subject && LIST_HOFS.has(name)) {
      return { kind: "list", elem: { kind: "ref", name: subject } };
    }
    if (callee.type === "Identifier") {
      const sig = signatures.get(name);
      if ((sig == null ? void 0 : sig.returns) && sig.returns.kind !== "invalid") return sig.returns;
    }
    return void 0;
  }
  return void 0;
};

// src/orgicore/utils/evaluation/formula/formulaFunctions.generated.ts
var FORMULA_FUNCTION_ARITIES = {
  if: { min: 3, max: 3 },
  and: { min: 0, max: null },
  or: { min: 0, max: null },
  length: { min: 1, max: 1 },
  sum: { min: 1, max: 1 },
  avg: { min: 1, max: 1 },
  min: { min: 0, max: null },
  max: { min: 0, max: null },
  first: { min: 1, max: 1 },
  last: { min: 1, max: 1 },
  flatten: { min: 1, max: 1 },
  clamp: { min: 3, max: 3 },
  split: { min: 1, max: 2 },
  join: { min: 1, max: 2 },
  concat: { min: 0, max: null },
  upper: { min: 1, max: 1 },
  lower: { min: 1, max: 1 },
  floor: { min: 1, max: 1 },
  ceil: { min: 1, max: 1 },
  round: { min: 1, max: 1 },
  abs: { min: 1, max: 1 },
  today: { min: 0, max: 0 },
  dateFrom: { min: 1, max: 1 },
  dateCompare: { min: 2, max: 2 },
  formatDate: { min: 1, max: 2 },
  formatPeriod: { min: 1, max: 2 },
  generateId: { min: 1, max: 2 },
  regex: { min: 1, max: 2 },
  fuzzyMatch: { min: 2, max: 2 },
  toJson: { min: 1, max: 2 },
  rand: { min: 1, max: 1 },
  uniq: { min: 1, max: 2 },
  sort: { min: 1, max: 3 },
  shuffle: { min: 2, max: 2 },
  handRank: { min: 1, max: 1 },
  replace: { min: 2, max: 3 },
  omit: { min: 2, max: 2 },
  pick: { min: 2, max: 2 },
  toMap: { min: 2, max: 3 },
  keys: { min: 1, max: 1 },
  values: { min: 1, max: 1 },
  entries: { min: 1, max: 1 },
  mapValues: { min: 2, max: 2 },
  escapeHtml: { min: 1, max: 1 },
  safeCssColor: { min: 1, max: 2 },
  urlEncode: { min: 1, max: 1 },
  html: { min: 1, max: null },
  progressBar: { min: 1, max: 2 },
  progressRing: { min: 1, max: 2 },
  sparkline: { min: 1, max: 2 },
  now: { min: 0, max: 0 },
  nextOccurrence: { min: 3, max: 4 },
  byId: { min: 2, max: 2 },
  forEach: { min: 2, max: 2 },
  filter: { min: 2, max: 2 },
  map: { min: 2, max: 2 },
  reduce: { min: 2, max: 3 },
  times: { min: 2, max: 2 },
  every: { min: 2, max: 2 },
  some: { min: 2, max: 2 },
  find: { min: 2, max: 2 },
  findIndex: { min: 2, max: 2 },
  count: { min: 1, max: 2 },
  groupBy: { min: 2, max: 2 },
  sumBy: { min: 2, max: 2 }
};

// src/validation/printFormula.ts
var isNode3 = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var PRECEDENCE = {
  ";": 1,
  "||": 1,
  "??": 1,
  "=": 2,
  "&&": 2,
  "|": 3,
  "^": 4,
  "&": 5,
  "==": 6,
  "!=": 6,
  "===": 6,
  "!==": 6,
  "<": 7,
  ">": 7,
  "<=": 7,
  ">=": 7,
  "<<": 8,
  ">>": 8,
  ">>>": 8,
  "+": 9,
  "-": 9,
  "*": 10,
  "/": 10,
  "%": 10
};
var precedenceOf = (n) => {
  var _a2;
  if (!isNode3(n)) return Infinity;
  if (n.type === "BinaryExpression") return (_a2 = PRECEDENCE[String(n.operator)]) != null ? _a2 : 0;
  if (n.type === "ConditionalExpression" || n.type === "ArrowFunctionExpression" || n.type === "LetExpression")
    return 0;
  return Infinity;
};
var wrapIf = (cond, s) => cond ? `(${s})` : s;
var printMember = (n) => {
  const object = printFormula(n.object);
  const obj = wrapIf(precedenceOf(n.object) !== Infinity, object);
  return n.computed ? `${obj}[${printFormula(n.property)}]` : `${obj}.${printFormula(n.property)}`;
};
var printTemplate = (n) => {
  const quasis = Array.isArray(n.quasis) ? n.quasis : [];
  const exprs = Array.isArray(n.expressions) ? n.expressions : [];
  let out = "`";
  quasis.forEach((q, i) => {
    const value = q.value;
    out += typeof (value == null ? void 0 : value.raw) === "string" ? value.raw : "";
    if (i < exprs.length) out += "${" + printFormula(exprs[i]) + "}";
  });
  return out + "`";
};
var printFormula = (n) => {
  if (!isNode3(n)) return "\u2026";
  switch (n.type) {
    case "Identifier":
      return String(n.name);
    case "Literal":
      return typeof n.raw === "string" ? n.raw : JSON.stringify(n.value);
    case "ThisExpression":
      return "this";
    case "MemberExpression":
      return printMember(n);
    case "CallExpression": {
      const args = Array.isArray(n.arguments) ? n.arguments : [];
      const callee = printFormula(n.callee);
      return `${wrapIf(precedenceOf(n.callee) !== Infinity, callee)}(${args.map(printFormula).join(", ")})`;
    }
    case "ArrayExpression": {
      const els = Array.isArray(n.elements) ? n.elements : [];
      return `[${els.map(printFormula).join(", ")}]`;
    }
    case "UnaryExpression": {
      const arg = printFormula(n.argument);
      return `${String(n.operator)}${wrapIf(
        precedenceOf(n.argument) !== Infinity,
        arg
      )}`;
    }
    case "BinaryExpression": {
      const p = precedenceOf(n);
      const left4 = printFormula(n.left);
      const right4 = printFormula(n.right);
      const sep = n.operator === ";" ? "; " : ` ${String(n.operator)} `;
      return `${wrapIf(precedenceOf(n.left) < p, left4)}${sep}${wrapIf(
        precedenceOf(n.right) <= p,
        right4
      )}`;
    }
    case "ConditionalExpression":
      return `${wrapIf(
        precedenceOf(n.test) === 0,
        printFormula(n.test)
      )} ? ${printFormula(n.consequent)} : ${printFormula(n.alternate)}`;
    case "TemplateLiteral":
      return printTemplate(n);
    case "ObjectExpression": {
      const props = Array.isArray(n.properties) ? n.properties : [];
      return props.length === 0 ? "{}" : `{ ${props.map(printFormula).join(", ")} }`;
    }
    case "Property": {
      if (n.shorthand) return printFormula(n.value);
      const key = printFormula(n.key);
      return `${n.computed ? `[${key}]` : key}: ${printFormula(n.value)}`;
    }
    case "SpreadElement":
      return `...${printFormula(n.argument)}`;
    case "ArrowFunctionExpression": {
      const params = Array.isArray(n.params) ? n.params : [];
      return `(${params.map(printFormula).join(", ")}) => ${printFormula(n.body)}`;
    }
    case "MatchExpression": {
      const arms = Array.isArray(n.arms) ? n.arms : [];
      const head2 = n.scrutinee !== void 0 ? `match ${printFormula(n.scrutinee)}` : "match";
      return `${head2} { ${arms.map(
        (arm) => isNode3(arm) ? `${printFormula(arm.test)} -> ${printFormula(arm.consequent)}` : "\u2026"
      ).join(", ")} }`;
    }
    case "LetExpression":
      return `let ${String(n.id)} = ${printFormula(n.init)}${n.body ? `; ${printFormula(n.body)}` : ""}`;
    default:
      return "\u2026";
  }
};

// src/validation/idiomHints.ts
var isNode4 = (v) => typeof v === "object" && v !== null && typeof v.type === "string";
var isBinary2 = (n, op) => isNode4(n) && n.type === "BinaryExpression" && n.operator === op;
var isNullLiteral = (n) => isNode4(n) && n.type === "Literal" && n.value === null;
var isPath = (n) => {
  if (!isNode4(n)) return false;
  if (n.type === "Identifier" || n.type === "ThisExpression") return true;
  if (n.type !== "MemberExpression" || !isPath(n.object)) return false;
  if (!n.computed) return true;
  return isPath(n.property) || isNode4(n.property) && n.property.type === "Literal";
};
var samePath = (a, b2) => {
  if (!isNode4(a) || !isNode4(b2) || a.type !== b2.type) return false;
  switch (a.type) {
    case "Identifier":
      return a.name === b2.name;
    case "ThisExpression":
      return true;
    case "Literal":
      return a.value === b2.value;
    case "MemberExpression":
      return Boolean(a.computed) === Boolean(b2.computed) && samePath(a.object, b2.object) && samePath(a.property, b2.property);
    default:
      return false;
  }
};
var pathPrefixes = (n) => n.type === "MemberExpression" && isNode4(n.object) ? [...pathPrefixes(n.object), n] : [n];
var andTerms = (n) => isBinary2(n, "&&") ? [...andTerms(n.left), n.right] : [n];
var isNullCheckOf = (test, op, path) => isBinary2(test, op) && (samePath(test.left, path) && isNullLiteral(test.right) || isNullLiteral(test.left) && samePath(test.right, path));
var guardedPath = (term) => {
  if (isPath(term)) return term;
  if (!isBinary2(term, "!=")) return void 0;
  if (isNullLiteral(term.right) && isPath(term.left)) return term.left;
  if (isNullLiteral(term.left) && isPath(term.right)) return term.right;
  return void 0;
};
var matchNullishGuard = (n) => {
  if (n.type !== "ConditionalExpression") return void 0;
  const { test, consequent, alternate } = n;
  if (isPath(consequent)) {
    const terms = andTerms(test);
    const prefixes = pathPrefixes(consequent);
    const last2 = terms[terms.length - 1];
    const guards = samePath(guardedPath(last2), consequent) && terms.every((t30) => prefixes.some((p) => samePath(guardedPath(t30), p)));
    if (guards)
      return {
        value: consequent,
        fallback: alternate,
        exact: isNullCheckOf(last2, "!=", consequent)
      };
  }
  if (isPath(alternate) && isNullCheckOf(test, "==", alternate))
    return { value: alternate, fallback: consequent, exact: true };
  return void 0;
};
var MAX_SHOWN = 120;
var shorten = (s) => s.length > MAX_SHOWN ? `${s.slice(0, MAX_SHOWN - 1)}\u2026` : s;
var nullishHint = (n, m) => {
  const printed = printFormula(m.fallback);
  const fallback = printed.includes("\u2026") ? "<the fallback>" : precedenceOf(m.fallback) <= 1 ? `(${printed})` : printed;
  const rewrite = `${printFormula(m.value)} ?? ${fallback}`;
  return {
    kind: "formula-prefer-nullish",
    message: `\`${shorten(printFormula(n))}\` is \`${shorten(rewrite)}\` \u2014 member access on nothing is already nothing, so the guard isn't needed.`,
    fixHint: m.exact ? `Write \`${shorten(rewrite)}\`.` : `Write \`${shorten(rewrite)}\`. Unlike the ternary, \`??\` keeps 0, '' and false \u2014 keep the ternary only if those must fall back too.`
  };
};
var isIdent = (n, name) => isNode4(n) && n.type === "Identifier" && n.name === name;
var isNumberLiteral = (n, value) => isNode4(n) && n.type === "Literal" && n.value === value;
var callArgs = (n) => Array.isArray(n.arguments) ? n.arguments : [];
var isFnCall = (n, name) => isNode4(n) && n.type === "CallExpression" && isIdent(n.callee, name);
var isMethodCall = (n, name) => isNode4(n) && n.type === "CallExpression" && isNode4(n.callee) && n.callee.type === "MemberExpression" && !n.callee.computed && isIdent(n.callee.property, name);
var isFilterCall = (n) => isFnCall(n, "filter") || isMethodCall(n, "filter");
var isLengthMember = (n) => isNode4(n) && n.type === "MemberExpression" && !n.computed && isIdent(n.property, "length");
var renameCall = (call, name) => {
  const args = callArgs(call).map(printFormula).join(", ");
  if (isIdent(call.callee, "filter")) return `${name}(${args})`;
  const callee = isNode4(call.callee) ? call.callee : void 0;
  return `${printFormula(callee == null ? void 0 : callee.object)}.${name}(${args})`;
};
var filterUnderLength = (n) => {
  if (isFnCall(n, "length")) {
    const arg = callArgs(n)[0];
    return isFilterCall(arg) ? arg : void 0;
  }
  if (isLengthMember(n) && isFilterCall(n.object)) return n.object;
  return void 0;
};
var ANY_MATCH = {
  ">": 0,
  ">=": 1,
  "!=": 0,
  "!==": 0
};
var NO_MATCH = { "==": 0, "===": 0, "<": 1, "<=": 0 };
var FLIP = {
  ">": "<",
  "<": ">",
  ">=": "<=",
  "<=": ">=",
  "==": "==",
  "===": "===",
  "!=": "!=",
  "!==": "!=="
};
var matchFilterCount = (n) => {
  var _a2;
  if (n.type !== "BinaryExpression") return void 0;
  const op = String(n.operator);
  const tryOrient = (l, o, r) => {
    const filter = filterUnderLength(l);
    if (!filter || !isNode4(r) || r.type !== "Literal") return void 0;
    if (o in ANY_MATCH && isNumberLiteral(r, ANY_MATCH[o]))
      return { filter, any: true };
    if (o in NO_MATCH && isNumberLiteral(r, NO_MATCH[o]))
      return { filter, any: false };
    return void 0;
  };
  return (_a2 = tryOrient(n.left, op, n.right)) != null ? _a2 : FLIP[op] ? tryOrient(n.right, FLIP[op], n.left) : void 0;
};
var flattenConcat = (n) => {
  if (!isFnCall(n, "concat")) return void 0;
  const args = callArgs(n);
  if (!args.some((a) => isFnCall(a, "concat"))) return void 0;
  const flat = (a) => isFnCall(a, "concat") ? callArgs(a).flatMap(flat) : [a];
  return args.flatMap(flat);
};
var show = (n) => shorten(printFormula(n));
var literalEqualityOf = (test) => {
  if (!isBinary2(test, "==")) return void 0;
  const { left: left4, right: right4 } = test;
  if (isPath(left4) && isNode4(right4) && right4.type === "Literal")
    return { path: left4, key: right4 };
  if (isPath(right4) && isNode4(left4) && left4.type === "Literal")
    return { path: right4, key: left4 };
  return void 0;
};
var collectDispatchArms = (tests) => {
  let path;
  const arms = [];
  for (const { test, value } of tests) {
    const eq = literalEqualityOf(test);
    if (!eq) return void 0;
    if (path && !samePath(path, eq.path)) return void 0;
    path = path != null ? path : eq.path;
    arms.push({ key: eq.key, value });
  }
  return path && arms.length >= 2 ? { path, arms } : void 0;
};
var matchTernaryDispatch = (n) => {
  if (n.type !== "ConditionalExpression") return void 0;
  const tests = [];
  const covered = [];
  let cur = n;
  while (isNode4(cur) && cur.type === "ConditionalExpression") {
    tests.push({ test: cur.test, value: cur.consequent });
    if (cur !== n) covered.push(cur);
    cur = cur.alternate;
  }
  const d = collectDispatchArms(tests);
  return d ? { ...d, fallback: { value: cur }, covered } : void 0;
};
var matchGuardDispatch = (n) => {
  if (n.type !== "MatchExpression" || n.scrutinee !== void 0)
    return void 0;
  const arms = Array.isArray(n.arms) ? n.arms : [];
  const tests = [];
  let fallback;
  for (const arm of arms) {
    if (!isNode4(arm)) return void 0;
    if (isIdent(arm.test, "_")) fallback = { value: arm.consequent };
    else tests.push({ test: arm.test, value: arm.consequent });
  }
  const d = collectDispatchArms(tests);
  return d ? { ...d, fallback, covered: [] } : void 0;
};
var valueMatchRewrite = (d) => {
  const arms = d.arms.map(
    (a) => `${printFormula(a.key)} -> ${printFormula(a.value)}`
  );
  if (d.fallback) arms.push(`_ -> ${printFormula(d.fallback.value)}`);
  return `match ${printFormula(d.path)} { ${arms.join(", ")} }`;
};
var valueMatchHint = (n, d) => {
  const rewrite = valueMatchRewrite(d);
  const what = n.type === "MatchExpression" ? `repeats \`${show(d.path)}\` in every arm` : `is a ${d.arms.length + 1}-way dispatch on \`${show(d.path)}\``;
  return {
    kind: "formula-prefer-value-match",
    message: `\`${show(n)}\` ${what} \u2014 a value \`match\` tests it once.`,
    fixHint: `Write \`${shorten(rewrite)}\`.`
  };
};
var walk4 = (n, out, quiet) => {
  var _a2;
  let childQuiet = quiet;
  const m = matchNullishGuard(n);
  if (m) out.push(nullishHint(n, m));
  const dispatch = quiet.has(n) ? void 0 : (_a2 = matchTernaryDispatch(n)) != null ? _a2 : matchGuardDispatch(n);
  if (dispatch) {
    out.push(valueMatchHint(n, dispatch));
    childQuiet = /* @__PURE__ */ new Set([...childQuiet, ...dispatch.covered]);
  }
  const some = matchFilterCount(n);
  if (some) {
    const rewrite = `${some.any ? "" : "!"}${renameCall(some.filter, "some")}`;
    out.push({
      kind: "formula-prefer-some",
      message: `\`${show(n)}\` counts every match to ask whether there is ${some.any ? "one" : "none"} \u2014 \`some\` stops at the first.`,
      fixHint: `Write \`${shorten(rewrite)}\`.`
    });
    childQuiet = /* @__PURE__ */ new Set([...quiet, n.left, n.right]);
  } else if (n.type === "MemberExpression" && n.computed && isNumberLiteral(n.property, 0) && isFilterCall(n.object)) {
    const rewrite = renameCall(n.object, "find");
    out.push({
      kind: "formula-prefer-find",
      message: `\`${show(n)}\` builds the whole filtered list to read its first item.`,
      fixHint: `Write \`${shorten(rewrite)}\` \u2014 the first match, or nothing.`
    });
  }
  const concatArgs = quiet.has(n) ? void 0 : flattenConcat(n);
  if (concatArgs) {
    out.push({
      kind: "formula-prefer-variadic-concat",
      message: `\`${show(n)}\` nests \`concat\` \u2014 it already takes any number of lists.`,
      fixHint: `Write \`${shorten(
        `concat(${concatArgs.map(printFormula).join(", ")})`
      )}\`.`
    });
    const nested = (a) => isFnCall(a, "concat") ? [a, ...callArgs(a).flatMap(nested)] : [];
    childQuiet = /* @__PURE__ */ new Set([...childQuiet, ...callArgs(n).flatMap(nested)]);
  }
  if (isLengthMember(n) && !quiet.has(n)) {
    out.push({
      kind: "formula-length-member",
      message: `\`${show(n)}\` does not read a list's length \u2014 on a list, \`.length\` maps over the elements (\`[null, \u2026]\`).`,
      fixHint: `Write \`${shorten(
        `length(${printFormula(n.object)})`
      )}\`, which works for lists and strings alike. (Ignore this if the value is a string or a record with a \`length\` field.)`
    });
  }
  if (n.type === "CallExpression" && isLengthMember(n.callee))
    childQuiet = /* @__PURE__ */ new Set([...childQuiet, n.callee]);
  for (const key of Object.keys(n)) {
    if (key === "parent") continue;
    const v = n[key];
    if (isNode4(v)) walk4(v, out, childQuiet);
    else if (Array.isArray(v))
      v.forEach((item) => isNode4(item) && walk4(item, out, childQuiet));
  }
};
var collectIdiomHints = (ast) => {
  const out = [];
  if (isNode4(ast)) walk4(ast, out, /* @__PURE__ */ new Set());
  return out;
};

// src/validation/formulas.ts
var KNOWN_SPECIAL_VARS = /* @__PURE__ */ new Set([
  "$index",
  "$parentIndex",
  "$scopeKey",
  "$state",
  "$data",
  // `$rows('subsectionName')` — repeating-subsection row-collection accessor
  // (#994). A context-resolved callee, so it surfaces here as an identifier.
  "$rows",
  "$args",
  "$variant",
  "$speech",
  "$now",
  // Site identity (#966) — read-only reactive identity context vars.
  "$email",
  "$username",
  "$userKey",
  "$authKind",
  // #2276 — the raw Firebase auth uid (anon included), = `ownerUid` on writes.
  "$authUid",
  // #2442 — third-party connection state (`$connections.spotify`).
  "$connections",
  // #2805 — the visitor's social graph (`$social.friends`, `.following`, …).
  "$social",
  // #2532 — creator-or-admin, for hiding owner-only (`writeAccess: "admins"`) controls.
  "$isOrgAdmin",
  // The visitor's entry in the org's `users` list (by username), else null.
  "$orgUser",
  "$value",
  "$ctx",
  // Span (#649) — read-only, available inside a row matched by a `data.span`.
  // Global rather than path-gated: a span row's subtree can hold any formula,
  // and there is no static way to tell from a path whether a node sits under a
  // spanning subsection. Out of context they resolve undefined, like $index.
  "$spanStart",
  "$spanEnd",
  "$spanPosition",
  // #1632. Omitting it here is the #994/#995 mistake: the gate would reject
  // every formula that uses it, so the resolver would ship unusable.
  "$spanLength"
]);
var isAstNode7 = (v) => typeof v === "object" && v !== null && "type" in v && typeof v.type === "string";
var HOF_MAX_ARITY = {
  map: 2,
  filter: 2,
  forEach: 3,
  reduce: 3,
  times: 1,
  every: 2,
  some: 2,
  find: 2,
  findIndex: 2,
  count: 2,
  groupBy: 2,
  sumBy: 2
};
var ALL_HOST_NAMES = new Set(
  HOST_FUNCTIONS.map((h) => h.name)
);
var SCRIPT_CONTEXT = "flowStep:script";
var HOSTS_BY_CONTEXT = new Map(
  HOST_FN_CONTEXTS.map((c) => [c, new Set(hostNamesForContext(c))])
);
var ASYNC_HOST_NAMES_SET = new Set(ASYNC_HOST_NAMES);
var ROWS_ACCESSOR = "$rows";
var BUILTIN_ARITIES = new Map(
  Object.entries(FORMULA_FUNCTION_ARITIES)
);
var NATIVE_METHOD_NAMES = /* @__PURE__ */ new Set([
  // string + array
  "includes",
  "indexOf",
  "lastIndexOf",
  "slice",
  "at",
  "toString",
  "toLocaleString",
  // string
  "toLowerCase",
  "toUpperCase",
  "trim",
  "trimStart",
  "trimEnd",
  "startsWith",
  "endsWith",
  "replaceAll",
  "substring",
  "substr",
  "charAt",
  "charCodeAt",
  "codePointAt",
  "padStart",
  "padEnd",
  "repeat",
  "localeCompare",
  "normalize",
  "match",
  "matchAll",
  "search",
  // array
  "flat",
  "flatMap",
  "keys",
  "values",
  "entries",
  "reverse",
  "findLast",
  "findLastIndex",
  "push",
  "pop",
  "shift",
  "unshift",
  "splice",
  "fill",
  "copyWithin",
  // number
  "toFixed",
  "toPrecision",
  // RegExp
  "test",
  "exec",
  // Temporal (today() results support date arithmetic member-style)
  "add",
  "subtract",
  "since",
  "until",
  "equals",
  "with",
  "toJSON",
  // Temporal conversions (e.g. a date + time-of-day → epoch ms, #2264)
  "toZonedDateTime",
  "toPlainDateTime",
  "toPlainDate",
  "toPlainTime",
  "toPlainYearMonth",
  "toPlainMonthDay",
  "toInstant",
  "round"
]);
var memberRootIdentifier = (n) => {
  let cur = n;
  while (isAstNode7(cur) && cur.type === "MemberExpression") {
    cur = cur.object;
  }
  if (isAstNode7(cur) && cur.type === "Identifier" && typeof cur.name === "string") {
    return cur.name;
  }
  return void 0;
};
var isStringOperand = (n) => isAstNode7(n) && (n.type === "Literal" && typeof n.value === "string" || n.type === "TemplateLiteral");
var arityPhrase = ({ min, max }) => {
  const word = (n) => `${n} argument${n === 1 ? "" : "s"}`;
  if (max === null) return `at least ${word(min)}`;
  if (min === max) return `exactly ${word(min)}`;
  return `${min} to ${max} arguments`;
};
var collectOrgFunctionArities = (json) => {
  const counts = /* @__PURE__ */ new Map();
  const conflicted = /* @__PURE__ */ new Set();
  walkJson(json, [], null, (val) => {
    if (!isPlainObject5(val)) return;
    if (val.type !== "function") return;
    const name = val.name;
    if (typeof name !== "string" || name.length === 0) return;
    const count = parameterNames(val.parameters).length;
    const existing = counts.get(name);
    if (existing !== void 0 && existing !== count) {
      conflicted.add(name);
    }
    if (existing === void 0) {
      counts.set(name, count);
    }
  });
  for (const name of conflicted) counts.delete(name);
  return counts;
};
var SCAN_HOF_NAMES = /* @__PURE__ */ new Set(["filter", "map", "reduce"]);
var scanCalleeName = (node2) => {
  const callee = node2.callee;
  if (!isAstNode7(callee)) return void 0;
  if (callee.type === "Identifier" && typeof callee.name === "string" && SCAN_HOF_NAMES.has(callee.name)) {
    return callee.name;
  }
  if (callee.type === "MemberExpression") {
    const prop5 = callee.property;
    if (isAstNode7(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string" && SCAN_HOF_NAMES.has(prop5.name)) {
      return prop5.name;
    }
  }
  return void 0;
};
var findDataTypeName = (n) => {
  if (Array.isArray(n)) {
    for (const c of n) {
      const found = findDataTypeName(c);
      if (found !== void 0) return found;
    }
    return void 0;
  }
  if (!isAstNode7(n)) return void 0;
  if (n.type === "MemberExpression") {
    const obj = n.object;
    const prop5 = n.property;
    if (isAstNode7(obj) && obj.type === "Identifier" && obj.name === "$data" && isAstNode7(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string") {
      return prop5.name;
    }
  }
  for (const [k, v] of Object.entries(n)) {
    if (NON_AST_CHILD_KEYS2.has(k)) continue;
    const found = findDataTypeName(v);
    if (found !== void 0) return found;
  }
  return void 0;
};
var collectDataTypeNames = (n, out) => {
  if (Array.isArray(n)) {
    for (const c of n) collectDataTypeNames(c, out);
    return;
  }
  if (!isAstNode7(n)) return;
  if (n.type === "MemberExpression") {
    const obj = n.object;
    const prop5 = n.property;
    if (isAstNode7(obj) && obj.type === "Identifier" && obj.name === "$data" && isAstNode7(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string") {
      out.add(prop5.name);
    }
  }
  for (const [k, v] of Object.entries(n)) {
    if (NON_AST_CHILD_KEYS2.has(k)) continue;
    collectDataTypeNames(v, out);
  }
};
var DATA_SOURCES = /* @__PURE__ */ new Set(["embedded", "storage", "realtime", "callable"]);
var collectAvailableDataTypes = (json) => {
  const available = /* @__PURE__ */ new Set();
  if (!isPlainObject5(json)) return available;
  const walk8 = (n) => {
    if (Array.isArray(n)) {
      for (const c of n) walk8(c);
      return;
    }
    if (!isPlainObject5(n)) return;
    const data = n.data;
    if (isPlainObject5(data) && DATA_SOURCES.has(String(data.source))) {
      if (n.type === TREE && isPlainObject5(n.nodeTypes)) {
        for (const t30 of Object.keys(n.nodeTypes)) available.add(t30);
      } else if (typeof data.dataType === "string") {
        available.add(data.dataType);
      }
    }
    for (const v of Object.values(n)) walk8(v);
  };
  walk8(json.appConfig);
  forEachSeedRecord(json.objectInstances, ({ type: type14 }) => {
    if (typeof type14 === "string") available.add(type14);
  });
  return available;
};
var MENTIONS_DATA = /\$data\s*\./;
var collectDuplicateDataScans = (ast) => {
  const counts = /* @__PURE__ */ new Map();
  const walk8 = (n) => {
    if (Array.isArray(n)) {
      n.forEach(walk8);
      return;
    }
    if (!isAstNode7(n)) return;
    if (n.type === "CallExpression" && scanCalleeName(n) !== void 0) {
      const key = JSON.stringify(n);
      if (key.includes('"name":"$data"')) {
        const entry = counts.get(key);
        if (entry) {
          entry.count++;
        } else {
          counts.set(key, { count: 1, key, node: n });
        }
      }
    }
    for (const [k, v] of Object.entries(n)) {
      if (NON_AST_CHILD_KEYS2.has(k)) continue;
      walk8(v);
    }
  };
  walk8(ast);
  const dupes = [...counts.values()].filter((d) => d.count > 1);
  return dupes.filter(
    (d) => !dupes.some((other) => other !== d && other.key.includes(d.key))
  );
};
var TRANSITION_SCOPE_VARS = /* @__PURE__ */ new Set([
  "$record",
  "$recordId",
  "$target",
  "$records",
  "$params",
  "$now",
  // `$seed` (#1363) is hoisted per event exactly like `$now`, so a
  // `shuffle(arr, $seed)` commits one permutation across transaction retries.
  "$seed",
  // The private records a rule declared it needs (`privateReadUids`, #1369),
  // keyed by the key that loaded them. Added by #1371, the first rule to
  // actually READ private state: #1369 shipped the engine half
  // (`buildRuleContext` puts `$private` in `sharedScope`) but never taught the
  // gate about it, so a `condition` reading a hidden hand failed to publish
  // with `formula-unknown-var` — the one field the read rail exists for.
  "$private"
]);
var BOT_ACTOR_SCOPE_VARS = /* @__PURE__ */ new Set([
  "$record",
  "$recordId",
  "$target",
  "$params",
  "$now",
  "$seed",
  "$seat",
  "$private",
  "$state",
  "$decision"
]);
var BOT_ACTOR_FORMULA_FIELDS = /* @__PURE__ */ new Set([
  "isBotTurn",
  "privateKey",
  "privateReady",
  "state",
  "questions",
  "moveId",
  "move",
  "fallbackMove",
  "delayMs"
]);
var TRANSITION_FORMULA_FIELDS = /* @__PURE__ */ new Set([
  "targetId",
  "condition",
  "next",
  // The private per-player half of a multi-target write (#1362). Shares
  // `next`'s scope exactly — same live `$target`, same transaction — so it
  // only needs to reach the transition-scope allow-list, or every `$target`
  // in a `nextPrivate` reads as an unknown var.
  "nextPrivate",
  // The key list for the private READ half (#1369). Registered here for the
  // same reason as `nextPrivate`: it is an @-formula over `$target`/`$record`,
  // so without membership the whole field skipped the battery — no unknown-var
  // check, no syntax check, no host-fn context check. Nothing else validated
  // it either, which made it the only unchecked formula in a published rule.
  "privateReadUids",
  "rejectionReason",
  // An onTimer rule's deadline expression (#1534). Shares condition's scope
  // minus the input record ($record/$recordId don't exist on a timer firing) —
  // that narrowing is enforced in the transitions stage, which knows the rule's
  // trigger. Here the field only needs to reach the transition-scope allow-list
  // at all, or every `$target` in a dueAt reads as an unknown var.
  "dueAt",
  // A perpetual rule's publish-time first state (#1521). Evaluated by the
  // rules mirror with $now/$params; the transitions stage bars the input
  // record from it, as for every other timer formula.
  "seed",
  // The list-push side effect (#2325). Evaluated after the commit against the
  // committed `$target` — the same scope as `next`, minus `$private` (that
  // narrowing lives in the transitions stage) — so they need the
  // transition-scope allow-list like every other rule formula.
  "notifyTo",
  "notifyTitle",
  "notifyBody",
  "notifyKey",
  "notifyClickVariant",
  // The SMS channel (#2254): the same post-commit scope as the list push,
  // plus a `$private` that its own post-commit read fills (the transitions
  // stage checks the read is declared).
  "smsTo",
  "smsBody",
  "smsReadUids",
  // The archive projection and id (#2324), evaluated by timerFire against the
  // final `$target` in `next`'s scope just before the end-of-life delete.
  "archiveData",
  "archiveId"
]);
var collectRepeatingSubsectionNames = (json) => {
  const names2 = /* @__PURE__ */ new Set();
  walkJson(json, [], null, (val, path) => {
    if (path[0] !== "appConfig") return;
    if (!isPlainObject5(val)) return;
    if (val.type === "subsection" && val.isRepeating === true && typeof val.name === "string" && val.name.length > 0) {
      names2.add(val.name);
    }
  });
  return names2;
};
var validateFormulaSource = (formula, path, ctx) => {
  var _a2, _b, _c, _d, _e, _f;
  const errors = [];
  const isScriptField = ctx.isScript;
  const isTransitionRuleField = ctx.isTransitionRuleField === true;
  const isBotActorField = ctx.isBotActorField === true;
  const subsectionNames = (_a2 = ctx.subsectionNames) != null ? _a2 : /* @__PURE__ */ new Set();
  const hostContext = (_b = ctx.hostContext) != null ? _b : SCRIPT_CONTEXT;
  const contextHosts = (_c = HOSTS_BY_CONTEXT.get(hostContext)) != null ? _c : /* @__PURE__ */ new Set();
  const extraSpecialVars = (_d = ctx.extraSpecialVars) != null ? _d : /* @__PURE__ */ new Set();
  let ast;
  try {
    ast = jsep(formula);
  } catch (err) {
    errors.push({
      path,
      kind: "formula-syntax",
      message: `Formula failed to parse: ${err instanceof Error ? err.message : "unknown"}.`,
      fixHint: "Check for unbalanced `${\u2026}`, missing quotes, or unsupported syntax.",
      severity: "error"
    });
    return errors;
  }
  if (isAstNode7(ast) && ast.type === "Compound") {
    errors.push({
      path,
      kind: "formula-syntax",
      message: "Formula parses as two adjacent expressions with no operator between them, which can never evaluate.",
      fixHint: "Usually a stray backtick inside a template literal \u2014 escape it or use a quote. Otherwise check for a missing operator or `;`.",
      severity: "error"
    });
    return errors;
  }
  walkAstWithContext(ast, null, (node2, parentHof) => {
    var _a3, _b2;
    if (node2.type === "Identifier" && typeof node2.name === "string") {
      const name = node2.name;
      const allowedTransitionVar = isTransitionRuleField && TRANSITION_SCOPE_VARS.has(name) || isBotActorField && BOT_ACTOR_SCOPE_VARS.has(name);
      if (name.startsWith("$") && !KNOWN_SPECIAL_VARS.has(name) && !allowedTransitionVar && !extraSpecialVars.has(name)) {
        errors.push({
          path,
          kind: "formula-unknown-var",
          message: `Formula references unknown special variable \`${name}\`.`,
          fixHint: `Special variables are: ${[...KNOWN_SPECIAL_VARS].join(
            ", "
          )}. Did you mean one of these?`,
          severity: "error"
        });
      }
    }
    if (node2.type === "MatchExpression") {
      const arms = Array.isArray(node2.arms) ? node2.arms : [];
      const hasDefault = arms.some((arm) => {
        if (!isAstNode7(arm)) return false;
        const test = arm.test;
        return isAstNode7(test) && test.type === "Identifier" && test.name === "_";
      });
      if (!hasDefault) {
        errors.push({
          path,
          kind: "match-no-default",
          message: `\`match\` block has no \`_\` default arm \u2014 when no condition matches it evaluates to \`undefined\` silently.`,
          fixHint: "Add a final `_ -> <fallback>` arm.",
          severity: "warning"
        });
      }
    }
    if (node2.type === "BinaryExpression" && node2.operator === "->") {
      errors.push({
        path,
        kind: "formula-syntax",
        message: "`->` is only valid inside a `match { }` block, as `condition -> expression`.",
        fixHint: "Wrap the arms in `match { \u2026 }`, or remove the stray `->`.",
        severity: "error"
      });
    }
    if (node2.type === "CallExpression" && isAstNode7(node2.callee)) {
      const callee = node2.callee;
      if (callee.type === "MemberExpression" && !callee.computed) {
        const prop5 = callee.property;
        const methodName = isAstNode7(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string" ? prop5.name : void 0;
        if (methodName !== void 0 && !CALLABLE_FUNCTION_NAMES.has(methodName) && !NATIVE_METHOD_NAMES.has(methodName)) {
          errors.push({
            path,
            kind: "formula-unknown-method",
            message: `Method \`.${methodName}(...)\` is not a registered builtin or a known native method \u2014 if the value doesn't provide it, the call silently evaluates to \`undefined\`.`,
            fixHint: nearMissHint(
              methodName,
              /* @__PURE__ */ new Set([...CALLABLE_FUNCTION_NAMES, ...NATIVE_METHOD_NAMES]),
              "method"
            ),
            severity: "warning"
          });
        }
      }
    }
    if (ctx.functionParams !== void 0 && ctx.functionParams.size > 0 && node2.type === "BinaryExpression" && node2.operator === "+") {
      const sides = [
        [node2.left, node2.right],
        [node2.right, node2.left]
      ];
      for (const [strSide, memberSide] of sides) {
        if (!isStringOperand(strSide)) continue;
        if (!isAstNode7(memberSide) || memberSide.type !== "MemberExpression") {
          continue;
        }
        const root = memberRootIdentifier(memberSide);
        if (root !== void 0 && ctx.functionParams.has(root)) {
          errors.push({
            path,
            kind: "function-undefined-concat",
            message: `Function body concatenates a field of parameter \`${root}\` into a string \u2014 when the field is absent this renders the literal text "undefined" (a template \`\${}\` slot would render "").`,
            fixHint: `Guard the access with a fallback, e.g. \`(${root}.field ?? '')\`, or return an html tagged template.`,
            severity: "warning"
          });
          break;
        }
      }
    }
    if (node2.type === "CallExpression" && isAstNode7(node2.callee)) {
      const rawArgs = Array.isArray(node2.arguments) ? node2.arguments : [];
      const hasSpread = rawArgs.some(
        (a) => isAstNode7(a) && a.type === "SpreadElement"
      );
      const argCount = rawArgs.length;
      if (!hasSpread && node2.callee.type === "Identifier") {
        const name = typeof node2.callee.name === "string" ? node2.callee.name : "";
        const fnParamCount = (_a3 = ctx.orgFunctions) == null ? void 0 : _a3.get(name);
        const sig = (_b2 = ctx.orgFunctionSignatures) == null ? void 0 : _b2.get(name);
        if (sig) {
          sig.params.forEach((param, i) => {
            var _a4;
            if (!param.type || i >= rawArgs.length) return;
            const declared = parseFieldType(param.type);
            if (declared.kind === "invalid") return;
            const actual = staticArgType(rawArgs[i], (_a4 = ctx.orgFunctionSignatures) != null ? _a4 : /* @__PURE__ */ new Map());
            if (!actual || sameType(actual, declared)) return;
            errors.push({
              path,
              kind: "function-arg-type",
              message: `Function \`${name}\` declares \`${param.name}: ${param.type}\` but argument ${i + 1} is a \`${formatFieldType(actual)}\`.`,
              fixHint: `Pass a \`${formatFieldType(declared)}\` here, or change the parameter's annotation.`,
              severity: "warning"
            });
          });
        }
        if (fnParamCount !== void 0) {
          if (argCount !== fnParamCount) {
            const over = argCount > fnParamCount;
            errors.push({
              path,
              kind: "formula-arity",
              message: `Function \`${name}\` declares ${fnParamCount} parameter(s) but is called with ${argCount} argument(s) \u2014 ${over ? "the extra arguments are silently dropped" : "the missing parameters bind `undefined`"}.`,
              fixHint: over ? `Remove the extra argument(s), or add parameter(s) to the \`${name}\` function node.` : `Pass all ${fnParamCount} argument(s), or drop unused parameter(s) from the \`${name}\` function node.`,
              // Under-application starts as a warning: some bodies
              // deliberately tolerate undefined trailing params (corpus
              // sweep, PR #1854 section).
              severity: over ? "error" : "warning"
            });
          }
        } else {
          const arity = BUILTIN_ARITIES.get(name);
          if (arity && (argCount < arity.min || arity.max !== null && argCount > arity.max)) {
            errors.push({
              path,
              kind: "formula-arity",
              message: `\`${name}(...)\` called with ${argCount} argument(s); it takes ${arityPhrase(arity)}.`,
              fixHint: `Check the formula reference for \`${name}\`'s signature \u2014 extra arguments are silently ignored and missing ones evaluate as \`undefined\`.`,
              severity: "error"
            });
          }
        }
      }
      if (!hasSpread && node2.callee.type === "MemberExpression") {
        const prop5 = node2.callee.property;
        const methodName = isAstNode7(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string" ? prop5.name : "";
        if (methodName in HOF_MAX_ARITY) {
          const arity = BUILTIN_ARITIES.get(methodName);
          if (arity) {
            const min = Math.max(arity.min - 1, 0);
            const max = arity.max === null ? null : arity.max - 1;
            if (argCount < min || max !== null && argCount > max) {
              errors.push({
                path,
                kind: "formula-arity",
                message: `\`.${methodName}(...)\` called with ${argCount} argument(s); method style takes ${arityPhrase({ min, max })} (the subject is the first).`,
                fixHint: `Check the formula reference for \`${methodName}\`'s signature.`,
                severity: "error"
              });
            }
          }
        }
      }
    }
    if (node2.type === "CallExpression" && isAstNode7(node2.callee) && node2.callee.type === "Identifier" && typeof node2.callee.name === "string") {
      const calleeName4 = node2.callee.name;
      if (ALL_HOST_NAMES.has(calleeName4)) {
        if (!isScriptField) {
          errors.push({
            path,
            kind: "host-fn-out-of-context",
            message: `Host function \`${calleeName4}\` is only callable from a script context (a \`flowStep:script\` node's \`script\` field or a Content \`data-script\` attribute) \u2014 found here in a regular formula.`,
            fixHint: `Move the action into a script context, or remove the call from this formula.`,
            severity: "error"
          });
        } else if (!contextHosts.has(calleeName4)) {
          errors.push({
            path,
            kind: "host-fn-out-of-context",
            message: `Host function \`${calleeName4}\` is not available in \`${hostContext}\`.`,
            fixHint: `Available hosts here: ${[...contextHosts].join(", ")}.`,
            severity: "error"
          });
        }
      }
      if (calleeName4 === ROWS_ACCESSOR) {
        const arg = Array.isArray(node2.arguments) ? node2.arguments[0] : void 0;
        const literalName = isAstNode7(arg) && arg.type === "Literal" && typeof arg.value === "string" ? arg.value : void 0;
        if (literalName === void 0) {
          errors.push({
            path,
            kind: "rows-dynamic-name",
            message: `\`$rows(...)\` requires a string-literal subsection name so it can be validated statically.`,
            fixHint: `Pass a literal name, e.g. \`$rows('matchRows')\`, not a variable or expression.`,
            severity: "error"
          });
        } else if (subsectionNames.size === 0) {
          errors.push({
            path,
            kind: "rows-out-of-context",
            message: `\`$rows('${literalName}')\` used but the org declares no repeating subsections \u2014 there is nothing for it to resolve against.`,
            fixHint: `\`$rows\` reads a repeating subsection's per-row state; add a \`subsection\` with \`isRepeating: true\` and a \`data\` binding first.`,
            severity: "error"
          });
        } else if (!subsectionNames.has(literalName)) {
          errors.push({
            path,
            kind: "rows-unknown-subsection",
            message: `\`$rows('${literalName}')\` references a subsection that is not a declared repeating subsection.`,
            fixHint: nearMissHint(
              literalName,
              subsectionNames,
              "repeating subsection"
            ),
            severity: "error"
          });
        }
      }
    }
    if (node2.type === "ArrowFunctionExpression") {
      const rawParams = Array.isArray(node2.params) ? node2.params : [];
      const paramNames = rawParams.filter(isAstNode7).map((p) => typeof p.name === "string" ? p.name : "<?>");
      const gobbledParam = rawParams.some(
        (p) => !isAstNode7(p) || p.type !== "Identifier"
      );
      const gobbledBody = isAstNode7(node2.body) && node2.body.type === "BinaryExpression" && node2.body.operator === ";";
      if (gobbledParam || gobbledBody) {
        errors.push({
          path,
          kind: "formula-arrow-syntax",
          message: gobbledParam ? "`=>` binds looser than `=`, so this arrow swallowed the assignment: `f = (x) => \u2026` parses as `(f = x) => \u2026` and `f` is never assigned." : "`=>` binds looser than `;`, so this arrow's body swallowed the rest of the statements.",
          fixHint: "Wrap the arrow in parentheses: `f = ((x) => x * 2); f(1)`. (An arrow passed directly as a call argument needs no extra parens.)",
          severity: "error"
        });
      }
      const maxArity = parentHof ? HOF_MAX_ARITY[parentHof] : void 0;
      if (typeof maxArity === "number" && paramNames.length > maxArity) {
        errors.push({
          path,
          kind: "formula-arrow-syntax",
          message: `Arrow with ${paramNames.length} param(s) [${paramNames.join(", ")}] exceeds \`.${parentHof}\` arity (max ${maxArity}).`,
          fixHint: `Use up to ${maxArity} positional param(s); they bind to ${parentHof === "reduce" ? "(acc, it, i)" : parentHof === "forEach" ? "(it, i, arr)" : parentHof === "times" ? "(i)" : "(it, i)"}.`,
          severity: "error"
        });
      }
    }
  });
  for (const arrow of collectTerminalArrows(ast)) {
    const rendered = arrow.kind === "formula" ? "this formula" : "a `${\u2026}` slot";
    errors.push({
      path,
      kind: "formula-arrow-syntax",
      message: `An arrow function is the value of ${rendered}, so a lambda is rendered where a value was expected.`,
      fixHint: "Call it, or drop the `(\u2026) =>` and write the expression directly. A lambda is only useful passed to a higher-order function (`map`/`filter`/`reduce`/\u2026) or stored for a later call.",
      severity: "error"
    });
  }
  errors.push(
    ...checkHostArgs(ast, {
      path,
      navTargets: ctx.navTargets,
      flowBearing: ctx.flowBearing,
      objectTypeFieldTypes: ctx.objectTypeFieldTypes
    })
  );
  for (const dup of collectDuplicateDataScans(ast)) {
    const hof = (_e = scanCalleeName(dup.node)) != null ? _e : "scan";
    const typeName = findDataTypeName(dup.node);
    const target = typeName !== void 0 ? `$data.${typeName}` : "$data";
    errors.push({
      path,
      kind: "formula-duplicate-data-scan",
      message: `The same \`${hof}\` over \`${target}\` appears ${dup.count}\xD7 byte-identically in this formula \u2014 each occurrence re-scans every ${typeName != null ? typeName : "$data"} record.`,
      fixHint: "Compute it once \u2014 bind the result in a `variable` node or pass it through a `function` parameter \u2014 and reference that. The engine memoizes memo-safe function calls, not repeated inline scans.",
      severity: "warning"
    });
  }
  for (const hint of collectIdiomHints(ast)) {
    errors.push({ path, ...hint, severity: "warning" });
  }
  if (isAstNode7(ast)) {
    const rebinds = [];
    collectLetRebinds(ast, /* @__PURE__ */ new Set(), rebinds);
    for (const r of rebinds) {
      errors.push({
        path,
        kind: "formula-let-rebind",
        message: r.how === "reassign" ? `\`${r.name}\` is a \`let\` binding and cannot be reassigned.` : `\`${r.name}\` is already \`let\`-bound in this chain and cannot be redeclared.`,
        fixHint: "Bind the new value to a fresh name (`let name2 = \u2026`), or use a plain `name = \u2026` assignment for a mutable local.",
        severity: "error"
      });
    }
  }
  if (ctx.functionParamTypes && ctx.functionParamTypes.size > 0 && ctx.objectTypeFieldTypes && isAstNode7(ast)) {
    const fieldTypes = ctx.objectTypeFieldTypes;
    const reported = /* @__PURE__ */ new Set();
    const visit = (n, shadowed) => {
      var _a3, _b2;
      if (!isAstNode7(n)) return;
      if (n.type === "ArrowFunctionExpression") {
        const params = Array.isArray(n.params) ? n.params : [];
        const inner = new Set(shadowed);
        for (const p of params) {
          if (isAstNode7(p) && p.type === "Identifier") inner.add(String(p.name));
        }
        visit(n.body, inner);
        return;
      }
      if (n.type === "LetExpression") {
        visit(n.init, shadowed);
        const inner = new Set(shadowed);
        if (typeof n.id === "string") inner.add(n.id);
        visit(n.body, inner);
        return;
      }
      if (n.type === "MemberExpression") {
        const chain = [];
        let cur = n;
        while (isAstNode7(cur) && cur.type === "MemberExpression") {
          chain.unshift(cur);
          cur = cur.object;
        }
        if (isAstNode7(cur) && cur.type === "Identifier") {
          const root = String(cur.name);
          const typeStr = (_a3 = ctx.functionParamTypes) == null ? void 0 : _a3.get(root);
          if (typeStr && !shadowed.has(root)) {
            const problem = checkMemberChain(
              chain,
              parseFieldType(typeStr),
              fieldTypes
            );
            if (problem && !reported.has(problem.chain)) {
              reported.add(problem.chain);
              const at = formula.indexOf(problem.chain);
              errors.push({
                path,
                kind: "function-param-unknown-field",
                message: `Function \`${(_b2 = ctx.functionName) != null ? _b2 : "?"}\`: \`${problem.chain}\` reads field \`${problem.field}\`, which objectType \`${problem.onType}\` does not declare${at >= 0 ? ` (at character ${at})` : ""}.`,
                fixHint: nearMissHint(problem.field, problem.candidates, "field"),
                severity: "error"
              });
            }
          }
        }
        for (const link of chain) {
          if (link.computed) visit(link.property, shadowed);
        }
        if (!(isAstNode7(cur) && cur.type === "Identifier")) visit(cur, shadowed);
        return;
      }
      for (const [k, v] of Object.entries(n)) {
        if (k === "parent") continue;
        if (Array.isArray(v)) {
          for (const c of v) visit(c, shadowed);
        } else if (isAstNode7(v)) {
          visit(v, shadowed);
        }
      }
    };
    visit(ast, /* @__PURE__ */ new Set());
  }
  if (ctx.knownNames && isAstNode7(ast)) {
    const seen = /* @__PURE__ */ new Set();
    const localNames = new Set((_f = ctx.functionParams) != null ? _f : []);
    for (const u of collectUnknownIdentifiers(
      ast,
      ctx.knownNames,
      localNames
    )) {
      if (seen.has(u.name)) continue;
      seen.add(u.name);
      errors.push({
        path,
        kind: "formula-unknown-identifier",
        message: `\`${u.name}\` does not resolve to anything in this org \u2014 no node, field, function, plugin, host or context value has that name, so at runtime it evaluates to \`undefined\` silently.`,
        fixHint: u.name === "value" ? (
          // The old content-state prompt taught `setValue(value…)`; the
          // near miss (`values`) would steer the fix the wrong way.
          "A content node reads its own value by its own NAME (a node named `quiz`: `${quiz.index}`, `setValue({index: quiz.index + 1})`). There is no `value` variable."
        ) : nearMissHint(u.name, ctx.knownNames, "name"),
        severity: "warning"
      });
    }
  }
  if (isScriptField) {
    const illegal = [];
    if (isAstNode7(ast)) {
      collectIllegalHostCalls(ast, true, illegal);
    }
    for (const node2 of illegal) {
      const callee = node2.callee;
      const name = isAstNode7(callee) && typeof callee.name === "string" ? callee.name : "<?>";
      errors.push({
        path,
        kind: "host-fn-nested",
        message: `Async host call \`${name}\` appears nested inside another expression. Async host calls must appear at statement top-level or as the RHS of an assignment.`,
        fixHint: "Bind the inner result to a variable first (e.g. `x = publishData(...); use(x)`).",
        severity: "error"
      });
    }
  }
  return errors;
};
var validateScriptSource = (scriptSource, path, subsectionNames = /* @__PURE__ */ new Set(), options = {}) => validateFormulaSource(scriptSource, path, {
  isScript: true,
  subsectionNames,
  ...options
});
var validateHtmlTemplateAttrs = (htmlTemplate, path, subsectionNames, nav, errors) => {
  var _a2, _b;
  const { text, removed } = substituteInterpolations(htmlTemplate);
  const sources = [text, ...interpolatedHtml(removed)];
  for (const m of sources.flatMap((s) => [...s.matchAll(DATA_ATTR_RE)])) {
    const attr = m[1];
    const body = (_b = (_a2 = m[2]) != null ? _a2 : m[3]) != null ? _b : "";
    if (body.trim().length === 0) continue;
    if (attr === "script") {
      errors.push(
        ...validateScriptSource(body, pathToString(path), subsectionNames, {
          ...nav,
          // Not the default `flowStep:script`: `advanceFlow` exists ONLY in
          // the content contexts, so the default refused squiggly's custom
          // flow Submit once #2108 made row-body scripts visible.
          hostContext: "content:data-script"
        })
      );
      continue;
    }
    const argsSource = body.startsWith("@") ? body.slice(1) : body;
    try {
      jsep(argsSource);
    } catch (err) {
      errors.push({
        path: pathToString(path),
        kind: "formula-syntax",
        message: `\`data-args\` attribute failed to parse as a formula: ${err instanceof Error ? err.message : "unknown"}.`,
        fixHint: "A `data-args` value must be a formula evaluating to a plain object, e.g. `data-args=\"@{ id: '${row.id}' }\"`. Check for unbalanced braces or missing quotes.",
        severity: "error"
      });
    }
  }
};
var validateFormulas = (json) => {
  const errors = [];
  const subsectionNames = collectRepeatingSubsectionNames(json);
  const json_ = isPlainObject5(json) ? json : {};
  const navTargets = collectNavTargets(json_.appConfig);
  const flowBearing = collectFlowBearingNames(json_.appConfig);
  const orgFunctions = collectOrgFunctionArities(json);
  const availableDataTypes = collectAvailableDataTypes(json);
  const dataReads = /* @__PURE__ */ new Map();
  const orgFunctionSignatures = collectOrgFunctionSignatures(json);
  const objectTypeFieldTypes = collectObjectTypeFieldTypes(json);
  const knownNames = collectKnownNames(json);
  const notificationKnownNames = /* @__PURE__ */ new Set([
    ...knownNames,
    "crudType"
  ]);
  walkJson(json, [], null, (val, path, parent) => {
    if (typeof val !== "string") return;
    const lastKey = path[path.length - 1];
    const parentType = isPlainObject5(parent) ? parent.type : void 0;
    const parentVariant = isPlainObject5(parent) ? parent.variant : void 0;
    const isScriptField = lastKey === "script" && (parentType === SCRIPT_CONTEXT || typeof parentType === "string" && isVariant(
      { type: parentType, variant: parentVariant },
      FLOW_STEP,
      "script"
    ));
    const isTransitionRuleField = path[0] === "appConfig" && path[1] === "transitions" && typeof lastKey === "string" && TRANSITION_FORMULA_FIELDS.has(lastKey);
    const isBotActorField = path[0] === "appConfig" && path[1] === "botActors" && typeof lastKey === "string" && BOT_ACTOR_FORMULA_FIELDS.has(lastKey);
    if (lastKey === "htmlTemplate" && (val.includes("data-script") || val.includes("data-args"))) {
      validateHtmlTemplateAttrs(
        val,
        path,
        subsectionNames,
        { navTargets, flowBearing, orgFunctions, objectTypeFieldTypes },
        errors
      );
    }
    const underContentActions = lastKey === "script" && path[path.length - 3] === "actions";
    if (underContentActions) return;
    if (lastKey === "htmlTemplate" && needsTemplateWrapper(val) && (parentType === "content" || path[0] === "plugins" && isPlainObject5(parent) && parent.runtime !== "sandboxed")) {
      errors.push({
        path: pathToString(path),
        kind: "template-missing-formula-wrapper",
        message: "This htmlTemplate has `${\u2026}` but no `@` formula marker. It renders correctly (the wrapper is inferred), but the stored form is non-canonical.",
        fixHint: "Either spelling interpolates: `<h1>${title}</h1>` or the canonical `@`<h1>${title}</h1>``.",
        severity: "warning"
      });
    }
    if (!isScriptField && !val.startsWith("@")) return;
    let functionParams;
    let functionParamTypes;
    let functionName;
    if (lastKey === "compute" && parentType === "function") {
      const paramStr = isPlainObject5(parent) ? parent.parameters : void 0;
      const specs = parseParameters(paramStr);
      functionParams = new Set(specs.map((p) => p.name));
      functionParamTypes = new Map(
        specs.flatMap((p) => p.type ? [[p.name, p.type]] : [])
      );
      functionName = isPlainObject5(parent) && typeof parent.name === "string" ? parent.name : void 0;
    }
    const formula = val.startsWith("@") ? val.slice(1) : val;
    if (MENTIONS_DATA.test(formula)) {
      try {
        const names2 = /* @__PURE__ */ new Set();
        collectDataTypeNames(jsep(formula), names2);
        for (const name of names2) {
          if (!dataReads.has(name)) dataReads.set(name, pathToString(path));
        }
      } catch {
      }
    }
    errors.push(
      ...validateFormulaSource(formula, pathToString(path), {
        isScript: isScriptField,
        isTransitionRuleField,
        isBotActorField,
        subsectionNames,
        navTargets,
        flowBearing,
        orgFunctions,
        functionParams,
        functionParamTypes,
        functionName,
        objectTypeFieldTypes,
        orgFunctionSignatures,
        knownNames: path[0] === "appConfig" && path[1] === "notifications" ? (
          // A push rule's `condition` is owned by the notifications
          // stage (#2353), which knows the record type it reads.
          lastKey === "condition" ? void 0 : notificationKnownNames
        ) : (
          // A vision step's `imageSource` is owned by the vision stage
          // (#1651), which reports an unresolvable source as an ERROR
          // rather than this stage's generic warning. Omitting knownNames
          // skips only the unknown-identifier check — syntax, arity and
          // every other formula check still run on the field.
          // A transition rule field is likewise owned by the transitions
          // stage's stricter rule-scope check (#2317), an ERROR: rules see
          // no repo, so a name this union would accept can still be wrong.
          // Bot-actor fields: same, `botactor-unknown-identifier`.
          lastKey === "imageSource" || isTransitionRuleField || isBotActorField ? void 0 : knownNames
        )
      })
    );
  });
  for (const [typeName, path] of dataReads) {
    if (availableDataTypes.has(typeName)) continue;
    errors.push({
      path,
      kind: "undeclared-data-read",
      message: `Formula reads \`$data.${typeName}\` but no node declares \`${typeName}\` as a datasource, and it has no records in \`objectInstances\` \u2014 the collection will be empty at runtime.`,
      // Seeding first: a fresh generated org has no storage rows either, so a
      // storage binding alone still opens on an empty screen (the 2026-10-15
      // chess-tutor /demo quiz).
      fixHint: `If the screen should show ${typeName} records on first load, add 2-4 sample \`${typeName}\` records to \`objectInstances\`. If records only ever come from users, declare the type on a node that needs it, or add a hidden subsection with \`data: { dataType: "${typeName}", source: "storage" }\`.`,
      severity: "warning"
    });
  }
  return errors;
};
var NON_AST_CHILD_KEYS2 = /* @__PURE__ */ new Set(["parent"]);
var collectIllegalHostCalls = (ast, positionIsLegal, out) => {
  if (ast.type === "CallExpression" && isAstNode7(ast.callee) && ast.callee.type === "Identifier" && typeof ast.callee.name === "string" && ASYNC_HOST_NAMES_SET.has(ast.callee.name)) {
    if (!positionIsLegal) out.push(ast);
    if (Array.isArray(ast.arguments)) {
      for (const arg of ast.arguments) {
        if (isAstNode7(arg)) collectIllegalHostCalls(arg, false, out);
      }
    }
    if (isAstNode7(ast.callee)) {
      collectIllegalHostCalls(ast.callee, false, out);
    }
    return;
  }
  if (ast.type === "BinaryExpression" && ast.operator === ";") {
    if (isAstNode7(ast.left)) {
      collectIllegalHostCalls(ast.left, positionIsLegal, out);
    }
    if (isAstNode7(ast.right)) {
      collectIllegalHostCalls(ast.right, positionIsLegal, out);
    }
    return;
  }
  if (ast.type === "LetExpression") {
    const init = ast.init;
    const body = ast.body;
    if (isAstNode7(init)) collectIllegalHostCalls(init, positionIsLegal, out);
    if (isAstNode7(body)) collectIllegalHostCalls(body, positionIsLegal, out);
    return;
  }
  if (ast.type === "BinaryExpression" && ast.operator === "=") {
    if (isAstNode7(ast.left)) collectIllegalHostCalls(ast.left, false, out);
    if (isAstNode7(ast.right)) {
      collectIllegalHostCalls(ast.right, positionIsLegal, out);
    }
    return;
  }
  for (const k of Object.keys(ast)) {
    if (NON_AST_CHILD_KEYS2.has(k)) continue;
    const v = ast[k];
    if (isAstNode7(v)) {
      collectIllegalHostCalls(v, false, out);
    } else if (Array.isArray(v)) {
      for (const c of v) {
        if (isAstNode7(c)) collectIllegalHostCalls(c, false, out);
      }
    }
  }
};
var collectLetRebinds = (ast, bound, out) => {
  if (!isAstNode7(ast)) return;
  if (ast.type === "LetExpression") {
    const id = ast.id;
    if (typeof id === "string" && bound.has(id)) {
      out.push({ name: id, how: "redeclare" });
    }
    collectLetRebinds(ast.init, bound, out);
    const body = ast.body;
    if (body !== void 0) {
      collectLetRebinds(
        body,
        typeof id === "string" ? /* @__PURE__ */ new Set([...bound, id]) : bound,
        out
      );
    }
    return;
  }
  if (ast.type === "BinaryExpression" && ast.operator === "=") {
    const target = ast.left;
    if (isAstNode7(target) && target.type === "Identifier" && typeof target.name === "string" && bound.has(target.name)) {
      out.push({ name: target.name, how: "reassign" });
    }
    collectLetRebinds(ast.right, bound, out);
    return;
  }
  if (ast.type === "ArrowFunctionExpression") {
    const params = ast.params;
    const names2 = Array.isArray(params) ? params.filter(isAstNode7).filter((p) => p.type === "Identifier").map((p) => String(p.name)) : [];
    const inner = new Set(bound);
    for (const nm of names2) inner.delete(nm);
    collectLetRebinds(ast.body, inner, out);
    return;
  }
  for (const k of Object.keys(ast)) {
    if (NON_AST_CHILD_KEYS2.has(k)) continue;
    const v = ast[k];
    if (isAstNode7(v)) {
      collectLetRebinds(v, bound, out);
    } else if (Array.isArray(v)) {
      for (const c of v) collectLetRebinds(c, bound, out);
    }
  }
};
var isPlainObject5 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var walkJson = (v, path, parent, visit) => {
  visit(v, path, parent);
  if (Array.isArray(v)) {
    v.forEach((child, i) => walkJson(child, [...path, i], v, visit));
  } else if (v && typeof v === "object") {
    for (const [k, val] of Object.entries(v)) {
      walkJson(val, [...path, k], v, visit);
    }
  }
};
var collectTerminalArrows = (ast) => {
  const out = [];
  const rightmost = (n) => isAstNode7(n) && n.type === "BinaryExpression" && n.operator === ";" ? rightmost(n.right) : n;
  const result = rightmost(ast);
  if (isAstNode7(result) && result.type === "ArrowFunctionExpression") {
    out.push({ node: result, kind: "formula" });
  }
  const walk8 = (n) => {
    if (!isAstNode7(n)) return;
    if (n.type === "TemplateLiteral" && Array.isArray(n.expressions)) {
      for (const e of n.expressions) {
        if (isAstNode7(e) && e.type === "ArrowFunctionExpression") {
          out.push({ node: e, kind: "slot" });
        }
      }
    }
    for (const v of Object.values(n)) {
      if (Array.isArray(v)) v.forEach(walk8);
      else if (v && typeof v === "object") walk8(v);
    }
  };
  walk8(ast);
  return out;
};
var walkAstWithContext = (ast, parentHof, visit) => {
  if (!isAstNode7(ast)) return;
  visit(ast, parentHof);
  let argHof = null;
  if (ast.type === "CallExpression") {
    const callee = ast.callee;
    if (isAstNode7(callee)) {
      if (callee.type === "MemberExpression") {
        const prop5 = callee.property;
        if (isAstNode7(prop5) && prop5.type === "Identifier" && typeof prop5.name === "string" && prop5.name in HOF_MAX_ARITY) {
          argHof = prop5.name;
        }
      }
      if (callee.type === "Identifier" && typeof callee.name === "string" && callee.name in HOF_MAX_ARITY) {
        argHof = callee.name;
      }
    }
  }
  for (const [k, v] of Object.entries(ast)) {
    const hofForChild = k === "arguments" ? argHof : null;
    if (Array.isArray(v)) {
      v.forEach((c) => walkAstWithContext(c, hofForChild, visit));
    } else if (v && typeof v === "object") {
      walkAstWithContext(v, hofForChild, visit);
    }
  }
};
var pathToString = (path) => path.reduce((acc, s) => {
  if (typeof s === "number") return `${acc}[${s}]`;
  if (acc === "") return String(s);
  return `${acc}.${s}`;
}, "");

// src/prompts/clickAttributes.ts
var CLICK_PATTERNS = [
  {
    attribute: "data-action",
    label: "Declared action",
    html: `<button data-action="openTab" data-param-id="\${id}">Open</button>`,
    purpose: "Invoke a named action declared in the node's `actions` field \u2014 a `script`; to move screens it calls `navigate('screen')`, usually as the last line. `data-param-*` values are render-time interpolated literals, read into `$params` with no click-time evaluation. If the action declares `confirm`, the click asks first and the script runs only on confirm; `data-confirm-title` and `data-confirm-body` override that confirm's wording per element (render-time literals, like `data-param-*` \u2014 they cannot create a confirm the action does not declare). See below",
    status: "current"
  },
  {
    attribute: "data-speak-action",
    label: "Stop speech",
    html: `<a data-speak-action="stop">Stop</a>`,
    purpose: "Stop Web Speech API playback started by `data-speak`. Only `stop` is supported",
    status: "legacy"
  },
  {
    attribute: "data-speak",
    label: "Speak",
    html: `<a data-speak="expr" data-speak-lang="expr" data-speak-rate="expr">Play</a>`,
    purpose: "Speak text via the Web Speech API; the optional `data-speak-lang` and `data-speak-rate` attributes are evaluated as formulas alongside the text",
    status: "legacy"
  },
  {
    attribute: "data-script",
    label: "One-tap action",
    html: `<a data-script='setAppValue("k", "\${id}"); navigate("screen")'>Open</a>`,
    purpose: "Run a script \u2014 app-state, realtime and publish writes \u2014 and optionally navigate, in a single tap with **no flow**. See below",
    status: "legacy"
  },
  {
    attribute: "setValue",
    label: "Set value",
    html: `<a setValue="expression">Text</a>`,
    purpose: "Evaluate expression and set as the current node's value",
    status: "legacy"
  },
  {
    attribute: "href",
    label: "External link",
    html: `<a href="https://example.com" target="_blank">Visit</a>`,
    purpose: "A genuine hyperlink \u2014 `http(s):`, `mailto:`, `tel:` and `/`-absolute URLs are left to the browser",
    status: "current"
  },
  {
    attribute: "href",
    label: "Navigate to screen",
    html: `<a href="screenName">Go</a>`,
    purpose: "Static link to another screen",
    status: "current"
  },
  {
    attribute: "href",
    label: "Go back",
    html: `<a href="@pop">Back</a>`,
    purpose: "Pop the navigation stack",
    status: "legacy"
  },
  {
    attribute: "href",
    label: "Submit form",
    html: `<a href="@submit">Save</a>`,
    purpose: "Submit the current form",
    status: "legacy"
  },
  {
    attribute: "href",
    label: "Navigate with args",
    html: `<a href="screenName" data-args="@{ key: value }">Go</a>`,
    purpose: "Navigate and attach named arguments, readable on the destination as `$args.key`. Pair with `byId('Type', id)` for cross-record links (e.g. linking from one record's detail page to a different record's). Works on `rel=` too",
    status: "legacy"
  },
  {
    attribute: "rel",
    label: "Navigate from list item",
    html: `<a rel="screenName">View</a>`,
    purpose: "From a repeating section \u2014 the destination inherits the current item's scope. Resolves *relative to the clicked row*, so it can only reach a screen nested inside that same subsection",
    status: "legacy"
  },
  {
    attribute: "rel",
    label: "Seed a form target",
    html: `<a rel="formScreen" param="expression">Edit</a>`,
    purpose: "Writes the evaluated value at the destination pointer before navigating. **Forms only** \u2014 a flow reads `data-args`/`$args`, never `param`",
    status: "legacy"
  }
];
var CLICK_DISPATCH_ORDER = CLICK_PATTERNS.map(
  (p) => p.attribute
).filter((attr, i, all) => all.indexOf(attr) === i);
var LEGACY_MIGRATIONS = [
  {
    id: "rel",
    tokens: ["rel="],
    attributes: ["rel"],
    fixHint: 'Declare an action whose script ends with navigate("screen") and reference it via data-action instead.',
    prompt: {
      order: 1,
      line: '`rel="screen"` / `href="screen" data-args=\u2026` \u2192 an action whose script calls `navigate(\'screen\', {id: $params.id})`'
    }
  },
  {
    id: "setValue",
    tokens: ["setValue="],
    attributes: ["setValue"],
    fixHint: "Declare an action whose `script` calls `setValue(...)` and reference it via `data-action` instead.",
    prompt: {
      order: 2,
      line: '`setValue="expr"` \u2192 an action whose `script` calls `setValue(expr)`'
    }
  },
  {
    id: "param",
    tokens: ["param="],
    attributes: ["param"],
    fixHint: "Declare the action's `params` and bind them via `data-param-*` attributes (or pass computed values as the args object of navigate(...)) instead.",
    prompt: {
      order: 7,
      line: '`param="expr"` \u2192 pass the value as a `data-param-*` binding and read it as `$params.<name>`'
    }
  },
  {
    id: "data-script",
    tokens: ["data-script"],
    attributes: ["data-script"],
    fixHint: "Declare an action with a `script` and reference it via `data-action` instead.",
    prompt: {
      order: 3,
      line: "`data-script='\u2026'` \u2192 an action's `script` (same script language, same host functions)"
    }
  },
  {
    // The gate needs a hint that names the args object of navigate(); rel's
    // bullet covers the prompt.
    id: "data-args",
    tokens: ["data-args"],
    attributes: ["data-args"],
    fixHint: "Pass the values as the second argument of navigate('screen', {\u2026}) in the action's script instead."
  },
  {
    id: "data-speak",
    tokens: [
      "data-speak",
      "data-speak-lang",
      "data-speak-rate",
      "data-speak-action"
    ],
    attributes: [
      "data-speak",
      "data-speak-lang",
      "data-speak-rate",
      "data-speak-action"
    ],
    fixHint: "Declare an action whose `script` calls `speak(...)`/`stopSpeech()` and reference it via `data-action` instead.",
    prompt: {
      order: 4,
      line: '`data-speak=\u2026` \u2192 an action whose `script` calls `speak(text, lang?, rate?)`; `data-speak-action="stop"` \u2192 `stopSpeech()`'
    }
  },
  {
    // `href` sentinels: the legacy signal is the attribute's VALUE, not its
    // name, so `hasAttr` cannot see them and the gate does not warn. Named in
    // the prompt all the same — the generator must not emit them.
    id: "href-pop",
    tokens: ['href="@pop"'],
    attributes: [],
    prompt: {
      order: 6,
      line: '`href="@pop"` \u2192 an action whose script is `back()`'
    }
  },
  {
    id: "href-submit",
    tokens: ['href="@submit"'],
    attributes: [],
    prompt: {
      order: 5,
      line: '`href="@submit"` \u2192 an action whose `script` calls `publishData({type, data: {\u2026}})`, naming each input'
    }
  }
];
var LEGACY_MIGRATION_BY_ID = Object.fromEntries(LEGACY_MIGRATIONS.map((m) => [m.id, m]));
var legacyChannelLabel = (m) => m.tokens.length > 1 ? `${m.tokens[0]}*` : `${m.tokens[0]}`;
var LEGACY_CHANNELS = LEGACY_MIGRATIONS.flatMap(
  (m) => [...m.tokens]
);
var LEGACY_REPLACEMENTS = LEGACY_MIGRATIONS.filter(
  (m) => m.prompt !== void 0
).sort((a, b2) => a.prompt.order - b2.prompt.order).map((m) => m.prompt.line);
var INPUT_SYNC = {
  description: "Form inputs inside content sync back to the node's own value automatically (bidirectional).",
  features: [
    {
      html: `<input data-trigger="input">`,
      purpose: "Update on every keystroke (default)"
    },
    {
      html: `<input data-trigger="blur">`,
      purpose: "Update only when the input loses focus"
    }
  ],
  notes: "Checkboxes return booleans, number inputs return numbers, others return strings."
};
var CONTENT_ACTION_SCOPE_VARS = /* @__PURE__ */ new Set([
  "$params"
]);

// src/validation/contentActions.ts
var ACTION_KEYS = /* @__PURE__ */ new Set(["params", "script", "confirm"]);
var CONFIRM_KEYS = /* @__PURE__ */ new Set([
  "title",
  "body",
  "ok",
  "cancel",
  "destructive",
  "info"
]);
var CONTENT_ACTION_CONTEXT = "content:action";
var CONTENT_ACTION_VARS = /* @__PURE__ */ new Set(["$params"]);
var PARAM_ATTR_PREFIX = "data-param-";
var CONFIRM_ATTRS = /* @__PURE__ */ new Set(["data-confirm-title", "data-confirm-body"]);
var validateContentActions = (json) => {
  if (!isObj12(json)) return [];
  const errors = [];
  const env = {
    navTargets: collectNavTargets(json.appConfig),
    flowBearing: collectFlowBearingNames(json.appConfig),
    subsectionNames: collectRepeatingSubsectionNames(json),
    knownNames: collectKnownNames(json),
    objectTypeFieldTypes: collectObjectTypeFieldTypes(json)
  };
  walkForContent(
    json.appConfig,
    ["appConfig"],
    (node2, path) => validateContentNode(node2, path, env, errors)
  );
  for (const { node: plugin2, path: at } of sectionItems(json.plugins)) {
    walkForContent(
      plugin2,
      ["plugins", ...at],
      (node2, path) => validateContentNode(node2, path, env, errors)
    );
  }
  return errors;
};
var walkForContent = (value, path, visit) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForContent(c, [...path, i], visit));
    return;
  }
  if (!isObj12(value)) return;
  if (value.type === "content" || isObj12(value.actions)) {
    visit(value, path);
  }
  const children2 = value._children;
  if (Array.isArray(children2)) {
    walkForContent(children2, [...path, "_children"], visit);
  }
};
var validateContentNode = (node2, path, env, errors) => {
  const actions = isObj12(node2.actions) ? node2.actions : void 0;
  const htmlTemplate = typeof node2.htmlTemplate === "string" ? node2.htmlTemplate : void 0;
  if (actions === void 0 && htmlTemplate === void 0) return;
  if (actions !== void 0) {
    for (const [name, action] of Object.entries(actions)) {
      if (!isObj12(action)) continue;
      validateAction(name, action, [...path, "actions", name], env, errors);
    }
  }
  if (htmlTemplate !== void 0) {
    crossCheckTemplate(htmlTemplate, actions, path, errors);
    checkLegacyChannels(htmlTemplate, path, errors);
  }
};
var validateAction = (name, action, actionPath, env, errors) => {
  for (const key of Object.keys(action)) {
    if (ACTION_KEYS.has(key)) continue;
    if (key === "navigate") {
      errors.push({
        path: pathStr([...actionPath, key]),
        kind: "unknown-field",
        message: `Action \`${name}\`: the declarative \`navigate\` member was removed \u2014 navigation is a host call in \`script\`.`,
        fixHint: "Move it into the script as its last statement: `navigate('screen', {id: $params.id})`, `replaceScreen('screen')` or `back()`.",
        severity: "error"
      });
      continue;
    }
    errors.push({
      path: pathStr([...actionPath, key]),
      kind: "unknown-field",
      message: `Unknown action property \`${key}\` on action \`${name}\`.`,
      fixHint: nearMissHint(key, ACTION_KEYS, "action property"),
      severity: "error"
    });
  }
  validateConfirm(name, action, actionPath, errors);
  const script = action.script;
  if (typeof script === "string" && script.trim().length > 0) {
    errors.push(
      ...validateScriptSource(
        script,
        pathStr([...actionPath, "script"]),
        env.subsectionNames,
        {
          hostContext: CONTENT_ACTION_CONTEXT,
          extraSpecialVars: CONTENT_ACTION_VARS,
          navTargets: env.navTargets,
          flowBearing: env.flowBearing,
          knownNames: env.knownNames,
          objectTypeFieldTypes: env.objectTypeFieldTypes
        }
      )
    );
  }
};
var validateConfirm = (name, action, actionPath, errors) => {
  if (!("confirm" in action)) return;
  const confirm = action.confirm;
  const confirmPath = [...actionPath, "confirm"];
  if (!isObj12(confirm)) {
    errors.push({
      path: pathStr(confirmPath),
      kind: "invalid-type",
      message: `Action \`${name}\`: \`confirm\` must be an object with at least a \`title\`.`,
      fixHint: 'e.g. `"confirm": { "title": "Leave the room?", "ok": "Leave" }`',
      severity: "error"
    });
    return;
  }
  for (const key of Object.keys(confirm)) {
    if (CONFIRM_KEYS.has(key)) continue;
    errors.push({
      path: pathStr([...confirmPath, key]),
      kind: "unknown-field",
      message: `Unknown \`confirm\` property \`${key}\` on action \`${name}\`.`,
      fixHint: nearMissHint(key, CONFIRM_KEYS, "confirm property"),
      severity: "error"
    });
  }
  const title = confirm.title;
  if (typeof title !== "string" || title.trim().length === 0) {
    errors.push({
      path: pathStr([...confirmPath, "title"]),
      kind: "action-confirm-untitled",
      message: `Action \`${name}\`: \`confirm\` needs a non-empty \`title\` \u2014 without it the whole \`confirm\` fails its codec and is dropped, so the action would run WITHOUT asking.`,
      fixHint: 'Give it the question, e.g. `"title": "Delete this list?"`, or remove `confirm` if the action should not ask.',
      severity: "error"
    });
  }
  if (confirm.info === true) {
    for (const key of ["cancel", "destructive"]) {
      if (!(key in confirm)) continue;
      errors.push({
        path: pathStr([...confirmPath, key]),
        kind: "action-confirm-info-dead-option",
        message: `Action \`${name}\`: \`confirm.${key}\` does nothing on an \`info\` confirm \u2014 it has a single OK button and no decision to make.`,
        fixHint: `Remove \`${key}\`, or drop \`info\` if the person should be able to back out.`,
        severity: "warning"
      });
    }
    return;
  }
  const script = action.script;
  if (typeof script !== "string" || script.trim().length === 0) {
    errors.push({
      path: pathStr(confirmPath),
      kind: "action-confirm-noop",
      message: `Action \`${name}\` declares \`confirm\` but has no \`script\` \u2014 confirming would do nothing at all.`,
      fixHint: "Add the `script` the confirmation is protecting, or remove `confirm`.",
      severity: "warning"
    });
  }
};
var crossCheckTemplate = (htmlTemplate, actions, path, errors) => {
  var _a2;
  const declared = new Set(Object.keys(actions != null ? actions : {}));
  const { text, removed } = substituteInterpolations(htmlTemplate);
  const sources = [text, ...interpolatedHtml(removed)];
  const refs = /* @__PURE__ */ new Map();
  let sawDynamicActionRef = false;
  for (const tag of sources.flatMap((s) => scanHtmlTags(s))) {
    if (tag.closing) continue;
    const actionName = readAttr(tag.attrs, "data-action");
    if (actionName === void 0 || actionName.length === 0) continue;
    if (isDynamicAttrValue(actionName)) {
      sawDynamicActionRef = true;
      continue;
    }
    if (!declared.has(actionName)) {
      errors.push({
        path: pathStr([...path, "htmlTemplate"]),
        kind: "action-unknown",
        message: `\`data-action="${actionName}"\` does not name a declared action on this content node \u2014 the click will fail at runtime.`,
        fixHint: declared.size === 0 ? `This content node declares no \`actions\`. Declare \`actions.${actionName}\` or remove the attribute.` : nearMissHint(actionName, declared, "declared action"),
        severity: "error"
      });
      continue;
    }
    const ref = (_a2 = refs.get(actionName)) != null ? _a2 : {
      boundParams: /* @__PURE__ */ new Set(),
      confirmAttrs: /* @__PURE__ */ new Set()
    };
    for (const attr of listAttrNames(tag.attrs)) {
      const lower = attr.toLowerCase();
      if (lower.startsWith(PARAM_ATTR_PREFIX)) {
        ref.boundParams.add(lower.slice(PARAM_ATTR_PREFIX.length));
      }
      if (CONFIRM_ATTRS.has(lower)) ref.confirmAttrs.add(lower);
    }
    refs.set(actionName, ref);
  }
  for (const [actionName, ref] of refs) {
    const action = actions == null ? void 0 : actions[actionName];
    if (ref.confirmAttrs.size > 0 && isObj12(action) && !isObj12(action.confirm)) {
      errors.push({
        path: pathStr([...path, "htmlTemplate"]),
        kind: "action-confirm-mismatch",
        message: `${[...ref.confirmAttrs].map((a) => `\`${a}\``).join(" and ")} ${ref.confirmAttrs.size > 1 ? "are" : "is"} bound on a \`data-action="${actionName}"\` element, but \`${actionName}\` declares no \`confirm\` \u2014 the attribute is ignored and the action runs without asking.`,
        fixHint: `Add \`confirm\` to action \`${actionName}\` (its \`title\` is the default the attribute overrides), or drop the attribute.`,
        severity: "warning"
      });
    }
    if (!isObj12(action) || !Array.isArray(action.params)) continue;
    const declaredParams = action.params.filter(
      (p) => typeof p === "string" && p.length > 0
    );
    const declaredSet = new Set(declaredParams);
    for (const bound of ref.boundParams) {
      if (!declaredSet.has(bound)) {
        errors.push({
          path: pathStr([...path, "htmlTemplate"]),
          kind: "action-param-mismatch",
          message: `\`data-param-${bound}\` is bound on a \`data-action="${actionName}"\` element but \`${bound}\` is not in the action's declared \`params\`. (HTML lowercases attribute names, so \`$params\` keys are lowercase.)`,
          fixHint: nearMissHint(bound, declaredSet, "declared param"),
          severity: "warning"
        });
      }
    }
    for (const param of declaredParams) {
      if (!ref.boundParams.has(param)) {
        errors.push({
          path: pathStr([...path, "actions", actionName, "params"]),
          kind: "action-param-mismatch",
          message: `Action \`${actionName}\` declares param \`${param}\`, but no \`data-action="${actionName}"\` element in the template binds \`data-param-${param}\` \u2014 \`$params.${param}\` will be undefined at click time.`,
          fixHint: `Bind it in the template (e.g. \`data-param-${param}="\${row.${param}}"\`) or remove \`${param}\` from \`params\`.`,
          severity: "warning"
        });
      }
    }
  }
  if (!sawDynamicActionRef) {
    for (const name of declared) {
      if (!refs.has(name)) {
        errors.push({
          path: pathStr([...path, "actions", name]),
          kind: "action-unused",
          message: `Action \`${name}\` is declared but never referenced by a \`data-action\` in this content node's htmlTemplate.`,
          fixHint: `Add \`data-action="${name}"\` to a \`<button>\` in the template, or remove the unused action.`,
          severity: "warning"
        });
      }
    }
  }
};
var REL_CHANNEL = LEGACY_MIGRATION_BY_ID.rel;
var ATTRIBUTE_CHANNELS = LEGACY_MIGRATIONS.filter(
  (m) => m.id !== "rel" && m.attributes.length > 0
);
var legacyChannelWarning = (channel, detail, templatePath, errors) => {
  errors.push({
    path: templatePath,
    kind: "legacy-click-channel",
    message: [`${detail} is a legacy click channel.`, channel.fixHint].filter((part) => part !== void 0 && part.length > 0).join(" "),
    severity: "warning"
  });
};
var checkLegacyChannels = (htmlTemplate, path, errors) => {
  const { text, removed } = substituteInterpolations(htmlTemplate);
  const templatePath = pathStr([...path, "htmlTemplate"]);
  const sources = [text, ...interpolatedHtml(removed)];
  for (const tag of sources.flatMap((s) => scanHtmlTags(s))) {
    if (tag.closing) continue;
    if ((tag.name === "a" || tag.name === "button") && !hasAttr(tag.attrs, "href")) {
      const rel = readAttr(tag.attrs, "rel");
      if (rel !== void 0 && rel.length > 0 && !isDynamicAttrValue(rel) && !rel.includes(" ") && !HTML_REL_TOKENS.has(rel)) {
        legacyChannelWarning(
          REL_CHANNEL,
          `\`rel="${rel}"\``,
          templatePath,
          errors
        );
      }
    }
    for (const channel of ATTRIBUTE_CHANNELS) {
      if (channel.attributes.some((attr) => hasAttr(tag.attrs, attr))) {
        legacyChannelWarning(
          channel,
          `\`${legacyChannelLabel(channel)}\``,
          templatePath,
          errors
        );
      }
    }
  }
};
var isObj12 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);

// src/validation/dataScope.ts
var isObj13 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var NON_PARTITIONING_SOURCES = /* @__PURE__ */ new Set(["embedded", "callable"]);
var walk5 = (value, path, errors, rails) => {
  var _a2, _b;
  if (Array.isArray(value)) {
    value.forEach((c, i) => walk5(c, [...path, i], errors, rails));
    return;
  }
  if (!isObj13(value)) return;
  const data = value.data;
  if (isObj13(data)) {
    const scope = data.scope;
    const source = (_a2 = data.source) != null ? _a2 : "embedded";
    const dataType = data.dataType;
    const scopePath = pathStr([...path, "data", "scope"]);
    if (scope === "user" && typeof source === "string" && NON_PARTITIONING_SOURCES.has(source)) {
      errors.push({
        path: scopePath,
        kind: "data-scope-user-unsupported-source",
        message: `\`scope: "user"\` on source \`${source}\` narrows nothing \u2014 it neither filters the records nor makes them private.`,
        fixHint: `Remove the \`scope\`, or set \`source: "storage"\` (per-user partition of stored records) or \`source: "realtime"\` (the private per-player realtime rail) if you meant one of those.`,
        severity: "error"
      });
    }
    if (scope === "group" && source !== "storage") {
      errors.push({
        path: scopePath,
        kind: "data-scope-group-unsupported-source",
        message: `\`scope: "group"\` on source \`${source}\` has no partition \u2014 group data only exists on the storage rail, behind the membership registry.`,
        fixHint: `Set \`source: "storage"\` for a group-partitioned collection, or remove the \`scope\`.`,
        severity: "error"
      });
    }
    if (scope === "inbox" && source !== "realtime") {
      errors.push({
        path: scopePath,
        kind: "data-scope-inbox-unsupported-source",
        message: `\`scope: "inbox"\` on source \`${source}\` has no inbox \u2014 the write-only inbox exists only on the realtime rail.`,
        fixHint: `Set \`source: "realtime"\` for an inbox type, or remove the \`scope\`.`,
        severity: "error"
      });
    }
    if (source === "realtime" && typeof dataType === "string" && dataType) {
      const entry = (_b = rails.get(dataType)) != null ? _b : {
        user: [],
        publicRail: [],
        inbox: [],
        other: []
      };
      if (scope === "inbox") {
        entry.inbox.push({ scope, path: scopePath });
      } else {
        entry.other.push({ scope: scope != null ? scope : "global", path: scopePath });
      }
      if (scope === "user") {
        entry.user.push({ scope, path: scopePath });
      } else if (scope === "global" || scope === "variant") {
        entry.publicRail.push({ scope, path: scopePath });
      }
      rails.set(dataType, entry);
    }
  }
  const formConfig = value.formConfig;
  if (isObj13(formConfig) && formConfig.scope === "group") {
    const groupKey = formConfig.groupKey;
    if (typeof groupKey !== "string" || groupKey.length === 0) {
      errors.push({
        path: pathStr([...path, "formConfig", "groupKey"]),
        kind: "data-scope-group-key-missing",
        message: 'A `scope: "group"` form has no `groupKey`, so a submit has no group to write into and is refused at save time.',
        fixHint: "Set `formConfig.groupKey` to the group this form writes into \u2014 usually an `@`-formula naming whichever group the screen is about (e.g. `@currentTab`), since a member can be in several at once.",
        severity: "error"
      });
    }
  }
  for (const [key, child] of Object.entries(value)) {
    if (key === "data") continue;
    walk5(child, [...path, key], errors, rails);
  }
};
var validateDataScope = (json) => {
  if (!isObj13(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj13(appConfig3)) return [];
  const errors = [];
  const rails = /* @__PURE__ */ new Map();
  walk5(appConfig3, ["appConfig"], errors, rails);
  for (const [dataType, { user, publicRail, inbox, other }] of rails) {
    if (inbox.length && other.length) {
      errors.push({
        path: inbox[0].path,
        kind: "data-scope-realtime-rail-conflict",
        message: `Realtime type \`${dataType}\` is declared \`scope: "inbox"\` here and \`scope: "${other[0].scope}"\` elsewhere, so its records would arrive on two rails and its writes could land in the public one.`,
        fixHint: `Declare \`${dataType}\` \`scope: "inbox"\` on every data node that binds it, or use a separate type for the half that isn't private.`,
        severity: "error",
        relatedPaths: other.map((d) => d.path)
      });
      continue;
    }
    if (!user.length || !publicRail.length) continue;
    errors.push({
      path: user[0].path,
      kind: "data-scope-realtime-rail-conflict",
      message: `Realtime type \`${dataType}\` is declared \`scope: "user"\` (the private per-player rail) here and \`scope: "${publicRail[0].scope}"\` (the shared public collection) elsewhere, so its records arrive on both rails and nothing marks which are private.`,
      fixHint: `Pick one rail for \`${dataType}\` \u2014 private (\`user\`) or shared (\`global\`) \u2014 on every data node that binds it. Use a separate type for the public half if the game needs both, or \`scope: "all"\` on a single node if you really do want both rails.`,
      severity: "error",
      relatedPaths: publicRail.map((d) => d.path)
    });
  }
  return errors;
};

// src/validation/mediaRooms.ts
var isObj14 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var DEFAULT_MODE = "video";
var walk6 = (value, path, rooms) => {
  var _a2;
  if (Array.isArray(value)) {
    value.forEach((c, i) => walk6(c, [...path, i], rooms));
    return;
  }
  if (!isObj14(value)) return;
  const type14 = value.type;
  if (typeof type14 === "string" && resolveNodeType(type14) === MEDIA_ROOM) {
    const roomKey = value.roomKey;
    if (typeof roomKey === "string" && roomKey !== "") {
      const mode2 = typeof value.mediaMode === "string" && value.mediaMode !== "" ? value.mediaMode : DEFAULT_MODE;
      const name = typeof value.name === "string" ? value.name : "(unnamed)";
      const entry = (_a2 = rooms.get(roomKey)) != null ? _a2 : [];
      entry.push({ name, mode: mode2, path: pathStr([...path, "mediaMode"]) });
      rooms.set(roomKey, entry);
    }
  }
  for (const [key, child] of Object.entries(value)) {
    if (key === "data") continue;
    walk6(child, [...path, key], rooms);
  }
};
var validateMediaRooms = (json) => {
  if (!isObj14(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj14(appConfig3)) return [];
  const rooms = /* @__PURE__ */ new Map();
  walk6(appConfig3, ["appConfig"], rooms);
  const errors = [];
  for (const [roomKey, nodes2] of rooms) {
    const modes = [...new Set(nodes2.map((n) => n.mode))];
    if (modes.length < 2) continue;
    const [first, ...rest] = nodes2;
    const describe2 = (n) => `\`${n.name}\` (\`${n.mode}\`)`;
    errors.push({
      path: first.path,
      kind: "media-mode-room-conflict",
      message: `Nodes sharing the room \`${roomKey}\` disagree on \`mediaMode\`: ${[first, ...rest].map(describe2).join(", ")}. The call is priced per session, so one view publishing video re-prices the whole room at the video rate \u2014 the mode is a property of the ROOM, not of a view.`,
      fixHint: `Set the same \`mediaMode\` on every \`mediaRoom\` node whose \`roomKey\` is \`${roomKey}\`. To vary only how a screen RENDERS the same call, use \`displayMode\` (\`audioOnly\` hides video without opting out of it), which is per-view by design.`,
      severity: "error",
      relatedPaths: rest.map((n) => n.path)
    });
  }
  return errors;
};

// src/validation/notifications.ts
var isObj15 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var declaredFields = (json, typeName) => {
  const ot = sectionNodes(json.objectTypes).find((t30) => t30.name === typeName);
  if (!isObj15(ot)) return void 0;
  const fields = Array.isArray(ot.fields) ? ot.fields : [];
  return new Set(
    fields.flatMap(
      (f) => isObj15(f) && typeof f.name === "string" ? [f.name] : []
    )
  );
};
var instanceKeys = (json, typeName) => {
  const out = /* @__PURE__ */ new Set();
  forEachSeedRecord(json.objectInstances, (rec) => {
    if (rec.type !== typeName) return;
    for (const k of Object.keys(rec)) out.add(k);
  });
  return out;
};
var conditionErrors = (json, rule, i) => {
  const src = rule.condition;
  const typeName = rule.onPublishType;
  if (typeof src !== "string" || src.trim().length === 0) return [];
  if (typeof typeName !== "string") return [];
  const fields = declaredFields(json, typeName);
  if (fields === void 0) return [];
  const path = `appConfig.notifications[${i}].condition`;
  const consts = collectConstNodes(json);
  const functions = /* @__PURE__ */ new Map();
  collectFunctionNodes2(json, [], functions);
  const notBound = (name) => consts.has(name) || functions.has(name) ? ` \`${name}\` is ${consts.has(name) ? "a const" : "a function"} node \u2014 a push condition sees the record alone: consts and functions are bound for transition rules, not here.` : "";
  const known = /* @__PURE__ */ new Set([
    ...fields,
    ...instanceKeys(json, typeName),
    ...RECORD_META_FIELDS,
    "crudType",
    ...CALLABLE_FUNCTION_NAMES,
    ...HOF_MAGIC_VARS,
    ...JS_GLOBALS
  ]);
  const callable = /* @__PURE__ */ new Set([
    ...CALLABLE_FUNCTION_NAMES,
    ...HOST_FUNCTIONS.map((h) => h.name)
  ]);
  const errors = [];
  for (const called of unknownCallsIn(src, callable)) {
    errors.push({
      path,
      kind: "notification-unknown-call",
      message: `Push condition calls \`${called}()\`, which is not a built-in formula function \u2014 the call evaluates to \`undefined\`, so this push is never sent.${notBound(called)}`,
      fixHint: "Use built-in functions only, over the record's own fields \u2014 e.g. `@length(actions) > 0`.",
      severity: "error"
    });
  }
  for (const miss of ruleScopeMissesIn(src, known, /* @__PURE__ */ new Map())) {
    errors.push({
      path,
      kind: "notification-unknown-identifier",
      message: `Push condition reads \`${miss.name}\`, which is not a field of \`${typeName}\` \u2014 it evaluates to \`undefined\`, so the condition can be silently false.${notBound(miss.name)}`,
      fixHint: `Read a field of the published \`${typeName}\` record (or \`crudType\`) by bare name. A value the condition needs from elsewhere must be stored on the record when it is published.`,
      severity: "error"
    });
  }
  return errors;
};
var isUnsafeClickPath = (v) => typeof v === "string" && v.length > 0 && (v.startsWith("/") || v.includes(":"));
var validateNotifications = (json) => {
  if (!isObj15(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj15(appConfig3)) return [];
  const raw = appConfig3.notifications;
  if (raw === void 0) return [];
  const rules = Array.isArray(raw) ? raw : [raw];
  const errors = [];
  rules.forEach((rule, i) => {
    if (!isObj15(rule)) return;
    if (isUnsafeClickPath(rule.clickPath)) {
      errors.push({
        path: `appConfig.notifications[${i}].clickPath`,
        kind: "unsafe-click-path",
        message: `Notification clickPath "${String(
          rule.clickPath
        )}" is not a valid in-org navigation target.`,
        fixHint: "Use an in-app screen name within this org (e.g. recipeDetail), or leave it blank to open the org home. A leading '/' or an external URL is rejected \u2014 the org is fixed by the system.",
        severity: "error"
      });
    }
    errors.push(...conditionErrors(json, rule, i));
  });
  return errors;
};

// src/validation/structure.ts
var REQUIRED_TOP_LEVEL_KEYS = [
  "versionId",
  "apiVersion",
  "appConfig",
  "objectTypes",
  "objectInstances",
  "plugins",
  "users"
];
var OPTIONAL_TOP_LEVEL_KEYS = ["orgSummary", "minClientVersion"];
var ALL_TOP_LEVEL_KEYS = [
  ...REQUIRED_TOP_LEVEL_KEYS,
  ...OPTIONAL_TOP_LEVEL_KEYS
];
var isTopLevelKey = (k) => ALL_TOP_LEVEL_KEYS.includes(k);
var validateStructure = (json) => {
  if (typeof json !== "object" || json === null || Array.isArray(json)) {
    return [
      {
        path: "",
        kind: "invalid-type",
        message: "Org config must be a JSON object at the top level.",
        fixHint: "Return a single JSON object, not an array or primitive.",
        severity: "error"
      }
    ];
  }
  const errors = [];
  const obj = json;
  for (const k of REQUIRED_TOP_LEVEL_KEYS) {
    if (obj[k] === void 0) {
      errors.push({
        path: k,
        kind: "missing-required-field",
        message: `Required top-level field \`${k}\` is missing.`,
        fixHint: `Add a top-level \`${k}\` to the org JSON.`,
        severity: "error"
      });
    }
  }
  for (const k of Object.keys(obj)) {
    if (isTopLevelKey(k)) continue;
    const ranked = ALL_TOP_LEVEL_KEYS.map((p) => ({
      p,
      d: levenshteinDistance(k, p)
    })).sort((a, b2) => a.d - b2.d);
    const closest = ranked[0];
    errors.push({
      path: k,
      kind: "unknown-field",
      message: `Top-level field \`${k}\` is not part of the org schema.`,
      fixHint: closest && closest.d <= 3 ? `Did you mean \`${closest.p}\`? Allowed top-level keys: ${ALL_TOP_LEVEL_KEYS.join(", ")}.` : `Allowed top-level keys: ${ALL_TOP_LEVEL_KEYS.join(", ")}.`,
      severity: "error"
    });
  }
  if (errors.some((e) => e.kind === "missing-required-field")) {
    return errors;
  }
  let raw;
  try {
    raw = treeToRawOrgData(json);
  } catch (err) {
    errors.push({
      path: "",
      kind: "invalid-type",
      message: `Failed to convert tree to raw org data: ${err instanceof Error ? err.message : "unknown"}.`,
      fixHint: "Check that every node has `id`, `type`, and `name`, and that `_children` is an array of nodes.",
      severity: "error"
    });
    return errors;
  }
  const decoded = orgData.decode(raw);
  if (isLeft3(decoded)) {
    errors.push(...decoded.left.map(ioTsErrToValidationError));
  }
  errors.push(...checkSectionRoots(obj));
  errors.push(...checkUniqueIds(obj));
  errors.push(...checkUniqueSiblingNames(obj));
  errors.push(...checkAmbiguousNameRefs(obj));
  errors.push(...checkEnumValues(obj));
  errors.push(...checkSpans(obj));
  errors.push(...checkSpatial(obj));
  errors.push(...checkRequiredFields(obj));
  return errors;
};
var SCANNED_SLOTS = [
  "appConfig",
  "objectTypes",
  "objectInstances",
  "plugins",
  "users"
];
var COPIED_LIBRARY_NODE_HINT = "This looks like a library node copied out of its folder to override it: give your copy a new `id` (the vendored copy keeps its deterministic one), then re-run `og library sync`, which omits the library's own.";
var checkUniqueIds = (obj) => {
  const errors = [];
  const seen = /* @__PURE__ */ new Map();
  const libraryFolders = collectLibraryFolders(obj).map(
    (f) => `${pathStr2(f.path)}.`
  );
  const inLibrary = (p) => libraryFolders.some((f) => p.startsWith(f));
  for (const slot2 of SCANNED_SLOTS) {
    walkForIds(obj[slot2], [slot2], (id, path) => {
      const firstPath = seen.get(id);
      if (firstPath === void 0) {
        seen.set(id, path);
        return;
      }
      const copiedOut = inLibrary(path) !== inLibrary(firstPath);
      errors.push({
        path,
        kind: "duplicate-id",
        message: `Duplicate id \`${id}\`.`,
        fixHint: copiedOut ? COPIED_LIBRARY_NODE_HINT : `Each node's \`id\` must be unique across the entire org. Rename one of the two occurrences.`,
        severity: "error",
        relatedPaths: [firstPath]
      });
    });
  }
  return errors;
};
var SECTION_ROOT_TYPE = {
  appConfig: "app",
  users: "users",
  objectTypes: "objectTypes",
  objectInstances: "objectInstances",
  plugins: "plugins"
};
var checkSectionRoots = (obj) => {
  const errors = [];
  for (const key of Object.keys(SECTION_ROOT_TYPE)) {
    const src = obj[key];
    if (!isObj16(src)) continue;
    if (typeof src.type === "string" && src.type.length > 0) continue;
    const kids5 = src._children;
    if (!Array.isArray(kids5) || kids5.length === 0) continue;
    errors.push({
      path: key,
      kind: "section-root-untyped",
      message: `Top-level section \`${key}\` holds ${kids5.length} node${kids5.length === 1 ? "" : "s"} but its root declares no \`type\`, so it is rebuilt as a generic folder and the root's own id and type are lost.`,
      fixHint: `Give the \`${key}\` root an explicit id and type: \`{ "id": "${key}", "type": "${SECTION_ROOT_TYPE[key]}", "_children": [...] }\`, as dave/jumbly does.`,
      severity: "warning"
    });
  }
  return errors;
};
var checkUniqueSiblingNames = (obj) => {
  const errors = [];
  for (const slot2 of SCANNED_SLOTS) {
    walkForSiblingNames(obj[slot2], [slot2], errors);
  }
  return errors;
};
var checkEnumValues = (obj) => {
  const errors = [];
  for (const slot2 of SCANNED_SLOTS) {
    walkForEnums(obj[slot2], [slot2], errors);
  }
  return errors;
};
var checkSpans = (obj) => {
  const errors = [];
  walkForSpans(obj.appConfig, ["appConfig"], errors, NO_SPAN_PARENT);
  return errors;
};
var checkSpatial = (obj) => {
  const errors = [];
  walkForSpatial(obj.appConfig, ["appConfig"], errors, false);
  return errors;
};
var checkRequiredFields = (obj) => {
  const errors = [];
  walkForRequired(obj.appConfig, ["appConfig"], errors);
  return errors;
};
var REQUIRED_BY_KEY = new Map(
  Object.entries(REQUIRED_FIELDS)
);
var requiredFieldsFor = (rawType, variant) => {
  var _a2, _b;
  const runtime = runtimeTypeOf({ type: rawType, variant });
  const base = (_a2 = REQUIRED_BY_KEY.get(runtime.type)) != null ? _a2 : [];
  if (runtime.variant === void 0) return base;
  const tagged = (_b = REQUIRED_BY_KEY.get(`${runtime.type}:${runtime.variant}`)) != null ? _b : [];
  return tagged.length === 0 ? base : [.../* @__PURE__ */ new Set([...base, ...tagged])];
};
var walkForRequired = (value, path, errors) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForRequired(c, [...path, i], errors));
    return;
  }
  if (!isObj16(value)) return;
  const type14 = value.type;
  if (typeof type14 === "string") {
    for (const field2 of requiredFieldsFor(type14, value.variant)) {
      const v = getByPath(value, field2);
      if (v !== void 0 && v !== null && v !== "") continue;
      errors.push({
        path: pathStr2([...path, ...field2.split(".")]),
        kind: "missing-required-field",
        message: `\`${type14}\` requires \`${field2}\`.`,
        fixHint: `Add \`${field2}\` to this ${type14} node.`,
        severity: "error"
      });
    }
  }
  const children2 = value._children;
  if (Array.isArray(children2)) {
    walkForRequired(children2, [...path, "_children"], errors);
  }
};
var SPAN_KEYS = /* @__PURE__ */ new Set(["startField", "endField", "axis", "mode", "slots"]);
var NO_SPAN_PARENT = { siblings: [], parentIsSpatial: false };
var walkForSpans = (value, path, errors, ctx) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForSpans(c, [...path, i], errors, ctx));
    return;
  }
  if (!isObj16(value)) return;
  const data = value.data;
  const span2 = isObj16(data) ? data.span : void 0;
  if (isObj16(span2) && Object.keys(span2).length > 0) {
    const spanPath = [...path, "data", "span"];
    for (const key of Object.keys(span2)) {
      if (!SPAN_KEYS.has(key)) {
        errors.push({
          path: pathStr2([...spanPath, key]),
          kind: "unknown-field",
          message: `Unknown span property \`${key}\`.`,
          fixHint: nearMissHint(key, SPAN_KEYS, "span property"),
          severity: "error"
        });
      }
    }
    const has = (k) => {
      const v = span2[k];
      return typeof v === "string" && v.length > 0;
    };
    const missing = ["startField", "axis"].filter((k) => !has(k));
    if (missing.length > 0) {
      errors.push({
        path: pathStr2(spanPath),
        kind: "span-incomplete",
        message: `Span is missing ${missing.map((m) => `\`${m}\``).join(" and ")}.`,
        fixHint: "A span needs `startField` (the record field the run starts at) and `axis` (a formula for the current slot's position, e.g. `@day`). Remove the span block entirely if you didn't mean to add one.",
        severity: "error"
      });
    }
    if (value.isRepeating !== true) {
      errors.push({
        path: pathStr2(spanPath),
        kind: "span-not-repeating",
        message: "Span is set on a node that does not repeat, so there are no slots for a record to cover.",
        fixHint: "Set `isRepeating: true` on this node, or remove the span block.",
        severity: "error"
      });
    }
    if (span2.mode === "grid") {
      if (!has("slots")) {
        errors.push({
          path: pathStr2(spanPath),
          kind: "span-incomplete",
          message: "Grid span is missing `slots`.",
          fixHint: 'Grid mode needs `slots` \u2014 the name of the repeating sibling in this spatial container whose rows are the slots (e.g. `"slots": "days"`). Use `mode: "repeat"` to render once per slot instead.',
          severity: "error"
        });
      } else if (!ctx.parentIsSpatial) {
        errors.push({
          path: pathStr2([...spanPath, "slots"]),
          kind: "span-slots-invalid",
          message: "Grid span is not inside a spatial container, so there is no cell grid to draw bars on.",
          fixHint: 'Set `layout: { "mode": "spatial", "cols": N }` on the parent container, or use `mode: "repeat"`.',
          severity: "error"
        });
      } else {
        const slotsName = span2.slots;
        const supplier = ctx.siblings.find(
          (sib) => isObj16(sib) && sib !== value && sib.name === slotsName
        );
        if (!isObj16(supplier)) {
          errors.push({
            path: pathStr2([...spanPath, "slots"]),
            kind: "span-slots-invalid",
            message: `Grid span names \`${String(slotsName)}\`, which is not a sibling in this container.`,
            fixHint: "`slots` must be the `name` of another child of the same spatial container \u2014 the repeating node whose rows are the slots.",
            severity: "error"
          });
        } else if (supplier.isRepeating !== true) {
          errors.push({
            path: pathStr2([...spanPath, "slots"]),
            kind: "span-slots-invalid",
            message: `Grid span names \`${String(slotsName)}\`, which does not repeat, so it supplies no slots.`,
            fixHint: "Set `isRepeating: true` on the named sibling, or point `slots` at the repeating node that lays out the cells.",
            severity: "error"
          });
        }
      }
      if (isObj16(value.place) && Object.keys(value.place).length > 0) {
        errors.push({
          path: pathStr2([...path, "place"]),
          kind: "place-span-conflict",
          message: "This node has both a `place` and a grid span, but a node's cell can only have one owner.",
          fixHint: 'Remove the `place` block (the platform positions a grid span from the slots it covers), or use `mode: "repeat"` if you meant to place this child yourself.',
          severity: "error"
        });
      }
    }
  }
  const children2 = value._children;
  if (Array.isArray(children2)) {
    walkForSpans(children2, [...path, "_children"], errors, {
      siblings: children2,
      parentIsSpatial: isSpatialLayout2(value)
    });
  }
};
var PLACE_KEYS = /* @__PURE__ */ new Set(["x", "y", "w", "h"]);
var isSpatialLayout2 = (node2) => isObj16(node2.layout) && node2.layout.mode === "spatial";
var isSetMember = (v) => typeof v === "number" || typeof v === "string" && v.length > 0;
var walkForSpatial = (value, path, errors, parentIsSpatial) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForSpatial(c, [...path, i], errors, parentIsSpatial));
    return;
  }
  if (!isObj16(value)) return;
  const spatial = isSpatialLayout2(value);
  if (spatial && isObj16(value.layout) && !isSetMember(value.layout.cols)) {
    errors.push({
      path: pathStr2([...path, "layout"]),
      kind: "spatial-missing-cols",
      message: "Spatial layout has no `cols`, so the grid has no width and the container falls back to flow layout.",
      fixHint: "Set `layout.cols` to the number of cells across (a number or an `@`-formula, e.g. `7` for a week grid), or switch `layout.mode` back to `columns`.",
      severity: "error"
    });
  }
  const place2 = value.place;
  if (isObj16(place2) && Object.keys(place2).length > 0) {
    const placePath = [...path, "place"];
    for (const key of Object.keys(place2)) {
      if (!PLACE_KEYS.has(key)) {
        errors.push({
          path: pathStr2([...placePath, key]),
          kind: "unknown-field",
          message: `Unknown place property \`${key}\`.`,
          fixHint: nearMissHint(key, PLACE_KEYS, "place property"),
          severity: "error"
        });
      }
    }
    const missing = ["x", "y"].filter((k) => !isSetMember(place2[k]));
    if (missing.length > 0) {
      errors.push({
        path: pathStr2(placePath),
        kind: "place-incomplete",
        message: `Place is missing ${missing.map((m) => `\`${m}\``).join(" and ")}.`,
        fixHint: "A place needs both `x` and `y` (0-based cell coordinates, each a number or an `@`-formula); `w`/`h` are optional spans. Remove the place block entirely to let the child auto-flow.",
        severity: "error"
      });
    }
    if (!parentIsSpatial) {
      errors.push({
        path: pathStr2(placePath),
        kind: "place-outside-spatial",
        message: "Place is set on a node whose parent is not in spatial layout, so it has no effect.",
        fixHint: 'Set `layout: { "mode": "spatial", "cols": N }` on the parent container, or remove the place block.',
        severity: "warning"
      });
    }
  }
  const children2 = value._children;
  if (Array.isArray(children2)) {
    walkForSpatial(children2, [...path, "_children"], errors, spatial);
  }
};
var getByPath = (node2, fieldPath) => fieldPath.split(".").reduce(
  (acc, key) => isObj16(acc) ? acc[key] : void 0,
  node2
);
var walkForEnums = (value, path, errors) => {
  var _a2;
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForEnums(c, [...path, i], errors));
    return;
  }
  if (!isObj16(value)) return;
  const type14 = value.type;
  if (typeof type14 === "string") {
    const fieldMap = ENUM_FIELDS[type14];
    if (fieldMap) {
      for (const [fieldPath, enumName] of Object.entries(fieldMap)) {
        const v = getByPath(value, fieldPath);
        if (typeof v !== "string" || v.length === 0 || v.startsWith("@")) {
          continue;
        }
        const allowed = (_a2 = ENUM_VALUES[enumName]) != null ? _a2 : [];
        if (!allowed.includes(v)) {
          const legacyTarget = enumName === "authMode" ? LEGACY_AUTH_MODE[v] : void 0;
          if (legacyTarget) {
            errors.push({
              path: pathStr2([...path, ...fieldPath.split(".")]),
              kind: "enum-unknown-value",
              message: `Legacy ${enumName} value \`${v}\` \u2014 normalized to \`${legacyTarget}\` at load time.`,
              fixHint: `Prefer \`${legacyTarget}\`.`,
              severity: "warning"
            });
          } else {
            errors.push({
              path: pathStr2([...path, ...fieldPath.split(".")]),
              kind: "enum-unknown-value",
              message: `Unknown ${enumName} value \`${v}\`.`,
              fixHint: nearMissHint(v, new Set(allowed), `${enumName} value`),
              severity: "error"
            });
          }
        }
      }
    }
  }
  const children2 = value._children;
  if (Array.isArray(children2)) {
    walkForEnums(children2, [...path, "_children"], errors);
  }
};
var walkForIds = (value, path, visit) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForIds(c, [...path, i], visit));
    return;
  }
  if (!isObj16(value)) return;
  const id = value.id;
  if (typeof id === "string" && id.length > 0) {
    visit(id, pathStr2(path));
  }
  const children2 = value._children;
  if (Array.isArray(children2)) {
    walkForIds(children2, [...path, "_children"], visit);
  }
};
var checkAmbiguousNameRefs = (obj) => {
  const appConfig3 = obj.appConfig;
  if (!isObj16(appConfig3)) return [];
  const errors = [];
  const visitScreen = (screen2, screenPath) => {
    const seen = /* @__PURE__ */ new Map();
    const sources = [];
    const descend = (node2, path, isScreenRoot) => {
      var _a2;
      if (Array.isArray(node2)) {
        node2.forEach((c, i) => descend(c, [...path, i], false));
        return;
      }
      if (!isObj16(node2)) return;
      const n = node2;
      if (!isScreenRoot) {
        if (n.type === "screen") return;
        const name = n.name;
        if (typeof name === "string" && name.length > 0) {
          const at = (_a2 = seen.get(name)) != null ? _a2 : [];
          at.push([...path, "name"]);
          seen.set(name, at);
        }
      }
      for (const [k, v] of Object.entries(n)) {
        if (k === "_children") continue;
        collectSources(v, k, sources);
      }
      const children2 = n._children;
      if (Array.isArray(children2)) {
        children2.forEach(
          (c, i) => descend(c, [...path, "_children", i], false)
        );
      }
    };
    descend(screen2, screenPath, true);
    const duplicated = [...seen.entries()].filter(([, at]) => at.length > 1);
    if (duplicated.length === 0) return;
    const read = readIdentifiers(sources);
    for (const [name, at] of duplicated) {
      if (!read.has(name)) continue;
      const first = at[0];
      const second = at[1];
      errors.push({
        path: pathStr2(second),
        kind: "ambiguous-name-ref",
        message: `\`${name}\` names ${at.length} nodes in this screen and is also read by bare name in a formula \u2014 the reference is ambiguous and will silently resolve to the wrong node (or crash, if it repeats).`,
        fixHint: `Rename one of the \`${name}\` nodes and update the formula that reads it. Duplicate names are only safe while nothing refers to them by name.`,
        severity: "error",
        relatedPaths: [pathStr2(first)]
      });
    }
  };
  const findScreens = (node2, path) => {
    if (Array.isArray(node2)) {
      node2.forEach((c, i) => findScreens(c, [...path, i]));
      return;
    }
    if (!isObj16(node2)) return;
    const n = node2;
    if (n.type === "screen") visitScreen(n, path);
    const children2 = n._children;
    if (Array.isArray(children2)) {
      children2.forEach((c, i) => findScreens(c, [...path, "_children", i]));
    }
  };
  findScreens(appConfig3, ["appConfig"]);
  return errors;
};
var collectSources = (value, key, out) => {
  if (typeof value === "string") {
    if (value.startsWith("@")) out.push(value.slice(1));
    else if (key === "script") out.push(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const v of value) collectSources(v, key, out);
    return;
  }
  if (isObj16(value)) {
    for (const [k, v] of Object.entries(value)) collectSources(v, k, out);
  }
};
var readIdentifiers = (sources) => {
  const names2 = /* @__PURE__ */ new Set();
  for (const source of sources) {
    let ast;
    try {
      ast = jsep(source);
    } catch {
      continue;
    }
    for (const u of collectUnknownIdentifiers(ast, EMPTY_KNOWN)) {
      names2.add(u.name);
    }
  }
  return names2;
};
var EMPTY_KNOWN = /* @__PURE__ */ new Set();
var walkForSiblingNames = (value, path, errors) => {
  if (!isObj16(value)) return;
  const children2 = value._children;
  if (Array.isArray(children2)) {
    const seenNames = /* @__PURE__ */ new Map();
    children2.forEach((c, i) => {
      if (!isObj16(c)) return;
      const name = c.name;
      if (typeof name === "string" && name.length > 0) {
        const firstIndex = seenNames.get(name);
        if (firstIndex === void 0) {
          seenNames.set(name, i);
        } else {
          errors.push({
            path: pathStr2([...path, "_children", i, "name"]),
            kind: "duplicate-sibling-name",
            message: `Duplicate sibling name \`${name}\`.`,
            fixHint: `Siblings under the same parent must have unique names. Rename one of the two occurrences.`,
            severity: "error",
            relatedPaths: [
              pathStr2([...path, "_children", firstIndex, "name"])
            ]
          });
        }
      }
      walkForSiblingNames(c, [...path, "_children", i], errors);
    });
  }
};
var isObj16 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);
var pathStr2 = (segs) => segs.reduce((acc, s) => {
  if (typeof s === "number") return `${acc}[${s}]`;
  if (acc === "") return String(s);
  return `${acc}.${s}`;
}, "");
var ioTsErrToValidationError = (e) => {
  var _a2, _b, _c;
  const path = e.context.filter((_, i) => i > 0).map((c) => c.key).join(".").replace(/\.(\d+)/g, "[$1]");
  const last2 = e.context[e.context.length - 1];
  const tag = (_b = (_a2 = last2 == null ? void 0 : last2.type) == null ? void 0 : _a2.name) != null ? _b : "unknown";
  if (e.value === void 0) {
    return {
      path: path || (last2 == null ? void 0 : last2.key) || "",
      kind: "missing-required-field",
      message: `Required field \`${(_c = last2 == null ? void 0 : last2.key) != null ? _c : path}\` is missing.`,
      fixHint: `Add \`${last2 == null ? void 0 : last2.key}\` (expected: ${tag}).`,
      severity: "error"
    };
  }
  return {
    path,
    kind: "invalid-type",
    message: `Expected ${tag} at \`${path}\`, got ${typeof e.value}.`,
    fixHint: `Provide a value matching ${tag}.`,
    severity: "error"
  };
};

// src/validation/rubric.ts
var CONTAINER_TYPES = /* @__PURE__ */ new Set([
  "screen",
  "subsection",
  "folder",
  "tabGroup"
]);
var DATA_SCRIPT_ATTR_RE = /\bdata-script\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
var validateRubric = (json) => {
  if (!isObj17(json)) return [];
  const errors = [];
  errors.push(...checkHomeScreen(json));
  errors.push(...checkScreensHaveObjectTypes(json));
  walkForRubric(json, errors, []);
  errors.push(...checkOrphanScreens(json));
  errors.push(...checkSparseInstances(json));
  errors.push(...checkLegacyFieldProps(json));
  errors.push(...checkFlowsOutsideScreens(json));
  return errors;
};
var checkFlowsOutsideScreens = (json) => {
  const errors = [];
  const walk8 = (node2, underScreen, path) => {
    if (!isObj17(node2)) return;
    if (node2.type === "flow" && !underScreen) {
      const name = typeof node2.name === "string" ? node2.name : "flow";
      errors.push({
        path: pathStr3(path),
        kind: "flow-outside-screen",
        message: `Flow \`${name}\` has no screen around it, so it renders as a bare page: no Next button, and \`advanceFlow()\` fails.`,
        fixHint: `Wrap it in a screen of its own and navigate to that screen: \`{"type": "screen", "name": "${name}Page", "_children": [ <this flow> ]}\`, then \`navigate('${name}Page')\`.`,
        severity: "error"
      });
    }
    const children2 = node2._children;
    if (!Array.isArray(children2)) return;
    const under = underScreen || node2.type === "screen";
    children2.forEach((c, i) => walk8(c, under, [...path, "_children", i]));
  };
  walk8(json.appConfig, false, ["appConfig"]);
  return errors;
};
var NON_RENDERING_HEAD_TYPES = /* @__PURE__ */ new Set(["variable", "function", "const"]);
var checkHomeScreen = (json) => {
  const appConfig3 = json.appConfig;
  const children2 = isObj17(appConfig3) ? appConfig3._children : void 0;
  if (!Array.isArray(children2)) {
    return [
      {
        path: "appConfig._children[0]",
        kind: "homeScreen-missing",
        message: "First child of appConfig must be the home screen.",
        fixHint: "Add a screen as the first entry in appConfig._children, or move an existing screen to position 0.",
        severity: "error"
      }
    ];
  }
  const firstRenderingIndex = children2.findIndex(
    (c) => !isObj17(c) || typeof c.type !== "string" || !NON_RENDERING_HEAD_TYPES.has(c.type)
  );
  const first = firstRenderingIndex === -1 ? void 0 : children2[firstRenderingIndex];
  if (!isObj17(first) || first.type !== "screen") {
    const path = firstRenderingIndex === -1 ? "appConfig._children[0]" : `appConfig._children[${firstRenderingIndex}]`;
    return [
      {
        path,
        kind: "homeScreen-missing",
        message: "First rendering child of appConfig must be the home screen (variable, function and const declarations may precede it).",
        fixHint: "Add a screen as the first entry after any variable, function or const declarations in appConfig._children, or move an existing screen there.",
        severity: "error"
      }
    ];
  }
  return [];
};
var checkScreensHaveObjectTypes = (json) => {
  const appConfig3 = json.appConfig;
  const dataNodeCount = isObj17(appConfig3) ? countDataNodes(appConfig3) : 0;
  if (dataNodeCount === 0) return [];
  if (sectionNodes(json.objectTypes).length > 0) return [];
  return [
    {
      path: "objectTypes._children",
      kind: "rubric-missing-objectType",
      message: `appConfig contains ${dataNodeCount} form/list node(s) but objectTypes is empty.`,
      fixHint: "Add at least one objectType under `objectTypes` that the form/list can bind to via `data.dataType`.",
      severity: "error"
    }
  ];
};
var countDataNodes = (node2) => {
  if (Array.isArray(node2)) {
    return node2.reduce((acc, c) => acc + countDataNodes(c), 0);
  }
  if (!isObj17(node2)) return 0;
  const isDataShape = node2.type === "form" || node2.type === "list" || node2.type === "subsection" && node2.variant === "masterDetail";
  const childCount = countDataNodes(node2._children);
  return (isDataShape ? 1 : 0) + childCount;
};
var walkForRubric = (value, errors, path) => {
  var _a2, _b, _c;
  if (Array.isArray(value)) {
    value.forEach((c, i) => walkForRubric(c, errors, [...path, i]));
    return;
  }
  if (!isObj17(value)) return;
  const node2 = value;
  if (node2.isRepeating === true) {
    const data = node2.data;
    const dataType = isObj17(data) ? data.dataType : void 0;
    const hasDataBinding = typeof dataType === "string" && dataType.length > 0;
    const hasMaxLength = node2.maxLength !== void 0 && node2.maxLength !== null;
    if (!hasDataBinding && !hasMaxLength) {
      errors.push({
        path: pathStr3(path),
        kind: "repeating-without-data",
        message: "Node has isRepeating: true but no data binding or maxLength.",
        fixHint: 'Add `data: { dataType: "<TypeName>", source: "embedded" }` (or `"storage"` etc.), or set `maxLength: "@<count-formula>"` for a fixed-length repeat, or remove `isRepeating`.',
        severity: "error"
      });
    }
  }
  if (path.length >= 1 && path[0] === "appConfig" && typeof node2.type === "string" && CONTAINER_TYPES.has(node2.type)) {
    const children2 = node2._children;
    if (!Array.isArray(children2) || children2.length === 0) {
      errors.push({
        path: pathStr3(path),
        kind: "empty-container",
        message: `Container node of type \`${node2.type}\` has no children.`,
        fixHint: "Add at least one child node, or remove the empty container.",
        severity: "warning"
      });
    }
  }
  if (path[0] === "appConfig" && node2.type === "flow") {
    checkScriptOnlyFlow(node2, errors, path);
  }
  if (path[0] === "appConfig" && typeof node2.type === "string") {
    checkLegacyNode(node2, node2.type, errors, path);
  }
  if (path[0] === "appConfig" && isSpatialLayout2(node2)) {
    checkSpatialChildren(node2, errors, path);
  }
  if (typeof node2.htmlTemplate === "string") {
    checkTagBalance(node2.htmlTemplate, errors, path);
    const anchorRe = /<a\s+([^>]*?)href="([^"]+)"([^>]*)>/g;
    for (const m of node2.htmlTemplate.matchAll(anchorRe)) {
      const before = (_a2 = m[1]) != null ? _a2 : "";
      const href = (_b = m[2]) != null ? _b : "";
      const after = (_c = m[3]) != null ? _c : "";
      const isCrossOrg = href.startsWith("orgiggly.com/") || href.startsWith("https://orgiggly.com/") || href.startsWith("http://orgiggly.com/") || href.startsWith("https://www.orgiggly.com/");
      const hasTargetBlank = `${before}${after}`.includes('target="_blank"');
      if (isCrossOrg && !hasTargetBlank) {
        errors.push({
          path: pathStr3([...path, "htmlTemplate"]),
          kind: "cross-org-link-missing-target-blank",
          message: `<a href="${href}"> will be intercepted by the SPA router.`,
          fixHint: 'Add target="_blank" to the anchor so the browser follows the link instead of the SPA.',
          severity: "error"
        });
      }
    }
  }
  for (const [k, val] of Object.entries(node2)) {
    walkForRubric(val, errors, [...path, k]);
  }
};
var SPATIAL_FLATTENED_CHROME = ["showHeader", "collapsible", "pageSize"];
var isScriptStep2 = (child) => isObj17(child) && (child.type === "script" || child.type === "flowStep" && child.variant === "script");
var checkScriptOnlyFlow = (node2, errors, path) => {
  const children2 = node2._children;
  if (!Array.isArray(children2) || children2.length === 0) return;
  if (!children2.every(isScriptStep2)) return;
  errors.push({
    path: pathStr3(path),
    kind: "flow-script-only",
    message: "This flow has only script steps, so the user taps in just to press its one button.",
    fixHint: 'Run the script straight from the button that opens the flow: declare it in that content node\'s `actions` (`<a data-action="start">` + `actions: {start: {script: "\u2026"}}`) and delete the flow screen. Add `confirm: {title: \u2026}` for a destructive action like Leave.',
    severity: "warning"
  });
};
var LEGACY_NODE_BY_TYPE = new Map(Object.entries(LEGACY_NODES));
var LEGACY_PROPS_BY_KEY = new Map(
  Object.entries(LEGACY_PROPS).map(([k, v]) => [k, Object.entries(v)])
);
var _a;
var LEGACY_FIELD_PROPS = Object.entries(
  (_a = LEGACY_HELPER_PROPS.objectTypeField) != null ? _a : {}
);
var isSet = (v) => v !== void 0 && v !== null && v !== "" && v !== false;
var checkLegacyNode = (node2, type14, errors, path) => {
  var _a2, _b, _c;
  const runtime = runtimeTypeOf({ type: type14, variant: node2.variant });
  const authored = (_a2 = authoredTypeOf(runtime.type, runtime.variant)) != null ? _a2 : runtime.type;
  const legacyNode = LEGACY_NODE_BY_TYPE.get(authored);
  if (legacyNode) {
    errors.push({
      path: pathStr3(path),
      kind: "legacy-node",
      message: `\`${authored}\` is a legacy node type (since ${legacyNode.since}): it still renders, but nothing new should be written with it.`,
      fixHint: replaceWith(legacyNode),
      severity: "warning"
    });
  }
  const props = [
    ...(_b = LEGACY_PROPS_BY_KEY.get(runtime.type)) != null ? _b : [],
    ...runtime.variant === void 0 ? [] : (_c = LEGACY_PROPS_BY_KEY.get(`${runtime.type}:${runtime.variant}`)) != null ? _c : []
  ];
  const seen = /* @__PURE__ */ new Set();
  for (const [prop5, status] of props) {
    if (seen.has(prop5) || !isSet(node2[prop5])) continue;
    seen.add(prop5);
    errors.push(legacyPropError([...path, prop5], prop5, status));
  }
};
var checkLegacyFieldProps = (json) => {
  const errors = [];
  for (const { node: ot, path: at } of sectionItems(json.objectTypes)) {
    if (!Array.isArray(ot.fields)) continue;
    ot.fields.forEach((field2, i) => {
      if (!isObj17(field2)) return;
      for (const [prop5, status] of LEGACY_FIELD_PROPS) {
        if (!isSet(field2[prop5])) continue;
        errors.push(
          legacyPropError(["objectTypes", ...at, "fields", i, prop5], prop5, status)
        );
      }
    });
  }
  return errors;
};
var replaceWith = (status) => `Write ${status.use} instead.`;
var legacyPropError = (path, prop5, status) => ({
  path: pathStr3(path),
  kind: "legacy-prop",
  message: `\`${prop5}\` is a legacy property (since ${status.since}): it still works, but nothing new should be written with it.`,
  fixHint: replaceWith(status),
  severity: "warning"
});
var checkSpatialChildren = (parent, errors, parentPath) => {
  const layout2 = isObj17(parent.layout) ? parent.layout : {};
  const cols = typeof layout2.cols === "number" ? layout2.cols : void 0;
  const rows = typeof layout2.rows === "number" ? layout2.rows : void 0;
  const children2 = parent._children;
  if (!Array.isArray(children2)) return;
  children2.forEach((child, i) => {
    if (!isObj17(child)) return;
    const childPath = [...parentPath, "_children", i];
    if (child.isRepeating === true) {
      for (const field2 of SPATIAL_FLATTENED_CHROME) {
        const v = child[field2];
        if (v === void 0 || v === null || v === false || v === "" || v === 0)
          continue;
        errors.push({
          path: pathStr3([...childPath, field2]),
          kind: "spatial-unsupported",
          message: `\`${field2}\` on a repeating child of a spatial container does not render \u2014 each iteration becomes its own grid cell, so the child has no list chrome of its own.`,
          fixHint: `Remove \`${field2}\` from this node, or move the chrome to the spatial container itself.`,
          severity: "warning"
        });
      }
    }
    const place2 = child.place;
    if (!isObj17(place2)) return;
    const outOfBounds = (axis2, extent, extentName) => {
      const v = place2[axis2];
      if (typeof v !== "number") return;
      if (v < 0 || extent !== void 0 && v >= extent) {
        const range = extent !== void 0 ? `${axis2} ${v} is outside ${extentName} ${extent} (valid: 0\u2013${extent - 1})` : `${axis2} ${v} is negative`;
        errors.push({
          path: pathStr3([...childPath, "place", axis2]),
          kind: "spatial-unsupported",
          message: `Place ${range}; the runtime will clamp it to the edge of the grid.`,
          fixHint: `Use a 0-based \`${axis2}\` inside the parent's \`${extentName}\`, or make \`${extentName}\` bigger.`,
          severity: "warning"
        });
      }
    };
    outOfBounds("x", cols, "cols");
    outOfBounds("y", rows, "rows");
  });
};
var VOID_ELEMENTS = /* @__PURE__ */ new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
]);
var AUTO_CLOSABLE = /* @__PURE__ */ new Set([
  "p",
  "li",
  "dt",
  "dd",
  "tr",
  "td",
  "th",
  "thead",
  "tbody",
  "tfoot",
  "option",
  "optgroup",
  "colgroup",
  "caption"
]);
var checkTagBalance = (htmlTemplate, errors, path) => {
  const { text, removed } = substituteInterpolations(htmlTemplate);
  if (removed.some((r) => r.includes("<") || r.includes(">"))) return;
  const stack = [];
  for (const tag of scanHtmlTags(text)) {
    if (VOID_ELEMENTS.has(tag.name)) continue;
    if (!tag.closing) {
      if (!tag.selfClosing) stack.push(tag.name);
      continue;
    }
    if (!stack.includes(tag.name)) {
      if (AUTO_CLOSABLE.has(tag.name)) continue;
      errors.push({
        path: pathStr3([...path, "htmlTemplate"]),
        kind: "html-unbalanced-tag",
        message: `Stray closing tag \`</${tag.name}>\` \u2014 no matching open \`<${tag.name}>\` in this template.`,
        fixHint: `Remove the \`</${tag.name}>\`, or add the missing \`<${tag.name}>\` where the element should start.`,
        severity: "error"
      });
      return;
    }
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      if (top === tag.name) {
        stack.pop();
        break;
      }
      if (AUTO_CLOSABLE.has(top)) {
        stack.pop();
        continue;
      }
      errors.push({
        path: pathStr3([...path, "htmlTemplate"]),
        kind: "html-unbalanced-tag",
        message: `Mismatched tags: \`</${tag.name}>\` closes \`<${top}>\` \u2014 \`<${top}>\` is still open at that point.`,
        fixHint: `Close \`<${top}>\` first (add \`</${top}>\`), or remove/move the stray tag.`,
        severity: "error"
      });
      return;
    }
  }
  const unclosed = stack.filter((name) => !AUTO_CLOSABLE.has(name));
  if (unclosed.length > 0) {
    const name = unclosed[unclosed.length - 1];
    errors.push({
      path: pathStr3([...path, "htmlTemplate"]),
      kind: "html-unbalanced-tag",
      message: `Unclosed tag \`<${name}>\` \u2014 the template ends while it is still open.`,
      fixHint: `Add the missing \`</${name}>\` (or self-close the element).`,
      severity: "error"
    });
  }
};
var checkOrphanScreens = (json) => {
  const appConfig3 = json.appConfig;
  if (!isObj17(appConfig3)) return [];
  const screens = [];
  const referenced = /* @__PURE__ */ new Set();
  let homeName;
  let hasDynamicHref = false;
  const collect2 = (value, path, parentType) => {
    var _a2, _b;
    if (Array.isArray(value)) {
      value.forEach((c, i) => collect2(c, [...path, i], parentType));
      return;
    }
    if (!isObj17(value)) return;
    const node2 = value;
    if (node2.type === "screen" && typeof node2.name === "string") {
      if (parentType !== "flow" && parentType !== "repeat") {
        screens.push({ name: node2.name, path: pathStr3(path) });
      }
    }
    if (node2.type === "tabGroup") {
      const tabChildren = node2._children;
      if (Array.isArray(tabChildren)) {
        for (const child of tabChildren) {
          if (isObj17(child) && typeof child.name === "string") {
            referenced.add(child.name);
          }
        }
      }
    }
    const addNavEdges = (source) => {
      const scan = scanNavCalls(source);
      if (scan.dynamic) hasDynamicHref = true;
      scan.targets.forEach((t30) => referenced.add(targetSuffix(t30)));
    };
    for (const val of Object.values(node2)) {
      if (typeof val !== "string" || !NAV_HOST_CALL_RE.test(val)) {
        continue;
      }
      addNavEdges(val.startsWith("@") ? val.slice(1) : val);
      for (const m of val.matchAll(DATA_SCRIPT_ATTR_RE)) {
        addNavEdges((_b = (_a2 = m[1]) != null ? _a2 : m[2]) != null ? _b : "");
      }
    }
    const actions = node2.actions;
    if (isObj17(actions)) {
      for (const action of Object.values(actions)) {
        if (isObj17(action) && typeof action.script === "string") {
          addNavEdges(action.script);
        }
      }
    }
    if (typeof node2.htmlTemplate === "string") {
      const allHrefRe = /href="([^"]+)"/g;
      for (const m of node2.htmlTemplate.matchAll(allHrefRe)) {
        const target = m[1];
        if (target === "@pop") continue;
        if (target.startsWith("http")) continue;
        if (target.includes("$") || target.includes("@")) {
          hasDynamicHref = true;
          continue;
        }
        if (target.includes("/")) continue;
        referenced.add(target);
      }
      const relRe = /rel="([^"]+)"/g;
      for (const m of node2.htmlTemplate.matchAll(relRe)) {
        const target = m[1];
        if (target.includes("$") || target.includes("@")) {
          hasDynamicHref = true;
          continue;
        }
        referenced.add(target);
      }
      if (!hasDynamicHref) {
        const hasAnchor = /<a\s+[^>]*href="/.test(node2.htmlTemplate);
        const hasFormulaContext = /\$\{|@[a-zA-Z_$]/.test(node2.htmlTemplate);
        if (hasAnchor && hasFormulaContext) {
          hasDynamicHref = true;
        }
      }
    }
    const children2 = node2._children;
    if (Array.isArray(children2)) {
      const ownType = typeof node2.type === "string" ? node2.type : void 0;
      children2.forEach(
        (c, i) => collect2(c, [...path, "_children", i], ownType)
      );
    }
  };
  const appConfigChildren = appConfig3._children;
  if (Array.isArray(appConfigChildren)) {
    appConfigChildren.forEach(
      (c, i) => collect2(c, ["appConfig", "_children", i])
    );
    const firstRendering = appConfigChildren.find(
      (c) => isObj17(c) && typeof c.type === "string" && !NON_RENDERING_HEAD_TYPES.has(
        c.type
      )
    );
    if (isObj17(firstRendering) && firstRendering.type === "screen" && typeof firstRendering.name === "string") {
      homeName = firstRendering.name;
    }
  }
  const unreferenced = screens.filter(
    ({ name }) => name !== homeName && !referenced.has(name)
  );
  if (hasDynamicHref) {
    if (unreferenced.length === 0) return [];
    const names2 = unreferenced.map((s) => `\`${s.name}\``).join(", ");
    return [
      {
        path: pathStr3(["appConfig"]),
        kind: "orphan-screen",
        message: `Reachability can't be proven for ${unreferenced.length} screen${unreferenced.length === 1 ? "" : "s"} \u2014 this config uses dynamic links (a computed href, rel or nav target), so any of them may be reached that way. Not referenced by anything static: ${names2}.`,
        fixHint: `No change is needed if the dynamic links reach them. Otherwise add an anchor or button that navigates to each, or remove the unused screens.`,
        severity: "info",
        relatedPaths: unreferenced.map((s) => s.path)
      }
    ];
  }
  return unreferenced.map(({ name, path }) => ({
    path,
    kind: "orphan-screen",
    message: `Screen \`${name}\` is not referenced by any static href or rel \u2014 may be unreachable.`,
    fixHint: `Add an anchor or button that navigates to \`${name}\`, or remove the screen if unused.`,
    severity: "warning"
  }));
};
var checkSparseInstances = (json) => {
  const otItems = sectionItems(json.objectTypes);
  const typeNames2 = /* @__PURE__ */ new Set();
  for (const { node: ot } of otItems) {
    if (typeof ot.name === "string") typeNames2.add(ot.name);
  }
  const counts = /* @__PURE__ */ new Map();
  forEachSeedRecord(json.objectInstances, ({ type: type14 }) => {
    var _a2;
    if (typeof type14 === "string" && typeNames2.has(type14)) {
      counts.set(type14, ((_a2 = counts.get(type14)) != null ? _a2 : 0) + 1);
    }
  });
  const errors = [];
  otItems.forEach(({ node: ot, path: at }) => {
    var _a2;
    const name = ot.name;
    if (typeof name !== "string") return;
    const count = (_a2 = counts.get(name)) != null ? _a2 : 0;
    if (count === 1) {
      errors.push({
        path: pathStr3(["objectTypes", ...at]),
        kind: "sparse-instances",
        message: `ObjectType \`${name}\` has only 1 inline instance; expected at least 2 (or 0 if the type is populated from Storage).`,
        fixHint: `Add another \`${name}\` record under objectInstances with realistic field values, or remove the lone placeholder if the type is meant to be storage-backed.`,
        // Info tier: useful during AI generation but never blocking.
        severity: "info"
      });
    }
  });
  return errors;
};
var isObj17 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);
var pathStr3 = (segs) => segs.reduce((acc, s) => {
  if (typeof s === "number") return `${acc}[${s}]`;
  if (acc === "") return String(s);
  return `${acc}.${s}`;
}, "");

// src/validation/transitions.ts
var isObj18 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var REALTIME_WRITE_FNS = /* @__PURE__ */ new Set([
  "pushRealtime",
  "setRealtime",
  "updateRealtime"
]);
var APP_VALUE_FN = "setAppValue";
var PRESENCE_VAR2 = "presence";
var pathStr4 = (segs) => segs.reduce((acc, s) => {
  if (typeof s === "number") return `${acc}[${s}]`;
  if (acc === "") return String(s);
  return `${acc}.${s}`;
}, "");
var collectRealtimeTypes = (json, out) => {
  if (Array.isArray(json)) {
    json.forEach((c) => collectRealtimeTypes(c, out));
    return;
  }
  if (!isObj18(json)) return;
  const source = json.source;
  const dataType = json.dataType;
  if (source === "realtime" && typeof dataType === "string" && dataType) {
    out.add(dataType);
  }
  for (const v of Object.values(json)) collectRealtimeTypes(v, out);
};
var collectPrivateRealtimeTypes = (json, out) => {
  if (Array.isArray(json)) {
    json.forEach((c) => collectPrivateRealtimeTypes(c, out));
    return;
  }
  if (!isObj18(json)) return;
  const { source, dataType, scope } = json;
  if (source === "realtime" && typeof dataType === "string" && dataType && (scope === "user" || scope === "all")) {
    out.add(dataType);
  }
  for (const v of Object.values(json)) collectPrivateRealtimeTypes(v, out);
};
var collectInboxRealtimeTypes = (json, out) => {
  if (Array.isArray(json)) {
    json.forEach((c) => collectInboxRealtimeTypes(c, out));
    return;
  }
  if (!isObj18(json)) return;
  const { source, dataType, scope } = json;
  if (source === "realtime" && typeof dataType === "string" && dataType && scope === "inbox") {
    out.add(dataType);
  }
  for (const v of Object.values(json)) collectInboxRealtimeTypes(v, out);
};
var PLATFORM_WRITTEN_REALTIME_TYPES = ["VideoSeat", "VideoRoom"];
var collectWritableRealtimeTypes = (json, out) => {
  if (Array.isArray(json)) {
    json.forEach((c) => collectWritableRealtimeTypes(c, out));
    return;
  }
  if (!isObj18(json)) return;
  const scan = (script) => {
    if (typeof script !== "string" || !script) return;
    const body = script.startsWith("@") ? script.slice(1) : script;
    try {
      collectWriteCalls(jsep(body), out);
    } catch {
    }
  };
  const isScriptNode = json.type === "flowStep:script" || typeof json.type === "string" && isVariant({ type: json.type, variant: json.variant }, FLOW_STEP, "script");
  if (isScriptNode) scan(json.script);
  if (isObj18(json.actions)) {
    for (const action of Object.values(json.actions)) {
      if (isObj18(action)) scan(action.script);
    }
  }
  for (const v of Object.values(json)) collectWritableRealtimeTypes(v, out);
};
var staticPathPrefix = (arg) => {
  if (!isAstNode5(arg)) return void 0;
  if (arg.type === "Literal") {
    return typeof arg.value === "string" ? arg.value : void 0;
  }
  if (arg.type === "BinaryExpression" && arg.operator === "+") {
    return staticPathPrefix(arg.left);
  }
  return void 0;
};
var typeFromPathArg = (arg) => {
  const prefix = staticPathPrefix(arg);
  if (prefix === void 0) return void 0;
  const typeName = prefix.split("/")[0];
  return typeName ? typeName : void 0;
};
var presencePathArg = (arg) => {
  if (!isAstNode5(arg) || arg.type !== "ObjectExpression") return void 0;
  const props = Array.isArray(arg.properties) ? arg.properties : [];
  for (const prop5 of props) {
    if (!isAstNode5(prop5) || prop5.computed === true) continue;
    const key = prop5.key;
    const named = isAstNode5(key) && key.type === "Identifier" ? key.name : isAstNode5(key) && key.type === "Literal" ? key.value : void 0;
    if (named === "path") return prop5.value;
  }
  return void 0;
};
var collectWriteCalls = (ast, out) => {
  if (Array.isArray(ast)) {
    ast.forEach((c) => collectWriteCalls(c, out));
    return;
  }
  if (!isAstNode5(ast)) return;
  if (ast.type === "CallExpression" && isAstNode5(ast.callee) && ast.callee.type === "Identifier" && typeof ast.callee.name === "string") {
    const fn = ast.callee.name;
    const args = Array.isArray(ast.arguments) ? ast.arguments : [];
    if (REALTIME_WRITE_FNS.has(fn)) {
      const typeName = typeFromPathArg(args[0]);
      if (typeName) out.add(typeName);
    } else if (fn === APP_VALUE_FN) {
      const target = args[0];
      const isPresence = isAstNode5(target) && target.type === "Literal" && target.value === PRESENCE_VAR2;
      if (isPresence) {
        const typeName = typeFromPathArg(presencePathArg(args[1]));
        if (typeName) out.add(typeName);
      }
    }
  }
  for (const [k, v] of Object.entries(ast)) {
    if (k === "parent") continue;
    collectWriteCalls(v, out);
  }
};
var isNonEmptyString = (v) => typeof v === "string" && v.trim().length > 0;
var INPUT_RECORD_VARS = /* @__PURE__ */ new Set(["$record", "$recordId"]);
var RULE_OWNER = {
  noun: "rule",
  prefix: "",
  recordVars: "`$target`/`$record`"
};
var referencedIdentifiers = (src, targets, functions) => {
  const found = /* @__PURE__ */ new Set();
  const walkedFns = /* @__PURE__ */ new Set();
  const seen = /* @__PURE__ */ new WeakSet();
  const walkSource = (source) => {
    const ast = parseBody(source);
    if (ast !== void 0) walk8(ast);
  };
  const walk8 = (node2) => {
    if (Array.isArray(node2)) {
      node2.forEach(walk8);
      return;
    }
    if (!isAstNode5(node2)) return;
    if (seen.has(node2)) return;
    seen.add(node2);
    if (node2.type === "Identifier" && typeof node2.name === "string") {
      const { name } = node2;
      if (targets.has(name)) found.add(name);
      const fn = functions.get(name);
      if (fn && !walkedFns.has(name)) {
        walkedFns.add(name);
        walkSource(fn.compute);
      }
    }
    for (const [k, v] of Object.entries(node2)) {
      if (k === "parent") continue;
      if (k === "property" && node2.type === "MemberExpression" && node2.computed !== true) {
        continue;
      }
      walk8(v);
    }
  };
  walkSource(src);
  return [...found].sort();
};
var IMPURE_DUE_IDENTIFIERS = /* @__PURE__ */ new Set(["$now", "$seed", "rand", "generateId"]);
var PLAIN_TARGET_FIELD = /^@\s*\$target\.[A-Za-z_$][\w$]*\s*$/;
var NOTIFY_FORMULA_FIELDS = [
  "notifyTo",
  "notifyTitle",
  "notifyBody",
  "notifyKey",
  "notifyClickVariant"
];
var SMS_FORMULA_FIELDS = ["smsTo", "smsBody", "smsReadUids"];
var PRIVATE_VARS = /* @__PURE__ */ new Set(["$private"]);
var SEED_ABSENT_VARS = /* @__PURE__ */ new Set([
  "$target",
  "$records",
  "$private"
]);
var ARCHIVE_FORMULA_FIELDS = ["archiveData", "archiveId"];
var ARCHIVE_FIELDS = ["archiveType", ...ARCHIVE_FORMULA_FIELDS];
var inputRecordVarsIn = (src, functions) => referencedIdentifiers(src, INPUT_RECORD_VARS, functions);
var impureDueRefsIn = (src, functions) => referencedIdentifiers(src, IMPURE_DUE_IDENTIFIERS, functions);
var validateTransitions = (json) => {
  if (!isObj18(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj18(appConfig3)) return [];
  const raw = appConfig3.transitions;
  if (raw === void 0) return [];
  const isArray2 = Array.isArray(raw);
  const rules = (isArray2 ? raw : [raw]).filter(isObj18);
  const basePath = (i) => isArray2 ? ["appConfig", "transitions", i] : ["appConfig", "transitions"];
  const realtimeTypes = /* @__PURE__ */ new Set();
  collectRealtimeTypes(json, realtimeTypes);
  const privateRealtimeTypes = /* @__PURE__ */ new Set();
  collectPrivateRealtimeTypes(json, privateRealtimeTypes);
  const inboxRealtimeTypes = /* @__PURE__ */ new Set();
  collectInboxRealtimeTypes(json, inboxRealtimeTypes);
  const writableTypes = new Set(PLATFORM_WRITTEN_REALTIME_TYPES);
  collectWritableRealtimeTypes(json, writableTypes);
  const objectTypeNames3 = new Set(collectObjectTypeFieldTypes(json).keys());
  const functions = /* @__PURE__ */ new Map();
  collectFunctionNodes2(json, [], functions);
  const callable = /* @__PURE__ */ new Set([
    ...CALLABLE_FUNCTION_NAMES,
    ...HOST_FUNCTIONS.map((h) => h.name),
    "$rows",
    ...functions.keys()
  ]);
  const consts = collectConstNodes(json);
  const ruleKnown = /* @__PURE__ */ new Set([
    ...TRANSITION_SCOPE_VARS,
    ...CALLABLE_FUNCTION_NAMES,
    ...HOF_MAGIC_VARS,
    ...JS_GLOBALS,
    ...functions.keys(),
    ...consts.keys()
  ]);
  const targetTypes = /* @__PURE__ */ new Set();
  for (const rule of rules) {
    if (rule.enabled === false) continue;
    if (isNonEmptyString(rule.targetType)) targetTypes.add(rule.targetType);
  }
  const errors = [];
  const timerTargetTypes = /* @__PURE__ */ new Map();
  const perpetualTypes = /* @__PURE__ */ new Map();
  rules.forEach((rule, i) => {
    var _a2;
    if (rule.enabled === false) return;
    const base = basePath(i);
    const watchType = rule.watchType;
    const targetType = rule.targetType;
    const trigger = rule.trigger;
    const isTimer = trigger === "onTimer";
    const triggerUnset = trigger === void 0 || trigger === null || typeof trigger === "string" && trigger.trim() === "";
    if (!triggerUnset && trigger !== "onWrite" && !isTimer) {
      errors.push({
        path: pathStr4([...base, "trigger"]),
        kind: "transition-unknown-trigger",
        message: `Unknown trigger \`${String(
          trigger
        )}\`; expected \`onWrite\` or \`onTimer\`.`,
        fixHint: 'Use `trigger: "onWrite"` (fired by a realtime write of watchType) or `trigger: "onTimer"` (fired when the deadline computed by dueAt passes).',
        severity: "error"
      });
    }
    const isPerpetual = isTimer && rule.perpetual === true;
    if (isPerpetual && isNonEmptyString(targetType)) {
      perpetualTypes.set(targetType, [
        ...(_a2 = perpetualTypes.get(targetType)) != null ? _a2 : [],
        i
      ]);
    } else if (isTimer && isNonEmptyString(targetType)) {
      const entry = timerTargetTypes.get(targetType);
      const hasEol = rule.endOfLife === true;
      if (entry) {
        entry.hasEol = entry.hasEol || hasEol;
      } else {
        timerTargetTypes.set(targetType, { hasEol, firstIndex: i });
      }
    }
    const hasDueAt = isNonEmptyString(rule.dueAt);
    if (isTimer && !hasDueAt) {
      errors.push({
        path: pathStr4([...base, "dueAt"]),
        kind: "transition-timer-missing-due",
        message: "An `onTimer` rule needs a non-empty `dueAt` \u2014 without a deadline to schedule against, nothing ever arms a timer for its records, so the rule can never fire.",
        fixHint: "Add an @-formula over $target returning an epoch-ms timestamp, e.g. `@$target.turnStartedAt + 600000`.",
        severity: "error"
      });
    }
    if (!isTimer && hasDueAt) {
      errors.push({
        path: pathStr4([...base, "dueAt"]),
        kind: "transition-due-at",
        message: "`dueAt` only applies to an `onTimer` rule; this rule fires on a write, so its deadline is never evaluated.",
        fixHint: 'Set `trigger: "onTimer"` if this should fire on a deadline, or fold the guard into `condition`.',
        severity: "error"
      });
    }
    if (isTimer) {
      const unexpected = [];
      if (isNonEmptyString(rule.watchType)) {
        unexpected.push([
          "watchType",
          "a timer rule is fired by its own deadline, not by a write"
        ]);
      }
      if (isNonEmptyString(rule.targetId) && !isPerpetual) {
        unexpected.push([
          "targetId",
          "the timer is armed against one specific record, so there is nothing to compute"
        ]);
      }
      if (rule.onDelete === true) {
        unexpected.push([
          "onDelete",
          "a timer rule watches nothing, so there is no delete to fire on"
        ]);
      }
      if (isNonEmptyString(rule.notifyTurnUidField)) {
        unexpected.push([
          "notifyTurnUidField",
          "timer commits never send turn pushes (only `onWrite` rules are indexed for `pushNotifyTurn`)"
        ]);
      }
      for (const [field2, why] of unexpected) {
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-timer-unexpected-field",
          message: `\`${field2}\` is not valid on an \`onTimer\` rule \u2014 ${why}.`,
          fixHint: `Remove \`${field2}\`, or switch the rule to \`trigger: "onWrite"\`.`,
          severity: "error"
        });
      }
    }
    if (!isTimer && rule.endOfLife === true) {
      errors.push({
        path: pathStr4([...base, "endOfLife"]),
        kind: "transition-timer-unexpected-field",
        message: "`endOfLife` is not valid on an `onWrite` rule \u2014 it flags a timer rule's `next` as the deadline this object's lifecycle ends at.",
        fixHint: 'Remove `endOfLife`, or switch the rule to `trigger: "onTimer"`.',
        severity: "error"
      });
    }
    if (rule.archive === true) {
      if (!isTimer) {
        errors.push({
          path: pathStr4([...base, "archive"]),
          kind: "transition-timer-unexpected-field",
          message: "`archive` is not valid on an `onWrite` rule \u2014 it archives a record to Storage when its end-of-life timer fires.",
          fixHint: "Remove `archive`, or make this an `onTimer` rule with `endOfLife: true`.",
          severity: "error"
        });
      } else if (rule.endOfLife !== true) {
        errors.push({
          path: pathStr4([...base, "archive"]),
          kind: "transition-archive-not-eol",
          message: "`archive` needs `endOfLife: true` on the same rule \u2014 it copies the record to Storage just before the end-of-life delete. On a rule that leaves the record alive it would publish a new copy on every firing.",
          fixHint: 'Add `endOfLife: true` (with a `next` of `"@null"`), or remove `archive`.',
          severity: "error"
        });
      }
    }
    if (rule.perpetual === true && !isTimer) {
      errors.push({
        path: pathStr4([...base, "perpetual"]),
        kind: "transition-perpetual-not-timer",
        message: "`perpetual` only applies to an `onTimer` rule \u2014 it marks a recurring schedule record that fires at a wall-clock time and re-arms itself.",
        fixHint: 'Set `trigger: "onTimer"` with `dueAt: "@$target.nextAt"`, or remove `perpetual`.',
        severity: "error"
      });
    }
    if (isPerpetual) {
      const missing = [];
      if (!isNonEmptyString(rule.targetId)) {
        missing.push([
          "targetId",
          "Name the one schedule record this rule keeps alive, e.g. `daily`."
        ]);
      }
      if (!isNonEmptyString(rule.seed)) {
        missing.push([
          "seed",
          "Add an @-formula for the record's first state, e.g. `@{ nextAt: nextOccurrence($now, '05:17', 'Europe/London') }`."
        ]);
      }
      for (const [field2, fixHint] of missing) {
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-missing-field",
          message: `A \`perpetual\` rule is missing a non-empty \`${field2}\`.`,
          fixHint,
          severity: "error"
        });
      }
      if (isNonEmptyString(rule.targetId) && rule.targetId.trim().startsWith("@")) {
        errors.push({
          path: pathStr4([...base, "targetId"]),
          kind: "transition-perpetual-target-id",
          message: "A `perpetual` rule's `targetId` must be a plain id, not a formula \u2014 it names the single schedule record, and there is no input record to compute one from.",
          fixHint: "Use a literal id such as `daily`.",
          severity: "error"
        });
      }
      if (isNonEmptyString(rule.dueAt) && !PLAIN_TARGET_FIELD.test(rule.dueAt)) {
        errors.push({
          path: pathStr4([...base, "dueAt"]),
          kind: "transition-perpetual-due-not-field",
          message: "A `perpetual` rule's `dueAt` must read one field of the schedule record, like `@$target.nextAt` \u2014 that field is what `next` moves to the next occurrence, so the deadline it reads always lies ahead.",
          fixHint: 'Use `dueAt: "@$target.nextAt"` and set `nextAt` in `seed` and `next` with `nextOccurrence(...)`.',
          severity: "error"
        });
      }
      for (const field2 of ["endOfLife", "archive"]) {
        if (rule[field2] !== true) continue;
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-timer-unexpected-field",
          message: `\`${field2}\` is not valid on a \`perpetual\` rule \u2014 a schedule record never ends.`,
          fixHint: `Remove \`${field2}\`, or remove \`perpetual\` to make this an ordinary timer rule.`,
          severity: "error"
        });
      }
    }
    if (isPerpetual && isNonEmptyString(rule.seed)) {
      const absent = referencedIdentifiers(
        rule.seed,
        SEED_ABSENT_VARS,
        functions
      );
      if (absent.length > 0) {
        errors.push({
          path: pathStr4([...base, "seed"]),
          kind: "transition-timer-record-var",
          message: `\`seed\` references ${absent.map((v) => `\`${v}\``).join(
            " and "
          )}, but it runs at publish, before the schedule record exists \u2014 only \`$now\`, \`$params\` and \`$seed\` are available, so this is always undefined.`,
          fixHint: "Build the first state from `$now`, e.g. `@{ nextAt: nextOccurrence($now, '05:17', 'Europe/London'), round: 0 }`.",
          severity: "error"
        });
      }
    }
    if (isNonEmptyString(rule.seed) && !isPerpetual) {
      errors.push({
        path: pathStr4([...base, "seed"]),
        kind: "transition-timer-unexpected-field",
        message: "`seed` only applies to a `perpetual` timer rule \u2014 it is the schedule record's first state, written at publish.",
        fixHint: "Remove `seed`, or mark the rule `perpetual: true`.",
        severity: "error"
      });
    }
    if (rule.archive !== true) {
      for (const field2 of ARCHIVE_FIELDS) {
        if (!isNonEmptyString(rule[field2])) continue;
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-archive-field-without-archive",
          message: `\`${field2}\` does nothing without \`archive: true\` on the same rule \u2014 it shapes the Storage copy an end-of-life timer makes.`,
          fixHint: `Add \`archive: true\` (on an \`onTimer\` rule with \`endOfLife: true\`), or remove \`${field2}\`.`,
          severity: "error"
        });
      }
    } else {
      const archiveType = rule.archiveType;
      if (isNonEmptyString(archiveType) && !objectTypeNames3.has(archiveType)) {
        errors.push({
          path: pathStr4([...base, "archiveType"]),
          kind: "transition-unknown-type",
          message: `archiveType \`${archiveType}\` is not a declared objectType.`,
          fixHint: nearMissHint(archiveType, objectTypeNames3, "objectType"),
          severity: "error"
        });
      }
      const data = rule.archiveData;
      if (isNonEmptyString(data) && data.replace(/^@/, "").trim() === "$target") {
        errors.push({
          path: pathStr4([...base, "archiveData"]),
          kind: "transition-archive-unprojected",
          message: "`archiveData` is the whole `$target`, the same as leaving it unset \u2014 every field of the record, private ones included, lands in world-readable Storage.",
          fixHint: "Return only the fields that may be public, e.g. `@{ title: $target.title, date: $target.date }`, or remove `archiveData`.",
          severity: "warning"
        });
      }
      const id = rule.archiveId;
      if (isNonEmptyString(id)) {
        const impure = impureDueRefsIn(id, functions);
        if (impure.length > 0) {
          errors.push({
            path: pathStr4([...base, "archiveId"]),
            kind: "transition-archive-impure-id",
            message: `\`archiveId\` references ${impure.map((v) => `\`${v}\``).join(
              " and "
            )}, so a retried firing computes a different id and archives a second copy instead of overwriting its own.`,
            fixHint: "Build the id from the record's own fields, e.g. `@$target.id + '-' + $target.date`.",
            severity: "error"
          });
        }
      }
    }
    const required = isTimer ? ["targetType", "next"] : ["watchType", "targetType", "targetId", "next"];
    const MISSING_HINTS = {
      watchType: "Name the realtime object type whose writes should fire the rule.",
      targetType: isTimer ? "Name the realtime object type whose records carry the timers." : "Name the realtime object type the engine should write.",
      targetId: "Add an @-formula over $record producing the target record id.",
      next: "Add an @-formula returning the full next target state."
    };
    for (const field2 of required) {
      if (!isNonEmptyString(rule[field2])) {
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-missing-field",
          message: `Transition rule is missing a non-empty \`${field2}\`.`,
          fixHint: MISSING_HINTS[field2],
          severity: "error"
        });
      }
    }
    if (isTimer) {
      for (const field2 of [
        "dueAt",
        "condition",
        "next",
        "seed",
        ...NOTIFY_FORMULA_FIELDS,
        ...SMS_FORMULA_FIELDS,
        ...ARCHIVE_FORMULA_FIELDS
      ]) {
        const src = rule[field2];
        if (!isNonEmptyString(src)) continue;
        const vars = inputRecordVarsIn(src, functions);
        if (vars.length === 0) continue;
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-timer-record-var",
          message: `\`${field2}\` references ${vars.map((v) => `\`${v}\``).join(
            " and "
          )}, but an \`onTimer\` rule has no input record \u2014 it fires on a target record, so this is always undefined at runtime.`,
          fixHint: "Use `$target` (the record the timer is armed against) instead of `$record`, and `$records` for its siblings.",
          severity: "error"
        });
      }
    }
    if (isTimer && isNonEmptyString(rule.dueAt)) {
      const impure = impureDueRefsIn(rule.dueAt, functions);
      if (impure.length > 0) {
        errors.push({
          path: pathStr4([...base, "dueAt"]),
          kind: "transition-timer-impure-due",
          message: `\`dueAt\` references ${impure.map((v) => `\`${v}\``).join(
            " and "
          )}, so it computes a different deadline every time it is evaluated \u2014 the timer re-arms an unchanged record on every write and fires in an unbounded loop.`,
          fixHint: "Anchor the deadline on a record field your `next` stamps, e.g. `@$target.turnStartedAt + 600000` \u2014 `$now` belongs in `next` (to move the anchor) or `condition`, never in the deadline value.",
          severity: "error"
        });
      }
    }
    for (const field2 of [
      "targetId",
      "condition",
      "next",
      "nextPrivate",
      // #1369's read half. It DOES have a runtime backstop the other fields
      // lack (`normalizePrivateReadKeys` rejects a non-array loudly), but a
      // typo'd helper still gets there as `undefined` with a message about the
      // shape rather than the name — so the gate still says it better.
      "privateReadUids",
      "dueAt",
      "seed",
      "rejectionReason",
      ...NOTIFY_FORMULA_FIELDS,
      ...SMS_FORMULA_FIELDS,
      ...ARCHIVE_FORMULA_FIELDS
    ]) {
      const src = rule[field2];
      if (!isNonEmptyString(src)) continue;
      if (field2 === "targetId" && rule.perpetual === true) continue;
      for (const called of unknownCallsIn(src, callable)) {
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-unknown-call",
          message: `\`${field2}\` calls \`${called}()\`, which is not a built-in formula function or an org \`function\` node \u2014 the evaluator treats an unknown call as a no-op, so this silently evaluates to \`undefined\`.`,
          // A const is a VALUE (#2317) — calling it is the likely slip when
          // a zero-arg `maxSeatsFn()` was converted to a `maxSeats` const.
          fixHint: consts.has(called) ? `\`${called}\` is a const \u2014 drop the \`()\`` : nearMissHint(called, new Set(callable), "callable function"),
          severity: "error"
        });
      }
      for (const miss of ruleScopeMissesIn(src, ruleKnown, functions)) {
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-unknown-identifier",
          ...ruleScopeMissText(miss, field2, RULE_OWNER),
          severity: "error"
        });
      }
    }
    for (const [field2, value] of [
      ["watchType", watchType],
      ["targetType", targetType]
    ]) {
      if (isNonEmptyString(value) && !realtimeTypes.has(value)) {
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-unknown-type",
          message: `${field2} \`${value}\` is not a realtime object type (no data node binds it with source: "realtime").`,
          fixHint: nearMissHint(value, realtimeTypes, "realtime object type"),
          severity: "error"
        });
      }
    }
    if (isNonEmptyString(watchType) && isNonEmptyString(targetType) && watchType === targetType) {
      errors.push({
        path: pathStr4([...base, "targetType"]),
        kind: "transition-self-target",
        message: `targetType must differ from watchType (both are \`${watchType}\`); self-chains are not allowed in v1.`,
        fixHint: "Watch one realtime type and write a different one.",
        severity: "error"
      });
    }
    const hasNotifyTo = isNonEmptyString(rule.notifyTo);
    if (hasNotifyTo && !isNonEmptyString(rule.notifyTitle)) {
      errors.push({
        path: pathStr4([...base, "notifyTitle"]),
        kind: "transition-notify-incomplete",
        message: "Rule sets `notifyTo` without `notifyTitle`; a push needs a title.",
        fixHint: "Add `notifyTitle`, an @-formula over the committed `$target` (e.g. `@'Reminder: ' + $target.title`).",
        severity: "error"
      });
    }
    if (!hasNotifyTo) {
      for (const field2 of [
        "notifyTitle",
        "notifyBody",
        "notifyKey",
        "notifyClickVariant"
      ]) {
        if (!isNonEmptyString(rule[field2])) continue;
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-notify-incomplete",
          message: `Rule sets \`${field2}\` without \`notifyTo\`, so nobody is ever sent it.`,
          fixHint: "Add `notifyTo` (an @-formula listing auth uids), or remove the field.",
          severity: "error"
        });
      }
    }
    if (hasNotifyTo && isNonEmptyString(rule.notifyTurnUidField)) {
      errors.push({
        path: pathStr4([...base, "notifyTo"]),
        kind: "transition-notify-conflict",
        message: "Rule sets both `notifyTo` and `notifyTurnUidField`; one rule sends one kind of push.",
        fixHint: "Use `notifyTo` alone \u2014 `@[$target.turnUid]` addresses one player the same way.",
        severity: "error"
      });
    }
    for (const field2 of NOTIFY_FORMULA_FIELDS) {
      const src = rule[field2];
      if (!isNonEmptyString(src)) continue;
      if (referencedIdentifiers(src, PRIVATE_VARS, functions).length === 0) continue;
      errors.push({
        path: pathStr4([...base, field2]),
        kind: "transition-notify-private",
        message: `\`${field2}\` references \`$private\`, which is always empty after the commit \u2014 a push is built from public state only.`,
        fixHint: "Address recipients by auth uid from the committed `$target` (e.g. a takers list's `uid`).",
        severity: "error"
      });
    }
    const hasSmsTo = isNonEmptyString(rule.smsTo);
    if (hasSmsTo && !isNonEmptyString(rule.smsBody)) {
      errors.push({
        path: pathStr4([...base, "smsBody"]),
        kind: "transition-sms-incomplete",
        message: "Rule sets `smsTo` without `smsBody`; a text needs something to say.",
        fixHint: "Add `smsBody`, an @-formula over the committed `$target` that names the event (e.g. `@'Thanks for helping at ' + $target.title`).",
        severity: "error"
      });
    }
    if (!hasSmsTo) {
      for (const field2 of ["smsBody", "smsReadUids", "smsPrivateType"]) {
        if (!isNonEmptyString(rule[field2])) continue;
        errors.push({
          path: pathStr4([...base, field2]),
          kind: "transition-sms-incomplete",
          message: `Rule sets \`${field2}\` without \`smsTo\`, so nobody is ever sent a text.`,
          fixHint: "Add `smsTo` (an @-formula listing phone numbers), or remove the field.",
          severity: "error"
        });
      }
    }
    const smsPrivateType = rule.smsPrivateType;
    const hasSmsRead = isNonEmptyString(rule.smsReadUids);
    const hasSmsPrivateType = isNonEmptyString(smsPrivateType);
    if (hasSmsTo && hasSmsRead !== hasSmsPrivateType) {
      const missing = hasSmsRead ? "smsPrivateType" : "smsReadUids";
      errors.push({
        path: pathStr4([...base, missing]),
        kind: "transition-sms-incomplete",
        message: `Rule sets \`${hasSmsRead ? "smsReadUids" : "smsPrivateType"}\` without \`${missing}\`; the SMS private read needs both the keys and the type.`,
        fixHint: hasSmsRead ? 'Add `smsPrivateType`: the private realtime type (scope: "user") holding the phone numbers.' : "Add `smsReadUids`: an @-formula listing the keys to read, e.g. `@[$target.ownerUid]`.",
        severity: "error"
      });
    }
    for (const field2 of SMS_FORMULA_FIELDS) {
      const src = rule[field2];
      if (!isNonEmptyString(src)) continue;
      if (field2 !== "smsReadUids" && hasSmsRead) continue;
      if (referencedIdentifiers(src, PRIVATE_VARS, functions).length === 0) continue;
      errors.push({
        path: pathStr4([...base, field2]),
        kind: "transition-sms-private",
        message: field2 === "smsReadUids" ? "`smsReadUids` references `$private`, which it is evaluated before \u2014 the keys must come from the committed `$target`." : `\`${field2}\` references \`$private\`, but the rule declares no \`smsReadUids\`, so \`$private\` is always empty here.`,
        fixHint: field2 === "smsReadUids" ? "Name the keys from public state, e.g. `@[$target.ownerUid]`." : "Add `smsReadUids` and `smsPrivateType` to load the private records holding the phone numbers.",
        severity: "error"
      });
    }
    if (hasSmsPrivateType && inboxRealtimeTypes.has(smsPrivateType)) {
      errors.push({
        path: pathStr4([...base, "smsPrivateType"]),
        kind: "transition-private-type-not-private",
        message: `smsPrivateType \`${smsPrivateType}\` is an inbox type (scope: "inbox"); the SMS read loads the private per-uid rail, \`realtimePrivate\`.`,
        fixHint: `Point smsPrivateType at the type declared with \`scope: "user"\` that holds the numbers.`,
        severity: "error"
      });
    } else if (hasSmsPrivateType && !privateRealtimeTypes.has(smsPrivateType)) {
      const declaredPublicly = realtimeTypes.has(smsPrivateType);
      errors.push({
        path: pathStr4([...base, "smsPrivateType"]),
        kind: "transition-private-type-not-private",
        message: declaredPublicly ? `smsPrivateType \`${smsPrivateType}\` is declared on the PUBLIC realtime rail \u2014 phone numbers stored there are readable by every client.` : `smsPrivateType \`${smsPrivateType}\` is not a private realtime object type (no data node binds it with source: "realtime" and scope: "user").`,
        fixHint: declaredPublicly ? `Set \`scope: "user"\` on the data node binding \`${smsPrivateType}\`, or point smsPrivateType at a different type.` : nearMissHint(
          smsPrivateType,
          privateRealtimeTypes,
          "private realtime object type"
        ),
        severity: "error"
      });
    }
    if (isNonEmptyString(targetType) && inboxRealtimeTypes.has(targetType)) {
      errors.push({
        path: pathStr4([...base, "targetType"]),
        kind: "transition-target-inbox",
        message: `targetType \`${targetType}\` is an inbox type (scope: "inbox"); a rule may watch an inbox type but never write one.`,
        fixHint: 'Target a public realtime type, and use `privateType` + `nextPrivate` (scope: "user") for what only one person should read.',
        severity: "error"
      });
    }
    const nextPrivate = rule.nextPrivate;
    const privateType = rule.privateType;
    const privateReadUids = rule.privateReadUids;
    const hasNextPrivate = isNonEmptyString(nextPrivate);
    const hasPrivateType = isNonEmptyString(privateType);
    const hasPrivateReadUids = isNonEmptyString(privateReadUids);
    if (hasNextPrivate && !hasPrivateType) {
      errors.push({
        path: pathStr4([...base, "privateType"]),
        kind: "transition-private-incomplete",
        message: "Rule sets `nextPrivate` without `privateType`; private per-player writes need both.",
        fixHint: 'Add `privateType`: the realtime object type the private records are written as (declared with `scope: "user"`).',
        severity: "error"
      });
    }
    if (hasPrivateReadUids && !hasPrivateType) {
      errors.push({
        path: pathStr4([...base, "privateType"]),
        kind: "transition-private-read-incomplete",
        message: "Rule sets `privateReadUids` without `privateType`; there is no type to read the private records as.",
        fixHint: 'Add `privateType`: the realtime object type declared with `scope: "user"` whose records this rule reads.',
        severity: "error"
      });
    }
    if (hasPrivateType && !hasNextPrivate && !hasPrivateReadUids) {
      errors.push({
        path: pathStr4([...base, "nextPrivate"]),
        kind: "transition-private-incomplete",
        message: "Rule sets `privateType` with neither `nextPrivate` nor `privateReadUids`; it reads as if private records were configured, but nothing writes or reads them.",
        fixHint: "Add `nextPrivate` (an @-formula returning a map keyed by auth uid), add `privateReadUids` (the keys to read), or remove `privateType`.",
        severity: "error"
      });
    }
    if (hasPrivateReadUids && isTimer) {
      errors.push({
        path: pathStr4([...base, "privateReadUids"]),
        kind: "transition-private-read-timer",
        message: "`privateReadUids` is not supported on an `onTimer` rule; the timer path evaluates with an empty `$private`.",
        fixHint: "Drive the private read from an `onWrite` rule watching a player-written input record, or drop `privateReadUids`.",
        severity: "error"
      });
    }
    if (hasPrivateType && inboxRealtimeTypes.has(privateType)) {
      errors.push({
        path: pathStr4([...base, "privateType"]),
        kind: "transition-private-type-not-private",
        message: `privateType \`${privateType}\` is an inbox type (scope: "inbox"), not the private per-uid rail the engine writes private records to.`,
        fixHint: `Use a separate type declared with \`scope: "user"\` for the private records.`,
        severity: "error"
      });
    } else if (hasPrivateType && !privateRealtimeTypes.has(privateType)) {
      const declaredPublicly = realtimeTypes.has(privateType);
      errors.push({
        path: pathStr4([...base, "privateType"]),
        kind: "transition-private-type-not-private",
        message: declaredPublicly ? `privateType \`${privateType}\` is declared on the PUBLIC realtime rail \u2014 records the engine writes as private would land in a collection every client subscribes to.` : `privateType \`${privateType}\` is not a private realtime object type (no data node binds it with source: "realtime" and scope: "user").`,
        fixHint: declaredPublicly ? `Set \`scope: "user"\` on the data node binding \`${privateType}\`, or point privateType at a different type.` : nearMissHint(
          privateType,
          privateRealtimeTypes,
          "private realtime object type"
        ),
        severity: "error"
      });
    }
    if (isNonEmptyString(watchType) && targetTypes.has(watchType)) {
      errors.push({
        path: pathStr4([...base, "watchType"]),
        kind: "transition-loop",
        message: `Rule watches \`${watchType}\`, which is written by a transition rule \u2014 rule chains are not allowed in v1.`,
        fixHint: "Restructure so no rule watches a type produced by another rule.",
        severity: "error"
      });
    }
    if (isNonEmptyString(watchType) && realtimeTypes.has(watchType) && !writableTypes.has(watchType)) {
      errors.push({
        path: pathStr4([...base, "watchType"]),
        kind: "transition-dead-rule",
        message: `No script writes \`${watchType}\` via pushRealtime/setRealtime/updateRealtime (or a \`presence\` descriptor), so this rule can never fire.`,
        fixHint: "Confirm the watched type is written somewhere, or remove the rule.",
        severity: "error"
      });
    }
    if (isTimer && // A perpetual rule's record is written by the PUBLISH itself (the
    // reconcile seeds it from `seed`, #1521), so no script needs to.
    rule.perpetual !== true && isNonEmptyString(targetType) && realtimeTypes.has(targetType) && !writableTypes.has(targetType)) {
      errors.push({
        path: pathStr4([...base, "targetType"]),
        kind: "transition-dead-rule",
        message: `No script writes \`${targetType}\` via pushRealtime/setRealtime/updateRealtime (or a \`presence\` descriptor), so no record of that type ever exists to arm a timer against and this rule can never fire.`,
        fixHint: "Write the target type from a script, or, for one schedule record (a daily reminder, a nightly reset), make this rule `perpetual: true` with a `targetId` and a `seed`: publishing creates that record, so no script needs to. `og reference recipes daily-push-reminder` shows the shape.",
        severity: "error"
      });
    }
  });
  for (const fn of functions.values()) {
    const vars = referencedIdentifiers(
      fn.compute,
      TRANSITION_SCOPE_VARS,
      // Deliberately NOT following calls: a var reached through a callee is
      // that callee's error, reported on that node, so following would just
      // duplicate it onto every caller.
      /* @__PURE__ */ new Map()
    );
    if (vars.length === 0) continue;
    errors.push({
      path: pathStr4([...fn.path, "compute"]),
      kind: "function-transition-scope-var",
      message: `Function \`${fn.name}\` references ${vars.map((v) => `\`${v}\``).join(
        " and "
      )}, but a \`function\` node's body has no transition scope of its own \u2014 it resolves only when called from a transition rule, and is \`undefined\` everywhere else.`,
      fixHint: `Add a parameter (e.g. \`parameters: "${fn.parameters ? `${fn.parameters}, ` : ""}target"\`) and pass the value in from the rule: \`@${fn.name}(${fn.parameters ? `${fn.parameters}, ` : ""}$target)\`.`,
      severity: "error"
    });
  }
  for (const [targetType, { hasEol, firstIndex }] of timerTargetTypes) {
    if (hasEol) continue;
    errors.push({
      path: pathStr4([...basePath(firstIndex), "targetType"]),
      kind: "transition-timer-missing-eol",
      message: `\`${targetType}\` has one or more \`onTimer\` rules but none flagged \`endOfLife: true\` \u2014 an object type with timers needs a clear end-of-life, or a re-arming timer has nothing to bound it.`,
      fixHint: `Add an \`endOfLife: true\` rule on \`${targetType}\` whose \`next\` deletes the record (conventionally \`next: "@null"\`).`,
      severity: "error"
    });
  }
  for (const [targetType, indices] of perpetualTypes) {
    const shared = indices.length > 1 || timerTargetTypes.has(targetType);
    if (!shared) continue;
    errors.push({
      path: pathStr4([...basePath(indices[0]), "targetType"]),
      kind: "transition-perpetual-mixed-type",
      message: `\`${targetType}\` carries a \`perpetual\` rule and other timer rules \u2014 a schedule needs its own object type, with exactly one perpetual rule and nothing else timed on it.`,
      fixHint: `Give each schedule its own type (e.g. \`DailyReset\`, \`WeeklyRotation\`), and keep ordinary timers on their own types.`,
      severity: "error"
    });
  }
  return errors;
};

// src/validation/botActors.ts
var REQUIRED_FIELDS2 = [
  "watchType",
  "moveType",
  "isBotTurn",
  "moveId",
  "move"
];
var BOT_ACTOR_FORMULA_FIELDS2 = [
  "isBotTurn",
  "privateKey",
  "privateReady",
  "state",
  "questions",
  "moveId",
  "move",
  "fallbackMove",
  "delayMs"
];
var isNonEmptyString2 = (v) => typeof v === "string" && v.length > 0;
var validateBotActors = (json) => {
  if (!isObj18(json)) return [];
  const appConfig3 = json.appConfig;
  if (!isObj18(appConfig3)) return [];
  const raw = appConfig3.botActors;
  if (raw === void 0) return [];
  const isArray2 = Array.isArray(raw);
  const actors = (isArray2 ? raw : [raw]).filter(isObj18);
  const basePath = (i) => isArray2 ? ["appConfig", "botActors", i] : ["appConfig", "botActors"];
  const realtimeTypes = /* @__PURE__ */ new Set();
  collectRealtimeTypes(json, realtimeTypes);
  const privateTypes = /* @__PURE__ */ new Set();
  collectPrivateRealtimeTypes(json, privateTypes);
  const functions = /* @__PURE__ */ new Map();
  collectFunctionNodes2(json, [], functions);
  const callable = /* @__PURE__ */ new Set([
    ...CALLABLE_FUNCTION_NAMES,
    ...HOST_FUNCTIONS.map((h) => h.name),
    ...functions.keys()
  ]);
  const consts = collectConstNodes(json);
  const actorKnown = /* @__PURE__ */ new Set([
    ...BOT_ACTOR_SCOPE_VARS,
    ...CALLABLE_FUNCTION_NAMES,
    ...HOF_MAGIC_VARS,
    ...JS_GLOBALS,
    ...functions.keys(),
    ...consts.keys()
  ]);
  const errors = [];
  actors.forEach((actor, i) => {
    if (actor.enabled === false) return;
    const path = basePath(i);
    const p = (field2) => pathStr4([...path, field2]);
    const label2 = isNonEmptyString2(actor.label) ? actor.label : `actor ${i}`;
    for (const field2 of REQUIRED_FIELDS2) {
      if (!isNonEmptyString2(actor[field2])) {
        errors.push({
          path: p(field2),
          kind: "botactor-missing-field",
          message: `botActor "${label2}" is missing \`${field2}\`, which every actor needs to act.`,
          fixHint: field2 === "watchType" || field2 === "moveType" ? "Name a realtime object type declared by a data node in this org." : "Provide the @-formula \u2014 see the botActor helper's field help.",
          severity: "error"
        });
      }
    }
    const watchType = actor.watchType;
    const moveType = actor.moveType;
    for (const [field2, type14] of [
      ["watchType", watchType],
      ["moveType", moveType]
    ]) {
      if (isNonEmptyString2(type14) && !realtimeTypes.has(type14)) {
        errors.push({
          path: p(field2),
          kind: "botactor-unknown-type",
          message: `botActor "${label2}" ${field2} \`${type14}\` is not a realtime object type in this org (no data node declares \`{ dataType: "${type14}", source: "realtime" }\`).`,
          fixHint: `Known realtime types: ${[...realtimeTypes].sort().join(", ") || "(none)"}.`,
          severity: "error"
        });
      }
    }
    if (isNonEmptyString2(watchType) && isNonEmptyString2(moveType) && watchType === moveType) {
      errors.push({
        path: p("moveType"),
        kind: "botactor-self-target",
        message: `botActor "${label2}" would write \`${moveType}\`, the very type whose writes wake it \u2014 a bot that wakes itself forever.`,
        fixHint: "Watch the public game record and write the move type (e.g. watchType: Game, moveType: Move).",
        severity: "error"
      });
    }
    const privateKey = actor.privateKey;
    const privateType = actor.privateType;
    if (isNonEmptyString2(privateKey) !== isNonEmptyString2(privateType)) {
      errors.push({
        path: p(isNonEmptyString2(privateKey) ? "privateType" : "privateKey"),
        kind: "botactor-private-incomplete",
        message: isNonEmptyString2(privateKey) ? `botActor "${label2}" has a \`privateKey\` but no \`privateType\`, so the function has no type to read the private record from.` : `botActor "${label2}" has a \`privateType\` but no \`privateKey\`, so nothing would ever be read.`,
        fixHint: "Set both `privateKey` and `privateType`, or neither.",
        severity: "error"
      });
    }
    if (isNonEmptyString2(privateType) && realtimeTypes.has(privateType) && !privateTypes.has(privateType)) {
      errors.push({
        path: p("privateType"),
        kind: "botactor-private-type-not-private",
        message: `botActor "${label2}" privateType \`${privateType}\` is only ever bound publicly \u2014 no data node puts it on the private rail (\`scope: "user"\`), so a hidden hand read from it would be one every client can see.`,
        fixHint: `Bind \`${privateType}\` with \`scope: "user"\` (or "all") on a realtime data node.`,
        severity: "error"
      });
    } else if (isNonEmptyString2(privateType) && !realtimeTypes.has(privateType)) {
      errors.push({
        path: p("privateType"),
        kind: "botactor-unknown-type",
        message: `botActor "${label2}" privateType \`${privateType}\` is not a realtime object type in this org.`,
        fixHint: `Known realtime types: ${[...realtimeTypes].sort().join(", ") || "(none)"}.`,
        severity: "error"
      });
    }
    for (const field2 of BOT_ACTOR_FORMULA_FIELDS2) {
      const src = actor[field2];
      if (!isNonEmptyString2(src)) continue;
      for (const called of unknownCallsIn(src, callable)) {
        errors.push({
          path: p(field2),
          kind: "botactor-unknown-call",
          message: `botActor "${label2}" \`${field2}\` calls \`${called}()\`, which is not a built-in formula function or an org \`function\` node \u2014 the evaluator treats an unknown call as a no-op, so this silently evaluates to \`undefined\`.`,
          // A const is a value, not a function (#2317).
          fixHint: consts.has(called) ? `\`${called}\` is a const \u2014 drop the \`()\`` : `Add a \`function\` node named \`${called}\` or fix the name.`,
          severity: "error"
        });
      }
      for (const miss of ruleScopeMissesIn(src, actorKnown, functions)) {
        errors.push({
          path: p(field2),
          kind: "botactor-unknown-identifier",
          ...ruleScopeMissText(miss, field2, {
            noun: "actor",
            prefix: `botActor "${label2}" \`${field2}\`: `,
            recordVars: "`$record`/`$state`"
          }),
          severity: "error"
        });
      }
    }
  });
  return errors;
};

// src/validation/builtinShadow.ts
var isObj19 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var VALUE_NODE_TYPES = /* @__PURE__ */ new Set([
  VARIABLE,
  TEXT_INPUT,
  NUMBER_INPUT,
  BOOLEAN_INPUT,
  TEMPORAL_INPUT,
  SELECT_INPUT,
  FILE_INPUT
]);
var walk7 = (value, path, errors) => {
  if (Array.isArray(value)) {
    value.forEach((c, i) => walk7(c, [...path, i], errors));
    return;
  }
  if (!isObj19(value)) return;
  const { type: type14, name } = value;
  if (typeof type14 === "string" && VALUE_NODE_TYPES.has(type14) && typeof name === "string" && BARE_BUILTIN_NAMES.has(name)) {
    errors.push({
      path: pathStr([...path, "name"]),
      kind: "name-shadowed-by-builtin",
      message: `\`${name}\` is a formula builtin. While this ${type14} is unset, \`${name}\` in a formula resolves to the builtin function (always truthy), not to this node's value.`,
      fixHint: `Rename the ${type14} (e.g. \`${name}Value\`) and update the formulas that read it.`,
      severity: "error"
    });
  }
  if (Array.isArray(value._children)) {
    walk7(value._children, [...path, "_children"], errors);
  }
};
var validateBuiltinShadow = (json) => {
  if (!isObj19(json) || !isObj19(json.appConfig)) return [];
  const errors = [];
  walk7(json.appConfig, ["appConfig"], errors);
  return errors;
};

// src/validation/consts.ts
var CONST_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;
var invalidNameErrors = (decls) => decls.filter((d) => d.type === CONST && !CONST_NAME.test(d.name)).map((d) => ({
  path: pathStr([...d.path, "name"]),
  kind: "const-name-invalid",
  message: d.name.includes("$") ? `Const \`${d.name}\` contains \`$\`: an RTDB key cannot hold it, so the const is never mirrored to transition rules, and \`$\` is the transition-scope prefix.` : `Const \`${d.name}\` is not a plain identifier, so a formula cannot read it by bare name.`,
  fixHint: "Name a const with letters, digits and `_` only, not starting with a digit (e.g. `maxSeats`).",
  severity: "error"
}));
var clashErrors2 = (decls) => {
  var _a2;
  const groups = /* @__PURE__ */ new Map();
  for (const d of decls) {
    const scope = d.folder ? pathStr(d.folder.path) : "";
    const key = `${scope}\0${d.name}`;
    groups.set(key, [...(_a2 = groups.get(key)) != null ? _a2 : [], d]);
  }
  const errors = [];
  for (const group of groups.values()) {
    const first = group.find((d) => d.type === CONST);
    if (!first || group.length < 2) continue;
    const others = group.filter((d) => d !== first);
    const withFunction = others.some((d) => d.type === FUNCTION);
    errors.push({
      path: pathStr([...first.path, "name"]),
      kind: "const-name-clash",
      message: withFunction ? `\`${first.name}\` names both a const and a function. The server binds consts first and skips the same-named function, so \`${first.name}()\` passes the formula checks and fails at runtime.` : `\`${first.name}\` names ${group.length} consts; only one is ever bound, so the others are silently ignored.`,
      fixHint: "Rename one of them \u2014 consts and functions share one namespace, so every name must be unique across both.",
      severity: "error",
      relatedPaths: others.map((d) => pathStr(d.path))
    });
  }
  return errors;
};
var validateConsts = (json) => {
  const decls = collectCallableDecls(json);
  return [...clashErrors2(decls), ...invalidNameErrors(decls)];
};

// src/validation/features.ts
var isObj20 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var slotNodesOf = (pin) => Array.isArray(pin.slots) ? pin.slots.flatMap(
  (s) => isObj20(s) && typeof s.node === "string" ? [s.node] : []
) : [];
var collectFeatureFolders = (json) => {
  const found = [];
  if (!isObj20(json)) return found;
  const walk8 = (v, section2, path) => {
    if (!isObj20(v)) return;
    const pin = v.feature;
    if (v.type === "folder" && isObj20(pin) && typeof pin.id === "string" && typeof pin.version === "number") {
      found.push({
        section: section2,
        name: typeof v.name === "string" ? v.name : "",
        id: pin.id,
        version: pin.version,
        ...typeof pin.hash === "string" ? { hash: pin.hash } : {},
        slotNodes: slotNodesOf(pin),
        children: Array.isArray(v._children) ? v._children : [],
        path
      });
      return;
    }
    if (Array.isArray(v._children)) {
      v._children.forEach(
        (c, i) => walk8(c, section2, [...path, "_children", i])
      );
    }
  };
  for (const section2 of FEATURE_SECTIONS)
    walk8(json[section2], section2, [section2]);
  return found;
};
var driftedFeatureFolders = (json) => {
  var _a2;
  const folders = collectFeatureFolders(json);
  const slotNodes = /* @__PURE__ */ new Map();
  for (const f of folders) {
    const key = `${f.id}\0${f.name}`;
    slotNodes.set(key, [...(_a2 = slotNodes.get(key)) != null ? _a2 : [], ...f.slotNodes]);
  }
  return folders.filter(
    (f) => {
      var _a3;
      return f.hash !== void 0 && featureFolderHash(
        f.children,
        (_a3 = slotNodes.get(`${f.id}\0${f.name}`)) != null ? _a3 : []
      ) !== f.hash;
    }
  );
};
var featureDriftFinding = (f) => ({
  path: pathStr([...f.path, "feature", "hash"]),
  kind: "feature-folder-drift",
  message: `${f.section}.${f.name} no longer matches ${f.id} v${f.version} as installed \u2014 something inside it was edited.`,
  fixHint: `Fine if the edit was meant: \`og feature status\` reports this folder as diverged. To go back to the bundle, run \`og feature install <org> ${f.id}@${f.version} --force\`, which discards the edit.`,
  severity: "warning"
});
var validateFeatureFolders = (json) => driftedFeatureFolders(json).map(featureDriftFinding);

// src/validation/behavioural.ts
var BY_ID_ARG = /byId\(\s*['"]([A-Za-z_]\w*)['"]\s*,\s*\$args\.([A-Za-z_]\w*)/g;
var FIX_HINT = {
  "run-render-error": "Fix the formula in this htmlTemplate \u2014 it throws with the seed data loaded.",
  "run-dead-interpolation": "A `${` reached the page as text: an interpolation inside a quoted string or a template that never ran.",
  "run-empty-list": "Check data.dataType names the seeded type and that maxLength / visibility don't hide every row.",
  "run-plugin-error": `Each plugin parameter value is a formula: quote literal text ("'My Bookings'"), and pass every declared parameter.`
};
var isObj21 = (x) => typeof x === "object" && x !== null && !Array.isArray(x);
var isOrgTree = (x) => isObj21(x) && isObj21(x.appConfig);
var errorMessage = (e) => e instanceof Error ? e.message : isObj21(e) && "message" in e ? String(e.message) : String(e);
var finding = (kind, path, message) => ({
  path,
  kind,
  message,
  fixHint: FIX_HINT[kind],
  severity: "warning"
});
var rawChild = (raw, node2) => {
  const kids5 = Array.isArray(raw._children) ? raw._children : [];
  const index = kids5.findIndex(
    (k) => isObj21(k) && (node2.id ? k.id === node2.id : node2.name !== void 0 && k.name === node2.name)
  );
  const child = kids5[index];
  return isObj21(child) ? { index, child } : void 0;
};
var runOrg = (tree2, opts) => {
  var _a2, _b;
  let api;
  try {
    api = createOrgPackApi(tree2, { records: opts.records, plugins: true });
  } catch {
    return void 0;
  }
  const findings = [];
  const objects = (_b = (_a2 = api.getState().repo) == null ? void 0 : _a2.objects) != null ? _b : [];
  const rowsOf = (type14) => objects.filter((o) => o.type === type14);
  const detailArgs = (screen2) => {
    var _a3;
    const args = {};
    for (const [, type14, arg] of JSON.stringify(screen2).matchAll(BY_ID_ARG)) {
      const id = (_a3 = rowsOf(type14)[0]) == null ? void 0 : _a3.id;
      if (id !== void 0 && !(arg in args)) args[arg] = String(id);
    }
    return Object.keys(args).length ? args : void 0;
  };
  const render = (key, node2, path) => {
    let html;
    try {
      html = api.renderHtml(key);
    } catch (e) {
      const plugin2 = typeof node2.pluginName === "string" ? node2.pluginName : void 0;
      findings.push(
        plugin2 ? finding("run-plugin-error", path, `${plugin2}(): ${errorMessage(e)}`) : finding("run-render-error", path, errorMessage(e))
      );
      return;
    }
    if (html.includes("${")) {
      findings.push(
        finding(
          "run-dead-interpolation",
          path,
          "Rendered output still contains a literal `${` \u2014 an interpolation that never ran."
        )
      );
    }
  };
  const walk8 = (key, raw, path, onScreen) => {
    var _a3, _b2;
    if (!api.isVisible(key)) return;
    const p = selectPointer(api.getState(), key);
    const { node: node2 } = p;
    let restore = onScreen;
    if (node2.type === SCREEN) {
      const args = detailArgs(node2);
      restore = () => api.setCursor(key, args);
      restore();
    }
    if (node2.type === CONTENT && typeof node2.htmlTemplate === "string") {
      render(key, node2, path);
    }
    let children2 = api.childKeys(key);
    if (isLoop(p)) {
      const { dataType, whereClause, source, span: span2 } = (_a3 = node2.data) != null ? _a3 : {};
      if (typeof dataType === "string" && (source === void 0 || source === "embedded") && !whereClause && !span2 && rowsOf(dataType).length > 0 && api.lengthOf(key) === 0) {
        findings.push(
          finding(
            "run-empty-list",
            path,
            `Bound to ${dataType}, which has ${rowsOf(dataType).length} seed row(s), but shows none.`
          )
        );
      }
      children2 = children2.slice(0, 1);
    }
    for (const child of children2) {
      const c = selectPointer(api.getState(), child);
      if (c.isIteration) {
        walk8(child, raw, path, restore);
      } else {
        const match2 = rawChild(raw, c.node);
        walk8(
          child,
          (_b2 = match2 == null ? void 0 : match2.child) != null ? _b2 : raw,
          match2 ? `${path}._children[${match2.index}]` : path,
          restore
        );
      }
      if (c.node.type === SCREEN) restore == null ? void 0 : restore();
    }
  };
  walk8("", isObj21(tree2.appConfig) ? tree2.appConfig : {}, "appConfig");
  return findings;
};
var smokeOrg = (tree2, opts = {}) => {
  var _a2;
  return (_a2 = runOrg(tree2, opts)) != null ? _a2 : [
    finding(
      "run-render-error",
      "appConfig",
      "The org does not decode, so nothing renders \u2014 run `validate` for the cause."
    )
  ];
};
var validateBehaviour = (json, opts = {}) => {
  var _a2;
  if (!isOrgTree(json)) return [];
  try {
    return (_a2 = runOrg(json, opts)) != null ? _a2 : [];
  } catch {
    return [];
  }
};
var storageTypesOf = (json) => {
  if (!isOrgTree(json)) return [];
  const od = selectOrgData(treeToRawOrgData(json));
  return isLeft(od) ? [] : selectScopedDatasourceObjTypes(od.right.appConfig, "storage");
};

// src/validation/orgValidator.ts
var GENERATION_BLOCKING_KINDS = /* @__PURE__ */ new Set([
  "undeclared-data-read"
]);
var isGenerationBlocking = (e) => e.severity === "error" || GENERATION_BLOCKING_KINDS.has(e.kind);
var validateOrg = (json, opts = {}) => {
  const errors = [];
  errors.push(...validateStructure(json));
  errors.push(...validateDataScope(json));
  errors.push(...validateMediaRooms(json));
  errors.push(...validateAiChatSteps(json));
  errors.push(...validateFlowRepeats(json));
  errors.push(...validateVisionSteps(json));
  errors.push(...validateFormulas(json));
  errors.push(...validateBuiltinShadow(json));
  errors.push(...validateConsts(json));
  errors.push(...validateLibraries(json));
  errors.push(...validateFeatureFolders(json));
  errors.push(...validateReferences(json));
  errors.push(...validateContentActions(json));
  errors.push(...validateRubric(json));
  errors.push(...validateNotifications(json));
  errors.push(...validateTransitions(json));
  errors.push(...validateBotActors(json));
  errors.push(...validateBehaviour(json, opts));
  const blocking = errors.filter((e) => e.severity === "error");
  return { ok: blocking.length === 0, errors };
};

// src/validation/breadcrumb.ts
var parsePath = (path) => {
  if (!path) return [];
  return path.split(".").map((chunk) => {
    const m = /^(.+)\[(\d+)\]$/.exec(chunk);
    if (m) return { name: m[1], idx: parseInt(m[2], 10) };
    return { name: chunk };
  });
};
var isObj22 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);
var formatNodeSegment = (node2, idx) => {
  var _a2;
  const name = typeof node2.name === "string" ? node2.name : null;
  const type14 = node2.type;
  const runtime = typeof type14 === "string" ? runtimeTypeOf({ type: type14, variant: node2.variant }) : void 0;
  const typeLabel = runtime ? (_a2 = runtime.variant) != null ? _a2 : runtime.type : typeof node2.variant === "string" && node2.variant !== "" ? node2.variant : "?";
  return `${name != null ? name : `[${idx}]`}(${typeLabel})`;
};
var nodeBreadcrumb = (tree2, path) => {
  if (!isObj22(tree2)) return path;
  const segs = parsePath(path);
  if (segs.length === 0) return path;
  let cursor = tree2;
  const out = [];
  for (const seg of segs) {
    if (seg.name === "_children" && typeof seg.idx === "number") {
      const children2 = isObj22(cursor) ? cursor._children : void 0;
      const next = Array.isArray(children2) ? children2[seg.idx] : void 0;
      if (isObj22(next)) {
        out.push(formatNodeSegment(next, seg.idx));
        cursor = next;
      } else {
        out.push(`_children[${seg.idx}]`);
        cursor = null;
      }
      continue;
    }
    if (typeof seg.idx === "number") {
      out.push(`${seg.name}[${seg.idx}]`);
      if (isObj22(cursor)) {
        const child = cursor[seg.name];
        cursor = Array.isArray(child) ? child[seg.idx] : null;
      } else {
        cursor = null;
      }
      continue;
    }
    out.push(seg.name);
    if (isObj22(cursor)) {
      cursor = cursor[seg.name];
    } else {
      cursor = null;
    }
  }
  return out.join("/");
};
var lastBreadcrumbSegment = (tree2, path) => {
  var _a2;
  return (_a2 = nodeBreadcrumb(tree2, path).split("/").pop()) != null ? _a2 : path;
};

// src/validation/telemetry.ts
var MAX_ERRORS_PER_ATTEMPT = 15;
var MAX_FINAL_ERRORS = 15;
var MAX_SAMPLE_ERRORS = 5;
var RANDOM_SUFFIX_BYTES = 3;
var makeGenerationId = (now) => {
  const suffix = Array.from(
    { length: RANDOM_SUFFIX_BYTES },
    () => Math.floor(Math.random() * 256).toString(16).padStart(2, "0")
  ).join("");
  return `${now}-${suffix}`;
};
var capErrors = (errors, cap) => errors.length <= cap ? errors : errors.slice(0, cap);
var buildHistogram = (attempts) => {
  const seen = /* @__PURE__ */ new Set();
  for (const attempt of attempts) {
    for (const err of attempt.errors) {
      seen.add(err.kind);
    }
  }
  const histogram = {};
  for (const kind of seen) {
    histogram[kind] = 1;
  }
  return histogram;
};
var buildSamples = (attempts) => {
  const seen = /* @__PURE__ */ new Set();
  const samples2 = [];
  for (const attempt of attempts) {
    for (const err of attempt.errors) {
      if (seen.has(err.kind)) continue;
      seen.add(err.kind);
      samples2.push(err);
      if (samples2.length >= MAX_SAMPLE_ERRORS) return samples2;
    }
  }
  return samples2;
};
var finalErrorsOf = (attempts) => {
  if (attempts.length === 0) return [];
  const last2 = attempts[attempts.length - 1];
  return capErrors(last2.errors, MAX_FINAL_ERRORS);
};
var truncate = (attempts) => attempts.map((a) => ({
  turn: a.turn,
  errors: capErrors(a.errors, MAX_ERRORS_PER_ATTEMPT)
}));
var wasTruncated = (attempts) => attempts.some((a) => a.errors.length > MAX_ERRORS_PER_ATTEMPT);
var buildValidationTelemetry = (input) => {
  var _a2, _b;
  const now = Date.now();
  const generationId = (_a2 = input.generationId) != null ? _a2 : makeGenerationId(now);
  const timestamp = (_b = input.timestamp) != null ? _b : new Date(now).toISOString();
  const fixTurnsRun = Math.max(0, input.attempts.length - 1);
  const histogram = buildHistogram(input.attempts);
  const finalErrors = finalErrorsOf(input.attempts);
  const telemetry = {
    generationId,
    timestamp,
    modelId: input.modelId,
    promptName: input.promptName,
    initialOk: input.initialOk,
    finalOk: input.finalOk,
    fixTurnsRun,
    maxFixTurns: input.maxFixTurns,
    kindHistogram: histogram,
    finalErrors,
    sampleErrors: buildSamples(input.attempts),
    attempts: truncate(input.attempts),
    cost: {
      totalCostUsd: input.totalCost.costUsd,
      fixTurnCostUsd: input.fixTurnCost.costUsd,
      totalCredits: input.totalCost.creditsCharged,
      fixTurnCredits: input.fixTurnCost.creditsCharged,
      inputTokens: input.totalCost.inputTokens,
      outputTokens: input.totalCost.outputTokens,
      fixTurnInputTokens: input.fixTurnCost.inputTokens,
      fixTurnOutputTokens: input.fixTurnCost.outputTokens
    },
    truncated: wasTruncated(input.attempts)
  };
  const summary = {
    generationId,
    finalOk: input.finalOk,
    fixTurnsRun,
    totalErrors: finalErrors.length,
    kindHistogram: histogram
  };
  return { telemetry, summary };
};

// src/validation/categories.ts
var STAGE_BY_KIND = {
  // structure
  "missing-required-field": "structure",
  "unknown-field": "structure",
  "invalid-type": "structure",
  "duplicate-id": "structure",
  "duplicate-sibling-name": "structure",
  "ambiguous-name-ref": "structure",
  "section-root-untyped": "structure",
  "parent-on-placement-field": "references",
  "parent-with-tree-placement": "references",
  "enum-unknown-value": "structure",
  // formulas
  "formula-syntax": "formulas",
  "formula-unknown-var": "formulas",
  "formula-arrow-syntax": "formulas",
  "host-fn-unknown": "formulas",
  "host-fn-out-of-context": "formulas",
  "host-fn-nested": "formulas",
  "host-arg-missing": "formulas",
  "host-arg-type": "formulas",
  "span-incomplete": "structure",
  "span-not-repeating": "structure",
  "span-slots-invalid": "structure",
  "place-span-conflict": "structure",
  "spatial-missing-cols": "structure",
  "place-incomplete": "structure",
  "place-outside-spatial": "structure",
  "spatial-unsupported": "rubric",
  "data-scope-user-unsupported-source": "structure",
  "data-scope-group-unsupported-source": "structure",
  "data-scope-inbox-unsupported-source": "structure",
  "data-scope-group-key-missing": "structure",
  "data-scope-realtime-rail-conflict": "structure",
  "media-mode-room-conflict": "structure",
  "vision-model-unsupported": "structure",
  "vision-source-unresolved": "references",
  "vision-low-fidelity-source": "references",
  // aiChat step hygiene (#1670) — warnings on one node's own fields, so they
  // ride `structure` like the mediaRoom check above (no new Category value).
  "aiChat-missing-prompt": "structure",
  "aiChat-turns-range": "structure",
  "aiChat-speech-lang-unused": "structure",
  "repeat-outside-flow": "structure",
  "repeat-nested": "structure",
  "repeat-child-not-screen": "structure",
  "repeat-duplicate-input": "structure",
  "span-unknown-field": "references",
  "rows-unknown-subsection": "formulas",
  "rows-out-of-context": "formulas",
  "rows-dynamic-name": "formulas",
  "formula-arity": "formulas",
  "formula-unknown-method": "formulas",
  "function-undefined-concat": "formulas",
  "match-no-default": "formulas",
  "formula-let-rebind": "formulas",
  "formula-unknown-identifier": "formulas",
  "name-shadowed-by-builtin": "formulas",
  "function-param-unknown-field": "formulas",
  "function-arg-type": "formulas",
  "formula-duplicate-data-scan": "formulas",
  "formula-prefer-nullish": "formulas",
  "formula-prefer-find": "formulas",
  "formula-prefer-some": "formulas",
  "formula-prefer-variadic-concat": "formulas",
  "formula-length-member": "formulas",
  "formula-prefer-value-match": "formulas",
  "undeclared-data-read": "formulas",
  // references
  "missing-objectType": "references",
  "missing-screen": "references",
  "missing-plugin": "references",
  "invalid-field-type": "references",
  "parent-relation-invalid": "references",
  "parent-relation-cycle": "references",
  "unknown-field-ref": "references",
  "unknown-sort-field": "references",
  "arbitrary-sort-field": "references",
  // tree-of-records — reference checks on the `tree` grammar, so they ride the
  // existing `references` category (a new Category value would ripple into the
  // web PublishOrgDialog's Record<Category, …> maps).
  "tree-roottype-unknown": "references",
  "tree-allowedchild-unknown": "references",
  "tree-nodetype-unknown-objecttype": "references",
  "tree-mixed-parent-spellings": "references",
  "tree-multiple-parentref-fields": "references",
  "tree-detail-unknown-child": "references",
  "tree-unreachable-nodetype": "references",
  "tree-source-not-writable": "references",
  "subsection-reorderable-sort-mismatch": "references",
  "subsection-reorderable-realtime": "references",
  "subsection-reorderable-partial-view": "references",
  "plugin-param-undeclared": "references",
  "plugin-param-missing": "references",
  "plugin-param-untyped": "references",
  "plugin-param-unparsable": "references",
  "plugin-name-invalid": "references",
  "widget-owner-invalid": "references",
  "widget-owner-not-sandboxed": "references",
  "widget-owner-capability": "references",
  "widget-owner-needs-min-client": "references",
  // content actions (#1746) — grouped into existing UI categories (no new
  // Category value, which would ripple into the web PublishOrgDialog): the
  // undeclared-action reference is references-like, and the template↔actions
  // hygiene warnings are rubric-like.
  "action-unknown": "references",
  "action-unused": "rubric",
  "action-param-mismatch": "rubric",
  "action-replace-into-flow": "rubric",
  // A dropped `confirm` means the action runs unguarded, so the untitled case
  // is a structure defect, not hygiene; the other two are template↔actions
  // hygiene like the pair above.
  "action-confirm-untitled": "structure",
  "action-confirm-noop": "rubric",
  "action-confirm-mismatch": "rubric",
  "action-confirm-info-dead-option": "rubric",
  // legacy click-channel migration (#1751) — non-blocking hygiene, same
  // home as the other template↔actions warnings above.
  "legacy-click-channel": "rubric",
  // Bare `@name` left in template text (#1520) — non-blocking hygiene, same
  // home as the other template warnings.
  "template-at-ref": "rubric",
  "template-missing-formula-wrapper": "formulas",
  // behavioural (#2126) — runtime quality, so it rides `rubric`
  "run-render-error": "rubric",
  "run-dead-interpolation": "rubric",
  "run-empty-list": "rubric",
  "run-plugin-error": "rubric",
  // rubric
  "html-unbalanced-tag": "rubric",
  "homeScreen-missing": "rubric",
  "repeating-without-data": "rubric",
  "cross-org-link-missing-target-blank": "rubric",
  "empty-container": "rubric",
  "flow-script-only": "rubric",
  "flow-outside-screen": "rubric",
  "legacy-node": "rubric",
  "legacy-prop": "rubric",
  "orphan-screen": "rubric",
  "sparse-instances": "rubric",
  "rubric-missing-objectType": "rubric",
  // notifications / security (validated alongside structure)
  "unsafe-click-path": "structure",
  "notification-unknown-identifier": "formulas",
  "notification-unknown-call": "formulas",
  // transitions — grouped into existing UI categories (no new Category value,
  // which would ripple into the web PublishOrgDialog): the type-reference check
  // is references-like, the dead-rule warning is rubric-like, the rest are
  // structural correctness.
  "transition-unknown-trigger": "structure",
  "transition-due-at": "structure",
  "transition-timer-missing-due": "structure",
  "transition-timer-unexpected-field": "structure",
  "transition-timer-missing-eol": "structure",
  "transition-archive-not-eol": "structure",
  "transition-perpetual-not-timer": "structure",
  "transition-perpetual-target-id": "structure",
  "transition-perpetual-due-not-field": "structure",
  "transition-perpetual-mixed-type": "structure",
  "transition-archive-field-without-archive": "structure",
  "transition-archive-unprojected": "rubric",
  "transition-archive-impure-id": "formulas",
  "transition-timer-record-var": "formulas",
  "transition-timer-impure-due": "formulas",
  "transition-missing-field": "structure",
  "transition-unknown-type": "references",
  "transition-self-target": "structure",
  "transition-loop": "structure",
  "transition-dead-rule": "rubric",
  "transition-private-incomplete": "structure",
  "transition-private-read-incomplete": "structure",
  "transition-target-inbox": "structure",
  "transition-notify-incomplete": "structure",
  "transition-notify-conflict": "structure",
  "transition-notify-private": "formulas",
  "transition-sms-incomplete": "structure",
  "transition-sms-private": "formulas",
  "transition-private-read-timer": "structure",
  "transition-private-type-not-private": "references",
  "transition-unknown-call": "formulas",
  "transition-unknown-identifier": "formulas",
  "function-transition-scope-var": "formulas",
  // bot actors (#2135) — same grouping logic as transitions
  "botactor-missing-field": "structure",
  "botactor-unknown-type": "references",
  "botactor-self-target": "structure",
  "botactor-private-incomplete": "structure",
  "botactor-private-type-not-private": "structure",
  "botactor-unknown-call": "formulas",
  "botactor-unknown-identifier": "formulas",
  // const names (#2317) and vendored libraries (#298) — naming and
  // provenance of nodes, so `structure` like duplicate-sibling-name.
  "const-name-clash": "structure",
  "const-name-invalid": "structure",
  "library-folder-drift": "structure",
  "library-name-clash": "structure",
  "library-override": "structure",
  "feature-folder-drift": "structure"
};
var categoryOf = (kind) => STAGE_BY_KIND[kind];
var KIND_LABEL = {
  // structure
  "missing-required-field": "Missing required field",
  "unknown-field": "Unknown field",
  "invalid-type": "Invalid type",
  "duplicate-id": "Duplicate IDs",
  "duplicate-sibling-name": "Duplicate sibling names",
  "ambiguous-name-ref": "Ambiguous name reference",
  "section-root-untyped": "Section root has no type",
  "parent-on-placement-field": "Ownership declared on a placement field",
  "parent-with-tree-placement": "Owned type also placed in a tree",
  "enum-unknown-value": "Unknown enum value",
  // formulas
  "formula-syntax": "Formula syntax",
  "formula-unknown-var": "Unknown formula variable",
  "formula-arrow-syntax": "Arrow function syntax",
  "host-fn-unknown": "Unknown host function",
  "host-fn-out-of-context": "Host function out of context",
  "host-fn-nested": "Nested async host call",
  "host-arg-missing": "Missing host-function argument",
  "host-arg-type": "Wrong host-function argument",
  "span-incomplete": "Incomplete span",
  "span-not-repeating": "Span on a non-repeating node",
  "span-slots-invalid": "Grid span without a usable slot supplier",
  "place-span-conflict": "Place and grid span on one node",
  "spatial-missing-cols": "Spatial layout without cols",
  "place-incomplete": "Incomplete place",
  "place-outside-spatial": "Place outside a spatial parent",
  "spatial-unsupported": "Unsupported in spatial mode",
  "data-scope-user-unsupported-source": "User scope on a source that can't partition",
  "data-scope-group-unsupported-source": "Group scope on a source other than storage",
  "data-scope-inbox-unsupported-source": "Inbox scope on a source other than realtime",
  "data-scope-group-key-missing": "Group-scoped form with no group key to write into",
  "data-scope-realtime-rail-conflict": "Realtime type on both the private and public rails",
  "media-mode-room-conflict": "Views of one media room disagree on video vs audio",
  "vision-model-unsupported": "Vision step names a model we cannot send images to",
  "aiChat-missing-prompt": "AI chat step without a prompt",
  "aiChat-turns-range": "AI chat maxUserTurns out of range",
  "aiChat-speech-lang-unused": "speechLang set without speakReplies",
  "repeat-outside-flow": "Repeat outside a flow",
  "repeat-nested": "Repeat inside a repeat",
  "repeat-child-not-screen": "Repeat child that isn't a screen",
  "repeat-duplicate-input": "Same input name twice in one repeat",
  "span-unknown-field": "Unknown span field",
  "rows-unknown-subsection": "Unknown $rows subsection",
  "rows-out-of-context": "$rows out of context",
  "rows-dynamic-name": "$rows dynamic argument",
  "formula-arity": "Wrong argument count",
  "formula-unknown-method": "Unknown method",
  "function-undefined-concat": "Unguarded concat renders undefined",
  "match-no-default": "Match without default arm",
  "formula-let-rebind": "Let binding reassigned",
  "formula-unknown-identifier": "Unknown identifier",
  "name-shadowed-by-builtin": "Name shadowed by a builtin",
  "function-param-unknown-field": "Unknown field on typed parameter",
  "function-arg-type": "Argument type mismatch",
  "formula-duplicate-data-scan": "Duplicated $data scan",
  "formula-prefer-nullish": "Guard ternary that is `??`",
  "formula-prefer-find": "`filter(\u2026)[0]` that is `find`",
  "formula-prefer-some": "Filter count that is `some`",
  "formula-prefer-variadic-concat": "Nested `concat`",
  "formula-length-member": "`.length` instead of `length()`",
  "formula-prefer-value-match": "`==` chain that is a value `match`",
  // references
  "missing-objectType": "Missing object type",
  "missing-screen": "Missing screen",
  "missing-plugin": "Missing plugin",
  "invalid-field-type": "Invalid field type",
  "parent-relation-invalid": "Invalid parent relation",
  "parent-relation-cycle": "Parent relation cycle",
  "unknown-field-ref": "Unknown field reference",
  "unknown-sort-field": "Unknown sort field",
  "arbitrary-sort-field": "Arbitrary sort field",
  "undeclared-data-read": "Undeclared $data read",
  // tree-of-records
  "tree-roottype-unknown": "Unknown tree rootType",
  "tree-allowedchild-unknown": "Unknown allowed child type",
  "tree-nodetype-unknown-objecttype": "Tree node type is not an object type",
  "tree-mixed-parent-spellings": "Mixed parent-link spellings",
  "tree-multiple-parentref-fields": "Multiple parentRef fields",
  "tree-detail-unknown-child": "Unknown tree detail child",
  "tree-unreachable-nodetype": "Unreachable tree node type",
  "tree-source-not-writable": "Mutable tree on a read-only source",
  "subsection-reorderable-sort-mismatch": "Reorderable list not sorted by its order field",
  "subsection-reorderable-realtime": "Reorderable list on the owner-only realtime rail",
  "subsection-reorderable-partial-view": "Reorderable list showing only part of its collection",
  "plugin-param-undeclared": "Undeclared plugin parameter",
  "plugin-param-missing": "Missing plugin parameter",
  "plugin-param-untyped": "Untyped plugin parameter",
  "plugin-param-unparsable": "Plugin parameter is not a formula",
  "plugin-name-invalid": "Plugin name is not an identifier",
  "widget-owner-invalid": "widgetOwner is not a uid",
  "widget-owner-not-sandboxed": "widgetOwner on a non-sandboxed plugin",
  "widget-owner-capability": "First-party-only capability on an owned widget",
  "widget-owner-needs-min-client": "widgetOwner needs minClientVersion 5",
  "vision-source-unresolved": "Vision step's imageSource names no such node",
  "vision-low-fidelity-source": "Vision source image is too low-resolution",
  // content actions
  "action-unknown": "Undeclared action",
  "action-unused": "Unused action",
  "action-param-mismatch": "Action param mismatch",
  "action-replace-into-flow": "Replace-mode navigate into a flow",
  "action-confirm-untitled": "Confirm without a title",
  "action-confirm-noop": "Confirm with nothing to gate",
  "action-confirm-mismatch": "Confirm override without a confirm",
  "action-confirm-info-dead-option": "Info-only confirm with a dead option",
  "legacy-click-channel": "Legacy click channel",
  "template-at-ref": "Bare @name in template text",
  "template-missing-formula-wrapper": "Template without @ marker",
  "run-render-error": "Template fails when rendered",
  "run-dead-interpolation": "Rendered output shows a raw ${",
  "run-empty-list": "List shows none of its seed rows",
  "run-plugin-error": "Plugin call fails when rendered",
  // rubric
  "html-unbalanced-tag": "Unbalanced HTML tags",
  "homeScreen-missing": "Missing home screen",
  "repeating-without-data": "Repeating without data",
  "cross-org-link-missing-target-blank": "Cross-org link target",
  "empty-container": "Empty container",
  "flow-script-only": "Flow with only script steps",
  "flow-outside-screen": "Flow outside a screen",
  "legacy-node": "Legacy node type",
  "legacy-prop": "Legacy property",
  "orphan-screen": "Orphan screen",
  "sparse-instances": "Sparse instances",
  "rubric-missing-objectType": "Missing object type (rubric)",
  "unsafe-click-path": "Unsafe notification click path",
  "notification-unknown-identifier": "Name a push condition cannot read",
  "notification-unknown-call": "Unknown function call in a push condition",
  // transitions
  "transition-unknown-trigger": "Unknown transition trigger",
  "transition-due-at": "dueAt on a non-timer rule",
  "transition-timer-missing-due": "Timer rule missing dueAt",
  "transition-timer-unexpected-field": "Field not valid on a timer rule",
  "transition-timer-missing-eol": "Timer-bearing type missing endOfLife",
  "transition-archive-not-eol": "archive on a rule that is not endOfLife",
  "transition-perpetual-not-timer": "perpetual on a rule that is not onTimer",
  "transition-perpetual-target-id": "perpetual targetId is a formula",
  "transition-perpetual-due-not-field": "perpetual dueAt is not a plain field read",
  "transition-perpetual-mixed-type": "Schedule type shared with other timer rules",
  "transition-archive-field-without-archive": "Archive field without archive: true",
  "transition-archive-unprojected": "Archive copies the whole record",
  "transition-archive-impure-id": "Impure archiveId on a timer rule",
  "transition-timer-record-var": "$record in a timer rule",
  "transition-timer-impure-due": "Impure dueAt on a timer rule",
  "transition-missing-field": "Missing transition field",
  "transition-unknown-type": "Unknown realtime type",
  "transition-self-target": "Transition self-target",
  "transition-loop": "Transition rule chain",
  "transition-dead-rule": "Dead transition rule",
  "transition-private-incomplete": "Incomplete private-write rule",
  "transition-private-read-incomplete": "Incomplete private-read rule",
  "transition-target-inbox": "Rule targets an inbox type",
  "transition-notify-incomplete": "Incomplete list push",
  "transition-notify-conflict": "List push and turn push on one rule",
  "transition-notify-private": "$private in a list push",
  "transition-sms-incomplete": "Incomplete SMS side effect",
  "transition-sms-private": "$private in an SMS without a read",
  "transition-private-read-timer": "Private read on a timer rule",
  "transition-private-type-not-private": "Private write to a non-private type",
  "transition-unknown-call": "Unknown function call in a rule",
  "transition-unknown-identifier": "Name not in rule scope",
  "function-transition-scope-var": "Transition var in a function body",
  "botactor-missing-field": "Bot actor missing a required field",
  "botactor-unknown-type": "Bot actor names an unknown realtime type",
  "botactor-self-target": "Bot actor writes the type it watches",
  "botactor-private-incomplete": "Bot actor private read half-configured",
  "botactor-private-type-not-private": "Bot actor private type is public",
  "botactor-unknown-call": "Unknown function call in a bot actor",
  "botactor-unknown-identifier": "Name not in bot actor scope",
  "const-name-clash": "Const shares a name with a function or const",
  "const-name-invalid": "Const name is not a plain identifier",
  "library-folder-drift": "Library folder edited since its sync",
  "library-name-clash": "Library name defined twice",
  "library-override": "Library name omitted (override)",
  "feature-folder-drift": "Installed feature edited since install"
};
var kindLabel = (kind) => {
  var _a2;
  return (_a2 = KIND_LABEL[kind]) != null ? _a2 : kind;
};

// src/validation/repairs.ts
var isObj23 = (x) => x !== null && typeof x === "object" && !Array.isArray(x);
var defaultGenerateNewId = (typePrefix) => `${typePrefix}-${generateId({ length: 4 })}`;
var repairDuplicateIds = (orgData3, options = {}) => {
  var _a2;
  const nextId = (_a2 = options.generateNewId) != null ? _a2 : defaultGenerateNewId;
  const existing = /* @__PURE__ */ new Set();
  for (const slot2 of orgDataKeys) {
    const instance = orgData3[slot2];
    if (!(instance == null ? void 0 : instance.data)) continue;
    for (const entry of instance.data) {
      const value = entry.value;
      if (!isObj23(value)) continue;
      const id = value.id;
      if (typeof id === "string" && id.length > 0) existing.add(id);
    }
  }
  const assigned = /* @__PURE__ */ new Set();
  const changes = [];
  const repaired = { ...orgData3 };
  for (const slot2 of orgDataKeys) {
    const instance = orgData3[slot2];
    if (!instance || !instance.data || instance.data.length === 0) {
      continue;
    }
    let slotChanged = false;
    const newData = instance.data.map((entry) => {
      const value = entry.value;
      if (!isObj23(value)) return entry;
      const id = value.id;
      if (typeof id !== "string" || id.length === 0) return entry;
      if (!assigned.has(id)) {
        assigned.add(id);
        return entry;
      }
      const type14 = value.type;
      const typePrefix = typeof type14 === "string" && type14.length > 0 ? type14 : "node";
      let candidate = nextId(typePrefix);
      while (existing.has(candidate) || assigned.has(candidate)) {
        candidate = nextId(typePrefix);
      }
      assigned.add(candidate);
      changes.push({
        slot: slot2,
        pointer: entry.pointer,
        oldId: id,
        newId: candidate,
        valueType: typeof type14 === "string" ? type14 : void 0,
        valueName: typeof value.name === "string" ? value.name : void 0
      });
      slotChanged = true;
      return { ...entry, value: { ...value, id: candidate } };
    });
    if (slotChanged) {
      repaired[slot2] = { ...instance, data: newData };
    }
  }
  return { repaired, changedCount: changes.length, changes };
};

// src/validation/compileOutline.ts
var defaultGenerateId = (typePrefix) => `${typePrefix}-${generateId({ length: 4 })}`;
var isRecord7 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var isEmpty2 = (v) => v === void 0 || v === null || v === "";
var childrenOf4 = (node2) => Array.isArray(node2._children) ? node2._children.filter(isRecord7) : [];
var segment = (node2, index) => {
  const shown = isEmpty2(node2.name) ? node2.label : node2.name;
  return typeof shown === "string" && shown !== "" ? shown : `[${index}]`;
};
var TODO = "TODO";
var genericPlaceholder = (type14, field2) => {
  var _a2, _b;
  const enumName = (_a2 = ENUM_FIELDS[type14]) == null ? void 0 : _a2[field2];
  if (enumName && ((_b = ENUM_VALUES[enumName]) == null ? void 0 : _b.length)) return ENUM_VALUES[enumName][0];
  if (field2 === "nodeTypes") return {};
  return TODO;
};
var setByPath = (node2, field2, value) => {
  const parts = field2.split(".");
  let cursor = node2;
  for (const part of parts.slice(0, -1)) {
    const next = cursor[part];
    if (!isRecord7(next)) cursor[part] = {};
    cursor = cursor[part];
  }
  cursor[parts[parts.length - 1]] = value;
};
var getByPath2 = (node2, field2) => field2.split(".").reduce((acc, part) => isRecord7(acc) ? acc[part] : void 0, node2);
var record8 = (f, path, kind, detail) => {
  f.fills.push({ path, kind, detail });
};
var fillId = (f, node2, path) => {
  if (!isEmpty2(node2.id)) return;
  const type14 = typeof node2.type === "string" && node2.type !== "" ? node2.type : "node";
  node2.id = f.nextId(type14);
  record8(f, `${path}.id`, "id", `${node2.id}`);
};
var inferDataType = (f, node2, ancestorOf) => {
  var _a2;
  if (ancestorOf) return ancestorOf;
  const name = typeof node2.name === "string" ? node2.name.toLowerCase() : "";
  const byPrefix = f.objectTypes.find((t30) => name !== "" && name.startsWith(t30.toLowerCase()));
  return (_a2 = byPrefix != null ? byPrefix : f.objectTypes[0]) != null ? _a2 : TODO;
};
var fillRequiredFields = (f, node2, path, ancestorOf) => {
  if (typeof node2.type !== "string") return;
  for (const field2 of requiredFieldsFor(node2.type, node2.variant)) {
    if (!isEmpty2(getByPath2(node2, field2))) continue;
    let value;
    let why;
    if (node2.type === "form" && field2 === "formConfig") {
      const dataType = inferDataType(f, node2, ancestorOf);
      value = { dataType };
      why = `formConfig: { dataType: "${dataType}" } inferred${ancestorOf ? " from the enclosing of=" : ""}; check it`;
    } else if (node2.type === "mediaRoom" && field2 === "roomKey") {
      value = `${typeof node2.name === "string" && node2.name !== "" ? node2.name : "media"}-room`;
      why = `roomKey "${value}" is a placeholder`;
    } else {
      value = genericPlaceholder(node2.type, field2);
      why = `${field2} is required on a ${node2.type}; ${JSON.stringify(value)} is a placeholder`;
    }
    setByPath(node2, field2, value);
    record8(f, `${path}.${field2}`, "placeholder", why);
  }
};
var fillTreeNode = (f, node2, path, ancestorOf) => {
  fillId(f, node2, path);
  const data = isRecord7(node2.data) ? node2.data : void 0;
  const of = typeof (data == null ? void 0 : data.dataType) === "string" && data.dataType !== "" ? data.dataType : void 0;
  if (node2.isRepeating === true && of === void 0 && isEmpty2(node2.maxLength)) {
    node2.maxLength = 1;
    record8(f, `${path}.maxLength`, "placeholder", "repeating with no of= or maxLength; repeats once until bound");
  }
  fillRequiredFields(f, node2, path, ancestorOf);
  if (node2.type === "content" && isEmpty2(node2.htmlTemplate) && typeof node2.purpose === "string" && node2.purpose !== "") {
    node2.htmlTemplate = `<!-- TODO(purpose): ${node2.purpose} -->`;
    record8(f, `${path}.htmlTemplate`, "placeholder", `an HTML comment carrying the purpose: ${node2.purpose}`);
  }
  childrenOf4(node2).forEach(
    (child, i) => fillTreeNode(f, child, `${path}.${segment(child, i)}`, of != null ? of : ancestorOf)
  );
};
var fillTransitions = (f, app2) => {
  if (!Array.isArray(app2.transitions)) return;
  const kept = [];
  app2.transitions.forEach((rule, i) => {
    if (isRecord7(rule) && isEmpty2(rule.next)) {
      const label2 = typeof rule.label === "string" ? ` "${rule.label}"` : "";
      record8(f, `appConfig.transitions[${i}]`, "dropped", `transition${label2} has no next=; a rule cannot be defaulted \u2014 write it in full at L2+`);
      return;
    }
    kept.push(rule);
  });
  if (kept.length === 0) delete app2.transitions;
  else app2.transitions = kept;
};
var fillPlugin = (f, plugin2, path) => {
  fillId(f, plugin2, path);
  if (!Array.isArray(plugin2.parameters)) {
    plugin2.parameters = [];
    record8(f, `${path}.parameters`, "default", "no parameters declared");
  }
  if (typeof plugin2.htmlTemplate !== "string") {
    plugin2.htmlTemplate = "";
    record8(f, `${path}.htmlTemplate`, "default", "the codec requires a template string; empty until written");
  }
};
var fillSlotItems = (f, slot2, slotName, each) => {
  if (!slot2) return;
  childrenOf4(slot2).forEach((item, i) => each(item, `${slotName}.${segment(item, i)}`));
};
var OMITTABLE_SLOTS = ["users", "objectTypes", "objectInstances", "plugins"];
var fillTree = (tree2, nextId) => {
  const f = {
    fills: [],
    nextId,
    objectTypes: tree2.objectTypes ? sectionNodes(tree2.objectTypes).flatMap((t30) => typeof t30.name === "string" ? [t30.name] : []) : []
  };
  for (const key of OMITTABLE_SLOTS) {
    if (tree2[key] !== void 0) continue;
    tree2[key] = { id: key, type: key, _children: [] };
    record8(f, key, "default", `the outline omitted the slot; an empty \`${key}\` root`);
  }
  if (tree2.appConfig) {
    fillTransitions(f, tree2.appConfig);
    fillTreeNode(f, tree2.appConfig, "appConfig", void 0);
  }
  fillSlotItems(f, tree2.users, "users", (u, p) => fillTreeNode(f, u, p, void 0));
  fillSlotItems(f, tree2.objectTypes, "objectTypes", (t30, p) => fillId(f, t30, p));
  fillSlotItems(f, tree2.objectInstances, "objectInstances", (folder2, p) => {
    fillId(f, folder2, p);
    childrenOf4(folder2).forEach((row, i) => fillId(f, row, `${p}[${i}]`));
  });
  fillSlotItems(f, tree2.plugins, "plugins", (p, path) => fillPlugin(f, p, path));
  return f.fills;
};
var OWED_KINDS = /* @__PURE__ */ new Set(["plugin-param-missing"]);
var decodeError = (message) => ({
  path: "",
  kind: "invalid-type",
  message: `The org does not decode: ${message}`,
  fixHint: "The io-ts codec rejected a node; compare against docs/OUTLINE.md and the node reference.",
  severity: "error"
});
var compileOutline = (text, options = {}) => {
  var _a2;
  const parsed = parseOutline(text, { lenient: true, lenientBody: options.lenientBody });
  if (isLeft(parsed)) return left({ kind: "parse", error: parsed.left });
  const tree2 = parsed.right;
  const fills = fillTree(tree2, (_a2 = options.generateId) != null ? _a2 : defaultGenerateId);
  const result = validateOrg(tree2);
  const owed = result.errors.filter((e) => OWED_KINDS.has(e.kind) && e.severity === "error");
  owed.forEach((e) => fills.push({ path: e.path, kind: "owed", detail: e.message }));
  const findings = result.errors.filter((e) => !owed.includes(e));
  const blocking = findings.filter((e) => e.severity === "error");
  if (blocking.length > 0) return left({ kind: "validation", errors: blocking, fills });
  const decoded = selectOrgData(treeToRawOrgData(tree2));
  if (isLeft(decoded)) {
    return left({ kind: "validation", errors: [decodeError(String(decoded.left))], fills });
  }
  return right({ tree: tree2, fills, warnings: findings });
};
var formatFill = (fill) => `${fill.kind.padEnd(11)} ${fill.path}: ${fill.detail}`;

// src/prompts/tools.ts
var SEARCH_IMAGES_TOOL = {
  name: "search_images",
  description: "Search for images to use. Returns verified image URLs. Use this instead of guessing or fabricating image URLs. Unsplash is free (stock photos). Brave searches the web (1 credit/query).",
  input_schema: {
    type: "object",
    properties: {
      searches: {
        type: "array",
        items: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description: "Descriptive image search query"
            },
            source: {
              type: "string",
              enum: ["unsplash", "brave"],
              description: "unsplash (default, free, stock photos) or brave (web search, 1 credit/query)"
            }
          },
          required: ["query"]
        },
        description: "Image searches to perform (max 10)"
      }
    },
    required: ["searches"]
  }
};
var MAX_IMAGE_SEARCHES = 10;
var SEARCH_WEB_TOOL = {
  name: "search_web",
  description: "Search the web for factual information about a topic. Returns titles, URLs, and descriptions. Costs 1 credit per query. Use to find accurate real-world details for what you're generating.",
  input_schema: {
    type: "object",
    properties: {
      searches: {
        type: "array",
        items: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description: "Web search query"
            }
          },
          required: ["query"]
        },
        description: "Web search queries (max 5)"
      }
    },
    required: ["searches"]
  }
};
var MAX_WEB_SEARCHES = 5;
var SEARCH_VIDEOS_TOOL = {
  name: "search_videos",
  description: "Search for videos to embed. Returns embed-ready URLs. Costs 1 credit per query. Use to find real tutorial, demo, or instructional videos.",
  input_schema: {
    type: "object",
    properties: {
      searches: {
        type: "array",
        items: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description: "Video search query"
            }
          },
          required: ["query"]
        },
        description: "Video search queries (max 5)"
      }
    },
    required: ["searches"]
  }
};
var MAX_VIDEO_SEARCHES = 5;
var GET_NODE_SCHEMA_TOOL = {
  name: "get_node_schema",
  description: "Look up the schema for a specific node type. Returns the field shape (TS-style), up to 3 minimal usage examples extracted from showcase orgs, and a list of common pitfalls. Call this BEFORE writing complex node trees to confirm field names, optional vs required fields, and to see real examples. Also answers the top-level `objectType` shape (its fields and `parent` relation).",
  input_schema: {
    type: "object",
    properties: {
      type: {
        type: "string",
        description: "The node type name, e.g. `screen`, `content`, `subsection`, `select`, `flow` \u2014 or `objectType` for the objectTypes schema."
      }
    },
    required: ["type"]
  }
};
var NOTE_ASSUMPTION_TOOL = {
  name: "note_assumption",
  description: "Record an assumption you made instead of asking the user a clarifying question. Use whenever a request is ambiguous and you've decided to make a reasonable choice and ship rather than block on the user. Call this tool one or more times in addition to (not instead of) generating the JSON output.",
  input_schema: {
    type: "object",
    properties: {
      assumption: {
        type: "string",
        description: "The assumption you made (1 sentence)."
      },
      rationale: {
        type: "string",
        description: "Why this was a reasonable choice (1 sentence)."
      }
    },
    required: ["assumption", "rationale"]
  }
};
var makeAskUserTool = (description) => ({
  name: "ask_user",
  description,
  input_schema: {
    type: "object",
    properties: {
      question: {
        type: "string",
        description: "The clarifying question to ask the user"
      }
    },
    required: ["question"]
  }
});
var getToolUseBlocks = (content2) => content2.filter((b2) => b2.type === "tool_use");

// src/prompts/nodeDiff.ts
import { parse as parsePartialJson, Allow } from "partial-json";
var ALLOWED_TOP_LEVEL_SLOTS = [
  "appConfig",
  "users",
  "objectTypes",
  "objectInstances",
  "plugins"
];
var UNSAFE_KEYS = /* @__PURE__ */ new Set(["__proto__", "prototype", "constructor"]);
var SEGMENT_RE = /^([A-Za-z_][A-Za-z0-9_]*)((?:\[\d+\])*)$/;
var INDEX_RE = /\[(\d+)\]/g;
var parsePath2 = (path) => {
  if (typeof path !== "string" || path.length === 0) {
    return { ok: false, error: "empty path" };
  }
  const segments = path.split(".");
  const steps = [];
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const m = SEGMENT_RE.exec(seg);
    if (!m) return { ok: false, error: `bad path segment: ${seg}` };
    const name = m[1];
    if (UNSAFE_KEYS.has(name)) {
      return { ok: false, error: `unsafe path segment: ${name}` };
    }
    if (i === 0) {
      if (!ALLOWED_TOP_LEVEL_SLOTS.includes(name)) {
        return { ok: false, error: `unknown top-level slot: ${name}` };
      }
    }
    steps.push({ kind: "prop", name });
    const indexPart = m[2];
    if (indexPart) {
      INDEX_RE.lastIndex = 0;
      let im;
      while ((im = INDEX_RE.exec(indexPart)) !== null) {
        steps.push({ kind: "index", index: Number(im[1]) });
      }
    }
  }
  return { ok: true, steps };
};
var walkTo = (root, steps) => {
  let cur = root;
  for (const step of steps) {
    if (cur === null || typeof cur !== "object") {
      return {
        ok: false,
        error: "path does not resolve (encountered non-object)"
      };
    }
    if (step.kind === "prop") {
      const obj = cur;
      if (!Object.prototype.hasOwnProperty.call(obj, step.name)) {
        return {
          ok: false,
          error: `path does not resolve (missing prop: ${step.name})`
        };
      }
      cur = obj[step.name];
    } else {
      if (!Array.isArray(cur)) {
        return {
          ok: false,
          error: "path does not resolve (index on non-array)"
        };
      }
      if (step.index < 0 || step.index >= cur.length) {
        return {
          ok: false,
          error: `path does not resolve (index out of range: ${step.index})`
        };
      }
      cur = cur[step.index];
    }
  }
  return { ok: true, value: cur };
};
var applyEdit = (root, op) => {
  const parsed = parsePath2(op.path);
  if (!parsed.ok) return parsed;
  const target = walkTo(root, parsed.steps);
  if (!target.ok) return target;
  if (target.value === null || typeof target.value !== "object") {
    return { ok: false, error: "edit target is not an object" };
  }
  const obj = target.value;
  if (op.props) {
    for (const k of Object.keys(op.props)) {
      if (UNSAFE_KEYS.has(k)) {
        return { ok: false, error: `unsafe prop key: ${k}` };
      }
      obj[k] = op.props[k];
    }
  }
  if (op.unset) {
    for (const k of op.unset) {
      if (UNSAFE_KEYS.has(k)) {
        return { ok: false, error: `unsafe prop key: ${k}` };
      }
      delete obj[k];
    }
  }
  return { ok: true };
};
var applyAdd = (root, op) => {
  const parsed = parsePath2(op.path);
  if (!parsed.ok) return parsed;
  const last2 = parsed.steps[parsed.steps.length - 1];
  if (!last2 || last2.kind !== "prop" || last2.name !== "_children") {
    return {
      ok: false,
      error: "add path must end at a _children array"
    };
  }
  const target = walkTo(root, parsed.steps);
  if (!target.ok) return target;
  if (!Array.isArray(target.value)) {
    return { ok: false, error: "add target is not an array" };
  }
  const payload = op.payload;
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
    return { ok: false, error: "add payload must be a node object" };
  }
  if (typeof payload.type !== "string") {
    return { ok: false, error: "add payload must have a type field" };
  }
  const arr = target.value;
  let at = op.at;
  if (at === void 0 || at === -1) at = arr.length;
  if (at < 0 || at > arr.length) {
    return { ok: false, error: `add at index out of range: ${op.at}` };
  }
  arr.splice(at, 0, payload);
  return { ok: true };
};
var applyRemove = (root, op) => {
  const parsed = parsePath2(op.path);
  if (!parsed.ok) return parsed;
  const last2 = parsed.steps[parsed.steps.length - 1];
  if (!last2 || last2.kind !== "index") {
    return {
      ok: false,
      error: "remove path must end at an indexed segment like _children[n]"
    };
  }
  const parentSteps = parsed.steps.slice(0, -1);
  const parent = walkTo(root, parentSteps);
  if (!parent.ok) return parent;
  if (!Array.isArray(parent.value)) {
    return { ok: false, error: "remove parent is not an array" };
  }
  const arr = parent.value;
  if (last2.index < 0 || last2.index >= arr.length) {
    return {
      ok: false,
      error: `remove index out of range: ${last2.index}`
    };
  }
  arr.splice(last2.index, 1);
  return { ok: true };
};
var applyNodeDiff = (prevJson, ops) => {
  const next = typeof structuredClone === "function" ? structuredClone(prevJson) : JSON.parse(JSON.stringify(prevJson));
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    let r;
    if (op.op === "edit") r = applyEdit(next, op);
    else if (op.op === "add") r = applyAdd(next, op);
    else if (op.op === "remove") r = applyRemove(next, op);
    else r = { ok: false, error: "unknown op kind" };
    if (!r.ok) return { ok: false, error: r.error, opIndex: i };
  }
  return { ok: true, result: next };
};
var PARTIAL_JSON_ALLOW = Allow.OBJ | Allow.ARR;
var validateOpShape = (raw) => {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: "op must be an object" };
  }
  const r = raw;
  const kind = r.op;
  if (typeof r.path !== "string") {
    return { ok: false, error: "op.path must be a string" };
  }
  if (kind === "edit") {
    const props = r.props;
    if (props !== void 0 && (props === null || typeof props !== "object" || Array.isArray(props))) {
      return { ok: false, error: "edit.props must be an object" };
    }
    const unset = r.unset;
    if (unset !== void 0 && !Array.isArray(unset)) {
      return { ok: false, error: "edit.unset must be an array of strings" };
    }
    if (Array.isArray(unset)) {
      for (const u of unset) {
        if (typeof u !== "string") {
          return { ok: false, error: "edit.unset must contain strings" };
        }
      }
    }
    return {
      ok: true,
      op: {
        op: "edit",
        path: r.path,
        props,
        unset
      }
    };
  }
  if (kind === "add") {
    const payload = r.payload;
    if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
      return { ok: false, error: "add.payload must be an object" };
    }
    const at = r.at;
    if (at !== void 0 && typeof at !== "number") {
      return { ok: false, error: "add.at must be a number" };
    }
    return {
      ok: true,
      op: {
        op: "add",
        path: r.path,
        at,
        payload
      }
    };
  }
  if (kind === "remove") {
    return { ok: true, op: { op: "remove", path: r.path } };
  }
  return { ok: false, error: `unknown op: ${String(kind)}` };
};
var parseNodeDiffArray = (text) => {
  const start = text.indexOf("[");
  if (start === -1) return { ok: false, error: "no JSON array found" };
  const slice = text.slice(start);
  let parsed;
  try {
    parsed = parsePartialJson(slice, PARTIAL_JSON_ALLOW);
  } catch {
    return { ok: false, error: "failed to parse JSON array" };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: "parsed value is not an array" };
  }
  const ops = [];
  for (let i = 0; i < parsed.length; i++) {
    const r = validateOpShape(parsed[i]);
    if (!r.ok) return { ok: false, error: `op[${i}]: ${r.error}` };
    ops.push(r.op);
  }
  return { ok: true, ops };
};

// src/prompts/generate.ts
var handleValidatePartial = (block) => {
  var _a2;
  const json = (_a2 = block.input.json) != null ? _a2 : null;
  const result = validateOrg(json);
  return {
    type: "tool_result",
    tool_use_id: block.id,
    content: JSON.stringify(result)
  };
};
var handleGetNodeSchema = (block) => {
  const requestedType = block.input.type;
  const entry = requestedType ? NODE_SCHEMA_INDEX[requestedType] : void 0;
  if (!entry) {
    const available = Object.keys(NODE_SCHEMA_INDEX).sort().join(", ");
    return {
      type: "tool_result",
      tool_use_id: block.id,
      content: JSON.stringify({
        error: `Unknown type: ${requestedType != null ? requestedType : "(missing)"}.`,
        availableTypes: available
      })
    };
  }
  const { legacy, ...rest } = entry;
  return {
    type: "tool_result",
    tool_use_id: block.id,
    content: JSON.stringify(legacy ? { legacy, ...rest } : entry)
  };
};
var isProseQuestion = (text) => !text.includes("{") && text.includes("?");
var proseAnswerText = (answer) => answer.trim() === "" ? "(No answer \u2014 proceed with sensible assumptions and produce the output now.)" : answer;
var isGenerateError = (result) => typeof result === "object" && result !== null && "error" in result && result.error === true;
var spentToolMessage = (name) => `${name} runs once per generation and has already run \u2014 its results are in an earlier tool_result. Do not call it again; continue with what you have.`;
var finalTurnToolMessage = (name) => `${name} was not run: the tool budget for this generation is spent and your next response is the last one. Do not call any more tools \u2014 produce the complete output now with what you have.`;
var handleSharedTools = async (allBlocks, turnAssistantContent, state, deps, turn, maxUserQuestions) => {
  var _a2, _b, _c, _d;
  const toolResults = [];
  const handledIds = /* @__PURE__ */ new Set();
  const refuse2 = (blocks, content2) => {
    for (const block of blocks) {
      toolResults.push({ type: "tool_result", tool_use_id: block.id, content: content2 });
      handledIds.add(block.id);
    }
  };
  const byName = (name) => allBlocks.filter((b2) => b2.name === name);
  const imageBlocks = byName("search_images");
  if (imageBlocks.length > 0 && state.searchImagesRequested) {
    refuse2(imageBlocks, spentToolMessage("search_images"));
  } else if (imageBlocks.length > 0) {
    if (deps.searchImages) {
      state.searchImagesRequested = true;
      const searches = imageBlocks.flatMap(
        (b2) => {
          var _a3;
          return (_a3 = b2.input.searches) != null ? _a3 : [];
        }
      ).slice(0, MAX_IMAGE_SEARCHES).map((s) => ({
        query: s.query,
        source: s.source
      }));
      (_a2 = deps.onStatus) == null ? void 0 : _a2.call(deps, `searching for ${searches.length} image(s)...`);
      const searchResult = await deps.searchImages(searches);
      state.totalUsage.creditsCharged += searchResult.creditsCharged;
      state.imageSearchResults.push(
        ...searchResult.results.map((r) => {
          var _a3;
          const match2 = searches.find((s) => s.query === r.query);
          return {
            query: r.query,
            source: (_a3 = match2 == null ? void 0 : match2.source) != null ? _a3 : "unsplash",
            url: r.url,
            alt: r.alt
          };
        })
      );
      for (const block of imageBlocks) {
        toolResults.push({
          type: "tool_result",
          tool_use_id: block.id,
          content: JSON.stringify(searchResult.results)
        });
        handledIds.add(block.id);
      }
    } else {
      for (const block of imageBlocks) {
        toolResults.push({
          type: "tool_result",
          tool_use_id: block.id,
          content: "Image search is not available. Use placeholder URLs like https://placehold.co/400x300 instead."
        });
        handledIds.add(block.id);
      }
    }
  }
  const webBlocks = byName("search_web");
  if (webBlocks.length > 0 && state.searchWebRequested) {
    refuse2(webBlocks, spentToolMessage("search_web"));
  } else if (webBlocks.length > 0) {
    if (deps.searchWeb) {
      state.searchWebRequested = true;
      const searches = webBlocks.flatMap(
        (b2) => {
          var _a3;
          return (_a3 = b2.input.searches) != null ? _a3 : [];
        }
      ).slice(0, MAX_WEB_SEARCHES).map((s) => ({ query: s.query }));
      (_b = deps.onStatus) == null ? void 0 : _b.call(
        deps,
        `searching the web (${searches.length} query/queries)...`
      );
      const searchResult = await deps.searchWeb(searches);
      state.totalUsage.creditsCharged += searchResult.creditsCharged;
      state.webSearchResults.push(...searchResult.results);
      for (const block of webBlocks) {
        toolResults.push({
          type: "tool_result",
          tool_use_id: block.id,
          content: JSON.stringify(searchResult.results)
        });
        handledIds.add(block.id);
      }
    } else {
      for (const block of webBlocks) {
        toolResults.push({
          type: "tool_result",
          tool_use_id: block.id,
          content: "Web search is not available. Use your training data instead."
        });
        handledIds.add(block.id);
      }
    }
  }
  const videoBlocks = byName("search_videos");
  if (videoBlocks.length > 0 && state.searchVideosRequested) {
    refuse2(videoBlocks, spentToolMessage("search_videos"));
  } else if (videoBlocks.length > 0) {
    if (deps.searchVideos) {
      state.searchVideosRequested = true;
      const searches = videoBlocks.flatMap(
        (b2) => {
          var _a3;
          return (_a3 = b2.input.searches) != null ? _a3 : [];
        }
      ).slice(0, MAX_VIDEO_SEARCHES).map((s) => ({ query: s.query }));
      (_c = deps.onStatus) == null ? void 0 : _c.call(
        deps,
        `searching for ${searches.length} video(s)...`
      );
      const searchResult = await deps.searchVideos(searches);
      state.totalUsage.creditsCharged += searchResult.creditsCharged;
      state.videoSearchResults.push(...searchResult.results);
      for (const block of videoBlocks) {
        toolResults.push({
          type: "tool_result",
          tool_use_id: block.id,
          content: JSON.stringify(searchResult.results)
        });
        handledIds.add(block.id);
      }
    } else {
      for (const block of videoBlocks) {
        toolResults.push({
          type: "tool_result",
          tool_use_id: block.id,
          content: "Video search is not available. Omit video embeds or use placeholder content."
        });
        handledIds.add(block.id);
      }
    }
  }
  const askBlocks = byName("ask_user");
  if (askBlocks.length > 0 && state.questionsAsked >= maxUserQuestions) {
    refuse2(
      askBlocks,
      "You have used all your questions. Do not call ask_user again \u2014 proceed with sensible assumptions and produce the final output now."
    );
  } else if (askBlocks.length > 0) {
    const questions = askBlocks.map((b2) => b2.input.question).filter(Boolean);
    const combinedQuestion = questions.length > 1 ? questions.map((q, i) => `${i + 1}. ${q}`).join("\n") : (_d = questions[0]) != null ? _d : "";
    const answer = await deps.onQuestion(combinedQuestion, turn, {
      messages: [...state.messages],
      assistantContent: turnAssistantContent
    });
    state.questionsAsked++;
    state.qaPairs.push({ question: combinedQuestion, answer });
    for (const block of askBlocks) {
      toolResults.push({
        type: "tool_result",
        tool_use_id: block.id,
        content: answer
      });
      handledIds.add(block.id);
    }
  }
  const nodeSchemaBlocks = byName("get_node_schema");
  for (const block of nodeSchemaBlocks) {
    toolResults.push(handleGetNodeSchema(block));
    handledIds.add(block.id);
  }
  const validateBlocks = byName("validate_partial");
  for (const block of validateBlocks) {
    toolResults.push(handleValidatePartial(block));
    handledIds.add(block.id);
  }
  return { toolResults, handledIds };
};
var formatGateErrorMessage = (errors) => {
  const top = errors.slice(0, 15);
  const lines = [
    "You proposed an org config but it failed validation. The errors are listed below.",
    "Reply with a corrected full JSON. The system will re-validate automatically.",
    "",
    `Errors (${errors.length}${errors.length > 15 ? ", showing 15" : ""}):`
  ];
  top.forEach((e, i) => {
    lines.push(`  ${i + 1}. [${e.kind}] ${e.path}`);
    lines.push(`     ${e.message}`);
    if (e.relatedPaths && e.relatedPaths.length > 0) {
      lines.push(`     Also at: ${e.relatedPaths.join(", ")}`);
    }
    if (e.fixHint) lines.push(`     fixHint: ${e.fixHint}`);
  });
  return lines.join("\n");
};
var formatGateDiffMessage = (errors) => {
  const top = errors.slice(0, 15);
  const lines = [
    "You proposed an org config but it failed validation. Errors are listed below.",
    "",
    "Reply with ONLY a JSON array of edit operations against the previous JSON. Do NOT rewrite the full config. No prose, no markdown fences.",
    "",
    "Operation shapes:",
    '  { "op": "edit",   "path": "<slot>._children[i]...", "props": { "<key>": <value>, ... }, "unset": ["<key>", ...] }',
    '  { "op": "add",    "path": "<slot>._children[i]..._children", "at": <index?>, "payload": { "type": "<nodeType>", ... } }',
    '  { "op": "remove", "path": "<slot>._children[i]..." }',
    "",
    "Rules:",
    "- Paths begin with a top-level slot: appConfig, users, objectTypes, objectInstances, plugins.",
    "- Walk into children via ._children[i] (structural index \u2014 count from the previous JSON).",
    "- `edit.props` shallow-merges; use `unset` to remove a prop. `null` is treated as a real value.",
    "- `add.path` must end at a _children array. Omit `at` to append. Payload requires a `type` field.",
    "- `remove.path` must end at an indexed _children[i] segment.",
    "- Emit only the ops needed to address the errors. Avoid touching unrelated nodes.",
    "",
    `Errors (${errors.length}${errors.length > 15 ? ", showing 15" : ""}):`
  ];
  top.forEach((e, i) => {
    lines.push(`  ${i + 1}. [${e.kind}] ${e.path}`);
    lines.push(`     ${e.message}`);
    if (e.relatedPaths && e.relatedPaths.length > 0) {
      lines.push(`     Also at: ${e.relatedPaths.join(", ")}`);
    }
    if (e.fixHint) lines.push(`     fixHint: ${e.fixHint}`);
  });
  return lines.join("\n");
};
var noop = () => {
};
var generate = async (prompt, modelId, spec, deps) => {
  var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u;
  const state = {
    prompt,
    messages: (_a2 = deps.initialMessages) != null ? _a2 : [{ role: "user", content: prompt }],
    totalUsage: {
      model: modelId,
      inputTokens: 0,
      outputTokens: 0,
      creditsCharged: 0
    },
    qaPairs: [...(_b = deps.initialQaPairs) != null ? _b : []],
    questionsAsked: (_d = (_c = deps.initialQaPairs) == null ? void 0 : _c.length) != null ? _d : 0,
    searchImagesRequested: false,
    searchWebRequested: false,
    searchVideosRequested: false,
    examplesRequested: false,
    imageSearchResults: [],
    webSearchResults: [],
    videoSearchResults: [],
    turnLogs: []
  };
  const systemPrompt = await deps.buildSystemPrompt();
  const sharedTools = [];
  if (!spec.disableAskUser && spec.maxUserQuestions > 0) {
    sharedTools.push(makeAskUserTool(spec.askUserDescription));
  }
  if (deps.searchImages) sharedTools.push(SEARCH_IMAGES_TOOL);
  if (deps.searchWeb) sharedTools.push(SEARCH_WEB_TOOL);
  if (deps.searchVideos) sharedTools.push(SEARCH_VIDEOS_TOOL);
  for (let turn = 0; turn < spec.maxTotalTurns; turn++) {
    (_e = deps.onStatus) == null ? void 0 : _e.call(deps, `generating (turn ${turn + 1})...`);
    (_f = deps.onEvent) == null ? void 0 : _f.call(deps, { kind: "turn-start", turnIndex: turn });
    const tools = [...sharedTools, ...spec.getExtraTools(state)];
    const toolNames = tools.map(
      (t30) => {
        var _a3;
        return (_a3 = t30.name) != null ? _a3 : "unknown";
      }
    );
    console.log(
      `[generate] turn ${turn + 1}: offering [${toolNames.join(", ")}]`
    );
    let turnData;
    try {
      (_g = deps.onEvent) == null ? void 0 : _g.call(deps, { kind: "stream-start", turnIndex: turn });
      turnData = await deps.callTurn({
        systemPrompt,
        messages: state.messages,
        tools: tools.length > 0 ? tools : void 0,
        model: modelId,
        maxTokens: spec.maxTokens,
        mock: !!deps.mockScenario,
        mockScenario: deps.mockScenario,
        demoSource: deps.demoSource,
        onProgress: (info) => {
          var _a3, _b2;
          (_a3 = deps.onEvent) == null ? void 0 : _a3.call(deps, {
            kind: "stream-progress",
            turnIndex: turn,
            outputTokens: info.outputTokens
          });
          if (info.text !== void 0 && spec.processStreamingText) {
            spec.processStreamingText(
              info.text,
              state,
              (_b2 = deps.onEvent) != null ? _b2 : noop
            );
          }
        }
      });
      (_h = deps.onEvent) == null ? void 0 : _h.call(deps, {
        kind: "stream-end",
        turnIndex: turn,
        outputTokens: turnData.usage.outputTokens
      });
    } catch (err) {
      return {
        error: true,
        message: err instanceof Error ? err.message : String(err),
        usage: state.totalUsage,
        conversationState: state.messages.length > 1 ? {
          messages: [...state.messages],
          qaPairs: [...state.qaPairs]
        } : void 0
      };
    }
    state.totalUsage.inputTokens += turnData.usage.inputTokens;
    state.totalUsage.outputTokens += turnData.usage.outputTokens;
    state.totalUsage.creditsCharged += turnData.usage.creditsCharged;
    state.totalUsage.model = turnData.usage.model;
    console.log(
      `[generate] turn ${turn + 1}: stopReason=${turnData.stopReason} type=${turnData.type} tokens=${turnData.usage.inputTokens}in/${turnData.usage.outputTokens}out`
    );
    if (turnData.type === "result" && !spec.disableAskUser && state.questionsAsked < spec.maxUserQuestions && isProseQuestion(turnData.text)) {
      const question = turnData.text.trim().replace(/\*\*(.+?)\*\*/g, "$1");
      state.messages.push({
        role: "assistant",
        content: turnData.assistantContent
      });
      state.turnLogs.push({
        turn,
        toolsOffered: toolNames,
        toolsUsed: ["ask_user (prose)"],
        stopReason: turnData.stopReason,
        inputTokens: turnData.usage.inputTokens,
        outputTokens: turnData.usage.outputTokens
      });
      (_i = deps.onEvent) == null ? void 0 : _i.call(deps, { kind: "tool-call", name: "ask_user", input: { question } });
      const answer = await deps.onQuestion(question, turn, {
        messages: [...state.messages],
        assistantContent: turnData.assistantContent
      });
      (_j = deps.onEvent) == null ? void 0 : _j.call(deps, { kind: "tool-result", name: "ask_user", ok: true });
      state.questionsAsked++;
      state.qaPairs.push({ question, answer });
      state.messages.push({ role: "user", content: proseAnswerText(answer) });
      continue;
    }
    if (turnData.type === "result") {
      state.turnLogs.push({
        turn,
        toolsOffered: toolNames,
        toolsUsed: [],
        stopReason: turnData.stopReason,
        inputTokens: turnData.usage.inputTokens,
        outputTokens: turnData.usage.outputTokens
      });
      let result = spec.processResult(
        turnData.text,
        turnData.stopReason,
        state
      );
      if (spec.validateFinal && !isGenerateError(result)) {
        const maxFixTurns = (_k = spec.maxFixTurns) != null ? _k : 0;
        const attempts = [];
        let lastAssistantContent = turnData.assistantContent;
        let lastValidation = spec.validateFinal(turnData.text);
        attempts.push({
          turn: 0,
          errors: lastValidation.ok ? [] : lastValidation.errors
        });
        const initialOk = lastValidation.ok;
        const usageBeforeFix = {
          inputTokens: state.totalUsage.inputTokens,
          outputTokens: state.totalUsage.outputTokens,
          creditsCharged: state.totalUsage.creditsCharged
        };
        let fixTurn = 0;
        while (!lastValidation.ok && fixTurn < maxFixTurns) {
          fixTurn += 1;
          const useDiffMode = fixTurn === maxFixTurns && !!spec.useDiffOnSecondFixTurn;
          const priorJsonForDiff = lastValidation.parsedJson;
          const syntheticToolUseId = `gate-${Date.now()}-${fixTurn}`;
          state.messages.push({
            role: "assistant",
            content: [
              ...lastAssistantContent,
              {
                type: "tool_use",
                id: syntheticToolUseId,
                name: "validate_partial",
                input: { json: lastValidation.parsedJson }
              }
            ]
          });
          state.messages.push({
            role: "user",
            content: [
              {
                type: "tool_result",
                tool_use_id: syntheticToolUseId,
                content: useDiffMode ? formatGateDiffMessage(lastValidation.errors) : formatGateErrorMessage(lastValidation.errors)
              }
            ]
          });
          const fixTurnIndex = turn + fixTurn;
          (_l = deps.onEvent) == null ? void 0 : _l.call(deps, { kind: "turn-start", turnIndex: fixTurnIndex });
          let fixTurnData;
          try {
            (_m = deps.onEvent) == null ? void 0 : _m.call(deps, {
              kind: "stream-start",
              turnIndex: fixTurnIndex
            });
            fixTurnData = await deps.callTurn({
              systemPrompt,
              messages: state.messages,
              tools: tools.length > 0 ? tools : void 0,
              model: modelId,
              maxTokens: spec.maxTokens,
              mock: !!deps.mockScenario,
              mockScenario: deps.mockScenario,
              demoSource: deps.demoSource
            });
            (_n = deps.onEvent) == null ? void 0 : _n.call(deps, {
              kind: "stream-end",
              turnIndex: fixTurnIndex,
              outputTokens: fixTurnData.usage.outputTokens
            });
          } catch (err) {
            return {
              error: true,
              message: err instanceof Error ? err.message : String(err),
              usage: state.totalUsage,
              conversationState: {
                messages: [...state.messages],
                qaPairs: [...state.qaPairs]
              }
            };
          }
          state.totalUsage.inputTokens += fixTurnData.usage.inputTokens;
          state.totalUsage.outputTokens += fixTurnData.usage.outputTokens;
          state.totalUsage.creditsCharged += fixTurnData.usage.creditsCharged;
          state.totalUsage.model = fixTurnData.usage.model;
          state.turnLogs.push({
            turn: fixTurnIndex,
            toolsOffered: toolNames,
            toolsUsed: fixTurnData.type === "tool_use" ? getToolUseBlocks(fixTurnData.assistantContent).map(
              (b2) => b2.name
            ) : [],
            stopReason: fixTurnData.stopReason,
            inputTokens: fixTurnData.usage.inputTokens,
            outputTokens: fixTurnData.usage.outputTokens
          });
          if (fixTurnData.type !== "result") {
            break;
          }
          lastAssistantContent = fixTurnData.assistantContent;
          if (useDiffMode) {
            const parsedDiff = parseNodeDiffArray(fixTurnData.text);
            if (!parsedDiff.ok) {
              console.log(
                `[generate] diff-mode parse failure: ${parsedDiff.error}`
              );
              break;
            }
            const applied = applyNodeDiff(priorJsonForDiff, parsedDiff.ops);
            if (!applied.ok) {
              console.log(
                `[generate] diff-mode apply failure (op ${applied.opIndex}): ${applied.error}`
              );
              break;
            }
            const patchedText = JSON.stringify(applied.result);
            const newResult = spec.processResult(
              patchedText,
              fixTurnData.stopReason,
              state
            );
            if (isGenerateError(newResult)) return newResult;
            result = newResult;
            lastValidation = spec.validateFinal(patchedText);
          } else {
            result = spec.processResult(
              fixTurnData.text,
              fixTurnData.stopReason,
              state
            );
            if (isGenerateError(result)) return result;
            lastValidation = spec.validateFinal(fixTurnData.text);
          }
          attempts.push({
            turn: fixTurn,
            errors: lastValidation.ok ? [] : lastValidation.errors
          });
        }
        if (!lastValidation.ok && !isGenerateError(result) && typeof result === "object" && result !== null) {
          result._validationErrors = lastValidation.errors;
        }
        if (!isGenerateError(result) && typeof result === "object" && result !== null) {
          const fixTurnInputTokens = state.totalUsage.inputTokens - usageBeforeFix.inputTokens;
          const fixTurnOutputTokens = state.totalUsage.outputTokens - usageBeforeFix.outputTokens;
          const totalCostUsd = calculateCost({
            model: state.totalUsage.model,
            inputTokens: state.totalUsage.inputTokens,
            outputTokens: state.totalUsage.outputTokens
          });
          const fixTurnCostUsd = calculateCost({
            model: state.totalUsage.model,
            inputTokens: fixTurnInputTokens,
            outputTokens: fixTurnOutputTokens
          });
          const fixTurnCost = {
            inputTokens: fixTurnInputTokens,
            outputTokens: fixTurnOutputTokens,
            creditsCharged: state.totalUsage.creditsCharged - usageBeforeFix.creditsCharged,
            costUsd: fixTurnCostUsd
          };
          const { telemetry } = buildValidationTelemetry({
            attempts,
            initialOk,
            finalOk: lastValidation.ok,
            modelId: state.totalUsage.model,
            promptName: (_o = spec.promptName) != null ? _o : "custom",
            maxFixTurns,
            totalCost: {
              inputTokens: state.totalUsage.inputTokens,
              outputTokens: state.totalUsage.outputTokens,
              creditsCharged: state.totalUsage.creditsCharged,
              costUsd: totalCostUsd
            },
            fixTurnCost
          });
          result._validationTelemetry = telemetry;
        }
      }
      if (!isGenerateError(result)) {
        (_p = deps.onEvent) == null ? void 0 : _p.call(deps, { kind: "done" });
      }
      return result;
    }
    const allBlocks = getToolUseBlocks(turnData.assistantContent);
    state.messages.push({
      role: "assistant",
      content: turnData.assistantContent
    });
    for (const block of allBlocks) {
      (_q = deps.onEvent) == null ? void 0 : _q.call(deps, {
        kind: "tool-call",
        name: block.name,
        input: block.input
      });
    }
    if (spec.reserveFinalTurn && turn === spec.maxTotalTurns - 2) {
      const refused = allBlocks.map((block) => ({
        type: "tool_result",
        tool_use_id: block.id,
        content: finalTurnToolMessage(block.name)
      }));
      for (const block of allBlocks) {
        (_r = deps.onEvent) == null ? void 0 : _r.call(deps, { kind: "tool-result", name: block.name, ok: false });
      }
      console.log(
        `[generate] turn ${turn + 1}: refused [${allBlocks.map((b2) => b2.name).join(", ")}] \u2014 final turn reserved`
      );
      state.turnLogs.push({
        turn,
        toolsOffered: toolNames,
        toolsUsed: allBlocks.map((b2) => b2.name),
        stopReason: turnData.stopReason,
        inputTokens: turnData.usage.inputTokens,
        outputTokens: turnData.usage.outputTokens
      });
      state.messages.push({ role: "user", content: refused });
      continue;
    }
    const { toolResults, handledIds } = await handleSharedTools(
      allBlocks,
      turnData.assistantContent,
      state,
      deps,
      turn,
      spec.maxUserQuestions
    );
    for (const block of allBlocks) {
      if (handledIds.has(block.id)) {
        (_s = deps.onEvent) == null ? void 0 : _s.call(deps, {
          kind: "tool-result",
          name: block.name,
          ok: true
        });
      }
    }
    for (const block of allBlocks) {
      if (handledIds.has(block.id)) continue;
      const outcome = await spec.handleExtraToolUse(block, state);
      if (outcome.action === "earlyReturn") {
        (_t = deps.onEvent) == null ? void 0 : _t.call(deps, {
          kind: "tool-result",
          name: block.name,
          ok: !isGenerateError(outcome.result)
        });
        return outcome.result;
      }
      (_u = deps.onEvent) == null ? void 0 : _u.call(deps, {
        kind: "tool-result",
        name: block.name,
        ok: true
      });
      toolResults.push(...outcome.toolResults);
    }
    const toolsUsed = allBlocks.map((b2) => b2.name);
    const toolsLogged = allBlocks.map(
      (b2) => {
        var _a3;
        return b2.name === "get_node_schema" ? `get_node_schema(${(_a3 = b2.input.type) != null ? _a3 : "?"})` : b2.name;
      }
    );
    console.log(
      `[generate] turn ${turn + 1}: model used [${toolsLogged.join(", ")}]`
    );
    state.turnLogs.push({
      turn,
      toolsOffered: toolNames,
      toolsUsed,
      stopReason: turnData.stopReason,
      inputTokens: turnData.usage.inputTokens,
      outputTokens: turnData.usage.outputTokens
    });
    state.messages.push({ role: "user", content: toolResults });
  }
  return {
    error: true,
    message: "Max conversation turns exceeded without generating a result.",
    usage: state.totalUsage
  };
};

// src/prompts/orgTreeWalker.ts
var ORG_TREE_SLOTS = [
  "appConfig",
  "users",
  "objectTypes",
  "objectInstances",
  "plugins"
];
var walkParsedTree = (root, onEvent) => {
  const visit = (node2, parentKey, childIndex) => {
    if (typeof node2 !== "object" || node2 === null) return;
    const nodeType2 = typeof node2.type === "string" ? node2.type : void 0;
    const nodeName = typeof node2.name === "string" ? node2.name : void 0;
    const pointerKey = childIndex === null ? parentKey : `${parentKey}.${nodeType2 != null ? nodeType2 : "node"}[${childIndex}]`;
    if (nodeType2) {
      onEvent({
        kind: "node-added",
        nodeType: nodeType2,
        nodeName,
        pointerKey
      });
    }
    const children2 = Array.isArray(node2._children) ? node2._children : [];
    for (const [i, child] of children2.entries()) {
      visit(child, pointerKey, i);
    }
  };
  for (const slot2 of ORG_TREE_SLOTS) {
    const subtree = root[slot2];
    if (subtree && typeof subtree === "object") {
      visit(subtree, slot2, null);
    }
  }
};

// src/prompts/streamingJson.ts
import { parse as parsePartialJson2, Allow as Allow2 } from "partial-json";
var isRecord8 = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var parseStreamingJson = (text, opts) => {
  const startIndex = text.indexOf(opts.start);
  if (startIndex === -1) return void 0;
  try {
    const parsed = parsePartialJson2(
      text.slice(startIndex),
      opts.partialStrings ? Allow2.ALL : Allow2.OBJ | Allow2.ARR
    );
    return typeof parsed === "object" && parsed !== null ? parsed : void 0;
  } catch {
    return void 0;
  }
};
var dropOpenEmpty = (value) => {
  if (Array.isArray(value)) {
    const last2 = value[value.length - 1];
    const items = isRecord8(last2) && Object.keys(last2).length === 0 ? value.slice(0, -1) : value;
    return items.map(dropOpenEmpty);
  }
  if (isRecord8(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, dropOpenEmpty(v)])
    );
  }
  return value;
};
var viewsFrom = (text, start) => {
  const partial12 = parseStreamingJson(text, { start, partialStrings: true });
  if (partial12 === void 0) return void 0;
  const ready = dropOpenEmpty(
    parseStreamingJson(text, { start, partialStrings: false })
  );
  return { partial: partial12, ready };
};
var opensAsList = (text) => {
  const list3 = /\[\s*(?:[{\]]|$)/.exec(text);
  if (!list3) return false;
  const brace = text.indexOf("{");
  return brace === -1 || list3.index < brace;
};
var settleBareList = (list3, field2) => {
  if (!Array.isArray(list3)) return list3;
  const wholeAnswers = list3.some((o) => isRecord8(o) && field2 in o);
  return wholeAnswers ? list3[0] : { [field2]: list3 };
};
var answerViews = (text, start, opts) => {
  const field2 = opts == null ? void 0 : opts.soleListField;
  if (start !== "{" || !field2 || !opensAsList(text)) {
    return viewsFrom(text, start);
  }
  const views = viewsFrom(text, "[");
  return views && {
    partial: settleBareList(views.partial, field2),
    ready: settleBareList(views.ready, field2)
  };
};

// src/prompts/streamingOrgWalker.ts
var isObject2 = (value) => typeof value === "object" && value !== null;
var hasOrgSlot = (parsed) => ORG_TREE_SLOTS.some((slot2) => parsed[slot2] != null);
var createStreamingOrgWalker = (emit) => {
  const emittedPointerKeys = /* @__PURE__ */ new Set();
  const emitDeduped = (event) => {
    if (event.kind !== "node-added") {
      emit(event);
      return;
    }
    if (emittedPointerKeys.has(event.pointerKey)) return;
    emittedPointerKeys.add(event.pointerKey);
    emit(event);
  };
  const tryParse = (text) => {
    const parsed = parseStreamingJson(text, {
      start: "{",
      partialStrings: false
    });
    return isObject2(parsed) ? parsed : null;
  };
  return {
    process: (accumulatedText) => {
      const parsed = tryParse(accumulatedText);
      if (!parsed) return;
      walkParsedTree(parsed, emitDeduped);
      if (hasOrgSlot(parsed)) {
        emit({ kind: "partial-tree", tree: parsed });
      }
    },
    walkParsed: (parsed) => {
      walkParsedTree(parsed, emitDeduped);
      emit({ kind: "partial-tree", tree: parsed });
    }
  };
};

// src/prompts/orgResult.ts
var processOrgResult = (rawOutput, stopReason, ctx) => {
  var _a2;
  const {
    prompt,
    qaPairs,
    totalUsage,
    exampleOrgsRequested,
    imageSearchResults,
    webSearchResults,
    videoSearchResults,
    turnLogs,
    assumptions,
    streamingWalker
  } = ctx;
  if (stopReason === "max_tokens") {
    return {
      error: true,
      message: "Generation was too large and got truncated. Try a simpler description.",
      usage: totalUsage
    };
  }
  const jsonStr = extractJsonResponseSimple(rawOutput);
  if (!jsonStr) {
    return {
      error: true,
      message: "AI response did not contain valid JSON.",
      usage: totalUsage
    };
  }
  let parsed;
  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    return {
      error: true,
      message: "Failed to parse AI response as JSON.",
      usage: totalUsage
    };
  }
  streamingWalker.walkParsed(parsed);
  const costUsd = calculateCost({
    model: totalUsage.model,
    inputTokens: totalUsage.inputTokens,
    outputTokens: totalUsage.outputTokens
  });
  const meta = {
    userPrompt: prompt,
    qaPairs,
    modelId: totalUsage.model,
    generatedAt: (/* @__PURE__ */ new Date()).toISOString(),
    usage: {
      inputTokens: totalUsage.inputTokens,
      outputTokens: totalUsage.outputTokens,
      costUsd
    },
    ...exampleOrgsRequested.length > 0 ? { exampleOrgsRequested } : {},
    ...imageSearchResults.length > 0 ? { imageSearches: imageSearchResults } : {},
    ...webSearchResults.length > 0 ? { webSearches: webSearchResults } : {},
    ...videoSearchResults.length > 0 ? {
      videoSearches: videoSearchResults.map((r) => ({
        query: r.query,
        title: r.title,
        url: r.url,
        embedUrl: r.embedUrl,
        duration: r.duration,
        creator: r.creator
      }))
    } : {},
    ...turnLogs.length > 0 ? { turnLogs } : {},
    ...(_a2 = ctx.extraMeta) != null ? _a2 : {}
  };
  const treeWithMeta = { ...parsed, _meta: meta };
  const treeJsonWithMeta = JSON.stringify(treeWithMeta, null, 2);
  const rawOrgData2 = treeToRawOrgData(
    parsed
  );
  const rawOrgDataJson = JSON.stringify(rawOrgData2, null, 2);
  const orgDataResult = selectOrgData(rawOrgData2);
  const validationError = isLeft(orgDataResult) ? String(orgDataResult.left) : void 0;
  return {
    treeJsonWithMeta,
    rawOrgDataJson,
    meta,
    orgSummary: parsed.orgSummary,
    usage: totalUsage,
    validationError,
    ...assumptions.length > 0 ? { assumptions } : {}
  };
};
var isGenerateOrgError = (result) => isGenerateError(result);

// src/prompts/graph/graphOps.ts
var isNode5 = (item) => typeof item.type === "string";
var SLOTS = ["appConfig", "users", "objectTypes", "objectInstances", "plugins"];
var isSlot2 = (s) => SLOTS.includes(s);
var isRecord9 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var clone = (v) => structuredClone(v);
var displayName3 = (n) => typeof n.name === "string" && n.name !== "" ? n.name : typeof n.label === "string" ? n.label : "";
var childrenOf5 = (n) => Array.isArray(n._children) ? n._children : [];
var listNames2 = (kids5) => {
  const names2 = kids5.map(displayName3).filter((n) => n !== "");
  return names2.length === 0 ? "(none)" : names2.map((n) => JSON.stringify(n)).join(", ");
};
var opShape = (raw, i) => {
  const bad = (msg) => ({ ok: false, error: `op ${i}${isRecord9(raw) && typeof raw.op === "string" ? ` (${raw.op})` : ""}: ${msg}` });
  if (!isRecord9(raw)) return bad("must be an object");
  const kind = raw.op;
  const path = raw.path;
  const needPath = () => typeof path === "string" && path !== "" ? path : void 0;
  switch (kind) {
    case "get": {
      const p = needPath();
      if (!p) return bad("path must be a string");
      const level = raw.level;
      if (level !== void 0 && level !== 1 && level !== 2 && level !== 3) return bad("level must be 1, 2 or 3");
      return { ok: true, op: { op: "get", path: p, ...level !== void 0 ? { level } : {} } };
    }
    case "set":
    case "add": {
      const p = needPath();
      if (!p) return bad("path must be a string");
      if (!isRecord9(raw.node)) return bad("node must be an object");
      if (kind === "add") {
        const at = raw.at;
        if (at !== void 0 && typeof at !== "number") return bad("at must be a number");
        return { ok: true, op: { op: "add", path: p, node: raw.node, ...at !== void 0 ? { at } : {} } };
      }
      return { ok: true, op: { op: "set", path: p, node: raw.node } };
    }
    case "remove": {
      const p = needPath();
      if (!p) return bad("path must be a string");
      return { ok: true, op: { op: "remove", path: p } };
    }
    case "template": {
      const p = needPath();
      if (!p) return bad("path must be a string");
      if (typeof raw.html !== "string") return bad("html must be a string");
      return { ok: true, op: { op: "template", path: p, html: raw.html } };
    }
    case "types":
      if (!Array.isArray(raw.objectTypes)) return bad("objectTypes must be an array");
      return { ok: true, op: { op: "types", objectTypes: raw.objectTypes } };
    case "data": {
      const oi = raw.objectInstances;
      const pl = raw.plugins;
      if (oi === void 0 && pl === void 0) return bad("data needs objectInstances and/or plugins");
      if (oi !== void 0 && !isRecord9(oi)) return bad("objectInstances must be an object");
      if (pl !== void 0 && !isRecord9(pl)) return bad("plugins must be an object");
      return { ok: true, op: { op: "data", ...oi ? { objectInstances: oi } : {}, ...pl ? { plugins: pl } : {} } };
    }
    case "validate":
    case "run":
      if (path !== void 0 && typeof path !== "string") return bad("path must be a string");
      return { ok: true, op: { op: kind, ...typeof path === "string" ? { path } : {} } };
    case "install": {
      if (typeof raw.feature !== "string" || raw.feature === "") return bad('feature must be a feature id like "mock/journal"');
      const slug = raw.slug;
      if (slug !== void 0 && (typeof slug !== "string" || slug === "")) return bad("slug must be a string");
      const skip = raw.skip;
      if (skip !== void 0 && !(Array.isArray(skip) && skip.every((k) => typeof k === "string"))) return bad("skip must be an array of option keys");
      return {
        ok: true,
        op: {
          op: "install",
          feature: raw.feature,
          ...typeof slug === "string" ? { slug } : {},
          ...Array.isArray(skip) ? { skip: skip.filter((k) => typeof k === "string") } : {}
        }
      };
    }
    case "customize":
      if (typeof raw.feature !== "string" || raw.feature === "") return bad("feature must be an installed feature's id");
      if (typeof raw.reason !== "string" || raw.reason.trim() === "") return bad("reason must say what you are changing and why");
      return { ok: true, op: { op: "customize", feature: raw.feature, reason: raw.reason } };
    case "done":
      return { ok: true, op: { op: "done" } };
    default:
      return { ok: false, error: `op ${i}: unknown op ${JSON.stringify(kind)}` };
  }
};
var parseOps = (text) => {
  const start = text.indexOf("[");
  const end = text.lastIndexOf("]");
  if (start === -1 || end <= start) return { ok: false, error: "reply must be a JSON array of ops" };
  let parsed;
  try {
    parsed = JSON.parse(text.slice(start, end + 1));
  } catch (e) {
    return { ok: false, error: `reply must be a JSON array of ops; JSON.parse: ${e instanceof Error ? e.message : String(e)}` };
  }
  if (!Array.isArray(parsed)) return { ok: false, error: "reply must be a JSON array of ops" };
  const ops = [];
  for (let i = 0; i < parsed.length; i++) {
    const r = opShape(parsed[i], i + 1);
    if (!r.ok) return r;
    ops.push(r.op);
  }
  return { ok: true, ops };
};
var findChild = (container, seg, structural) => {
  const kids5 = childrenOf5(container);
  const index = kids5.findIndex((k) => displayName3(k) === seg);
  if (index !== -1) return { node: kids5[index], parent: container, index, structural: `${structural}._children[${index}]` };
  for (const [i, k] of kids5.entries()) {
    if (k.type !== "folder") continue;
    const hit = findChild(k, seg, `${structural}._children[${i}]`);
    if (hit) return hit;
  }
  return void 0;
};
var resolve = (graph, path) => {
  const segments = path.split(".");
  const [head2, ...rest] = segments;
  if (!head2 || segments.some((s) => s === "")) {
    return { ok: false, error: `invalid path "${path}"; expected dotted names like appConfig.home or objectTypes.Recipe` };
  }
  if (!isSlot2(head2)) return { ok: false, error: `unknown slot "${head2}"; expected one of ${SLOTS.join(", ")}` };
  const root = graph[head2];
  if (!root) return { ok: false, error: `the org has no ${head2} slot` };
  if (rest.length === 0) return { ok: true, target: { kind: "slot", slot: head2, root, structural: head2 } };
  let container = root;
  let structural = head2;
  let prefix = head2;
  for (let i = 0; i < rest.length; i++) {
    const seg = rest[i];
    const hit = findChild(container, seg, structural);
    if (!hit) return { ok: false, error: `no "${seg}" under ${prefix}; available: ${listNames2(childrenOf5(container))}` };
    structural = hit.structural;
    prefix = `${prefix}.${seg}`;
    if (i === rest.length - 1) {
      return { ok: true, target: { kind: "node", slot: head2, node: hit.node, parent: hit.parent, index: hit.index, structural } };
    }
    container = hit.node;
  }
  return { ok: false, error: `invalid path "${path}"` };
};
var toNamePath = (graph, structural) => {
  const segs = structural.split(".");
  const [head2, ...rest] = segs;
  if (!head2 || !isSlot2(head2) || !graph[head2]) return structural;
  let node2 = graph[head2];
  const out = [head2];
  let i = 0;
  for (; i < rest.length; i++) {
    const m = /^_children\[(\d+)\]$/.exec(rest[i]);
    if (!m || !node2) break;
    const child = childrenOf5(node2)[Number(m[1])];
    if (!child) break;
    out.push(displayName3(child) || rest[i]);
    node2 = child;
  }
  return [...out, ...rest.slice(i)].join(".");
};
var mintIds = (node2, mint, fallbackType = "node") => {
  if (node2.id === void 0 || node2.id === "") {
    node2.id = mint(typeof node2.type === "string" && node2.type !== "" ? node2.type : fallbackType);
  }
  childrenOf5(node2).forEach((c) => mintIds(c, mint, fallbackType));
};
var defaultMint = /* @__PURE__ */ (() => {
  let n = 0;
  return (type14) => `${type14}-${(++n).toString(36).padStart(4, "0")}`;
})();
var withIds = (input, mint = defaultMint) => {
  const graph = clone(input);
  if (graph.appConfig) mintIds(graph.appConfig, mint);
  if (graph.users) mintIds(graph.users, mint);
  if (graph.objectTypes) childrenOf5(graph.objectTypes).forEach((t30) => mintIds(t30, mint, "objectType"));
  if (graph.objectInstances) mintIds(graph.objectInstances, mint, "folder");
  if (graph.plugins) childrenOf5(graph.plugins).forEach((p) => mintIds(p, mint, "htmlPlugin"));
  return graph;
};
var isGroupFolder = (n) => n.type === "folder" && Array.isArray(n._children);
var groupRows = (slot2) => {
  var _a2, _b;
  if (typeof slot2.type !== "string") slot2.type = "folder";
  const groups = /* @__PURE__ */ new Map();
  const order = [];
  const group = (name) => {
    let g = groups.get(name);
    if (!g) {
      g = { type: "folder", name };
      groups.set(name, g);
      order.push(name);
    }
    return g;
  };
  for (const entry of childrenOf5(slot2)) {
    if (isGroupFolder(entry)) {
      const name = displayName3(entry) || String((_b = (_a2 = childrenOf5(entry)[0]) == null ? void 0 : _a2.type) != null ? _b : "rows");
      const g = group(name);
      g._children = [...childrenOf5(g), ...childrenOf5(entry)];
      if (entry.id !== void 0 && g.id === void 0) g.id = entry.id;
    } else {
      const g = group(typeof entry.type === "string" ? entry.type : "rows");
      g._children = [...childrenOf5(g), entry];
    }
  }
  slot2._children = order.map((n) => groups.get(n));
  return slot2;
};
var seedRowCount = (graph) => {
  if (!isRecord9(graph) || !isRecord9(graph.objectInstances)) return 0;
  const slot2 = graph.objectInstances;
  return childrenOf5(slot2).reduce((a, g) => a + (isGroupFolder(g) ? childrenOf5(g).length : 1), 0);
};
var pinnedFeature = (n) => n.type === "folder" && isRecord9(n.feature) && typeof n.feature.id === "string" ? n.feature.id : void 0;
var featureAt = (graph, structural) => {
  const [head2, ...rest] = structural.split(".");
  if (!head2 || !isSlot2(head2)) return void 0;
  let node2 = graph[head2];
  for (const seg of rest) {
    const m = /^_children\[(\d+)\]$/.exec(seg);
    if (!m || !node2) return void 0;
    node2 = childrenOf5(node2)[Number(m[1])];
    const id = node2 ? pinnedFeature(node2) : void 0;
    if (id !== void 0) return id;
  }
  return void 0;
};
var keepFeatureFolders = (current, written) => {
  const kept = childrenOf5(current != null ? current : { type: "folder" }).filter((c) => pinnedFeature(c) !== void 0);
  if (kept.length === 0) return { entries: written, kept };
  const keptFolders = new Set(kept.map(displayName3));
  const keptNames = new Set(kept.flatMap((f) => childrenOf5(f).map(displayName3)).filter((n) => n !== ""));
  const entries = written.filter(
    (e) => !keptFolders.has(displayName3(e)) && !(e.type !== "folder" && keptNames.has(displayName3(e)))
  );
  return { entries, kept };
};
var errorKeys = (graph) => validateOrg(graph).errors.filter((e) => e.severity === "error").map((e) => `[${e.kind}] ${e.message}`);
var added = (before, after) => {
  var _a2;
  const left4 = /* @__PURE__ */ new Map();
  for (const k of before) left4.set(k, ((_a2 = left4.get(k)) != null ? _a2 : 0) + 1);
  return after.filter((k) => {
    var _a3;
    const n = (_a3 = left4.get(k)) != null ? _a3 : 0;
    if (n === 0) return true;
    left4.set(k, n - 1);
    return false;
  });
};
var names = (xs) => xs.length === 0 ? "none" : xs.join(", ");
var renderInstalled = (bundle, report, options) => {
  var _a2, _b;
  const { provides } = bundle.manifest;
  const renamed = [...report.graft.nodes, ...report.graft.types].map((r) => `${r.from} \u2192 ${r.to}`);
  const lines = [
    `ok (installed ${report.id} v${report.version} as feature-${report.slug})`,
    `screens  ${names((_a2 = provides.screens) != null ? _a2 : [])}`,
    `types    ${names((_b = provides.types) != null ? _b : [])}`
  ];
  if (renamed.length > 0) lines.push(`renamed  ${renamed.join(", ")} \u2014 use the new names`);
  for (const b2 of report.bindings) {
    lines.push(b2.host ? `${b2.wellKnown}: uses this org's ${b2.host}` : `${b2.wellKnown}: brought its own ${b2.brought}`);
  }
  for (const o of options) {
    lines.push(`option   ${o.key}: ${report.applied.includes(o.key) ? "added" : "skipped"} (${o.question})`);
  }
  for (const w of report.seams) {
    const fills = w.contributions.map((c) => `${c.feature}.${c.seam}`);
    const waiting = w.waiting.map((c) => `${c.feature}.${c.seam} (waiting for a ${c.hostType} type)`);
    lines.push(`slot     ${w.feature}.${w.slot} \u2190 ${names([...fills, ...waiting])}`);
  }
  for (const n of report.notes) lines.push(`note     ${n}`);
  lines.push(`Its nodes are this org's now, but they belong to ${report.id}: change one only after a customize op.`);
  return lines.join("\n");
};
var describe = (n) => `${String(n.type)} ${JSON.stringify(displayName3(n))}`;
var applyOps = (input, ops, opts = {}) => {
  var _a2, _b, _c, _d, _e;
  const mint = (_a2 = opts.mintId) != null ? _a2 : defaultMint;
  const graph = clone(input);
  const results = [];
  let mutations = 0;
  let done = false;
  const customized = [];
  const declared = (id) => {
    var _a3, _b2;
    return customized.includes(id) || ((_b2 = (_a3 = opts.customized) == null ? void 0 : _a3.has(id)) != null ? _b2 : false);
  };
  const featureNote = (structural) => {
    const id = featureAt(graph, structural);
    if (id === void 0 || declared(id)) return "";
    return ` \u2014 this is inside installed feature ${id}: declare the change with {"op":"customize","feature":"${id}","reason":"\u2026"}, or the closing gate refuses it`;
  };
  const ok = (op, text, mutated = false) => {
    if (mutated) mutations += 1;
    results.push({ op, ok: true, text });
  };
  const err = (op, text) => {
    results.push({ op, ok: false, text });
  };
  for (const op of ops) {
    if (done) break;
    const forbidden = (_b = opts.forbid) == null ? void 0 : _b[op.op];
    if (forbidden !== void 0) {
      err(op, forbidden);
      continue;
    }
    switch (op.op) {
      case "done":
        done = true;
        ok(op, "ok");
        break;
      case "get": {
        try {
          ok(op, orgOutline(graph, { level: (_c = op.level) != null ? _c : 2, at: op.path }));
        } catch (e) {
          err(op, e instanceof Error ? e.message : String(e));
        }
        break;
      }
      case "set": {
        const r = resolve(graph, op.path);
        if (!r.ok) {
          err(op, r.error);
          break;
        }
        if (r.target.kind !== "node") {
          err(op, `${op.path} is a slot; use types/data to replace a whole slot, or add to put a node in it`);
          break;
        }
        const next = clone(op.node);
        if (next.id === void 0 || next.id === "") next.id = r.target.node.id;
        mintIds(next, mint);
        const note = featureNote(r.target.structural);
        childrenOf5(r.target.parent)[r.target.index] = next;
        ok(op, `ok (replaced ${describe(next)}, ${childrenOf5(next).length} children)${note}`, true);
        break;
      }
      case "add": {
        const r = resolve(graph, op.path);
        if (!r.ok) {
          err(op, r.error);
          break;
        }
        const container = r.target.kind === "slot" ? r.target.root : r.target.node;
        const kids5 = Array.isArray(container._children) ? container._children : container._children = [];
        const next = clone(op.node);
        mintIds(next, mint, r.target.slot === "objectTypes" ? "objectType" : "node");
        const at = op.at === void 0 ? kids5.length : Math.max(0, Math.min(kids5.length, op.at));
        kids5.splice(at, 0, next);
        const note = r.target.kind === "node" ? featureNote(r.target.structural) : "";
        ok(op, `ok (${describe(next)} ${op.at === void 0 ? "appended" : "inserted"} at index ${at})${note}`, true);
        break;
      }
      case "remove": {
        const r = resolve(graph, op.path);
        if (!r.ok) {
          err(op, r.error);
          break;
        }
        if (r.target.kind !== "node") {
          err(op, `${op.path} is a slot and cannot be removed`);
          break;
        }
        const note = featureNote(r.target.structural);
        childrenOf5(r.target.parent).splice(r.target.index, 1);
        ok(op, `ok (removed ${describe(r.target.node)})${note}`, true);
        break;
      }
      case "template": {
        const r = resolve(graph, op.path);
        if (!r.ok) {
          err(op, r.error);
          break;
        }
        if (r.target.kind !== "node" || !(r.target.node.type === "content" || r.target.slot === "plugins")) {
          const what = r.target.kind === "node" ? `a ${String(r.target.node.type)}` : "a slot";
          err(op, `${op.path} is ${what}, not a content node or plugin`);
          break;
        }
        r.target.node.htmlTemplate = op.html;
        ok(op, `ok (${op.html.length} chars)${featureNote(r.target.structural)}`, true);
        break;
      }
      case "types": {
        const written = op.objectTypes.filter(isRecord9).map((t30) => clone(t30));
        const { entries, kept } = keepFeatureFolders(graph.objectTypes, written);
        entries.forEach((t30) => mintIds(t30, mint, "objectType"));
        graph.objectTypes = { ...(_d = graph.objectTypes) != null ? _d : { type: "folder" }, _children: [...entries, ...kept] };
        const keptText = kept.length > 0 ? `; kept installed ${listNames2(kept)} as they are` : "";
        ok(op, `ok (${entries.length} object types: ${listNames2(entries)}${keptText})`, true);
        break;
      }
      case "data": {
        const parts = [];
        if (op.objectInstances) {
          const written = clone(op.objectInstances);
          const { entries, kept } = keepFeatureFolders(graph.objectInstances, childrenOf5(written));
          const slot2 = groupRows({ ...written, _children: entries });
          mintIds(slot2, mint, "folder");
          slot2._children = [...childrenOf5(slot2), ...kept];
          graph.objectInstances = slot2;
          const keptText = kept.length > 0 ? `; kept installed ${listNames2(kept)} as they are` : "";
          parts.push(`objectInstances: ${childrenOf5(slot2).length - kept.length} groups, ${seedRowCount({ objectInstances: { type: "folder", _children: entries } })} rows${keptText}`);
        }
        if (op.plugins) {
          const slot2 = clone(op.plugins);
          if (typeof slot2.type !== "string") slot2.type = "folder";
          mintIds(slot2, mint, "htmlPlugin");
          graph.plugins = slot2;
          parts.push(`plugins: ${listNames2(childrenOf5(slot2))}`);
        }
        ok(op, `ok (${parts.join("; ")})`, true);
        break;
      }
      case "install": {
        const catalogue = (_e = opts.features) != null ? _e : [];
        if (catalogue.length === 0) {
          err(op, "no feature catalogue in this session \u2014 build it with add/set instead");
          break;
        }
        const bundle = catalogue.find((b2) => b2.manifest.id === op.feature);
        if (!bundle) {
          err(op, `no feature "${op.feature}"; available: ${catalogue.map((b2) => b2.manifest.id).join(", ")}`);
          break;
        }
        const out = installFeature(graph, bundle, {
          ...op.slug !== void 0 ? { slug: op.slug } : {},
          ...op.skip !== void 0 ? { skip: op.skip } : {}
        });
        if (isLeft3(out)) {
          err(op, out.left.message);
          break;
        }
        const tree2 = out.right.tree;
        const next = {};
        for (const slot2 of SLOTS) {
          const v = tree2[slot2];
          if (isRecord9(v) && isNode5(v)) next[slot2] = v;
        }
        const before = errorKeys(graph);
        const after = errorKeys({ ...graph, ...next });
        const broken = added(before, after);
        if (broken.length > 0) {
          err(op, `installing ${op.feature} would add validator errors, so nothing was installed:
${broken.join("\n")}`);
          break;
        }
        Object.assign(graph, next);
        ok(op, renderInstalled(bundle, out.right.report, out.right.options), true);
        break;
      }
      case "customize": {
        if (!collectFeatureFolders(graph).some((f) => f.id === op.feature)) {
          const ids = [...new Set(collectFeatureFolders(graph).map((f) => f.id))];
          err(op, `${op.feature} is not installed here; installed: ${ids.length ? ids.join(", ") : "none"}`);
          break;
        }
        if (!customized.includes(op.feature)) customized.push(op.feature);
        ok(op, `ok (${op.feature} is customised from here on; og feature status will report it as diverged from its bundle)`);
        break;
      }
      case "validate": {
        let prefix;
        if (op.path !== void 0) {
          const r = resolve(graph, op.path);
          if (!r.ok) {
            err(op, r.error);
            break;
          }
          prefix = r.target.structural;
        }
        const errors = validateOrg(graph).errors.filter(
          (e) => prefix === void 0 || e.path === prefix || e.path.startsWith(`${prefix}.`)
        );
        const where = op.path ? ` under ${op.path}` : "";
        ok(op, errors.length === 0 ? `no errors${where}` : errors.map((e) => renderError(graph, e)).join("\n"));
        break;
      }
      case "run": {
        let prefix;
        if (op.path !== void 0) {
          const r = resolve(graph, op.path);
          if (!r.ok) {
            err(op, r.error);
            break;
          }
          prefix = r.target.structural;
        }
        const findings = smokeOrg(graph).filter(
          (e) => prefix === void 0 || e.path === prefix || e.path.startsWith(`${prefix}.`)
        );
        const where = op.path ? ` under ${op.path}` : "";
        ok(op, findings.length === 0 ? `renders cleanly${where}` : findings.map((e) => renderError(graph, e)).join("\n"));
        break;
      }
    }
  }
  return { graph, results, done, mutations, customized };
};
var renderError = (graph, e) => `[${e.severity === "error" ? "" : `${e.severity} `}${e.kind}] ${toNamePath(graph, e.path)}: ${e.message}${e.fixHint ? ` (fix: ${e.fixHint})` : ""}`;
var renderValidationErrors = (graph, errors) => errors.map((e) => renderError(graph, e));
var PLACEHOLDER = /^<!--\s*TODO\(purpose\)[\s\S]*-->$/;
var isBlankTemplate = (t30) => {
  if (typeof t30 !== "string") return true;
  if (/^@`[\s\S]*`$/.test(t30.trim())) return false;
  const body = t30.trim();
  return body === "" || PLACEHOLDER.test(body);
};
var todoKeys = (v, prefix, out) => {
  if (Array.isArray(v)) return v.forEach((x, i) => todoKeys(x, `${prefix}[${i}]`, out));
  if (!isRecord9(v)) return;
  for (const [k, val] of Object.entries(v)) {
    if (k === "_children") continue;
    if (val === "TODO") out.push(`${prefix}.${k}`);
    else todoKeys(val, `${prefix}.${k}`, out);
  }
};
var outstandingFills = (graph) => {
  const fills = [];
  const pluginNames = new Set(sectionNodes(graph.plugins).filter(isNode5).map(displayName3).filter((n) => n !== ""));
  const hasParams = (n) => isRecord9(n.parameters) && Object.keys(n.parameters).length > 0;
  const walk8 = (n, path) => {
    if (n.type === "content" && isBlankTemplate(n.htmlTemplate)) {
      const purpose = typeof n.purpose === "string" && n.purpose !== "" ? n.purpose : "no purpose given";
      fills.push({ kind: "template", path, detail: purpose });
    }
    if (typeof n.type === "string" && pluginNames.has(n.type) && !hasParams(n)) {
      fills.push({ kind: "required", path: `${path}.parameters`, detail: `${n.type} plugin instance has no parameters; pass every parameter the plugin declares` });
    }
    const todos = [];
    todoKeys(n, path, todos);
    todos.forEach((p) => fills.push({ kind: "required", path: p, detail: 'required field left as "TODO"' }));
    childrenOf5(n).forEach((c) => walk8(c, `${path}.${displayName3(c)}`));
  };
  if (graph.appConfig) childrenOf5(graph.appConfig).forEach((c) => walk8(c, `appConfig.${displayName3(c)}`));
  for (const p of sectionNodes(graph.plugins).filter(isNode5)) {
    if (isBlankTemplate(p.htmlTemplate)) {
      fills.push({ kind: "template", path: `plugins.${displayName3(p)}`, detail: "plugin has no template" });
    }
  }
  if (sectionNodes(graph.objectTypes).length > 0 && seedRowCount(graph) === 0) {
    fills.push({ kind: "required", path: "objectInstances", detail: "no seed rows; use data to seed every object type" });
  }
  return fills;
};
var opPath = (op) => "path" in op && op.path ? ` ${op.path}` : "";
var renderReply = (results, fills, opts) => {
  var _a2;
  const cap = (_a2 = opts.cap) != null ? _a2 : 40;
  const lines = [];
  results.forEach((r, i) => {
    const head2 = `${i + 1} ${r.op.op}${opPath(r.op)}:`;
    const body = r.ok ? r.text : `ERROR ${r.text}`;
    if (body.includes("\n")) {
      lines.push(head2);
      lines.push(...body.split("\n").map((l) => `  ${l}`));
    } else {
      lines.push(`${head2} ${body}`);
    }
  });
  lines.push("");
  if (fills.length === 0) {
    lines.push("Outstanding: none");
  } else {
    lines.push(`Outstanding (${fills.length}):`);
    fills.slice(0, cap).forEach((f) => lines.push(`  ${f.kind.padEnd(9)} ${f.path}: ${f.detail}`));
    if (fills.length > cap) lines.push(`  \u2026 ${fills.length - cap} more`);
  }
  return lines.join("\n");
};

// src/prompts/graph/graphPrompts.ts
var FIELD_TYPES = [...FIELD_TYPE_BUILTINS].filter((t30) => t30 !== "variable");
var referenceSection = (refs) => `

## Reference structures
Real Orgiggly apps related to this request, as L1 outlines. Learn the patterns from them \u2014 screen layout, data model, navigation \u2014 but this is not a spec: design what the user asked for.

${refs.map((r) => `### ${r.orgId}
\`\`\`jsx
${r.outline}
\`\`\``).join("\n\n")}`;
var structurePrompt = (userPrompt, refs = []) => `Stage 1 of 2 \u2014 STRUCTURE.

The user asked for: ${userPrompt}

Reply with ONLY an **L1 outline** of the whole org \u2014 no JSON, no prose, no code fence. It must contain:
- \`<org>\` as the root, then \`<appConfig name="\u2026">\` holding EVERY screen, subsection, content, form, flow, variable and plugin instance as elements (tag = node type), each with \`name\`, a \`label\` where it differs, and the data-binding sugar (\`repeating\`, \`of="Type"\`, \`source=\`) where it applies.
- a \`purpose="\u2026"\` on EVERY content node \u2014 one sentence saying what the block shows or does. Stage 2 writes the HTML from it.
- \`<objectTypes>\` with every type and its \`<field name:type/>\` children, \`parent={\u2026}\` where a type nests under another. A field's type must be one of ${FIELD_TYPES.map((t30) => `\`${t30}\``).join(", ")}, a declared object type's name, or \`list<T>\` / \`map<T>\` over one of those \u2014 dates and times are \`temporal\`.
- \`<objectInstances>\` with one \`<Type count={n} eg={[\u2026]}/>\` line per type you will seed.
- \`<plugins>\` with any plugin you define and its \`params={[\u2026]}\`.

No htmlTemplate, no formulas, no layout yet \u2014 those are stage 2. Get the screens, the navigation between them and the data model right.${refs.length ? referenceSection(refs) : ""}`;
var structureFixPrompt = (problems) => `The outline could not be used:
${problems.map((p) => `- ${p}`).join("\n")}

Reply with the corrected L1 outline of the whole org, and nothing else.`;
var sliceOutlineGrammar = (doc) => {
  const start = doc.indexOf("## Grammar");
  const end = doc.indexOf("## Parsing");
  if (start === -1 || end === -1) {
    throw new Error("docs/OUTLINE.md no longer has ## Grammar / ## Parsing");
  }
  return doc.slice(start, end).trim();
};
var structureEditPrompt = (edit) => `The user reviewed this outline and wants a change before anything is built: ${edit}

Reply with the revised L1 outline of the whole org, and nothing else.`;
var OUTLINE_READING = `### Reading the outline \`get\` returns

\`get\` returns the requested part of the org as a compact **outline**, not JSON. It is a reading format only \u2014 you never write it. How it maps to the JSON you do write:
- **Element = node.** The tag is the node's \`type\`, \`name="\u2026"\` its \`name\`, nested elements its \`_children\`.
- **Attribute values:** \`"\u2026"\` is a JSON string, \`{\u2026}\` is JSON (number, object, array, \`null\`, \`false\`), a bare key is \`true\`. \`id\`s and empty values are omitted.
- **Data-binding shorthand:** bare \`repeating\` is \`"isRepeating": true\`; \`of="Book"\` is \`"data": { "dataType": "Book" }\`; \`source="storage"\` is \`data.source\`.
- **\`purpose="\u2026"\`** on a content node says what its HTML must do. **\`html="\u2026"\`** (level 2) is the first line of an existing template, clipped; at level 3 the full template is the element's body.
- **\`<objectTypes>\`:** the tag is the type name; \`<field title:text/>\` is a field \`{ "name": "title", "type": "text" }\`; bare \`list\` is \`"isList": true\`.
- **\`<objectInstances>\`:** \`<Book count={12} eg={["Dune","Emma"]}/>\` summarises seed rows. **\`<plugins>\`:** the tag is the plugin name.`;
var structureSection = (grammar) => `## Stage 1 \u2014 structure, written as an outline

The first reply in this session is an **L1 outline** of the whole org \u2014 outline notation, not JSON. Everything above about node types, fields and data binding still applies; only the syntax changes for that one reply. The notation is exact: the system parses it, **indentation is significant** (two spaces per level, a child sits exactly two spaces in from its parent), and a node with children closes with its own tag. After the structure is accepted you switch to JSON ops (next section) and never write outline syntax again.

${grammar}`;
var OP_PROTOCOL = `**Every reply is ONE JSON array of ops and nothing else** \u2014 no prose, no code fence needed. The harness applies each op in order and answers with one result line per op, then the worklist of what is still outstanding.

| op | fields | what it does |
|---|---|---|
| \`get\` | \`path\`, optional \`level\` 1\u20133 (default 2) | returns that part of the org as an outline (see below) |
| \`set\` | \`path\`, \`node\` | REPLACES the node at \`path\` with \`node\` (a full JSON node, \`_children\` included) |
| \`add\` | \`path\`, \`node\`, optional \`at\` | appends \`node\` as a child of the node (or slot) at \`path\`, or inserts at index \`at\` |
| \`remove\` | \`path\` | removes the node at \`path\` |
| \`template\` | \`path\`, \`html\` | sets the \`htmlTemplate\` of the content node or plugin at \`path\` \u2014 \`html\` is the template, any HTML, any indentation; \`\${\u2026}\` interpolates, with or without the \`@\` + backtick wrapper |
| \`types\` | \`objectTypes\` (array of object type JSON) | replaces ALL object types |
| \`data\` | \`objectInstances\` and/or \`plugins\` | replaces the seed data and/or plugins slot \u2014 shapes below |
| \`validate\` | optional \`path\` | runs the validator; reports errors under \`path\` (or everywhere) with fix hints |
| \`run\` | optional \`path\` | renders every screen with the seed data, as the page would; reports templates that fail or show a raw \`\${\`, plugin calls that fail, and unfiltered lists that show no rows |
| \`done\` | \u2014 | you are finished; the harness runs the closing gate |

**Paths are dotted NAMES**, exactly as the outline shows them: \`appConfig.home\`, \`appConfig.home.recipes.card\`, \`objectTypes.Recipe\`, \`objectInstances.Recipe\`, \`plugins.PictureLink\`. Never indices. A wrong path is answered with the names that exist at that point.

**Seed data shape.** \`objectInstances\` is a folder of one folder PER TYPE, each holding that type's rows: \`{ "type": "folder", "_children": [ { "type": "folder", "name": "Recipe", "_children": [ { "type": "Recipe", "Name": "Toast", \u2026 }, \u2026 ] }, { "type": "folder", "name": "Category", "_children": [ \u2026 ] } ] }\`. Every row carries its type in \`type\` and every field the type declares. \`plugins\` is \`{ "type": "folder", "_children": [ { "type": "htmlPlugin", "name": "RecipeCard", "parameters": [ { "name": "title", "type": "text" } ], "htmlTemplate": "\u2026" } ] }\`.

**One write per node.** \`template\` sets a content node's HTML in place; \`set\` replaces the whole node. Use one or the other for a given node in a batch, never both \u2014 a \`set\` that includes \`htmlTemplate\` already carries the template.

**Node JSON:** the same shapes as everywhere in this prompt. You may omit \`id\` \u2014 the harness mints one (and keeps the old id on \`set\`). \`_children\` is the child array. A \`set\` of a screen with its whole subtree is one op; so is a \`template\` on one content node. Choose whichever is less writing.`;
var list2 = (xs) => xs && xs.length > 0 ? xs.join(", ") : "\u2014";
var featureEntry = (b2) => {
  var _a2, _b, _c;
  const m = b2.manifest;
  const lines = [
    `### ${m.id} (v${m.version})`,
    m.description,
    `- brings screens ${list2(m.provides.screens)}; types ${list2(m.provides.types)}`
  ];
  const needs = ((_a2 = m.requires.types) != null ? _a2 : []).map((t30) => t30.name);
  if (needs.length > 0) lines.push(`- needs the org to have ${needs.join(", ")} (or a feature that brings it)`);
  for (const slot2 of (_b = m.provides.slots) != null ? _b : []) lines.push(`- slot \`${slot2.name}\`${slot2.description ? `: ${slot2.description}` : ""}`);
  for (const seam of (_c = m.seams) != null ? _c : []) lines.push(`- fills ${seam.slot} when a ${seam.hostType.name} type is present${seam.description ? `: ${seam.description}` : ""}`);
  return lines.join("\n");
};
var CUSTOMIZE_ROW = "| `customize` | `feature`, `reason` | declares that you are changing an installed feature's own nodes. Without it, the closing gate refuses any change inside a `feature-\u2026` folder |";
var installedSection = `

## Installed features

Folders named \`feature-<slug>\` hold installed features: tested functionality this org shares with others. Change a node inside one only when the request needs it, and declare the change first:

| op | fields | what it does |
|---|---|---|
${CUSTOMIZE_ROW}`;
var featuresSection = (features, hasInstalled = false) => {
  if (features.length === 0) return hasInstalled ? installedSection : "";
  return `

## Installable features

Some functionality comes as a ready-made, tested **feature**. **When the request asks for something a feature below covers, install it \u2014 do not build it with add/set.** Build only what no feature covers.

| op | fields | what it does |
|---|---|---|
| \`install\` | \`feature\` (an id below), optional \`slug\`, optional \`skip\` (option keys) | grafts the feature into the org as \`feature-<slug>\` folders \u2014 screens, functions, types and seed rows \u2014 adds its menu rows and notifications, and connects it to the features already installed. The result line lists what arrived, any names changed to avoid a clash, and the options it applied; it refuses an install that would add validator errors and says why |
${CUSTOMIZE_ROW}

Install what a feature needs first (its "needs" line). After an install, \`get\` its screens before linking to them, and refer to them by the names the result line gave. **When you build new functionality yourself, keep it together**: put its screens and functions in ONE \`folder\` node named \`feature-<slug>\` under appConfig, and its object types in a folder of the same name under objectTypes, so it can be extracted as a feature later.

${features.map(featureEntry).join("\n\n")}`;
};
var graphSection = () => `## Graph-edit generation \u2014 the op protocol

In this session the org already EXISTS as a compiled skeleton: every screen, subsection, content node, object type and plugin from the structure stage is in place, with ids, and it passes the codec. Content nodes carry a \`purpose\` and a placeholder template. Your job is to **fill and refine that graph in place**, one node or one subtree at a time. Everything above about node types, fields, data binding, forms, formulas and HTML templates still applies \u2014 you write ordinary org JSON nodes; only the delivery changes.

${OP_PROTOCOL}

**Working method.** Take the worklist screen by screen: \`get\` a screen at level 1 or 2 when you need its exact names or what is already there, write its templates and any nodes it needs (variables, buttons, forms), then \`validate\` that screen and fix what it reports before moving on. Refer to object types, fields and other screens exactly as the structure names them; \`get objectTypes\` if unsure. Seed realistic data with \`data\` once the types are final. Reply \`[{"op":"done"}]\` only when the worklist is empty and \`validate\` is clean.

${OUTLINE_READING}`;
var editSection = (features = [], hasInstalled = false) => `## Editing an existing org \u2014 the op protocol

In this session the org already EXISTS and is LIVE: real screens, templates, formulas, object types and seed data that its users rely on. Your job is to make **exactly the change the user asks for, and nothing else** \u2014 by editing the graph in place, one node or one subtree at a time. Everything above about node types, fields, data binding, forms, formulas and HTML templates still applies; only the delivery changes.

${OP_PROTOCOL}${featuresSection(features, hasInstalled)}

**Change only what the request needs.** Every node you do not address stays byte-for-byte as it is \u2014 do not tidy, rename, restyle, reformat or "improve" anything the request did not ask for, even where you would have written it differently. \`get\` before you \`set\`: a \`set\` replaces the WHOLE node, so read it at level 3 first and carry over every field and child you are not changing. Prefer \`template\` for an HTML-only change and \`add\` / \`remove\` for a single child over a \`set\` of its parent.

**Never use \`data\`** \u2014 it replaces every seed row in the org; it is refused in this session. To change one object type, \`set\` it at \`objectTypes.<Type>\` rather than \`types\`, which replaces them all.

**Working method.** \`get\` the screens the request touches, make the change, \`validate\` those paths and fix what they report \u2014 but only findings your change introduced; leave existing warnings alone. Reply \`[{"op":"done"}]\` when the change is complete.

${OUTLINE_READING}`;
var editKickoffPrompt = (request, outline) => `The user wants this change to their org: ${request}

Here is the whole org at level 1 (structure only \u2014 \`get\` a path at level 2 or 3 for its fields and templates):

${outline}

Reply with ONLY a JSON array of ops. Start by reading what the change touches.`;
var kickoffPrompt = (userPrompt, skeletonText, worklist) => `The user asked for: ${userPrompt}

The org's structure is compiled and live. Here it is at level 1:

${skeletonText}

${worklist}

Reply with ONLY a JSON array of ops. Start filling the first screen.`;
var nudgePrompt = (error2) => `That reply could not be used: ${error2}

Reply with ONLY a JSON array of ops \u2014 e.g. [{"op":"template","path":"appConfig.home.hero","html":"<h1>\u2026</h1>"}] \u2014 and nothing else.`;
var gateRepairPrompt = (errorLines) => `You said done, but the closing gate reports these blocking errors:
${errorLines.map((l) => `- ${l}`).join("\n")}

Fix them with ops on the nodes named (set / add / remove / template / types / data, or customize for an installed feature you meant to change), then reply done again. Reply with ONLY the JSON array of ops.`;

// src/prompts/graph/graphFill.ts
var ERROR_SAMPLES = 5;
var kindCounts = (errors) => errors.reduce((a, e) => {
  var _a2;
  return a[e.kind] = ((_a2 = a[e.kind]) != null ? _a2 : 0) + 1, a;
}, {});
var gateErrors = (graph) => {
  const blocking = validateOrg(graph).errors.filter(isGenerationBlocking);
  if (blocking.length > 0) return blocking;
  const decoded = selectOrgData(treeToRawOrgData(graph));
  if (isLeft(decoded)) {
    return [
      {
        path: "",
        kind: "invalid-type",
        message: `The org does not decode: ${String(decoded.left)}`,
        fixHint: "The io-ts codec rejected a node; check required fields against the node reference.",
        severity: "error"
      }
    ];
  }
  return [];
};
var folderKey = (f) => `${f.section}\0${f.name}`;
var undeclaredDrift = (graph, before, customized) => driftedFeatureFolders(graph).filter((f) => !before.has(folderKey(f)) && !customized.has(f.id)).map((f) => ({
  ...featureDriftFinding(f),
  message: `This session changed installed feature ${f.id} (${f.section}.${f.name}) without declaring it.`,
  fixHint: `If the change is meant, reply with {"op":"customize","feature":"${f.id}","reason":"\u2026"}. Otherwise undo it: the feature's own nodes should stay as installed \u2014 make the change outside its folder.`,
  severity: "error"
}));
var withoutBaseline = (errors, baseline) => {
  var _a2;
  const key = (e) => `${e.kind}\0${e.message}`;
  const left4 = /* @__PURE__ */ new Map();
  for (const e of baseline) left4.set(key(e), ((_a2 = left4.get(key(e))) != null ? _a2 : 0) + 1);
  return errors.filter((e) => {
    var _a3;
    const n = (_a3 = left4.get(key(e))) != null ? _a3 : 0;
    if (n === 0) return true;
    left4.set(key(e), n - 1);
    return false;
  });
};
var runGraphFill = async (input, opts) => {
  var _a2, _b;
  let graph = input;
  const showWorklist = (_a2 = opts.worklist) != null ? _a2 : true;
  const worklist = (g) => showWorklist ? outstandingFills(g) : [];
  const messages = [
    { role: "user", content: (_b = opts.kickoff) != null ? _b : kickoffPrompt(opts.userPrompt, opts.skeletonText, renderReply([], worklist(graph), {})) }
  ];
  const ops = {};
  let turns = 0;
  let nudges = 0;
  let idle = 0;
  let done = false;
  let idleStop = false;
  const driftBefore = new Set(driftedFeatureFolders(input).map(folderKey));
  const customized = /* @__PURE__ */ new Set();
  const installed = [];
  let doneRefused = 0;
  let opErrors = 0;
  const errorSamples = [];
  const applyReply = (text, allowRefusal) => {
    var _a3, _b2;
    const parsed = parseOps(text);
    if (!parsed.ok) {
      nudges += 1;
      return { applied: false, done: false, reply: nudgePrompt(parsed.error) };
    }
    const r = applyOps(graph, parsed.ops, {
      mintId: opts.mintId,
      forbid: opts.forbid,
      features: opts.features,
      customized
    });
    graph = r.graph;
    r.customized.forEach((id) => customized.add(id));
    for (const res of r.results) if (res.ok && res.op.op === "install") installed.push(res.op.feature);
    if (r.mutations > 0) (_a3 = opts.onGraph) == null ? void 0 : _a3.call(opts, graph);
    for (const res of r.results) {
      if (res.ok) continue;
      opErrors += 1;
      if (errorSamples.length < ERROR_SAMPLES) {
        const where = "path" in res.op && res.op.path ? ` ${res.op.path}` : "";
        errorSamples.push(`${res.op.op}${where}: ${res.text}`);
      }
    }
    for (const op of parsed.ops) ops[op.op] = ((_b2 = ops[op.op]) != null ? _b2 : 0) + 1;
    const fills = worklist(graph);
    let reply = renderReply(r.results, fills, {});
    let accepted = r.done;
    if (r.done && allowRefusal && fills.length > 0 && doneRefused < opts.maxDoneRefusals) {
      doneRefused += 1;
      accepted = false;
      const templates = fills.filter((f) => f.kind === "template").length;
      const required = fills.length - templates;
      const what = [
        templates > 0 ? `${templates} template${templates === 1 ? "" : "s"}` : "",
        required > 0 ? `${required} required field${required === 1 ? "" : "s"}` : ""
      ].filter(Boolean).join(" and ");
      reply += `

Not done: ${what} still outstanding (listed above). Fill them, then reply done.`;
    }
    return { applied: r.mutations > 0, done: accepted, reply };
  };
  while (turns < opts.maxTurns && !done) {
    turns += 1;
    const label2 = `fill-${turns}`;
    const text = await opts.call(label2, messages);
    const r = applyReply(text, true);
    messages.push({ role: "assistant", content: text }, { role: "user", content: r.reply });
    done = r.done;
    idle = r.applied || done ? 0 : idle + 1;
    if (!done && idle >= opts.maxIdle) {
      idleStop = true;
      break;
    }
  }
  const gate = (g) => {
    var _a3;
    return [
      ...withoutBaseline(gateErrors(g), (_a3 = opts.baselineErrors) != null ? _a3 : []),
      ...undeclaredDrift(g, driftBefore, customized)
    ];
  };
  let errors = gate(graph);
  const initialBlocking = errors.length;
  const initialKinds = kindCounts(errors);
  const attempts = [errors];
  let repairRounds = 0;
  while (errors.length > 0 && repairRounds < opts.maxRepairRounds) {
    repairRounds += 1;
    messages.pop();
    messages.push({ role: "user", content: gateRepairPrompt(renderValidationErrors(graph, errors)) });
    const text = await opts.call(`repair-${repairRounds}`, messages);
    const r = applyReply(text, false);
    messages.push({ role: "assistant", content: text }, { role: "user", content: r.reply });
    errors = gate(graph);
    attempts.push(errors);
  }
  return {
    ok: errors.length === 0,
    graph,
    fill: { installed, customized: [...customized], turns, nudges, done, idleStop, ops, outstandingAtEnd: outstandingFills(graph).length, doneRefused, opErrors, errorSamples },
    gate: {
      initialBlocking,
      initialKinds,
      repairRounds,
      finalOk: errors.length === 0,
      finalErrors: errors.map((e) => `[${e.kind}] ${e.path}: ${e.message}`),
      attempts
    }
  };
};

// src/prompts/graph/graphSession.ts
var MAX_TOKENS = 32768;
var addUsage = (into, more) => {
  into.inputTokens += more.inputTokens;
  into.outputTokens += more.outputTokens;
  into.creditsCharged += more.creditsCharged;
};
var withCostUsd = (model, cost) => ({
  ...cost,
  costUsd: calculateCost({
    model,
    inputTokens: cost.inputTokens,
    outputTokens: cost.outputTokens
  })
});
var createGraphSession = (deps, modelId, systemPrompt) => {
  const usage = { model: modelId, inputTokens: 0, outputTokens: 0, creditsCharged: 0 };
  const repairCost = { inputTokens: 0, outputTokens: 0, creditsCharged: 0 };
  const emit = (event) => {
    var _a2;
    return (_a2 = deps.onEvent) == null ? void 0 : _a2.call(deps, event);
  };
  let turnIndex = 0;
  const call = async (label2, messages) => {
    var _a2;
    const turn = turnIndex++;
    (_a2 = deps.onStatus) == null ? void 0 : _a2.call(deps, `${label2} (turn ${turn + 1})...`);
    emit({ kind: "turn-start", turnIndex: turn });
    emit({ kind: "stream-start", turnIndex: turn });
    const out = await deps.callTurn({
      systemPrompt,
      messages,
      model: modelId,
      maxTokens: MAX_TOKENS,
      demoSource: deps.demoSource,
      // The provider's `graph` scenario scripts this path (#2375).
      ...deps.mockScenario ? { mock: true, mockScenario: deps.mockScenario } : {},
      onProgress: (info) => emit({
        kind: "stream-progress",
        turnIndex: turn,
        outputTokens: info.outputTokens
      })
    });
    addUsage(usage, out.usage);
    if (label2.startsWith("repair-")) addUsage(repairCost, out.usage);
    emit({
      kind: "stream-end",
      turnIndex: turn,
      outputTokens: out.usage.outputTokens
    });
    return out.type === "result" ? out.text : "";
  };
  return { call, emit, usage, repairCost };
};
var finishGraphResult = (args) => {
  const { session, report, prompt, modelId } = args;
  const result = processOrgResult(JSON.stringify(report.graph), "end_turn", {
    prompt,
    qaPairs: [],
    totalUsage: session.usage,
    exampleOrgsRequested: [],
    imageSearchResults: [],
    webSearchResults: [],
    videoSearchResults: [],
    turnLogs: [],
    assumptions: [],
    streamingWalker: args.walker,
    extraMeta: args.extraMeta
  });
  if (isGenerateError(result)) return result;
  const finalErrors = report.gate.attempts[report.gate.attempts.length - 1];
  if (finalErrors.length > 0) result._validationErrors = finalErrors;
  const { telemetry } = buildValidationTelemetry({
    attempts: report.gate.attempts.map((errors, turn) => ({ turn, errors })),
    initialOk: report.gate.initialBlocking === 0,
    finalOk: report.gate.finalOk,
    modelId,
    promptName: args.promptName,
    maxFixTurns: args.maxRepairRounds,
    totalCost: withCostUsd(modelId, session.usage),
    fixTurnCost: withCostUsd(modelId, session.repairCost)
  });
  result._validationTelemetry = telemetry;
  return result;
};

// src/prompts/graph/reindentOutline.ts
var INDENT2 = "  ";
var classify2 = (line) => {
  var _a2;
  if (!line.startsWith("<") || !line.endsWith(">")) return "other";
  if (line.startsWith("</")) return /^<\/[A-Za-z_][A-Za-z0-9_]*>$/.test(line) ? "close" : "other";
  let i = 1;
  const isName = (c) => /[A-Za-z0-9_:]/.test(c);
  if (!/[A-Za-z_]/.test((_a2 = line[i]) != null ? _a2 : "")) return "other";
  while (i < line.length && isName(line[i])) i++;
  for (; ; ) {
    while (i < line.length && line[i] === " ") i++;
    if (line.startsWith("/>", i)) return i + 2 === line.length ? "self" : "other";
    if (line[i] === ">") return i + 1 === line.length ? "open" : "other";
    if (i >= line.length) return "other";
    const k0 = i;
    while (i < line.length && isName(line[i])) i++;
    if (i === k0) return "other";
    if (line[i] !== "=") continue;
    i++;
    if (line[i] === '"') {
      i++;
      while (i < line.length && line[i] !== '"') i += line[i] === "\\" ? 2 : 1;
      if (i >= line.length) return "other";
      i++;
    } else if (line[i] === "{") {
      let depth = 0;
      let inStr = false;
      for (; i < line.length; i++) {
        const c = line[i];
        if (inStr) {
          if (c === "\\") i++;
          else if (c === '"') inStr = false;
        } else if (c === '"') inStr = true;
        else if (c === "{") depth++;
        else if (c === "}" && --depth === 0) {
          i++;
          break;
        }
      }
      if (depth !== 0) return "other";
    } else return "other";
  }
};
var reindentOutline = (text) => {
  const lines = text.split("\n");
  const out = [];
  let depth = 0;
  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") {
      out.push("");
      continue;
    }
    const kind = classify2(line);
    if (kind === "other") return text;
    if (kind === "close") {
      depth -= 1;
      if (depth < 0) return text;
    }
    out.push(`${INDENT2.repeat(depth)}${line}`);
    if (kind === "open") depth += 1;
  }
  return out.join("\n");
};

// src/prompts/graph/structureStage.ts
var isNode6 = (item) => typeof item.type === "string";
var nameOf3 = (n) => typeof n.name === "string" && n.name ? n.name : typeof n.label === "string" ? n.label : "";
var findNodeUnits = (app2) => {
  const units = [];
  const walk8 = (n, path) => {
    var _a2;
    for (const c of (_a2 = n._children) != null ? _a2 : []) {
      const p = [...path, nameOf3(c)];
      if (c.type === "screen" || c.type === "flow") {
        units.push({ kind: "node", path: p, tag: c.type });
      } else walk8(c, p);
    }
  };
  if (app2) walk8(app2, []);
  return units;
};
var typeNames = (tree2) => new Set(sectionNodes(tree2.objectTypes).filter(isNode6).map(nameOf3));
var danglingDataTypes = (tree2) => {
  const types2 = typeNames(tree2);
  const bad = [];
  const walk8 = (n) => {
    var _a2;
    const data = n.data;
    if (typeof data === "object" && data !== null && "dataType" in data) {
      const dt = data.dataType;
      if (typeof dt === "string" && !types2.has(dt)) bad.push(dt);
    }
    ((_a2 = n._children) != null ? _a2 : []).forEach(walk8);
  };
  if (tree2.appConfig) walk8(tree2.appConfig);
  return [...new Set(bad)];
};
var unfence = (text) => {
  const m = /```[a-z]*\n([\s\S]*?)\n```/.exec(text);
  return (m ? m[1] : text).trim();
};
var checkStructure = (text) => {
  const parsed = parseOutline(text, { lenient: true });
  if (isLeft(parsed)) return { problems: [formatParseError(parsed.left)] };
  const tree2 = parsed.right;
  const problems = [];
  if (findNodeUnits(tree2.appConfig).length === 0) problems.push("no screens in <appConfig>");
  for (const dt of danglingDataTypes(tree2)) {
    problems.push(`of="${dt}" names no type in <objectTypes>`);
  }
  return { tree: tree2, problems };
};
var runStructureStage = async (userPrompt, call, refs = [], revise) => {
  const structureMsgs = [{ role: "user", content: structurePrompt(userPrompt, refs) }];
  if (revise) {
    structureMsgs.push(
      { role: "assistant", content: revise.previous },
      { role: "user", content: structureEditPrompt(revise.edit) }
    );
  }
  let structureText = reindentOutline(
    unfence(await call(revise ? "structure-edit" : "structure", structureMsgs))
  );
  let checked = checkStructure(structureText);
  let fixTurn = false;
  if (checked.problems.length > 0) {
    fixTurn = true;
    structureText = reindentOutline(
      unfence(
        await call("structure-fix", [
          ...structureMsgs,
          { role: "assistant", content: structureText },
          { role: "user", content: structureFixPrompt(checked.problems) }
        ])
      )
    );
    checked = checkStructure(structureText);
  }
  if (!checked.tree || checked.problems.length > 0) {
    return { ok: false, error: `structure unusable: ${checked.problems.join("; ")}`, fixTurn };
  }
  const skeleton = checked.tree;
  return {
    ok: true,
    skeleton,
    skeletonText: orgOutline(skeleton, { level: 1 }),
    fixTurn,
    screens: findNodeUnits(skeleton.appConfig).length
  };
};

// src/prompts/graph/generateOrgGraph.ts
var MAX_TURNS = 25;
var MAX_IDLE = 3;
var MAX_REPAIR_ROUNDS = 2;
var MAX_DONE_REFUSALS = 2;
var DEFAULT_API_VERSION = "0.1";
var MAX_STRUCTURE_EDITS = 1;
var GRAPH_PROMPT_NAME = "orgSystemPromptV2+graph";
var generateOrgGraph = async (prompt, modelId, deps, options) => {
  const base = await deps.buildSystemPrompt();
  const systemPrompt = `${base}

${structureSection(options.outlineGrammar)}

${graphSection()}`;
  const session = createGraphSession(deps, modelId, systemPrompt);
  const { call, emit, usage } = session;
  const walker = createStreamingOrgWalker(emit);
  const fail3 = (message) => ({ error: true, message, usage });
  let structured = await runStructureStage(prompt, call, options.referenceOutlines);
  if (!structured.ok) {
    return fail3(
      `The structure stage could not produce a usable outline: ${structured.error}`
    );
  }
  let built = buildSkeleton(structured.skeleton);
  if ("error" in built) return fail3(built.error);
  let edits = 0;
  for (; ; ) {
    const ready = {
      outline: built.skeletonText,
      screens: screenNames(built.skeleton),
      canEdit: !!deps.onStructure && edits < MAX_STRUCTURE_EDITS
    };
    emit({ kind: "structure-ready", ...ready });
    if (!deps.onStructure) break;
    const decision = await deps.onStructure(ready);
    const edit = decision === "go" ? "" : decision.edit.trim();
    if (!ready.canEdit || !edit) break;
    edits += 1;
    structured = await runStructureStage(prompt, call, options.referenceOutlines, {
      previous: built.skeletonText,
      edit
    });
    if (!structured.ok) {
      return fail3(`The edited structure could not be used: ${structured.error}`);
    }
    built = buildSkeleton(structured.skeleton);
    if ("error" in built) return fail3(built.error);
  }
  const { graph, skeletonText } = built;
  walker.walkParsed(graph);
  const maxRepairRounds = deps.disableClosingGate ? 0 : MAX_REPAIR_ROUNDS;
  const report = await runGraphFill(graph, {
    call,
    userPrompt: prompt,
    skeletonText,
    maxTurns: MAX_TURNS,
    maxIdle: MAX_IDLE,
    maxRepairRounds,
    maxDoneRefusals: MAX_DONE_REFUSALS,
    onGraph: (g) => walker.walkParsed(g)
  });
  const result = finishGraphResult({
    session,
    report,
    prompt,
    modelId,
    promptName: GRAPH_PROMPT_NAME,
    maxRepairRounds,
    walker,
    // The gate passes a hollow org; this is what says the ops never landed.
    extraMeta: {
      graphFill: {
        ...report.fill,
        repairRounds: report.gate.repairRounds,
        structureFixTurn: structured.fixTurn,
        structureEdits: edits
      }
    }
  });
  if (isGenerateError(result)) return result;
  emit({ kind: "done" });
  return result;
};
var buildSkeleton = (skeleton) => {
  if (!skeleton.apiVersion) skeleton.apiVersion = DEFAULT_API_VERSION;
  const skeletonText = orgOutline(skeleton, { level: 1 });
  const compiled = compileOutline(skeletonText);
  let graph;
  if (isLeft(compiled)) {
    if (compiled.left.kind === "parse") {
      return {
        error: `The structure does not re-parse: ${formatParseError(compiled.left.error)}`
      };
    }
    graph = withIds(skeleton);
  } else {
    graph = compiled.right.tree;
  }
  if (!graph.users) graph.users = { id: "users", type: "users", _children: [] };
  return { skeleton, skeletonText, graph };
};
var screenNames = (skeleton) => findNodeUnits(skeleton.appConfig).filter((u) => u.tag === "screen").map((u) => u.path.join("/"));

// src/prompts/showcase/showcaseIndex.generated.ts
var SHOWCASE_INDEX = [
  {
    "orgId": "dave/FitPlan",
    "label": "FitPlan",
    "description": "A fitness-plan generator inspired by the experience of asking ChatGPT for a personal training plan ahead of a real-world goal (a holiday, an event, just feeling stronger).",
    "features": [
      "flows",
      "tabs",
      "forms",
      "ai-query",
      "image-generation",
      "storage-data",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 3,
    "types": 1,
    "size": "medium"
  },
  {
    "orgId": "dave/RecipeNotebook",
    "label": "RecipeNotebook",
    "description": "A simple personal cookbook app for collecting and organising recipes. Each recipe carries a hero image, prep/cook times, servings, difficulty, ingredients, instructions, and personal notes.",
    "features": [
      "flows",
      "forms",
      "ai-query",
      "image-generation",
      "storage-data"
    ],
    "screens": 7,
    "types": 1,
    "size": "complex"
  },
  {
    "orgId": "dave/StoryLingo",
    "label": "StoryLingo",
    "description": "A bilingual story generator that uses AI to create short stories in two languages side by side.",
    "features": [
      "flows",
      "tabs",
      "forms",
      "ai-query",
      "image-generation",
      "storage-data",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 3,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "dave/bandwagon",
    "label": "Bandwagon",
    "description": "Exquisite-corpse songwriting as a live party game.",
    "features": [
      "flows",
      "forms",
      "realtime",
      "transitions",
      "ai-query",
      "storage-data",
      "embedded-data",
      "user-scoped",
      "audio"
    ],
    "screens": 8,
    "types": 8,
    "size": "complex"
  },
  {
    "orgId": "dave/bluffy",
    "label": "bluffy",
    "description": "A real-time, multi-device No-Limit Texas Hold'em sit-and-go for 2\u20134 seats, where the host fills the empty seats with character bots whose decisions come from TypeSafe Jev (a System One classifier)\u2026",
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "transitions",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 8,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "dave/cassino",
    "label": "cassino",
    "description": "A real-time, multi-device implementation of the folk fishing game Cassino \u2014 specifically the Royal Cassino branch, in which face cards carry numeric values and the 2\u2660 / 10\u2666 are Little and Big Cassino.",
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "transitions",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 8,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "dave/christmas-carol",
    "label": "christmas-carol",
    "description": "Last-minute GCSE English Literature revision companion for A Christmas Carol by Charles Dickens. Built for dave's daughter the night before her exam.",
    "features": [
      "embedded-data"
    ],
    "screens": 9,
    "types": 6,
    "size": "medium"
  },
  {
    "orgId": "dave/cinque-terre",
    "label": "Cinque Terre Trip Planner",
    "description": "Trip-planning companion for Dave's 4-night Cinque Terre getaway, Mon 14 \u2013 Fri 18 Sep 2026, flying from South East England.",
    "features": [
      "plugins",
      "embedded-data",
      "maps"
    ],
    "screens": 12,
    "types": 10,
    "size": "medium"
  },
  {
    "orgId": "dave/cockpit",
    "label": "Cockpit",
    "description": "An Orgiggly-native supervision cockpit for Claude Code worker sessions \u2014 the phone-based replacement for Dave manually acting as the event loop across up to three concurrent Claude Code sessions (one\u2026",
    "features": [
      "record-tree",
      "forms",
      "push",
      "storage-data",
      "auth-private"
    ],
    "screens": 5,
    "types": 5,
    "size": "simple"
  },
  {
    "orgId": "dave/dailyspot",
    "label": "dailyspot",
    "description": "A Wordle-like daily poker decision puzzle. One shared Texas Hold'em spot per calendar day, frozen at a decision point, with the same three options for everybody.",
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "storage-data"
    ],
    "screens": 6,
    "types": 3,
    "size": "complex"
  },
  {
    "orgId": "dave/david-webber",
    "label": "david-webber",
    "description": "Personal portfolio site for David Webber, a senior software engineer.",
    "features": [
      "plugins",
      "embedded-data"
    ],
    "screens": 5,
    "types": 3,
    "size": "simple"
  },
  {
    "orgId": "dave/docs",
    "label": "docs",
    "description": "A dogfooded Orgiggly org that documents Orgiggly itself.",
    "features": [
      "plugins",
      "storage-data",
      "embedded-data"
    ],
    "screens": 13,
    "types": 7,
    "size": "complex"
  },
  {
    "orgId": "dave/gcse-biology",
    "label": "gcse-biology",
    "description": "GCSE Biology revision companion for dave's daughter, sourced from the CGP GCSE Biology Revision Guide (online edition, code B2R45DF).",
    "features": [
      "tabs",
      "embedded-data"
    ],
    "screens": 2,
    "types": 3,
    "size": "simple"
  },
  {
    "orgId": "dave/gcse-chemistry",
    "label": "gcse-chemistry",
    "description": "GCSE Chemistry revision companion for dave's daughter, sourced from the CGP GCSE Chemistry Revision Guide (online edition, code C2R45DF).",
    "features": [
      "tabs",
      "embedded-data"
    ],
    "screens": 2,
    "types": 3,
    "size": "simple"
  },
  {
    "orgId": "dave/gcse-physics",
    "label": "gcse-physics",
    "description": "GCSE Physics revision companion, sourced from the CGP GCSE Physics Revision Guide (online edition, code P2R45DF).",
    "features": [
      "tabs",
      "embedded-data"
    ],
    "screens": 2,
    "types": 3,
    "size": "simple"
  },
  {
    "orgId": "dave/guides",
    "label": "guides",
    "description": "The browsable shelf at orgiggly.com/guides.",
    "features": [
      "storage-data",
      "embedded-data"
    ],
    "screens": 1,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "dave/handsofvictory",
    "label": "handsofvictory",
    "description": "Character poker: no-limit Hold'em sit-and-go for 2\u20136 seats, humans and Jev bots mixed, where every seat is one of eight characters with a signature power, a passive, and the universal Swap, paid for\u2026",
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "transitions",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 7,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "dave/hsk",
    "label": "hsk",
    "description": "A signed-in HSK 3.0 Chinese-vocabulary flashcard app with Anki-style spaced-repetition (SM-2-lite), TTS audio, component/radical cross-linking, and animated stroke order.",
    "features": [
      "plugins",
      "widgets",
      "storage-data",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 9,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "dave/intro-quiz",
    "label": "Intro Quiz",
    "description": `A quiz-master's tool for "name that intro" rounds. The AI suggests songs with distinctive openings for the round you describe, or they come from your Spotify playlist.`,
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "ai-query",
      "storage-data",
      "user-scoped",
      "audio"
    ],
    "screens": 8,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "dave/jumbly",
    "label": "jumbly",
    "description": 'A real-time, multi-device word game in the Boggle/"word search under a clock" family: a 4\xD74 grid of letters is revealed to everyone at once, and for three minutes every player races to trace as many\u2026',
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "transitions",
      "storage-data",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 7,
    "types": 10,
    "size": "complex"
  },
  {
    "orgId": "dave/lingotutor",
    "label": "Lingo Tutor",
    "description": "The showcase for the aiChat flow step (epic #1660, sub-task #1673): a spoken language tutor.",
    "features": [
      "flows",
      "forms",
      "ai-chat",
      "storage-data",
      "user-scoped"
    ],
    "screens": 5,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "dave/norn-iron",
    "label": "norn-iron",
    "description": "Trip-planning companion for Dave and his wife's five-night campervan tour of Northern Ireland, Sat 19 \u2013 Thu 24 Sep 2026, in a compact 2-berth van, starting from home at BN26 6TD (Alfriston, East\u2026",
    "features": [
      "plugins",
      "embedded-data",
      "maps"
    ],
    "screens": 12,
    "types": 10,
    "size": "medium"
  },
  {
    "orgId": "dave/oli1970",
    "label": "oli1970",
    "description": "A one-off birthday gift mini-app for Dave's brother-in-law Oli, born Tuesday 11 August 1970.",
    "features": [
      "plugins",
      "embedded-data",
      "video",
      "audio"
    ],
    "screens": 6,
    "types": 5,
    "size": "medium"
  },
  {
    "orgId": "dave/pairing-desk",
    "label": "pairing-desk",
    "description": 'A working, two-sided founder\u2194investor matching prototype built on Orgiggly, shipped as a live link inside a job application for the Paires Founding AI Engineer role \u2014 an "open homage" that\u2026',
    "features": [
      "flows",
      "forms",
      "ai-query",
      "storage-data",
      "embedded-data",
      "user-scoped"
    ],
    "screens": 10,
    "types": 3,
    "size": "medium"
  },
  {
    "orgId": "dave/pitch",
    "label": "pitch",
    "description": "A single shareable URL \u2014 orgiggly.com/pitch \u2014 Dave can DM to dev friends instead of explaining Orgiggly inline.",
    "features": [
      "plugins",
      "embedded-data"
    ],
    "screens": 1,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "dave/rallyround",
    "label": "Rallyround",
    "description": "Volunteer rotas for village fayres and community events. Whipround collects money; Rallyround collects people. First real user: the Berwick Produce Association (BPA) summer fayre. Epic #2257.",
    "features": [
      "flows",
      "forms",
      "realtime",
      "transitions",
      "user-scoped"
    ],
    "screens": 11,
    "types": 7,
    "size": "complex"
  },
  {
    "orgId": "dave/sf-admin-exam",
    "label": "sf-admin-exam",
    "description": "Revision companion for the Salesforce Certified Platform Administrator exam (ADM-201 / Plat-Admn-201) for Dave's day job.",
    "features": [
      "tabs",
      "embedded-data"
    ],
    "screens": 2,
    "types": 5,
    "size": "simple"
  },
  {
    "orgId": "dave/shuffly",
    "label": "shuffly",
    "description": "A real-time, multi-device implementation of the folk card game known as Contract Rummy / Shanghai Rummy / Kalooki.",
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "transitions",
      "embedded-data",
      "user-scoped",
      "video"
    ],
    "screens": 7,
    "types": 9,
    "size": "complex"
  },
  {
    "orgId": "dave/squiggly",
    "label": "squiggly",
    "description": 'A real-time, multi-device party game in the Telestrations/"paper-telephone" family: each player secretly writes a phrase, the next player draws it, the next guesses the drawing, and so on around the\u2026',
    "features": [
      "flows",
      "forms",
      "plugins",
      "widgets",
      "realtime",
      "transitions",
      "storage-data",
      "embedded-data",
      "org-login",
      "video"
    ],
    "screens": 9,
    "types": 7,
    "size": "complex"
  },
  {
    "orgId": "dave/uk-ai-grants",
    "label": "uk-ai-grants",
    "description": 'A browsable snapshot of the UK government grant and support landscape for AI startups, written for Dave to digest at leisure after asking "what UK grants are open to a solo AI founder, and could any\u2026',
    "features": [
      "embedded-data"
    ],
    "screens": 6,
    "types": 0,
    "size": "simple"
  },
  {
    "orgId": "dave/whatisit",
    "label": "What Is It?",
    "description": "An app with one question in it. Point a camera at anything at all, get told what it is, and keep what you find.",
    "features": [
      "flows",
      "forms",
      "ai-chat",
      "ai-query",
      "storage-data"
    ],
    "screens": 8,
    "types": 1,
    "size": "medium"
  },
  {
    "orgId": "dave/whipround",
    "label": "Whipround",
    "description": "A shared-expense ledger for groups who split costs \u2014 a travelling group settling up at the end of a trip, or two family members with an ongoing household split.",
    "features": [
      "flows",
      "forms",
      "storage-data",
      "embedded-data",
      "group-scoped",
      "auth-private",
      "org-login"
    ],
    "screens": 27,
    "types": 5,
    "size": "complex"
  },
  {
    "orgId": "dave/worldcup2026",
    "label": "worldcup2026",
    "description": "A friends prediction league for the 2026 FIFA World Cup group stage (June 11\u201327, 2026; 48 teams, 12 groups, 72 matches).",
    "features": [
      "flows",
      "forms",
      "push",
      "storage-data",
      "embedded-data",
      "org-login"
    ],
    "screens": 14,
    "types": 7,
    "size": "complex"
  },
  {
    "orgId": "dave/worldcup2026-v2",
    "label": "worldcup2026-v2",
    "description": "A friends prediction league for the 2026 FIFA World Cup group stage (June 11\u201327, 2026; 48 teams, 12 groups, 72 matches).",
    "features": [
      "flows",
      "forms",
      "push",
      "storage-data",
      "embedded-data",
      "org-login"
    ],
    "screens": 13,
    "types": 7,
    "size": "complex"
  },
  {
    "orgId": "elias_webber/MA145",
    "label": "MA145",
    "description": "Study companion for MA145 Mathematical Methods and Modelling 2 (Term 2 2026, Warwick)",
    "features": [
      "tabs",
      "storage-data",
      "embedded-data"
    ],
    "screens": 2,
    "types": 5,
    "size": "simple"
  },
  {
    "orgId": "elias_webber/MacroEcon",
    "label": "MacroEcon",
    "description": "Study companion for EC106 Macroeconomics (Lent Term 2026)",
    "features": [
      "tabs",
      "embedded-data"
    ],
    "screens": 2,
    "types": 5,
    "size": "simple"
  },
  {
    "orgId": "elias_webber/devspeak",
    "label": "devspeak",
    "description": "Teach Elias (and any newcomer without a CS background) the practical, everyday vocabulary of software engineering \u2014 the words Claude Code and working engineers use without explanation.",
    "features": [
      "tabs",
      "embedded-data"
    ],
    "screens": 2,
    "types": 5,
    "size": "simple"
  },
  {
    "orgId": "mock/Oom",
    "label": "Oom",
    "description": "The complete text of Oom, a children's sci-fi novel by David Higgins, as a readable, illustrated web book.",
    "features": [
      "plugins",
      "embedded-data"
    ],
    "screens": 2,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "mock/billSplitter",
    "label": "billSplitter",
    "description": "A single-screen utility that splits bills proportionally among friends.",
    "features": [
      "embedded-data"
    ],
    "screens": 1,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "mock/blog",
    "label": "blog",
    "description": "The Orgiggly product blog. A simple article-list + article-detail site mounted at orgiggly.com/blog, intended for posts about platform shipping, product strategy, and YC narrative-supporting material.",
    "features": [
      "plugins",
      "storage-data",
      "embedded-data"
    ],
    "screens": 2,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "mock/cultheroes",
    "label": "cultheroes",
    "description": "A Stupid & Contagious extras spin-off \u2014 thirteen acts the hosts have a soft spot for that don't usually get the airtime they deserve.",
    "features": [
      "embedded-data"
    ],
    "screens": 1,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "mock/feta-compli",
    "label": "feta-compli",
    "description": "Family-holiday companion for a 9-person trip to Corfu (Saint George Palace, Agios Georgios Pagon \xB7 27 Jun \u2013 4 Jul 2026).",
    "features": [
      "flows",
      "forms",
      "plugins",
      "push",
      "storage-data",
      "embedded-data",
      "video",
      "maps"
    ],
    "screens": 23,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "mock/jingleTest",
    "label": "Jingle Test",
    "description": "Tiny demo org for the audioGeneration flow step.",
    "features": [
      "flows",
      "forms",
      "storage-data",
      "user-scoped",
      "audio"
    ],
    "screens": 4,
    "types": 1,
    "size": "simple"
  },
  {
    "orgId": "mock/mercury90s",
    "label": "mercury90s",
    "description": "An archive of the Mercury Music Prize across the 1990s, built as an extra for a Stupid & Contagious podcast episode about the prize's 90s nominees and winners.",
    "features": [
      "plugins",
      "storage-data",
      "embedded-data",
      "audio"
    ],
    "screens": 6,
    "types": 6,
    "size": "medium"
  },
  {
    "orgId": "mock/orgiggly",
    "label": "Orgiggly \u2014 where Claude-built apps live",
    "description": "The orgiggly product information site. The home screen is plugin-first for visitors (hero with a live example app, the Claude Code plugin install commands, the in-app issue-report loop, the runtime)\u2026",
    "features": [
      "plugins",
      "embedded-data"
    ],
    "screens": 10,
    "types": 10,
    "size": "complex"
  },
  {
    "orgId": "mock/pluginsOrg",
    "label": "pluginsOrg",
    "description": "Plugin development and showcase org. Contains reusable plugins published to the Orgiggly marketplace, with demo screens showing example usage of each plugin.",
    "features": [
      "plugins",
      "widgets",
      "video",
      "audio",
      "maps"
    ],
    "screens": 6,
    "types": 0,
    "size": "medium"
  },
  {
    "orgId": "mock/scorpioRising",
    "label": "scorpioRising",
    "description": `A Stupid & Contagious podcast spin-off about Scorpio Rising \u2014 the English rock band from Birkenhead (1989\u20131994) hailed by NME in '91 as the next thing on the back of "Watermelon", who toured with\u2026`,
    "features": [
      "plugins",
      "embedded-data",
      "video",
      "audio"
    ],
    "screens": 8,
    "types": 4,
    "size": "medium"
  },
  {
    "orgId": "mock/stupidandcontagiouspodcast",
    "label": "stupidandcontagiouspodcast",
    "description": 'Companion app for a music podcast. Features a home page that highlights the latest episode plus an episode browser, per-episode embedded video/audio players, an optional per-episode "Extras" tab for\u2026',
    "features": [
      "tabs",
      "forms",
      "plugins",
      "widgets",
      "storage-data",
      "embedded-data",
      "video"
    ],
    "screens": 8,
    "types": 6,
    "size": "complex"
  },
  {
    "orgId": "mock/user",
    "label": "user",
    "description": "",
    "features": [
      "forms",
      "plugins",
      "embedded-data"
    ],
    "screens": 2,
    "types": 7,
    "size": "medium"
  },
  {
    "orgId": "mock/wang",
    "label": "wang",
    "description": "A single variant-parameterized org that replaces the per-year mock/wang199{4,5,6} siblings.",
    "features": [
      "plugins",
      "storage-data",
      "embedded-data",
      "video",
      "audio"
    ],
    "screens": 6,
    "types": 7,
    "size": "medium"
  },
  {
    "orgId": "mock/wang1994",
    "label": "wang1994",
    "description": `Nostalgia companion for the Stupid & Contagious podcast's 1994 "Wang" episode \u2014 the year Britpop arrived in two punches (Parklife in April, Definitely Maybe in August), Wet Wet Wet refused to leave #1\u2026`,
    "features": [
      "plugins",
      "embedded-data",
      "video",
      "audio"
    ],
    "screens": 6,
    "types": 6,
    "size": "medium"
  },
  {
    "orgId": "mock/wang1995",
    "label": "wang1995",
    "description": `Nostalgia companion for the Stupid & Contagious podcast's 1995 "Wang" episode \u2014 the year of the Britpop civil war, Cantona's flying foot, Windows 95 launching to "Start Me Up", GoldenEye, Diana's\u2026`,
    "features": [
      "plugins",
      "embedded-data",
      "video",
      "audio"
    ],
    "screens": 6,
    "types": 6,
    "size": "medium"
  },
  {
    "orgId": "mock/wang1996",
    "label": "wang1996",
    "description": `Nostalgia companion for the Stupid & Contagious podcast's 1996 "Wang" episode \u2014 a trip back through the music, films, TV, news and daft ephemera of the year.`,
    "features": [
      "plugins",
      "embedded-data",
      "video",
      "audio"
    ],
    "screens": 6,
    "types": 6,
    "size": "medium"
  },
  {
    "orgId": "mock/webberoonies",
    "label": "webberoonies",
    "description": "A family organisation app with recipes, events calendar, todo lists, and a journal.",
    "features": [
      "flows",
      "tabs",
      "forms",
      "plugins",
      "push",
      "storage-data",
      "embedded-data"
    ],
    "screens": 30,
    "types": 5,
    "size": "complex"
  }
];

// src/prompts/showcase/menu.ts
var showcaseMenuFromIndex = (index = SHOWCASE_INDEX) => index.map((e) => ({
  id: e.orgId,
  orgId: e.orgId,
  name: e.label,
  description: e.description,
  features: [...e.features],
  complexity: e.size
}));
var formatShowcaseMatches = (entries) => {
  if (entries.length === 0) return "";
  const lines = entries.map((e) => {
    const features = e.features.length ? `  [${e.features.join(", ")}]` : "";
    const description = e.description ? `: ${e.description}` : "";
    return `- \`${e.orgId}\` \u2014 ${e.label}${description}${features}`;
  });
  return `## Showcase matches for this request
${lines.join("\n")}

Request 1\u20133 of these (or any catalogue id) with \`request_examples\` if they would help.`;
};

// src/prompts/trimOrgConfig.ts
var MAX_INSTANCES_PER_TYPE = 2;
var trimOrgConfigForExample = (config) => {
  const trimmed = {};
  const keepKeys = [
    "apiVersion",
    "appConfig",
    "objectTypes",
    "plugins",
    "users",
    "orgSummary"
  ];
  for (const key of keepKeys) {
    if (key in config) {
      trimmed[key] = config[key];
    }
  }
  if (config.objectInstances && typeof config.objectInstances === "object" && !Array.isArray(config.objectInstances)) {
    const root = config.objectInstances;
    const typeFolders = root._children;
    if (Array.isArray(typeFolders)) {
      trimmed.objectInstances = {
        ...root,
        _children: typeFolders.map(
          (folder2) => isRecord10(folder2) ? trimFolder(folder2) : folder2
        )
      };
    } else {
      trimmed.objectInstances = config.objectInstances;
    }
  }
  return trimmed;
};
var isRecord10 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var isFolder = (v) => isRecord10(v) && v.type === "folder";
var trimFolder = (folder2) => {
  const kids5 = folder2._children;
  if (!Array.isArray(kids5)) return folder2;
  let kept = 0;
  return {
    ...folder2,
    _children: kids5.flatMap(
      (k) => isFolder(k) ? [trimFolder(k)] : kept++ < MAX_INSTANCES_PER_TYPE ? [k] : []
    )
  };
};
var formatExampleOrg = (tree2, format) => format === "outline" ? `\`\`\`jsx
${orgOutline(tree2, { level: 2 })}
\`\`\`` : `\`\`\`json
${JSON.stringify(trimOrgConfigForExample(tree2), null, 2)}
\`\`\``;
var formatRequestedExample = (tree2) => formatExampleOrg(tree2, "outline");
var formatInspirationOrg = (tree2) => formatExampleOrg(tree2, "json");

// src/prompts/requirementsMessage.ts
var templateExample = (spec) => {
  const template = getTemplate(spec.verticalId);
  if (template === void 0) return "";
  const cloned = cloneTemplate(template, template.paramsFromSpec(spec));
  if (isLeft3(cloned)) return "";
  return `### Starting point
Our ${template.manifest.verticalId} template, personalised for this request. Reuse its patterns where they fit the requirements; it is an outline, not a spec.

${formatRequestedExample(rawOrgDataToTree(cloned.right))}`;
};
var requirementsMessageBlocks = (spec) => [formatRequirementsBlock(spec), templateExample(spec)].filter(
  (block) => block.length > 0
);

// src/prompts/generateOrg.ts
var REQUEST_EXAMPLES_TOOL = {
  name: "request_examples",
  description: "Request full org configurations from the showcase to use as reference.",
  input_schema: {
    type: "object",
    properties: {
      orgIds: {
        type: "array",
        items: { type: "string" },
        description: "Showcase org IDs to fetch (1-3 most relevant)"
      }
    },
    required: ["orgIds"]
  }
};
var firstMessage = (prompt, deps) => {
  var _a2, _b;
  if (deps.demoSource === "demo") return void 0;
  const offered = !!(((_a2 = deps.showcaseMenu) == null ? void 0 : _a2.length) && deps.fetchOrgConfigs);
  const blocks = [
    ...deps.requirements ? requirementsMessageBlocks(deps.requirements) : [],
    offered ? formatShowcaseMatches((_b = deps.showcaseMatches) != null ? _b : []) : ""
  ].filter((block) => block.length > 0);
  if (blocks.length === 0) return void 0;
  return [{ role: "user", content: [prompt, ...blocks].join("\n\n") }];
};
var generateOrg = async (prompt, modelId, deps) => {
  var _a2;
  const effectiveModelId = deps.demoMode ? MODEL_ID_HAIKU : modelId;
  if (deps.graphEdit) {
    if (deps.requirements) {
      throw new Error("generateOrg: `requirements` is not supported with `graphEdit`.");
    }
    return generateOrgGraph(prompt, effectiveModelId, deps, deps.graphEdit);
  }
  const provenance = deps.requirements ? createProvenance(deps.requirements) : void 0;
  const exampleOrgsRequested = [];
  const assumptions = [];
  const streamingWalker = createStreamingOrgWalker(
    (event) => {
      var _a3;
      return (_a3 = deps.onEvent) == null ? void 0 : _a3.call(deps, event);
    }
  );
  const spec = {
    maxTokens: 32768,
    maxTotalTurns: 7,
    reserveFinalTurn: true,
    maxUserQuestions: 2,
    askUserDescription: "Ask the user a clarifying question before generating the org JSON. Use this when the request is ambiguous or missing key details.",
    disableAskUser: !!deps.disableAskUser,
    // Same list every turn (cache prefix — see `getExtraTools`); a second
    // request_examples is refused in its handler.
    getExtraTools: () => {
      var _a3;
      const tools = [GET_NODE_SCHEMA_TOOL];
      if (((_a3 = deps.showcaseMenu) == null ? void 0 : _a3.length) && deps.fetchOrgConfigs) {
        tools.push(REQUEST_EXAMPLES_TOOL);
      }
      if (deps.demoMode) {
        tools.push(NOTE_ASSUMPTION_TOOL);
      }
      return tools;
    },
    handleExtraToolUse: async (block, state) => {
      var _a3, _b, _c;
      if (block.name === "note_assumption") {
        const input = block.input;
        if (typeof input.assumption === "string" && typeof input.rationale === "string") {
          assumptions.push({
            assumption: input.assumption,
            rationale: input.rationale
          });
          (_a3 = deps.onStatus) == null ? void 0 : _a3.call(deps, `recorded assumption: ${input.assumption}`);
        }
        return {
          action: "continue",
          toolResults: [
            {
              type: "tool_result",
              tool_use_id: block.id,
              content: "Assumption recorded."
            }
          ]
        };
      }
      if (block.name === "request_examples" && state.examplesRequested) {
        return {
          action: "continue",
          toolResults: [
            {
              type: "tool_result",
              tool_use_id: block.id,
              content: spentToolMessage("request_examples")
            }
          ]
        };
      }
      if (block.name === "request_examples" && deps.fetchOrgConfigs) {
        state.examplesRequested = true;
        const orgIds = ((_b = block.input.orgIds) != null ? _b : []).slice(0, 3);
        const resolvedOrgIds = deps.showcaseMenu ? orgIds.map((id) => {
          var _a4;
          const entry = deps.showcaseMenu.find((m) => m.id === id);
          return (_a4 = entry == null ? void 0 : entry.orgId) != null ? _a4 : id;
        }) : orgIds;
        exampleOrgsRequested.push(...resolvedOrgIds);
        (_c = deps.onStatus) == null ? void 0 : _c.call(
          deps,
          `fetching ${resolvedOrgIds.length} example(s)...`
        );
        const configs = await deps.fetchOrgConfigs(resolvedOrgIds);
        const content2 = configs.map((c) => `## ${c.orgId}
${c.config}`).join("\n\n");
        return {
          action: "continue",
          toolResults: [
            {
              type: "tool_result",
              tool_use_id: block.id,
              content: content2 || "No configurations found for the requested IDs."
            }
          ]
        };
      }
      return {
        action: "continue",
        toolResults: [
          {
            type: "tool_result",
            tool_use_id: block.id,
            content: ""
          }
        ]
      };
    },
    processStreamingText: (accumulatedText) => {
      streamingWalker.process(accumulatedText);
    },
    processResult: (text, stopReason, state) => {
      return processOrgResult(text, stopReason, {
        prompt: state.prompt,
        qaPairs: state.qaPairs,
        totalUsage: state.totalUsage,
        exampleOrgsRequested,
        imageSearchResults: state.imageSearchResults,
        webSearchResults: state.webSearchResults,
        videoSearchResults: state.videoSearchResults,
        turnLogs: state.turnLogs,
        assumptions,
        streamingWalker,
        ...provenance ? {
          extraMeta: {
            create: { ...provenance, spec: deps.requirements }
          }
        } : {}
      });
    },
    // Skip the closing gate when the caller signals no real Anthropic model
    // is on the other side (mock providers, recorded playback). Fix-turn
    // callbacks would overrun fixed-length recordings — `routeRecordedTurn`
    // throws "out of bounds" — and aborts the whole generation. Real model
    // paths leave `disableClosingGate` unset so the gate fires.
    validateFinal: deps.disableClosingGate ? void 0 : (text) => {
      const jsonStr = extractJsonResponseSimple(text);
      if (!jsonStr) return { ok: true };
      let parsed;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        return { ok: true };
      }
      const result2 = validateOrg(parsed);
      if (!result2.errors.some(isGenerationBlocking)) return { ok: true };
      return { ok: false, errors: result2.errors, parsedJson: parsed };
    },
    // #641: when the closing gate is active, ask for a node-diff on the second
    // fix turn instead of a full rewrite. Turn 1 still asks for a full rewrite
    // so the model can do major restructuring when that's right. Only fires
    // when `maxFixTurns >= 2`.
    useDiffOnSecondFixTurn: !deps.disableClosingGate,
    // #643 shipped this as 0 ("record, don't act") while the validator's
    // false-positive profile was calibrated. Calibration (a validateOrg sweep
    // over all 38 committed known-good org configs + the telemetry report,
    // feat/enable-closing-gate-repair-loop) fixed the two observed FP classes
    // (anchor-to-named-node hrefs, legacy authMode values), so repair is now
    // ON: up to 2 fix turns — a full rewrite, then a cheap node-diff turn.
    // Fix turns only run when the gate fails, and only spend within the same
    // rate/credit guards as the original turns.
    maxFixTurns: 2,
    promptName: "orgSystemPromptV2"
  };
  const baseDeps = {
    callTurn: deps.callTurn,
    buildSystemPrompt: deps.buildSystemPrompt,
    onQuestion: deps.onQuestion,
    onStatus: deps.onStatus,
    onEvent: deps.onEvent,
    mockScenario: deps.mockScenario,
    initialMessages: (_a2 = deps.initialMessages) != null ? _a2 : firstMessage(prompt, deps),
    initialQaPairs: deps.initialQaPairs,
    searchImages: deps.searchImages,
    searchWeb: deps.searchWeb,
    searchVideos: deps.searchVideos,
    demoSource: deps.demoSource
  };
  const result = await generate(prompt, effectiveModelId, spec, baseDeps);
  if (!provenance || isGenerateOrgError(result) || !result._validationTelemetry) {
    return result;
  }
  const telemetry = {
    ...result._validationTelemetry,
    create: provenance
  };
  return { ...result, _validationTelemetry: telemetry };
};

// src/prompts/graph/editOrgGraph.ts
var MAX_TURNS2 = 15;
var MAX_IDLE2 = 3;
var MAX_REPAIR_ROUNDS2 = 2;
var EDIT_PROMPT_NAME = "orgSystemPromptV2+graph-edit";
var DATA_REFUSED = "data is refused when editing an existing org \u2014 it replaces every seed row. Change one object type with set objectTypes.<Type>.";
var editOrgGraph = async (request, existing, modelId, deps) => {
  var _a2;
  const noUsage = { model: modelId, inputTokens: 0, outputTokens: 0, creditsCharged: 0 };
  if (deps.mockScenario) {
    return { error: true, message: "Graph-edit has no mock provider \u2014 unset mockScenario.", usage: noUsage };
  }
  const duplicates = validateOrg(existing).errors.filter((e) => e.kind === "duplicate-sibling-name");
  if (duplicates.length > 0) {
    return {
      error: true,
      message: "This org has sibling nodes sharing a name, so an edit could address the wrong one. Rename them first:\n" + duplicates.map((e) => `  ${e.path}: ${e.message}`).join("\n"),
      usage: noUsage
    };
  }
  const base = await deps.buildSystemPrompt();
  const features = (_a2 = deps.features) != null ? _a2 : [];
  const hasInstalled = collectFeatureFolders(existing).length > 0;
  const session = createGraphSession(deps, modelId, `${base}

${editSection(features, hasInstalled)}`);
  const walker = createStreamingOrgWalker(session.emit);
  const outline = orgOutline(existing, { level: 1 });
  const maxRepairRounds = deps.disableClosingGate ? 0 : MAX_REPAIR_ROUNDS2;
  const report = await runGraphFill(existing, {
    call: session.call,
    userPrompt: request,
    skeletonText: outline,
    kickoff: editKickoffPrompt(request, outline),
    worklist: false,
    forbid: { data: DATA_REFUSED },
    features,
    baselineErrors: gateErrors(existing),
    maxTurns: MAX_TURNS2,
    maxIdle: MAX_IDLE2,
    maxRepairRounds,
    maxDoneRefusals: 0
  });
  const result = finishGraphResult({
    session,
    report,
    prompt: request,
    modelId,
    promptName: EDIT_PROMPT_NAME,
    maxRepairRounds,
    walker,
    extraMeta: {
      graphEdit: { ...report.fill, repairRounds: report.gate.repairRounds }
    }
  });
  if (!isGenerateError(result)) session.emit({ kind: "done" });
  return result;
};

// src/prompts/generatePlugin.ts
var REQUEST_EXAMPLES_TOOL2 = {
  name: "request_examples",
  description: "Request full plugin configurations from the marketplace to use as reference.",
  input_schema: {
    type: "object",
    properties: {
      pluginIds: {
        type: "array",
        items: { type: "string" },
        description: "Marketplace plugin IDs to fetch (1-3 most relevant)"
      }
    },
    required: ["pluginIds"]
  }
};
var SUGGEST_EXISTING_PLUGIN_TOOL = {
  name: "suggest_existing_plugin",
  description: "Suggest an existing marketplace plugin that closely matches the user's request. Use BEFORE generating from scratch when a marketplace plugin already fulfills the need.",
  input_schema: {
    type: "object",
    properties: {
      pluginId: {
        type: "string",
        description: "Marketplace plugin ID from the list"
      },
      reason: {
        type: "string",
        description: "Why this plugin matches the user's request"
      }
    },
    required: ["pluginId", "reason"]
  }
};
var USE_EXISTING_SENTINEL = "__USE_EXISTING__";
var generatePlugin = async (prompt, modelId, deps) => {
  const examplePluginsRequested = [];
  let suggestionMade = false;
  let turnCount = 0;
  const spec = {
    maxTokens: 16384,
    maxTotalTurns: 7,
    reserveFinalTurn: true,
    maxUserQuestions: 2,
    askUserDescription: "Ask the user a clarifying question before generating the plugin JSON. Use this when the request is ambiguous or missing key details.",
    // Same list every turn (cache prefix — see `getExtraTools`); a spent
    // tool is refused in its handler instead of being un-offered.
    getExtraTools: () => {
      var _a2, _b;
      turnCount++;
      const tools = [];
      if (((_a2 = deps.marketplaceMenu) == null ? void 0 : _a2.length) && deps.fetchPluginConfigs) {
        tools.push(REQUEST_EXAMPLES_TOOL2);
      }
      if (deps.onSuggestExisting && ((_b = deps.marketplaceMenu) == null ? void 0 : _b.length)) {
        tools.push(SUGGEST_EXISTING_PLUGIN_TOOL);
      }
      return tools;
    },
    handleExtraToolUse: async (block, state) => {
      var _a2, _b, _c, _d, _e, _f, _g, _h, _i;
      const refuse2 = (content2) => ({
        action: "continue",
        toolResults: [
          { type: "tool_result", tool_use_id: block.id, content: content2 }
        ]
      });
      if (block.name === "request_examples" && state.examplesRequested) {
        return refuse2(spentToolMessage("request_examples"));
      }
      if (block.name === "suggest_existing_plugin" && (suggestionMade || state.examplesRequested)) {
        return refuse2(
          "suggest_existing_plugin is no longer available \u2014 generate the plugin JSON now."
        );
      }
      if (block.name === "request_examples" && deps.fetchPluginConfigs) {
        state.examplesRequested = true;
        const pluginIds = ((_b = (_a2 = block.input.pluginIds) != null ? _a2 : block.input.orgIds) != null ? _b : []).slice(0, 3);
        const resolvedIds = deps.marketplaceMenu ? pluginIds.map((id) => {
          var _a3;
          const entry = deps.marketplaceMenu.find((m) => m.id === id);
          return (_a3 = entry == null ? void 0 : entry.qualifiedPluginId) != null ? _a3 : id;
        }) : pluginIds;
        examplePluginsRequested.push(...resolvedIds);
        (_c = deps.onStatus) == null ? void 0 : _c.call(
          deps,
          `fetching ${resolvedIds.length} example(s)...`
        );
        const configs = await deps.fetchPluginConfigs(resolvedIds);
        const content2 = configs.map(
          (c) => `## ${c.pluginId}
\`\`\`json
${c.config}
\`\`\``
        ).join("\n\n");
        return {
          action: "continue",
          toolResults: [
            {
              type: "tool_result",
              tool_use_id: block.id,
              content: content2 || "No configurations found for the requested IDs."
            }
          ]
        };
      }
      if (block.name === "suggest_existing_plugin" && deps.onSuggestExisting) {
        suggestionMade = true;
        const pluginId = (_d = block.input.pluginId) != null ? _d : "";
        const reason = (_e = block.input.reason) != null ? _e : "";
        const entry = (_f = deps.marketplaceMenu) == null ? void 0 : _f.find(
          (m) => m.id === pluginId
        );
        const qualifiedPluginId = (_g = entry == null ? void 0 : entry.qualifiedPluginId) != null ? _g : pluginId;
        const pluginName = (_h = entry == null ? void 0 : entry.name) != null ? _h : pluginId;
        const currentTurn = turnCount - 1;
        const lastMsg = state.messages[state.messages.length - 1];
        const assistantContent = (_i = lastMsg == null ? void 0 : lastMsg.content) != null ? _i : [];
        const userResponse = await deps.onSuggestExisting(
          { pluginId, qualifiedPluginId, pluginName, reason },
          currentTurn,
          {
            messages: [...state.messages],
            assistantContent
          }
        );
        if (userResponse === USE_EXISTING_SENTINEL) {
          const costUsd = calculateCost({
            model: state.totalUsage.model,
            inputTokens: state.totalUsage.inputTokens,
            outputTokens: state.totalUsage.outputTokens
          });
          return {
            action: "earlyReturn",
            result: {
              meta: {
                userPrompt: state.prompt,
                qaPairs: state.qaPairs,
                modelId: state.totalUsage.model,
                generatedAt: (/* @__PURE__ */ new Date()).toISOString(),
                usage: {
                  inputTokens: state.totalUsage.inputTokens,
                  outputTokens: state.totalUsage.outputTokens,
                  costUsd
                }
              },
              usage: state.totalUsage,
              usedExisting: { qualifiedPluginId, pluginName }
            }
          };
        }
        let existingConfigText = "";
        if (deps.fetchPluginConfigs) {
          const configs = await deps.fetchPluginConfigs([qualifiedPluginId]);
          if (configs.length > 0) {
            existingConfigText = `

Existing plugin config for reference:
\`\`\`json
${configs[0].config}
\`\`\``;
          }
        }
        return {
          action: "continue",
          toolResults: [
            {
              type: "tool_result",
              tool_use_id: block.id,
              content: `User wants customization: ${userResponse}${existingConfigText}`
            }
          ]
        };
      }
      return {
        action: "continue",
        toolResults: [
          {
            type: "tool_result",
            tool_use_id: block.id,
            content: ""
          }
        ]
      };
    },
    processResult: (text, stopReason, state) => {
      return processResult(text, stopReason, {
        prompt: state.prompt,
        qaPairs: state.qaPairs,
        totalUsage: state.totalUsage,
        examplePluginsRequested,
        imageSearchResults: state.imageSearchResults,
        webSearchResults: state.webSearchResults,
        videoSearchResults: state.videoSearchResults,
        turnLogs: state.turnLogs
      });
    }
  };
  const baseDeps = {
    callTurn: deps.callTurn,
    buildSystemPrompt: deps.buildSystemPrompt,
    onQuestion: deps.onQuestion,
    onStatus: deps.onStatus,
    onEvent: deps.onEvent,
    mockScenario: deps.mockScenario,
    initialMessages: deps.initialMessages,
    initialQaPairs: deps.initialQaPairs,
    searchImages: deps.searchImages,
    searchWeb: deps.searchWeb,
    searchVideos: deps.searchVideos
  };
  return generate(prompt, modelId, spec, baseDeps);
};
var processResult = (rawOutput, stopReason, ctx) => {
  const {
    prompt,
    qaPairs,
    totalUsage,
    examplePluginsRequested,
    imageSearchResults,
    webSearchResults,
    videoSearchResults,
    turnLogs
  } = ctx;
  if (stopReason === "max_tokens") {
    return {
      error: true,
      message: "Generation was too large and got truncated. Try a simpler description.",
      usage: totalUsage
    };
  }
  const jsonStr = extractJsonResponseSimple(rawOutput);
  if (!jsonStr) {
    return {
      error: true,
      message: "AI response did not contain valid JSON.",
      usage: totalUsage
    };
  }
  let parsed;
  try {
    parsed = JSON.parse(jsonStr);
  } catch (e) {
    const parseErr = e instanceof Error ? e.message : String(e);
    const preview = jsonStr.length > 800 ? jsonStr.slice(0, 800) + "..." : jsonStr;
    return {
      error: true,
      message: `Failed to parse AI response as JSON.
Parse error: ${parseErr}
Extracted (first 800 chars): ${preview}`,
      usage: totalUsage
    };
  }
  const validationError = htmlPlugin.is(parsed) ? void 0 : "Plugin JSON does not match expected schema (requires name, parameters, htmlTemplate).";
  const costUsd = calculateCost({
    model: totalUsage.model,
    inputTokens: totalUsage.inputTokens,
    outputTokens: totalUsage.outputTokens
  });
  const meta = {
    userPrompt: prompt,
    qaPairs,
    modelId: totalUsage.model,
    generatedAt: (/* @__PURE__ */ new Date()).toISOString(),
    usage: {
      inputTokens: totalUsage.inputTokens,
      outputTokens: totalUsage.outputTokens,
      costUsd
    },
    ...examplePluginsRequested.length > 0 ? { examplePluginsRequested } : {},
    ...imageSearchResults.length > 0 ? { imageSearches: imageSearchResults } : {},
    ...webSearchResults.length > 0 ? { webSearches: webSearchResults } : {},
    ...videoSearchResults.length > 0 ? {
      videoSearches: videoSearchResults.map((r) => ({
        query: r.query,
        title: r.title,
        url: r.url,
        embedUrl: r.embedUrl,
        duration: r.duration,
        creator: r.creator
      }))
    } : {},
    ...turnLogs.length > 0 ? { turnLogs } : {}
  };
  const pluginObj = parsed;
  pluginObj.type = "plugin";
  const pluginName = typeof pluginObj.name === "string" ? pluginObj.name : "Unknown";
  const params = Array.isArray(pluginObj.parameters) ? pluginObj.parameters : [];
  const paramNames = params.map((p) => p.name).filter(Boolean).join(", ");
  const pluginSummary = `${pluginName} \u2014 ${params.length} parameter(s): ${paramNames}`;
  return {
    pluginJson: JSON.stringify(parsed, null, 2),
    meta,
    pluginSummary,
    usage: totalUsage,
    validationError
  };
};
var isGeneratePluginError = (result) => isGenerateError(result);

// src/prompts/generateObjects.ts
var composeUserMessage = (prompt, config) => {
  const quantityStr = typeof config.quantity === "number" ? `${config.quantity}` : config.quantity === "some" ? "a handful of" : "many";
  const parts = [`Generate ${quantityStr} ${prompt}.`];
  if (config.quantity === 1) {
    parts.push(
      "\nReturn exactly ONE value of the type below, shaped exactly as the type says: an object type is one JSON object (with any list the request asks for inside its fields), a list type is one JSON array. Do not wrap it in an extra array."
    );
  }
  parts.push(`
Type specification:
${config.typeDescription}`);
  if (config.additionalContext) {
    parts.push(`
Additional context: ${config.additionalContext}`);
  }
  return parts.join("\n");
};
var imageBlock = (url) => {
  if (/^https?:/i.test(url)) {
    return { type: "image", source: { type: "url", url } };
  }
  const match2 = /^data:([^;,]+);base64,(.+)$/i.exec(url);
  if (!match2) return void 0;
  return {
    type: "image",
    source: { type: "base64", media_type: match2[1], data: match2[2] }
  };
};
var composeMultimodalMessages = (text, images) => {
  if (!(images == null ? void 0 : images.length)) return void 0;
  const blocks = images.map(imageBlock).filter((b2) => b2 !== void 0);
  if (!blocks.length) return void 0;
  return [{ role: "user", content: [...blocks, { type: "text", text }] }];
};
var maxTokensForQuantity = (quantity) => {
  if (typeof quantity === "number") return quantity <= 10 ? 16384 : 32768;
  return quantity === "some" ? 16384 : 32768;
};
var extractJsonArray = (s) => {
  const firstBracket = s.indexOf("[");
  const lastBracket = s.lastIndexOf("]");
  if (firstBracket !== -1 && lastBracket !== -1 && firstBracket < lastBracket) {
    return { json: s.slice(firstBracket, lastBracket + 1), wrapped: false };
  }
  const objectJson = extractJsonResponseSimple(s);
  if (objectJson) return { json: `[${objectJson}]`, wrapped: true };
  return { json: "", wrapped: false };
};
var parses = (s) => {
  try {
    JSON.parse(s);
    return true;
  } catch {
    return false;
  }
};
var extractJsonValue = (s) => {
  var _a2, _b, _c;
  const fenced = (_b = (_a2 = /```(?:json)?\s*([\s\S]*?)```/.exec(s)) == null ? void 0 : _a2[1]) == null ? void 0 : _b.trim();
  if (fenced && parses(fenced)) return fenced;
  const brace = s.indexOf("{");
  const bracket = s.indexOf("[");
  const objectSlice = brace === -1 ? "" : s.slice(brace, s.lastIndexOf("}") + 1);
  const arraySlice = bracket === -1 ? "" : s.slice(bracket, s.lastIndexOf("]") + 1);
  const ordered = brace !== -1 && (bracket === -1 || brace < bracket) ? [objectSlice, arraySlice] : [arraySlice, objectSlice];
  const whole = s.trim();
  return (_c = ordered.find((c) => c && parses(c))) != null ? _c : parses(whole) ? whole : "";
};
var isListType = (typeDescription) => {
  const src = typeDescription.replace(/\/\/[^\n]*/g, "").trim().replace(/;$/, "").trim();
  return /\[\]$/.test(src) || /^Array<[\s\S]*>$/.test(src);
};
var soleListField = (typeDescription) => {
  const src = typeDescription.replace(/\/\/[^\n]*/g, "");
  const open = src.indexOf("{");
  const close = src.lastIndexOf("}");
  if (open === -1 || close <= open) return void 0;
  const body = src.slice(open + 1, close);
  const fields = [];
  let depth = 0;
  let current = "";
  for (const ch of body) {
    if (ch === "{" || ch === "[" || ch === "(" || ch === "<") depth++;
    if (ch === "}" || ch === "]" || ch === ")" || ch === ">") depth--;
    if ((ch === ";" || ch === ",") && depth === 0) {
      if (current.trim()) fields.push(splitField(current));
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) fields.push(splitField(current));
  const lists = fields.filter(
    ({ name, type: type14 }) => name && /\[\]$|^Array<.*>$/.test(type14)
  );
  return lists.length === 1 ? lists[0].name : void 0;
};
var splitField = (raw) => {
  const i = raw.indexOf(":");
  return i === -1 ? { name: "", type: "" } : {
    name: raw.slice(0, i).trim().replace(/\?$/, ""),
    type: raw.slice(i + 1).trim()
  };
};
var toSingleValue = (value, typeDescription) => {
  if (!Array.isArray(value) || isListType(typeDescription)) return value;
  const field2 = soleListField(typeDescription);
  const carriesField = !!field2 && value.some((o) => !!o && typeof o === "object" && field2 in o);
  if (field2 && !carriesField) return { [field2]: value };
  return value[0];
};
var generateObjects = async (prompt, modelId, config, deps, options) => {
  var _a2, _b, _c, _d, _e;
  const userMessage = composeUserMessage(prompt, config);
  const { onAnswerProgress } = deps;
  const answerStart = config.quantity === 1 && !isListType(config.typeDescription) ? "{" : "[";
  const answerField = answerStart === "{" ? soleListField(config.typeDescription) : void 0;
  const spec = {
    maxTokens: (_a2 = options == null ? void 0 : options.maxTokens) != null ? _a2 : maxTokensForQuantity(config.quantity),
    maxTotalTurns: (_b = options == null ? void 0 : options.maxTurns) != null ? _b : 3,
    maxUserQuestions: (_c = options == null ? void 0 : options.maxQuestions) != null ? _c : 1,
    askUserDescription: "Ask the user a clarifying question about what objects to generate. Use this when the request is ambiguous or the type specification is unclear.",
    processStreamingText: onAnswerProgress ? (text) => onAnswerProgress(
      answerViews(text, answerStart, { soleListField: answerField })
    ) : void 0,
    getExtraTools: () => [],
    handleExtraToolUse: async (block) => ({
      action: "continue",
      toolResults: [
        {
          type: "tool_result",
          tool_use_id: block.id,
          content: ""
        }
      ]
    }),
    processResult: (text, stopReason, state) => {
      return processResult2(text, stopReason, config, {
        prompt: state.prompt,
        qaPairs: state.qaPairs,
        totalUsage: state.totalUsage,
        imageSearchResults: state.imageSearchResults,
        webSearchResults: state.webSearchResults,
        videoSearchResults: state.videoSearchResults,
        turnLogs: state.turnLogs
      });
    }
  };
  const baseDeps = {
    callTurn: deps.callTurn,
    buildSystemPrompt: deps.buildSystemPrompt,
    onQuestion: (_d = deps.onQuestion) != null ? _d : async () => "No clarification available, use your best judgment.",
    onStatus: deps.onStatus,
    onEvent: onAnswerProgress ? (event) => {
      var _a3;
      if (event.kind === "turn-start") onAnswerProgress(void 0);
      (_a3 = deps.onEvent) == null ? void 0 : _a3.call(deps, event);
    } : deps.onEvent,
    mockScenario: deps.mockScenario,
    // An explicit `deps.initialMessages` always wins — that is the resume path,
    // which replays a whole conversation and must not be rebuilt from config.
    initialMessages: (_e = deps.initialMessages) != null ? _e : composeMultimodalMessages(userMessage, config.images),
    initialQaPairs: deps.initialQaPairs,
    searchImages: deps.searchImages,
    searchWeb: deps.searchWeb,
    searchVideos: deps.searchVideos
  };
  return generate(userMessage, modelId, spec, baseDeps);
};
var processResult2 = (rawOutput, stopReason, config, ctx) => {
  const {
    prompt,
    qaPairs,
    totalUsage,
    imageSearchResults,
    webSearchResults,
    videoSearchResults,
    turnLogs
  } = ctx;
  const single2 = config.quantity === 1;
  if (stopReason === "max_tokens") {
    return {
      error: true,
      message: `The AI's answer was cut off at its length limit before it finished. ${single2 ? "Try asking for something shorter." : "Try fewer objects or a simpler type."}`,
      usage: totalUsage
    };
  }
  const { json: jsonStr, wrapped } = single2 ? { json: extractJsonValue(rawOutput), wrapped: false } : extractJsonArray(rawOutput);
  if (!jsonStr) {
    const preview = rawOutput.trim().slice(0, 200);
    return {
      error: true,
      message: preview ? `AI did not return valid JSON. Response: "${preview}${rawOutput.length > 200 ? "\u2026" : ""}"` : "AI response did not contain valid JSON.",
      usage: totalUsage
    };
  }
  let parsed;
  const validationError = wrapped ? "Response was a single object, wrapped in an array." : void 0;
  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    return {
      error: true,
      message: "Failed to parse AI response as JSON.",
      usage: totalUsage
    };
  }
  if (single2) {
    parsed = toSingleValue(parsed, config.typeDescription);
  } else if (!Array.isArray(parsed)) {
    parsed = [parsed];
  }
  const objectCount = single2 ? 1 : parsed.length;
  const json = JSON.stringify(parsed, null, 2);
  const costUsd = calculateCost({
    model: totalUsage.model,
    inputTokens: totalUsage.inputTokens,
    outputTokens: totalUsage.outputTokens
  });
  const meta = {
    userPrompt: prompt,
    typeDescription: config.typeDescription,
    quantity: config.quantity,
    qaPairs,
    modelId: totalUsage.model,
    generatedAt: (/* @__PURE__ */ new Date()).toISOString(),
    usage: {
      inputTokens: totalUsage.inputTokens,
      outputTokens: totalUsage.outputTokens,
      costUsd
    },
    ...imageSearchResults.length > 0 ? { imageSearches: imageSearchResults } : {},
    ...webSearchResults.length > 0 ? { webSearches: webSearchResults } : {},
    ...videoSearchResults.length > 0 ? {
      videoSearches: videoSearchResults.map((r) => ({
        query: r.query,
        title: r.title,
        url: r.url,
        embedUrl: r.embedUrl,
        duration: r.duration,
        creator: r.creator
      }))
    } : {},
    ...turnLogs.length > 0 ? { turnLogs } : {}
  };
  const summary = `Generated ${objectCount} object${objectCount === 1 ? "" : "s"}`;
  return {
    json,
    objectCount,
    summary,
    meta,
    usage: totalUsage,
    validationError
  };
};
var isGenerateObjectsError = (result) => isGenerateError(result);

// src/prompts/demoPrompts.ts
var DEMO_PROMPTS = [
  {
    slug: "book-club-tracker",
    label: "Book club tracker",
    prompt: "A book club tracker"
  },
  {
    slug: "gym-class-booking",
    label: "Gym class booking",
    prompt: "A gym class booking app"
  },
  {
    slug: "recipe-notebook",
    label: "Recipe notebook",
    prompt: "A recipe notebook"
  }
];

// src/prompts/showcase/types.ts
var SHOWCASE_FEATURES = [
  "flows",
  "tabs",
  "record-tree",
  "forms",
  "plugins",
  "widgets",
  "realtime",
  "transitions",
  "push",
  "ai-chat",
  "ai-query",
  "image-generation",
  "storage-data",
  "embedded-data",
  "user-scoped",
  "group-scoped",
  "auth-private",
  "org-login",
  "video",
  "audio",
  "maps"
];

// src/prompts/showcase/rank.ts
var STOPWORDS = new Set(
  "a an the and or for of to in on with my our your me i we it its this that app apps application something some thing things help keep can like want make build create need would should could be is are was let lets using use simple new tool tools just also all any".split(" ")
);
var fold2 = (t30) => t30.length > 4 && t30.endsWith("ies") ? `${t30.slice(0, -3)}y` : t30.length > 3 && t30.endsWith("s") && !t30.endsWith("ss") ? t30.slice(0, -1) : t30;
var tokenize = (text) => text.toLowerCase().split(/[^a-z0-9]+/).filter((t30) => t30.length >= 2 && !STOPWORDS.has(t30)).map(fold2);
var WORD_FEATURE_LIST = [
  [
    ["chat", "tutor", "talk", "conversation", "speak", "spoken", "voice"],
    ["ai-chat"]
  ],
  [
    ["ai", "generate", "generated", "generator", "smart"],
    ["ai-query", "image-generation"]
  ],
  [
    ["image", "picture", "photo", "camera", "illustrated"],
    ["image-generation", "ai-query"]
  ],
  [
    ["game", "party", "multiplayer", "player", "live", "realtime", "round"],
    ["realtime", "transitions"]
  ],
  [
    [
      "quiz",
      "revise",
      "revision",
      "flashcard",
      "exam",
      "study",
      "learn",
      "lesson"
    ],
    ["flows", "tabs"]
  ],
  [
    [
      "booking",
      "book",
      "schedule",
      "calendar",
      "rota",
      "signup",
      "form",
      "track",
      "tracker",
      "log"
    ],
    ["forms", "storage-data"]
  ],
  [["notify", "notification", "reminder", "alert"], ["push"]],
  [["video", "call", "youtube"], ["video"]],
  [["music", "song", "audio", "jingle", "playlist", "podcast"], ["audio"]],
  [["map", "trip", "travel", "holiday", "itinerary", "route"], ["maps"]],
  [
    ["login", "member", "private", "pin"],
    ["org-login", "auth-private"]
  ],
  [
    ["team", "group", "share", "shared", "friend"],
    ["group-scoped", "realtime"]
  ],
  [["personal", "journal", "diary"], ["user-scoped"]]
];
var WORD_FEATURES = new Map(
  WORD_FEATURE_LIST.flatMap(
    ([words, features]) => words.map((w) => [fold2(w), features])
  )
);
var LABEL_WEIGHT = 3;
var DESCRIPTION_WEIGHT = 2;
var FEATURE_WEIGHT = 1;
var MIN_SCORE = 2;
var SIZE_RANK = { simple: 0, medium: 1, complex: 2 };
var bySizeThenId = (a, b2) => SIZE_RANK[a.size] - SIZE_RANK[b2.size] || a.screens - b2.screens || (a.orgId < b2.orgId ? -1 : a.orgId > b2.orgId ? 1 : 0);
var score = (tokens, entry) => {
  var _a2, _b;
  const label2 = new Set(
    tokenize(`${entry.label} ${(_a2 = entry.orgId.split("/").pop()) != null ? _a2 : ""}`)
  );
  const description = new Set(tokenize(entry.description));
  const features = new Set(entry.features);
  let s = 0;
  for (const t30 of new Set(tokens)) {
    if (label2.has(t30)) s += LABEL_WEIGHT;
    else if (description.has(t30)) s += DESCRIPTION_WEIGHT;
    for (const f of (_b = WORD_FEATURES.get(t30)) != null ? _b : [])
      if (features.has(f)) s += FEATURE_WEIGHT;
  }
  return s;
};
var defaultMatches = (index, limit = 8) => {
  var _a2;
  const counts = /* @__PURE__ */ new Map();
  for (const entry of index)
    for (const f of entry.features) counts.set(f, ((_a2 = counts.get(f)) != null ? _a2 : 0) + 1);
  const common = SHOWCASE_FEATURES.filter((f) => counts.has(f)).sort(
    (a, b2) => {
      var _a3, _b;
      return ((_a3 = counts.get(b2)) != null ? _a3 : 0) - ((_b = counts.get(a)) != null ? _b : 0);
    }
  );
  const picked = [];
  const has = (e) => picked.some((p) => p.orgId === e.orgId);
  const described = index.filter((e) => e.description);
  for (const f of common) {
    if (picked.length >= limit) break;
    const best = described.filter((e) => e.features.includes(f) && !has(e)).sort(bySizeThenId)[0];
    if (best) picked.push(best);
  }
  for (const e of [
    ...described.sort(bySizeThenId),
    ...[...index].sort(bySizeThenId)
  ]) {
    if (picked.length >= limit) break;
    if (!has(e)) picked.push(e);
  }
  return picked;
};
var rankShowcase = (prompt, index, { limit = 8 } = {}) => {
  const tokens = tokenize(prompt);
  const scored = index.map((entry) => ({ entry, s: score(tokens, entry) })).filter((x) => x.s > 0).sort((a, b2) => b2.s - a.s || bySizeThenId(a.entry, b2.entry));
  const hits = scored.length > 0 && scored[0].s >= MIN_SCORE ? scored.map((x) => x.entry).slice(0, limit) : [];
  const out = [...hits];
  for (const d of defaultMatches(index, limit)) {
    if (out.length >= limit) break;
    if (!out.some((o) => o.orgId === d.orgId)) out.push(d);
  }
  return out;
};

// src/resources/types.ts
var toResourceId = (storagePath) => storagePath.replace(/[/.]/g, "_");

// src/resources/storagePaths.ts
var storagePathFromUrl = (value) => {
  if (!value) return void 0;
  if (value.startsWith("gs://")) {
    const afterScheme = value.slice("gs://".length);
    const slash = afterScheme.indexOf("/");
    if (slash < 0) return void 0;
    const path = afterScheme.slice(slash + 1);
    return path || void 0;
  }
  const marker = "/o/";
  const at = value.indexOf(marker);
  if (at < 0) return void 0;
  const encoded = afterQueryless(value.slice(at + marker.length));
  if (!encoded) return void 0;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return void 0;
  }
};
var afterQueryless = (s) => {
  var _a2;
  return (_a2 = s.split("?")[0]) != null ? _a2 : "";
};
var orgPublicPrefix = (orgId) => `public/${orgId}/`;
var isOrgUploadPath = (path, orgId) => {
  const prefix = orgPublicPrefix(orgId);
  if (!orgId || !path.startsWith(prefix)) return false;
  const rest = path.slice(prefix.length);
  if (rest.length === 0 || rest.endsWith("/")) return false;
  if (rest.startsWith("data/")) return false;
  if (/^(logo|hero-[^/]*)\.[^/.]+$/.test(rest)) return false;
  return true;
};
var collectStoragePaths = (root, into = /* @__PURE__ */ new Set()) => {
  const visit = (node2) => {
    if (typeof node2 === "string") {
      const path = storagePathFromUrl(node2);
      if (path) into.add(path);
      return;
    }
    if (Array.isArray(node2)) {
      for (const child of node2) visit(child);
      return;
    }
    if (node2 && typeof node2 === "object") {
      for (const child of Object.values(node2)) {
        visit(child);
      }
    }
  };
  visit(root);
  return into;
};

// src/resources/hardDeletePlan.ts
var SCOPE_DIRS = {
  users: "user",
  variants: "variant",
  groups: "group"
};
var isScopeDir = (s) => s === "users" || s === "variants" || s === "groups";
var dataPartitionOf = (path, orgId, type14) => {
  const base = `public/${orgId}/data/`;
  if (!path.startsWith(base) || !path.endsWith(".json")) return void 0;
  const rest = path.slice(base.length, -".json".length);
  if (rest === type14) return { scope: "global", form: "batch" };
  if (!rest.startsWith(`${type14}/`)) return void 0;
  const segs = rest.slice(type14.length + 1).split("/");
  if (segs.some((s) => s.length === 0)) return void 0;
  if (segs.length === 1) return { scope: "global", form: "individual" };
  const [dir, key] = segs;
  if (!isScopeDir(dir) || key === void 0) return void 0;
  if (segs.length === 2) return { scope: SCOPE_DIRS[dir], key, form: "batch" };
  if (segs.length === 3) {
    return { scope: SCOPE_DIRS[dir], key, form: "individual" };
  }
  return void 0;
};
var hardDeleteClosure = ({
  objectTypes,
  realtimeTypes,
  rootType
}) => {
  var _a2;
  const children2 = childrenOf(objectTypes);
  const types2 = [];
  const seen = /* @__PURE__ */ new Set();
  const queue = [rootType];
  for (let i = 0; i < queue.length; i++) {
    const type14 = queue[i];
    if (seen.has(type14)) continue;
    seen.add(type14);
    types2.push(type14);
    for (const link of (_a2 = children2[type14]) != null ? _a2 : []) queue.push(link.type);
  }
  const cycle = parentCycles(objectTypes).find(
    (c) => c.some((t30) => seen.has(t30))
  );
  if (cycle) {
    return {
      refusal: {
        reason: "cycle",
        message: `Cannot cascade-delete ${rootType}: parent relations form a cycle (${cycle.join(" \u2192 ")}).`
      }
    };
  }
  const realtime = types2.filter((t30) => realtimeTypes.has(t30));
  if (realtime.length > 0) {
    const which = realtime[0] === rootType ? `${rootType} is a realtime-rail type` : `its cascade reaches realtime-rail type(s) ${realtime.join(", ")}`;
    return {
      refusal: {
        reason: "realtime",
        message: `Cannot hard-delete ${rootType}: ${which}. Realtime records are removed by the transition engine's cascade, not from Storage.`
      }
    };
  }
  return { types: types2 };
};
var isRecord11 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var idOf = (record10) => typeof record10.id === "string" && record10.id.length > 0 ? record10.id : void 0;
var partitionKey = (type14, p) => p.scope === "global" ? `${type14}|global` : `${type14}|${p.scope}|${p.key}`;
var toPartition = (parsed) => parsed.scope === "global" ? { scope: "global" } : { scope: parsed.scope, key: parsed.key };
var planHardDelete = ({
  orgId,
  objectTypes,
  realtimeTypes,
  rootType,
  rootIds,
  filesByType
}) => {
  var _a2, _b, _c, _d, _e, _f, _g, _h, _i;
  const closure = hardDeleteClosure({ objectTypes, realtimeTypes, rootType });
  if ("refusal" in closure) throw new Error(closure.refusal.message);
  const entriesByType = /* @__PURE__ */ new Map();
  for (const type14 of closure.types) {
    const entries = [];
    for (const file of (_a2 = filesByType[type14]) != null ? _a2 : []) {
      const parsed = dataPartitionOf(file.path, orgId, type14);
      if (!parsed) continue;
      const records = parsed.form === "batch" ? Array.isArray(file.json) ? file.json.filter(isRecord11) : [] : isRecord11(file.json) ? [file.json] : [];
      entries.push({ file, parsed, records });
    }
    entriesByType.set(type14, entries);
  }
  const recordIds = (entry, record10) => {
    var _a3;
    const own = idOf(record10);
    if (own || entry.parsed.form === "batch") return own;
    const name = (_a3 = entry.file.path.split("/").pop()) != null ? _a3 : "";
    return name.slice(0, -".json".length) || void 0;
  };
  const idsByType = /* @__PURE__ */ new Map();
  idsByType.set(rootType, new Set(rootIds));
  const children2 = childrenOf(objectTypes);
  for (const type14 of closure.types) {
    const parentIds = idsByType.get(type14);
    if (!parentIds || parentIds.size === 0) continue;
    for (const link of (_b = children2[type14]) != null ? _b : []) {
      const childIds = (_c = idsByType.get(link.type)) != null ? _c : /* @__PURE__ */ new Set();
      for (const entry of (_d = entriesByType.get(link.type)) != null ? _d : []) {
        for (const record10 of entry.records) {
          const parentRef = record10[link.idField];
          const id = recordIds(entry, record10);
          if (id && typeof parentRef === "string" && parentIds.has(parentRef)) {
            childIds.add(id);
          }
        }
      }
      idsByType.set(link.type, childIds);
    }
  }
  const edits = [];
  const bumps = /* @__PURE__ */ new Map();
  const deletedRecords = [];
  const found = /* @__PURE__ */ new Map();
  const rootCopies = {};
  for (const type14 of closure.types) {
    const ids = idsByType.get(type14);
    if (!ids || ids.size === 0) continue;
    const foundIds = (_e = found.get(type14)) != null ? _e : /* @__PURE__ */ new Set();
    found.set(type14, foundIds);
    for (const entry of (_f = entriesByType.get(type14)) != null ? _f : []) {
      const hit = entry.records.filter((r) => {
        const id = recordIds(entry, r);
        return id !== void 0 && ids.has(id);
      });
      if (hit.length === 0) continue;
      for (const record10 of hit) {
        const id = recordIds(entry, record10);
        foundIds.add(id);
        deletedRecords.push(record10);
        if (type14 === rootType) ((_g = rootCopies[id]) != null ? _g : rootCopies[id] = []).push(record10);
      }
      const partition = toPartition(entry.parsed);
      if (entry.parsed.form === "individual") {
        edits.push({ kind: "deleteFile", path: entry.file.path });
      } else {
        const all = Array.isArray(entry.file.json) ? entry.file.json : [];
        const keep = all.filter((r) => !(isRecord11(r) && hit.includes(r)));
        edits.push({
          kind: "rewriteBatch",
          path: entry.file.path,
          records: keep
        });
      }
      const key = entry.parsed.form === "batch" ? "latestBatch" : "latestIndividual";
      bumps.set(`${partitionKey(type14, partition)}|${key}`, {
        type: type14,
        partition,
        key
      });
    }
  }
  const deleted = [];
  for (const type14 of [...closure.types].reverse()) {
    for (const id of [...(_h = found.get(type14)) != null ? _h : []].sort()) {
      deleted.push({ type: type14, id });
    }
  }
  const foundRoots = (_i = found.get(rootType)) != null ? _i : /* @__PURE__ */ new Set();
  return {
    deleted,
    edits,
    bumps: [...bumps.values()],
    deletedRecords,
    rootCopies,
    notFound: [...new Set(rootIds)].filter((id) => !foundRoots.has(id))
  };
};
var blobsToDelete = ({
  orgId,
  deletedRecords,
  survivingRoots
}) => {
  const candidates = [...collectStoragePaths(deletedRecords)].filter((p) => isOrgUploadPath(p, orgId)).sort();
  const referenced = /* @__PURE__ */ new Set();
  for (const root of survivingRoots) collectStoragePaths(root, referenced);
  return {
    delete: candidates.filter((p) => !referenced.has(p)),
    skippedShared: candidates.filter((p) => referenced.has(p))
  };
};

// src/slack/blockKit.ts
var ORGIGGLY_SITE_ORIGIN = "https://orgiggly.com";
var SLACK_LIMITS = {
  headerTextMax: 150,
  sectionTextMax: 3e3,
  buttonTextMax: 75,
  modalTitleMax: 24,
  buttonsPerActionsRow: 5
};
var truncate2 = (s, max) => s.length > max ? `${s.slice(0, max - 1)}\u2026` : s;
var titleCase = (s) => s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
var screenLabel = (node2) => {
  var _a2, _b;
  return ((_a2 = node2.label) == null ? void 0 : _a2.trim()) || ((_b = node2.name) == null ? void 0 : _b.trim()) || "Screen";
};
var orgScreens = (config) => {
  var _a2;
  return ((_a2 = config.appConfig._children) != null ? _a2 : []).filter((c) => c.type === "screen");
};
var orgDisplayName = (config, qualifiedOrgId) => {
  var _a2, _b;
  const fromConfig = (_a2 = config.appConfig.name) == null ? void 0 : _a2.trim();
  if (fromConfig) return fromConfig;
  const orgName = (_b = qualifiedOrgId.split("/").pop()) != null ? _b : qualifiedOrgId;
  return titleCase(orgName);
};
var orgRootUrl = (qualifiedOrgId) => `${ORGIGGLY_SITE_ORIGIN}/${qualifiedOrgId}`;
var screenDeepLink = (qualifiedOrgId, screen2) => {
  const root = orgRootUrl(qualifiedOrgId);
  if (!screen2.name) return root;
  return `${root}#${encodeURIComponent(screen2.name)}`;
};
var buildNavButton = (qualifiedOrgId, screen2) => {
  var _a2;
  return {
    type: "button",
    text: {
      type: "plain_text",
      text: truncate2(screenLabel(screen2), SLACK_LIMITS.buttonTextMax)
    },
    url: screenDeepLink(qualifiedOrgId, screen2),
    action_id: `open_screen_${(_a2 = screen2.name) != null ? _a2 : "screen"}`
  };
};
var buildMoreButton = (qualifiedOrgId) => ({
  type: "button",
  text: { type: "plain_text", text: "More on Orgiggly" },
  url: orgRootUrl(qualifiedOrgId),
  action_id: "open_org_more"
});
var buildNavButtons = (qualifiedOrgId, screens) => {
  if (screens.length <= SLACK_LIMITS.buttonsPerActionsRow) {
    return screens.map((s) => buildNavButton(qualifiedOrgId, s));
  }
  const topFour = screens.slice(0, SLACK_LIMITS.buttonsPerActionsRow - 1).map((s) => buildNavButton(qualifiedOrgId, s));
  return [...topFour, buildMoreButton(qualifiedOrgId)];
};
var renderOrgUnfurl = (config, qualifiedOrgId, logoUrl, description) => {
  const displayName4 = orgDisplayName(config, qualifiedOrgId);
  const screens = orgScreens(config);
  const header = {
    type: "header",
    text: {
      type: "plain_text",
      text: truncate2(displayName4, SLACK_LIMITS.headerTextMax)
    }
  };
  const image = {
    type: "image",
    image_url: logoUrl,
    alt_text: `${displayName4} logo`
  };
  const blocks = [header, image];
  const trimmedDescription = description == null ? void 0 : description.trim();
  if (trimmedDescription) {
    const descriptionBlock = {
      type: "section",
      text: {
        type: "mrkdwn",
        text: truncate2(trimmedDescription, SLACK_LIMITS.sectionTextMax)
      }
    };
    blocks.push(descriptionBlock);
  }
  if (screens.length > 0) {
    const actions = {
      type: "actions",
      elements: buildNavButtons(qualifiedOrgId, screens)
    };
    blocks.push(actions);
  }
  return blocks;
};
var collectScreenChildLabels = (screen2) => {
  var _a2;
  return ((_a2 = screen2._children) != null ? _a2 : []).map((c) => {
    var _a3, _b;
    return ((_a3 = c.label) == null ? void 0 : _a3.trim()) || ((_b = c.name) == null ? void 0 : _b.trim());
  }).filter((s) => !!s);
};
var renderScreenModal = (screen2, _storageData, qualifiedOrgId) => {
  const label2 = screenLabel(screen2);
  const childLabels = collectScreenChildLabels(screen2);
  const blocks = [
    {
      type: "header",
      text: {
        type: "plain_text",
        text: truncate2(label2, SLACK_LIMITS.headerTextMax)
      }
    }
  ];
  if (childLabels.length > 0) {
    const bulletList = childLabels.map((l) => `\u2022 ${l}`).join("\n");
    blocks.push({
      type: "section",
      text: {
        type: "mrkdwn",
        text: truncate2(bulletList, SLACK_LIMITS.sectionTextMax)
      }
    });
  }
  blocks.push({ type: "divider" });
  blocks.push({
    type: "actions",
    elements: [
      {
        type: "button",
        text: { type: "plain_text", text: "Open in Orgiggly" },
        url: orgRootUrl(qualifiedOrgId),
        action_id: "open_in_orgiggly",
        style: "primary"
      }
    ]
  });
  return {
    type: "modal",
    title: {
      type: "plain_text",
      text: truncate2(label2, SLACK_LIMITS.modalTitleMax)
    },
    close: { type: "plain_text", text: "Close" },
    blocks
  };
};

// src/types/orgIndex.ts
import * as t28 from "io-ts";
var RESERVED_CONTAINER_ROOT = "root";
var RESERVED_CONTAINER_TRASH = "__trash__";
var RESERVED_CONTAINER_PRIVATE = "__private__";
var RESERVED_CONTAINER_IDS = [
  RESERVED_CONTAINER_ROOT,
  RESERVED_CONTAINER_TRASH,
  RESERVED_CONTAINER_PRIVATE
];
var isReservedContainer = (id) => RESERVED_CONTAINER_IDS.includes(id);
var orgIndexFolder = t28.intersection([
  t28.type({ name: t28.string }),
  t28.partial({ parent: t28.union([t28.string, t28.null]) })
]);
var orgIndexContainer = t28.type({
  orgIds: t28.array(t28.string)
});
var orgIndexCodec = t28.partial({
  folders: t28.record(t28.string, orgIndexFolder),
  containers: t28.record(t28.string, orgIndexContainer),
  /**
   * Author-chosen display order for folder tiles (#737). Absent means "no
   * preference" and consumers fall back to alphabetical-by-name, which is what
   * the chooser did before folders became draggable — so an index written by
   * an older client keeps its existing appearance.
   *
   * Only an ORDER, deliberately not a membership list: an id here that names no
   * folder is ignored, and a folder missing from here sorts after the listed
   * ones. That keeps the array from becoming a second source of truth about
   * which folders exist, which `folders` already owns.
   */
  folderOrder: t28.array(t28.string)
});
var emptyOrgIndex = {
  folders: {},
  containers: {}
};
var orgIndexPath = (uid) => `orgiggly/users/${uid}/orgIndex`;
var cloneOrgIndex = (index) => {
  var _a2, _b;
  return {
    folders: { ...(_a2 = index.folders) != null ? _a2 : {} },
    containers: Object.fromEntries(
      Object.entries((_b = index.containers) != null ? _b : {}).map(([k, v]) => [
        k,
        { orgIds: [...v.orgIds] }
      ])
    ),
    ...index.folderOrder ? { folderOrder: [...index.folderOrder] } : {}
  };
};
var containerForOrg = (index, orgId) => {
  var _a2, _b;
  return (_b = Object.entries((_a2 = index.containers) != null ? _a2 : {}).find(
    ([, c]) => c.orgIds.includes(orgId)
  )) == null ? void 0 : _b[0];
};
var withoutOrg = (index, containerId, orgId) => {
  var _a2, _b, _c;
  const next = cloneOrgIndex(index);
  next.containers = (_a2 = next.containers) != null ? _a2 : {};
  const filtered = ((_c = (_b = next.containers[containerId]) == null ? void 0 : _b.orgIds) != null ? _c : []).filter(
    (id) => id !== orgId
  );
  if (filtered.length === 0 && containerId !== RESERVED_CONTAINER_ROOT) {
    delete next.containers[containerId];
  } else {
    next.containers[containerId] = { orgIds: filtered };
  }
  return next;
};
var moveOrgInIndex = (index, orgId, toContainer, toIndex) => {
  var _a2, _b, _c;
  const from = containerForOrg(index, orgId);
  const next = cloneOrgIndex(
    from === void 0 ? index : withoutOrg(index, from, orgId)
  );
  next.containers = (_a2 = next.containers) != null ? _a2 : {};
  const current = ((_c = (_b = next.containers[toContainer]) == null ? void 0 : _b.orgIds) != null ? _c : []).filter(
    (id) => id !== orgId
  );
  const insertAt = toIndex === void 0 || toIndex < 0 || toIndex > current.length ? current.length : toIndex;
  next.containers[toContainer] = {
    orgIds: [...current.slice(0, insertAt), orgId, ...current.slice(insertAt)]
  };
  return next;
};
var orderFolders = (folders, folderOrder) => {
  const byName = (a, b2) => a.name.localeCompare(b2.name);
  if (!(folderOrder == null ? void 0 : folderOrder.length)) return [...folders].sort(byName);
  const remaining = new Map(folders.map((f) => [f.id, f]));
  const ordered = [];
  for (const id of folderOrder) {
    const folder2 = remaining.get(id);
    if (!folder2) continue;
    remaining.delete(id);
    ordered.push(folder2);
  }
  return [...ordered, ...[...remaining.values()].sort(byName)];
};

// src/types/issueReport.ts
import * as t29 from "io-ts";
var ISSUE_REPORT_STATUSES = [
  "new",
  "claimed",
  "fixed-draft",
  "published",
  "closed"
];
var issueReportStatus = t29.keyof({
  new: null,
  claimed: null,
  "fixed-draft": null,
  published: null,
  closed: null
});
var nullable = (codec) => t29.union([codec, t29.null]);
var issueReportTarget = t29.intersection([
  t29.type({ qualifiedOrgId: t29.string }),
  t29.partial({
    // useConfigSource(): published | server-draft | local-draft. Kept a plain
    // string so a reader never rejects a record from a newer client.
    configSource: nullable(t29.string),
    versionId: nullable(t29.string),
    previewDraft: nullable(t29.boolean),
    variant: nullable(t29.string)
  })
]);
var issueReportLocation = t29.intersection([
  t29.type({ pathname: t29.string }),
  t29.partial({
    search: nullable(t29.string),
    pointerKey: nullable(t29.string),
    screenLabel: nullable(t29.string)
  })
]);
var issueReportClient = t29.partial({
  buildCommit: t29.string,
  buildTimestamp: t29.string,
  capabilityVersion: t29.number,
  userAgent: t29.string,
  platform: t29.string,
  language: t29.string,
  timezone: t29.string,
  viewport: t29.type({ w: t29.number, h: t29.number }),
  dpr: t29.number,
  standalone: t29.boolean,
  online: t29.boolean,
  touch: t29.boolean
});
var issueReportAttachments = t29.partial({
  screenshot: t29.type({
    path: t29.string,
    bytes: t29.number,
    w: t29.number,
    h: t29.number
  }),
  state: t29.type({ path: t29.string, bytes: t29.number })
});
var issueReportStatusNotes = {
  statusNote: nullable(t29.string),
  branch: nullable(t29.string),
  prUrl: nullable(t29.string)
};
var IssueReportCodec = t29.intersection([
  t29.type({
    id: t29.string,
    createdAt: t29.string,
    reporterUid: t29.string,
    description: t29.string,
    target: issueReportTarget,
    location: issueReportLocation,
    status: issueReportStatus
  }),
  t29.partial({
    reporterUsername: nullable(t29.string),
    reporterIsOwner: nullable(t29.boolean),
    client: nullable(issueReportClient),
    attachments: nullable(issueReportAttachments),
    ...issueReportStatusNotes,
    statusUpdatedAt: nullable(t29.string),
    statusUpdatedBy: nullable(t29.string)
  })
]);
var IssueReportStatusUpdateCodec = t29.intersection([
  t29.type({
    status: issueReportStatus,
    statusUpdatedAt: t29.string,
    statusUpdatedBy: t29.string
  }),
  t29.partial(issueReportStatusNotes)
]);
var FORWARD_TRANSITIONS = {
  new: ["claimed"],
  claimed: ["fixed-draft"],
  "fixed-draft": ["published"],
  published: [],
  closed: []
};
var isLegalStatusTransition = (from, to) => from !== "closed" && (to === "closed" || FORWARD_TRANSITIONS[from].includes(to));
var ISSUE_REPORT_ID_PREFIX = "IssueReport-";
var ISSUE_REPORT_ID_LENGTH = 12;
var ISSUE_REPORT_ID_RE = /^IssueReport-[A-Za-z0-9]{12}$/;
var newIssueReportId = (generateId2) => `${ISSUE_REPORT_ID_PREFIX}${generateId2(ISSUE_REPORT_ID_LENGTH)}`;
var formatIssueReportRef = (r) => `${r.owner}/${r.slug}/${r.reportId}`;
var parseIssueReportRef = (s) => {
  const parts = s.split("/");
  if (parts.length !== 3) return void 0;
  const [owner, slug, reportId] = parts;
  if (!owner || !slug || !ISSUE_REPORT_ID_RE.test(reportId)) return void 0;
  return { owner, slug, reportId };
};
var issueReportsNodePath = (owner, slug) => `orgiggly/orgs/${owner}/${slug}/issueReports`;
var issueReportDbPath = (r) => `${issueReportsNodePath(r.owner, r.slug)}/${r.reportId}`;
var issueReportStoragePath = (r, file) => `issue-reports/${r.owner}/${r.slug}/${r.reportId}/${file}`;
var isOwnIssueReportAttachmentPath = (report, file, path) => {
  const [owner, slug, ...rest] = report.target.qualifiedOrgId.split("/");
  if (!owner || !slug || rest.length > 0) return false;
  return path === issueReportStoragePath({ owner, slug, reportId: report.id }, file);
};
var issueReportSiteUrl = (origin, owner, slug) => `${origin}/issues/${owner}~${slug}`;
var parseIssuesVariant = (variant) => {
  if (!variant) return { kind: "mine" };
  if (!variant.includes("~")) return { kind: "user", username: variant };
  const parts = variant.split("~");
  const [owner, slug] = parts;
  return parts.length === 2 && owner && slug ? { kind: "org", owner, slug } : { kind: "mine" };
};

// src/utils/storageUrl.ts
var DEFAULT_STORAGE_ORIGIN = "https://firebasestorage.googleapis.com";
var storageDownloadUrl = (bucket, path, origin = DEFAULT_STORAGE_ORIGIN, version) => {
  const encoded = encodeURIComponent(path);
  const suffix = version ? `&v=${encodeURIComponent(version)}` : "";
  return `${origin}/v0/b/${bucket}/o/${encoded}?alt=media${suffix}`;
};

// src/utils/orgLogo.ts
var picsumLogoUrl = (seed, width, height) => `https://picsum.photos/seed/${seed.replace(/\//g, "")}/${width}/${height}`;
var LOGO_FILENAME = "logo.webp";
var orgLogoStoragePath = (qualifiedOrgId) => `public/${qualifiedOrgId}/${LOGO_FILENAME}`;
var orgLogoUrl = (bucket, qualifiedOrgId, origin = DEFAULT_STORAGE_ORIGIN) => storageDownloadUrl(bucket, orgLogoStoragePath(qualifiedOrgId), origin);

// src/utils/orgCard.ts
var orgCardPath = (orgId) => `orgiggly/orgCards/${orgId}`;
var PRIVATE_CONTAINER = "__private__";
var isPlainObject6 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var asString = (v) => typeof v === "string" && v.length > 0 ? v : void 0;
var orgSlugOf = (orgId) => {
  var _a2;
  return (_a2 = orgId.split("/").pop()) != null ? _a2 : orgId;
};
var resolveOrgCardLabel = (orgId, appLabel, displayName4) => {
  var _a2;
  return (_a2 = appLabel != null ? appLabel : displayName4) != null ? _a2 : orgSlugOf(orgId);
};
var appLabelOf = (config) => {
  if (!isPlainObject6(config)) return void 0;
  const appConfig3 = config.appConfig;
  if (!isPlainObject6(appConfig3)) return void 0;
  if (Array.isArray(appConfig3.data)) {
    const entry = appConfig3.data.find(
      (e) => isPlainObject6(e) && isPlainObject6(e.value) && e.value.type === "app"
    );
    return isPlainObject6(entry) && isPlainObject6(entry.value) ? asString(entry.value.label) : void 0;
  }
  return appConfig3.type === "app" ? asString(appConfig3.label) : void 0;
};
var privateOrgIdsOf = (orgIndex) => {
  if (!isPlainObject6(orgIndex) || !isPlainObject6(orgIndex.containers)) {
    return [];
  }
  const container = orgIndex.containers[PRIVATE_CONTAINER];
  return isPlainObject6(container) && Array.isArray(container.orgIds) ? container.orgIds.filter((id) => typeof id === "string") : [];
};
var isPrivateInOrgIndex = (orgIndex, orgId) => privateOrgIdsOf(orgIndex).includes(orgId);
var buildOrgCard = (facts) => {
  const displayName4 = asString(facts.displayName);
  const description = asString(facts.description);
  const creatorUid = asString(facts.creatorUid);
  const createdAt = asString(facts.createdAt);
  const lastPublished = asString(facts.lastPublished);
  const listed = !facts.orgId.startsWith("guest/") && !isPrivateInOrgIndex(facts.ownerOrgIndex, facts.orgId);
  return {
    label: resolveOrgCardLabel(facts.orgId, facts.appLabel, displayName4),
    slug: orgSlugOf(facts.orgId),
    ...description ? { description } : {},
    ...creatorUid ? { creatorUid } : {},
    ...createdAt ? { createdAt } : {},
    ...lastPublished ? { lastPublished } : {},
    thumbnail: orgLogoUrl(facts.bucketName, facts.orgId),
    listed,
    ...facts.appLabel ? { appLabel: facts.appLabel } : {},
    ...displayName4 ? { displayName: displayName4 } : {}
  };
};
var OPTIONAL_FIELDS = [
  "description",
  "creatorUid",
  "createdAt",
  "lastPublished",
  "appLabel",
  "displayName"
];
var orgCardUpdate = (card) => {
  const out = { ...card };
  for (const key of OPTIONAL_FIELDS) {
    if (out[key] === void 0) out[key] = null;
  }
  return out;
};

// src/utils/orgFeed.ts
var FEED_LATEST_CAP = 50;
var RECENTLY_ACTIVE_DAYS = 7;
var RECENTLY_ACTIVE_CAP = 500;
var TRENDING_CAP = 50;
var FEED_EXCLUDED_PREFIXES = ["guest/", "lab/", "mock/"];
var FEED_LATEST_PATH = "orgiggly/feed/latest";
var FEED_RECENTLY_ACTIVE_PATH = "orgiggly/feed/recentlyActive";
var FEED_TRENDING_PATH = "orgiggly/feed/trending";
var HOME_KICKED_PATH = "orgiggly/moderation/homeKicked";
var DAY_MS = 24 * 60 * 60 * 1e3;
var isPlainObject7 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var orgFeedKey = (orgId) => orgId.replace("/", "~");
var orgIdFromFeedKey = (key) => key.replace("~", "/");
var publishActivityKey = (orgId) => `publish_${orgFeedKey(orgId)}`;
var kickedOrgIdsOf = (stored) => new Set(isPlainObject7(stored) ? Object.keys(stored).map(orgIdFromFeedKey) : []);
var isFeedEligible = (orgId, listed) => listed && !FEED_EXCLUDED_PREFIXES.some((p) => orgId.startsWith(p));
var feedAtOf = (lastPublished, fallback) => {
  const at = typeof lastPublished === "string" ? Date.parse(lastPublished) : NaN;
  return Number.isFinite(at) ? at : fallback;
};
var readFeedLatest = (stored) => {
  const items = Array.isArray(stored) ? stored : isPlainObject7(stored) ? Object.values(stored) : [];
  return items.filter(
    (e) => isPlainObject7(e) && typeof e.orgId === "string" && typeof e.at === "number"
  );
};
var readFeedTrending = (stored) => {
  const items = Array.isArray(stored) ? stored : isPlainObject7(stored) ? Object.values(stored) : [];
  return items.filter(
    (e) => isPlainObject7(e) && typeof e.orgId === "string" && typeof e.at === "number" && typeof e.score === "number"
  );
};
var upsertFeedLatest = (stored, entry, cap = FEED_LATEST_CAP) => {
  const rest = readFeedLatest(stored).filter((e) => e.orgId !== entry.orgId);
  const i = rest.findIndex((e) => e.at <= entry.at);
  const next = [...rest];
  next.splice(i === -1 ? rest.length : i, 0, entry);
  return next.slice(0, cap);
};
var removeFromFeedLatest = (stored, orgIds) => readFeedLatest(stored).filter((e) => !orgIds.includes(e.orgId));
var removeFromFeedTrending = (stored, orgIds) => readFeedTrending(stored).filter((e) => !orgIds.includes(e.orgId));
var sameFeedLatest = (a, b2) => JSON.stringify(readFeedLatest(a)) === JSON.stringify(b2);
var buildFeedLatest = (cards, cap = FEED_LATEST_CAP) => cards.filter((c) => isFeedEligible(c.orgId, c.listed)).map((c) => ({ orgId: c.orgId, at: feedAtOf(c.lastPublished, NaN) })).filter((e) => Number.isFinite(e.at)).sort((a, b2) => b2.at - a.at || a.orgId.localeCompare(b2.orgId)).slice(0, cap);
var touchRecentlyActive = (stored, now, orgId, at = now, cap = RECENTLY_ACTIVE_CAP) => {
  var _a2;
  const oldest = now - RECENTLY_ACTIVE_DAYS * DAY_MS;
  const entries = Object.entries(isPlainObject7(stored) ? stored : {}).filter(
    (kv) => typeof kv[1] === "number" && kv[1] >= oldest
  );
  const map2 = new Map(entries);
  if (orgId && at >= oldest) {
    const key = orgFeedKey(orgId);
    map2.set(key, Math.max(at, (_a2 = map2.get(key)) != null ? _a2 : 0));
  }
  return Object.fromEntries(
    [...map2].sort((a, b2) => b2[1] - a[1]).slice(0, cap)
  );
};

// src/utils/widgetUrl.ts
var WIDGET_FILENAME = "index.html";
var OWNED_WIDGET_CONTENT_TYPE = "text/plain; charset=utf-8";
var OWNED_WIDGET_CONTENT_DISPOSITION = "attachment";
var WIDGET_DEPLOY_REGION = "europe-west2";
var widgetStoragePath = (name, owner) => owner === void 0 ? `public/widgets/${name}/${WIDGET_FILENAME}` : `public/widgets/${owner}/${name}/${WIDGET_FILENAME}`;
var widgetUrl = (bucket, name, origin = DEFAULT_STORAGE_ORIGIN, owner) => storageDownloadUrl(bucket, widgetStoragePath(name, owner), origin);

// src/utils/mediaIds.ts
var SPOTIFY_RE = /^https:\/\/open\.spotify\.com\/(track|album)\/([a-zA-Z0-9]{22})(?:[?#].*)?$/;
var YOUTUBE_EMBED_RE = /^https:\/\/www\.youtube\.com\/embed\/([a-zA-Z0-9_-]{11})(?:[?#].*)?$/;
var toSpotifyId = (url, kind) => {
  const m = url.match(SPOTIFY_RE);
  if (!m) return null;
  if (m[1] !== kind) return null;
  return m[2];
};
var toYouTubeIdFromEmbed = (url) => {
  const m = url.match(YOUTUBE_EMBED_RE);
  return m ? m[1] : null;
};

// src/utils/cliToken.ts
var CLI_TOKEN_PREFIX = "og1.";
var utf8ToBinary = (text) => encodeURIComponent(text).replace(
  /%([0-9A-F]{2})/g,
  (_, hex) => String.fromCharCode(parseInt(hex, 16))
);
var binaryToUtf8 = (binary2) => decodeURIComponent(
  Array.from(
    binary2,
    (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`
  ).join("")
);
var toBase64Url = (text) => btoa(utf8ToBinary(text)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
var fromBase64Url = (encoded) => {
  const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
  return binaryToUtf8(atob(b64 + "=".repeat((4 - b64.length % 4) % 4)));
};
var isRecord12 = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var isCliTokenPayload = (value) => isRecord12(value) && value.v === 1 && typeof value.uid === "string" && value.uid.length > 0 && (typeof value.email === "string" || value.email === null) && typeof value.refreshToken === "string" && value.refreshToken.length > 0;
var encodeCliToken = ({ uid, email, refreshToken }) => CLI_TOKEN_PREFIX + toBase64Url(JSON.stringify({ v: 1, uid, email, refreshToken }));
var decodeCliToken = (raw) => {
  const value = raw.trim().replace(/^["']|["']$/g, "");
  if (!value.startsWith(CLI_TOKEN_PREFIX)) return null;
  try {
    const parsed = JSON.parse(
      fromBase64Url(value.slice(CLI_TOKEN_PREFIX.length))
    );
    if (!isCliTokenPayload(parsed)) return null;
    return {
      uid: parsed.uid,
      email: parsed.email,
      refreshToken: parsed.refreshToken
    };
  } catch {
    return null;
  }
};

// src/utils/defaultAvatar.ts
var defaultAvatarUrl = (seed) => `https://orgiggly.com/api/avatar?seed=${encodeURIComponent(seed)}`;
export {
  AI_MODELS,
  APP,
  APP_CONTEXT_NAMES,
  APP_NODE,
  ARM_HORIZON_MS,
  AT_REF_PATTERN,
  AUDIO_EXTS,
  AUTO_PLACE,
  AXIS_X,
  AXIS_Y,
  AppContext,
  AppDataProvider,
  AppProvider,
  BARE_BUILTIN_NAMES,
  BOOLEAN_INPUT,
  BOT_ACTOR_FORMULA_FIELDS2 as BOT_ACTOR_FORMULA_FIELDS,
  BULK_ACTION,
  BUTTON,
  CACHE_READ_MULTIPLIER,
  CACHE_WRITE_1H_MULTIPLIER,
  CACHE_WRITE_5M_MULTIPLIER,
  CALLABLE_BUTTON,
  CALLABLE_FUNCTION_NAMES,
  CLEAR_SUBTREE,
  CLEAR_VALUE,
  CLICK_DISPATCH_ORDER,
  CLICK_PATTERNS,
  CLI_TOKEN_PREFIX,
  COLUMNS,
  CONST,
  CONTENT,
  CONTENT_ACTION_SCOPE_VARS,
  CREDIT_SYMBOL,
  CROSS_CUTTING_PITFALL_TARGETS,
  CSS_AT_RULES,
  CURRENT_REQUIREMENTS_SPEC_VERSION,
  CUSTOM_ENTITY_THRESHOLD,
  CursorView,
  DATA_VOLUME_KEYWORDS,
  DEFAULT_APP_DEF,
  DEFAULT_APP_INSTANCE,
  DEFAULT_MAX_ATTEMPTS,
  DEFAULT_ORDER_FIELD,
  DEFAULT_REPO,
  DEFAULT_STORAGE_ORIGIN,
  DEGRADED_NODE_ATTR,
  DEGRADED_NODE_HTML,
  DEMO_PROMPTS,
  DebouncedPersistence,
  EDIT_PROMPT_NAME,
  EMPTY_ARRAY,
  EMPTY_LIST_BODY,
  ERROR_KIND_TO_NODE_TYPE,
  FEATURE_SECTIONS,
  FEATURE_THRESHOLD,
  FEED_EXCLUDED_PREFIXES,
  FEED_LATEST_CAP,
  FEED_LATEST_PATH,
  FEED_RECENTLY_ACTIVE_PATH,
  FEED_TRENDING_PATH,
  FIELD_TYPE_BUILTINS,
  FIELD_TYPE_BUILTIN_VALUES,
  FILE_INPUT,
  FLOW,
  FLOW_STEP,
  FORM,
  FORMULA_PREFIX,
  FUNCTION,
  ForEach,
  ForEachChild,
  ForEachChildTreeNode,
  FormContextProvider,
  FormWrapper,
  GAME_MECHANIC_KEYWORDS,
  GRAPH_PROMPT_NAME,
  GRID,
  HOME_KICKED_PATH,
  ILLEGAL_KEY_CHARS,
  IMAGE_MODELS,
  INIT_APP,
  INIT_APP_INSTANCE,
  INPUT_SYNC,
  INSERT_SLOT,
  ISSUE_REPORT_ID_RE,
  ISSUE_REPORT_STATUSES,
  IssueReportCodec,
  IssueReportStatusUpdateCodec,
  LANE_HEIGHT_PX,
  LANE_STRIP_GUTTER_PX,
  LEGACY_AUTH_MODE,
  LEGACY_HELPER_PROPS,
  LEGACY_NODES,
  LEGACY_PROPS,
  LEGACY_VIDEO_ROOM,
  LOAD_MORE,
  LOGO_FILENAME,
  MAX_VISION_IMAGES,
  MEDIA_ROOM,
  MIRROR_BOT_ACTOR_FORMULA_FIELDS,
  MODEL_ALIASES,
  MODEL_ID_HAIKU,
  MODEL_ID_OPUS,
  MODEL_ID_SONNET,
  MOVE_NODE,
  MOVE_SLOT,
  NODE_SCHEMA_INDEX,
  NODE_TYPE_ALIASES,
  NON_STANDARD_INTERACTION_KEYWORDS,
  NOTE_ASSUMPTION_TOOL,
  NUMBER_INPUT,
  ORG_LIMITS,
  OUTLINE_NOTATION,
  OUTLINE_READING,
  OUTLINE_SLOTS,
  OWNED_WIDGET_CONTENT_DISPOSITION,
  OWNED_WIDGET_CONTENT_TYPE,
  PAST_REARM_FLOOR_MS,
  PLUGIN_LEAF,
  PLUGIN_NAME_PATTERN,
  POP_CURSOR,
  PRIVATE_READ_LIMIT,
  PUSH_CURSOR,
  PersistenceKeyProvider,
  PersistenceProvider,
  PointerContext,
  PointerProvider,
  PointerWrapper,
  RECENTLY_ACTIVE_CAP,
  RECENTLY_ACTIVE_DAYS,
  RECORD_META_AUDIT,
  RECORD_META_AUDIT_UID,
  RECORD_META_FIELDS,
  RECORD_META_IDENTITY,
  RECORD_META_PLACEMENT,
  RECORD_META_SYSTEM,
  RECORD_META_TIERS,
  REMOVE_NODE,
  REPEAT,
  REPO_READ_META,
  RESERVED_CONTAINER_IDS,
  RESERVED_CONTAINER_PRIVATE,
  RESERVED_CONTAINER_ROOT,
  RESERVED_CONTAINER_TRASH,
  SCREEN,
  SEARCH_IMAGES_TOOL,
  SEARCH_VIDEOS_TOOL,
  SEARCH_WEB_TOOL,
  SELECT_INPUT,
  SET_CURSOR,
  SET_PAGE,
  SHOWCASE_FEATURES,
  SHOWCASE_INDEX,
  SIM_EPOCH,
  SPATIAL_DEFAULT_MIN_CELL_WIDTH,
  STAMPED_META_FIELDS,
  SUBSECTION,
  SWAP_SLOT,
  SafeHtml,
  StateTreeView,
  TEMPORAL_INPUT,
  TEXT_INPUT,
  TREE,
  TRENDING_CAP,
  TimerLoopError,
  TransitionLoopError,
  UPDATE_CONTEXT_DATA,
  UPDATE_DEF,
  UPDATE_REPO,
  UPDATE_VALUE,
  VARIABLE,
  VIDEO_EXTS,
  ValueOrFormulaError,
  WELL_KNOWN_TYPES,
  WIDGET_CONNECTION_ALLOWLIST,
  WIDGET_DEPLOY_REGION,
  WIDGET_DESCRIPTOR_MARKER,
  WIDGET_FILENAME,
  WIDGET_NAME_PATTERN,
  WIDGET_OWNER_MIN_CLIENT_VERSION,
  WIDGET_OWNER_PATTERN,
  actionConfirm,
  actionCreators,
  adminOnlyTypes,
  aiChatStep,
  aiQueryStep,
  answerViews,
  anthropicRequestExtras,
  app,
  appLabelOf,
  appNode,
  appNodeVariant,
  applyOps,
  armRecordTimers,
  armTimers,
  armedMarkerPath,
  arrangeIndexesInto2D,
  arrangeInto2D,
  asTemplateFormula,
  assertFormulaEvaluationError,
  assertNotNullOrUndefined,
  assignTarget,
  audioGenerationModel,
  audioGenerationStep,
  authMode,
  axis,
  b,
  basicType,
  basicTypes,
  blobsToDelete,
  booleanInput,
  booleanRecord,
  botActor,
  buildArchiveCopy,
  buildBotActorIndex,
  buildChatSystemPrompt,
  buildExistingHtmlSection,
  buildExistingOrgSection,
  buildFeedLatest,
  buildHtmlSystemPromptFromTemplate,
  buildInspirationOrgSection,
  buildLastArm,
  buildLastFire,
  buildLastOutcome,
  buildMarketplaceMenuSection,
  buildMirror,
  buildObjectsSystemPrompt,
  buildOrgCard,
  buildOrgSystemPromptV2,
  buildPluginSystemPrompt,
  buildPrivateUpdates,
  buildRecordForest,
  buildRequirementsSpec,
  buildRuleContext,
  buildShowcaseMenuSection,
  buildTimerRuleContext,
  buildTreeStructure,
  buildValidationTelemetry,
  buildVariablesSection,
  bulkActions,
  byNodeType,
  cacheRates,
  calculateCost,
  calculateImageCost,
  callableButton,
  callableButtonFunction,
  callableButtonVariant,
  callableSource,
  camelCaseToLabel,
  canCreateOrg,
  canonicalOrgTree,
  castToString,
  categoryOf,
  checkEntityRenames,
  childrenOf,
  clampPlace,
  cloneOrgIndex,
  cloneTemplate,
  cloneTemplateTree,
  codecPropNames,
  coerceToString,
  collectFeatureFolders,
  collectLibraryFolders,
  collectNodeNames,
  collectRealtimeTypes,
  collectStoragePaths,
  collectionRecords,
  commitGuarded,
  compactJoin,
  compareValues,
  compileOutline,
  completeField,
  computeRiskScore,
  configNode,
  configureLogging,
  connectionProvider,
  constBuilder as const,
  containerForOrg,
  containerStyle,
  content,
  contentAction,
  createActionApi,
  createAppInstance,
  createAppStateHook,
  createAppWrapper,
  createBooleanStateHook,
  createContextTools,
  createDb,
  createDbForObjEditing,
  createDesignDef,
  createEditor,
  createFormulaError,
  createHtmlPluginFn,
  createHtmlPluginFns,
  createImplicitSubmitNodes,
  createLogger,
  createMutableContext,
  createNamespacedPersistence,
  createOrgPackApi,
  createPluginParamForm,
  createPluginPropsHook,
  createPointerSelHook,
  createProvenance,
  createRealtimeStore,
  createSelectPropDef,
  createSelectors,
  createShiftOptions,
  createShiftSelector,
  createToggle,
  createTransitionSim,
  createTraverseOptions,
  createTreeNode,
  createTreeNodeTypes,
  createTreeNodeWithChildren,
  createTypedComputedValueSelector,
  createTypedOutputSelector,
  createTypedPluginPropsSelector,
  createTypedValueSelector,
  createTypedValueSelectorWithStringCoercion,
  cutFeature,
  danglingDataTypes,
  dataOptions,
  dataPartitionOf,
  dataScope,
  dataSourceType,
  dateCompare,
  dateFormat,
  dateFrom,
  decideMoveAck,
  decode,
  decodeCliToken,
  decodeUnknown,
  decodeWithStringCoercion,
  defaultAvatarUrl,
  defaultMatches,
  defaultShiftOptions,
  defaultTraverseOptions,
  deriveTier,
  describeEvents,
  describeFirings,
  designToAppDef,
  diag,
  dimension,
  discardEntry,
  discoverParentRefField,
  displayText,
  dragAndDropState,
  drainOutbox,
  driftedFeatureFolders,
  dropOpenEmpty,
  dropPosition,
  dropZone,
  dynamicPluginParam,
  editOrgGraph,
  editState,
  effectiveOrgLimit,
  emptyLookupContext,
  emptyObj,
  emptyOrgIndex,
  emptyReconcileSummary,
  emptySeedSlots,
  encodeCliToken,
  enqueue,
  error,
  errorToMessage,
  evalAndDecode,
  evalExpr,
  evalGuards,
  evalRuleField,
  evalScriptAsync,
  evalTargetId,
  evaluate,
  evaluateAndThrow,
  evaluatePropAndDecode,
  evaluateScriptInState,
  evaluateSpatialMember,
  expandedKey,
  extractBotActors,
  extractConstNodes,
  extractFunctionNodes,
  extractJsonResponseSimple,
  extractObjectTypes,
  extractTransitionRules,
  featureBody,
  featureBundle,
  featureChildrenHash,
  featureDriftFinding,
  featureFolderHash,
  featureFolderId,
  featureFolderName,
  featureInstallParams,
  featureManifest,
  featureParams,
  featureProvides,
  featureRef,
  featureRequires,
  featureScope,
  featureSeam,
  featureSeamPin,
  featureShell,
  featureSlot,
  featureSlotKind,
  featureSlotPin,
  feedAtOf,
  fetchDataStep,
  fieldExpectation,
  fieldTypeBuiltin,
  fieldTypeBuiltinLabel,
  fieldTypeToTypeString,
  fileInput,
  fileUploadItemValue,
  fileUploadValue,
  fileUploadValueOrArray,
  fileVariant,
  findFeatureFolders,
  findNavRegistry,
  findNodeUnits,
  findWellKnownHost,
  fireTimer,
  flow2 as flow,
  flowStep,
  flowStepVariant,
  folder,
  form,
  formConfigOptions,
  formatCostUSD,
  formatCredits,
  formatDate,
  formatDate2,
  formatExampleOrg,
  formatFieldType,
  formatFill,
  formatFriendly,
  formatInspirationOrg,
  formatIssueReportRef,
  formatParseError,
  formatPeriod,
  formatRequestedExample,
  formatRequirementsBlock,
  formatShowcaseMatches,
  functionBuilder as function,
  fuzzyMatchAnswer,
  fuzzyMatchScore,
  gateErrors,
  gateRepairPrompt,
  generate,
  generateId,
  generateNameFormula,
  generateObjects,
  generateOrg,
  generateOrgGraph,
  generatePlugin,
  getBestNameField,
  getError,
  getErrorMessage,
  getErrorPath,
  getErrors,
  getEvalErrorMessage,
  getInputSource,
  getLeft,
  getLiteralUnionValues,
  getModel,
  getModelsByProvider,
  getMultiline,
  getNamespaceAndPointerKey,
  getRaw,
  getRight,
  getRightOr,
  getSource,
  getTemplate,
  getToolUseBlocks,
  getTreeVarSuffix,
  getUnwrappedProps,
  graftFeature,
  grantedConnections,
  graphSection,
  gridPlacementStyle,
  hardDeleteClosure,
  hasLapsedSubscription,
  hasPendingWrite,
  htmlPlugin,
  identifiersIn,
  imageFormat,
  imageGenerationModel,
  imageGenerationStep,
  imageOptions,
  initWith,
  initialisedNodeValue,
  inputSize,
  insertListItem,
  installFeature,
  installOptions,
  installedPins,
  isAdmin,
  isAnyError,
  isArrayExpression,
  isArrowExpression,
  isAudioExt,
  isBinaryExpression,
  isBrowserHref,
  isCallExpression,
  isClientTooOld,
  isClosure,
  isCollectionType,
  isCompositeNodeType,
  isCompound,
  isConditionalExpression,
  isDataQuery,
  isError,
  isFaithfulRename,
  isFeedEligible,
  isFieldTypeBuiltin,
  isFormulaEvaluationError,
  isFormulaParseError,
  isGenerateError,
  isGenerateObjectsError,
  isGenerateOrgError,
  isGeneratePluginError,
  isGenerationBlocking,
  isGridSpanNode,
  isIdentifier,
  isInputFieldType,
  isLeft,
  isLegalStatusTransition,
  isLetExpression,
  isListType,
  isLiteral,
  isMatchExpression,
  isMemberExpression,
  isNaiveDateTime,
  isNavRoute,
  isNonImageExt,
  isObjectExpression,
  isOrgFunction,
  isOrgUploadPath,
  isOwnIssueReportAttachmentPath,
  isPaidOrComped,
  isPersistErrorType,
  isPlainDate,
  isPrivateInOrgIndex,
  isProseQuestion,
  isRepoObject,
  isReservedContainer,
  isRight,
  isSafeHtml,
  isScope,
  isSequenceExpression,
  isSpreadElement,
  isTaggedTemplateExpression,
  isTemplateLiteral,
  isThisExpression,
  isTimerTaskPayload,
  isTrue,
  isUnaryExpression,
  isVideoExt,
  issueReportDbPath,
  issueReportSiteUrl,
  issueReportStoragePath,
  issueReportsNodePath,
  jsep,
  kickedOrgIdsOf,
  kickoffPrompt,
  kindLabel,
  labelToVariableName,
  laneCountsByRow,
  laneSegmentStyle,
  laneStripStyle,
  lastBreadcrumbSegment,
  laxEnum,
  layout,
  layoutSpanSegments,
  left,
  letScope,
  levenshteinDistance,
  libraryChildrenHash,
  libraryRef,
  list,
  listTemplates,
  loadPrivateRecords,
  log,
  makeAskUserTool,
  makeWidgetDescriptor,
  many,
  markFailed,
  markSent,
  mediaMode,
  mediaRoom,
  missingWellKnownFields,
  mode,
  moveNodeType,
  moveOrgInIndex,
  nameOf3 as nameOf,
  navContribution,
  navKind,
  navigationConfig,
  navigationStyle,
  needsTemplateWrapper,
  newIssueReportId,
  nextOccurrence,
  nextPending,
  nextRetryDelayMs,
  node,
  nodeBreadcrumb,
  nodeDef,
  nodeOptionsCodec,
  nodeType,
  normalizeForComparison,
  normalizePrivateReadKeys,
  normalizeRecordShape,
  normalizeRepoObjects,
  notFoundError,
  nudgePrompt,
  numberInput,
  numberVariant,
  objInstance,
  objectType,
  objectTypeField,
  objectTypeToMockInstances,
  objectTypeToTypeDescription,
  objectTypeWriteAccess,
  objectTypesIn,
  optional,
  optionalBoolean,
  optionalNumber,
  optionalNumberVariant,
  optionalString,
  orderFolders,
  orgCardPath,
  orgCardUpdate,
  orgData,
  orgDataKeys,
  orgFeedKey,
  orgIdFromFeedKey,
  orgIndexCodec,
  orgIndexContainer,
  orgIndexFolder,
  orgIndexPath,
  orgLimitForTier,
  orgLogoStoragePath,
  orgLogoUrl,
  orgOutline,
  orgPublicPrefix,
  orgSlugOf,
  orgsToKeepLive,
  outstandingFills,
  parentCycles,
  parentLinkOf,
  parentLinkPatch,
  parentRelation,
  parseExpressionWithMeta,
  parseFieldType,
  parseFormula,
  parseIssueReportRef,
  parseIssuesVariant,
  parseJson,
  parseLibrarySource,
  parseOps,
  parseOutline,
  parsePropDef,
  parseQueue,
  parseStreamingJson,
  parseTypeKey,
  parseUsernames,
  partialField,
  pickerIdiom,
  pickerOption,
  pickerVariant,
  picsumLogoUrl,
  place,
  planHardDelete,
  planOrgLocks,
  planWeave,
  pluginLeaf,
  pluginParam,
  pluginParameter,
  pluginRuntime,
  point,
  prependFormulaPrefix,
  privateOrgIdsOf,
  promptFromRequirements,
  proseAnswerText,
  provide,
  pruneForRtdb,
  publishActivityKey,
  publishDataStep,
  pushRule,
  pushScope,
  rankShowcase,
  rawOrgDataToTree,
  reachableConsts,
  reachableConstsFor,
  reachableFunctions,
  reachableFunctionsFor,
  readFeedLatest,
  readFeedTrending,
  readParentId,
  reconcileTimers,
  record5 as record,
  recordMetaField,
  reducer,
  referencesRecords,
  reindentOutline,
  removeFromFeedLatest,
  removeFromFeedTrending,
  renameNavTarget,
  renameNodesInFormula,
  renameNodesInTree,
  renameTypesInConfig,
  renameTypesInFormula,
  renderOrgUnfurl,
  renderReply,
  renderScreenModal,
  renderValidationErrors,
  reorderWrites,
  repairDuplicateIds,
  repeat,
  requiredType,
  requirementsSpec,
  requirementsSpecInput,
  resolveImageSources,
  resolveModelAlias,
  resolveNodeType,
  resolveOrgCardLabel,
  resolveOutlinePath,
  retryEntry,
  right,
  riskAssessment,
  riskFactorResult,
  riskStrategy,
  runDataQuery,
  runGraphFill,
  runStructureStage,
  runTransition,
  safeParse,
  safeSelectByNodeId,
  safeSelectPointer,
  safeSelectScope,
  safeStringify,
  sameFeedLatest,
  sanitizeAppInstance,
  scalarOf,
  screen,
  scriptStep,
  seedRowCount,
  seededId,
  selectAiChatStepNode,
  selectAiChatStepProps,
  selectAiQueryStepNode,
  selectAiQueryStepProps,
  selectAllErrors,
  selectAllowedChildTypeNames,
  selectAllowedChildTypes,
  selectAppConfigNodes,
  selectAppContext,
  selectAppDef,
  selectAppInstance,
  selectAppNodeNode,
  selectAppProps,
  selectAppState,
  selectAudioGenerationStepNode,
  selectAudioGenerationStepProps,
  selectBestNameField,
  selectBestPointerKey,
  selectBooleanInputNode,
  selectBooleanInputProps,
  selectByNodeId,
  selectByUniqueName,
  selectCalculatedPluginProps,
  selectCallableButtonNode,
  selectCallableButtonProps,
  selectCallableDataNodes,
  selectChildIterationPointers,
  selectChildNodePointers,
  selectChildPointers,
  selectChildPointersToRender,
  selectChildTreeNodes,
  selectClosestAncestor,
  selectClosestSubmitContext,
  selectCommonInputNode,
  selectCommonInputProps,
  selectCommonNode,
  selectCommonPicker,
  selectCommonPickerProps,
  selectCommonProps,
  selectCompute,
  selectComputedValue,
  selectConstNode,
  selectConstProps,
  selectContainerStyle,
  selectContentNode,
  selectContentProps,
  selectCurrentNavRoute,
  selectCurrentPointer,
  selectCurrentScreenHidesHeader,
  selectCursor,
  selectCursorArgs,
  selectCursorToPop,
  selectData,
  selectDataByPointer,
  selectDataOptions,
  selectDatasourceObjTypes,
  selectDb,
  selectDeclaredPointerKey,
  selectDefaultIterationLabelFormula,
  selectDefaultLabel,
  selectDefaultPopTarget,
  selectDropOptions,
  selectDropZone,
  selectEvaluatedPlace,
  selectEvaluation,
  selectExpandedKey,
  selectFetchDataStepNode,
  selectFetchDataStepProps,
  selectFieldByName,
  selectFields,
  selectFieldsByName,
  selectFileInputNode,
  selectFileInputProps,
  selectFileUploadValue,
  selectFirstError,
  selectFirstErrorSafe,
  selectFirstTreeNode,
  selectFlowNode,
  selectFlowProps,
  selectFlowStepNode,
  selectFlowStepProps,
  selectFolderProps,
  selectFormConfig,
  selectFormNode,
  selectFormProps,
  selectFunctionNode,
  selectFunctionProps,
  selectGridSpanChildren,
  selectGroup,
  selectGroups,
  selectHasChildTreeNodes,
  selectHasStoredValues,
  selectHidden,
  selectImageGenerationStepNode,
  selectImageGenerationStepProps,
  selectImageOutputOptions,
  selectInput,
  selectInputInErrorShiftOptions,
  selectIsDeleteAllowed,
  selectIsSameOrAncestorOf,
  selectLabel,
  selectLayout,
  selectLength,
  selectLengths,
  selectLengthsByPointer,
  selectMatchingNodes,
  selectMaxLength,
  selectMediaRoomNode,
  selectMediaRoomProps,
  selectMetaModel,
  selectMissingRequired,
  selectMultiPickerInputNode,
  selectMultiPickerInputProps,
  selectName,
  selectNameFormula,
  selectNavStack,
  selectNavStackArgs,
  selectNext,
  selectNextExpanded,
  selectNextNavRoute,
  selectNextNode,
  selectNode,
  selectNodes,
  selectNodesByName,
  selectNumberArrayInputNode,
  selectNumberArrayInputProps,
  selectNumberInputNode,
  selectNumberInputProps,
  selectNumberVariant,
  selectObjectInstances,
  selectObjectTypes,
  selectObjects,
  selectObjectsById,
  selectObjectsByType,
  selectOptionalAppInstance,
  selectOptionalBooleanValue,
  selectOptionalNumberArrayValue,
  selectOptionalNumberValue,
  selectOptionalObjectComputedValue,
  selectOptionalObjectValue,
  selectOptionalStringArrayValue,
  selectOptionalStringValue,
  selectOptionalTreeNodeValue,
  selectOrgData,
  selectOrgDataLenient,
  selectOrgUserByUsername,
  selectOutput,
  selectOutputObject,
  selectPageSize,
  selectPaginationInfo,
  selectParent,
  selectParentNavRoute,
  selectParentNodeDef,
  selectParentTreeNode,
  selectPassScreenKeys,
  selectPassStatus,
  selectPickerOptions,
  selectPickerValues,
  selectPluginByName,
  selectPluginName,
  selectPluginNames,
  selectPluginNode,
  selectPluginNodeTypes,
  selectPluginParams,
  selectPluginProps,
  selectPlugins,
  selectPluginsByName,
  selectPointer,
  selectPointer2,
  selectPointerNode,
  selectPrevious,
  selectPreviousExpanded,
  selectPreviousNavRoute,
  selectPreviousNode,
  selectPublishDataStepNode,
  selectPublishDataStepProps,
  selectRecordTreeNode,
  selectRecordTreeProps,
  selectRepeatPasses,
  selectRepeatProps,
  selectRepo,
  selectRequired,
  selectRootOutputObject,
  selectRootPointer,
  selectRunningSlotChildKeys,
  selectScope,
  selectScopeIndex,
  selectScopeKeyWithIndexAt,
  selectScopePath,
  selectScopedDatasourceObjTypes,
  selectScriptStepNode,
  selectScriptStepProps,
  selectShowsAnswerProgress,
  selectSiblings,
  selectSinglePickerInputNode,
  selectSinglePickerInputProps,
  selectSize,
  selectSpanSegments,
  selectSpatialGrid,
  selectSpatialItemPointers,
  selectSpatialSlots,
  selectStringArrayInputNode,
  selectStringArrayInputProps,
  selectStringValue,
  selectSubTreeTraverseOptions,
  selectSubmitContextObject,
  selectSubsectionNode,
  selectSubsectionProps,
  selectTabGroupProps,
  selectTabProps,
  selectTemporalInputNode,
  selectTemporalInputProps,
  selectTextInputNode,
  selectTextInputProps,
  selectTextVariant,
  selectTopLevelNavRoutes,
  selectTranscribeStepNode,
  selectTranscribeStepProps,
  selectTreeDepth,
  selectTreeNode,
  selectTreeNodeExpanded,
  selectTreeNodeIcon,
  selectTreeNodeIconColor,
  selectTreeNodeType,
  selectTreeNodeTypeName,
  selectTreeNodeValue,
  selectTreeProps,
  selectTypeByName,
  selectTypesByName,
  selectUncommittedFileUploadPointers,
  selectUncommittedFileUploads,
  selectUniqueKey,
  selectUniqueNodeNames,
  selectUser,
  selectUserByEmail,
  selectUserByName,
  selectUserNodes,
  selectUsers,
  selectUsersByEmail,
  selectUsersByName,
  selectValidation,
  selectValidationError,
  selectValue,
  selectValueKey,
  selectVariableNode,
  selectVariableProps,
  selectVisibleChildPointersToRender,
  selectVisibleInputErrors,
  selectVisibleInputTraverseOptions,
  serializationError,
  servedModelId,
  shift,
  shiftCollect,
  showcaseMenuFromIndex,
  sliceOutlineGrammar,
  slotBody,
  slotKind,
  smokeOrg,
  sortOptions,
  sortOrgsByAgeAsc,
  span,
  spanMode,
  spatialTemplate,
  splitCamelCase,
  staticPluginParam,
  storageDownloadUrl,
  storageError,
  storageObjToRepoObj,
  storagePathFromUrl,
  storageTypesOf,
  stringOrPickerOption,
  stringRecord,
  stripRepoMeta,
  structureEditPrompt,
  structureFixPrompt,
  structurePrompt,
  structureSection,
  subsection,
  subsectionVariant,
  success2 as success,
  summarize,
  summarizeErrors,
  tab,
  tabGroup,
  templateManifest,
  templateOverrides,
  templateParams,
  templateThemePreset,
  temporalInput,
  temporalVariant,
  textAnswer,
  textGenerationModel,
  textInput,
  textPricingKey,
  textRateCard,
  textTransform,
  textVariant,
  themeColors,
  themePreset,
  toArray,
  toDynamicContext,
  toHeadAndTail,
  toPlainDate,
  toResourceId,
  toSingleValue,
  toSpotifyId,
  toStemAndLeaf,
  toTypeName,
  toYouTubeIdFromEmbed,
  toZonedDateTime,
  today,
  touchRecentlyActive,
  transcribeModel,
  transcribeStep,
  transitionRule,
  transitionTrigger,
  transpose2DArray,
  tree,
  treeNodeOptions,
  treeNodeValue,
  treeToRawOrgData,
  triageStrategy,
  trimOrgConfigForExample,
  tryParseWidgetDescriptor,
  tryStripFormulaPrefix,
  typeKeyToAuthored,
  typeWriteAccess,
  unfence,
  uninstallFeature,
  unknownError,
  unknownRecord,
  unknownRecordArray,
  unwrapProps,
  updateObjects,
  updateValue2 as updateValue,
  upsertFeedLatest,
  useAiChatStepProps,
  useAiQueryStepProps,
  useAllowedChildTypes,
  useAppContext,
  useAppData,
  useAppProps,
  useAudioGenerationStepProps,
  useAutoFlush,
  useBooleanInputProps,
  useCalculatedPluginProps,
  useCallableButtonProps,
  useChildPointers,
  useChildTreeNodes,
  useClearValue,
  useCommonInputProps,
  useCommonProps,
  useConstProps,
  useContainerStyle,
  useContentProps,
  useCreateAppContext,
  useCreateDebouncedPersistence,
  useCreateNode,
  useCurrentNavRoute,
  useCurrentPointer,
  useCurrentScreenHidesHeader,
  useCursor,
  useCursorArgs,
  useDataOptions,
  useEvalFormula,
  useEvaluatedPlace,
  useFetchDataStepProps,
  useFileInputProps,
  useFirstError,
  useFirstTreeNode,
  useFlowProps,
  useFlowStepProps,
  useFolderProps,
  useFormContext,
  useFormProps,
  useFormulaEvaluator,
  useFunctionProps,
  useGridSpanChildren,
  useHasChildTreeNodes,
  useHasChildren,
  useImageGenerationStepProps,
  useImmediateAppContext,
  useImmediatePointerContext,
  useInsertListItem,
  useIsDeleteAllowed,
  useIsNodePropsError,
  useIsSelected,
  useLabel,
  useLayout,
  useLength,
  useLoadMore,
  useMediaRoomProps,
  useMultiPickerInputProps,
  useNavNext,
  useNavPop,
  useNavPrevious,
  useNavTo,
  useNavToParent,
  useNodeErrors,
  useNodeName,
  useNodeProps,
  useNodeType,
  useNumberArrayInputProps,
  useNumberInputProps,
  useNumberInputVariant,
  usePaginationInfo,
  usePersistence,
  usePersistenceKey,
  usePickerInputProps,
  usePluginProps,
  usePointer,
  usePointerContext,
  usePointerKey,
  usePointerSelector,
  usePublishDataStepProps,
  usePushCursor,
  useRecordTreeProps,
  useRemoveNode,
  useRepeatProps,
  useRootAppContext,
  useScriptStepProps,
  useSelectSelf,
  useSetCursor,
  useSetPage,
  useSetTreeNodeExpanded,
  useSetTreeNodeValue,
  useSetValue,
  useSinglePickerInputProps,
  useSpanSegments,
  useSpatialGrid,
  useSpatialItemPointers,
  useStringArrayInputProps,
  useSubsectionProps,
  useTabGroupProps,
  useTabProps,
  useTemporalInputProps,
  useTextInputProps,
  useTopLevelNavRoutes,
  useTranscribeStepProps,
  useTreeDepth,
  useTreeNavNext,
  useTreeNavPrevious,
  useTreeNodeIcon,
  useTreeNodeIconColor,
  useTreeNodeTypeName,
  useTreeProps,
  useUncommittedFileUploads,
  useUpdateValue,
  useValue,
  useVariableProps,
  useVisibleChildPointers,
  userNode,
  validateEnumValue,
  validateFeatureFolders,
  validateLibraries,
  validateOrThrow,
  validateOrg,
  validationRule,
  valueKey,
  variable2 as variable,
  vendorLibrary,
  videoDisplayMode,
  weaveSeams,
  wellKnownName,
  wellKnownType,
  widgetDescriptor,
  widgetStoragePath,
  widgetUrl,
  withBetterError,
  withContextErrors,
  withIds,
  writeLastArm,
  writePolicyPath
};
//# sourceMappingURL=index.mjs.map